import * as application from './alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Capplication
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Capplication|undefined;
	public abstract get root():Capplication;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
type Vvalue_object = { name: 'choice', definition: application.Cstate_group}|{ name: 'collection', definition: application.Ccollection_object}|{ name: 'node', definition: application.Cnode__application}|{ name: 'leaf', definition: application.Cleaf_value}
export class Cvalue_object extends AlanStruct {
	constructor(
		public readonly variant:Vvalue_object) { super(); }
	public cast<K extends Vvalue_object['name']>(_variant:K):Extract<Vvalue_object, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vvalue_object['name']]:(($:Extract<Vvalue_object, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'value object'`; }
	public is(other:Cvalue_object):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vstep_type = { name: 'node step', definition: (typeof Cstep_type.Pnode_step)}|{ name: 'entry step', definition: application.Centry_step_definition}
export class Cstep_type extends AlanStruct {
	public static Pnode_step:Cstep_type = new class PrimitiveInstance extends Cstep_type {
		constructor () {
			super({name: 'node step', definition: undefined as unknown as Cstep_type})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vstep_type) { super(); }
	public cast<K extends Vstep_type['name']>(_variant:K):Extract<Vstep_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vstep_type['name']]:(($:Extract<Vstep_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'step type'`; }
	public is(other:Cstep_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnode_step__step_type = application.Cstep_type;
type Vleaf_value = { name: 'reference', definition: application.Creference_value}|{ name: 'file', definition: application.Cfile}|{ name: 'scalar', definition: application.Cscalar_value}
export class Cleaf_value extends AlanStruct {
	constructor(
		public readonly variant:Vleaf_value) { super(); }
	public definitions:{
		value_object: Cvalue_object;
	} = {
		value_object: new Cvalue_object({name:'leaf', definition: this})
	}
	public cast<K extends Vleaf_value['name']>(_variant:K):Extract<Vleaf_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vleaf_value['name']]:(($:Extract<Vleaf_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'leaf value'`; }
	public is(other:Cleaf_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vreference_value = { name: 'node reference', definition: application.Cnode_reference}|{ name: 'entry reference', definition: application.Centry_reference_definition}
export class Creference_value extends AlanStruct {
	constructor(
		public readonly variant:Vreference_value, public input: {
			definer: () => application.Creference_definer
		}) { super(); }
	public definitions:{
		leaf_value: Cleaf_value;
	} = {
		leaf_value: new Cleaf_value({name:'reference', definition: this})
	}
	public readonly output:{
		definer: () => application.Creference_definer;
	} = {
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.definer()).result!)
	};
	public cast<K extends Vreference_value['name']>(_variant:K):Extract<Vreference_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vreference_value['name']]:(($:Extract<Vreference_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'reference value'`; }
	public is(other:Creference_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vreference_definer = { name: 'rule', definition: application.Crules}|{ name: 'text', definition: application.Cyes__has_reference}
export class Creference_definer extends AlanStruct {
	constructor(
		public readonly variant:Vreference_definer, public input: {
			behaviour: () => application.Creference_behaviour,
			defining_property: () => application.Cproperty__type__attributes,
			dependency: () => application.Cdependency__application,
			direction: () => application.Cdirection_annotation,
			location: () => application.Crelative_object_location,
			recursion: () => application.Crecursion_annotation,
			referenced_node_type: () => application.Cnode__application,
			step_dependency: () => application.Cdependency__application,
			value_source_dependence: () => application.Cvalue_source
		}) { super(); }
	public readonly output:{
		behaviour: () => application.Creference_behaviour;
		computation_phase: () => application.Ccomputation_phase;
		defining_property: () => application.Cproperty__type__attributes;
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection_annotation;
		location: () => application.Crelative_object_location;
		recursion: () => application.Crecursion_annotation;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		behaviour: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.behaviour()).result!),
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'rule': {
						const application__reference_definer_out___computation_phase___rule_nval = context.cast('rule');
						return resolve(context)
							.then(context => application__reference_definer_out___computation_phase___rule_nval)
							.then(context => context?.properties.type?.state.node.output.phase()).result;
					}
					case 'text': {
						const application__reference_definer_out___computation_phase___text_nval = context.cast('text');
						return resolve(context)
							.then(context => application__reference_definer_out___computation_phase___text_nval)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.phase()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		defining_property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.defining_property()).result!),
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.direction()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!),
		recursion: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.recursion()).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.referenced_node_type()).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.step_dependency()).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source_dependence()).result!)
	};
	public cast<K extends Vreference_definer['name']>(_variant:K):Extract<Vreference_definer, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vreference_definer['name']]:(($:Extract<Vreference_definer, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'reference definer'`; }
	public is(other:Creference_definer):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Ventry_step_definition = { name: 'containment', definition: application.Ccontainment__type__node_reference}|{ name: 'reference', definition: application.Centry_reference_definition}
export class Centry_step_definition extends AlanStruct {
	constructor(
		public readonly variant:Ventry_step_definition) { super(); }
	public definitions:{
		step_type: Cstep_type;
	} = {
		step_type: new Cstep_type({name:'entry step', definition: this})
	}
	public readonly output:{
		collection: () => application.Ccollection__type__property;
		definer: () => application.Creference_definer;
		iubc: () => application.Cupper_bound_cardinality;
		local_iubc: () => application.Cupper_bound_cardinality;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'containment': {
						const application__entry_step_definition_out___collection___containment_nval = context.cast('containment');
						return resolve(context)
							.then(context => application__entry_step_definition_out___collection___containment_nval)
							.then(context => context?.inferences.collection()).result;
					}
					case 'reference': {
						const application__entry_step_definition_out___collection___reference_nval = context.cast('reference');
						return resolve(context)
							.then(context => application__entry_step_definition_out___collection___reference_nval)
							.then(context => context?.component_root.output.collection()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'containment': {
						const application__entry_step_definition_out___definer___containment_nval = context.cast('containment');
						return resolve(context)
							.then(context => application__entry_step_definition_out___definer___containment_nval)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.definitions.reference_definer).result;
					}
					case 'reference': {
						const application__entry_step_definition_out___definer___reference_nval = context.cast('reference');
						return resolve(context)
							.then(context => application__entry_step_definition_out___definer___reference_nval)
							.then(context => context?.component_root.output.definer())
							.then(context => context?.component_root.output.reference_definer()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		iubc: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'containment': {
						const application__entry_step_definition_out___iubc___containment_nval = context.cast('containment');
						return resolve(context)
							.then(context => application__entry_step_definition_out___iubc___containment_nval)
							.then(context => context?.properties.key_node_path?.inferences.containment_iubc()).result;
					}
					case 'reference': {
						const application__entry_step_definition_out___iubc___reference_nval = context.cast('reference');
						return resolve(context)
							.then(context => application__entry_step_definition_out___iubc___reference_nval)
							.then(context => context?.component_root.output.iubc()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		local_iubc: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'containment': {
						const application__entry_step_definition_out___local_iubc___containment_nval = context.cast('containment');
						return resolve(context)
							.then(context => application__entry_step_definition_out___local_iubc___containment_nval)
							.then(context => context?.properties.key_node_path?.inferences.local_key_node_iubc()).result;
					}
					case 'reference': {
						const application__entry_step_definition_out___local_iubc___reference_nval = context.cast('reference');
						return resolve(context)
							.then(context => application__entry_step_definition_out___local_iubc___reference_nval)
							.then(context => context?.component_root.output.local_iubc()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Ventry_step_definition['name']>(_variant:K):Extract<Ventry_step_definition, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Ventry_step_definition['name']]:(($:Extract<Ventry_step_definition, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'entry step definition'`; }
	public is(other:Centry_step_definition):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Ventry_reference_definer = { name: 'rule', definition: application.Centry_reference}|{ name: 'text', definition: application.Cyes__has_reference}
export class Centry_reference_definer extends AlanStruct {
	constructor(
		public readonly variant:Ventry_reference_definer, public input: {
			defining_property: () => application.Cproperty__type__attributes
		}) { super(); }
	public readonly output:{
		defining_property: () => application.Cproperty__type__attributes;
		reference_definer: () => application.Creference_definer;
	} = {
		defining_property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.defining_property()).result!),
		reference_definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'rule': {
						const application__entry_reference_definer_out___reference_definer___rule_nval = context.cast('rule');
						return resolve(context)
							.then(context => application__entry_reference_definer_out___reference_definer___rule_nval)
							.then(context => context?.parent)
							.then(context => context?.definitions.reference_definer).result;
					}
					case 'text': {
						const application__entry_reference_definer_out___reference_definer___text_nval = context.cast('text');
						return resolve(context)
							.then(context => application__entry_reference_definer_out___reference_definer___text_nval)
							.then(context => context?.definitions.reference_definer).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Ventry_reference_definer['name']>(_variant:K):Extract<Ventry_reference_definer, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Ventry_reference_definer['name']]:(($:Extract<Ventry_reference_definer, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'entry reference definer'`; }
	public is(other:Centry_reference_definer):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Twhere_rules_definition = {
	'has rule':'no'|['no', {}]|['yes', Tyes__has_rule];
	'rules':Record<string, Trules>;
};
export class Cwhere_rules_definition extends AlanNode {
	public readonly properties:{
		readonly has_rule:Cwhere_rules_definition.Dhas_rule,
		readonly rules:Cwhere_rules_definition.Drules
	};
	constructor(init:Twhere_rules_definition, public location:AlanNode, public input: {
		definer: () => application.Cwhere_rules_definer,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_rule: new Cwhere_rules_definition.Dhas_rule(init['has rule'], $this),
			rules: new Cwhere_rules_definition.Drules(init['rules'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_rule = {
};
export class Cno__has_rule extends AlanNode {
	constructor(init:Tno__has_rule, public parent:Cwhere_rules_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has rule'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_rule = {
	'first':string;
};
export class Cyes__has_rule extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_rule.Dfirst
	};
	constructor(init:Tyes__has_rule, public parent:Cwhere_rules_definition) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_rule.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has rule'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Trules = {
	'behaviour':Treference_behaviour;
	'direction':Tdirection_annotation;
	'has predecessor':'no'|['no', {}]|['yes', Tyes__has_predecessor__rules];
	'recursion':Trecursion_annotation;
	'type':['entry reference', Tentry_reference]|['node reference', Tnode_reference];
	'ui':Tui_reference_rule;
};
export class Crules extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		reference_definer: Creference_definer;
	} = {
		reference_definer: new Creference_definer({name:'rule', definition: this}, {
			behaviour: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.behaviour).result!),
			defining_property: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.definer())
				.then(context => context?.component_root.output.defining_property()).result!),
			dependency: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.type?.state.node.output.dependency()).result!),
			direction: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.direction).result!),
			location: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.type?.state.node.output.location()).result!),
			recursion: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.recursion).result!),
			referenced_node_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!),
			step_dependency: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.type?.state.node.output.step_dependency()).result!),
			value_source_dependence: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.type?.state.node.output.value_source_dependence()).result!)
		})
	}
	public readonly properties:{
		readonly behaviour:Creference_behaviour & { readonly inferences: {
			existence_requirement_for_path: () => application.Cexistence__application;
		} },
		readonly direction:Cdirection_annotation,
		readonly has_predecessor:Crules.Dhas_predecessor,
		readonly recursion:Crecursion_annotation,
		readonly type:Crules.Dtype,
		readonly ui:Cui_reference_rule
	};
	constructor(key:string, init:Trules, public parent:Cwhere_rules_definition) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			behaviour: new Crules.Dbehaviour(init['behaviour'], $this),
			direction: new Crules.Ddirection(init['direction'], $this),
			has_predecessor: new Crules.Dhas_predecessor(init['has predecessor'], $this),
			recursion: new Crules.Drecursion(init['recursion'], $this),
			type: new Crules.Dtype(init['type'], $this),
			ui: new Crules.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rules'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_predecessor__rules = {
};
export class Cno__has_predecessor__rules extends AlanNode {
	constructor(init:Tno__has_predecessor__rules, public parent:Crules) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_predecessor__rules = {
	'rule':string;
};
export class Cyes__has_predecessor__rules extends AlanNode {
	public readonly properties:{
		readonly rule:Cyes__has_predecessor__rules.Drule
	};
	constructor(init:Tyes__has_predecessor__rules, public parent:Crules) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cyes__has_predecessor__rules.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_reference = {
	'expression':Tentry_reference_definition;
};
export class Centry_reference extends AlanNode {
	public definitions:{
		entry_reference_definer: Centry_reference_definer;
	} = {
		entry_reference_definer: new Centry_reference_definer({name:'rule', definition: this}, {
			defining_property: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.definer())
				.then(context => context?.component_root.output.defining_property()).result!)
		})
	}
	public readonly properties:{
		readonly expression:Centry_reference_definition
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		location: () => application.Crelative_object_location;
		phase: () => application.Ccomputation_phase;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		step_type: () => application.Cstep_type;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.direction()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.location()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.phase()).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.referenced_node_type()).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.step_dependency()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.definitions.entry_step_definition)
			.then(context => context?.definitions.step_type).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.value_source_dependence()).result!)
	}
	public readonly inferences:{
		behaviour: () => application.Cexistence__application,
		key_value_source: () => application.Cvalue_source
	} = {
		behaviour: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__entry_reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.definer())
				.then(context => {
					switch (context?.variant.name) {
						case 'state': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
						}
						case 'text': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source())
								.then(context => {
									switch (context?.variant.name) {
										case 'base': {
											return resolve(context)
												.then(() => this.parent)
												.then(context => context?.properties.behaviour)
												.then(context => context?.component_root.output.existence())
												.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result;
										}
										case 'derived': {
											return resolve(context)
												.then(() => this.parent)
												.then(context => context?.properties.behaviour)
												.then(context => context?.component_root.output.existence())
												.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		key_value_source: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__entry_reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.definer())
				.then(context => {
					switch (context?.variant.name) {
						case 'state': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source())
								.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
								.then(context => context?.definitions.value_source).result;
						}
						case 'text': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source()).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tentry_reference, public parent:Crules) {
		super();
		const $this = this;
		this.properties = {
			expression: new Centry_reference.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'entry reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_reference = {
	'object path':Twhere_rule_object_path;
	'type':['containment', Tcontainment__type__node_reference]|['equality', Tequality__type__node_reference]|'existence'|['existence', {}];
};
export class Cnode_reference extends AlanNode {
	public definitions:{
		reference_value: Creference_value;
	} = {
		reference_value: new Creference_value({name:'node reference', definition: this}, {
			definer: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.definitions.reference_definer).result!)
		})
	}
	public readonly properties:{
		readonly object_path:Cwhere_rule_object_path,
		readonly type:Cnode_reference.Dtype
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		location: () => application.Crelative_object_location;
		phase: () => application.Ccomputation_phase;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		step_type: () => application.Cstep_type;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.direction()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.location()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.phase()).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.step_dependency()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.step_type()).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source_dependence()).result!)
	}
	constructor(init:Tnode_reference, public parent:Crules) {
		super();
		const $this = this;
		this.properties = {
			object_path: new Cnode_reference.Dobject_path(init['object path'], $this),
			type: new Cnode_reference.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontainment__type__node_reference = {
	'key node path':Twhere_rule_object_path;
};
export class Ccontainment__type__node_reference extends AlanNode {
	public definitions:{
		entry_step_definition: Centry_step_definition;
	} = {
		entry_step_definition: new Centry_step_definition({name:'containment', definition: this})
	}
	public readonly properties:{
		readonly key_node_path:Cwhere_rule_object_path & { readonly inferences: {
			containment_iubc: () => application.Cupper_bound_cardinality;
			local_key_node_iubc: () => application.Cupper_bound_cardinality;
			node_context: () => application.Cnode_navigation_context;
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		location: () => application.Crelative_object_location;
		phase: () => application.Ccomputation_phase;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		step_type: () => application.Cstep_type;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.direction()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.location()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.object_path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.key_node_path)
					.then(context => context?.component_root.output.phase()).result!

				})).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.node).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.entry_step_definition).result!;
				return new Cdependency_definition({name: 'entry step', definition: conv_context},{
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.inferences.collection())
					.then(context => context?.definitions.entity)
					.then(context => context?.definitions.relative_object_location).result!
				, 
				parent: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.object_path)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.dependency()).result!

				});
			})
			.then(context => context?.definitions.dependency).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.entry_step_definition)
			.then(context => context?.definitions.step_type).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.value_source_dependence())
			.then(context => {
				switch (context?.variant.name) {
					case 'base': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.properties.key_node_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result;
					}
					case 'derived': {
						const application__where_rules_definition__rules__type__node_reference__type__containment_sout___value_source_dependence___derived_nval = context.cast('derived');
						return resolve(context)
							.then(context => application__where_rules_definition__rules__type__node_reference__type__containment_sout___value_source_dependence___derived_nval)
							.then(context => context?.definitions.value_source).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property,
		key_reference_definer: () => application.Cyes__has_reference,
		reference_definer: () => application.Creference_definer,
		value_source_when_mandatory: () => application.Cvalue_source
	} = {
		collection: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__containment_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.object_path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as application.Cvalue_object : undefined)
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined)
				.then(context => context?.variant.name === 'collection attribute' ? context.variant.definition as application.Ccollection_attribute : undefined)
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		key_reference_definer: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__containment_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => context?.properties.key_property?.ref)
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		reference_definer: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__containment_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.reference_definer).result!;
		})
		,
		value_source_when_mandatory: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__containment_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.behaviour)
				.then(context => context?.component_root.output.existence())
				.then(context => {
					switch (context?.variant.name) {
						case 'mandatory': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source())
								.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
								.then(context => context?.definitions.value_source).result;
						}
						case 'optional': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source()).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tcontainment__type__node_reference, public parent:Cnode_reference) {
		super();
		const $this = this;
		this.properties = {
			key_node_path: new Ccontainment__type__node_reference.Dkey_node_path(init['key node path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'containment'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequality__type__node_reference = {
	'node path':Twhere_rule_object_path;
};
export class Cequality__type__node_reference extends AlanNode {
	public readonly properties:{
		readonly node_path:Cwhere_rule_object_path & { readonly inferences: {
			node: () => application.Cnode__application;
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		location: () => application.Crelative_object_location;
		phase: () => application.Ccomputation_phase;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		step_type: () => application.Cstep_type;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.direction()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.location()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.phase()).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node_type()).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.dependency()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cstep_type.Pnode_step).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.value_source_dependence()).result!)
	}
	public readonly inferences:{
		node_type: () => application.Cnode__application,
		value_source_when_mandatory: () => application.Cvalue_source
	} = {
		node_type: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__equality_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.object_path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})
		,
		value_source_when_mandatory: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__equality_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.behaviour)
				.then(context => context?.component_root.output.existence())
				.then(context => {
					switch (context?.variant.name) {
						case 'mandatory': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source())
								.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
								.then(context => context?.definitions.value_source).result;
						}
						case 'optional': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.value_source()).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tequality__type__node_reference, public parent:Cnode_reference) {
		super();
		const $this = this;
		this.properties = {
			node_path: new Cequality__type__node_reference.Dnode_path(init['node path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'equality'`; }
	public get entity() { return this.parent.entity; }
}
export type Texistence__type__node_reference = {
};
export class Cexistence__type__node_reference extends AlanNode {
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		location: () => application.Crelative_object_location;
		phase: () => application.Ccomputation_phase;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		step_type: () => application.Cstep_type;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.direction()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.location()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.phase()).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node_type()).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.dependency()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cstep_type.Pnode_step).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.object_path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.value_source_dependence()).result!)
	}
	public readonly inferences:{
		node_type: () => application.Cnode__application
	} = {
		node_type: cache((detach:boolean) => {
			const application__where_rules_definition__rules__type__node_reference__type__existence_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.object_path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Texistence__type__node_reference, public parent:Cnode_reference) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existence'`; }
	public get entity() { return this.parent.entity; }
}
type Vwhere_rules_definer = { name: 'state', definition: application.Cstates__state_group}|{ name: 'text', definition: application.Cyes__has_reference}
export class Cwhere_rules_definer extends AlanStruct {
	constructor(
		public readonly variant:Vwhere_rules_definer) { super(); }
	public readonly output:{
		defining_property: () => application.Cproperty__type__attributes;
		value_source: () => application.Cvalue_source;
	} = {
		defining_property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'state': {
						const application__where_rules_definer_out___defining_property___state_nval = context.cast('state');
						return resolve(context)
							.then(context => application__where_rules_definer_out___defining_property___state_nval)
							.then(context => context?.parent)
							.then(context => context?.parent).result;
					}
					case 'text': {
						const application__where_rules_definer_out___defining_property___text_nval = context.cast('text');
						return resolve(context)
							.then(context => application__where_rules_definer_out___defining_property___text_nval)
							.then(context => context?.parent)
							.then(context => context?.parent).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.output.defining_property())
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	};
	public cast<K extends Vwhere_rules_definer['name']>(_variant:K):Extract<Vwhere_rules_definer, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vwhere_rules_definer['name']]:(($:Extract<Vwhere_rules_definer, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'where rules definer'`; }
	public is(other:Cwhere_rules_definer):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Twhere_rule_step = {
	'rule':string;
};
export class Cwhere_rule_step extends AlanNode {
	public readonly properties:{
		readonly rule:Cwhere_rule_step.Drule
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		definer: () => application.Creference_definer;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type())
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(context => evaluate__inline_dependency_chain(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.properties.rule?.ref)
							.then(context => context?.properties.type?.state.node.output.step_dependency()).result!, {
						target: () => resolve(this)
							.then(context => evaluate__eliminate_dependencies(detach, 
								resolve(context)
									.then(() => this)
									.then(context => context?.properties.rule?.ref)
									.then(context => context?.properties.type?.state.node.output.location())
									.then(context => context?.component_root.output.entity()).result!, {
								chain: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.context())
									.then(context => context?.component_root.output.dependency()).result!

								})).result!

						})).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(context => evaluate__step_existence(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.existence()).result!, {
						step_existence: () => resolve(this)
							.then(() => this)
							.then(context => context?.properties.rule?.inferences.existence()).result!

						})).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.rule?.inferences.relative_location_of_referenced_node()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(context => evaluate__step_value_source_dependence(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!, {
						step_value_source_dependence: () => resolve(this)
							.then(() => this)
							.then(context => context?.properties.rule?.inferences.reference())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						})).result!

				});
			}).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.definitions.reference_definer).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.rule?.ref)
					.then(context => context?.properties.type?.state.node.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.phase()).result!

				})).result!)
	};
	constructor(init:Twhere_rule_step, public location:AlanNode, public input: {
		context: () => application.Cnode_navigation_context,
		definition: () => application.Cwhere_rules_definition,
		dependency_location_support: () => application.Crecursion_dependence,
		existence_support: () => application.Cexistence__application,
		phase: () => application.Ccomputation_phase,
		referenced_location_support: () => application.Crecursion_dependence,
		target_root_location: () => application.Croot_location,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cwhere_rule_step.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Twhere_rule_object_path = {
	'context':['sibling rule', Tsibling_rule]|['this', Tthis__context__where_rule_object_path];
	'path':Tobject_path_tail;
};
export class Cwhere_rule_object_path extends AlanNode {
	public readonly properties:{
		readonly context:Cwhere_rule_object_path.Dcontext,
		readonly path:Cobject_path_tail
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!)
	};
	constructor(init:Twhere_rule_object_path, public location:AlanNode, public input: {
		definer: () => application.Cwhere_rules_definer,
		existence_support: () => application.Cexistence__application,
		rule: () => application.Crules,
		value_source_support: () => application.Cvalue_source,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			context: new Cwhere_rule_object_path.Dcontext(init['context'], $this),
			path: new Cwhere_rule_object_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tsibling_rule = {
	'rule':string;
};
export class Csibling_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Csibling_rule.Drule
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type())
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.rule?.ref)
					.then(context => context?.properties.type?.state.node.output.step_dependency()).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.rule?.ref)
					.then(context => context?.properties.type?.state.node.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.rule?.ref)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.existence()).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.rule?.ref)
					.then(context => context?.properties.type?.state.node.output.location()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.rule?.ref)
					.then(context => context?.properties.type?.state.node.output.value_source_dependence()).result!

				});
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.phase()).result!)
	}
	constructor(init:Tsibling_rule, public parent:Cwhere_rule_object_path) {
		super();
		const $this = this;
		this.properties = {
			rule: new Csibling_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'sibling rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tthis__context__where_rule_object_path = {
	'head':Tcontext_object_step;
};
export class Cthis__context__where_rule_object_path extends AlanNode {
	public readonly properties:{
		readonly head:Ccontext_object_step
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tthis__context__where_rule_object_path, public parent:Cwhere_rule_object_path) {
		super();
		const $this = this;
		this.properties = {
			head: new Cthis__context__where_rule_object_path.Dhead(init['head'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'this'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvariablized_object_path = {
	'head':Tcontext_object_step;
	'path':Tobject_path_tail;
};
export class Cvariablized_object_path extends AlanNode {
	public readonly properties:{
		readonly head:Ccontext_object_step,
		readonly path:Cobject_path_tail
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!)
	};
	constructor(init:Tvariablized_object_path, public location:AlanNode, public input: {
		dependency_location_support: () => application.Crecursion_dependence,
		direction_support: () => application.Cdirection,
		existence_support: () => application.Cexistence__application,
		modifier: () => application.Cvalue_source,
		parent_navigation_support: () => application.Cnavigation_support,
		this_property: () => application.Cproperty__type__attributes,
		value_source_support: () => application.Cvalue_source,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			head: new Cvariablized_object_path.Dhead(init['head'], $this),
			path: new Cvariablized_object_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vvariable__application = { name: 'assigned', definition: application.Cvariable_assignment}|{ name: 'unassigned', definition: (typeof Cvariable__application.Punassigned)}
export class Cvariable__application extends AlanStruct {
	public static Punassigned:Cvariable__application = new class PrimitiveInstance extends Cvariable__application {
		constructor () {
			super({name: 'unassigned', definition: undefined as unknown as Cvariable__application})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vvariable__application) { super(); }
	public cast<K extends Vvariable__application['name']>(_variant:K):Extract<Vvariable__application, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vvariable__application['name']]:(($:Extract<Vvariable__application, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'variable'`; }
	public is(other:Cvariable__application):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cunassigned__variable = application.Cvariable__application;
export type Tvariable_assignment = {
	'name':['explicit', Texplicit__name__variable_assignment]|'implicit'|['implicit', {}];
};
export class Cvariable_assignment extends AlanNode {
	public definitions:{
		variable: Cvariable__application;
	} = {
		variable: new Cvariable__application({name:'assigned', definition: this})
	}
	public readonly properties:{
		readonly name:Cvariable_assignment.Dname
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_variable: () => application.Cvariable__application;
		variable: () => application.Cvariable__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node_type()).result!),
		parent_variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_variable()).result!),
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.variable).result!)
	};
	constructor(init:Tvariable_assignment, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application,
		parent_variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			name: new Cvariable_assignment.Dname(init['name'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texplicit__name__variable_assignment = {
	'name':string;
	'named objects':Record<string, Tnamed_objects__explicit__name__variable_assignment>;
};
export class Cexplicit__name__variable_assignment extends AlanNode {
	public readonly properties:{
		readonly name:Cexplicit__name__variable_assignment.Dname,
		readonly named_objects:Cexplicit__name__variable_assignment.Dnamed_objects
	};
	constructor(init:Texplicit__name__variable_assignment, public parent:Cvariable_assignment) {
		super();
		const $this = this;
		this.properties = {
			name: new Cexplicit__name__variable_assignment.Dname(init['name'], $this),
			named_objects: new Cexplicit__name__variable_assignment.Dnamed_objects(init['named objects'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnamed_objects__explicit__name__variable_assignment = {
	'has successor':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cnamed_objects__explicit__name__variable_assignment extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_successor:Cnamed_objects__explicit__name__variable_assignment.Dhas_successor
	};
	constructor(key:string, init:Tnamed_objects__explicit__name__variable_assignment, public parent:Cexplicit__name__variable_assignment) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_successor: new Cnamed_objects__explicit__name__variable_assignment.Dhas_successor(init['has successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'named objects'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor__named_objects__explicit__name__variable_assignment = {
};
export class Cno__has_successor__named_objects__explicit__name__variable_assignment extends AlanNode {
	constructor(init:Tno__has_successor__named_objects__explicit__name__variable_assignment, public parent:Cnamed_objects__explicit__name__variable_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__named_objects__explicit__name__variable_assignment = {
};
export class Cyes__has_successor__named_objects__explicit__name__variable_assignment extends AlanNode {
	constructor(init:Tyes__has_successor__named_objects__explicit__name__variable_assignment, public parent:Cnamed_objects__explicit__name__variable_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Timplicit__name__variable_assignment = {
};
export class Cimplicit__name__variable_assignment extends AlanNode {
	constructor(init:Timplicit__name__variable_assignment, public parent:Cvariable_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'implicit'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__value_source_checker_impl(detach:boolean, application__value_source_checker_impl_nval:application.Cvalue_source, input: {
	value_source_support: () => application.Cvalue_source
}):application.Cvalue_source {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.value_source_support())
		.then(context => {
			switch (context?.variant.name) {
				case 'base': {
					return resolve(context)
						.then(context => {
							const left = resolve(context)
								.then(context => application__value_source_checker_impl_nval).result;
							const right = resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.value_source_support()).result;
							return left?.is(right!) ? left : undefined
						}).result;
				}
				case 'derived': {
					return resolve(context)
						.then(context => application__value_source_checker_impl_nval).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__value_source_checker(detach:boolean, application__value_source_checker_nval:application.Cvalue_source, input: {
	dependency_root_location: () => application.Croot_location,
	target_root_location: () => application.Croot_location,
	value_source_support: () => application.Cvalue_source
}):application.Cvalue_source {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.target_root_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'dataset': {
					return resolve(context)
						.then(context => evaluate__value_source_checker_impl(detach, 
							resolve(context)
								.then(context => application__value_source_checker_nval).result!, {
							value_source_support: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.value_source_support()).result!

							})).result;
				}
				case 'operation': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_root_location())
						.then(context => {
							switch (context?.variant.name) {
								case 'dataset': {
									return resolve(context)
										.then(context => application__value_source_checker_nval).result;
								}
								case 'operation': {
									return resolve(context)
										.then(context => evaluate__value_source_checker_impl(detach, 
											resolve(context)
												.then(context => application__value_source_checker_nval).result!, {
											value_source_support: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.value_source_support()).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
type Vvalue_source = { name: 'derived', definition: application.Cderived_value_source}|{ name: 'base', definition: application.Cbase_value_source}
export class Cvalue_source extends AlanStruct {
	constructor(
		public readonly variant:Vvalue_source) { super(); }
	public cast<K extends Vvalue_source['name']>(_variant:K):Extract<Vvalue_source, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vvalue_source['name']]:(($:Extract<Vvalue_source, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'value source'`; }
	public is(other:Cvalue_source):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vvalue_member_inclusion = { name: 'no', definition: (typeof Cvalue_member_inclusion.Pno)}|{ name: 'yes', definition: (typeof Cvalue_member_inclusion.Pyes)}
export class Cvalue_member_inclusion extends AlanStruct {
	public static Pno:Cvalue_member_inclusion = new class PrimitiveInstance extends Cvalue_member_inclusion {
		constructor () {
			super({name: 'no', definition: undefined as unknown as Cvalue_member_inclusion})
			this.variant.definition = this;
		}
	}
	public static Pyes:Cvalue_member_inclusion = new class PrimitiveInstance extends Cvalue_member_inclusion {
		constructor () {
			super({name: 'yes', definition: undefined as unknown as Cvalue_member_inclusion})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vvalue_member_inclusion) { super(); }
	public cast<K extends Vvalue_member_inclusion['name']>(_variant:K):Extract<Vvalue_member_inclusion, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vvalue_member_inclusion['name']]:(($:Extract<Vvalue_member_inclusion, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'value member inclusion'`; }
	public is(other:Cvalue_member_inclusion):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cno__value_member_inclusion = application.Cvalue_member_inclusion;
export import Cyes__value_member_inclusion = application.Cvalue_member_inclusion;
function evaluate__value_member_filter(detach:boolean, application__value_member_filter_nval:application.Cproperty__type__attributes, input: {
	filter: () => application.Cproperty__type__attributes
}):application.Cvalue_member_inclusion {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(context => application__value_member_filter_nval).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.filter()).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(() => self)
			.then(() => application.Cvalue_member_inclusion.Pno)
			.then(context => context?.variant.name === 'yes' ? context.variant.definition as application.Cyes__value_member_inclusion : undefined).result
			} else {
				return resolve(match_context)
				.then(() => self)
				.then(() => application.Cvalue_member_inclusion.Pyes)
				.then(context => context?.variant.name === 'yes' ? context.variant.definition as application.Cyes__value_member_inclusion : undefined).result
			}
		}).result!
}
function evaluate__user_source_checker(detach:boolean, application__user_source_checker_nval:application.Cproperty__type__attributes, ):application.Cbase_value_source {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => application__user_source_checker_nval)
		.then(context => context?.component_root.output.location())
		.then(context => context?.component_root.output.root())
		.then(context => {
			switch (context?.variant.name) {
				case 'dataset': {
					return resolve(context)
						.then(context => application__user_source_checker_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
						.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
						.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined)
						.then(context => context?.definitions.base_value_source).result;
				}
				case 'operation': {
					return resolve(context)
						.then(context => application__user_source_checker_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tuser_requirement = {
	'expression':Tnode_expression;
	'has alternative':'no'|['no', {}]|['yes', Tyes__has_alternative];
	'has filter':'no'|['no', {}]|['yes', Tyes__has_filter];
};
export class Cuser_requirement extends AlanNode {
	public readonly properties:{
		readonly expression:Cnode_expression,
		readonly has_alternative:Cuser_requirement.Dhas_alternative,
		readonly has_filter:Cuser_requirement.Dhas_filter
	};
	constructor(init:Tuser_requirement, public location:AlanNode, public input: {
		definition_node_type: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cuser_requirement.Dexpression(init['expression'], $this),
			has_alternative: new Cuser_requirement.Dhas_alternative(init['has alternative'], $this),
			has_filter: new Cuser_requirement.Dhas_filter(init['has filter'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_alternative = {
};
export class Cno__has_alternative extends AlanNode {
	constructor(init:Tno__has_alternative, public parent:Cuser_requirement) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_alternative = {
	'alternative':Tuser_requirement;
};
export class Cyes__has_alternative extends AlanNode {
	public readonly properties:{
		readonly alternative:Cuser_requirement
	};
	constructor(init:Tyes__has_alternative, public parent:Cuser_requirement) {
		super();
		const $this = this;
		this.properties = {
			alternative: new Cyes__has_alternative.Dalternative(init['alternative'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_filter = {
};
export class Cno__has_filter extends AlanNode {
	constructor(init:Tno__has_filter, public parent:Cuser_requirement) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_filter = {
	'assignment':Toptional_variable_assignment;
	'path':Tuser_requirement;
};
export class Cyes__has_filter extends AlanNode {
	public readonly properties:{
		readonly assignment:Coptional_variable_assignment,
		readonly path:Cuser_requirement
	};
	constructor(init:Tyes__has_filter, public parent:Cuser_requirement) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cyes__has_filter.Dassignment(init['assignment'], $this),
			path: new Cyes__has_filter.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Voptional_command_operation = { name: 'operation', definition: application.Ccommand_operation}|{ name: 'none', definition: (typeof Coptional_command_operation.Pnone)}
export class Coptional_command_operation extends AlanStruct {
	public static Pnone:Coptional_command_operation = new class PrimitiveInstance extends Coptional_command_operation {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_command_operation})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_command_operation) { super(); }
	public cast<K extends Voptional_command_operation['name']>(_variant:K):Extract<Voptional_command_operation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_command_operation['name']]:(($:Extract<Voptional_command_operation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional command operation'`; }
	public is(other:Coptional_command_operation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_command_operation = application.Coptional_command_operation;
type Vcommand_operation = { name: 'update', definition: (typeof Ccommand_operation.Pupdate)}|{ name: 'initialization', definition: application.Ccommand_initialization_operation}
export class Ccommand_operation extends AlanStruct {
	public static Pupdate:Ccommand_operation = new class PrimitiveInstance extends Ccommand_operation {
		constructor () {
			super({name: 'update', definition: undefined as unknown as Ccommand_operation})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vcommand_operation) { super(); }
	public definitions:{
		optional_command_operation: Coptional_command_operation;
	} = {
		optional_command_operation: new Coptional_command_operation({name:'operation', definition: this})
	}
	public cast<K extends Vcommand_operation['name']>(_variant:K):Extract<Vcommand_operation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_operation['name']]:(($:Extract<Vcommand_operation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command operation'`; }
	public is(other:Ccommand_operation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cupdate__command_operation = application.Ccommand_operation;
type Vcommand_initialization_operation = { name: 'object', definition: (typeof Ccommand_initialization_operation.Pobject)}|{ name: 'interface connection status', definition: application.Ccases__yes__connection_status}|{ name: 'interface instance', definition: application.Cinterface_instance_initializer}|{ name: 'identity', definition: application.Cidentity_initializer}|{ name: 'password', definition: application.Cpassword_initializer}|{ name: 'user', definition: application.Cuser_initializer}
export class Ccommand_initialization_operation extends AlanStruct {
	public static Pobject:Ccommand_initialization_operation = new class PrimitiveInstance extends Ccommand_initialization_operation {
		constructor () {
			super({name: 'object', definition: undefined as unknown as Ccommand_initialization_operation})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vcommand_initialization_operation) { super(); }
	public definitions:{
		command_operation: Ccommand_operation;
	} = {
		command_operation: new Ccommand_operation({name:'initialization', definition: this})
	}
	public cast<K extends Vcommand_initialization_operation['name']>(_variant:K):Extract<Vcommand_initialization_operation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_initialization_operation['name']]:(($:Extract<Vcommand_initialization_operation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command initialization operation'`; }
	public is(other:Ccommand_initialization_operation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cobject__command_initialization_operation = application.Ccommand_initialization_operation;
export type Tuser_initializer = {
	'initializer':Tcommand_object_expression;
};
export class Cuser_initializer extends AlanNode {
	public definitions:{
		command_initialization_operation: Ccommand_initialization_operation;
	} = {
		command_initialization_operation: new Ccommand_initialization_operation({name:'user', definition: this})
	}
	public readonly properties:{
		readonly initializer:Ccommand_object_expression
	};
	public readonly output:{
		username: () => application.Cproperty__type__attributes;
	} = {
		username: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.users_collection())
			.then(context => context?.properties.key_property?.ref)
			.then(context => context?.parent).result!)
	};
	constructor(init:Tuser_initializer, public location:AlanNode, public input: {
		users_collection: () => application.Ccollection__type__property
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initializer: new Cuser_initializer.Dinitializer(init['initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vupper_bound_cardinality = { name: 'many', definition: (typeof Cupper_bound_cardinality.Pmany), widening_index: 0}|{ name: 'one', definition: (typeof Cupper_bound_cardinality.Pone), widening_index: 1}
export class Cupper_bound_cardinality extends AlanStruct {
	public static Pmany:Cupper_bound_cardinality = new class PrimitiveInstance extends Cupper_bound_cardinality {
		constructor () {
			super({name: 'many', definition: undefined as unknown as Cupper_bound_cardinality, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pone:Cupper_bound_cardinality = new class PrimitiveInstance extends Cupper_bound_cardinality {
		constructor () {
			super({name: 'one', definition: undefined as unknown as Cupper_bound_cardinality, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vupper_bound_cardinality) { super(); }
	public cast<K extends Vupper_bound_cardinality['name']>(_variant:K):Extract<Vupper_bound_cardinality, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vupper_bound_cardinality['name']]:(($:Extract<Vupper_bound_cardinality, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'upper bound cardinality'`; }
	public is(other:Cupper_bound_cardinality):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cmany__upper_bound_cardinality = application.Cupper_bound_cardinality;
export import Cone__upper_bound_cardinality = application.Cupper_bound_cardinality;
function evaluate__unwind_sibling_steps(detach:boolean, application__unwind_sibling_steps_nval:application.Cdependency__application, input: {
	location_support: () => application.Crelative_object_location
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__unwind_sibling_steps_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application__unwind_sibling_steps_nval).result;
				}
				case 'step': {
					const application__unwind_sibling_steps___step_nval = context.cast('step');
					return resolve(context)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(context => {
								const left = resolve(context)
									.then(context => application__unwind_sibling_steps___step_nval)
									.then(context => context?.component_root.output.entity()).result;
								const right = resolve(context)
									.then(() => self)
									.then(context => context?.component_root.input.location_support())
									.then(context => context?.component_root.output.entity()).result;
								return left?.is(right!) ? left : undefined
							}).result;
							if (expression_context !== undefined) {
							return resolve(match_context)
							.then(() => self)
							.then(context => context?.component_root.input.location_support())
							.then(context => {
								switch (context?.variant.name) {
									case 'context entity': {
										return resolve(context)
											.then(context => evaluate__unwind_sibling_steps(detach, 
												resolve(context)
													.then(context => application__unwind_sibling_steps___step_nval)
													.then(context => context?.component_root.output.parent()).result!, {
												location_support: () => resolve(self)
													.then(() => self)
													.then(context => context?.component_root.input.location_support()).result!

												})).result;
									}
									case 'sibling entity': {
										const application__unwind_sibling_steps___step___struct___sibling_entity_nval = context.cast('sibling entity');
										return resolve(context)
											.then(context => application__unwind_sibling_steps___step___struct___sibling_entity_nval)
											.then(context => {
												switch (context?.variant.name) {
													case 'graph step': {
														const application__unwind_sibling_steps___step___struct___sibling_entity___graph_step_nval = context.cast('graph step');
														return resolve(context)
															.then(match_context => { 
																const expression_context = resolve(match_context)
																.then(context => {
																	const left = resolve(context)
																		.then(context => application__unwind_sibling_steps___step_nval)
																		.then(context => context?.component_root.output.location()).result;
																	const right = resolve(context)
																		.then(() => self)
																		.then(context => context?.component_root.input.location_support()).result;
																	return left?.is(right!) ? left : undefined
																}).result;
																if (expression_context !== undefined) {
																return resolve(match_context)
																.then(context => evaluate__unwind_sibling_steps(detach, 
																	resolve(context)
																		.then(context => application__unwind_sibling_steps___step_nval)
																		.then(context => context?.component_root.output.parent()).result!, {
																	location_support: () => resolve(self)
																		.then(() => self)
																		.then(context => context?.component_root.input.location_support()).result!

																	})).result
																} else {
																	return resolve(match_context)
																	.then(context => application__unwind_sibling_steps___step_nval)
																	.then(context => {
																		switch (context?.variant.name) {
																			case 'collection step': {
																				return resolve(context)
																					.then(context => application__unwind_sibling_steps_nval).result;
																			}
																			case 'entry step': {
																				return resolve(context)
																					.then(context => application__unwind_sibling_steps_nval).result;
																			}
																			case 'inverse reference step': {
																				return resolve(context)
																					.then(context => application__unwind_sibling_steps_nval).result;
																			}
																			case 'recursion start step': {
																				const application__unwind_sibling_steps___step___struct___sibling_entity___graph_step___none___recursion_start_step_nval = context.cast('recursion start step');
																				return resolve(context)
																					.then(match_context => { 
																						const expression_context = resolve(match_context)
																						.then(context => {
																							const left = resolve(context)
																								.then(context => application__unwind_sibling_steps___step___struct___sibling_entity___graph_step___none___recursion_start_step_nval)
																								.then(context => context?.properties.graph_traversal)
																								.then(context => context?.component_root.output.type()).result;
																							const right = resolve(context)
																								.then(context => application__unwind_sibling_steps___step___struct___sibling_entity___graph_step_nval).result;
																							return left?.is(right!) ? left : undefined
																						}).result;
																						if (expression_context !== undefined) {
																						return resolve(match_context)
																						.then(context => evaluate__unwind_sibling_steps(detach, 
																							resolve(context)
																								.then(context => application__unwind_sibling_steps___step_nval)
																								.then(context => context?.component_root.output.parent()).result!, {
																							location_support: () => resolve(self)
																								.then(() => self)
																								.then(context => context?.component_root.input.location_support()).result!

																							})).result
																						} else {
																							return resolve(match_context)
																							.then(context => application__unwind_sibling_steps_nval).result
																						}
																					}).result;
																			}
																			case undefined: return undefined;
																			default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																		};
																	}).result
																}
															}).result;
													}
													case 'sibling or self step': {
														return resolve(context)
															.then(context => evaluate__unwind_sibling_steps(detach, 
																resolve(context)
																	.then(context => application__unwind_sibling_steps___step_nval)
																	.then(context => context?.component_root.output.parent()).result!, {
																location_support: () => resolve(self)
																	.then(() => self)
																	.then(context => context?.component_root.input.location_support()).result!

																})).result;
													}
													case 'sibling step': {
														return resolve(context)
															.then(context => application__unwind_sibling_steps___step_nval)
															.then(context => context?.component_root.output.location())
															.then(context => {
																switch (context?.variant.name) {
																	case 'context entity': {
																		return resolve(context)
																			.then(context => application__unwind_sibling_steps_nval).result;
																	}
																	case 'sibling entity': {
																		const application__unwind_sibling_steps___step___struct___sibling_entity___sibling_step___sibling_entity_nval = context.cast('sibling entity');
																		return resolve(context)
																			.then(context => application__unwind_sibling_steps___step___struct___sibling_entity___sibling_step___sibling_entity_nval)
																			.then(context => {
																				switch (context?.variant.name) {
																					case 'graph step': {
																						return resolve(context)
																							.then(context => evaluate__unwind_sibling_steps(detach, 
																								resolve(context)
																									.then(context => application__unwind_sibling_steps___step_nval)
																									.then(context => context?.component_root.output.parent()).result!, {
																								location_support: () => resolve(self)
																									.then(context => application__unwind_sibling_steps___step_nval)
																									.then(context => context?.component_root.output.location()).result!

																								})).result;
																					}
																					case 'sibling or self step': {
																						return resolve(context)
																							.then(context => application__unwind_sibling_steps_nval).result;
																					}
																					case 'sibling step': {
																						return resolve(context)
																							.then(context => application__unwind_sibling_steps___step_nval)
																							.then(context => context?.component_root.output.parent()).result;
																					}
																					case undefined: return undefined;
																					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																				};
																			}).result;
																	}
																	case undefined: return undefined;
																	default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
																};
															}).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
												};
											}).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result
							} else {
								return resolve(match_context)
								.then(context => application__unwind_sibling_steps_nval).result
							}
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__unwind_graph_traversing_steps(detach:boolean, application__unwind_graph_traversing_steps_nval:application.Crelative_object_location, input: {
	dependency: () => application.Cdependency__application,
	recursion_start: () => application.Cstart
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.dependency())
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency()).result;
				}
				case 'step': {
					const application__unwind_graph_traversing_steps___step_nval = context.cast('step');
					return resolve(context)
						.then(context => application__unwind_graph_traversing_steps___step_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'collection step': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.dependency()).result;
								}
								case 'entry step': {
									const application__unwind_graph_traversing_steps___step___entry_step_nval = context.cast('entry step');
									return resolve(context)
										.then(context => evaluate__unwind_graph_traversing_steps(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(context => application__unwind_graph_traversing_steps___step___entry_step_nval)
														.then(context => context?.component_root.output.definer())
														.then(context => context?.component_root.output.location()).result;
													const right = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.recursion_start())
														.then(context => context?.properties.graph_traversal)
														.then(context => context?.component_root.output.type())
														.then(context => context?.definitions.sibling_dependency_retrieval)
														.then(context => context?.definitions.relative_object_location).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											dependency: () => resolve(self)
												.then(context => application__unwind_graph_traversing_steps___step_nval)
												.then(context => context?.component_root.output.parent()).result!
											, 
											recursion_start: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.recursion_start()).result!

											})).result;
								}
								case 'inverse reference step': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.dependency()).result;
								}
								case 'recursion start step': {
									const application__unwind_graph_traversing_steps___step___recursion_start_step_nval = context.cast('recursion start step');
									return resolve(context)
										.then(match_context => { 
											const expression_context = resolve(match_context)
											.then(context => {
												const left = resolve(context)
													.then(context => application__unwind_graph_traversing_steps___step___recursion_start_step_nval).result;
												const right = resolve(context)
													.then(() => self)
													.then(context => context?.component_root.input.recursion_start()).result;
												return left?.is(right!) ? left : undefined
											}).result;
											if (expression_context !== undefined) {
											return resolve(match_context)
											.then(() => self)
											.then(context => context?.component_root.input.dependency()).result
											} else {
												return resolve(match_context)
												.then(match_context => { 
													const expression_context = resolve(match_context)
													.then(context => {
														const left = resolve(context)
															.then(context => application__unwind_graph_traversing_steps___step___recursion_start_step_nval)
															.then(context => context?.properties.graph_traversal)
															.then(context => context?.component_root.output.type()).result;
														const right = resolve(context)
															.then(() => self)
															.then(context => context?.component_root.input.recursion_start())
															.then(context => context?.properties.graph_traversal)
															.then(context => context?.component_root.output.type()).result;
														return left?.is(right!) ? left : undefined
													}).result;
													if (expression_context !== undefined) {
													return resolve(match_context)
													.then(context => evaluate__unwind_graph_traversing_steps(detach, 
														resolve(context)
															.then(context => application__unwind_graph_traversing_steps_nval).result!, {
														dependency: () => resolve(self)
															.then(context => application__unwind_graph_traversing_steps___step_nval)
															.then(context => context?.component_root.output.parent()).result!
														, 
														recursion_start: () => resolve(self)
															.then(() => self)
															.then(context => context?.component_root.input.recursion_start()).result!

														})).result
													} else {
														return resolve(match_context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency()).result
													}
												}).result
											}
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tunary_number_expression = {
	'expression':Tderivation_expression_tail;
};
export class Cunary_number_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression_tail
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	};
	constructor(init:Tunary_number_expression, public location:AlanNode, public input: {
		target: () => application.Cnumber_value,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cunary_number_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vui_walk_operation_support = { name: 'yes', definition: (typeof Cui_walk_operation_support.Pyes)}|{ name: 'no', definition: (typeof Cui_walk_operation_support.Pno)}
export class Cui_walk_operation_support extends AlanStruct {
	public static Pyes:Cui_walk_operation_support = new class PrimitiveInstance extends Cui_walk_operation_support {
		constructor () {
			super({name: 'yes', definition: undefined as unknown as Cui_walk_operation_support})
			this.variant.definition = this;
		}
	}
	public static Pno:Cui_walk_operation_support = new class PrimitiveInstance extends Cui_walk_operation_support {
		constructor () {
			super({name: 'no', definition: undefined as unknown as Cui_walk_operation_support})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vui_walk_operation_support) { super(); }
	public cast<K extends Vui_walk_operation_support['name']>(_variant:K):Extract<Vui_walk_operation_support, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_walk_operation_support['name']]:(($:Extract<Vui_walk_operation_support, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui walk operation support'`; }
	public is(other:Cui_walk_operation_support):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cno__ui_walk_operation_support = application.Cui_walk_operation_support;
export import Cyes__ui_walk_operation_support = application.Cui_walk_operation_support;
export type Tui_todo = {
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_todo];
};
export class Cui_todo extends AlanNode {
	public readonly properties:{
		readonly has_description:Cui_todo.Dhas_description
	};
	constructor(init:Tui_todo, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_description: new Cui_todo.Dhas_description(init['has description'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_description__ui_todo = {
};
export class Cno__has_description__ui_todo extends AlanNode {
	constructor(init:Tno__has_description__ui_todo, public parent:Cui_todo) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_todo = {
	'description':string;
};
export class Cyes__has_description__ui_todo extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_todo, public parent:Cui_todo) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_text_validation = {
	'regular expression':string;
};
export class Cui_text_validation extends AlanNode {
	public readonly properties:{
		readonly regular_expression:string
	};
	constructor(init:Tui_text_validation, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			regular_expression: init['regular expression']
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tui_text_property = {
	'classification':Tui_property_classification;
	'default value':'no'|['no', {}]|['yes', Tyes__default_value];
	'emphasis':'no'|['no', {}]|'yes'|['yes', {}];
	'has custom identifying properties':'no'|['no', {}]|['yes', Tyes__has_custom_identifying_properties];
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_text_property];
	'has validation':'no'|['no', {}]|['yes', Tyes__has_validation__ui_text_property];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_text_property];
	'is label':'no'|['no', {}]|'yes'|['yes', {}];
	'type':'color'|['color', {}]|'default'|['default', {}]|'html'|['html', {}]|'markdown'|['markdown', {}]|'multi-line'|['multi-line', {}]|'url'|['url', {}];
};
export class Cui_text_property extends AlanNode {
	public readonly properties:{
		readonly classification:Cui_property_classification,
		readonly default_value:Cui_text_property.Ddefault_value,
		readonly emphasis:Cui_text_property.Demphasis,
		readonly has_custom_identifying_properties:Cui_text_property.Dhas_custom_identifying_properties,
		readonly has_description:Cui_text_property.Dhas_description,
		readonly has_validation:Cui_text_property.Dhas_validation,
		readonly icon:Cui_text_property.Dicon,
		readonly is_label:Cui_text_property.Dis_label,
		readonly type:Cui_text_property.Dtype
	};
	constructor(init:Tui_text_property, public location:AlanNode, public input: {
		text_property: () => application.Ctext__type__property
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			classification: new Cui_text_property.Dclassification(init['classification'], $this),
			default_value: new Cui_text_property.Ddefault_value(init['default value'], $this),
			emphasis: new Cui_text_property.Demphasis(init['emphasis'], $this),
			has_custom_identifying_properties: new Cui_text_property.Dhas_custom_identifying_properties(init['has custom identifying properties'], $this),
			has_description: new Cui_text_property.Dhas_description(init['has description'], $this),
			has_validation: new Cui_text_property.Dhas_validation(init['has validation'], $this),
			icon: new Cui_text_property.Dicon(init['icon'], $this),
			is_label: new Cui_text_property.Dis_label(init['is label'], $this),
			type: new Cui_text_property.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__default_value = {
};
export class Cno__default_value extends AlanNode {
	constructor(init:Tno__default_value, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default value'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default_value = {
	'default':Tui_scalar_default;
};
export class Cyes__default_value extends AlanNode {
	public readonly properties:{
		readonly default_:Cui_scalar_default
	};
	public readonly inferences:{
		base_value: () => application.Cbase__type__text
	} = {
		base_value: cache((detach:boolean) => {
			const application__ui_text_property__default_value__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.text_property())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__default_value, public parent:Cui_text_property) {
		super();
		const $this = this;
		this.properties = {
			default_: new Cyes__default_value.Ddefault_(init['default'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default value'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__emphasis__ui_text_property = {
};
export class Cno__emphasis__ui_text_property extends AlanNode {
	constructor(init:Tno__emphasis__ui_text_property, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'emphasis'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__emphasis__ui_text_property = {
};
export class Cyes__emphasis__ui_text_property extends AlanNode {
	constructor(init:Tyes__emphasis__ui_text_property, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'emphasis'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_custom_identifying_properties = {
};
export class Cno__has_custom_identifying_properties extends AlanNode {
	constructor(init:Tno__has_custom_identifying_properties, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has custom identifying properties'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_custom_identifying_properties = {
	'selection':Tui_identifying_property_selection;
};
export class Cyes__has_custom_identifying_properties extends AlanNode {
	public readonly properties:{
		readonly selection:Cui_identifying_property_selection
	};
	public readonly inferences:{
		reference: () => application.Cyes__has_reference
	} = {
		reference: cache((detach:boolean) => {
			const application__ui_text_property__has_custom_identifying_properties__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.text_property())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_custom_identifying_properties, public parent:Cui_text_property) {
		super();
		const $this = this;
		this.properties = {
			selection: new Cyes__has_custom_identifying_properties.Dselection(init['selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has custom identifying properties'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_text_property = {
};
export class Cno__has_description__ui_text_property extends AlanNode {
	constructor(init:Tno__has_description__ui_text_property, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_text_property = {
	'description':string;
};
export class Cyes__has_description__ui_text_property extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_text_property, public parent:Cui_text_property) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_validation__ui_text_property = {
};
export class Cno__has_validation__ui_text_property extends AlanNode {
	constructor(init:Tno__has_validation__ui_text_property, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has validation'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_validation__ui_text_property = {
	'rules':Tui_text_validation;
};
export class Cyes__has_validation__ui_text_property extends AlanNode {
	public readonly properties:{
		readonly rules:Cui_text_validation
	};
	public readonly inferences:{
		base_text: () => application.Cbase__type__text,
		optional_reference: () => application.Coptional__existence,
		reference: () => application.Cexistence__application
	} = {
		base_text: cache((detach:boolean) => {
			const application__ui_text_property__has_validation__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.text_property())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		optional_reference: cache((detach:boolean) => {
			const application__ui_text_property__has_validation__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.reference())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})
		,
		reference: cache((detach:boolean) => {
			const application__ui_text_property__has_validation__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.text_property())
				.then(context => context?.properties.has_reference)
				.then(context => {
					const state_group = context;
					switch (state_group?.state.name) {
						case 'no': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => application.Cexistence__application.Poptional).result;
						}
						case 'yes': {
							const application__ui_text_property__has_validation__yes_inf___reference___yes_nval = state_group.cast('yes');
							return resolve(context)
								.then(context => application__ui_text_property__has_validation__yes_inf___reference___yes_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
					};}).result!;
		})

	}
	constructor(init:Tyes__has_validation__ui_text_property, public parent:Cui_text_property) {
		super();
		const $this = this;
		this.properties = {
			rules: new Cyes__has_validation__ui_text_property.Drules(init['rules'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has validation'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_text_property = {
};
export class Cno__icon__ui_text_property extends AlanNode {
	constructor(init:Tno__icon__ui_text_property, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_text_property = {
	'name':string;
};
export class Cyes__icon__ui_text_property extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_text_property, public parent:Cui_text_property) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_label = {
};
export class Cno__is_label extends AlanNode {
	constructor(init:Tno__is_label, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is label'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_label = {
};
export class Cyes__is_label extends AlanNode {
	public readonly inferences:{
		is_key: () => application.Ccollection__type__property
	} = {
		is_key: cache((detach:boolean) => {
			const application__ui_text_property__is_label__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.text_property())
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.type?.state.node.output.attribute_type())
				.then(context => context?.variant.name === 'key' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tyes__is_label, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is label'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcolor = {
};
export class Ccolor extends AlanNode {
	constructor(init:Tcolor, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'color'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdefault = {
};
export class Cdefault extends AlanNode {
	constructor(init:Tdefault, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'default'`; }
	public get entity() { return this.parent.entity; }
}
export type Thtml = {
};
export class Chtml extends AlanNode {
	constructor(init:Thtml, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'html'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmarkdown = {
};
export class Cmarkdown extends AlanNode {
	constructor(init:Tmarkdown, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'markdown'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmulti_line = {
};
export class Cmulti_line extends AlanNode {
	constructor(init:Tmulti_line, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'multi-line'`; }
	public get entity() { return this.parent.entity; }
}
export type Turl = {
};
export class Curl extends AlanNode {
	constructor(init:Turl, public parent:Cui_text_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'url'`; }
	public get entity() { return this.parent.entity; }
}
type Vui_target = { name: 'action', definition: application.Cui_action}|{ name: 'object', definition: application.Cui_object}|{ name: 'unsupported file', definition: (typeof Cui_target.Punsupported_file)}|{ name: 'collection', definition: application.Cui_target_collection}|{ name: 'stategroup', definition: application.Cui_target_stategroup}|{ name: 'scalar', definition: application.Cui_scalar}
export class Cui_target extends AlanStruct {
	public static Punsupported_file:Cui_target = new class PrimitiveInstance extends Cui_target {
		constructor () {
			super({name: 'unsupported file', definition: undefined as unknown as Cui_target})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vui_target) { super(); }
	public readonly output:{
		supports_walk_operation: () => application.Cui_walk_operation_support;
	} = {
		supports_walk_operation: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'action': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cui_walk_operation_support.Pyes).result;
					}
					case 'collection': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cui_walk_operation_support.Pyes).result;
					}
					case 'object': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cui_walk_operation_support.Pno).result;
					}
					case 'scalar': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cui_walk_operation_support.Pno).result;
					}
					case 'stategroup': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cui_walk_operation_support.Pno).result;
					}
					case 'unsupported file': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cui_walk_operation_support.Pno).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vui_target['name']>(_variant:K):Extract<Vui_target, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_target['name']]:(($:Extract<Vui_target, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui target'`; }
	public is(other:Cui_target):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cunsupported_file = application.Cui_target;
type Vui_scalar = { name: 'style', definition: (typeof Cui_scalar.Pstyle)}|{ name: 'property value', definition: application.Cui_property_value}
export class Cui_scalar extends AlanStruct {
	public static Pstyle:Cui_scalar = new class PrimitiveInstance extends Cui_scalar {
		constructor () {
			super({name: 'style', definition: undefined as unknown as Cui_scalar})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vui_scalar) { super(); }
	public definitions:{
		ui_target: Cui_target;
	} = {
		ui_target: new Cui_target({name:'scalar', definition: this})
	}
	public cast<K extends Vui_scalar['name']>(_variant:K):Extract<Vui_scalar, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_scalar['name']]:(($:Extract<Vui_scalar, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui scalar'`; }
	public is(other:Cui_scalar):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cstyle__ui_scalar = application.Cui_scalar;
type Vui_property_value = { name: 'state choice', definition: application.Cui_state_choice}|{ name: 'file', definition: application.Cfile}|{ name: 'text', definition: application.Cui_text}|{ name: 'number', definition: application.Cui_number}
export class Cui_property_value extends AlanStruct {
	constructor(
		public readonly variant:Vui_property_value) { super(); }
	public definitions:{
		ui_scalar: Cui_scalar;
	} = {
		ui_scalar: new Cui_scalar({name:'property value', definition: this})
	}
	public cast<K extends Vui_property_value['name']>(_variant:K):Extract<Vui_property_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_property_value['name']]:(($:Extract<Vui_property_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui property value'`; }
	public is(other:Cui_property_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vui_text = { name: 'intermediate result', definition: application.Capplication}|{ name: 'text property', definition: application.Ctext__type__property}
export class Cui_text extends AlanStruct {
	constructor(
		public readonly variant:Vui_text) { super(); }
	public definitions:{
		ui_property_value: Cui_property_value;
	} = {
		ui_property_value: new Cui_property_value({name:'text', definition: this})
	}
	public cast<K extends Vui_text['name']>(_variant:K):Extract<Vui_text, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_text['name']]:(($:Extract<Vui_text, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui text'`; }
	public is(other:Cui_text):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vui_target_stategroup = { name: 'stategroup property', definition: application.Cstate_group}
export class Cui_target_stategroup extends AlanStruct {
	constructor(
		public readonly variant:Vui_target_stategroup) { super(); }
	public definitions:{
		ui_target: Cui_target;
	} = {
		ui_target: new Cui_target({name:'stategroup', definition: this})
	}
	public cast<K extends Vui_target_stategroup['name']>(_variant:K):Extract<Vui_target_stategroup, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_target_stategroup['name']]:(($:Extract<Vui_target_stategroup, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui target stategroup'`; }
	public is(other:Cui_target_stategroup):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vui_target_state = { name: 'state', definition: application.Cstates__state_group}
export class Cui_target_state extends AlanStruct {
	constructor(
		public readonly variant:Vui_target_state) { super(); }
	public cast<K extends Vui_target_state['name']>(_variant:K):Extract<Vui_target_state, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_target_state['name']]:(($:Extract<Vui_target_state, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui target state'`; }
	public is(other:Cui_target_state):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vui_target_collection = { name: 'collection property', definition: application.Ccollection__type__property}
export class Cui_target_collection extends AlanStruct {
	constructor(
		public readonly variant:Vui_target_collection, public input: {
			entry_object: () => application.Cui_object,
			value_source: () => application.Cvalue_source
		}) { super(); }
	public definitions:{
		ui_target: Cui_target;
	} = {
		ui_target: new Cui_target({name:'collection', definition: this})
	}
	public readonly output:{
		entry_object: () => application.Cui_object;
		value_source: () => application.Cvalue_source;
	} = {
		entry_object: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.entry_object()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	};
	public cast<K extends Vui_target_collection['name']>(_variant:K):Extract<Vui_target_collection, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_target_collection['name']]:(($:Extract<Vui_target_collection, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui target collection'`; }
	public is(other:Cui_target_collection):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tui_style = {
	'style':'accent'|['accent', {}]|'background'|['background', {}]|'brand'|['brand', {}]|'error'|['error', {}]|'foreground'|['foreground', {}]|'link'|['link', {}]|'success'|['success', {}]|'warning'|['warning', {}];
};
export class Cui_style extends AlanNode {
	public readonly properties:{
		readonly style:Cui_style.Dstyle
	};
	constructor(init:Tui_style, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			style: new Cui_style.Dstyle(init['style'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Taccent = {
};
export class Caccent extends AlanNode {
	constructor(init:Taccent, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'accent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbackground = {
};
export class Cbackground extends AlanNode {
	constructor(init:Tbackground, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'background'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbrand = {
};
export class Cbrand extends AlanNode {
	constructor(init:Tbrand, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'brand'`; }
	public get entity() { return this.parent.entity; }
}
export type Terror = {
};
export class Cerror extends AlanNode {
	constructor(init:Terror, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'error'`; }
	public get entity() { return this.parent.entity; }
}
export type Tforeground = {
};
export class Cforeground extends AlanNode {
	constructor(init:Tforeground, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'foreground'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlink = {
};
export class Clink extends AlanNode {
	constructor(init:Tlink, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'link'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess = {
};
export class Csuccess extends AlanNode {
	constructor(init:Tsuccess, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Twarning = {
};
export class Cwarning extends AlanNode {
	constructor(init:Twarning, public parent:Cui_style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'warning'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_state_group_property = {
	'classification':Tui_property_classification;
	'default state':'no'|['no', {}]|['yes', Tyes__default_state];
	'emphasis':'no'|['no', {}]|'yes'|['yes', {}];
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_state_group_property];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_state_group_property];
};
export class Cui_state_group_property extends AlanNode {
	public readonly properties:{
		readonly classification:Cui_property_classification,
		readonly default_state:Cui_state_group_property.Ddefault_state,
		readonly emphasis:Cui_state_group_property.Demphasis,
		readonly has_description:Cui_state_group_property.Dhas_description,
		readonly icon:Cui_state_group_property.Dicon
	};
	constructor(init:Tui_state_group_property, public location:AlanNode, public input: {
		state_group_property: () => application.Cstate_group
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			classification: new Cui_state_group_property.Dclassification(init['classification'], $this),
			default_state: new Cui_state_group_property.Ddefault_state(init['default state'], $this),
			emphasis: new Cui_state_group_property.Demphasis(init['emphasis'], $this),
			has_description: new Cui_state_group_property.Dhas_description(init['has description'], $this),
			icon: new Cui_state_group_property.Dicon(init['icon'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__default_state = {
};
export class Cno__default_state extends AlanNode {
	constructor(init:Tno__default_state, public parent:Cui_state_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default state'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default_state = {
	'default':Tui_scalar_default;
};
export class Cyes__default_state extends AlanNode {
	public readonly properties:{
		readonly default_:Cui_scalar_default
	};
	public readonly inferences:{
		base_value: () => application.Cbase__type__state_group
	} = {
		base_value: cache((detach:boolean) => {
			const application__ui_state_group_property__default_state__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.state_group_property())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__state_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__default_state, public parent:Cui_state_group_property) {
		super();
		const $this = this;
		this.properties = {
			default_: new Cyes__default_state.Ddefault_(init['default'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default state'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__emphasis__ui_state_group_property = {
};
export class Cno__emphasis__ui_state_group_property extends AlanNode {
	constructor(init:Tno__emphasis__ui_state_group_property, public parent:Cui_state_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'emphasis'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__emphasis__ui_state_group_property = {
};
export class Cyes__emphasis__ui_state_group_property extends AlanNode {
	constructor(init:Tyes__emphasis__ui_state_group_property, public parent:Cui_state_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'emphasis'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_state_group_property = {
};
export class Cno__has_description__ui_state_group_property extends AlanNode {
	constructor(init:Tno__has_description__ui_state_group_property, public parent:Cui_state_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_state_group_property = {
	'description':string;
};
export class Cyes__has_description__ui_state_group_property extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_state_group_property, public parent:Cui_state_group_property) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_state_group_property = {
};
export class Cno__icon__ui_state_group_property extends AlanNode {
	constructor(init:Tno__icon__ui_state_group_property, public parent:Cui_state_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_state_group_property = {
	'name':string;
};
export class Cyes__icon__ui_state_group_property extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_state_group_property, public parent:Cui_state_group_property) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vui_state_choice = { name: 'state node choice', definition: application.Cstate_group}
export class Cui_state_choice extends AlanStruct {
	constructor(
		public readonly variant:Vui_state_choice) { super(); }
	public definitions:{
		ui_property_value: Cui_property_value;
	} = {
		ui_property_value: new Cui_property_value({name:'state choice', definition: this})
	}
	public cast<K extends Vui_state_choice['name']>(_variant:K):Extract<Vui_state_choice, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_state_choice['name']]:(($:Extract<Vui_state_choice, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui state choice'`; }
	public is(other:Cui_state_choice):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tui_state = {
	'desired state':'no'|['no', {}]|'yes'|['yes', {}];
	'has style':'no'|['no', {}]|['yes', Tyes__has_style__ui_state];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_state];
	'transitions':Record<string, Ttransitions>;
	'verified state':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cui_state extends AlanNode {
	public readonly properties:{
		readonly desired_state:Cui_state.Ddesired_state,
		readonly has_style:Cui_state.Dhas_style,
		readonly icon:Cui_state.Dicon,
		readonly transitions:Cui_state.Dtransitions,
		readonly verified_state:Cui_state.Dverified_state
	};
	constructor(init:Tui_state, public location:AlanNode, public input: {
		state: () => application.Cstates__state_group
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			desired_state: new Cui_state.Ddesired_state(init['desired state'], $this),
			has_style: new Cui_state.Dhas_style(init['has style'], $this),
			icon: new Cui_state.Dicon(init['icon'], $this),
			transitions: new Cui_state.Dtransitions(init['transitions'], $this),
			verified_state: new Cui_state.Dverified_state(init['verified state'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__desired_state = {
};
export class Cno__desired_state extends AlanNode {
	constructor(init:Tno__desired_state, public parent:Cui_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'desired state'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__desired_state = {
};
export class Cyes__desired_state extends AlanNode {
	constructor(init:Tyes__desired_state, public parent:Cui_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'desired state'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_style__ui_state = {
};
export class Cno__has_style__ui_state extends AlanNode {
	constructor(init:Tno__has_style__ui_state, public parent:Cui_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has style'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_style__ui_state = {
	'style expression':Tui_expression;
};
export class Cyes__has_style__ui_state extends AlanNode {
	public readonly properties:{
		readonly style_expression:Cui_expression
	};
	constructor(init:Tyes__has_style__ui_state, public parent:Cui_state) {
		super();
		const $this = this;
		this.properties = {
			style_expression: new Cyes__has_style__ui_state.Dstyle_expression(init['style expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has style'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_state = {
};
export class Cno__icon__ui_state extends AlanNode {
	constructor(init:Tno__icon__ui_state, public parent:Cui_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_state = {
	'name':string;
};
export class Cyes__icon__ui_state extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_state, public parent:Cui_state) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Ktransitions extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Ctransitions) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.state())
			.then(context => {
				const entry = context?.parent.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'transitions'::key`; }
}
export type Ttransitions = {
	'action':string;
};
export class Ctransitions extends AlanDictionaryEntry {
	public key:Ktransitions;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly action:Ctransitions.Daction
	};
	constructor(key:string, init:Ttransitions, public parent:Cui_state) {
		super();
		const $this = this;
		this.key = new Ktransitions(key, $this);
		this.properties = {
			action: new Ctransitions.Daction(init['action'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'transitions'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__verified_state = {
};
export class Cno__verified_state extends AlanNode {
	constructor(init:Tno__verified_state, public parent:Cui_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'verified state'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__verified_state = {
};
export class Cyes__verified_state extends AlanNode {
	constructor(init:Tyes__verified_state, public parent:Cui_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'verified state'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_scalar_value_expression_list = {
	'has tail':'no'|['no', {}]|['yes', Tyes__has_tail__ui_scalar_value_expression_list];
	'value':Tui_scalar_value_expression;
};
export class Cui_scalar_value_expression_list extends AlanNode {
	public readonly properties:{
		readonly has_tail:Cui_scalar_value_expression_list.Dhas_tail,
		readonly value:Cui_scalar_value_expression
	};
	constructor(init:Tui_scalar_value_expression_list, public location:AlanNode, public input: {
		target: () => application.Cui_scalar,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_tail: new Cui_scalar_value_expression_list.Dhas_tail(init['has tail'], $this),
			value: new Cui_scalar_value_expression_list.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_tail__ui_scalar_value_expression_list = {
};
export class Cno__has_tail__ui_scalar_value_expression_list extends AlanNode {
	constructor(init:Tno__has_tail__ui_scalar_value_expression_list, public parent:Cui_scalar_value_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_tail__ui_scalar_value_expression_list = {
	'tail':Tui_scalar_value_expression_list;
};
export class Cyes__has_tail__ui_scalar_value_expression_list extends AlanNode {
	public readonly properties:{
		readonly tail:Cui_scalar_value_expression_list
	};
	constructor(init:Tyes__has_tail__ui_scalar_value_expression_list, public parent:Cui_scalar_value_expression_list) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_tail__ui_scalar_value_expression_list.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_scalar_value_expression = {
	'type':['dynamic', Tdynamic__type__ui_scalar_value_expression]|['number', Tnumber__type__ui_scalar_value_expression]|['state', Tstate__type__ui_scalar_value_expression]|'sticky'|['sticky', {}]|['style', Tstyle__type]|['text', Ttext__type__ui_scalar_value_expression];
};
export class Cui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cui_scalar_value_expression.Dtype
	};
	constructor(init:Tui_scalar_value_expression, public location:AlanNode, public input: {
		target: () => application.Cui_scalar,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cui_scalar_value_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic__type__ui_scalar_value_expression = {
	'path':Tui_parametrized_node_path;
	'property':string;
};
export class Cdynamic__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly path:Cui_parametrized_node_path,
		readonly property:Cdynamic__type__ui_scalar_value_expression.Dproperty
	};
	public readonly inferences:{
		property_target: () => application.Cui_property_value
	} = {
		property_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__dynamic_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'property value' ? context.variant.definition as application.Cui_property_value : undefined).result!;
		})

	}
	constructor(init:Tdynamic__type__ui_scalar_value_expression, public parent:Cui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cdynamic__type__ui_scalar_value_expression.Dpath(init['path'], $this),
			property: new Cdynamic__type__ui_scalar_value_expression.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__ui_scalar_value_expression = {
	'type':['binary expression', Tbinary_expression__type__number__type__ui_scalar_value_expression]|['constant', Tconstant__type__number__type__ui_scalar_value_expression]|['list expression', Tlist_expression__type__number__type__ui_scalar_value_expression]|'now in seconds'|['now in seconds', {}]|['unary expression', Tunary_expression__type__number__type__ui_scalar_value_expression];
};
export class Cnumber__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber__type__ui_scalar_value_expression.Dtype
	};
	public readonly inferences:{
		number_target: () => application.Cui_number
	} = {
		number_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'property value' ? context.variant.definition as application.Cui_property_value : undefined)
				.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Cui_number : undefined).result!;
		})

	}
	constructor(init:Tnumber__type__ui_scalar_value_expression, public parent:Cui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber__type__ui_scalar_value_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinary_expression__type__number__type__ui_scalar_value_expression = {
	'expressions':Texpressions__binary_expression__type__number__type__ui_scalar_value_expression;
	'operation':['division', Tdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression]|'remainder'|['remainder', {}];
};
export class Cbinary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly expressions:Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression,
		readonly operation:Cbinary_expression__type__number__type__ui_scalar_value_expression.Doperation
	};
	constructor(init:Tbinary_expression__type__number__type__ui_scalar_value_expression, public parent:Cnumber__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			expressions: new Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression(init['expressions'], $this),
			operation: new Cbinary_expression__type__number__type__ui_scalar_value_expression.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binary expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpressions__binary_expression__type__number__type__ui_scalar_value_expression = {
	'left':Tui_scalar_value_expression;
	'right':Tui_scalar_value_expression;
};
export class Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly left:Cui_scalar_value_expression,
		readonly right:Cui_scalar_value_expression
	};
	constructor(init:Texpressions__binary_expression__type__number__type__ui_scalar_value_expression, public parent:Cbinary_expression__type__number__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			left: new Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression.Dleft(init['left'], $this),
			right: new Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression.Dright(init['right'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'expressions'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression = {
	'rounding':'ceil'|['ceil', {}]|'floor'|['floor', {}]|'ordinary'|['ordinary', {}];
};
export class Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly rounding:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression.Drounding
	};
	constructor(init:Tdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression, public parent:Cbinary_expression__type__number__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			rounding: new Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression.Drounding(init['rounding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'division'`; }
	public get entity() { return this.parent.entity; }
}
export type Tceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, public parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ceil'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, public parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'floor'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, public parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ordinary'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression, public parent:Cbinary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'remainder'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconstant__type__number__type__ui_scalar_value_expression = {
	'value':Tconstant_number_value;
};
export class Cconstant__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly value:Cconstant_number_value
	};
	constructor(init:Tconstant__type__number__type__ui_scalar_value_expression, public parent:Cnumber__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cconstant__type__number__type__ui_scalar_value_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'constant'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist_expression__type__number__type__ui_scalar_value_expression = {
	'operation':'maximum'|['maximum', {}]|'minimum'|['minimum', {}]|'product'|['product', {}]|'sum'|['sum', {}];
	'value':Tui_scalar_value_expression_list;
};
export class Clist_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly operation:Clist_expression__type__number__type__ui_scalar_value_expression.Doperation,
		readonly value:Cui_scalar_value_expression_list
	};
	constructor(init:Tlist_expression__type__number__type__ui_scalar_value_expression, public parent:Cnumber__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			operation: new Clist_expression__type__number__type__ui_scalar_value_expression.Doperation(init['operation'], $this),
			value: new Clist_expression__type__number__type__ui_scalar_value_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'list expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression, public parent:Clist_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'maximum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminimum__operation__list_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cminimum__operation__list_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tminimum__operation__list_expression__type__number__type__ui_scalar_value_expression, public parent:Clist_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'minimum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduct__operation__list_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cproduct__operation__list_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tproduct__operation__list_expression__type__number__type__ui_scalar_value_expression, public parent:Clist_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'product'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsum__operation__list_expression__type__number__type__ui_scalar_value_expression = {
};
export class Csum__operation__list_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tsum__operation__list_expression__type__number__type__ui_scalar_value_expression, public parent:Clist_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'sum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnow_in_seconds__type__number__type__ui_scalar_value_expression = {
};
export class Cnow_in_seconds__type__number__type__ui_scalar_value_expression extends AlanNode {
	constructor(init:Tnow_in_seconds__type__number__type__ui_scalar_value_expression, public parent:Cnumber__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'now in seconds'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunary_expression__type__number__type__ui_scalar_value_expression = {
	'expression':Tui_scalar_value_expression;
	'type':'absolute value'|['absolute value', {}]|['numerical type conversion', Tnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression]|'sign inversion'|['sign inversion', {}];
};
export class Cunary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_scalar_value_expression,
		readonly type:Cunary_expression__type__number__type__ui_scalar_value_expression.Dtype
	};
	constructor(init:Tunary_expression__type__number__type__ui_scalar_value_expression, public parent:Cnumber__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cunary_expression__type__number__type__ui_scalar_value_expression.Dexpression(init['expression'], $this),
			type: new Cunary_expression__type__number__type__ui_scalar_value_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'unary expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression = {
};
export class Cabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.number_target())
			.then(context => context?.component_root.output.numerical_type()).result!)
	}
	constructor(init:Tabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression, public parent:Cunary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'absolute value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression = {
	'conversion':string;
	'representation based':'no'|['no', {}]|'ui date and time'|['ui date and time', {}];
};
export class Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly conversion:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression.Dconversion,
		readonly representation_based:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression.Drepresentation_based
	};
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.conversion?.ref)
			.then(context => context?.key?.ref).result!)
	}
	constructor(init:Tnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression, public parent:Cunary_expression__type__number__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			conversion: new Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression.Dconversion(init['conversion'], $this),
			representation_based: new Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression.Drepresentation_based(init['representation based'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'numerical type conversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__representation_based = {
};
export class Cno__representation_based extends AlanNode {
	constructor(init:Tno__representation_based, public parent:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation based'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_date_and_time = {
};
export class Cui_date_and_time extends AlanNode {
	public readonly inferences:{
		ui_date_and_time_source: () => application.Cdate_and_time,
		ui_date_target: () => application.Cdate
	} = {
		ui_date_and_time_source: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__number__type__unary_expression__type__numerical_type_conversion__representation_based__ui_date_and_time_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.conversion?.ref)
				.then(context => context?.key?.ref)
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.represent_as.state.name === 'date and time') {
						return resolve(context.properties.represent_as.state.node as application.Cdate_and_time).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		ui_date_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__number__type__unary_expression__type__numerical_type_conversion__representation_based__ui_date_and_time_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.numerical_type())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.represent_as.state.name === 'date') {
						return resolve(context.properties.represent_as.state.node as application.Cdate).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tui_date_and_time, public parent:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation based'?'ui date and time'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression = {
};
export class Csign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression extends AlanNode {
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.number_target())
			.then(context => context?.component_root.output.numerical_type()).result!)
	}
	constructor(init:Tsign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression, public parent:Cunary_expression__type__number__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sign inversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__ui_scalar_value_expression = {
	'state':string;
};
export class Cstate__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__ui_scalar_value_expression.Dstate
	};
	public readonly inferences:{
		state_choice_target: () => application.Cui_state_choice
	} = {
		state_choice_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'property value' ? context.variant.definition as application.Cui_property_value : undefined)
				.then(context => context?.variant.name === 'state choice' ? context.variant.definition as application.Cui_state_choice : undefined).result!;
		})

	}
	constructor(init:Tstate__type__ui_scalar_value_expression, public parent:Cui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__ui_scalar_value_expression.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsticky = {
};
export class Csticky extends AlanNode {
	constructor(init:Tsticky, public parent:Cui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sticky'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstyle__type = {
	'type':['fixed', Tfixed]|['property', Tproperty__type__style];
};
export class Cstyle__type extends AlanNode {
	public readonly properties:{
		readonly type:Cstyle__type.Dtype
	};
	public readonly inferences:{
		style_target: () => application.Cstyle__ui_scalar
	} = {
		style_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__style_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'style' ? context.variant.definition as application.Cstyle__ui_scalar : undefined).result!;
		})

	}
	constructor(init:Tstyle__type, public parent:Cui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Cstyle__type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'style'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfixed = {
	'style':Tui_style;
};
export class Cfixed extends AlanNode {
	public readonly properties:{
		readonly style:Cui_style
	};
	constructor(init:Tfixed, public parent:Cstyle__type) {
		super();
		const $this = this;
		this.properties = {
			style: new Cfixed.Dstyle(init['style'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'fixed'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__style = {
	'expression':Tui_scalar_value_expression;
};
export class Cproperty__type__style extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_scalar_value_expression
	};
	constructor(init:Tproperty__type__style, public parent:Cstyle__type) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cproperty__type__style.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__ui_scalar_value_expression = {
	'type':['auto increment', Tauto_increment]|['concatenation', Tconcatenation__type__text__type__ui_scalar_value_expression]|'guid'|['guid', {}]|['number to text', Tnumber_to_text]|['static', Tstatic__type__text__type__ui_scalar_value_expression];
};
export class Ctext__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly type:Ctext__type__ui_scalar_value_expression.Dtype
	};
	public readonly inferences:{
		text_target: () => application.Cui_text
	} = {
		text_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'property value' ? context.variant.definition as application.Cui_property_value : undefined)
				.then(context => context?.variant.name === 'text' ? context.variant.definition as application.Cui_text : undefined).result!;
		})

	}
	constructor(init:Ttext__type__ui_scalar_value_expression, public parent:Cui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext__type__ui_scalar_value_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tauto_increment = {
	'scope':Tancestor_node_path;
};
export class Cauto_increment extends AlanNode {
	public readonly properties:{
		readonly scope:Cancestor_node_path & { readonly inferences: {
			root_location: () => application.Croot_location;
			valid_increment_scope: () => application.Ccollection__type__property;
		} }
	};
	public readonly inferences:{
		text_property_target: () => application.Ctext__type__property
	} = {
		text_property_target: cache((detach:boolean) => {
			const application__ui_scalar_value_expression__type__text__type__auto_increment_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text_target())
				.then(context => context?.variant.name === 'text property' ? context.variant.definition as application.Ctext__type__property : undefined).result!;
		})

	}
	constructor(init:Tauto_increment, public parent:Ctext__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			scope: new Cauto_increment.Dscope(init['scope'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'auto increment'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconcatenation__type__text__type__ui_scalar_value_expression = {
	'list':Tui_scalar_value_expression_list;
};
export class Cconcatenation__type__text__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly list:Cui_scalar_value_expression_list
	};
	constructor(init:Tconcatenation__type__text__type__ui_scalar_value_expression, public parent:Ctext__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			list: new Cconcatenation__type__text__type__ui_scalar_value_expression.Dlist(init['list'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'concatenation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tguid = {
};
export class Cguid extends AlanNode {
	constructor(init:Tguid, public parent:Ctext__type__ui_scalar_value_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'guid'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_to_text = {
	'expression':Tui_scalar_value_expression;
	'numerical type':string;
	'pad':'no'|['no', {}]|['yes', Tyes__pad];
};
export class Cnumber_to_text extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_scalar_value_expression,
		readonly numerical_type:Cnumber_to_text.Dnumerical_type,
		readonly pad:Cnumber_to_text.Dpad
	};
	constructor(init:Tnumber_to_text, public parent:Ctext__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber_to_text.Dexpression(init['expression'], $this),
			numerical_type: new Cnumber_to_text.Dnumerical_type(init['numerical type'], $this),
			pad: new Cnumber_to_text.Dpad(init['pad'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number to text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__pad = {
};
export class Cno__pad extends AlanNode {
	constructor(init:Tno__pad, public parent:Cnumber_to_text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'pad'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__pad = {
	'character':string;
	'size':number;
};
export class Cyes__pad extends AlanNode {
	public readonly properties:{
		readonly character:string,
		readonly size:Cyes__pad.Dsize
	};
	constructor(init:Tyes__pad, public parent:Cnumber_to_text) {
		super();
		const $this = this;
		this.properties = {
			character: init['character'],
			size: new Cyes__pad.Dsize(init['size'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'pad'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__text__type__ui_scalar_value_expression = {
	'value':string;
};
export class Cstatic__type__text__type__ui_scalar_value_expression extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tstatic__type__text__type__ui_scalar_value_expression, public parent:Ctext__type__ui_scalar_value_expression) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_scalar_default = {
	'expression':Tui_expression;
	'has fallback':'no'|['no', {}]|['yes', Tyes__has_fallback];
};
export class Cui_scalar_default extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_expression,
		readonly has_fallback:Cui_scalar_default.Dhas_fallback
	};
	constructor(init:Tui_scalar_default, public location:AlanNode, public input: {
		target: () => application.Cui_scalar,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cui_scalar_default.Dexpression(init['expression'], $this),
			has_fallback: new Cui_scalar_default.Dhas_fallback(init['has fallback'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_fallback = {
};
export class Cno__has_fallback extends AlanNode {
	constructor(init:Tno__has_fallback, public parent:Cui_scalar_default) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has fallback'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_fallback = {
	'fallback':Tui_scalar_default;
};
export class Cyes__has_fallback extends AlanNode {
	public readonly properties:{
		readonly fallback:Cui_scalar_default
	};
	constructor(init:Tyes__has_fallback, public parent:Cui_scalar_default) {
		super();
		const $this = this;
		this.properties = {
			fallback: new Cyes__has_fallback.Dfallback(init['fallback'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has fallback'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_reference_set_attribute = {
	'break out':'no'|['no', {}]|'yes'|['yes', {}];
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_reference_set_attribute];
	'visible':'false'|['false', {}]|'true'|['true', {}];
};
export class Cui_reference_set_attribute extends AlanNode {
	public readonly properties:{
		readonly break_out:Cui_reference_set_attribute.Dbreak_out,
		readonly has_description:Cui_reference_set_attribute.Dhas_description,
		readonly visible:Cui_reference_set_attribute.Dvisible
	};
	constructor(init:Tui_reference_set_attribute, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			break_out: new Cui_reference_set_attribute.Dbreak_out(init['break out'], $this),
			has_description: new Cui_reference_set_attribute.Dhas_description(init['has description'], $this),
			visible: new Cui_reference_set_attribute.Dvisible(init['visible'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__break_out__ui_reference_set_attribute = {
};
export class Cno__break_out__ui_reference_set_attribute extends AlanNode {
	constructor(init:Tno__break_out__ui_reference_set_attribute, public parent:Cui_reference_set_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'break out'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__break_out__ui_reference_set_attribute = {
};
export class Cyes__break_out__ui_reference_set_attribute extends AlanNode {
	constructor(init:Tyes__break_out__ui_reference_set_attribute, public parent:Cui_reference_set_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'break out'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_reference_set_attribute = {
};
export class Cno__has_description__ui_reference_set_attribute extends AlanNode {
	constructor(init:Tno__has_description__ui_reference_set_attribute, public parent:Cui_reference_set_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_reference_set_attribute = {
	'description':string;
};
export class Cyes__has_description__ui_reference_set_attribute extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_reference_set_attribute, public parent:Cui_reference_set_attribute) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfalse__visible__ui_reference_set_attribute = {
};
export class Cfalse__visible__ui_reference_set_attribute extends AlanNode {
	constructor(init:Tfalse__visible__ui_reference_set_attribute, public parent:Cui_reference_set_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'visible'?'false'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttrue__visible__ui_reference_set_attribute = {
};
export class Ctrue__visible__ui_reference_set_attribute extends AlanNode {
	constructor(init:Ttrue__visible__ui_reference_set_attribute, public parent:Cui_reference_set_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'visible'?'true'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_reference_rule = {
	'has validation':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cui_reference_rule extends AlanNode {
	public readonly properties:{
		readonly has_validation:Cui_reference_rule.Dhas_validation
	};
	constructor(init:Tui_reference_rule, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_validation: new Cui_reference_rule.Dhas_validation(init['has validation'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_validation__ui_reference_rule = {
};
export class Cno__has_validation__ui_reference_rule extends AlanNode {
	constructor(init:Tno__has_validation__ui_reference_rule, public parent:Cui_reference_rule) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has validation'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_validation__ui_reference_rule = {
};
export class Cyes__has_validation__ui_reference_rule extends AlanNode {
	constructor(init:Tyes__has_validation__ui_reference_rule, public parent:Cui_reference_rule) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has validation'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__ui_property_target_derivation(detach:boolean, application__ui_property_target_derivation_nval:application.Cproperty__type__attributes, input: {
	operation: () => application.Cui_properties_operation
}):application.Cui_target {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__ui_property_target_derivation_nval)
		.then(context => context?.properties.type)
		.then(context => {
			const state_group = context;
			switch (state_group?.state.name) {
				case 'collection': {
					const application__ui_property_target_derivation___collection_nval = state_group.cast('collection');
					return resolve(context)
						.then(context => application__ui_property_target_derivation___collection_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cui_target_collection({name: 'collection property', definition: conv_context},{
							entry_object: () => resolve(self)
								.then(context => application__ui_property_target_derivation___collection_nval)
								.then(context => context?.properties.node)
								.then(context => {
									const conv_context = resolve(context).result!;
									return new Cui_object({name: 'node', definition: conv_context},{
									operation: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.operation()).result!

									});
								}).result!
							, 
							value_source: () => resolve(self)
								.then(context => application__ui_property_target_derivation___collection_nval)
								.then(context => context?.properties.type?.state.node.output.value_source()).result!

							});
						})
						.then(context => context?.definitions.ui_target).result;
				}
				case 'file': {
					return resolve(context)
						.then(() => self)
						.then(() => application.Cui_target.Punsupported_file).result;
				}
				case 'group': {
					const application__ui_property_target_derivation___group_nval = state_group.cast('group');
					return resolve(context)
						.then(context => application__ui_property_target_derivation___group_nval)
						.then(context => context?.properties.node)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cui_object({name: 'node', definition: conv_context},{
							operation: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.operation()).result!

							});
						})
						.then(context => context?.definitions.ui_target).result;
				}
				case 'number': {
					const application__ui_property_target_derivation___number_nval = state_group.cast('number');
					return resolve(context)
						.then(context => application__ui_property_target_derivation___number_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cui_number({name: 'number property', definition: conv_context},{
							numerical_type: () => resolve(self)
								.then(context => application__ui_property_target_derivation___number_nval)
								.then(context => context?.properties.numerical_type?.ref).result!
							, 
							set_type: () => resolve(self)
								.then(context => application__ui_property_target_derivation___number_nval)
								.then(context => context?.properties.set_type?.state.node.output.set_type()).result!

							});
						})
						.then(context => context?.definitions.ui_property_value)
						.then(context => context?.definitions.ui_scalar)
						.then(context => context?.definitions.ui_target).result;
				}
				case 'state group': {
					const application__ui_property_target_derivation___state_group_nval = state_group.cast('state group');
					return resolve(context)
						.then(context => application__ui_property_target_derivation___state_group_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cui_target_stategroup({name: 'stategroup property', definition: conv_context});
						})
						.then(context => context?.definitions.ui_target).result;
				}
				case 'text': {
					const application__ui_property_target_derivation___text_nval = state_group.cast('text');
					return resolve(context)
						.then(context => application__ui_property_target_derivation___text_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cui_text({name: 'text property', definition: conv_context});
						})
						.then(context => context?.definitions.ui_property_value)
						.then(context => context?.definitions.ui_scalar)
						.then(context => context?.definitions.ui_target).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
			};}).result!
}
export type Tui_property_classification = {
	'classification':'hidden'|['hidden', {}]|'identifying'|['identifying', {}]|'outlining'|['outlining', {}]|'standard'|['standard', {}];
};
export class Cui_property_classification extends AlanNode {
	public readonly properties:{
		readonly classification:Cui_property_classification.Dclassification
	};
	constructor(init:Tui_property_classification, public location:AlanNode, public input: {
		property: () => application.Cproperty__type__attributes
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			classification: new Cui_property_classification.Dclassification(init['classification'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Thidden = {
};
export class Chidden extends AlanNode {
	public readonly inferences:{
		derived_value: () => application.Cderived_value_source
	} = {
		derived_value: cache((detach:boolean) => {
			const application__ui_property_classification__classification__hidden_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.property())
				.then(context => context?.properties.type?.state.node.output.value_source())
				.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined).result!;
		})

	}
	constructor(init:Thidden, public parent:Cui_property_classification) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'classification'?'hidden'`; }
	public get entity() { return this.parent.entity; }
}
export type Tidentifying = {
};
export class Cidentifying extends AlanNode {
	constructor(init:Tidentifying, public parent:Cui_property_classification) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'classification'?'identifying'`; }
	public get entity() { return this.parent.entity; }
}
export type Toutlining = {
};
export class Coutlining extends AlanNode {
	constructor(init:Toutlining, public parent:Cui_property_classification) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'classification'?'outlining'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstandard = {
};
export class Cstandard extends AlanNode {
	constructor(init:Tstandard, public parent:Cui_property_classification) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'classification'?'standard'`; }
	public get entity() { return this.parent.entity; }
}
type Vui_properties_operation = { name: 'update', definition: (typeof Cui_properties_operation.Pupdate)}|{ name: 'initialization', definition: (typeof Cui_properties_operation.Pinitialization)}
export class Cui_properties_operation extends AlanStruct {
	public static Pupdate:Cui_properties_operation = new class PrimitiveInstance extends Cui_properties_operation {
		constructor () {
			super({name: 'update', definition: undefined as unknown as Cui_properties_operation})
			this.variant.definition = this;
		}
	}
	public static Pinitialization:Cui_properties_operation = new class PrimitiveInstance extends Cui_properties_operation {
		constructor () {
			super({name: 'initialization', definition: undefined as unknown as Cui_properties_operation})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vui_properties_operation) { super(); }
	public cast<K extends Vui_properties_operation['name']>(_variant:K):Extract<Vui_properties_operation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_properties_operation['name']]:(($:Extract<Vui_properties_operation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui properties operation'`; }
	public is(other:Cui_properties_operation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cinitialization = application.Cui_properties_operation;
export import Cupdate__ui_properties_operation = application.Cui_properties_operation;
export type Tui_parametrized_node_path = {
	'path':Tconditional_node_path;
};
export class Cui_parametrized_node_path extends AlanNode {
	public readonly properties:{
		readonly path:Cconditional_node_path
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.node_type()).result!)
	};
	constructor(init:Tui_parametrized_node_path, public location:AlanNode, public input: {
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Cui_parametrized_node_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tui_object_expression = {
	'properties':Record<string, Tproperties__ui_object_expression>;
};
export class Cui_object_expression extends AlanNode {
	public readonly properties:{
		readonly properties:Cui_object_expression.Dproperties
	};
	constructor(init:Tui_object_expression, public location:AlanNode, public input: {
		target: () => application.Cui_object,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cui_object_expression.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__ui_object_expression extends Reference<application.Cproperty__type__attributes, string> {
	public readonly inferences:{
		target: () => application.Cui_target,
		user_value: () => application.Cbase_value_source
	}
	constructor(key:string, $this:Cproperties__ui_object_expression) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.target())
			.then(context => context?.component_root.output.node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as application.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
		this.inferences = {
			target: cache((detach:boolean) => {
				const application__ui_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => evaluate__ui_property_target_derivation(detach, 
						resolve(context)
							.then(context => application__ui_object_expression__properties_key_nval).result!, {
						operation: () => resolve($this.parent)
							.then(() => $this.parent)
							.then(context => context?.component_root.input.target())
							.then(context => context?.component_root.output.operation()).result!

						})).result!;
			})
			,
			user_value: cache((detach:boolean) => {
				const application__ui_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => evaluate__user_source_checker(detach, 
						resolve(context)
							.then(context => application__ui_object_expression__properties_key_nval).result!, )).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__ui_object_expression = {
	'default':Tui_expression;
};
export class Cproperties__ui_object_expression extends AlanDictionaryEntry {
	public key:Kproperties__ui_object_expression;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly default_:Cui_expression
	};
	constructor(key:string, init:Tproperties__ui_object_expression, public parent:Cui_object_expression) {
		super();
		const $this = this;
		this.key = new Kproperties__ui_object_expression(key, $this);
		this.properties = {
			default_: new Cproperties__ui_object_expression.Ddefault_(init['default'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
type Vui_object = { name: 'node', definition: application.Cnode__application}
export class Cui_object extends AlanStruct {
	constructor(
		public readonly variant:Vui_object, public input: {
			operation: () => application.Cui_properties_operation
		}) { super(); }
	public definitions:{
		ui_target: Cui_target;
	} = {
		ui_target: new Cui_target({name:'object', definition: this})
	}
	public readonly output:{
		node: () => application.Cnode__application;
		operation: () => application.Cui_properties_operation;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'node': {
						const application__ui_object_out___node___node_nval = context.cast('node');
						return resolve(context)
							.then(context => application__ui_object_out___node___node_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		operation: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.operation()).result!)
	};
	public cast<K extends Vui_object['name']>(_variant:K):Extract<Vui_object, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_object['name']]:(($:Extract<Vui_object, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui object'`; }
	public is(other:Cui_object):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tui_numerical_type = {
	'represent as':['custom type', Tcustom_type]|'date'|['date', {}]|'date and time'|['date and time', {}]|['duration', Tduration]|'model'|['model', {}];
};
export class Cui_numerical_type extends AlanNode {
	public readonly properties:{
		readonly represent_as:Cui_numerical_type.Drepresent_as
	};
	constructor(init:Tui_numerical_type, public location:AlanNode, public input: {
		numerical_type: () => application.Cnumerical_types
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			represent_as: new Cui_numerical_type.Drepresent_as(init['represent as'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tcustom_type = {
	'binding':['dynamic', Tdynamic__binding]|'root'|['root', {}];
	'properties':Tproperties__custom_type;
};
export class Ccustom_type extends AlanNode {
	public readonly properties:{
		readonly binding:Ccustom_type.Dbinding,
		readonly properties:Cproperties__custom_type
	};
	constructor(init:Tcustom_type, public parent:Cui_numerical_type) {
		super();
		const $this = this;
		this.properties = {
			binding: new Ccustom_type.Dbinding(init['binding'], $this),
			properties: new Cproperties__custom_type(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'represent as'?'custom type'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdynamic__binding = {
	'assignment':Tvariable_assignment;
	'path':Tnode_type_id;
};
export class Cdynamic__binding extends AlanNode {
	public readonly properties:{
		readonly assignment:Cvariable_assignment,
		readonly path:Cnode_type_id
	};
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assignment)
			.then(context => context?.component_root.output.variable()).result!)
	}
	constructor(init:Tdynamic__binding, public parent:Ccustom_type) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cdynamic__binding.Dassignment(init['assignment'], $this),
			path: new Cdynamic__binding.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot = {
};
export class Croot extends AlanNode {
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cvariable__application.Punassigned).result!)
	}
	constructor(init:Troot, public parent:Ccustom_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperties__custom_type = {
	'conversion':'none'|['none', {}]|['point translation', Tpoint_translation];
	'label':Tui_scalar_value_expression;
};
export class Cproperties__custom_type extends AlanNode {
	public readonly properties:{
		readonly conversion:Cproperties__custom_type.Dconversion,
		readonly label:Cui_scalar_value_expression
	};
	constructor(init:Tproperties__custom_type, public parent:Ccustom_type) {
		super();
		const $this = this;
		this.properties = {
			conversion: new Cproperties__custom_type.Dconversion(init['conversion'], $this),
			label: new Cproperties__custom_type.Dlabel(init['label'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__conversion = {
};
export class Cnone__conversion extends AlanNode {
	constructor(init:Tnone__conversion, public parent:Cproperties__custom_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'conversion'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpoint_translation = {
	'decimals':Tui_scalar_value_expression;
};
export class Cpoint_translation extends AlanNode {
	public readonly properties:{
		readonly decimals:Cui_scalar_value_expression
	};
	constructor(init:Tpoint_translation, public parent:Cproperties__custom_type) {
		super();
		const $this = this;
		this.properties = {
			decimals: new Cpoint_translation.Ddecimals(init['decimals'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'conversion'?'point translation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdate = {
};
export class Cdate extends AlanNode {
	public readonly inferences:{
		scale_type: () => application.Cscale
	} = {
		scale_type: cache((detach:boolean) => {
			const application__ui_numerical_type__represent_as__date_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'scale') {
						return resolve(context.properties.type.state.node as application.Cscale).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdate, public parent:Cui_numerical_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'represent as'?'date'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdate_and_time = {
};
export class Cdate_and_time extends AlanNode {
	public readonly inferences:{
		scale_type: () => application.Cscale
	} = {
		scale_type: cache((detach:boolean) => {
			const application__ui_numerical_type__represent_as__date_and_time_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'scale') {
						return resolve(context.properties.type.state.node as application.Cscale).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdate_and_time, public parent:Cui_numerical_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'represent as'?'date and time'`; }
	public get entity() { return this.parent.entity; }
}
export type Tduration = {
	'unit':'hours'|['hours', {}]|'minutes'|['minutes', {}]|'seconds'|['seconds', {}];
};
export class Cduration extends AlanNode {
	public readonly properties:{
		readonly unit:Cduration.Dunit
	};
	constructor(init:Tduration, public parent:Cui_numerical_type) {
		super();
		const $this = this;
		this.properties = {
			unit: new Cduration.Dunit(init['unit'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'represent as'?'duration'`; }
	public get entity() { return this.parent.entity; }
}
export type Thours = {
};
export class Chours extends AlanNode {
	constructor(init:Thours, public parent:Cduration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit'?'hours'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminutes = {
};
export class Cminutes extends AlanNode {
	constructor(init:Tminutes, public parent:Cduration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit'?'minutes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tseconds__unit = {
};
export class Cseconds__unit extends AlanNode {
	constructor(init:Tseconds__unit, public parent:Cduration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit'?'seconds'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmodel = {
};
export class Cmodel extends AlanNode {
	constructor(init:Tmodel, public parent:Cui_numerical_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'represent as'?'model'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_number_property = {
	'classification':Tui_property_classification;
	'default':'no'|['no', {}]|['yes', Tyes__default__ui_number_property];
	'dynamic numerical type':'no'|['no', {}]|['yes', Tyes__dynamic_numerical_type];
	'emphasis':'no'|['no', {}]|'yes'|['yes', {}];
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_number_property];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_number_property];
	'metadata':'no'|['no', {}]|'yes'|['yes', {}];
	'validation':Tvalidation;
};
export class Cui_number_property extends AlanNode {
	public readonly properties:{
		readonly classification:Cui_property_classification,
		readonly default_:Cui_number_property.Ddefault_,
		readonly dynamic_numerical_type:Cui_number_property.Ddynamic_numerical_type,
		readonly emphasis:Cui_number_property.Demphasis,
		readonly has_description:Cui_number_property.Dhas_description,
		readonly icon:Cui_number_property.Dicon,
		readonly metadata:Cui_number_property.Dmetadata,
		readonly validation:Cvalidation
	};
	constructor(init:Tui_number_property, public location:AlanNode, public input: {
		number_property: () => application.Cnumber__type__property
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			classification: new Cui_number_property.Dclassification(init['classification'], $this),
			default_: new Cui_number_property.Ddefault_(init['default'], $this),
			dynamic_numerical_type: new Cui_number_property.Ddynamic_numerical_type(init['dynamic numerical type'], $this),
			emphasis: new Cui_number_property.Demphasis(init['emphasis'], $this),
			has_description: new Cui_number_property.Dhas_description(init['has description'], $this),
			icon: new Cui_number_property.Dicon(init['icon'], $this),
			metadata: new Cui_number_property.Dmetadata(init['metadata'], $this),
			validation: new Cvalidation(init['validation'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__default__ui_number_property = {
};
export class Cno__default__ui_number_property extends AlanNode {
	constructor(init:Tno__default__ui_number_property, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default__ui_number_property = {
	'default':Tui_scalar_default;
};
export class Cyes__default__ui_number_property extends AlanNode {
	public readonly properties:{
		readonly default_:Cui_scalar_default
	};
	public readonly inferences:{
		base_value: () => application.Cbase__type__number
	} = {
		base_value: cache((detach:boolean) => {
			const application__ui_number_property__default__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.number_property())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__default__ui_number_property, public parent:Cui_number_property) {
		super();
		const $this = this;
		this.properties = {
			default_: new Cyes__default__ui_number_property.Ddefault_(init['default'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__dynamic_numerical_type = {
};
export class Cno__dynamic_numerical_type extends AlanNode {
	constructor(init:Tno__dynamic_numerical_type, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'dynamic numerical type'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__dynamic_numerical_type = {
	'binding path':Tnode_path;
};
export class Cyes__dynamic_numerical_type extends AlanNode {
	public readonly properties:{
		readonly binding_path:Cnode_path & { readonly inferences: {
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly inferences:{
		dynamic_numerical_type: () => application.Cdynamic__binding
	} = {
		dynamic_numerical_type: cache((detach:boolean) => {
			const application__ui_number_property__dynamic_numerical_type__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.number_property())
				.then(context => context?.properties.numerical_type?.ref)
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.represent_as.state.name === 'custom type') {
						return resolve(context.properties.represent_as.state.node as application.Ccustom_type)
						.then(context => {
							if (context?.properties.binding.state.name === 'dynamic') {
								return context.properties.binding.state.node as application.Cdynamic__binding;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__dynamic_numerical_type, public parent:Cui_number_property) {
		super();
		const $this = this;
		this.properties = {
			binding_path: new Cyes__dynamic_numerical_type.Dbinding_path(init['binding path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'dynamic numerical type'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__emphasis__ui_number_property = {
};
export class Cno__emphasis__ui_number_property extends AlanNode {
	constructor(init:Tno__emphasis__ui_number_property, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'emphasis'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__emphasis__ui_number_property = {
};
export class Cyes__emphasis__ui_number_property extends AlanNode {
	constructor(init:Tyes__emphasis__ui_number_property, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'emphasis'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_number_property = {
};
export class Cno__has_description__ui_number_property extends AlanNode {
	constructor(init:Tno__has_description__ui_number_property, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_number_property = {
	'description':string;
};
export class Cyes__has_description__ui_number_property extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_number_property, public parent:Cui_number_property) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_number_property = {
};
export class Cno__icon__ui_number_property extends AlanNode {
	constructor(init:Tno__icon__ui_number_property, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_number_property = {
	'name':string;
};
export class Cyes__icon__ui_number_property extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_number_property, public parent:Cui_number_property) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__metadata = {
};
export class Cno__metadata extends AlanNode {
	constructor(init:Tno__metadata, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'metadata'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__metadata = {
};
export class Cyes__metadata extends AlanNode {
	public readonly inferences:{
		has_default: () => application.Cyes__default__ui_number_property
	} = {
		has_default: cache((detach:boolean) => {
			const application__ui_number_property__metadata__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.default_.state.name === 'yes') {
						return resolve(context.properties.default_.state.node as application.Cyes__default__ui_number_property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__metadata, public parent:Cui_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'metadata'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalidation = {
	'has maximum':'no'|['no', {}]|['yes', Tyes__has_maximum];
	'has minimum':'no'|['no', {}]|['yes', Tyes__has_minimum];
};
export class Cvalidation extends AlanNode {
	public readonly properties:{
		readonly has_maximum:Cvalidation.Dhas_maximum,
		readonly has_minimum:Cvalidation.Dhas_minimum
	};
	constructor(init:Tvalidation, public parent:Cui_number_property) {
		super();
		const $this = this;
		this.properties = {
			has_maximum: new Cvalidation.Dhas_maximum(init['has maximum'], $this),
			has_minimum: new Cvalidation.Dhas_minimum(init['has minimum'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_maximum = {
};
export class Cno__has_maximum extends AlanNode {
	constructor(init:Tno__has_maximum, public parent:Cvalidation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has maximum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_maximum = {
	'maximum':Tui_expression;
};
export class Cyes__has_maximum extends AlanNode {
	public readonly properties:{
		readonly maximum:Cui_expression
	};
	public readonly inferences:{
		base_value: () => application.Cbase__type__number
	} = {
		base_value: cache((detach:boolean) => {
			const application__ui_number_property__validation__has_maximum__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.number_property())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_maximum, public parent:Cvalidation) {
		super();
		const $this = this;
		this.properties = {
			maximum: new Cyes__has_maximum.Dmaximum(init['maximum'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has maximum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_minimum = {
};
export class Cno__has_minimum extends AlanNode {
	constructor(init:Tno__has_minimum, public parent:Cvalidation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has minimum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_minimum = {
	'minimum':Tui_expression;
};
export class Cyes__has_minimum extends AlanNode {
	public readonly properties:{
		readonly minimum:Cui_expression
	};
	public readonly inferences:{
		base_value: () => application.Cbase__type__number
	} = {
		base_value: cache((detach:boolean) => {
			const application__ui_number_property__validation__has_minimum__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.number_property())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_minimum, public parent:Cvalidation) {
		super();
		const $this = this;
		this.properties = {
			minimum: new Cyes__has_minimum.Dminimum(init['minimum'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has minimum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vui_number = { name: 'intermediate result', definition: application.Capplication}|{ name: 'number property', definition: application.Cnumber__type__property}
export class Cui_number extends AlanStruct {
	constructor(
		public readonly variant:Vui_number, public input: {
			numerical_type: () => application.Cnumerical_types,
			set_type: () => application.Cnumber_set_type
		}) { super(); }
	public definitions:{
		ui_property_value: Cui_property_value;
	} = {
		ui_property_value: new Cui_property_value({name:'number', definition: this})
	}
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
		set_type: () => application.Cnumber_set_type;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.numerical_type()).result!),
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.set_type()).result!)
	};
	public cast<K extends Vui_number['name']>(_variant:K):Extract<Vui_number, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vui_number['name']]:(($:Extract<Vui_number, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'ui number'`; }
	public is(other:Cui_number):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tui_node_path = {
	'path':Tnode_path;
};
export class Cui_node_path extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.node_type()).result!)
	};
	constructor(init:Tui_node_path, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Cui_node_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tui_identifying_property_selection = {
	'has properties':'no'|['no', {}]|['yes', Tyes__has_properties];
	'properties':Record<string, Tproperties__ui_identifying_property_selection>;
};
export class Cui_identifying_property_selection extends AlanNode {
	public readonly properties:{
		readonly has_properties:Cui_identifying_property_selection.Dhas_properties,
		readonly properties:Cui_identifying_property_selection.Dproperties
	};
	constructor(init:Tui_identifying_property_selection, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_properties: new Cui_identifying_property_selection.Dhas_properties(init['has properties'], $this),
			properties: new Cui_identifying_property_selection.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_properties = {
};
export class Cno__has_properties extends AlanNode {
	constructor(init:Tno__has_properties, public parent:Cui_identifying_property_selection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has properties'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_properties = {
	'first':string;
};
export class Cyes__has_properties extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_properties.Dfirst
	};
	constructor(init:Tyes__has_properties, public parent:Cui_identifying_property_selection) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_properties.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has properties'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kproperties__ui_identifying_property_selection extends Reference<application.Cproperty__type__attributes, string> {
	constructor(key:string, $this:Cproperties__ui_identifying_property_selection) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.context_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as application.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__ui_identifying_property_selection = {
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__properties];
	'value type':['choice', Tchoice__value_type__properties]|['node', Tnode__value_type__properties]|'scalar'|['scalar', {}];
};
export class Cproperties__ui_identifying_property_selection extends AlanDictionaryEntry {
	public key:Kproperties__ui_identifying_property_selection;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly has_successor:Cproperties__ui_identifying_property_selection.Dhas_successor,
		readonly value_type:Cproperties__ui_identifying_property_selection.Dvalue_type
	};
	constructor(key:string, init:Tproperties__ui_identifying_property_selection, public parent:Cui_identifying_property_selection) {
		super();
		const $this = this;
		this.key = new Kproperties__ui_identifying_property_selection(key, $this);
		this.properties = {
			has_successor: new Cproperties__ui_identifying_property_selection.Dhas_successor(init['has successor'], $this),
			value_type: new Cproperties__ui_identifying_property_selection.Dvalue_type(init['value type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor__properties = {
};
export class Cno__has_successor__properties extends AlanNode {
	constructor(init:Tno__has_successor__properties, public parent:Cproperties__ui_identifying_property_selection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__properties = {
	'successor':string;
};
export class Cyes__has_successor__properties extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__properties.Dsuccessor
	};
	constructor(init:Tyes__has_successor__properties, public parent:Cproperties__ui_identifying_property_selection) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__properties.Dsuccessor(init['successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tchoice__value_type__properties = {
	'first state':string;
	'state key is identifying':'no'|['no', {}]|'yes'|['yes', {}];
	'states':Record<string, Tstates__choice>;
};
export class Cchoice__value_type__properties extends AlanNode {
	public readonly properties:{
		readonly first_state:Cchoice__value_type__properties.Dfirst_state,
		readonly state_key_is_identifying:Cchoice__value_type__properties.Dstate_key_is_identifying,
		readonly states:Cchoice__value_type__properties.Dstates
	};
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const application__ui_identifying_property_selection__properties__value_type__choice_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tchoice__value_type__properties, public parent:Cproperties__ui_identifying_property_selection) {
		super();
		const $this = this;
		this.properties = {
			first_state: new Cchoice__value_type__properties.Dfirst_state(init['first state'], $this),
			state_key_is_identifying: new Cchoice__value_type__properties.Dstate_key_is_identifying(init['state key is identifying'], $this),
			states: new Cchoice__value_type__properties.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'choice'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__state_key_is_identifying = {
};
export class Cno__state_key_is_identifying extends AlanNode {
	constructor(init:Tno__state_key_is_identifying, public parent:Cchoice__value_type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'state key is identifying'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__state_key_is_identifying = {
};
export class Cyes__state_key_is_identifying extends AlanNode {
	constructor(init:Tyes__state_key_is_identifying, public parent:Cchoice__value_type__properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'state key is identifying'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__choice extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__choice) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__choice = {
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__states__choice];
	'selection':Tui_identifying_property_selection;
};
export class Cstates__choice extends AlanDictionaryEntry {
	public key:Kstates__choice;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly has_successor:Cstates__choice.Dhas_successor,
		readonly selection:Cui_identifying_property_selection
	};
	constructor(key:string, init:Tstates__choice, public parent:Cchoice__value_type__properties) {
		super();
		const $this = this;
		this.key = new Kstates__choice(key, $this);
		this.properties = {
			has_successor: new Cstates__choice.Dhas_successor(init['has successor'], $this),
			selection: new Cstates__choice.Dselection(init['selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor__states__choice = {
};
export class Cno__has_successor__states__choice extends AlanNode {
	constructor(init:Tno__has_successor__states__choice, public parent:Cstates__choice) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__states__choice = {
	'successor':string;
};
export class Cyes__has_successor__states__choice extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__states__choice.Dsuccessor
	};
	constructor(init:Tyes__has_successor__states__choice, public parent:Cstates__choice) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__states__choice.Dsuccessor(init['successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__value_type__properties = {
	'selection':Tui_identifying_property_selection;
};
export class Cnode__value_type__properties extends AlanNode {
	public readonly properties:{
		readonly selection:Cui_identifying_property_selection
	};
	public readonly inferences:{
		group_property: () => application.Cgroup__type__property
	} = {
		group_property: cache((detach:boolean) => {
			const application__ui_identifying_property_selection__properties__value_type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode__value_type__properties, public parent:Cproperties__ui_identifying_property_selection) {
		super();
		const $this = this;
		this.properties = {
			selection: new Cnode__value_type__properties.Dselection(init['selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscalar__value_type = {
};
export class Cscalar__value_type extends AlanNode {
	public readonly inferences:{
		scalar_value: () => application.Cscalar_value,
		value_type: () => application.Cvalue_object
	} = {
		scalar_value: cache((detach:boolean) => {
			const application__ui_identifying_property_selection__properties__value_type__scalar_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.value_type())
				.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined).result!;
		})
		,
		value_type: cache((detach:boolean) => {
			const application__ui_identifying_property_selection__properties__value_type__scalar_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => context?.properties.type)
				.then(context => {
					const state_group = context;
					switch (state_group?.state.name) {
						case 'collection': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.type?.state.node.output.value()).result;
						}
						case 'file': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.type?.state.node.output.value()).result;
						}
						case 'group': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.type?.state.node.output.value()).result;
						}
						case 'number': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.type?.state.node.output.value()).result;
						}
						case 'state group': {
							const application__ui_identifying_property_selection__properties__value_type__scalar_inf___value_type___state_group_nval = state_group.cast('state group');
							return resolve(context)
								.then(context => application__ui_identifying_property_selection__properties__value_type__scalar_inf___value_type___state_group_nval)
								.then(context => context?.definitions.scalar_value)
								.then(context => context?.definitions.leaf_value)
								.then(context => context?.definitions.value_object).result;
						}
						case 'text': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.key?.ref)
								.then(context => context?.properties.type?.state.node.output.value()).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
					};}).result!;
		})

	}
	constructor(init:Tscalar__value_type, public parent:Cproperties__ui_identifying_property_selection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'scalar'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_group_property = {
	'break out':'no'|['no', {}]|'yes'|['yes', {}];
	'classification':Tui_property_classification;
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_group_property];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_group_property];
};
export class Cui_group_property extends AlanNode {
	public readonly properties:{
		readonly break_out:Cui_group_property.Dbreak_out,
		readonly classification:Cui_property_classification,
		readonly has_description:Cui_group_property.Dhas_description,
		readonly icon:Cui_group_property.Dicon
	};
	constructor(init:Tui_group_property, public location:AlanNode, public input: {
		group_property: () => application.Cgroup__type__property
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			break_out: new Cui_group_property.Dbreak_out(init['break out'], $this),
			classification: new Cui_group_property.Dclassification(init['classification'], $this),
			has_description: new Cui_group_property.Dhas_description(init['has description'], $this),
			icon: new Cui_group_property.Dicon(init['icon'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__break_out__ui_group_property = {
};
export class Cno__break_out__ui_group_property extends AlanNode {
	constructor(init:Tno__break_out__ui_group_property, public parent:Cui_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'break out'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__break_out__ui_group_property = {
};
export class Cyes__break_out__ui_group_property extends AlanNode {
	constructor(init:Tyes__break_out__ui_group_property, public parent:Cui_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'break out'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_group_property = {
};
export class Cno__has_description__ui_group_property extends AlanNode {
	constructor(init:Tno__has_description__ui_group_property, public parent:Cui_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_group_property = {
	'description':string;
};
export class Cyes__has_description__ui_group_property extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_group_property, public parent:Cui_group_property) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_group_property = {
};
export class Cno__icon__ui_group_property extends AlanNode {
	constructor(init:Tno__icon__ui_group_property, public parent:Cui_group_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_group_property = {
	'name':string;
};
export class Cyes__icon__ui_group_property extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_group_property, public parent:Cui_group_property) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_file_property = {
	'classification':Tui_property_classification;
	'file name expression':'no'|['no', {}]|['yes', Tyes__file_name_expression];
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_file_property];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_file_property];
};
export class Cui_file_property extends AlanNode {
	public readonly properties:{
		readonly classification:Cui_property_classification,
		readonly file_name_expression:Cui_file_property.Dfile_name_expression,
		readonly has_description:Cui_file_property.Dhas_description,
		readonly icon:Cui_file_property.Dicon
	};
	constructor(init:Tui_file_property, public location:AlanNode, public input: {
		file_property: () => application.Cfile
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			classification: new Cui_file_property.Dclassification(init['classification'], $this),
			file_name_expression: new Cui_file_property.Dfile_name_expression(init['file name expression'], $this),
			has_description: new Cui_file_property.Dhas_description(init['has description'], $this),
			icon: new Cui_file_property.Dicon(init['icon'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__file_name_expression = {
};
export class Cno__file_name_expression extends AlanNode {
	constructor(init:Tno__file_name_expression, public parent:Cui_file_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'file name expression'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__file_name_expression = {
	'file name expression':Tui_scalar_default;
};
export class Cyes__file_name_expression extends AlanNode {
	public readonly properties:{
		readonly file_name_expression:Cui_scalar_default
	};
	constructor(init:Tyes__file_name_expression, public parent:Cui_file_property) {
		super();
		const $this = this;
		this.properties = {
			file_name_expression: new Cyes__file_name_expression.Dfile_name_expression(init['file name expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'file name expression'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_file_property = {
};
export class Cno__has_description__ui_file_property extends AlanNode {
	constructor(init:Tno__has_description__ui_file_property, public parent:Cui_file_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_file_property = {
	'description':string;
};
export class Cyes__has_description__ui_file_property extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_file_property, public parent:Cui_file_property) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_file_property = {
};
export class Cno__icon__ui_file_property extends AlanNode {
	constructor(init:Tno__icon__ui_file_property, public parent:Cui_file_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_file_property = {
	'name':string;
};
export class Cyes__icon__ui_file_property extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_file_property, public parent:Cui_file_property) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_expression = {
	'operation':['entry', Tentry__operation__ui_expression]|['entry list', Tentry_list]|['execute operation', Texecute_operation__operation__ui_expression]|'ignore'|['ignore', {}]|['node switch', Tnode_switch__operation__ui_expression]|['produce value', Tproduce_value__operation__ui_expression]|['state switch', Tstate_switch__operation__ui_expression]|['update properties', Tupdate_properties__operation__ui_expression]|['walk', Twalk__operation__ui_expression];
};
export class Cui_expression extends AlanNode {
	public readonly properties:{
		readonly operation:Cui_expression.Doperation
	};
	constructor(init:Tui_expression, public location:AlanNode, public input: {
		target: () => application.Cui_target,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			operation: new Cui_expression.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tentry__operation__ui_expression = {
	'type':['create', Tcreate__type__entry__operation__ui_expression]|['delete', Tdelete__type__entry__operation__ui_expression];
};
export class Centry__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly type:Centry__operation__ui_expression.Dtype
	};
	public readonly inferences:{
		target_collection: () => application.Cui_target_collection
	} = {
		target_collection: cache((detach:boolean) => {
			const application__ui_expression__operation__entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Cui_target_collection : undefined).result!;
		})

	}
	constructor(init:Tentry__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Centry__operation__ui_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcreate__type__entry__operation__ui_expression = {
	'expression':Tui_object_expression;
	'interaction':Tui_action_interaction;
	'visualization':Tui_action_visualization;
};
export class Ccreate__type__entry__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_object_expression,
		readonly interaction:Cui_action_interaction,
		readonly visualization:Cui_action_visualization
	};
	constructor(init:Tcreate__type__entry__operation__ui_expression, public parent:Centry__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ccreate__type__entry__operation__ui_expression.Dexpression(init['expression'], $this),
			interaction: new Ccreate__type__entry__operation__ui_expression.Dinteraction(init['interaction'], $this),
			visualization: new Ccreate__type__entry__operation__ui_expression.Dvisualization(init['visualization'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'create'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdelete__type__entry__operation__ui_expression = {
	'interaction':Tui_action_interaction;
	'path':Tsingular_node_path;
};
export class Cdelete__type__entry__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly interaction:Cui_action_interaction,
		readonly path:Csingular_node_path & { readonly inferences: {
			collection_node_type: () => application.Cnode__application;
		} }
	};
	public readonly inferences:{
		collection_property: () => application.Ccollection__type__property,
		update: () => application.Cupdate__ui_properties_operation
	} = {
		collection_property: cache((detach:boolean) => {
			const application__ui_expression__operation__entry__type__delete_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_collection())
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		update: cache((detach:boolean) => {
			const application__ui_expression__operation__entry__type__delete_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_collection())
				.then(context => context?.component_root.output.entry_object())
				.then(context => context?.component_root.output.operation())
				.then(context => context?.variant.name === 'update' ? context.variant.definition as application.Cupdate__ui_properties_operation : undefined).result!;
		})

	}
	constructor(init:Tdelete__type__entry__operation__ui_expression, public parent:Centry__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			interaction: new Cdelete__type__entry__operation__ui_expression.Dinteraction(init['interaction'], $this),
			path: new Cdelete__type__entry__operation__ui_expression.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'delete'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_list = {
	'entries':Tui_entry_expression_list;
};
export class Centry_list extends AlanNode {
	public readonly properties:{
		readonly entries:Cui_entry_expression_list
	};
	public readonly inferences:{
		collection: () => application.Cui_target_collection
	} = {
		collection: cache((detach:boolean) => {
			const application__ui_expression__operation__entry_list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Cui_target_collection : undefined).result!;
		})

	}
	constructor(init:Tentry_list, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			entries: new Centry_list.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'entry list'`; }
	public get entity() { return this.parent.entity; }
}
export type Texecute_operation__operation__ui_expression = {
	'expression':Tui_object_expression;
	'interaction':Tui_action_interaction;
	'operation':string;
	'path':Tsingular_node_path;
};
export class Cexecute_operation__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_object_expression,
		readonly interaction:Cui_action_interaction,
		readonly operation:Cexecute_operation__operation__ui_expression.Doperation,
		readonly path:Csingular_node_path
	};
	public readonly inferences:{
		action_target: () => application.Cui_action
	} = {
		action_target: cache((detach:boolean) => {
			const application__ui_expression__operation__execute_operation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'action' ? context.variant.definition as application.Cui_action : undefined).result!;
		})

	}
	constructor(init:Texecute_operation__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cexecute_operation__operation__ui_expression.Dexpression(init['expression'], $this),
			interaction: new Cexecute_operation__operation__ui_expression.Dinteraction(init['interaction'], $this),
			operation: new Cexecute_operation__operation__ui_expression.Doperation(init['operation'], $this),
			path: new Cexecute_operation__operation__ui_expression.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'execute operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tignore__operation__ui_expression = {
};
export class Cignore__operation__ui_expression extends AlanNode {
	constructor(init:Tignore__operation__ui_expression, public parent:Cui_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'ignore'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_switch__operation__ui_expression = {
	'cases':Tcases__node_switch__operation__ui_expression;
	'expression':Tnode_expression;
};
export class Cnode_switch__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases__node_switch__operation__ui_expression,
		readonly expression:Cnode_expression
	};
	constructor(init:Tnode_switch__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases__node_switch__operation__ui_expression(init['cases'], $this),
			expression: new Cnode_switch__operation__ui_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'node switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases__node_switch__operation__ui_expression = {
	'node case':Tnode_case__cases__node_switch__operation__ui_expression;
	'none case':Tnone_case__cases__node_switch__operation__ui_expression;
};
export class Ccases__node_switch__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly node_case:Cnode_case__cases__node_switch__operation__ui_expression,
		readonly none_case:Cnone_case__cases__node_switch__operation__ui_expression
	};
	constructor(init:Tcases__node_switch__operation__ui_expression, public parent:Cnode_switch__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			node_case: new Cnode_case__cases__node_switch__operation__ui_expression(init['node case'], $this),
			none_case: new Cnone_case__cases__node_switch__operation__ui_expression(init['none case'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_case__cases__node_switch__operation__ui_expression = {
	'expression':Tui_expression;
	'variable assignment':Toptional_variable_assignment;
};
export class Cnode_case__cases__node_switch__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_expression,
		readonly variable_assignment:Coptional_variable_assignment
	};
	constructor(init:Tnode_case__cases__node_switch__operation__ui_expression, public parent:Ccases__node_switch__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnode_case__cases__node_switch__operation__ui_expression.Dexpression(init['expression'], $this),
			variable_assignment: new Cnode_case__cases__node_switch__operation__ui_expression.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'node case'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone_case__cases__node_switch__operation__ui_expression = {
	'expression':Tui_expression;
};
export class Cnone_case__cases__node_switch__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_expression
	};
	constructor(init:Tnone_case__cases__node_switch__operation__ui_expression, public parent:Ccases__node_switch__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnone_case__cases__node_switch__operation__ui_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'none case'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduce_value__operation__ui_expression = {
	'value':['object', Tobject__value__produce_value__operation__ui_expression]|['scalar', Tscalar__value__produce_value__operation__ui_expression]|['state', Tstate__value__produce_value__operation__ui_expression];
};
export class Cproduce_value__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly value:Cproduce_value__operation__ui_expression.Dvalue
	};
	constructor(init:Tproduce_value__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cproduce_value__operation__ui_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'produce value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tobject__value__produce_value__operation__ui_expression = {
	'expression':Tui_object_expression;
};
export class Cobject__value__produce_value__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_object_expression
	};
	public readonly inferences:{
		target_object: () => application.Cui_object
	} = {
		target_object: cache((detach:boolean) => {
			const application__ui_expression__operation__produce_value__value__object_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'object' ? context.variant.definition as application.Cui_object : undefined).result!;
		})

	}
	constructor(init:Tobject__value__produce_value__operation__ui_expression, public parent:Cproduce_value__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cobject__value__produce_value__operation__ui_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'object'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscalar__value__produce_value__operation__ui_expression = {
	'expression':Tui_scalar_value_expression;
};
export class Cscalar__value__produce_value__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_scalar_value_expression
	};
	public readonly inferences:{
		target_scalar: () => application.Cui_scalar
	} = {
		target_scalar: cache((detach:boolean) => {
			const application__ui_expression__operation__produce_value__value__scalar_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cui_scalar : undefined).result!;
		})

	}
	constructor(init:Tscalar__value__produce_value__operation__ui_expression, public parent:Cproduce_value__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cscalar__value__produce_value__operation__ui_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'scalar'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__value__produce_value__operation__ui_expression = {
	'expression':Tui_object_expression;
	'state':string;
};
export class Cstate__value__produce_value__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_object_expression,
		readonly state:Cstate__value__produce_value__operation__ui_expression.Dstate
	};
	public readonly inferences:{
		target_state_group: () => application.Cui_target_stategroup
	} = {
		target_state_group: cache((detach:boolean) => {
			const application__ui_expression__operation__produce_value__value__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'stategroup' ? context.variant.definition as application.Cui_target_stategroup : undefined).result!;
		})

	}
	constructor(init:Tstate__value__produce_value__operation__ui_expression, public parent:Cproduce_value__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cstate__value__produce_value__operation__ui_expression.Dexpression(init['expression'], $this),
			state: new Cstate__value__produce_value__operation__ui_expression.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_switch__operation__ui_expression = {
	'path':Tpath__state_switch__operation__ui_expression;
	'states':Record<string, Tstates__state_switch__operation__ui_expression>;
};
export class Cstate_switch__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly path:Cpath__state_switch__operation__ui_expression,
		readonly states:Cstate_switch__operation__ui_expression.Dstates
	};
	constructor(init:Tstate_switch__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cpath__state_switch__operation__ui_expression(init['path'], $this),
			states: new Cstate_switch__operation__ui_expression.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'state switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpath__state_switch__operation__ui_expression = {
	'path':Tui_parametrized_node_path;
	'stategroup':string;
};
export class Cpath__state_switch__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly path:Cui_parametrized_node_path,
		readonly stategroup:Cpath__state_switch__operation__ui_expression.Dstategroup
	};
	constructor(init:Tpath__state_switch__operation__ui_expression, public parent:Cstate_switch__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cpath__state_switch__operation__ui_expression.Dpath(init['path'], $this),
			stategroup: new Cpath__state_switch__operation__ui_expression.Dstategroup(init['stategroup'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'path'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_switch__operation__ui_expression extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_switch__operation__ui_expression) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.path)
			.then(context => context?.properties.stategroup?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_switch__operation__ui_expression = {
	'expression':Tui_expression;
	'variable assignment':Toptional_variable_assignment;
};
export class Cstates__state_switch__operation__ui_expression extends AlanDictionaryEntry {
	public key:Kstates__state_switch__operation__ui_expression;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly expression:Cui_expression,
		readonly variable_assignment:Coptional_variable_assignment
	};
	constructor(key:string, init:Tstates__state_switch__operation__ui_expression, public parent:Cstate_switch__operation__ui_expression) {
		super();
		const $this = this;
		this.key = new Kstates__state_switch__operation__ui_expression(key, $this);
		this.properties = {
			expression: new Cstates__state_switch__operation__ui_expression.Dexpression(init['expression'], $this),
			variable_assignment: new Cstates__state_switch__operation__ui_expression.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tupdate_properties__operation__ui_expression = {
	'interaction':Tui_action_interaction;
	'path':Tsingular_node_path;
	'target':['node', Tnode__target__update_properties__operation__ui_expression]|['property', Tproperty__target__update_properties__operation__ui_expression];
	'visualization':Tui_action_visualization;
};
export class Cupdate_properties__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly interaction:Cui_action_interaction,
		readonly path:Csingular_node_path & { readonly inferences: {
			user_value: () => application.Cuser__external_source;
		} },
		readonly target:Cupdate_properties__operation__ui_expression.Dtarget,
		readonly visualization:Cui_action_visualization
	};
	public readonly inferences:{
		action_target: () => application.Cui_action
	} = {
		action_target: cache((detach:boolean) => {
			const application__ui_expression__operation__update_properties_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'action' ? context.variant.definition as application.Cui_action : undefined).result!;
		})

	}
	constructor(init:Tupdate_properties__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			interaction: new Cupdate_properties__operation__ui_expression.Dinteraction(init['interaction'], $this),
			path: new Cupdate_properties__operation__ui_expression.Dpath(init['path'], $this),
			target: new Cupdate_properties__operation__ui_expression.Dtarget(init['target'], $this),
			visualization: new Cupdate_properties__operation__ui_expression.Dvisualization(init['visualization'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'update properties'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__target__update_properties__operation__ui_expression = {
	'expression':Tui_object_expression;
};
export class Cnode__target__update_properties__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_object_expression
	};
	public readonly inferences:{
		node: () => application.Cui_object
	} = {
		node: cache((detach:boolean) => {
			const application__ui_expression__operation__update_properties__target__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const conv_context = resolve(context).result!;
					return new Cui_object({name: 'node', definition: conv_context},{
					operation: () => resolve(this.parent)
						.then(() => this.parent)
						.then(() => application.Cui_properties_operation.Pupdate).result!

					});
				}).result!;
		})

	}
	constructor(init:Tnode__target__update_properties__operation__ui_expression, public parent:Cupdate_properties__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnode__target__update_properties__operation__ui_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'target'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__target__update_properties__operation__ui_expression = {
	'expression':Tui_expression;
	'property':string;
};
export class Cproperty__target__update_properties__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cui_expression,
		readonly property:Cproperty__target__update_properties__operation__ui_expression.Dproperty
	};
	constructor(init:Tproperty__target__update_properties__operation__ui_expression, public parent:Cupdate_properties__operation__ui_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cproperty__target__update_properties__operation__ui_expression.Dexpression(init['expression'], $this),
			property: new Cproperty__target__update_properties__operation__ui_expression.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'target'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Twalk__operation__ui_expression = {
	'collection':string;
	'expression':Tui_expression;
	'path':Tsingular_node_path;
	'tail':Tnode_path_tail;
	'variable assignment':Toptional_variable_assignment;
};
export class Cwalk__operation__ui_expression extends AlanNode {
	public readonly properties:{
		readonly collection:Cwalk__operation__ui_expression.Dcollection,
		readonly expression:Cui_expression,
		readonly path:Csingular_node_path,
		readonly tail:Cnode_path_tail,
		readonly variable_assignment:Coptional_variable_assignment
	};
	public readonly inferences:{
		context_supports_walk: () => application.Cyes__ui_walk_operation_support
	} = {
		context_supports_walk: cache((detach:boolean) => {
			const application__ui_expression__operation__walk_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.supports_walk_operation())
				.then(context => context?.variant.name === 'yes' ? context.variant.definition as application.Cyes__ui_walk_operation_support : undefined).result!;
		})

	}
	constructor(init:Twalk__operation__ui_expression, public parent:Cui_expression) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cwalk__operation__ui_expression.Dcollection(init['collection'], $this),
			expression: new Cwalk__operation__ui_expression.Dexpression(init['expression'], $this),
			path: new Cwalk__operation__ui_expression.Dpath(init['path'], $this),
			tail: new Cwalk__operation__ui_expression.Dtail(init['tail'], $this),
			variable_assignment: new Cwalk__operation__ui_expression.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'walk'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_entry_expression_list = {
	'entry expression':Tui_expression;
	'more entries':'no'|['no', {}]|['yes', Tyes__more_entries];
};
export class Cui_entry_expression_list extends AlanNode {
	public readonly properties:{
		readonly entry_expression:Cui_expression,
		readonly more_entries:Cui_entry_expression_list.Dmore_entries
	};
	constructor(init:Tui_entry_expression_list, public location:AlanNode, public input: {
		target: () => application.Cui_target_collection,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			entry_expression: new Cui_entry_expression_list.Dentry_expression(init['entry expression'], $this),
			more_entries: new Cui_entry_expression_list.Dmore_entries(init['more entries'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__more_entries = {
};
export class Cno__more_entries extends AlanNode {
	constructor(init:Tno__more_entries, public parent:Cui_entry_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'more entries'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__more_entries = {
	'tail':Tui_entry_expression_list;
};
export class Cyes__more_entries extends AlanNode {
	public readonly properties:{
		readonly tail:Cui_entry_expression_list
	};
	constructor(init:Tyes__more_entries, public parent:Cui_entry_expression_list) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__more_entries.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'more entries'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__ui_dynamic_state_choice_check(detach:boolean, application__ui_dynamic_state_choice_check_nval:application.Cui_state_choice, input: {
	selected_state_group: () => application.Cui_state_choice
}):application.Cui_state_choice {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => {
			const left = resolve(context)
				.then(() => self)
				.then(context => context?.component_root.input.selected_state_group()).result;
			const right = resolve(context)
				.then(context => application__ui_dynamic_state_choice_check_nval).result;
			return left?.is(right!) ? left : undefined
		}).result!
}
export type Tui_command_attribute = {
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_command_attribute];
	'visible':'false'|['false', {}]|'true'|['true', {}];
};
export class Cui_command_attribute extends AlanNode {
	public readonly properties:{
		readonly has_description:Cui_command_attribute.Dhas_description,
		readonly visible:Cui_command_attribute.Dvisible
	};
	constructor(init:Tui_command_attribute, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_description: new Cui_command_attribute.Dhas_description(init['has description'], $this),
			visible: new Cui_command_attribute.Dvisible(init['visible'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_description__ui_command_attribute = {
};
export class Cno__has_description__ui_command_attribute extends AlanNode {
	constructor(init:Tno__has_description__ui_command_attribute, public parent:Cui_command_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_command_attribute = {
	'description':string;
};
export class Cyes__has_description__ui_command_attribute extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_command_attribute, public parent:Cui_command_attribute) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfalse__visible__ui_command_attribute = {
};
export class Cfalse__visible__ui_command_attribute extends AlanNode {
	constructor(init:Tfalse__visible__ui_command_attribute, public parent:Cui_command_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'visible'?'false'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttrue__visible__ui_command_attribute = {
};
export class Ctrue__visible__ui_command_attribute extends AlanNode {
	constructor(init:Ttrue__visible__ui_command_attribute, public parent:Cui_command_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'visible'?'true'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_collection_property = {
	'break out':'no'|['no', {}]|'yes'|['yes', {}];
	'can be dormant':'no'|['no', {}]|['yes', Tyes__can_be_dormant];
	'classification':Tui_property_classification;
	'default':'no'|['no', {}]|['yes', Tyes__default__ui_collection_property];
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_collection_property];
	'has style':'no'|['no', {}]|['yes', Tyes__has_style__ui_collection_property];
	'icon':'no'|['no', {}]|['yes', Tyes__icon__ui_collection_property];
	'size':'large'|['large', {}]|'small'|['small', {}];
	'sort':'no'|['no', {}]|['yes', Tyes__sort];
};
export class Cui_collection_property extends AlanNode {
	public readonly properties:{
		readonly break_out:Cui_collection_property.Dbreak_out,
		readonly can_be_dormant:Cui_collection_property.Dcan_be_dormant,
		readonly classification:Cui_property_classification,
		readonly default_:Cui_collection_property.Ddefault_,
		readonly has_description:Cui_collection_property.Dhas_description,
		readonly has_style:Cui_collection_property.Dhas_style,
		readonly icon:Cui_collection_property.Dicon,
		readonly size:Cui_collection_property.Dsize,
		readonly sort:Cui_collection_property.Dsort
	};
	constructor(init:Tui_collection_property, public location:AlanNode, public input: {
		collection_property: () => application.Ccollection__type__property
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			break_out: new Cui_collection_property.Dbreak_out(init['break out'], $this),
			can_be_dormant: new Cui_collection_property.Dcan_be_dormant(init['can be dormant'], $this),
			classification: new Cui_collection_property.Dclassification(init['classification'], $this),
			default_: new Cui_collection_property.Ddefault_(init['default'], $this),
			has_description: new Cui_collection_property.Dhas_description(init['has description'], $this),
			has_style: new Cui_collection_property.Dhas_style(init['has style'], $this),
			icon: new Cui_collection_property.Dicon(init['icon'], $this),
			size: new Cui_collection_property.Dsize(init['size'], $this),
			sort: new Cui_collection_property.Dsort(init['sort'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__break_out__ui_collection_property = {
};
export class Cno__break_out__ui_collection_property extends AlanNode {
	constructor(init:Tno__break_out__ui_collection_property, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'break out'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__break_out__ui_collection_property = {
};
export class Cyes__break_out__ui_collection_property extends AlanNode {
	constructor(init:Tyes__break_out__ui_collection_property, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'break out'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_be_dormant = {
};
export class Cno__can_be_dormant extends AlanNode {
	constructor(init:Tno__can_be_dormant, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can be dormant'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_be_dormant = {
	'expression':Tnode_path_tail;
};
export class Cyes__can_be_dormant extends AlanNode {
	public readonly properties:{
		readonly expression:Cnode_path_tail & { readonly inferences: {
			state_node_type: () => application.Cstates__state_group;
		} }
	};
	constructor(init:Tyes__can_be_dormant, public parent:Cui_collection_property) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cyes__can_be_dormant.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can be dormant'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__default__ui_collection_property = {
};
export class Cno__default__ui_collection_property extends AlanNode {
	constructor(init:Tno__default__ui_collection_property, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default__ui_collection_property = {
	'entry filter':Tnode_path_tail;
	'key reference':Tentry_reference_selector;
};
export class Cyes__default__ui_collection_property extends AlanNode {
	public readonly properties:{
		readonly entry_filter:Cnode_path_tail,
		readonly key_reference:Centry_reference_selector & { readonly inferences: {
			key: () => application.Ctext__type__property;
		} }
	};
	constructor(init:Tyes__default__ui_collection_property, public parent:Cui_collection_property) {
		super();
		const $this = this;
		this.properties = {
			entry_filter: new Cyes__default__ui_collection_property.Dentry_filter(init['entry filter'], $this),
			key_reference: new Cyes__default__ui_collection_property.Dkey_reference(init['key reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_description__ui_collection_property = {
};
export class Cno__has_description__ui_collection_property extends AlanNode {
	constructor(init:Tno__has_description__ui_collection_property, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_collection_property = {
	'description':string;
};
export class Cyes__has_description__ui_collection_property extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_collection_property, public parent:Cui_collection_property) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_style__ui_collection_property = {
};
export class Cno__has_style__ui_collection_property extends AlanNode {
	constructor(init:Tno__has_style__ui_collection_property, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has style'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_style__ui_collection_property = {
	'style expression':Tui_expression;
};
export class Cyes__has_style__ui_collection_property extends AlanNode {
	public readonly properties:{
		readonly style_expression:Cui_expression
	};
	constructor(init:Tyes__has_style__ui_collection_property, public parent:Cui_collection_property) {
		super();
		const $this = this;
		this.properties = {
			style_expression: new Cyes__has_style__ui_collection_property.Dstyle_expression(init['style expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has style'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__icon__ui_collection_property = {
};
export class Cno__icon__ui_collection_property extends AlanNode {
	constructor(init:Tno__icon__ui_collection_property, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__icon__ui_collection_property = {
	'name':string;
};
export class Cyes__icon__ui_collection_property extends AlanNode {
	public readonly properties:{
		readonly name:string
	};
	constructor(init:Tyes__icon__ui_collection_property, public parent:Cui_collection_property) {
		super();
		const $this = this;
		this.properties = {
			name: init['name']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'icon'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlarge = {
};
export class Clarge extends AlanNode {
	constructor(init:Tlarge, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'size'?'large'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsmall = {
};
export class Csmall extends AlanNode {
	constructor(init:Tsmall, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'size'?'small'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__sort = {
};
export class Cno__sort extends AlanNode {
	constructor(init:Tno__sort, public parent:Cui_collection_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sort'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__sort = {
	'direction':'ascending'|['ascending', {}]|'descending'|['descending', {}];
	'path':Tui_node_path;
	'property':string;
};
export class Cyes__sort extends AlanNode {
	public readonly properties:{
		readonly direction:Cyes__sort.Ddirection,
		readonly path:Cui_node_path,
		readonly property:Cyes__sort.Dproperty
	};
	constructor(init:Tyes__sort, public parent:Cui_collection_property) {
		super();
		const $this = this;
		this.properties = {
			direction: new Cyes__sort.Ddirection(init['direction'], $this),
			path: new Cyes__sort.Dpath(init['path'], $this),
			property: new Cyes__sort.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sort'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tascending = {
};
export class Cascending extends AlanNode {
	constructor(init:Tascending, public parent:Cyes__sort) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'direction'?'ascending'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdescending = {
};
export class Cdescending extends AlanNode {
	constructor(init:Tdescending, public parent:Cyes__sort) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'direction'?'descending'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_action_visualization = {
	'show target':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cui_action_visualization extends AlanNode {
	public readonly properties:{
		readonly show_target:Cui_action_visualization.Dshow_target
	};
	constructor(init:Tui_action_visualization, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			show_target: new Cui_action_visualization.Dshow_target(init['show target'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__show_target = {
};
export class Cno__show_target extends AlanNode {
	constructor(init:Tno__show_target, public parent:Cui_action_visualization) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'show target'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__show_target = {
};
export class Cyes__show_target extends AlanNode {
	constructor(init:Tyes__show_target, public parent:Cui_action_visualization) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'show target'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_action_interaction = {
	'interactive':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cui_action_interaction extends AlanNode {
	public readonly properties:{
		readonly interactive:Cui_action_interaction.Dinteractive
	};
	constructor(init:Tui_action_interaction, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			interactive: new Cui_action_interaction.Dinteractive(init['interactive'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__interactive = {
};
export class Cno__interactive extends AlanNode {
	constructor(init:Tno__interactive, public parent:Cui_action_interaction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'interactive'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__interactive = {
};
export class Cyes__interactive extends AlanNode {
	constructor(init:Tyes__interactive, public parent:Cui_action_interaction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'interactive'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_action_attribute = {
	'has description':'no'|['no', {}]|['yes', Tyes__has_description__ui_action_attribute];
};
export class Cui_action_attribute extends AlanNode {
	public readonly properties:{
		readonly has_description:Cui_action_attribute.Dhas_description
	};
	constructor(init:Tui_action_attribute, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_description: new Cui_action_attribute.Dhas_description(init['has description'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_description__ui_action_attribute = {
};
export class Cno__has_description__ui_action_attribute extends AlanNode {
	constructor(init:Tno__has_description__ui_action_attribute, public parent:Cui_action_attribute) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_description__ui_action_attribute = {
	'description':string;
};
export class Cyes__has_description__ui_action_attribute extends AlanNode {
	public readonly properties:{
		readonly description:string
	};
	constructor(init:Tyes__has_description__ui_action_attribute, public parent:Cui_action_attribute) {
		super();
		const $this = this;
		this.properties = {
			description: init['description']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has description'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tui_action = {
	'expression':Tui_expression;
	'has next action':'no'|['no', {}]|['yes', Tyes__has_next_action];
};
export class Cui_action extends AlanNode {
	public definitions:{
		ui_target: Cui_target;
	} = {
		ui_target: new Cui_target({name:'action', definition: this})
	}
	public readonly properties:{
		readonly expression:Cui_expression,
		readonly has_next_action:Cui_action.Dhas_next_action
	};
	constructor(init:Tui_action, public location:AlanNode, public input: {
		node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cui_action.Dexpression(init['expression'], $this),
			has_next_action: new Cui_action.Dhas_next_action(init['has next action'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next_action = {
};
export class Cno__has_next_action extends AlanNode {
	constructor(init:Tno__has_next_action, public parent:Cui_action) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next action'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next_action = {
	'action':Tui_action;
};
export class Cyes__has_next_action extends AlanNode {
	public readonly properties:{
		readonly action:Cui_action
	};
	constructor(init:Tyes__has_next_action, public parent:Cui_action) {
		super();
		const $this = this;
		this.properties = {
			action: new Cyes__has_next_action.Daction(init['action'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next action'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttodo_definition = {
	'todo':'no'|['no', {}]|['yes', Tyes__todo];
};
export class Ctodo_definition extends AlanNode {
	public readonly properties:{
		readonly todo:Ctodo_definition.Dtodo
	};
	constructor(init:Ttodo_definition, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			todo: new Ctodo_definition.Dtodo(init['todo'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__todo = {
};
export class Cno__todo extends AlanNode {
	constructor(init:Tno__todo, public parent:Ctodo_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__todo = {
	'requirement':Tuser_requirement;
	'ui':Tui_todo;
};
export class Cyes__todo extends AlanNode {
	public readonly properties:{
		readonly requirement:Cuser_requirement,
		readonly ui:Cui_todo
	};
	public readonly inferences:{
		dataset_node: () => application.Capplication
	} = {
		dataset_node: cache((detach:boolean) => {
			const application__todo_definition__todo__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tyes__todo, public parent:Ctodo_definition) {
		super();
		const $this = this;
		this.properties = {
			requirement: new Cyes__todo.Drequirement(init['requirement'], $this),
			ui: new Cyes__todo.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__step_value_source_dependence(detach:boolean, application__step_value_source_dependence_nval:application.Cvalue_source, input: {
	step_value_source_dependence: () => application.Cvalue_source
}):application.Cvalue_source {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__step_value_source_dependence_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'base': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.step_value_source_dependence()).result;
				}
				case 'derived': {
					return resolve(context)
						.then(context => application__step_value_source_dependence_nval).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__step_existence(detach:boolean, application__step_existence_nval:application.Cexistence__application, input: {
	step_existence: () => application.Cexistence__application
}):application.Cexistence__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__step_existence_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'mandatory': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.step_existence()).result;
				}
				case 'optional': {
					return resolve(context)
						.then(context => application__step_existence_nval).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tstate_initializer = {
	'node initializer':Tnode_initializer;
	'rule arguments':Record<string, Trule_arguments>;
	'state':string;
};
export class Cstate_initializer extends AlanNode {
	public readonly properties:{
		readonly node_initializer:Cnode_initializer,
		readonly rule_arguments:Cstate_initializer.Drule_arguments,
		readonly state:Cstate_initializer.Dstate
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.node_initializer)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(reduce_context => 
						resolve(reduce_context)
						.then(() => this)
						.then(context => context?.properties.rule_arguments)
						.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
						.then(set => set.map(entry => resolve(entry)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.computation_phase()).result))
							.then(set => set.reduce((prev:application.Ccomputation_phase, curr) => curr.variant.widening_index < prev!.variant.widening_index ? curr : prev, application.Ccomputation_phase.Pzero)).result).result!

				})).result!)
	};
	constructor(init:Tstate_initializer, public location:AlanNode, public input: {
		state_group: () => application.Cstate_group,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			node_initializer: new Cstate_initializer.Dnode_initializer(init['node initializer'], $this),
			rule_arguments: new Cstate_initializer.Drule_arguments(init['rule arguments'], $this),
			state: new Cstate_initializer.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Krule_arguments extends Reference<application.Crules, string> {
	public readonly inferences:{
		mandatory: () => application.Cmandatory__existence
	}
	constructor(key:string, $this:Crule_arguments) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.rules)
			.then(context => {
				const entry = context?.properties.rules.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			mandatory: cache((detach:boolean) => {
				const application__state_initializer__rule_arguments_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => application__state_initializer__rule_arguments_key_nval)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.existence())
					.then(context => context?.variant.name === 'mandatory' ? context.variant.definition as application.Cmandatory__existence : undefined).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rule arguments'::key`; }
}
export type Trule_arguments = {
	'expression':Tderivation_expression;
};
export class Crule_arguments extends AlanDictionaryEntry {
	public key:Krule_arguments;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly expression:Cderivation_expression
	};
	constructor(key:string, init:Trule_arguments, public parent:Cstate_initializer) {
		super();
		const $this = this;
		this.key = new Krule_arguments(key, $this);
		this.properties = {
			expression: new Crule_arguments.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rule arguments'['${this.key.entry}']`; }
	public get entity() { return this; }
}
function evaluate__state_group_checker(detach:boolean, application__state_group_checker_nval:application.Cstate_group, input: {
	expected: () => application.Cstate_group
}):application.Cstate_group {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => {
			const left = resolve(context)
				.then(context => application__state_group_checker_nval).result;
			const right = resolve(context)
				.then(() => self)
				.then(context => context?.component_root.input.expected()).result;
			return left?.is(right!) ? left : undefined
		}).result!
}
export type Tsingular_variablized_object_path = {
	'path':Tvariablized_object_path;
};
export class Csingular_variablized_object_path extends AlanNode {
	public readonly properties:{
		readonly path:Cvariablized_object_path
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!)
	};
	constructor(init:Tsingular_variablized_object_path, public location:AlanNode, public input: {
		modifier: () => application.Cvalue_source,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Csingular_variablized_object_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tsingular_node_path = {
	'path':Tnode_path;
};
export class Csingular_node_path extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.node_type()).result!)
	};
	constructor(init:Tsingular_node_path, public location:AlanNode, public input: {
		modifier: () => application.Cbase_value_source,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Csingular_node_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tsibling_reference_definition = {
	'graph participation':['no', Tno__graph_participation]|['yes', Tyes__graph_participation];
};
export class Csibling_reference_definition extends AlanNode {
	public readonly properties:{
		readonly graph_participation:Csibling_reference_definition.Dgraph_participation
	};
	public readonly output:{
		location: () => application.Csibling_dependency_retrieval;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.graph_participation?.state.node.output.location()).result!)
	};
	constructor(init:Tsibling_reference_definition, public location:AlanNode, public input: {
		collection: () => application.Ccollection__type__property,
		definer_root_location: () => application.Croot_location,
		definer_value_source: () => application.Cvalue_source,
		existence: () => application.Cexistence__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			graph_participation: new Csibling_reference_definition.Dgraph_participation(init['graph participation'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__graph_participation = {
	'support self reference':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cno__graph_participation extends AlanNode {
	public readonly properties:{
		readonly support_self_reference:Cno__graph_participation.Dsupport_self_reference
	};
	public readonly output:{
		location: () => application.Csibling_dependency_retrieval;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.support_self_reference?.state.node.output.location()).result!)
	}
	constructor(init:Tno__graph_participation, public parent:Csibling_reference_definition) {
		super();
		const $this = this;
		this.properties = {
			support_self_reference: new Cno__graph_participation.Dsupport_self_reference(init['support self reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph participation'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__support_self_reference = {
};
export class Cno__support_self_reference extends AlanNode {
	public readonly output:{
		location: () => application.Csibling_dependency_retrieval;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.collection())
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.entity).result!;
				return new Csibling_dependency_retrieval({name: 'sibling step', definition: conv_context});
			}).result!)
	}
	constructor(init:Tno__support_self_reference, public parent:Cno__graph_participation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'support self reference'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__support_self_reference = {
};
export class Cyes__support_self_reference extends AlanNode {
	public readonly output:{
		location: () => application.Csibling_dependency_retrieval;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.collection())
			.then(context => context?.definitions.entity)
			.then(context => context?.definitions.sibling_dependency_retrieval).result!)
	}
	constructor(init:Tyes__support_self_reference, public parent:Cno__graph_participation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'support self reference'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__graph_participation = {
	'graph traversal':Tgraph_traversal_definition;
};
export class Cyes__graph_participation extends AlanNode {
	public readonly properties:{
		readonly graph_traversal:Cgraph_traversal_definition
	};
	public readonly output:{
		location: () => application.Csibling_dependency_retrieval;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.graph_traversal)
			.then(context => context?.component_root.output.type())
			.then(context => context?.definitions.sibling_dependency_retrieval).result!)
	}
	public readonly inferences:{
		base_collection: () => application.Cbase__type__collection,
		mandatory_reference: () => application.Cmandatory__existence,
		root_location: () => application.Croot_location
	} = {
		base_collection: cache((detach:boolean) => {
			const application__sibling_reference_definition__graph_participation__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.collection())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__collection).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		mandatory_reference: cache((detach:boolean) => {
			const application__sibling_reference_definition__graph_participation__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence())
				.then(context => context?.variant.name === 'mandatory' ? context.variant.definition as application.Cmandatory__existence : undefined).result!;
		})
		,
		root_location: cache((detach:boolean) => {
			const application__sibling_reference_definition__graph_participation__yes_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.collection())
						.then(context => context?.properties.node)
						.then(context => context?.component_root.output.location())
						.then(context => context?.component_root.output.root()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.definer_root_location()).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tyes__graph_participation, public parent:Csibling_reference_definition) {
		super();
		const $this = this;
		this.properties = {
			graph_traversal: new Cyes__graph_participation.Dgraph_traversal(init['graph traversal'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graph participation'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vrelative_object_location = { name: 'context entity', definition: application.Centity__application}|{ name: 'sibling entity', definition: application.Csibling_dependency_retrieval}
export class Crelative_object_location extends AlanStruct {
	constructor(
		public readonly variant:Vrelative_object_location) { super(); }
	public readonly output:{
		entity: () => application.Centity__application;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'context entity': {
						const application__relative_object_location_out___entity___context_entity_nval = context.cast('context entity');
						return resolve(context)
							.then(context => application__relative_object_location_out___entity___context_entity_nval).result;
					}
					case 'sibling entity': {
						const application__relative_object_location_out___entity___sibling_entity_nval = context.cast('sibling entity');
						return resolve(context)
							.then(context => application__relative_object_location_out___entity___sibling_entity_nval)
							.then(context => context?.component_root.output.entity()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vrelative_object_location['name']>(_variant:K):Extract<Vrelative_object_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vrelative_object_location['name']]:(($:Extract<Vrelative_object_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'relative object location'`; }
	public is(other:Crelative_object_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vsibling_dependency_retrieval = { name: 'sibling or self step', definition: application.Centity__application}|{ name: 'sibling step', definition: application.Centity__application}|{ name: 'graph step', definition: application.Cgraph_traversal_type}
export class Csibling_dependency_retrieval extends AlanStruct {
	constructor(
		public readonly variant:Vsibling_dependency_retrieval) { super(); }
	public definitions:{
		relative_object_location: Crelative_object_location;
	} = {
		relative_object_location: new Crelative_object_location({name:'sibling entity', definition: this})
	}
	public readonly output:{
		entity: () => application.Centity__application;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'graph step': {
						const application__sibling_dependency_retrieval_out___entity___graph_step_nval = context.cast('graph step');
						return resolve(context)
							.then(context => application__sibling_dependency_retrieval_out___entity___graph_step_nval)
							.then(context => context?.component_root.output.entity()).result;
					}
					case 'sibling or self step': {
						const application__sibling_dependency_retrieval_out___entity___sibling_or_self_step_nval = context.cast('sibling or self step');
						return resolve(context)
							.then(context => application__sibling_dependency_retrieval_out___entity___sibling_or_self_step_nval).result;
					}
					case 'sibling step': {
						const application__sibling_dependency_retrieval_out___entity___sibling_step_nval = context.cast('sibling step');
						return resolve(context)
							.then(context => application__sibling_dependency_retrieval_out___entity___sibling_step_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vsibling_dependency_retrieval['name']>(_variant:K):Extract<Vsibling_dependency_retrieval, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vsibling_dependency_retrieval['name']]:(($:Extract<Vsibling_dependency_retrieval, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'sibling dependency retrieval'`; }
	public is(other:Csibling_dependency_retrieval):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vscalar_value = { name: 'state choice', definition: application.Cstate_group}|{ name: 'number', definition: application.Cnumber_value}|{ name: 'text', definition: application.Ctext__type__property}
export class Cscalar_value extends AlanStruct {
	constructor(
		public readonly variant:Vscalar_value) { super(); }
	public definitions:{
		leaf_value: Cleaf_value;
	} = {
		leaf_value: new Cleaf_value({name:'scalar', definition: this})
	}
	public cast<K extends Vscalar_value['name']>(_variant:K):Extract<Vscalar_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vscalar_value['name']]:(($:Extract<Vscalar_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'scalar value'`; }
	public is(other:Cscalar_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tscalar_expression_list = {
	'has tail':'no'|['no', {}]|['yes', Tyes__has_tail__scalar_expression_list];
	'head':Tscalar_expression;
};
export class Cscalar_expression_list extends AlanNode {
	public readonly properties:{
		readonly has_tail:Cscalar_expression_list.Dhas_tail,
		readonly head:Cscalar_expression
	};
	constructor(init:Tscalar_expression_list, public location:AlanNode, public input: {
		source: () => application.Cbase_value_source,
		target: () => application.Ccommand_scalar,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_tail: new Cscalar_expression_list.Dhas_tail(init['has tail'], $this),
			head: new Cscalar_expression_list.Dhead(init['head'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_tail__scalar_expression_list = {
};
export class Cno__has_tail__scalar_expression_list extends AlanNode {
	constructor(init:Tno__has_tail__scalar_expression_list, public parent:Cscalar_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_tail__scalar_expression_list = {
	'tail':Tscalar_expression_list;
};
export class Cyes__has_tail__scalar_expression_list extends AlanNode {
	public readonly properties:{
		readonly tail:Cscalar_expression_list
	};
	constructor(init:Tyes__has_tail__scalar_expression_list, public parent:Cscalar_expression_list) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_tail__scalar_expression_list.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscalar_expression = {
	'type':['dynamic', Tdynamic__type__scalar_expression]|['number', Tnumber__type__scalar_expression]|['text', Ttext__type__scalar_expression];
};
export class Cscalar_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cscalar_expression.Dtype
	};
	constructor(init:Tscalar_expression, public location:AlanNode, public input: {
		source: () => application.Cbase_value_source,
		target: () => application.Ccommand_scalar,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cscalar_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic__type__scalar_expression = {
	'path':Tsingular_node_path;
	'property':string;
};
export class Cdynamic__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly path:Csingular_node_path,
		readonly property:Cdynamic__type__scalar_expression.Dproperty
	};
	constructor(init:Tdynamic__type__scalar_expression, public parent:Cscalar_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cdynamic__type__scalar_expression.Dpath(init['path'], $this),
			property: new Cdynamic__type__scalar_expression.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__scalar_expression = {
	'type':['binary expression', Tbinary_expression__type__number__type__scalar_expression]|['constant', Tconstant__type__number__type__scalar_expression]|['list expression', Tlist_expression__type__number__type__scalar_expression]|'now in seconds'|['now in seconds', {}]|['unary expression', Tunary_expression__type__number__type__scalar_expression];
};
export class Cnumber__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber__type__scalar_expression.Dtype
	};
	public readonly inferences:{
		number_target: () => application.Ccommand_number
	} = {
		number_target: cache((detach:boolean) => {
			const application__scalar_expression__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Ccommand_number : undefined).result!;
		})

	}
	constructor(init:Tnumber__type__scalar_expression, public parent:Cscalar_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber__type__scalar_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinary_expression__type__number__type__scalar_expression = {
	'expressions':Texpressions__binary_expression__type__number__type__scalar_expression;
	'operation':['division', Tdivision__operation__binary_expression__type__number__type__scalar_expression]|'remainder'|['remainder', {}];
};
export class Cbinary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly expressions:Cexpressions__binary_expression__type__number__type__scalar_expression,
		readonly operation:Cbinary_expression__type__number__type__scalar_expression.Doperation
	};
	constructor(init:Tbinary_expression__type__number__type__scalar_expression, public parent:Cnumber__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			expressions: new Cexpressions__binary_expression__type__number__type__scalar_expression(init['expressions'], $this),
			operation: new Cbinary_expression__type__number__type__scalar_expression.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binary expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpressions__binary_expression__type__number__type__scalar_expression = {
	'left':Tscalar_expression;
	'right':Tscalar_expression;
};
export class Cexpressions__binary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly left:Cscalar_expression,
		readonly right:Cscalar_expression
	};
	constructor(init:Texpressions__binary_expression__type__number__type__scalar_expression, public parent:Cbinary_expression__type__number__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			left: new Cexpressions__binary_expression__type__number__type__scalar_expression.Dleft(init['left'], $this),
			right: new Cexpressions__binary_expression__type__number__type__scalar_expression.Dright(init['right'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'expressions'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdivision__operation__binary_expression__type__number__type__scalar_expression = {
	'rounding':'ceil'|['ceil', {}]|'floor'|['floor', {}]|'ordinary'|['ordinary', {}];
};
export class Cdivision__operation__binary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly rounding:Cdivision__operation__binary_expression__type__number__type__scalar_expression.Drounding
	};
	public readonly output:{
		left_set_type: () => application.Cnumber_set_type;
	} = {
		left_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rounding?.state.node.output.numerator_set_type()).result!)
	}
	constructor(init:Tdivision__operation__binary_expression__type__number__type__scalar_expression, public parent:Cbinary_expression__type__number__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			rounding: new Cdivision__operation__binary_expression__type__number__type__scalar_expression.Drounding(init['rounding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'division'`; }
	public get entity() { return this.parent.entity; }
}
export type Tceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression = {
};
export class Cceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly output:{
		numerator_set_type: () => application.Cnumber_set_type;
	} = {
		numerator_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.number_target())
			.then(context => context?.component_root.output.set_type()).result!)
	}
	constructor(init:Tceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression, public parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ceil'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression = {
};
export class Cfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly output:{
		numerator_set_type: () => application.Cnumber_set_type;
	} = {
		numerator_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.integer_target()).result!)
	}
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type
	} = {
		integer_target: cache((detach:boolean) => {
			const application__scalar_expression__type__number__type__binary_expression__operation__division__rounding__floor_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression, public parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'floor'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression = {
};
export class Cordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly output:{
		numerator_set_type: () => application.Cnumber_set_type;
	} = {
		numerator_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.integer_target()).result!)
	}
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type
	} = {
		integer_target: cache((detach:boolean) => {
			const application__scalar_expression__type__number__type__binary_expression__operation__division__rounding__ordinary_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression, public parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ordinary'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremainder__operation__binary_expression__type__number__type__scalar_expression = {
};
export class Cremainder__operation__binary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly output:{
		left_set_type: () => application.Cnumber_set_type;
	} = {
		left_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.integer_target()).result!)
	}
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type
	} = {
		integer_target: cache((detach:boolean) => {
			const application__scalar_expression__type__number__type__binary_expression__operation__remainder_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tremainder__operation__binary_expression__type__number__type__scalar_expression, public parent:Cbinary_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'remainder'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconstant__type__number__type__scalar_expression = {
	'value':Tconstant_number_value;
};
export class Cconstant__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly value:Cconstant_number_value & { readonly inferences: {
			set_type: () => application.Cnumber_set_type;
		} }
	};
	constructor(init:Tconstant__type__number__type__scalar_expression, public parent:Cnumber__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cconstant__type__number__type__scalar_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'constant'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist_expression__type__number__type__scalar_expression = {
	'expression':Tscalar_expression_list;
	'operation':'maximum'|['maximum', {}]|'minimum'|['minimum', {}]|'product'|['product', {}]|'sum'|['sum', {}];
};
export class Clist_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cscalar_expression_list,
		readonly operation:Clist_expression__type__number__type__scalar_expression.Doperation
	};
	constructor(init:Tlist_expression__type__number__type__scalar_expression, public parent:Cnumber__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Clist_expression__type__number__type__scalar_expression.Dexpression(init['expression'], $this),
			operation: new Clist_expression__type__number__type__scalar_expression.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'list expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmaximum__operation__list_expression__type__number__type__scalar_expression = {
};
export class Cmaximum__operation__list_expression__type__number__type__scalar_expression extends AlanNode {
	constructor(init:Tmaximum__operation__list_expression__type__number__type__scalar_expression, public parent:Clist_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'maximum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminimum__operation__list_expression__type__number__type__scalar_expression = {
};
export class Cminimum__operation__list_expression__type__number__type__scalar_expression extends AlanNode {
	constructor(init:Tminimum__operation__list_expression__type__number__type__scalar_expression, public parent:Clist_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'minimum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduct__operation__list_expression__type__number__type__scalar_expression = {
};
export class Cproduct__operation__list_expression__type__number__type__scalar_expression extends AlanNode {
	constructor(init:Tproduct__operation__list_expression__type__number__type__scalar_expression, public parent:Clist_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'product'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsum__operation__list_expression__type__number__type__scalar_expression = {
};
export class Csum__operation__list_expression__type__number__type__scalar_expression extends AlanNode {
	constructor(init:Tsum__operation__list_expression__type__number__type__scalar_expression, public parent:Clist_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'sum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnow_in_seconds__type__number__type__scalar_expression = {
};
export class Cnow_in_seconds__type__number__type__scalar_expression extends AlanNode {
	constructor(init:Tnow_in_seconds__type__number__type__scalar_expression, public parent:Cnumber__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'now in seconds'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunary_expression__type__number__type__scalar_expression = {
	'expression':Tscalar_expression;
	'type':'absolute value'|['absolute value', {}]|['numerical type conversion', Tnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression]|'sign inversion'|['sign inversion', {}];
};
export class Cunary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cscalar_expression,
		readonly type:Cunary_expression__type__number__type__scalar_expression.Dtype
	};
	constructor(init:Tunary_expression__type__number__type__scalar_expression, public parent:Cnumber__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cunary_expression__type__number__type__scalar_expression.Dexpression(init['expression'], $this),
			type: new Cunary_expression__type__number__type__scalar_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'unary expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tabsolute_value__type__unary_expression__type__number__type__scalar_expression = {
};
export class Cabsolute_value__type__unary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.number_target())
			.then(context => context?.component_root.output.numerical_type()).result!)
	}
	constructor(init:Tabsolute_value__type__unary_expression__type__number__type__scalar_expression, public parent:Cunary_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'absolute value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression = {
	'conversion':string;
};
export class Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly conversion:Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression.Dconversion
	};
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.conversion?.ref)
			.then(context => context?.key?.ref).result!)
	}
	constructor(init:Tnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression, public parent:Cunary_expression__type__number__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			conversion: new Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression.Dconversion(init['conversion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'numerical type conversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsign_inversion__type__unary_expression__type__number__type__scalar_expression = {
};
export class Csign_inversion__type__unary_expression__type__number__type__scalar_expression extends AlanNode {
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.number_target())
			.then(context => context?.component_root.output.numerical_type()).result!)
	}
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type
	} = {
		integer_target: cache((detach:boolean) => {
			const application__scalar_expression__type__number__type__unary_expression__type__sign_inversion_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tsign_inversion__type__unary_expression__type__number__type__scalar_expression, public parent:Cunary_expression__type__number__type__scalar_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sign inversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__scalar_expression = {
	'type':['static', Tstatic__type__text__type__scalar_expression];
};
export class Ctext__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly type:Ctext__type__scalar_expression.Dtype
	};
	public readonly inferences:{
		text_target: () => application.Ccommand_text
	} = {
		text_target: cache((detach:boolean) => {
			const application__scalar_expression__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'text' ? context.variant.definition as application.Ccommand_text : undefined).result!;
		})

	}
	constructor(init:Ttext__type__scalar_expression, public parent:Cscalar_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext__type__scalar_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__text__type__scalar_expression = {
	'value':string;
};
export class Cstatic__type__text__type__scalar_expression extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly inferences:{
		no_mandatory_reference: () => application.Cexistence__application
	} = {
		no_mandatory_reference: cache((detach:boolean) => {
			const application__scalar_expression__type__text__type__static_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text_target())
				.then(context => {
					switch (context?.variant.name) {
						case 'temporary': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => application.Cexistence__application.Poptional).result;
						}
						case 'text property': {
							const application__scalar_expression__type__text__type__static_inf___no_mandatory_reference___text_property_nval = context.cast('text property');
							return resolve(context)
								.then(context => application__scalar_expression__type__text__type__static_inf___no_mandatory_reference___text_property_nval)
								.then(context => context?.properties.has_reference)
								.then(context => {
									const state_group = context;
									switch (state_group?.state.name) {
										case 'no': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => application.Cexistence__application.Poptional).result;
										}
										case 'yes': {
											const application__scalar_expression__type__text__type__static_inf___no_mandatory_reference___text_property___yes_nval = state_group.cast('yes');
											return resolve(context)
												.then(context => application__scalar_expression__type__text__type__static_inf___no_mandatory_reference___text_property___yes_nval)
												.then(context => context?.properties.behaviour)
												.then(context => context?.component_root.output.existence())
												.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
									};}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tstatic__type__text__type__scalar_expression, public parent:Ctext__type__scalar_expression) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
type Vnode_location = { name: 'group', definition: application.Cgroup__type__property}|{ name: 'state', definition: application.Cstates__state_group}|{ name: 'collection', definition: application.Ccollection__type__property}|{ name: 'root', definition: application.Croot_location}
export class Cnode_location extends AlanStruct {
	constructor(
		public readonly variant:Vnode_location, public input: {
			attribute: () => application.Cnode_parent_attribute,
			node: () => application.Cnode_parent,
			root: () => application.Croot_location
		}) { super(); }
	public readonly output:{
		attribute: () => application.Cnode_parent_attribute;
		node: () => application.Cnode_parent;
		root: () => application.Croot_location;
	} = {
		attribute: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.attribute()).result!),
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!),
		root: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.root()).result!)
	};
	public cast<K extends Vnode_location['name']>(_variant:K):Extract<Vnode_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_location['name']]:(($:Extract<Vnode_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node location'`; }
	public is(other:Cnode_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vroot_location = { name: 'operation', definition: application.Coperation}|{ name: 'dataset', definition: application.Capplication}
export class Croot_location extends AlanStruct {
	constructor(
		public readonly variant:Vroot_location) { super(); }
	public definitions:{
		node_location: Cnode_location;
	} = {
		node_location: new Cnode_location({name:'root', definition: this}, {
			attribute: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => {
					switch (context?.variant.name) {
						case 'dataset': {
							return resolve(context)
								.then(() => this)
								.then(() => application.Cnode_parent_attribute.Pnone).result;
						}
						case 'operation': {
							const application__root_location_var___node_location_in___attribute___operation_nval = context.cast('operation');
							return resolve(context)
								.then(context => application__root_location_var___node_location_in___attribute___operation_nval)
								.then(context => context?.component_root.output.attribute())
								.then(context => context?.definitions.node_parent_attribute).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!),
			node: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => {
					switch (context?.variant.name) {
						case 'dataset': {
							return resolve(context)
								.then(() => this)
								.then(() => application.Cnode_parent.Pnone).result;
						}
						case 'operation': {
							const application__root_location_var___node_location_in___node___operation_nval = context.cast('operation');
							return resolve(context)
								.then(context => application__root_location_var___node_location_in___node___operation_nval)
								.then(context => context?.component_root.output.attribute())
								.then(context => context?.parent)
								.then(context => context?.definitions.node_parent).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this).result!)
		})
	}
	public readonly output:{
		root_node_type: () => application.Cnode__application;
	} = {
		root_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'dataset': {
						const application__root_location_out___root_node_type___dataset_nval = context.cast('dataset');
						return resolve(context)
							.then(context => application__root_location_out___root_node_type___dataset_nval)
							.then(context => context?.properties.root).result;
					}
					case 'operation': {
						const application__root_location_out___root_node_type___operation_nval = context.cast('operation');
						return resolve(context)
							.then(context => application__root_location_out___root_node_type___operation_nval)
							.then(context => context?.component_root.output.root_node_type()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vroot_location['name']>(_variant:K):Extract<Vroot_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vroot_location['name']]:(($:Extract<Vroot_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'root location'`; }
	public is(other:Croot_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
function evaluate__relative_object_location_validator(detach:boolean, application__relative_object_location_validator_nval:application.Crelative_object_location, input: {
	expected: () => application.Crelative_object_location
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.expected())
		.then(context => {
			switch (context?.variant.name) {
				case 'context entity': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.expected()).result;
				}
				case 'sibling entity': {
					const application__relative_object_location_validator___sibling_entity_nval = context.cast('sibling entity');
					return resolve(context)
						.then(context => application__relative_object_location_validator___sibling_entity_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'graph step': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(context => application__relative_object_location_validator_nval).result;
											const right = resolve(context)
												.then(() => self)
												.then(context => context?.component_root.input.expected()).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'sibling or self step': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.expected()).result;
								}
								case 'sibling step': {
									return resolve(context)
										.then(context => application__relative_object_location_validator_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'context entity': {
													return resolve(context)
														.then(context => {
															const left = resolve(context)
																.then(context => application__relative_object_location_validator_nval).result;
															const right = resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.expected()).result;
															return left?.is(right!) ? left : undefined
														}).result;
												}
												case 'sibling entity': {
													const application__relative_object_location_validator___sibling_entity___sibling_step___sibling_entity_nval = context.cast('sibling entity');
													return resolve(context)
														.then(context => application__relative_object_location_validator___sibling_entity___sibling_step___sibling_entity_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'graph step': {
																	return resolve(context)
																		.then(() => self)
																		.then(context => context?.component_root.input.expected()).result;
																}
																case 'sibling or self step': {
																	return resolve(context)
																		.then(context => {
																			const left = resolve(context)
																				.then(context => application__relative_object_location_validator_nval).result;
																			const right = resolve(context)
																				.then(() => self)
																				.then(context => context?.component_root.input.expected()).result;
																			return left?.is(right!) ? left : undefined
																		}).result;
																}
																case 'sibling step': {
																	return resolve(context)
																		.then(() => self)
																		.then(context => context?.component_root.input.expected()).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Treference_set_subset_step = {
	'subset':'no'|['no', {}]|['yes', Tyes__subset];
};
export class Creference_set_subset_step extends AlanNode {
	public readonly properties:{
		readonly subset:Creference_set_subset_step.Dsubset
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		node_type: () => application.Cnode__application;
		phase: () => application.Ccomputation_phase;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.subset?.state.node.output.dependency()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.subset?.state.node.output.lower_bound_cardinality()).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.subset?.state.node.output.node_type()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.subset?.state.node.output.phase()).result!)
	};
	constructor(init:Treference_set_subset_step, public location:AlanNode, public input: {
		context: () => application.Cnavigation_context,
		phase: () => application.Ccomputation_phase,
		reference_set: () => application.Creference_set__type__attributes,
		reference_set_dependency: () => application.Cdependency__application,
		this_property: () => application.Cproperty__type__attributes
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			subset: new Creference_set_subset_step.Dsubset(init['subset'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__subset = {
};
export class Cno__subset extends AlanNode {
	public readonly output:{
		dependency: () => application.Cdependency__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		node_type: () => application.Cnode__application;
		phase: () => application.Ccomputation_phase;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.reference_set_dependency()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.existence())
			.then(context => context?.component_root.output.lower_bound_cardinality())
			.then(context => {
				switch (context?.variant.name) {
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.reference_set())
							.then(context => context?.properties.value_source?.state.node.output.lower_bound_cardinality()).result;
					}
					case 'zero': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Clower_bound_cardinality.Pzero).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.reference_set())
			.then(context => context?.properties.referenced_type_path)
			.then(context => context?.properties.path?.inferences.node_type()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!)
	}
	constructor(init:Tno__subset, public parent:Creference_set_subset_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'subset'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__subset = {
	'head':Tvariablized_object_path;
	'type':'sibling'|['sibling', {}]|'simple'|['simple', {}];
};
export class Cyes__subset extends AlanNode {
	public readonly properties:{
		readonly head:Cvariablized_object_path & { readonly inferences: {
			dependency_of_reference_set: () => application.Cdependency__application;
			subset_dependency: () => application.Cdependency__application;
			type_dependency: () => application.Cdependency__application;
		} },
		readonly type:Cyes__subset.Dtype
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		node_type: () => application.Cnode__application;
		phase: () => application.Ccomputation_phase;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head?.inferences.type_dependency()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.existence())
			.then(context => context?.component_root.output.lower_bound_cardinality())
			.then(context => {
				switch (context?.variant.name) {
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.reference_set())
							.then(context => context?.properties.value_source?.state.node.output.lower_bound_cardinality()).result;
					}
					case 'zero': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Clower_bound_cardinality.Pzero).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.collection())
			.then(context => context?.properties.node).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tyes__subset, public parent:Creference_set_subset_step) {
		super();
		const $this = this;
		this.properties = {
			head: new Cyes__subset.Dhead(init['head'], $this),
			type: new Cyes__subset.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'subset'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsibling__type__yes = {
};
export class Csibling__type__yes extends AlanNode {
	public readonly output:{
		collection: () => application.Ccollection__type__property;
		entry_location: () => application.Crelative_object_location;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.sibling_reference_set())
			.then(context => context?.inferences.collection()).result!),
		entry_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.sibling_reference_set())
			.then(context => context?.properties.definition)
			.then(context => context?.component_root.output.location())
			.then(context => context?.definitions.relative_object_location).result!)
	}
	public readonly inferences:{
		head_node_type: () => application.Cnode__application,
		sibling_node_type: () => application.Cnode__application,
		sibling_reference_set: () => application.Csibling__root
	} = {
		head_node_type: cache((detach:boolean) => {
			const application__reference_set_subset_step__subset__yes__type__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})
		,
		sibling_node_type: cache((detach:boolean) => {
			const application__reference_set_subset_step__subset__yes__type__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.inferences.head_node_type()).result;
					const right = resolve(context)
						.then(() => this.inferences.sibling_reference_set())
						.then(context => context?.inferences.collection())
						.then(context => context?.properties.node).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})
		,
		sibling_reference_set: cache((detach:boolean) => {
			const application__reference_set_subset_step__subset__yes__type__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.reference_set())
				.then(context => context?.properties.referenced_type_path)
				.then(context => {
					if (context?.properties.root.state.name === 'sibling') {
						return resolve(context.properties.root.state.node as application.Csibling__root).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tsibling__type__yes, public parent:Cyes__subset) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sibling'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsimple__type__yes = {
};
export class Csimple__type__yes extends AlanNode {
	public readonly output:{
		collection: () => application.Ccollection__type__property;
		entry_location: () => application.Crelative_object_location;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection()).result!),
		entry_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.definitions.entity)
			.then(context => context?.definitions.relative_object_location).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const application__reference_set_subset_step__subset__yes__type__simple_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined)
				.then(context => context?.variant.name === 'collection attribute' ? context.variant.definition as application.Ccollection_attribute : undefined)
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tsimple__type__yes, public parent:Cyes__subset) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'simple'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_set_step = {
	'reference set':string;
};
export class Creference_set_step extends AlanNode {
	public readonly properties:{
		readonly reference_set:Creference_set_step.Dreference_set
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.reference_set?.ref)
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.collection_attribute)
					.then(context => context?.definitions.collection_object)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.dependency()).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.existence()).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.reference_set?.inferences.relative_location_of_referenced_nodes()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(context => evaluate__step_value_source_dependence(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!, {
						step_value_source_dependence: () => resolve(this)
							.then(() => this)
							.then(context => context?.properties.reference_set?.ref)
							.then(context => context?.properties.value_source?.state.node.output.reference_definer())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						})).result!

				});
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.component_root.input.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.reference_set?.inferences.dependencies()).result!

				})).result!)
	};
	constructor(init:Treference_set_step, public location:AlanNode, public input: {
		context: () => application.Cnode_navigation_context,
		dependency_location_support: () => application.Crecursion_dependence,
		phase: () => application.Ccomputation_phase,
		target_root_location: () => application.Croot_location,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			reference_set: new Creference_set_step.Dreference_set(init['reference set'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
function evaluate__reference_set_dependencies_checker(detach:boolean, application__reference_set_dependencies_checker_nval:application.Creference_set__type__attributes, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location: () => application.Crecursion_dependence,
	dependency_location_support: () => application.Crecursion_dependence,
	dependency_root_location: () => application.Croot_location,
	target_root_location: () => application.Croot_location
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.target_root_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'dataset': {
					return resolve(context)
						.then(context => evaluate__reference_set_computation_checker_impl(detach, 
							resolve(context)
								.then(context => application__reference_set_dependencies_checker_nval).result!, {
							context_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.context_location()).result!
							, 
							dependency_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location()).result!
							, 
							dependency_location_support: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location_support()).result!

							})).result;
				}
				case 'operation': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_root_location())
						.then(context => {
							switch (context?.variant.name) {
								case 'dataset': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Ccomputation_phase.Pzero).result;
								}
								case 'operation': {
									return resolve(context)
										.then(context => evaluate__reference_set_computation_checker_impl(detach, 
											resolve(context)
												.then(context => application__reference_set_dependencies_checker_nval).result!, {
											context_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location()).result!
											, 
											dependency_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location()).result!
											, 
											dependency_location_support: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location_support()).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__reference_set_computation_checker_impl(detach:boolean, application__reference_set_computation_checker_impl_nval:application.Creference_set__type__attributes, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location: () => application.Crecursion_dependence,
	dependency_location_support: () => application.Crecursion_dependence
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.dependency_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'none': {
					return resolve(context)
						.then(context => application__reference_set_computation_checker_impl_nval)
						.then(context => context?.properties.value_source?.state.node.output.computation_phase()).result;
				}
				case 'recursion': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_location_support())
						.then(context => {
							switch (context?.variant.name) {
								case 'none': {
									return resolve(context)
										.then(context => application__reference_set_computation_checker_impl_nval)
										.then(context => context?.properties.value_source?.state.node.output.computation_phase()).result;
								}
								case 'recursion': {
									return resolve(context)
										.then(context => evaluate__identical_recursion_dependence(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency_location_support()).result;
													const right = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency_location()).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											phase: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location())
												.then(context => {
													switch (context?.variant.name) {
														case 'context entity': {
															return resolve(context)
																.then(context => application__reference_set_computation_checker_impl_nval)
																.then(context => context?.properties.value_source?.state.node.output.computation_phase()).result;
														}
														case 'sibling entity': {
															return resolve(context)
																.then(() => self)
																.then(() => application.Ccomputation_phase.Pzero).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
													};
												}).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__reference_dependencies_checker(detach:boolean, application__reference_dependencies_checker_nval:application.Creference_definer, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location: () => application.Crecursion_dependence,
	dependency_location_support: () => application.Crecursion_dependence,
	dependency_root_location: () => application.Croot_location,
	target_root_location: () => application.Croot_location
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.target_root_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'dataset': {
					return resolve(context)
						.then(context => evaluate__reference_computation_checker_impl(detach, 
							resolve(context)
								.then(context => application__reference_dependencies_checker_nval).result!, {
							context_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.context_location()).result!
							, 
							dependency_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location()).result!
							, 
							dependency_location_support: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location_support()).result!

							})).result;
				}
				case 'operation': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_root_location())
						.then(context => {
							switch (context?.variant.name) {
								case 'dataset': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Ccomputation_phase.Pzero).result;
								}
								case 'operation': {
									return resolve(context)
										.then(context => evaluate__reference_computation_checker_impl(detach, 
											resolve(context)
												.then(context => application__reference_dependencies_checker_nval).result!, {
											context_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location()).result!
											, 
											dependency_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location()).result!
											, 
											dependency_location_support: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location_support()).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__reference_computation_checker_impl(detach:boolean, application__reference_computation_checker_impl_nval:application.Creference_definer, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location: () => application.Crecursion_dependence,
	dependency_location_support: () => application.Crecursion_dependence
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.dependency_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'none': {
					return resolve(context)
						.then(context => application__reference_computation_checker_impl_nval)
						.then(context => context?.component_root.output.computation_phase()).result;
				}
				case 'recursion': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_location_support())
						.then(context => {
							switch (context?.variant.name) {
								case 'none': {
									return resolve(context)
										.then(context => application__reference_computation_checker_impl_nval)
										.then(context => context?.component_root.output.computation_phase()).result;
								}
								case 'recursion': {
									return resolve(context)
										.then(context => evaluate__identical_recursion_dependence(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency_location_support()).result;
													const right = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency_location()).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											phase: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location())
												.then(context => {
													switch (context?.variant.name) {
														case 'context entity': {
															return resolve(context)
																.then(context => application__reference_computation_checker_impl_nval)
																.then(context => context?.component_root.output.computation_phase()).result;
														}
														case 'sibling entity': {
															return resolve(context)
																.then(() => self)
																.then(() => application.Ccomputation_phase.Pzero).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
													};
												}).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Treference_behaviour = {
	'type':'mandatory'|['mandatory', {}]|'optional'|['optional', {}];
};
export class Creference_behaviour extends AlanNode {
	public readonly properties:{
		readonly type:Creference_behaviour.Dtype
	};
	public readonly output:{
		existence: () => application.Cexistence__application;
		value_source: () => application.Cvalue_source;
	} = {
		existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.existence()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	};
	constructor(init:Treference_behaviour, public location:AlanNode, public input: {
		target_value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Creference_behaviour.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tmandatory__type = {
};
export class Cmandatory__type extends AlanNode {
	public readonly output:{
		existence: () => application.Cexistence__application;
		value_source: () => application.Cvalue_source;
	} = {
		existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cexistence__application.Pmandatory).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.target_value_source()).result!)
	}
	constructor(init:Tmandatory__type, public parent:Creference_behaviour) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'mandatory'`; }
	public get entity() { return this.parent.entity; }
}
export type Toptional__type = {
};
export class Coptional__type extends AlanNode {
	public readonly output:{
		existence: () => application.Cexistence__application;
		value_source: () => application.Cvalue_source;
	} = {
		existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cexistence__application.Poptional).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	constructor(init:Toptional__type, public parent:Creference_behaviour) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'optional'`; }
	public get entity() { return this.parent.entity; }
}
type Vrecursion_dependence = { name: 'none', definition: (typeof Crecursion_dependence.Pnone)}|{ name: 'recursion', definition: application.Cgraph_traversal}
export class Crecursion_dependence extends AlanStruct {
	public static Pnone:Crecursion_dependence = new class PrimitiveInstance extends Crecursion_dependence {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Crecursion_dependence})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vrecursion_dependence) { super(); }
	public cast<K extends Vrecursion_dependence['name']>(_variant:K):Extract<Vrecursion_dependence, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vrecursion_dependence['name']]:(($:Extract<Vrecursion_dependence, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'recursion dependence'`; }
	public is(other:Crecursion_dependence):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__recursion_dependence = application.Crecursion_dependence;
export type Trecursion_annotation = {
	'recursive graph traversal':'no'|['no', {}]|['yes', Tyes__recursive_graph_traversal];
};
export class Crecursion_annotation extends AlanNode {
	public readonly properties:{
		readonly recursive_graph_traversal:Crecursion_annotation.Drecursive_graph_traversal
	};
	public readonly output:{
		location: () => application.Crecursion_dependence;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.recursive_graph_traversal?.state.node.output.location()).result!)
	};
	constructor(init:Trecursion_annotation, public location:AlanNode, public input: {
		this_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			recursive_graph_traversal: new Crecursion_annotation.Drecursive_graph_traversal(init['recursive graph traversal'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__recursive_graph_traversal = {
};
export class Cno__recursive_graph_traversal extends AlanNode {
	public readonly output:{
		location: () => application.Crecursion_dependence;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!)
	}
	constructor(init:Tno__recursive_graph_traversal, public parent:Crecursion_annotation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'recursive graph traversal'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__recursive_graph_traversal = {
	'selector':Tgraph_traversal_selector;
};
export class Cyes__recursive_graph_traversal extends AlanNode {
	public readonly properties:{
		readonly selector:Cgraph_traversal_selector
	};
	public readonly output:{
		location: () => application.Crecursion_dependence;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.selector)
			.then(context => context?.component_root.output.traversal())
			.then(context => context?.definitions.recursion_dependence).result!)
	}
	constructor(init:Tyes__recursive_graph_traversal, public parent:Crecursion_annotation) {
		super();
		const $this = this;
		this.properties = {
			selector: new Cyes__recursive_graph_traversal.Dselector(init['selector'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'recursive graph traversal'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__property_value_dependencies_checker(detach:boolean, application__property_value_dependencies_checker_nval:application.Cproperty__type__attributes, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location: () => application.Crecursion_dependence,
	dependency_location_support: () => application.Crecursion_dependence,
	dependency_root_location: () => application.Croot_location,
	target_root_location: () => application.Croot_location
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.target_root_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'dataset': {
					return resolve(context)
						.then(context => evaluate__property_value_computation_checker_impl(detach, 
							resolve(context)
								.then(context => application__property_value_dependencies_checker_nval).result!, {
							context_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.context_location()).result!
							, 
							dependency_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location()).result!
							, 
							dependency_location_support: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location_support()).result!

							})).result;
				}
				case 'operation': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_root_location())
						.then(context => {
							switch (context?.variant.name) {
								case 'dataset': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Ccomputation_phase.Pzero).result;
								}
								case 'operation': {
									return resolve(context)
										.then(context => evaluate__property_value_computation_checker_impl(detach, 
											resolve(context)
												.then(context => application__property_value_dependencies_checker_nval).result!, {
											context_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location()).result!
											, 
											dependency_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location()).result!
											, 
											dependency_location_support: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location_support()).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__property_value_computation_checker_impl(detach:boolean, application__property_value_computation_checker_impl_nval:application.Cproperty__type__attributes, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location: () => application.Crecursion_dependence,
	dependency_location_support: () => application.Crecursion_dependence
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.dependency_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'none': {
					return resolve(context)
						.then(context => application__property_value_computation_checker_impl_nval)
						.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result;
				}
				case 'recursion': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_location_support())
						.then(context => {
							switch (context?.variant.name) {
								case 'none': {
									return resolve(context)
										.then(context => application__property_value_computation_checker_impl_nval)
										.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result;
								}
								case 'recursion': {
									return resolve(context)
										.then(context => evaluate__identical_recursion_dependence(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency_location_support()).result;
													const right = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.dependency_location()).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											phase: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location())
												.then(context => {
													switch (context?.variant.name) {
														case 'context entity': {
															return resolve(context)
																.then(context => application__property_value_computation_checker_impl_nval)
																.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result;
														}
														case 'sibling entity': {
															return resolve(context)
																.then(() => self)
																.then(() => application.Ccomputation_phase.Pzero).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
													};
												}).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tproperty_step = {
	'property':string;
};
export class Cproperty_step extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty_step.Dproperty
	};
	public readonly output:{
		phase: () => application.Ccomputation_phase;
		property: () => application.Cproperty__type__attributes;
	} = {
		phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.property?.inferences.dependencies()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.phase()).result!

				})).result!),
		property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.ref).result!)
	};
	constructor(init:Tproperty_step, public location:AlanNode, public input: {
		context: () => application.Cnode_navigation_context,
		dependency_location_support: () => application.Crecursion_dependence,
		phase: () => application.Ccomputation_phase,
		target_root_location: () => application.Croot_location,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty_step.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tplural_reference_expression = {
	'type':['augment', Taugment]|['union', Tunion];
};
export class Cplural_reference_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cplural_reference_expression.Dtype
	};
	public readonly output:{
		collection: () => application.Cyes__key_constraint;
		value_computation_phase: () => application.Ccomputation_phase;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.collection()).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result!)
	};
	constructor(init:Tplural_reference_expression, public location:AlanNode, public input: {
		collection: () => application.Cyes__key_constraint,
		lower_bound_cardinality_support: () => application.Clower_bound_cardinality
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cplural_reference_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Taugment = {
	'collection path':Tvariablized_object_path;
	'filter path':Tdescendant_object_path;
};
export class Caugment extends AlanNode {
	public readonly properties:{
		readonly collection_path:Cvariablized_object_path & { readonly inferences: {
			collection: () => application.Ccollection_object;
			collection_property: () => application.Ccollection__type__property;
			dependency: () => application.Cdependency__application;
			lower_bound_cardinality: () => application.Clower_bound_cardinality;
		} },
		readonly filter_path:Cdescendant_object_path & { readonly inferences: {
			node: () => application.Cnode__application;
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.filter_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Taugment, public parent:Cplural_reference_expression) {
		super();
		const $this = this;
		this.properties = {
			collection_path: new Caugment.Dcollection_path(init['collection path'], $this),
			filter_path: new Caugment.Dfilter_path(init['filter path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'augment'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunion = {
	'branches':Record<string, Tbranches__union>;
	'first branch':string;
};
export class Cunion extends AlanNode {
	public readonly properties:{
		readonly branches:Cunion.Dbranches,
		readonly first_branch:Cunion.Dfirst_branch
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.branches?.inferences.computation_phase()).result!)
	}
	constructor(init:Tunion, public parent:Cplural_reference_expression) {
		super();
		const $this = this;
		this.properties = {
			branches: new Cunion.Dbranches(init['branches'], $this),
			first_branch: new Cunion.Dfirst_branch(init['first branch'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'union'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbranches__union = {
	'has predecessor':'no'|['no', {}]|'yes'|['yes', {}];
	'has successor':'no'|['no', {}]|'yes'|['yes', {}];
	'source':['dependency', Tdependency__source]|['dependency inversion', Tdependency_inversion];
};
export class Cbranches__union extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_predecessor:Cbranches__union.Dhas_predecessor,
		readonly has_successor:Cbranches__union.Dhas_successor,
		readonly source:Cbranches__union.Dsource
	};
	constructor(key:string, init:Tbranches__union, public parent:Cunion) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_predecessor: new Cbranches__union.Dhas_predecessor(init['has predecessor'], $this),
			has_successor: new Cbranches__union.Dhas_successor(init['has successor'], $this),
			source: new Cbranches__union.Dsource(init['source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'branches'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_predecessor__branches = {
};
export class Cno__has_predecessor__branches extends AlanNode {
	public readonly output:{
		dependency_location_support: () => application.Crecursion_dependence;
	} = {
		dependency_location_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.lower_bound_cardinality_support())
			.then(context => {
				switch (context?.variant.name) {
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Crecursion_dependence.Pnone).result;
					}
					case 'zero': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.collection())
							.then(context => context?.parent)
							.then(context => context?.properties.recursion)
							.then(context => context?.component_root.output.location()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	}
	public readonly inferences:{
		lower_bound_cardinality_for_first_branch: () => application.Clower_bound_cardinality
	} = {
		lower_bound_cardinality_for_first_branch: cache((detach:boolean) => {
			const application__plural_reference_expression__type__union__branches__has_predecessor__no_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.properties.source?.state.node.output.lower_bound_cardinality()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.lower_bound_cardinality_support()).result;
					return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tno__has_predecessor__branches, public parent:Cbranches__union) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_predecessor__branches = {
};
export class Cyes__has_predecessor__branches extends AlanNode {
	public readonly output:{
		dependency_location_support: () => application.Crecursion_dependence;
	} = {
		dependency_location_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.collection())
			.then(context => context?.parent)
			.then(context => context?.properties.recursion)
			.then(context => context?.component_root.output.location()).result!)
	}
	constructor(init:Tyes__has_predecessor__branches, public parent:Cbranches__union) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_successor__branches = {
};
export class Cno__has_successor__branches extends AlanNode {
	constructor(init:Tno__has_successor__branches, public parent:Cbranches__union) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__branches = {
};
export class Cyes__has_successor__branches extends AlanNode {
	constructor(init:Tyes__has_successor__branches, public parent:Cbranches__union) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdependency__source = {
	'dependency':Tdependency_step;
	'path':Tplural_object_path;
};
export class Cdependency__source extends AlanNode {
	public readonly properties:{
		readonly dependency:Cdependency_step & { readonly inferences: {
			_inspect_dependency_step_dependency: () => application.Cdependency__application;
			dependency: () => application.Cdependency__application;
			dependency_step: () => application.Cdependency_definition;
			entry_step: () => application.Centry_step_definition;
			node: () => application.Cnode__application;
			node_type: () => application.Cnode__application;
			unified_dependency: () => application.Cdependency__application;
		} },
		readonly path:Cplural_object_path & { readonly inferences: {
			node: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
	} = {
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.dependency)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.existence())
			.then(context => context?.component_root.output.lower_bound_cardinality())
			.then(context => {
				switch (context?.variant.name) {
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.lower_bound_cardinality()).result;
					}
					case 'zero': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Clower_bound_cardinality.Pzero).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.dependency)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tdependency__source, public parent:Cbranches__union) {
		super();
		const $this = this;
		this.properties = {
			dependency: new Cdependency__source.Ddependency(init['dependency'], $this),
			path: new Cdependency__source.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'dependency'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdependency_inversion = {
	'reference set path':Tvariablized_object_path;
	'subset path':Treference_set_subset_step;
};
export class Cdependency_inversion extends AlanNode {
	public readonly properties:{
		readonly reference_set_path:Cvariablized_object_path & { readonly inferences: {
			reference_set: () => application.Creference_set__type__attributes;
		} },
		readonly subset_path:Creference_set_subset_step & { readonly inferences: {
			dependency_required_by_key: () => application.Cdependency__application;
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
	} = {
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.subset_path)
			.then(context => context?.component_root.output.lower_bound_cardinality()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.subset_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tdependency_inversion, public parent:Cbranches__union) {
		super();
		const $this = this;
		this.properties = {
			reference_set_path: new Cdependency_inversion.Dreference_set_path(init['reference set path'], $this),
			subset_path: new Cdependency_inversion.Dsubset_path(init['subset path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'dependency inversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Tplural_object_path_tail = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__plural_object_path_tail];
};
export class Cplural_object_path_tail extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cplural_object_path_tail.Dhas_steps
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		dependency_gen: () => application.Coptional_dependency_gen;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.context()).result!),
		dependency_gen: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.dependency_gen()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.upper_bound_cardinality()).result!)
	};
	constructor(init:Tplural_object_path_tail, public location:AlanNode, public input: {
		context: () => application.Cnavigation_context,
		dependency_gen: () => application.Coptional_dependency_gen,
		dependency_location_support: () => application.Crecursion_dependence,
		lower_bound_cardinality_support: () => application.Clower_bound_cardinality,
		parent_navigation_support: () => application.Cnavigation_support,
		phase: () => application.Ccomputation_phase,
		reference_navigation_support: () => application.Cnavigation_support,
		target_root_location: () => application.Croot_location,
		upper_bound_cardinality_support: () => application.Cupper_bound_cardinality,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cplural_object_path_tail.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__plural_object_path_tail = {
};
export class Cno__has_steps__plural_object_path_tail extends AlanNode {
	public readonly output:{
		context: () => application.Cnavigation_context;
		dependency_gen: () => application.Coptional_dependency_gen;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!),
		dependency_gen: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.dependency_gen()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cupper_bound_cardinality.Pone).result!)
	}
	constructor(init:Tno__has_steps__plural_object_path_tail, public parent:Cplural_object_path_tail) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__plural_object_path_tail = {
	'path':Tobject_path_tail;
	'tail':Tplural_object_path_tail;
};
export class Cyes__has_steps__plural_object_path_tail extends AlanNode {
	public readonly properties:{
		readonly path:Cobject_path_tail,
		readonly tail:Cplural_object_path_tail
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		dependency_gen: () => application.Coptional_dependency_gen;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context()).result!),
		dependency_gen: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.dependency_gen()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.component_root.output.upper_bound_cardinality())
			.then(context => {
				switch (context?.variant.name) {
					case 'many': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.inferences.collection())
							.then(context => context?.component_root.output.upper_bound_cardinality()).result;
					}
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.properties.tail)
							.then(context => context?.component_root.output.upper_bound_cardinality()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection_object,
		dependency_gen: () => application.Coptional_dependency_gen,
		entry_location: () => application.Crelative_object_location,
		lower_bound_cardinality: () => application.Clower_bound_cardinality
	} = {
		collection: cache((detach:boolean) => {
			const application__plural_object_path_tail__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined).result!;
		})
		,
		dependency_gen: cache((detach:boolean) => {
			const application__plural_object_path_tail__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => context?.component_root.output.source_collection())
				.then(context => {
					switch (context?.variant.name) {
						case 'collection property': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.dependency_gen()).result;
						}
						case 'reference set': {
							const application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set_nval = context.cast('reference set');
							return resolve(context)
								.then(context => application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set_nval)
								.then(context => context?.properties.value_source)
								.then(context => {
									const state_group = context;
									switch (state_group?.state.name) {
										case 'reference': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => application.Coptional_dependency_gen.Pnone).result;
										}
										case 'union branch': {
											const application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set___union_branch_nval = state_group.cast('union branch');
											return resolve(context).then(context => new Cdependency_gen({
												dependency: () => resolve(this.parent)
													.then(context => evaluate__inline_dependency_chain(detach, 
														resolve(context)
															.then(context => application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set___union_branch_nval)
															.then(context => context?.properties.branch?.ref)
															.then(context => context?.properties.dependency)
															.then(context => context?.component_root.output.context())
															.then(context => context?.component_root.output.dependency()).result!, {
														target: () => resolve(this.parent)
															.then(context => evaluate__eliminate_dependencies(detach, 
																resolve(context)
																	.then(context => application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set___union_branch_nval)
																	.then(context => context?.properties.branch?.ref)
																	.then(context => context?.properties.dependency)
																	.then(context => context?.component_root.output.context())
																	.then(context => context?.component_root.output.dependency())
																	.then(context => context?.component_root.output.location())
																	.then(context => context?.component_root.output.entity()).result!, {
																chain: () => resolve(this.parent)
																	.then(() => this.parent)
																	.then(context => context?.component_root.input.context())
																	.then(context => context?.component_root.output.dependency()).result!

																})).result!

														})).result!, 
												gen: () => resolve(this.parent)
													.then(context => evaluate__inline_dependency_chain(detach, 
														resolve(context)
															.then(context => application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set___union_branch_nval)
															.then(context => context?.properties.branch?.ref)
															.then(context => context?.component_root.output.collection())
															.then(context => context?.inferences.key_reference())
															.then(context => context?.properties.expression)
															.then(context => context?.component_root.output.step_dependency()).result!, {
														target: () => resolve(this.parent)
															.then(context => evaluate__eliminate_dependencies(detach, 
																resolve(context)
																	.then(context => application__plural_object_path_tail__has_steps__yes_inf___dependency_gen___reference_set___union_branch_nval)
																	.then(context => context?.properties.branch?.ref)
																	.then(context => context?.component_root.output.collection())
																	.then(context => context?.inferences.key_reference())
																	.then(context => context?.properties.expression)
																	.then(context => context?.component_root.output.location())
																	.then(context => context?.component_root.output.entity()).result!, {
																chain: () => resolve(this.parent)
																	.then(() => this.parent)
																	.then(context => context?.component_root.input.context())
																	.then(context => context?.component_root.output.dependency()).result!

																})).result!

														})).result!}), )
												.then(context => context?.definitions.optional_dependency_gen).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
									};}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		entry_location: cache((detach:boolean) => {
			const application__plural_object_path_tail__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(context => evaluate__merge_locations(detach, 
					resolve(context)
						.then(() => this.parent)
						.then(context => context?.root).result!, {
					context_location: () => resolve(this.parent)
						.then(() => this.parent)
						.then(context => context?.component_root.input.context())
						.then(context => context?.component_root.output.location()).result!
					, 
					dependency_location_support: () => resolve(this.parent)
						.then(() => this.parent)
						.then(context => context?.component_root.input.dependency_location_support()).result!
					, 
					dependency_root_location: () => resolve(this.parent)
						.then(() => this.inferences.collection())
						.then(context => context?.component_root.output.source_collection())
						.then(context => context?.component_root.output.root_location()).result!
					, 
					reference_context_location: () => resolve(this.parent)
						.then(() => this.inferences.collection())
						.then(context => context?.component_root.output.value_location()).result!
					, 
					target_root_location: () => resolve(this.parent)
						.then(() => this.parent)
						.then(context => context?.component_root.input.target_root_location()).result!

					})).result!;
		})
		,
		lower_bound_cardinality: cache((detach:boolean) => {
			const application__plural_object_path_tail__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.inferences.collection())
						.then(context => context?.component_root.output.lower_bound_cardinality()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.lower_bound_cardinality_support()).result;
					return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tyes__has_steps__plural_object_path_tail, public parent:Cplural_object_path_tail) {
		super();
		const $this = this;
		this.properties = {
			path: new Cyes__has_steps__plural_object_path_tail.Dpath(init['path'], $this),
			tail: new Cyes__has_steps__plural_object_path_tail.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tplural_object_path = {
	'head':Tvariablized_object_path;
	'path':Tplural_object_path_tail;
};
export class Cplural_object_path extends AlanNode {
	public readonly properties:{
		readonly head:Cvariablized_object_path,
		readonly path:Cplural_object_path_tail
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		dependency_gen: () => application.Coptional_dependency_gen;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context()).result!),
		dependency_gen: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.dependency_gen()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.existence())
			.then(context => context?.component_root.output.lower_bound_cardinality()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.upper_bound_cardinality()).result!)
	};
	constructor(init:Tplural_object_path, public location:AlanNode, public input: {
		dependency_location_support: () => application.Crecursion_dependence,
		direction_support: () => application.Cdirection,
		lower_bound_cardinality_support: () => application.Clower_bound_cardinality,
		modifier: () => application.Cvalue_source,
		parent_navigation_support_for_head: () => application.Cnavigation_support,
		parent_navigation_support_for_path: () => application.Cnavigation_support,
		this_property: () => application.Cproperty__type__attributes,
		upper_bound_cardinality_support: () => application.Cupper_bound_cardinality,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			head: new Cplural_object_path.Dhead(init['head'], $this),
			path: new Cplural_object_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tplural_descendant_node_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__plural_descendant_node_path];
};
export class Cplural_descendant_node_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cplural_descendant_node_path.Dhas_steps
	};
	public readonly output:{
		context: () => application.Cnode_navigation_context;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.context()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.lower_bound_cardinality()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.upper_bound_cardinality()).result!)
	};
	constructor(init:Tplural_descendant_node_path, public location:AlanNode, public input: {
		context: () => application.Cnode_navigation_context,
		dependency_location_support: () => application.Crecursion_dependence,
		lower_bound_cardinality: () => application.Clower_bound_cardinality,
		phase: () => application.Ccomputation_phase,
		target_root_location: () => application.Croot_location,
		upper_bound_cardinality: () => application.Cupper_bound_cardinality,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cplural_descendant_node_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__plural_descendant_node_path = {
};
export class Cno__has_steps__plural_descendant_node_path extends AlanNode {
	public readonly output:{
		context: () => application.Cnode_navigation_context;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.lower_bound_cardinality()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.upper_bound_cardinality()).result!)
	}
	constructor(init:Tno__has_steps__plural_descendant_node_path, public parent:Cplural_descendant_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__plural_descendant_node_path = {
	'property':Tproperty_step;
	'tail':Tplural_descendant_node_path;
	'value type':['choice', Tchoice__value_type__yes__has_steps__plural_descendant_node_path]|'collection'|['collection', {}]|'node'|['node', {}];
};
export class Cyes__has_steps__plural_descendant_node_path extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty_step,
		readonly tail:Cplural_descendant_node_path,
		readonly value_type:Cyes__has_steps__plural_descendant_node_path.Dvalue_type
	};
	public readonly output:{
		context: () => application.Cnode_navigation_context;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.lower_bound_cardinality()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.upper_bound_cardinality()).result!)
	}
	constructor(init:Tyes__has_steps__plural_descendant_node_path, public parent:Cplural_descendant_node_path) {
		super();
		const $this = this;
		this.properties = {
			property: new Cyes__has_steps__plural_descendant_node_path.Dproperty(init['property'], $this),
			tail: new Cyes__has_steps__plural_descendant_node_path.Dtail(init['tail'], $this),
			value_type: new Cyes__has_steps__plural_descendant_node_path.Dvalue_type(init['value type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tchoice__value_type__yes__has_steps__plural_descendant_node_path = {
	'state':string;
};
export class Cchoice__value_type__yes__has_steps__plural_descendant_node_path extends AlanNode {
	public readonly properties:{
		readonly state:Cchoice__value_type__yes__has_steps__plural_descendant_node_path.Dstate
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		node_type: () => application.Cnode__application;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.dependency()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pzero).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.upper_bound_cardinality()).result!)
	}
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const application__plural_descendant_node_path__has_steps__yes__value_type__choice_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property)
				.then(context => context?.component_root.output.property())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tchoice__value_type__yes__has_steps__plural_descendant_node_path, public parent:Cyes__has_steps__plural_descendant_node_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cchoice__value_type__yes__has_steps__plural_descendant_node_path.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'choice'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__value_type__yes__has_steps__plural_descendant_node_path = {
};
export class Ccollection__value_type__yes__has_steps__plural_descendant_node_path extends AlanNode {
	public readonly output:{
		dependency: () => application.Cdependency__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		node_type: () => application.Cnode__application;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cdependency_definition({name: 'collection step', definition: conv_context},{
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.inferences.collection())
					.then(context => context?.definitions.entity)
					.then(context => context?.definitions.relative_object_location).result!
				, 
				parent: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.dependency()).result!

				});
			})
			.then(context => context?.definitions.dependency).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.lower_bound_cardinality())
			.then(context => {
				switch (context?.variant.name) {
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.inferences.collection())
							.then(context => context?.properties.cardinality_constraint)
							.then(context => context?.component_root.output.cardinality()).result;
					}
					case 'zero': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.lower_bound_cardinality()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.node).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cupper_bound_cardinality.Pmany).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property,
		valid_key_value_source: () => application.Cvalue_source
	} = {
		collection: cache((detach:boolean) => {
			const application__plural_descendant_node_path__has_steps__yes__value_type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property)
				.then(context => context?.component_root.output.property())
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		valid_key_value_source: cache((detach:boolean) => {
			const application__plural_descendant_node_path__has_steps__yes__value_type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => context?.properties.type?.state.node.output.key_value_source())
				.then(context => context?.properties.type?.state.node.output.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.definitions.value_source).result!;
		})

	}
	constructor(init:Tcollection__value_type__yes__has_steps__plural_descendant_node_path, public parent:Cyes__has_steps__plural_descendant_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__value_type__yes__has_steps__plural_descendant_node_path = {
};
export class Cnode__value_type__yes__has_steps__plural_descendant_node_path extends AlanNode {
	public readonly output:{
		dependency: () => application.Cdependency__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		node_type: () => application.Cnode__application;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.dependency()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.lower_bound_cardinality()).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.group())
			.then(context => context?.properties.node).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.upper_bound_cardinality()).result!)
	}
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const application__plural_descendant_node_path__has_steps__yes__value_type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property)
				.then(context => context?.component_root.output.property())
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode__value_type__yes__has_steps__plural_descendant_node_path, public parent:Cyes__has_steps__plural_descendant_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpermission = {
	'source':['imported interface', Timported_interface]|['user', Tuser__source];
};
export class Cpermission extends AlanNode {
	public readonly properties:{
		readonly source:Cpermission.Dsource
	};
	public readonly output:{
		source: () => application.Cexternal_source;
	} = {
		source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.source?.state.node.output.source()).result!)
	};
	constructor(init:Tpermission, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			source: new Cpermission.Dsource(init['source'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Timported_interface = {
	'interface':string;
};
export class Cimported_interface extends AlanNode {
	public readonly properties:{
		readonly interface_:Cimported_interface.Dinterface_
	};
	public readonly output:{
		source: () => application.Cexternal_source;
	} = {
		source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.interface_?.ref)
			.then(context => context?.definitions.external_source).result!)
	}
	constructor(init:Timported_interface, public parent:Cpermission) {
		super();
		const $this = this;
		this.properties = {
			interface_: new Cimported_interface.Dinterface_(init['interface'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'imported interface'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__source = {
	'requirement':Tuser_requirement;
};
export class Cuser__source extends AlanNode {
	public readonly properties:{
		readonly requirement:Cuser_requirement
	};
	public readonly output:{
		source: () => application.Cexternal_source;
	} = {
		source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cexternal_source.Puser).result!)
	}
	constructor(init:Tuser__source, public parent:Cpermission) {
		super();
		const $this = this;
		this.properties = {
			requirement: new Cuser__source.Drequirement(init['requirement'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpassword_initializer = {
	'initializer':Tcommand_object_expression;
};
export class Cpassword_initializer extends AlanNode {
	public definitions:{
		command_initialization_operation: Ccommand_initialization_operation;
	} = {
		command_initialization_operation: new Ccommand_initialization_operation({name:'password', definition: this})
	}
	public readonly properties:{
		readonly initializer:Ccommand_object_expression
	};
	public readonly output:{
		password: () => application.Cproperty__type__attributes;
		password_status: () => application.Cproperty__type__attributes;
		username: () => application.Cproperty__type__attributes;
	} = {
		password: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.password_property()).result!),
		password_status: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.password_status()).result!),
		username: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.password_collection())
			.then(context => context?.properties.key_property?.ref)
			.then(context => context?.parent).result!)
	};
	constructor(init:Tpassword_initializer, public location:AlanNode, public input: {
		password_collection: () => application.Ccollection__type__property,
		password_property: () => application.Cproperty__type__attributes,
		password_status: () => application.Cproperty__type__attributes
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initializer: new Cpassword_initializer.Dinitializer(init['initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Toptional_variable_assignment = {
	'has assignment':'no'|['no', {}]|['yes', Tyes__has_assignment__optional_variable_assignment];
};
export class Coptional_variable_assignment extends AlanNode {
	public readonly properties:{
		readonly has_assignment:Coptional_variable_assignment.Dhas_assignment
	};
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_assignment?.state.node.output.variable()).result!)
	};
	constructor(init:Toptional_variable_assignment, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application,
		parent_variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_assignment: new Coptional_variable_assignment.Dhas_assignment(init['has assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_assignment__optional_variable_assignment = {
};
export class Cno__has_assignment__optional_variable_assignment extends AlanNode {
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_variable()).result!)
	}
	constructor(init:Tno__has_assignment__optional_variable_assignment, public parent:Coptional_variable_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has assignment'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_assignment__optional_variable_assignment = {
	'assignment':Tvariable_assignment;
};
export class Cyes__has_assignment__optional_variable_assignment extends AlanNode {
	public readonly properties:{
		readonly assignment:Cvariable_assignment
	};
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assignment)
			.then(context => context?.component_root.output.variable()).result!)
	}
	constructor(init:Tyes__has_assignment__optional_variable_assignment, public parent:Coptional_variable_assignment) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cyes__has_assignment__optional_variable_assignment.Dassignment(init['assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has assignment'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Voptional_node_navigation_context = { name: 'node', definition: application.Cnode_navigation_context}|{ name: 'none', definition: (typeof Coptional_node_navigation_context.Pnone)}
export class Coptional_node_navigation_context extends AlanStruct {
	public static Pnone:Coptional_node_navigation_context = new class PrimitiveInstance extends Coptional_node_navigation_context {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_node_navigation_context})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_node_navigation_context) { super(); }
	public cast<K extends Voptional_node_navigation_context['name']>(_variant:K):Extract<Voptional_node_navigation_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_node_navigation_context['name']]:(($:Extract<Voptional_node_navigation_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional node navigation context'`; }
	public is(other:Coptional_node_navigation_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_node_navigation_context = application.Coptional_node_navigation_context;
type Vnamed_object_definer = { name: 'recursion start', definition: application.Cstart}|{ name: 'optional assignment', definition: application.Cyes__has_assignment__optional_named_object_assignment}
export class Cnamed_object_definer extends AlanStruct {
	constructor(
		public readonly variant:Vnamed_object_definer) { super(); }
	public cast<K extends Vnamed_object_definer['name']>(_variant:K):Extract<Vnamed_object_definer, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnamed_object_definer['name']]:(($:Extract<Vnamed_object_definer, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'named object definer'`; }
	public is(other:Cnamed_object_definer):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Toptional_named_object_assignment = {
	'has assignment':'no'|['no', {}]|['yes', Tyes__has_assignment__optional_named_object_assignment];
};
export class Coptional_named_object_assignment extends AlanNode {
	public readonly properties:{
		readonly has_assignment:Coptional_named_object_assignment.Dhas_assignment
	};
	public readonly output:{
		variable: () => application.Cnamed_object;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_assignment?.state.node.output.variable()).result!)
	};
	constructor(init:Toptional_named_object_assignment, public location:AlanNode, public input: {
		context: () => application.Cnavigation_context,
		parent_variable: () => application.Cnamed_object,
		phase: () => application.Ccomputation_phase,
		value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_assignment: new Coptional_named_object_assignment.Dhas_assignment(init['has assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_assignment__optional_named_object_assignment = {
};
export class Cno__has_assignment__optional_named_object_assignment extends AlanNode {
	public readonly output:{
		variable: () => application.Cnamed_object;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_variable()).result!)
	}
	constructor(init:Tno__has_assignment__optional_named_object_assignment, public parent:Coptional_named_object_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has assignment'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_assignment__optional_named_object_assignment = {
	'assignment':Tnamed_object_assignment;
};
export class Cyes__has_assignment__optional_named_object_assignment extends AlanNode {
	public definitions:{
		named_object_definer: Cnamed_object_definer;
	} = {
		named_object_definer: new Cnamed_object_definer({name:'optional assignment', definition: this})
	}
	public readonly properties:{
		readonly assignment:Cnamed_object_assignment
	};
	public readonly output:{
		variable: () => application.Cnamed_object;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assignment)
			.then(context => context?.component_root.output.variable()).result!)
	}
	constructor(init:Tyes__has_assignment__optional_named_object_assignment, public parent:Coptional_named_object_assignment) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cyes__has_assignment__optional_named_object_assignment.Dassignment(init['assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has assignment'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Voptional_graph_traversal = { name: 'none', definition: application.Centity__application}|{ name: 'graph', definition: application.Cgraph_traversal}
export class Coptional_graph_traversal extends AlanStruct {
	constructor(
		public readonly variant:Voptional_graph_traversal) { super(); }
	public cast<K extends Voptional_graph_traversal['name']>(_variant:K):Extract<Voptional_graph_traversal, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_graph_traversal['name']]:(($:Extract<Voptional_graph_traversal, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional graph traversal'`; }
	public is(other:Coptional_graph_traversal):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Voptional_dependency_gen = { name: 'gen', definition: application.Cdependency_gen}|{ name: 'none', definition: (typeof Coptional_dependency_gen.Pnone)}
export class Coptional_dependency_gen extends AlanStruct {
	public static Pnone:Coptional_dependency_gen = new class PrimitiveInstance extends Coptional_dependency_gen {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_dependency_gen})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_dependency_gen) { super(); }
	public cast<K extends Voptional_dependency_gen['name']>(_variant:K):Extract<Voptional_dependency_gen, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_dependency_gen['name']]:(($:Extract<Voptional_dependency_gen, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional dependency gen'`; }
	public is(other:Coptional_dependency_gen):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_dependency_gen = application.Coptional_dependency_gen;
type Voperation = { name: 'action', definition: application.Caction}|{ name: 'command', definition: application.Ccommand__type}
export class Coperation extends AlanStruct {
	constructor(
		public readonly variant:Voperation) { super(); }
	public definitions:{
		root_location: Croot_location;
	} = {
		root_location: new Croot_location({name:'operation', definition: this})
	}
	public readonly output:{
		attribute: () => application.Cattributes;
		root_node_type: () => application.Cnode__application;
	} = {
		attribute: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'action': {
						const application__operation_out___attribute___action_nval = context.cast('action');
						return resolve(context)
							.then(context => application__operation_out___attribute___action_nval)
							.then(context => context?.parent).result;
					}
					case 'command': {
						const application__operation_out___attribute___command_nval = context.cast('command');
						return resolve(context)
							.then(context => application__operation_out___attribute___command_nval)
							.then(context => context?.parent).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		root_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'action': {
						const application__operation_out___root_node_type___action_nval = context.cast('action');
						return resolve(context)
							.then(context => application__operation_out___root_node_type___action_nval)
							.then(context => context?.properties.parameters).result;
					}
					case 'command': {
						const application__operation_out___root_node_type___command_nval = context.cast('command');
						return resolve(context)
							.then(context => application__operation_out___root_node_type___command_nval)
							.then(context => context?.properties.parameters).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Voperation['name']>(_variant:K):Extract<Voperation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voperation['name']]:(($:Extract<Voperation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'operation'`; }
	public is(other:Coperation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tobject_set_path = {
	'collection path':Tsingular_variablized_object_path;
	'value path':Tobject_path_tail;
};
export class Cobject_set_path extends AlanNode {
	public readonly properties:{
		readonly collection_path:Csingular_variablized_object_path & { readonly inferences: {
			cardinality: () => application.Clower_bound_cardinality;
			collection: () => application.Ccollection_object;
		} },
		readonly value_path:Cobject_path_tail & { readonly inferences: {
			lower_bound_cardinality: () => application.Clower_bound_cardinality;
		} }
	};
	public readonly output:{
		collection: () => application.Ccollection_object;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		object_context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection_path?.inferences.collection()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_path?.inferences.lower_bound_cardinality()).result!),
		object_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_path)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_path)
			.then(context => context?.component_root.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection_path?.inferences.collection())
			.then(context => context?.component_root.output.upper_bound_cardinality()).result!)
	};
	constructor(init:Tobject_set_path, public location:AlanNode, public input: {
		lower_bound_cardinality_support: () => application.Clower_bound_cardinality,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			collection_path: new Cobject_set_path.Dcollection_path(init['collection path'], $this),
			value_path: new Cobject_set_path.Dvalue_path(init['value path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tobject_path_tail = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__object_path_tail];
};
export class Cobject_path_tail extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cobject_path_tail.Dhas_steps
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.phase()).result!)
	};
	constructor(init:Tobject_path_tail, public location:AlanNode, public input: {
		context: () => application.Cnavigation_context,
		dependency_location_support: () => application.Crecursion_dependence,
		existence_support: () => application.Cexistence__application,
		parent_navigation_support: () => application.Cnavigation_support,
		phase: () => application.Ccomputation_phase,
		reference_navigation_support: () => application.Cnavigation_support,
		target_root_location: () => application.Croot_location,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cobject_path_tail.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__object_path_tail = {
};
export class Cno__has_steps__object_path_tail extends AlanNode {
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!)
	}
	constructor(init:Tno__has_steps__object_path_tail, public parent:Cobject_path_tail) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__object_path_tail = {
	'tail':Tobject_path_tail;
	'type':['dependency', Tdependency__type]|'parent'|['parent', {}]|['property value', Tproperty_value]|['reference set', Treference_set__type__yes]|['state', Tstate__type__yes];
};
export class Cyes__has_steps__object_path_tail extends AlanNode {
	public readonly properties:{
		readonly tail:Cobject_path_tail,
		readonly type:Cyes__has_steps__object_path_tail.Dtype
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tyes__has_steps__object_path_tail, public parent:Cobject_path_tail) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__object_path_tail.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__object_path_tail.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdependency__type = {
	'dependency':Tdependency_step;
};
export class Cdependency__type extends AlanNode {
	public readonly properties:{
		readonly dependency:Cdependency_step
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.dependency)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.dependency)
			.then(context => context?.component_root.output.phase()).result!)
	}
	public readonly inferences:{
		node_context: () => application.Cnode_navigation_context,
		reference_navigation_support: () => application.Cunbounded__navigation_support
	} = {
		node_context: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__dependency_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.node_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
		})
		,
		reference_navigation_support: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__dependency_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.reference_navigation_support())
				.then(context => context?.variant.name === 'unbounded' ? context.variant.definition as application.Cunbounded__navigation_support : undefined).result!;
		})

	}
	constructor(init:Tdependency__type, public parent:Cyes__has_steps__object_path_tail) {
		super();
		const $this = this;
		this.properties = {
			dependency: new Cdependency__type.Ddependency(init['dependency'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dependency'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent__type__yes = {
};
export class Cparent__type__yes extends AlanNode {
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent_context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!)
	}
	public readonly inferences:{
		context_entity: () => application.Centity__application,
		node_context: () => application.Cnode_navigation_context,
		parent: () => application.Cnode_navigation_context,
		parent_context: () => application.Cnavigation_context,
		parent_navigation_support: () => application.Cnavigation_support
	} = {
		context_entity: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.dependency())
				.then(context => {
					switch (context?.variant.name) {
						case 'self': {
							return resolve(context)
								.then(() => this.inferences.node_context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.entity()).result;
						}
						case 'step': {
							const application__object_path_tail__has_steps__yes__type__parent_inf___context_entity___step_nval = context.cast('step');
							return resolve(context)
								.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___context_entity___step_nval)
								.then(context => {
									switch (context?.variant.name) {
										case 'collection step': {
											return resolve(context)
												.then(() => this.inferences.parent())
												.then(context => context?.component_root.output.node())
												.then(context => context?.component_root.output.entity()).result;
										}
										case 'entry step': {
											return resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(() => this.inferences.parent())
														.then(context => context?.component_root.output.node())
														.then(context => context?.component_root.output.entity()).result;
													const right = resolve(context)
														.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___context_entity___step_nval)
														.then(context => context?.component_root.output.entity()).result;
													return left?.is(right!) ? left : undefined
												}).result;
										}
										case 'inverse reference step': {
											return resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(() => this.inferences.parent())
														.then(context => context?.component_root.output.node())
														.then(context => context?.component_root.output.entity()).result;
													const right = resolve(context)
														.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___context_entity___step_nval)
														.then(context => context?.component_root.output.entity()).result;
													return left?.is(right!) ? left : undefined
												}).result;
										}
										case 'recursion start step': {
											return resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(() => this.inferences.parent())
														.then(context => context?.component_root.output.node())
														.then(context => context?.component_root.output.entity()).result;
													const right = resolve(context)
														.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___context_entity___step_nval)
														.then(context => context?.component_root.output.entity()).result;
													return left?.is(right!) ? left : undefined
												}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		node_context: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.node_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
		})
		,
		parent: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.parent_context())
				.then(context => context?.component_root.output.node_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
		})
		,
		parent_context: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.node_context())
				.then(context => {
					switch (context?.variant.name) {
						case 'attribute': {
							const application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___attribute_nval = context.cast('attribute');
							return resolve(context)
								.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___attribute_nval)
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.attribute())
								.then(context => context?.variant.name === 'attribute' ? context.variant.definition as application.Cattributes : undefined)
								.then(context => {
									const conv_context = resolve(context).result!;
									const application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___attribute_vcon_nval = context;
									return new Cnavigation_context({name: 'attribute', definition: conv_context},{
									dependency: () => resolve(this.parent)
										.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___attribute_vcon_nval)
										.then(context => context?.component_root.output.entity())
										.then(context => context?.definitions.dependency).result!
									, 
									direction: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.direction()).result!
									, 
									existence: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.existence()).result!
									, 
									location: () => resolve(this.parent)
										.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___attribute_vcon_nval)
										.then(context => context?.component_root.output.entity())
										.then(context => context?.definitions.relative_object_location).result!
									, 
									value_source_dependence: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.value_source_dependence()).result!

									});
								}).result;
						}
						case 'node': {
							const application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node_nval = context.cast('node');
							return resolve(context)
								.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node_nval)
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.node())
								.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined)
								.then(context => {
									const conv_context = resolve(context)
										.then(context => context?.definitions.value_object).result!;
									const application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node_vcon_nval = context;
									return new Cnavigation_context({name: 'value', definition: conv_context},{
									dependency: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.dependency())
										.then(context => {
											switch (context?.variant.name) {
												case 'self': {
													const application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node___dependency___self_nval = context.cast('self');
													return resolve(context)
														.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node___dependency___self_nval)
														.then(context => context?.definitions.dependency).result;
												}
												case 'step': {
													const application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node___dependency___step_nval = context.cast('step');
													return resolve(context)
														.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node___dependency___step_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'collection step': {
																	return resolve(context)
																		.then(match_context => { 
																			const expression_context = resolve(match_context)
																			.then(context => {
																				const left = resolve(context)
																					.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node_vcon_nval)
																					.then(context => context?.component_root.output.entity()).result;
																				const right = resolve(context)
																					.then(() => this.inferences.node_context())
																					.then(context => context?.component_root.output.node())
																					.then(context => context?.component_root.output.entity()).result;
																				return left?.is(right!) ? left : undefined
																			}).result;
																			if (expression_context !== undefined) {
																			return resolve(match_context)
																			.then(() => this.parent)
																			.then(context => context?.component_root.input.context())
																			.then(context => context?.component_root.output.dependency()).result
																			} else {
																				return resolve(match_context)
																				.then(context => application__object_path_tail__has_steps__yes__type__parent_inf___parent_context___node___dependency___step_nval)
																				.then(context => context?.component_root.output.parent()).result
																			}
																		}).result;
																}
																case 'entry step': {
																	return resolve(context)
																		.then(() => this.parent)
																		.then(context => context?.component_root.input.context())
																		.then(context => context?.component_root.output.dependency()).result;
																}
																case 'inverse reference step': {
																	return resolve(context)
																		.then(() => this.parent)
																		.then(context => context?.component_root.input.context())
																		.then(context => context?.component_root.output.dependency()).result;
																}
																case 'recursion start step': {
																	return resolve(context)
																		.then(() => this.parent)
																		.then(context => context?.component_root.input.context())
																		.then(context => context?.component_root.output.dependency()).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result!
									, 
									direction: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.direction()).result!
									, 
									existence: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.existence()).result!
									, 
									location: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.location()).result!
									, 
									value_source_dependence: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.context())
										.then(context => context?.component_root.output.value_source_dependence()).result!

									});
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		parent_navigation_support: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.parent_navigation_support()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(() => application.Cnavigation_support.Pentity).result;
					return left!.variant.widening_index <= right.variant.widening_index ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tparent__type__yes, public parent:Cyes__has_steps__object_path_tail) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty_value = {
	'property':Tproperty_step;
};
export class Cproperty_value extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty_step
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property)
			.then(context => context?.component_root.output.property())
			.then(context => context?.properties.type?.state.node.output.value())
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.dependency()).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.existence()).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.location()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(context => evaluate__step_value_source_dependence(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!, {
						step_value_source_dependence: () => resolve(this)
							.then(() => this)
							.then(context => context?.properties.property)
							.then(context => context?.component_root.output.property())
							.then(context => context?.properties.type?.state.node.output.value_source()).result!

						})).result!

				});
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property)
			.then(context => context?.component_root.output.phase()).result!)
	}
	public readonly inferences:{
		node_context: () => application.Cnode_navigation_context
	} = {
		node_context: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__property_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.node_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
		})

	}
	constructor(init:Tproperty_value, public parent:Cyes__has_steps__object_path_tail) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty_value.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property value'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_set__type__yes = {
	'step':Treference_set_step;
};
export class Creference_set__type__yes extends AlanNode {
	public readonly properties:{
		readonly step:Creference_set_step
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.step)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.step)
			.then(context => context?.component_root.output.phase()).result!)
	}
	public readonly inferences:{
		node_context: () => application.Cnode_navigation_context,
		reference_navigation_support: () => application.Cunbounded__navigation_support
	} = {
		node_context: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__reference_set_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.node_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
		})
		,
		reference_navigation_support: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__reference_set_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.reference_navigation_support())
				.then(context => context?.variant.name === 'unbounded' ? context.variant.definition as application.Cunbounded__navigation_support : undefined).result!;
		})

	}
	constructor(init:Treference_set__type__yes, public parent:Cyes__has_steps__object_path_tail) {
		super();
		const $this = this;
		this.properties = {
			step: new Creference_set__type__yes.Dstep(init['step'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference set'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes = {
	'state':string;
};
export class Cstate__type__yes extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes.Dstate
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node)
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.dependency()).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(() => application.Cexistence__application.Poptional).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.location()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.value_source_dependence()).result!

				});
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!)
	}
	public readonly inferences:{
		optional_existence_support: () => application.Coptional__existence,
		state_group: () => application.Cstate_group
	} = {
		optional_existence_support: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})
		,
		state_group: cache((detach:boolean) => {
			const application__object_path_tail__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'value' ? context.variant.definition as application.Cvalue_object : undefined)
				.then(context => context?.variant.name === 'choice' ? context.variant.definition as application.Cstate_group : undefined).result!;
		})

	}
	constructor(init:Tstate__type__yes, public parent:Cyes__has_steps__object_path_tail) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
type Vnumerical_type_requirement = { name: 'known', definition: application.Cnumerical_types}|{ name: 'unknown', definition: (typeof Cnumerical_type_requirement.Punknown)}
export class Cnumerical_type_requirement extends AlanStruct {
	public static Punknown:Cnumerical_type_requirement = new class PrimitiveInstance extends Cnumerical_type_requirement {
		constructor () {
			super({name: 'unknown', definition: undefined as unknown as Cnumerical_type_requirement})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnumerical_type_requirement) { super(); }
	public cast<K extends Vnumerical_type_requirement['name']>(_variant:K):Extract<Vnumerical_type_requirement, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnumerical_type_requirement['name']]:(($:Extract<Vnumerical_type_requirement, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'numerical type requirement'`; }
	public is(other:Cnumerical_type_requirement):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cunknown = application.Cnumerical_type_requirement;
type Vnumber_value = { name: 'compound number', definition: application.Cnumber__application}|{ name: 'number property', definition: application.Cnumber__type__property}
export class Cnumber_value extends AlanStruct {
	constructor(
		public readonly variant:Vnumber_value, public input: {
			numerical_type: () => application.Cnumerical_type_requirement,
			set_type: () => application.Cnumber_set_type
		}) { super(); }
	public definitions:{
		scalar_value: Cscalar_value;
	} = {
		scalar_value: new Cscalar_value({name:'number', definition: this})
	}
	public readonly output:{
		numerical_type: () => application.Cnumerical_type_requirement;
		set_type: () => application.Cnumber_set_type;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.numerical_type()).result!),
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.set_type()).result!)
	};
	public cast<K extends Vnumber_value['name']>(_variant:K):Extract<Vnumber_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnumber_value['name']]:(($:Extract<Vnumber_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'number value'`; }
	public is(other:Cnumber_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vnumber_set_type = { name: 'integer', definition: (typeof Cnumber_set_type.Pinteger), widening_index: 0}|{ name: 'positive', definition: (typeof Cnumber_set_type.Ppositive), widening_index: 1}
export class Cnumber_set_type extends AlanStruct {
	public static Pinteger:Cnumber_set_type = new class PrimitiveInstance extends Cnumber_set_type {
		constructor () {
			super({name: 'integer', definition: undefined as unknown as Cnumber_set_type, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Ppositive:Cnumber_set_type = new class PrimitiveInstance extends Cnumber_set_type {
		constructor () {
			super({name: 'positive', definition: undefined as unknown as Cnumber_set_type, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnumber_set_type) { super(); }
	public cast<K extends Vnumber_set_type['name']>(_variant:K):Extract<Vnumber_set_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnumber_set_type['name']]:(($:Extract<Vnumber_set_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'number set type'`; }
	public is(other:Cnumber_set_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cinteger__number_set_type = application.Cnumber_set_type;
export import Cpositive__number_set_type = application.Cnumber_set_type;
export type Tnumber_expression = {
	'type':['aggregate', Taggregate]|['constant', Tconstant__type__number_expression]|['unary', Tunary__type];
};
export class Cnumber_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber_expression.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	};
	constructor(init:Tnumber_expression, public location:AlanNode, public input: {
		support: () => application.Cderivation_expression_support,
		target: () => application.Cnumber_value,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Taggregate = {
	'type':['addition', Taddition]|['collection operation', Tcollection_operation]|['difference', Tdifference]|['division', Tdivision__type]|['list operation', Tlist_operation]|['product', Tproduct__type]|['remainder', Tremainder__type];
};
export class Caggregate extends AlanNode {
	public readonly properties:{
		readonly type:Caggregate.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	}
	public readonly inferences:{
		no_nested_aggregation: () => application.Cunrestricted,
		target_numerical_type: () => application.Cnumerical_types
	} = {
		no_nested_aggregation: cache((detach:boolean) => {
			const application__number_expression__type__aggregate_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.support())
				.then(context => context?.variant.name === 'unrestricted' ? context.variant.definition as application.Cunrestricted : undefined).result!;
		})
		,
		target_numerical_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.numerical_type())
				.then(context => context?.variant.name === 'known' ? context.variant.definition as application.Cnumerical_types : undefined).result!;
		})

	}
	constructor(init:Taggregate, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Caggregate.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'aggregate'`; }
	public get entity() { return this.parent.entity; }
}
export type Taddition = {
	'left':Tunary_number_expression;
	'right':Tunary_number_expression;
};
export class Caddition extends AlanNode {
	public readonly properties:{
		readonly left:Cunary_number_expression,
		readonly right:Cunary_number_expression
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.left)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.right)
					.then(context => context?.component_root.output.computation_phase()).result!

				})).result!)
	}
	public readonly inferences:{
		relative_type: () => application.Cscale
	} = {
		relative_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__addition_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'scale') {
						return resolve(context.properties.type.state.node as application.Cscale).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Taddition, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			left: new Caddition.Dleft(init['left'], $this),
			right: new Caddition.Dright(init['right'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'addition'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_operation = {
	'type':['count', Tcount]|['property', Tproperty__type__collection_operation];
};
export class Ccollection_operation extends AlanNode {
	public readonly properties:{
		readonly type:Ccollection_operation.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	}
	constructor(init:Tcollection_operation, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			type: new Ccollection_operation.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcount = {
	'nodes path':Tobject_set_path;
};
export class Ccount extends AlanNode {
	public readonly properties:{
		readonly nodes_path:Cobject_set_path & { readonly inferences: {
			node: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.nodes_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	public readonly inferences:{
		quantity_type: () => application.Cquantity
	} = {
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__collection_operation__type__count_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcount, public parent:Ccollection_operation) {
		super();
		const $this = this;
		this.properties = {
			nodes_path: new Ccount.Dnodes_path(init['nodes path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'count'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__collection_operation = {
	'numbers path':Tobject_set_path;
	'operation':'maximum'|['maximum', {}]|'minimum'|['minimum', {}]|'standard deviation'|['standard deviation', {}]|'sum'|['sum', {}];
};
export class Cproperty__type__collection_operation extends AlanNode {
	public readonly properties:{
		readonly numbers_path:Cobject_set_path & { readonly inferences: {
			number: () => application.Cnumber_value;
			numerical_type: () => application.Cnumerical_type_requirement;
			set_type: () => application.Cnumber_set_type;
		} },
		readonly operation:Cproperty__type__collection_operation.Doperation
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.numbers_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tproperty__type__collection_operation, public parent:Ccollection_operation) {
		super();
		const $this = this;
		this.properties = {
			numbers_path: new Cproperty__type__collection_operation.Dnumbers_path(init['numbers path'], $this),
			operation: new Cproperty__type__collection_operation.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmaximum__operation__property = {
};
export class Cmaximum__operation__property extends AlanNode {
	public readonly output:{
		required_cardinality: () => application.Clower_bound_cardinality;
	} = {
		required_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pone).result!)
	}
	constructor(init:Tmaximum__operation__property, public parent:Cproperty__type__collection_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'maximum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminimum__operation__property = {
};
export class Cminimum__operation__property extends AlanNode {
	public readonly output:{
		required_cardinality: () => application.Clower_bound_cardinality;
	} = {
		required_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pone).result!)
	}
	constructor(init:Tminimum__operation__property, public parent:Cproperty__type__collection_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'minimum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstandard_deviation = {
};
export class Cstandard_deviation extends AlanNode {
	public readonly output:{
		required_cardinality: () => application.Clower_bound_cardinality;
	} = {
		required_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pone).result!)
	}
	public readonly inferences:{
		expected_set_type: () => application.Cinteger__number_set_type
	} = {
		expected_set_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__collection_operation__type__property__operation__standard_deviation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tstandard_deviation, public parent:Cproperty__type__collection_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'standard deviation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsum__operation__property = {
};
export class Csum__operation__property extends AlanNode {
	public readonly output:{
		required_cardinality: () => application.Clower_bound_cardinality;
	} = {
		required_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pzero).result!)
	}
	public readonly inferences:{
		quantity_type: () => application.Cquantity
	} = {
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__collection_operation__type__property__operation__sum_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tsum__operation__property, public parent:Cproperty__type__collection_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'sum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdifference = {
	'expressions':Texpressions__difference;
	'scale type':string;
};
export class Cdifference extends AlanNode {
	public readonly properties:{
		readonly expressions:Cexpressions__difference,
		readonly scale_type:Cdifference.Dscale_type
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.expressions)
					.then(context => context?.properties.left)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.expressions)
					.then(context => context?.properties.right)
					.then(context => context?.component_root.output.computation_phase()).result!

				})).result!)
	}
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type,
		quantity_type: () => application.Cquantity
	} = {
		integer_target: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__difference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})
		,
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__difference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdifference, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			expressions: new Cexpressions__difference(init['expressions'], $this),
			scale_type: new Cdifference.Dscale_type(init['scale type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'difference'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpressions__difference = {
	'left':Tunary_number_expression;
	'right':Tunary_number_expression;
};
export class Cexpressions__difference extends AlanNode {
	public readonly properties:{
		readonly left:Cunary_number_expression,
		readonly right:Cunary_number_expression
	};
	constructor(init:Texpressions__difference, public parent:Cdifference) {
		super();
		const $this = this;
		this.properties = {
			left: new Cexpressions__difference.Dleft(init['left'], $this),
			right: new Cexpressions__difference.Dright(init['right'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'expressions'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdivision__type = {
	'expressions':Texpressions__division;
	'rounding':'ceil'|['ceil', {}]|'floor'|['floor', {}]|'ordinary'|['ordinary', {}];
};
export class Cdivision__type extends AlanNode {
	public readonly properties:{
		readonly expressions:Cexpressions__division,
		readonly rounding:Cdivision__type.Drounding
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.expressions)
					.then(context => context?.properties.numerator)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.expressions)
					.then(context => context?.properties.denominator)
					.then(context => context?.component_root.output.computation_phase()).result!

				})).result!)
	}
	public readonly inferences:{
		quantity_type: () => application.Cquantity
	} = {
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__division_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tdivision__type, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			expressions: new Cexpressions__division(init['expressions'], $this),
			rounding: new Cdivision__type.Drounding(init['rounding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'division'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpressions__division = {
	'conversion rule':string;
	'denominator':Tunary_number_expression;
	'numerator':Tunary_number_expression;
};
export class Cexpressions__division extends AlanNode {
	public readonly properties:{
		readonly conversion_rule:Cexpressions__division.Dconversion_rule,
		readonly denominator:Cunary_number_expression,
		readonly numerator:Cunary_number_expression
	};
	constructor(init:Texpressions__division, public parent:Cdivision__type) {
		super();
		const $this = this;
		this.properties = {
			conversion_rule: new Cexpressions__division.Dconversion_rule(init['conversion rule'], $this),
			denominator: new Cexpressions__division.Ddenominator(init['denominator'], $this),
			numerator: new Cexpressions__division.Dnumerator(init['numerator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'expressions'`; }
	public get entity() { return this.parent.entity; }
}
export type Tceil__rounding__division__type = {
};
export class Cceil__rounding__division__type extends AlanNode {
	constructor(init:Tceil__rounding__division__type, public parent:Cdivision__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ceil'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfloor__rounding__division__type = {
};
export class Cfloor__rounding__division__type extends AlanNode {
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type
	} = {
		integer_target: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__division__rounding__floor_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tfloor__rounding__division__type, public parent:Cdivision__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'floor'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordinary__rounding__division__type = {
};
export class Cordinary__rounding__division__type extends AlanNode {
	public readonly inferences:{
		integer_target: () => application.Cinteger__number_set_type
	} = {
		integer_target: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__division__rounding__ordinary_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tordinary__rounding__division__type, public parent:Cdivision__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ordinary'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist_operation = {
	'numbers':Tderivation_expression_list;
	'operation':'maximum'|['maximum', {}]|'minimum'|['minimum', {}]|'sum'|['sum', {}];
};
export class Clist_operation extends AlanNode {
	public readonly properties:{
		readonly numbers:Cderivation_expression_list,
		readonly operation:Clist_operation.Doperation
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.numbers)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	constructor(init:Tlist_operation, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			numbers: new Clist_operation.Dnumbers(init['numbers'], $this),
			operation: new Clist_operation.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'list operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmaximum__operation__list_operation = {
};
export class Cmaximum__operation__list_operation extends AlanNode {
	constructor(init:Tmaximum__operation__list_operation, public parent:Clist_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'maximum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminimum__operation__list_operation = {
};
export class Cminimum__operation__list_operation extends AlanNode {
	constructor(init:Tminimum__operation__list_operation, public parent:Clist_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'minimum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsum__operation__list_operation = {
};
export class Csum__operation__list_operation extends AlanNode {
	public readonly inferences:{
		quantity_type: () => application.Cquantity
	} = {
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__list_operation__operation__sum_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tsum__operation__list_operation, public parent:Clist_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'sum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduct__type = {
	'conversion rule':string;
	'left':Tunary_number_expression;
	'right':Tunary_number_expression;
};
export class Cproduct__type extends AlanNode {
	public readonly properties:{
		readonly conversion_rule:Cproduct__type.Dconversion_rule,
		readonly left:Cunary_number_expression,
		readonly right:Cunary_number_expression
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.left)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.right)
					.then(context => context?.component_root.output.computation_phase()).result!

				})).result!)
	}
	public readonly inferences:{
		quantity_type: () => application.Cquantity
	} = {
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__product_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproduct__type, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			conversion_rule: new Cproduct__type.Dconversion_rule(init['conversion rule'], $this),
			left: new Cproduct__type.Dleft(init['left'], $this),
			right: new Cproduct__type.Dright(init['right'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'product'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremainder__type = {
	'denominator':Tunary_number_expression;
	'numerator':Tunary_number_expression;
};
export class Cremainder__type extends AlanNode {
	public readonly properties:{
		readonly denominator:Cunary_number_expression,
		readonly numerator:Cunary_number_expression
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.numerator)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.denominator)
					.then(context => context?.component_root.output.computation_phase()).result!

				})).result!)
	}
	public readonly inferences:{
		integer: () => application.Cinteger__number_set_type,
		quantity_type: () => application.Cquantity
	} = {
		integer: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__remainder_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})
		,
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__aggregate__type__remainder_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tremainder__type, public parent:Caggregate) {
		super();
		const $this = this;
		this.properties = {
			denominator: new Cremainder__type.Ddenominator(init['denominator'], $this),
			numerator: new Cremainder__type.Dnumerator(init['numerator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'remainder'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconstant__type__number_expression = {
	'value':Tconstant_number_value;
};
export class Cconstant__type__number_expression extends AlanNode {
	public readonly properties:{
		readonly value:Cconstant_number_value & { readonly inferences: {
			set_type: () => application.Cnumber_set_type;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	constructor(init:Tconstant__type__number_expression, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cconstant__type__number_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'constant'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunary__type = {
	'expression':Tderivation_expression_tail;
	'type':'absolute value'|['absolute value', {}]|['numerical type conversion', Tnumerical_type_conversion__type__unary]|'sign inversion'|['sign inversion', {}];
};
export class Cunary__type extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression_tail,
		readonly type:Cunary__type.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	constructor(init:Tunary__type, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cunary__type.Dexpression(init['expression'], $this),
			type: new Cunary__type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'unary'`; }
	public get entity() { return this.parent.entity; }
}
export type Tabsolute_value__type__unary = {
};
export class Cabsolute_value__type__unary extends AlanNode {
	public readonly output:{
		numerical_type: () => application.Cnumerical_type_requirement;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.target())
			.then(context => context?.component_root.output.numerical_type()).result!)
	}
	constructor(init:Tabsolute_value__type__unary, public parent:Cunary__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'absolute value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumerical_type_conversion__type__unary = {
	'conversion':string;
};
export class Cnumerical_type_conversion__type__unary extends AlanNode {
	public readonly properties:{
		readonly conversion:Cnumerical_type_conversion__type__unary.Dconversion
	};
	public readonly output:{
		numerical_type: () => application.Cnumerical_type_requirement;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.conversion?.ref)
			.then(context => context?.key?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
			}).result!)
	}
	public readonly inferences:{
		known_target_numerical_type: () => application.Cnumerical_types
	} = {
		known_target_numerical_type: cache((detach:boolean) => {
			const application__number_expression__type__unary__type__numerical_type_conversion_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.numerical_type())
				.then(context => context?.variant.name === 'known' ? context.variant.definition as application.Cnumerical_types : undefined).result!;
		})

	}
	constructor(init:Tnumerical_type_conversion__type__unary, public parent:Cunary__type) {
		super();
		const $this = this;
		this.properties = {
			conversion: new Cnumerical_type_conversion__type__unary.Dconversion(init['conversion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'numerical type conversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsign_inversion__type__unary = {
};
export class Csign_inversion__type__unary extends AlanNode {
	public readonly output:{
		numerical_type: () => application.Cnumerical_type_requirement;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.target())
			.then(context => context?.component_root.output.numerical_type()).result!)
	}
	public readonly inferences:{
		integer: () => application.Cinteger__number_set_type,
		predefined_numerical_type: () => application.Cnumerical_types,
		quantity_type: () => application.Cquantity
	} = {
		integer: cache((detach:boolean) => {
			const application__number_expression__type__unary__type__sign_inversion_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})
		,
		predefined_numerical_type: cache((detach:boolean) => {
			const application__number_expression__type__unary__type__sign_inversion_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.numerical_type())
				.then(context => context?.variant.name === 'known' ? context.variant.definition as application.Cnumerical_types : undefined).result!;
		})
		,
		quantity_type: cache((detach:boolean) => {
			const application__number_expression__type__unary__type__sign_inversion_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.predefined_numerical_type())
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tsign_inversion__type__unary, public parent:Cunary__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sign inversion'`; }
	public get entity() { return this.parent.entity; }
}
type Vnumber_comparison_case = { name: 'defined', definition: application.Ccases__number_switch}|{ name: 'none', definition: (typeof Cnumber_comparison_case.Pnone)}
export class Cnumber_comparison_case extends AlanStruct {
	public static Pnone:Cnumber_comparison_case = new class PrimitiveInstance extends Cnumber_comparison_case {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Cnumber_comparison_case})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnumber_comparison_case) { super(); }
	public cast<K extends Vnumber_comparison_case['name']>(_variant:K):Extract<Vnumber_comparison_case, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnumber_comparison_case['name']]:(($:Extract<Vnumber_comparison_case, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'number comparison case'`; }
	public is(other:Cnumber_comparison_case):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__number_comparison_case = application.Cnumber_comparison_case;
function evaluate__number_checker(detach:boolean, application__number_checker_nval:application.Cnumber_value, input: {
	target: () => application.Cnumber_value
}):application.Cnumber__application {
	const self = { component_root: { input: input } };
	return resolve(self).then(context => new Cnumber__application({
		numerical_type: () => resolve(self)
			.then(context => {
				const left = resolve(context)
					.then(context => application__number_checker_nval)
					.then(context => context?.component_root.output.numerical_type()).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.numerical_type()).result;
				return left?.is(right!) ? left : undefined
			}).result!, 
		set_type: () => resolve(self)
			.then(context => {
				const left = resolve(context)
					.then(context => application__number_checker_nval)
					.then(context => context?.component_root.output.set_type()).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.set_type()).result;
				return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
			}).result!}), ).result!
}
export class Cnumber__application extends AlanStruct {
	constructor(
		public readonly properties: {
			numerical_type: () => application.Cnumerical_type_requirement, 
			set_type: () => application.Cnumber_set_type
		}
	) { super(); }
	public definitions:{
		number_value: Cnumber_value;
	} = {
		number_value: new Cnumber_value({name:'compound number', definition: this}, {
			numerical_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.numerical_type()).result!),
			set_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.set_type()).result!)
		})
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'number'`; }
	public is(other:Cnumber__application):boolean {
		if (!this.properties.numerical_type().is(other.properties.numerical_type())) return false;
		if (!this.properties.set_type().is(other.properties.set_type())) return false;
		return true;
	}
}
function evaluate__node_type_validator(detach:boolean, application__node_type_validator_nval:application.Cnode__application, input: {
	expected: () => application.Cnode__application
}):application.Cnode__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => {
			const left = resolve(context)
				.then(context => application__node_type_validator_nval).result;
			const right = resolve(context)
				.then(() => self)
				.then(context => context?.component_root.input.expected()).result;
			return left?.is(right!) ? left : undefined
		}).result!
}
export type Tnode_type_id_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__node_type_id_path];
};
export class Cnode_type_id_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cnode_type_id_path.Dhas_steps
	};
	public readonly output:{
		context: () => application.Cnode_type_id_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.context()).result!)
	};
	constructor(init:Tnode_type_id_path, public location:AlanNode, public input: {
		context: () => application.Cnode_type_id_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cnode_type_id_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__node_type_id_path = {
};
export class Cno__has_steps__node_type_id_path extends AlanNode {
	public readonly output:{
		context: () => application.Cnode_type_id_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!)
	}
	constructor(init:Tno__has_steps__node_type_id_path, public parent:Cnode_type_id_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__node_type_id_path = {
	'property':string;
	'tail':Tnode_type_id_path;
	'value type':['choice', Tchoice__value_type__yes__has_steps__node_type_id_path]|'collection'|['collection', {}]|'node'|['node', {}];
};
export class Cyes__has_steps__node_type_id_path extends AlanNode {
	public readonly properties:{
		readonly property:Cyes__has_steps__node_type_id_path.Dproperty,
		readonly tail:Cnode_type_id_path,
		readonly value_type:Cyes__has_steps__node_type_id_path.Dvalue_type
	};
	public readonly output:{
		context: () => application.Cnode_type_id_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context()).result!)
	}
	constructor(init:Tyes__has_steps__node_type_id_path, public parent:Cnode_type_id_path) {
		super();
		const $this = this;
		this.properties = {
			property: new Cyes__has_steps__node_type_id_path.Dproperty(init['property'], $this),
			tail: new Cyes__has_steps__node_type_id_path.Dtail(init['tail'], $this),
			value_type: new Cyes__has_steps__node_type_id_path.Dvalue_type(init['value type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tchoice__value_type__yes__has_steps__node_type_id_path = {
	'state':string;
};
export class Cchoice__value_type__yes__has_steps__node_type_id_path extends AlanNode {
	public readonly properties:{
		readonly state:Cchoice__value_type__yes__has_steps__node_type_id_path.Dstate
	};
	public readonly output:{
		dependency: () => application.Cdependency__application;
		node_type: () => application.Cnode__application;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.dependency()).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const application__node_type_id_path__has_steps__yes__value_type__choice_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tchoice__value_type__yes__has_steps__node_type_id_path, public parent:Cyes__has_steps__node_type_id_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cchoice__value_type__yes__has_steps__node_type_id_path.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'choice'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__value_type__yes__has_steps__node_type_id_path = {
};
export class Ccollection__value_type__yes__has_steps__node_type_id_path extends AlanNode {
	public readonly output:{
		dependency: () => application.Cdependency__application;
		node_type: () => application.Cnode__application;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cdependency_definition({name: 'collection step', definition: conv_context},{
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.inferences.collection())
					.then(context => context?.definitions.entity)
					.then(context => context?.definitions.relative_object_location).result!
				, 
				parent: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.dependency()).result!

				});
			})
			.then(context => context?.definitions.dependency).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const application__node_type_id_path__has_steps__yes__value_type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__value_type__yes__has_steps__node_type_id_path, public parent:Cyes__has_steps__node_type_id_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__value_type__yes__has_steps__node_type_id_path = {
};
export class Cnode__value_type__yes__has_steps__node_type_id_path extends AlanNode {
	public readonly output:{
		dependency: () => application.Cdependency__application;
		node_type: () => application.Cnode__application;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.dependency()).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.group())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const application__node_type_id_path__has_steps__yes__value_type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode__value_type__yes__has_steps__node_type_id_path, public parent:Cyes__has_steps__node_type_id_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
type Vnode_type_id_context = { name: 'attribute', definition: application.Cattributes}|{ name: 'node', definition: application.Cnode__application}
export class Cnode_type_id_context extends AlanStruct {
	constructor(
		public readonly variant:Vnode_type_id_context, public input: {
			dependency: () => application.Cdependency__application
		}) { super(); }
	public readonly output:{
		dependency: () => application.Cdependency__application;
		node_type: () => application.Cnode__application;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.dependency()).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'attribute': {
						const application__node_type_id_context_out___node_type___attribute_nval = context.cast('attribute');
						return resolve(context)
							.then(context => application__node_type_id_context_out___node_type___attribute_nval)
							.then(context => context?.parent).result;
					}
					case 'node': {
						const application__node_type_id_context_out___node_type___node_nval = context.cast('node');
						return resolve(context)
							.then(context => application__node_type_id_context_out___node_type___node_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vnode_type_id_context['name']>(_variant:K):Extract<Vnode_type_id_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_type_id_context['name']]:(($:Extract<Vnode_type_id_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node type id context'`; }
	public is(other:Cnode_type_id_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tnode_type_id = {
	'steps':Tnode_type_id_path;
};
export class Cnode_type_id extends AlanNode {
	public readonly properties:{
		readonly steps:Cnode_type_id_path
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.steps)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.node_type()).result!)
	};
	constructor(init:Tnode_type_id, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			steps: new Cnode_type_id.Dsteps(init['steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tnode_step__application = {
	'type':['group', Tgroup__type__node_step]|'parent'|['parent', {}]|['property rule', Tproperty_rule]|['reference', Treference__type__node_step]|['state', Tstate__type__node_step]|['state context rule', Tstate_context_rule__type__node_step];
};
export class Cnode_step__application extends AlanNode {
	public readonly properties:{
		readonly type:Cnode_step__application.Dtype
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.node_type()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.parent_navigation_support()).result!)
	};
	constructor(init:Tnode_step__application, public location:AlanNode, public input: {
		existence_support: () => application.Cexistence__application,
		node_type: () => application.Cnode__application,
		parent_navigation_support: () => application.Cnavigation_support,
		reference_navigation_support: () => application.Cnavigation_support
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnode_step__application.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tgroup__type__node_step = {
	'group':string;
};
export class Cgroup__type__node_step extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__node_step.Dgroup
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_navigation_support()).result!)
	}
	constructor(init:Tgroup__type__node_step, public parent:Cnode_step__application) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__node_step.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent__type__node_step = {
};
export class Cparent__type__node_step extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent_node()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_navigation_support()).result!)
	}
	public readonly inferences:{
		context_entity: () => application.Centity__application,
		parent_navigation_support: () => application.Cnavigation_support,
		parent_node: () => application.Cnode__application
	} = {
		context_entity: cache((detach:boolean) => {
			const application__node_step__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.parent_navigation_support())
				.then(context => {
					switch (context?.variant.name) {
						case 'descendant': {
							return resolve(context)
								.then(() => this.inferences.parent_node())
								.then(context => context?.component_root.output.entity()).result;
						}
						case 'entity': {
							return resolve(context)
								.then(context => {
									const left = resolve(context)
										.then(() => this.inferences.parent_node())
										.then(context => context?.component_root.output.entity()).result;
									const right = resolve(context)
										.then(() => this.parent)
										.then(context => context?.component_root.input.node_type())
										.then(context => context?.component_root.output.entity()).result;
									return left?.is(right!) ? left : undefined
								}).result;
						}
						case 'unbounded': {
							return resolve(context)
								.then(() => this.inferences.parent_node())
								.then(context => context?.component_root.output.entity()).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		parent_navigation_support: cache((detach:boolean) => {
			const application__node_step__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.parent_navigation_support()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(() => application.Cnavigation_support.Pentity).result;
					return left!.variant.widening_index <= right.variant.widening_index ? left : undefined
				}).result!;
		})
		,
		parent_node: cache((detach:boolean) => {
			const application__node_step__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tparent__type__node_step, public parent:Cnode_step__application) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty_rule = {
	'rule':string;
	'text':string;
};
export class Cproperty_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Cproperty_rule.Drule,
		readonly text:Cproperty_rule.Dtext
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnavigation_support.Pentity).result!)
	}
	public readonly inferences:{
		navigation_support: () => application.Cunbounded__navigation_support
	} = {
		navigation_support: cache((detach:boolean) => {
			const application__node_step__type__property_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.reference_navigation_support())
				.then(context => context?.variant.name === 'unbounded' ? context.variant.definition as application.Cunbounded__navigation_support : undefined).result!;
		})

	}
	constructor(init:Tproperty_rule, public parent:Cnode_step__application) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cproperty_rule.Drule(init['rule'], $this),
			text: new Cproperty_rule.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference__type__node_step = {
	'text':string;
};
export class Creference__type__node_step extends AlanNode {
	public readonly properties:{
		readonly text:Creference__type__node_step.Dtext
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.text?.inferences.reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.referenced_node_type()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnavigation_support.Pentity).result!)
	}
	constructor(init:Treference__type__node_step, public parent:Cnode_step__application) {
		super();
		const $this = this;
		this.properties = {
			text: new Creference__type__node_step.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__node_step = {
	'state':string;
	'state group':string;
};
export class Cstate__type__node_step extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__node_step.Dstate,
		readonly state_group:Cstate__type__node_step.Dstate_group
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_navigation_support()).result!)
	}
	public readonly inferences:{
		existence_support: () => application.Coptional__existence
	} = {
		existence_support: cache((detach:boolean) => {
			const application__node_step__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tstate__type__node_step, public parent:Cnode_step__application) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__node_step.Dstate(init['state'], $this),
			state_group: new Cstate__type__node_step.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_context_rule__type__node_step = {
	'rule':string;
};
export class Cstate_context_rule__type__node_step extends AlanNode {
	public readonly properties:{
		readonly rule:Cstate_context_rule__type__node_step.Drule
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnavigation_support.Pentity).result!)
	}
	public readonly inferences:{
		navigation_support: () => application.Cunbounded__navigation_support,
		state: () => application.Cstates__state_group
	} = {
		navigation_support: cache((detach:boolean) => {
			const application__node_step__type__state_context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.reference_navigation_support())
				.then(context => context?.variant.name === 'unbounded' ? context.variant.definition as application.Cunbounded__navigation_support : undefined).result!;
		})
		,
		state: cache((detach:boolean) => {
			const application__node_step__type__state_context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as application.Cstates__state_group : undefined).result!;
		})

	}
	constructor(init:Tstate_context_rule__type__node_step, public parent:Cnode_step__application) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cstate_context_rule__type__node_step.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state context rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_permissions_definition = {
	'read permission':['explicit', Texplicit__read_permission]|'inherited'|['inherited', {}];
	'update permission':['explicit', Texplicit__update_permission]|'inherited'|['inherited', {}];
};
export class Cnode_permissions_definition extends AlanNode {
	public readonly properties:{
		readonly read_permission:Cnode_permissions_definition.Dread_permission,
		readonly update_permission:Cnode_permissions_definition.Dupdate_permission
	};
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.update_permission?.state.node.output.value_source()).result!)
	};
	constructor(init:Tnode_permissions_definition, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application,
		value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			read_permission: new Cnode_permissions_definition.Dread_permission(init['read permission'], $this),
			update_permission: new Cnode_permissions_definition.Dupdate_permission(init['update permission'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texplicit__read_permission = {
	'permission':Tpermission;
};
export class Cexplicit__read_permission extends AlanNode {
	public readonly properties:{
		readonly permission:Cpermission
	};
	public readonly inferences:{
		dataset_node_type: () => application.Capplication
	} = {
		dataset_node_type: cache((detach:boolean) => {
			const application__node_permissions_definition__read_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Texplicit__read_permission, public parent:Cnode_permissions_definition) {
		super();
		const $this = this;
		this.properties = {
			permission: new Cexplicit__read_permission.Dpermission(init['permission'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'read permission'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinherited__read_permission = {
};
export class Cinherited__read_permission extends AlanNode {
	constructor(init:Tinherited__read_permission, public parent:Cnode_permissions_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'read permission'?'inherited'`; }
	public get entity() { return this.parent.entity; }
}
export type Texplicit__update_permission = {
	'permission':Tpermission;
};
export class Cexplicit__update_permission extends AlanNode {
	public readonly properties:{
		readonly permission:Cpermission
	};
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.permission)
			.then(context => context?.component_root.output.source())
			.then(context => context?.definitions.base_value_source)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		dataset_node_type: () => application.Capplication,
		user_value: () => application.Cuser__external_source
	} = {
		dataset_node_type: cache((detach:boolean) => {
			const application__node_permissions_definition__update_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})
		,
		user_value: cache((detach:boolean) => {
			const application__node_permissions_definition__update_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
		})

	}
	constructor(init:Texplicit__update_permission, public parent:Cnode_permissions_definition) {
		super();
		const $this = this;
		this.properties = {
			permission: new Cexplicit__update_permission.Dpermission(init['permission'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'update permission'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinherited__update_permission = {
};
export class Cinherited__update_permission extends AlanNode {
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	}
	constructor(init:Tinherited__update_permission, public parent:Cnode_permissions_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'update permission'?'inherited'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_path_tail = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__node_path_tail];
};
export class Cnode_path_tail extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cnode_path_tail.Dhas_steps
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.node_type()).result!)
	};
	constructor(init:Tnode_path_tail, public location:AlanNode, public input: {
		existence_support: () => application.Cexistence__application,
		node_type: () => application.Cnode__application,
		parent_navigation_support: () => application.Cnavigation_support,
		reference_navigation_support: () => application.Cnavigation_support
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cnode_path_tail.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__node_path_tail = {
};
export class Cno__has_steps__node_path_tail extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node_type()).result!)
	}
	constructor(init:Tno__has_steps__node_path_tail, public parent:Cnode_path_tail) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__node_path_tail = {
	'step':Tnode_step__application;
	'tail':Tnode_path_tail;
};
export class Cyes__has_steps__node_path_tail extends AlanNode {
	public readonly properties:{
		readonly step:Cnode_step__application,
		readonly tail:Cnode_path_tail
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node_type()).result!)
	}
	constructor(init:Tyes__has_steps__node_path_tail, public parent:Cnode_path_tail) {
		super();
		const $this = this;
		this.properties = {
			step: new Cyes__has_steps__node_path_tail.Dstep(init['step'], $this),
			tail: new Cyes__has_steps__node_path_tail.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_path = {
	'head':Tcontext_node_path;
	'tail':Tnode_path_tail;
};
export class Cnode_path extends AlanNode {
	public readonly properties:{
		readonly head:Ccontext_node_path,
		readonly tail:Cnode_path_tail
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node_type()).result!)
	};
	constructor(init:Tnode_path, public location:AlanNode, public input: {
		existence_support: () => application.Cexistence__application,
		modifier: () => application.Cbase_value_source,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			head: new Cnode_path.Dhead(init['head'], $this),
			tail: new Cnode_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vnode_parent_attribute = { name: 'attribute', definition: application.Cattributes}|{ name: 'none', definition: (typeof Cnode_parent_attribute.Pnone)}
export class Cnode_parent_attribute extends AlanStruct {
	public static Pnone:Cnode_parent_attribute = new class PrimitiveInstance extends Cnode_parent_attribute {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Cnode_parent_attribute})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnode_parent_attribute) { super(); }
	public cast<K extends Vnode_parent_attribute['name']>(_variant:K):Extract<Vnode_parent_attribute, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_parent_attribute['name']]:(($:Extract<Vnode_parent_attribute, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node parent attribute'`; }
	public is(other:Cnode_parent_attribute):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__node_parent_attribute = application.Cnode_parent_attribute;
type Vnode_parent = { name: 'node', definition: application.Cnode__application}|{ name: 'none', definition: (typeof Cnode_parent.Pnone)}
export class Cnode_parent extends AlanStruct {
	public static Pnone:Cnode_parent = new class PrimitiveInstance extends Cnode_parent {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Cnode_parent})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnode_parent) { super(); }
	public cast<K extends Vnode_parent['name']>(_variant:K):Extract<Vnode_parent, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_parent['name']]:(($:Extract<Vnode_parent, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node parent'`; }
	public is(other:Cnode_parent):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__node_parent = application.Cnode_parent;
type Vnode_navigation_context = { name: 'attribute', definition: application.Cattributes}|{ name: 'node', definition: application.Cnode__application}
export class Cnode_navigation_context extends AlanStruct {
	constructor(
		public readonly variant:Vnode_navigation_context, public input: {
			dependency: () => application.Cdependency__application,
			direction: () => application.Cdirection,
			existence: () => application.Cexistence__application,
			location: () => application.Crelative_object_location,
			value_source_dependence: () => application.Cvalue_source
		}) { super(); }
	public definitions:{
		optional_node_navigation_context: Coptional_node_navigation_context;
	} = {
		optional_node_navigation_context: new Coptional_node_navigation_context({name:'node', definition: this})
	}
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		existence: () => application.Cexistence__application;
		location: () => application.Crelative_object_location;
		node: () => application.Cnode__application;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.direction()).result!),
		existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.existence()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!),
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'attribute': {
						const application__node_navigation_context_out___node___attribute_nval = context.cast('attribute');
						return resolve(context)
							.then(context => application__node_navigation_context_out___node___attribute_nval)
							.then(context => context?.parent).result;
					}
					case 'node': {
						const application__node_navigation_context_out___node___node_nval = context.cast('node');
						return resolve(context)
							.then(context => application__node_navigation_context_out___node___node_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source_dependence()).result!)
	};
	public cast<K extends Vnode_navigation_context['name']>(_variant:K):Extract<Vnode_navigation_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_navigation_context['name']]:(($:Extract<Vnode_navigation_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node navigation context'`; }
	public is(other:Cnode_navigation_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vderived_value_source = { name: 'initializer', definition: application.Cnode_initializing_expression}|{ name: 'expression', definition: (typeof Cderived_value_source.Pexpression)}|{ name: 'inference', definition: (typeof Cderived_value_source.Pinference)}
export class Cderived_value_source extends AlanStruct {
	public static Pexpression:Cderived_value_source = new class PrimitiveInstance extends Cderived_value_source {
		constructor () {
			super({name: 'expression', definition: undefined as unknown as Cderived_value_source})
			this.variant.definition = this;
		}
	}
	public static Pinference:Cderived_value_source = new class PrimitiveInstance extends Cderived_value_source {
		constructor () {
			super({name: 'inference', definition: undefined as unknown as Cderived_value_source})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vderived_value_source) { super(); }
	public definitions:{
		value_source: Cvalue_source;
	} = {
		value_source: new Cvalue_source({name:'derived', definition: this})
	}
	public cast<K extends Vderived_value_source['name']>(_variant:K):Extract<Vderived_value_source, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vderived_value_source['name']]:(($:Extract<Vderived_value_source, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'derived value source'`; }
	public is(other:Cderived_value_source):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cexpression__derived_value_source = application.Cderived_value_source;
export import Cinference = application.Cderived_value_source;
type Vnode_initializing_expression = { name: 'collection', definition: application.Cno__key_constraint}|{ name: 'state group', definition: application.Cderived__type__state_group}
export class Cnode_initializing_expression extends AlanStruct {
	constructor(
		public readonly variant:Vnode_initializing_expression) { super(); }
	public definitions:{
		derived_value_source: Cderived_value_source;
	} = {
		derived_value_source: new Cderived_value_source({name:'initializer', definition: this})
	}
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection': {
						const application__node_initializing_expression_out___computation_phase___collection_nval = context.cast('collection');
						return resolve(context)
							.then(context => application__node_initializing_expression_out___computation_phase___collection_nval)
							.then(context => context?.parent)
							.then(context => context?.properties.key_constraint?.state.node.output.value_computation_phase()).result;
					}
					case 'state group': {
						const application__node_initializing_expression_out___computation_phase___state_group_nval = context.cast('state group');
						return resolve(context)
							.then(context => application__node_initializing_expression_out___computation_phase___state_group_nval)
							.then(context => context?.properties.value_source?.state.node.output.value_computation_phase()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vnode_initializing_expression['name']>(_variant:K):Extract<Vnode_initializing_expression, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_initializing_expression['name']]:(($:Extract<Vnode_initializing_expression, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node initializing expression'`; }
	public is(other:Cnode_initializing_expression):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tnode_initializer = {
	'arguments':Record<string, Targuments>;
};
export class Cnode_initializer extends AlanNode {
	public readonly properties:{
		readonly arguments:Cnode_initializer.Darguments
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(reduce_context => 
				resolve(reduce_context)
				.then(() => this)
				.then(context => context?.properties.arguments)
				.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
				.then(set => set.map(entry => resolve(entry)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.computation_phase()).result))
					.then(set => set.reduce((prev:application.Ccomputation_phase, curr) => curr.variant.widening_index < prev!.variant.widening_index ? curr : prev, application.Ccomputation_phase.Pzero)).result).result!)
	};
	constructor(init:Tnode_initializer, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			arguments: new Cnode_initializer.Darguments(init['arguments'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Karguments extends Reference<application.Cproperty__type__attributes, string> {
	public readonly inferences:{
		parameter: () => application.Cnode_initializing_expression
	}
	constructor(key:string, $this:Carguments) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as application.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
		this.inferences = {
			parameter: cache((detach:boolean) => {
				const application__node_initializer__arguments_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => application__node_initializer__arguments_key_nval)
					.then(context => context?.properties.type?.state.node.output.value_source())
					.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
					.then(context => context?.variant.name === 'initializer' ? context.variant.definition as application.Cnode_initializing_expression : undefined).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'arguments'::key`; }
}
export type Targuments = {
	'expression':Tderivation_expression;
};
export class Carguments extends AlanDictionaryEntry {
	public key:Karguments;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly expression:Cderivation_expression
	};
	constructor(key:string, init:Targuments, public parent:Cnode_initializer) {
		super();
		const $this = this;
		this.key = new Karguments(key, $this);
		this.properties = {
			expression: new Carguments.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'arguments'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tnode_expression = {
	'node path':Tconditional_node_path;
	'type':['containment', Tcontainment__type__node_expression]|['equality', Tequality__type__node_expression]|'existence'|['existence', {}];
};
export class Cnode_expression extends AlanNode {
	public readonly properties:{
		readonly node_path:Cconditional_node_path,
		readonly type:Cnode_expression.Dtype
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.node_type()).result!)
	};
	constructor(init:Tnode_expression, public location:AlanNode, public input: {
		modifier: () => application.Cbase_value_source,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			node_path: new Cnode_expression.Dnode_path(init['node path'], $this),
			type: new Cnode_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tcontainment__type__node_expression = {
	'collection':string;
	'key path':Tkey_path;
};
export class Ccontainment__type__node_expression extends AlanNode {
	public readonly properties:{
		readonly collection:Ccontainment__type__node_expression.Dcollection,
		readonly key_path:Ckey_path
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcontainment__type__node_expression, public parent:Cnode_expression) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccontainment__type__node_expression.Dcollection(init['collection'], $this),
			key_path: new Ckey_path(init['key path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'containment'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkey_path = {
	'path':Tconditional_node_path;
	'text':string;
};
export class Ckey_path extends AlanNode {
	public readonly properties:{
		readonly path:Cconditional_node_path,
		readonly text:Ckey_path.Dtext
	};
	constructor(init:Tkey_path, public parent:Ccontainment__type__node_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Ckey_path.Dpath(init['path'], $this),
			text: new Ckey_path.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequality__type__node_expression = {
	'expected node path':Tconditional_node_path;
};
export class Cequality__type__node_expression extends AlanNode {
	public readonly properties:{
		readonly expected_node_path:Cconditional_node_path & { readonly inferences: {
			expected_node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.node_path)
			.then(context => context?.component_root.output.node_type()).result!)
	}
	constructor(init:Tequality__type__node_expression, public parent:Cnode_expression) {
		super();
		const $this = this;
		this.properties = {
			expected_node_path: new Cequality__type__node_expression.Dexpected_node_path(init['expected node path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'equality'`; }
	public get entity() { return this.parent.entity; }
}
export type Texistence__type__node_expression = {
};
export class Cexistence__type__node_expression extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.node_path)
			.then(context => context?.component_root.output.node_type()).result!)
	}
	constructor(init:Texistence__type__node_expression, public parent:Cnode_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existence'`; }
	public get entity() { return this.parent.entity; }
}
type Vdependency__application = { name: 'step', definition: application.Cdependency_definition}|{ name: 'self', definition: application.Centity__application}
export class Cdependency__application extends AlanStruct {
	constructor(
		public readonly variant:Vdependency__application) { super(); }
	public readonly output:{
		definer: () => application.Centity__application;
		entity: () => application.Centity__application;
		location: () => application.Crelative_object_location;
	} = {
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'self': {
						const application__dependency_out___definer___self_nval = context.cast('self');
						return resolve(context)
							.then(context => application__dependency_out___definer___self_nval).result;
					}
					case 'step': {
						const application__dependency_out___definer___step_nval = context.cast('step');
						return resolve(context)
							.then(context => application__dependency_out___definer___step_nval)
							.then(context => context?.component_root.output.definer()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'self': {
						const application__dependency_out___entity___self_nval = context.cast('self');
						return resolve(context)
							.then(context => application__dependency_out___entity___self_nval).result;
					}
					case 'step': {
						const application__dependency_out___entity___step_nval = context.cast('step');
						return resolve(context)
							.then(context => application__dependency_out___entity___step_nval)
							.then(context => context?.component_root.output.entity()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'self': {
						const application__dependency_out___location___self_nval = context.cast('self');
						return resolve(context)
							.then(context => application__dependency_out___location___self_nval)
							.then(context => context?.definitions.relative_object_location).result;
					}
					case 'step': {
						const application__dependency_out___location___step_nval = context.cast('step');
						return resolve(context)
							.then(context => application__dependency_out___location___step_nval)
							.then(context => context?.component_root.output.location()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vdependency__application['name']>(_variant:K):Extract<Vdependency__application, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vdependency__application['name']]:(($:Extract<Vdependency__application, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'dependency'`; }
	public is(other:Cdependency__application):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Ventity__application = { name: 'collection', definition: application.Ccollection__type__property}|{ name: 'root', definition: application.Capplication}
export class Centity__application extends AlanStruct {
	constructor(
		public readonly variant:Ventity__application) { super(); }
	public definitions:{
		dependency: Cdependency__application;
		relative_object_location: Crelative_object_location;
		sibling_dependency_retrieval: Csibling_dependency_retrieval;
	} = {
		dependency: new Cdependency__application({name:'self', definition: this}),
		relative_object_location: new Crelative_object_location({name:'context entity', definition: this}),
		sibling_dependency_retrieval: new Csibling_dependency_retrieval({name:'sibling or self step', definition: this})
	}
	public cast<K extends Ventity__application['name']>(_variant:K):Extract<Ventity__application, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Ventity__application['name']]:(($:Extract<Ventity__application, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'entity'`; }
	public is(other:Centity__application):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcollection_object = { name: 'derived set', definition: application.Cderived_set}|{ name: 'collection attribute', definition: application.Ccollection_attribute}
export class Ccollection_object extends AlanStruct {
	constructor(
		public readonly variant:Vcollection_object, public input: {
			lower_bound_cardinality: () => application.Clower_bound_cardinality,
			source_entry_existence: () => application.Cexistence__application,
			upper_bound_cardinality: () => application.Cupper_bound_cardinality,
			value_location: () => application.Crelative_object_location,
			value_type: () => application.Cvalue_object
		}) { super(); }
	public definitions:{
		value_object: Cvalue_object;
	} = {
		value_object: new Cvalue_object({name:'collection', definition: this})
	}
	public readonly output:{
		entity: () => application.Centity__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		source_collection: () => application.Ccollection_attribute;
		source_entry_existence: () => application.Cexistence__application;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
		value_location: () => application.Crelative_object_location;
		value_type: () => application.Cvalue_object;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection attribute': {
						const application__collection_object_out___entity___collection_attribute_nval = context.cast('collection attribute');
						return resolve(context)
							.then(context => application__collection_object_out___entity___collection_attribute_nval)
							.then(context => context?.component_root.output.entity()).result;
					}
					case 'derived set': {
						const application__collection_object_out___entity___derived_set_nval = context.cast('derived set');
						return resolve(context)
							.then(context => application__collection_object_out___entity___derived_set_nval)
							.then(context => context?.component_root.output.entity()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.lower_bound_cardinality()).result!),
		source_collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection attribute': {
						const application__collection_object_out___source_collection___collection_attribute_nval = context.cast('collection attribute');
						return resolve(context)
							.then(context => application__collection_object_out___source_collection___collection_attribute_nval).result;
					}
					case 'derived set': {
						const application__collection_object_out___source_collection___derived_set_nval = context.cast('derived set');
						return resolve(context)
							.then(context => application__collection_object_out___source_collection___derived_set_nval)
							.then(context => context?.component_root.output.source_collection()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		source_entry_existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.source_entry_existence()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.upper_bound_cardinality()).result!),
		value_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_location()).result!),
		value_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_type()).result!)
	};
	public cast<K extends Vcollection_object['name']>(_variant:K):Extract<Vcollection_object, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcollection_object['name']]:(($:Extract<Vcollection_object, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'collection object'`; }
	public is(other:Ccollection_object):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcollection_attribute = { name: 'reference set', definition: application.Creference_set__type__attributes}|{ name: 'collection property', definition: application.Ccollection__type__property}
export class Ccollection_attribute extends AlanStruct {
	constructor(
		public readonly variant:Vcollection_attribute, public input: {
			lower_bound_cardinality: () => application.Clower_bound_cardinality,
			upper_bound_cardinality: () => application.Cupper_bound_cardinality,
			value_location: () => application.Crelative_object_location,
			value_type: () => application.Cnode__application
		}) { super(); }
	public definitions:{
		collection_object: Ccollection_object;
	} = {
		collection_object: new Ccollection_object({name:'collection attribute', definition: this}, {
			lower_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.lower_bound_cardinality()).result!),
			source_entry_existence: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(() => application.Cexistence__application.Pmandatory).result!),
			upper_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.upper_bound_cardinality()).result!),
			value_location: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.value_location()).result!),
			value_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.value_type())
				.then(context => context?.definitions.value_object).result!)
		})
	}
	public readonly output:{
		entity: () => application.Centity__application;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		root_location: () => application.Croot_location;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
		value_type: () => application.Cnode__application;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection property': {
						const application__collection_attribute_out___entity___collection_property_nval = context.cast('collection property');
						return resolve(context)
							.then(context => application__collection_attribute_out___entity___collection_property_nval)
							.then(context => context?.definitions.entity).result;
					}
					case 'reference set': {
						const application__collection_attribute_out___entity___reference_set_nval = context.cast('reference set');
						return resolve(context)
							.then(context => application__collection_attribute_out___entity___reference_set_nval)
							.then(context => context?.properties.referenced_type_path)
							.then(context => context?.properties.path?.inferences.node_type())
							.then(context => context?.component_root.output.entity()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.lower_bound_cardinality()).result!),
		root_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection property': {
						const application__collection_attribute_out___root_location___collection_property_nval = context.cast('collection property');
						return resolve(context)
							.then(context => application__collection_attribute_out___root_location___collection_property_nval)
							.then(context => context?.component_root.output.location())
							.then(context => context?.component_root.output.root()).result;
					}
					case 'reference set': {
						const application__collection_attribute_out___root_location___reference_set_nval = context.cast('reference set');
						return resolve(context)
							.then(context => application__collection_attribute_out___root_location___reference_set_nval)
							.then(context => context?.component_root.output.location())
							.then(context => context?.component_root.output.root()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.upper_bound_cardinality()).result!),
		value_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_type()).result!)
	};
	public cast<K extends Vcollection_attribute['name']>(_variant:K):Extract<Vcollection_attribute, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcollection_attribute['name']]:(($:Extract<Vcollection_attribute, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'collection attribute'`; }
	public is(other:Ccollection_attribute):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vattribute_type = { name: 'simple', definition: (typeof Cattribute_type.Psimple)}|{ name: 'key', definition: application.Ccollection__type__property}
export class Cattribute_type extends AlanStruct {
	public static Psimple:Cattribute_type = new class PrimitiveInstance extends Cattribute_type {
		constructor () {
			super({name: 'simple', definition: undefined as unknown as Cattribute_type})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vattribute_type) { super(); }
	public cast<K extends Vattribute_type['name']>(_variant:K):Extract<Vattribute_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vattribute_type['name']]:(($:Extract<Vattribute_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'attribute type'`; }
	public is(other:Cattribute_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Csimple__attribute_type = application.Cattribute_type;
export type Tnode__application = {
	'attributes':Record<string, Tattributes>;
	'has attributes':'no'|['no', {}]|['yes', Tyes__has_attributes];
	'permissions definition':Tnode_permissions_definition;
	'todo definition':Ttodo_definition;
};
export class Cnode__application extends AlanNode {
	public definitions:{
		node_parent: Cnode_parent;
		value_object: Cvalue_object;
	} = {
		node_parent: new Cnode_parent({name:'node', definition: this}),
		value_object: new Cvalue_object({name:'node', definition: this})
	}
	public readonly properties:{
		readonly attributes:Cnode__application.Dattributes,
		readonly has_attributes:Cnode__application.Dhas_attributes,
		readonly permissions_definition:Cnode_permissions_definition,
		readonly todo_definition:Ctodo_definition
	};
	public readonly output:{
		entity: () => application.Centity__application;
		location: () => application.Cnode_location;
		node: () => application.Cnode__application;
		value_source: () => application.Cvalue_source;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.entity()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!),
		node: cache((detach:boolean) => resolve(this)
			.then(() => this).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!)
	};
	constructor(init:Tnode__application, public location:AlanNode, public input: {
		entity: () => application.Centity__application,
		location: () => application.Cnode_location,
		value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cnode__application.Dattributes(init['attributes'], $this),
			has_attributes: new Cnode__application.Dhas_attributes(init['has attributes'], $this),
			permissions_definition: new Cnode__application.Dpermissions_definition(init['permissions definition'], $this),
			todo_definition: new Cnode__application.Dtodo_definition(init['todo definition'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tattributes = {
	'has predecessor':'no'|['no', {}]|['yes', Tyes__has_predecessor__attributes];
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__attributes];
	'type':['action', Taction]|['command', Tcommand__type]|['property', Tproperty__type__attributes]|['reference set', Treference_set__type__attributes];
};
export class Cattributes extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		node_parent_attribute: Cnode_parent_attribute;
	} = {
		node_parent_attribute: new Cnode_parent_attribute({name:'attribute', definition: this})
	}
	public readonly properties:{
		readonly has_predecessor:Cattributes.Dhas_predecessor,
		readonly has_successor:Cattributes.Dhas_successor,
		readonly type:Cattributes.Dtype
	};
	constructor(key:string, init:Tattributes, public parent:Cnode__application) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_predecessor: new Cattributes.Dhas_predecessor(init['has predecessor'], $this),
			has_successor: new Cattributes.Dhas_successor(init['has successor'], $this),
			type: new Cattributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_predecessor__attributes = {
};
export class Cno__has_predecessor__attributes extends AlanNode {
	constructor(init:Tno__has_predecessor__attributes, public parent:Cattributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_predecessor__attributes = {
	'attribute':string;
};
export class Cyes__has_predecessor__attributes extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes__has_predecessor__attributes.Dattribute
	};
	constructor(init:Tyes__has_predecessor__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes__has_predecessor__attributes.Dattribute(init['attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_successor__attributes = {
};
export class Cno__has_successor__attributes extends AlanNode {
	constructor(init:Tno__has_successor__attributes, public parent:Cattributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__attributes = {
	'attribute':string;
};
export class Cyes__has_successor__attributes extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes__has_successor__attributes.Dattribute
	};
	constructor(init:Tyes__has_successor__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes__has_successor__attributes.Dattribute(init['attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Taction = {
	'action':Tui_action;
	'parameters':Tnode__application;
	'ui':Tui_action_attribute;
	'variable assignment':Toptional_variable_assignment;
};
export class Caction extends AlanNode {
	public definitions:{
		operation: Coperation;
	} = {
		operation: new Coperation({name:'action', definition: this})
	}
	public readonly properties:{
		readonly action:Cui_action,
		readonly parameters:Cnode__application,
		readonly ui:Cui_action_attribute,
		readonly variable_assignment:Coptional_variable_assignment
	};
	public readonly output:{
		attribute_type: () => application.Cattribute_type;
	} = {
		attribute_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cattribute_type.Psimple).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Taction, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			action: new Caction.Daction(init['action'], $this),
			parameters: new Caction.Dparameters(init['parameters'], $this),
			ui: new Caction.Dui(init['ui'], $this),
			variable_assignment: new Caction.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'action'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand__type = {
	'implementation':'external'|['external', {}]|['internal', Tinternal__implementation];
	'parameters':Tnode__application;
	'permission definition':Tcommand_permission_definition;
	'type':'component'|['component', {}]|['global', Tglobal];
};
export class Ccommand__type extends AlanNode {
	public definitions:{
		operation: Coperation;
	} = {
		operation: new Coperation({name:'command', definition: this})
	}
	public readonly properties:{
		readonly implementation:Ccommand__type.Dimplementation,
		readonly parameters:Cnode__application,
		readonly permission_definition:Ccommand_permission_definition,
		readonly type:Ccommand__type.Dtype
	};
	public readonly output:{
		attribute_type: () => application.Cattribute_type;
	} = {
		attribute_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cattribute_type.Psimple).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tcommand__type, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			implementation: new Ccommand__type.Dimplementation(init['implementation'], $this),
			parameters: new Ccommand__type.Dparameters(init['parameters'], $this),
			permission_definition: new Ccommand__type.Dpermission_definition(init['permission definition'], $this),
			type: new Ccommand__type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Texternal = {
};
export class Cexternal extends AlanNode {
	public readonly inferences:{
		imported_interface_source: () => application.Cimported_interfaces
	} = {
		imported_interface_source: cache((detach:boolean) => {
			const application__node__attributes__type__command__implementation__external_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.permissions_definition)
				.then(context => context?.component_root.output.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'imported interface' ? context.variant.definition as application.Cimported_interfaces : undefined).result!;
		})

	}
	constructor(init:Texternal, public parent:Ccommand__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'implementation'?'external'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinternal__implementation = {
	'implementation':Tcommand__application;
	'variable assignment':Toptional_variable_assignment;
};
export class Cinternal__implementation extends AlanNode {
	public readonly properties:{
		readonly implementation:Ccommand__application,
		readonly variable_assignment:Coptional_variable_assignment
	};
	constructor(init:Tinternal__implementation, public parent:Ccommand__type) {
		super();
		const $this = this;
		this.properties = {
			implementation: new Cinternal__implementation.Dimplementation(init['implementation'], $this),
			variable_assignment: new Cinternal__implementation.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'implementation'?'internal'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcomponent = {
};
export class Ccomponent extends AlanNode {
	constructor(init:Tcomponent, public parent:Ccommand__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'component'`; }
	public get entity() { return this.parent.entity; }
}
export type Tglobal = {
	'ui':Tui_command_attribute;
};
export class Cglobal extends AlanNode {
	public readonly properties:{
		readonly ui:Cui_command_attribute
	};
	constructor(init:Tglobal, public parent:Ccommand__type) {
		super();
		const $this = this;
		this.properties = {
			ui: new Cglobal.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'global'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__attributes = {
	'type':['collection', Tcollection__type__property]|['file', Tfile]|['group', Tgroup__type__property]|['number', Tnumber__type__property]|['state group', Tstate_group]|['text', Ttext__type__property];
};
export class Cproperty__type__attributes extends AlanNode {
	public readonly properties:{
		readonly type:Cproperty__type__attributes.Dtype
	};
	public readonly output:{
		attribute_type: () => application.Cattribute_type;
	} = {
		attribute_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.entity())
			.then(context => {
				switch (context?.variant.name) {
					case 'collection': {
						const application__node__attributes__type__property_sout___attribute_type___collection_nval = context.cast('collection');
						return resolve(context)
							.then(match_context => { 
								const expression_context = resolve(match_context)
								.then(context => {
									const left = resolve(context)
										.then(() => this).result;
									const right = resolve(context)
										.then(context => application__node__attributes__type__property_sout___attribute_type___collection_nval)
										.then(context => context?.properties.key_property?.ref)
										.then(context => context?.parent).result;
									return left?.is(right!) ? left : undefined
								}).result;
								if (expression_context !== undefined) {
								return resolve(match_context)
								.then(context => application__node__attributes__type__property_sout___attribute_type___collection_nval)
								.then(context => context?.definitions.attribute_type).result
								} else {
									return resolve(match_context)
									.then(() => this)
									.then(() => application.Cattribute_type.Psimple).result
								}
							}).result;
					}
					case 'root': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cattribute_type.Psimple).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	}
	constructor(init:Tproperty__type__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cproperty__type__attributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property = {
	'cardinality constraint':Tlower_bound_cardinality_constraint;
	'graph constraints':Tgraph_constraints_definition;
	'key property':string;
	'node':Tnode__application;
	'permissions':Titem_permissions_definition;
	'type':'base'|['base', {}]|['derived', Tderived__type__collection];
	'ui':Tui_collection_property;
};
export class Ccollection__type__property extends AlanNode {
	public definitions:{
		attribute_type: Cattribute_type;
		collection_attribute: Ccollection_attribute;
		entity: Centity__application;
		node_location: Cnode_location;
	} = {
		attribute_type: new Cattribute_type({name:'key', definition: this}),
		collection_attribute: new Ccollection_attribute({name:'collection property', definition: this}, {
			lower_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.cardinality_constraint)
				.then(context => context?.component_root.output.cardinality()).result!),
			upper_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(() => application.Cupper_bound_cardinality.Pmany).result!),
			value_location: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.definitions.entity)
				.then(context => context?.definitions.relative_object_location).result!),
			value_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.node).result!)
		}),
		entity: new Centity__application({name:'collection', definition: this}),
		node_location: new Cnode_location({name:'collection', definition: this}, {
			attribute: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent_attribute).result!),
			node: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root()).result!)
		})
	}
	public readonly properties:{
		readonly cardinality_constraint:Clower_bound_cardinality_constraint,
		readonly graph_constraints:Cgraph_constraints_definition,
		readonly key_property:Ccollection__type__property.Dkey_property,
		readonly node:Cnode__application,
		readonly permissions:Citem_permissions_definition,
		readonly type:Ccollection__type__property.Dtype,
		readonly ui:Cui_collection_property
	};
	public readonly output:{
		value: () => application.Cvalue_object;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.collection_attribute)
			.then(context => context?.definitions.collection_object)
			.then(context => context?.definitions.value_object).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	constructor(init:Tcollection__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			cardinality_constraint: new Ccollection__type__property.Dcardinality_constraint(init['cardinality constraint'], $this),
			graph_constraints: new Ccollection__type__property.Dgraph_constraints(init['graph constraints'], $this),
			key_property: new Ccollection__type__property.Dkey_property(init['key property'], $this),
			node: new Ccollection__type__property.Dnode(init['node'], $this),
			permissions: new Ccollection__type__property.Dpermissions(init['permissions'], $this),
			type: new Ccollection__type__property.Dtype(init['type'], $this),
			ui: new Ccollection__type__property.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__type__collection = {
};
export class Cbase__type__collection extends AlanNode {
	public readonly output:{
		entry_value_source: () => application.Cvalue_source;
		key_value_source: () => application.Ctext__type__property;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		entry_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!),
		key_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.key_property?.ref).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!)
	}
	public readonly inferences:{
		key_value: () => application.Cbase__type__text,
		parent_source: () => application.Cbase_value_source
	} = {
		key_value: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__collection__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.key_property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		parent_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__collection__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tbase__type__collection, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived__type__collection = {
	'key constraint':['no', Tno__key_constraint]|['yes', Tyes__key_constraint];
	'recursion':Trecursion_annotation;
};
export class Cderived__type__collection extends AlanNode {
	public readonly properties:{
		readonly key_constraint:Cderived__type__collection.Dkey_constraint,
		readonly recursion:Crecursion_annotation
	};
	public readonly output:{
		entry_value_source: () => application.Cvalue_source;
		key_value_source: () => application.Ctext__type__property;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		entry_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.key_constraint?.state.node.output.entry_value_source())
			.then(context => context?.definitions.value_source).result!),
		key_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.key_constraint?.state.node.output.key_value_source()).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.key_constraint?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.recursion)
			.then(context => context?.component_root.output.location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__collection__type__derived_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tderived__type__collection, public parent:Ccollection__type__property) {
		super();
		const $this = this;
		this.properties = {
			key_constraint: new Cderived__type__collection.Dkey_constraint(init['key constraint'], $this),
			recursion: new Cderived__type__collection.Drecursion(init['recursion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'derived'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__key_constraint = {
	'branches':Record<string, Tbranches__no>;
	'separator':'colon'|['colon', {}]|'dash'|['dash', {}]|'dot'|['dot', {}]|'greater than'|['greater than', {}]|'space'|['space', {}];
};
export class Cno__key_constraint extends AlanNode {
	public definitions:{
		node_initializing_expression: Cnode_initializing_expression;
	} = {
		node_initializing_expression: new Cnode_initializing_expression({name:'collection', definition: this})
	}
	public readonly properties:{
		readonly branches:Cno__key_constraint.Dbranches,
		readonly separator:Cno__key_constraint.Dseparator
	};
	public readonly output:{
		entry_value_source: () => application.Cderived_value_source;
		key_value_source: () => application.Ctext__type__property;
		value_computation_phase: () => application.Ccomputation_phase;
	} = {
		entry_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.node_initializing_expression)
			.then(context => context?.definitions.derived_value_source).result!),
		key_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.key_property?.ref).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.branches?.inferences.computation_phase()).result!)
	}
	public readonly inferences:{
		no_key_reference: () => application.Cno__has_reference
	} = {
		no_key_reference: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__collection__type__derived__key_constraint__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.key_property?.ref)
				.then(context => {
					if (context?.properties.has_reference.state.name === 'no') {
						return resolve(context.properties.has_reference.state.node as application.Cno__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__key_constraint, public parent:Cderived__type__collection) {
		super();
		const $this = this;
		this.properties = {
			branches: new Cno__key_constraint.Dbranches(init['branches'], $this),
			separator: new Cno__key_constraint.Dseparator(init['separator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key constraint'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbranches__no = {
	'expression':Tflatten_expression;
	'node initializer':Tnode_initializer;
	'variable assignment':Toptional_named_object_assignment;
};
export class Cbranches__no extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly expression:Cflatten_expression,
		readonly node_initializer:Cnode_initializer & { readonly inferences: {
			computation_phase: () => application.Ccomputation_phase;
		} },
		readonly variable_assignment:Coptional_named_object_assignment
	};
	constructor(key:string, init:Tbranches__no, public parent:Cno__key_constraint) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			expression: new Cbranches__no.Dexpression(init['expression'], $this),
			node_initializer: new Cbranches__no.Dnode_initializer(init['node initializer'], $this),
			variable_assignment: new Cbranches__no.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'branches'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcolon = {
};
export class Ccolon extends AlanNode {
	constructor(init:Tcolon, public parent:Cno__key_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'separator'?'colon'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdash = {
};
export class Cdash extends AlanNode {
	constructor(init:Tdash, public parent:Cno__key_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'separator'?'dash'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdot = {
};
export class Cdot extends AlanNode {
	constructor(init:Tdot, public parent:Cno__key_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'separator'?'dot'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgreater_than = {
};
export class Cgreater_than extends AlanNode {
	constructor(init:Tgreater_than, public parent:Cno__key_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'separator'?'greater than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tspace = {
};
export class Cspace extends AlanNode {
	constructor(init:Tspace, public parent:Cno__key_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'separator'?'space'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__key_constraint = {
	'expression':Tplural_reference_expression;
};
export class Cyes__key_constraint extends AlanNode {
	public readonly properties:{
		readonly expression:Cplural_reference_expression
	};
	public readonly output:{
		entry_value_source: () => application.Cderived_value_source;
		key_value_source: () => application.Ctext__type__property;
		value_computation_phase: () => application.Ccomputation_phase;
	} = {
		entry_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression).result!),
		key_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.key_reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.key_value_source()).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.value_computation_phase()).result!)
	}
	public readonly inferences:{
		key_constraint: () => application.Cmandatory__type,
		key_reference: () => application.Cyes__has_reference
	} = {
		key_constraint: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__collection__type__derived__key_constraint__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.key_reference())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'mandatory') {
						return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		key_reference: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__collection__type__derived__key_constraint__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.key_property?.ref)
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__key_constraint, public parent:Cderived__type__collection) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cyes__key_constraint.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key constraint'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile = {
	'type':'base'|['base', {}]|['derived', Tderived__type__file];
	'ui':Tui_file_property;
};
export class Cfile extends AlanNode {
	public definitions:{
		leaf_value: Cleaf_value;
	} = {
		leaf_value: new Cleaf_value({name:'file', definition: this})
	}
	public readonly properties:{
		readonly type:Cfile.Dtype,
		readonly ui:Cui_file_property
	};
	public readonly output:{
		value: () => application.Cvalue_object;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.leaf_value)
			.then(context => context?.definitions.value_object).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	constructor(init:Tfile, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cfile.Dtype(init['type'], $this),
			ui: new Cfile.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__type__file = {
};
export class Cbase__type__file extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!)
	}
	public readonly inferences:{
		parent_source: () => application.Cbase_value_source
	} = {
		parent_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__file__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tbase__type__file, public parent:Cfile) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived__type__file = {
	'value source':['expression', Texpression__value_source__derived__type__file]|'parameter'|['parameter', {}];
};
export class Cderived__type__file extends AlanNode {
	public readonly properties:{
		readonly value_source:Cderived__type__file.Dvalue_source
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_source()).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__file__type__derived_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tderived__type__file, public parent:Cfile) {
		super();
		const $this = this;
		this.properties = {
			value_source: new Cderived__type__file.Dvalue_source(init['value source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'derived'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__value_source__derived__type__file = {
	'expression':Tderivation_expression;
	'recursion':Trecursion_annotation;
};
export class Cexpression__value_source__derived__type__file extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly recursion:Crecursion_annotation
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.recursion)
			.then(context => context?.component_root.output.location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	constructor(init:Texpression__value_source__derived__type__file, public parent:Cderived__type__file) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cexpression__value_source__derived__type__file.Dexpression(init['expression'], $this),
			recursion: new Cexpression__value_source__derived__type__file.Drecursion(init['recursion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter__value_source__derived__type__file = {
};
export class Cparameter__value_source__derived__type__file extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	}
	public readonly inferences:{
		initializer: () => application.Cnode_initializing_expression
	} = {
		initializer: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__file__type__derived__value_source__parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
				.then(context => context?.variant.name === 'initializer' ? context.variant.definition as application.Cnode_initializing_expression : undefined).result!;
		})

	}
	constructor(init:Tparameter__value_source__derived__type__file, public parent:Cderived__type__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__property = {
	'node':Tnode__application;
	'type':'base'|['base', {}]|'derived'|['derived', {}];
	'ui':Tui_group_property;
};
export class Cgroup__type__property extends AlanNode {
	public definitions:{
		node_location: Cnode_location;
	} = {
		node_location: new Cnode_location({name:'group', definition: this}, {
			attribute: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent_attribute).result!),
			node: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root()).result!)
		})
	}
	public readonly properties:{
		readonly node:Cnode__application,
		readonly type:Cgroup__type__property.Dtype,
		readonly ui:Cui_group_property
	};
	public readonly output:{
		value: () => application.Cvalue_object;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.node)
			.then(context => context?.definitions.value_object).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	constructor(init:Tgroup__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			node: new Cgroup__type__property.Dnode(init['node'], $this),
			type: new Cgroup__type__property.Dtype(init['type'], $this),
			ui: new Cgroup__type__property.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__type__group = {
};
export class Cbase__type__group extends AlanNode {
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!)
	}
	public readonly inferences:{
		parent_source: () => application.Cbase_value_source
	} = {
		parent_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__group__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tbase__type__group, public parent:Cgroup__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived__type__group = {
};
export class Cderived__type__group extends AlanNode {
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__group__type__derived_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tderived__type__group, public parent:Cgroup__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'derived'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property = {
	'behaviour':'none'|['none', {}]|['timer', Ttimer];
	'numerical type':string;
	'set type':'integer'|['integer', {}]|'positive'|['positive', {}];
	'type':['base', Tbase__type__number]|['derived', Tderived__type__number];
	'ui':Tui_number_property;
};
export class Cnumber__type__property extends AlanNode {
	public definitions:{
		number_value: Cnumber_value;
	} = {
		number_value: new Cnumber_value({name:'number property', definition: this}, {
			numerical_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.numerical_type?.ref)
				.then(context => {
					const conv_context = resolve(context).result!;
					return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
				}).result!),
			set_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.set_type?.state.node.output.set_type()).result!)
		})
	}
	public readonly properties:{
		readonly behaviour:Cnumber__type__property.Dbehaviour,
		readonly numerical_type:Cnumber__type__property.Dnumerical_type,
		readonly set_type:Cnumber__type__property.Dset_type,
		readonly type:Cnumber__type__property.Dtype,
		readonly ui:Cui_number_property
	};
	public readonly output:{
		value: () => application.Cvalue_object;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.number_value)
			.then(context => context?.definitions.scalar_value)
			.then(context => context?.definitions.leaf_value)
			.then(context => context?.definitions.value_object).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	constructor(init:Tnumber__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			behaviour: new Cnumber__type__property.Dbehaviour(init['behaviour'], $this),
			numerical_type: new Cnumber__type__property.Dnumerical_type(init['numerical type'], $this),
			set_type: new Cnumber__type__property.Dset_type(init['set type'], $this),
			type: new Cnumber__type__property.Dtype(init['type'], $this),
			ui: new Cnumber__type__property.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__behaviour = {
};
export class Cnone__behaviour extends AlanNode {
	constructor(init:Tnone__behaviour, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'behaviour'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttimer = {
	'implementation':Tcommand__application;
};
export class Ctimer extends AlanNode {
	public readonly properties:{
		readonly implementation:Ccommand__application
	};
	public readonly inferences:{
		timer_resolution: () => application.Cseconds__timer_resolution
	} = {
		timer_resolution: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__behaviour__timer_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.numerical_type?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'scale') {
						return resolve(context.properties.type.state.node as application.Cscale)
						.then(context => {
							if (context?.properties.timer_resolution.state.name === 'seconds') {
								return context.properties.timer_resolution.state.node as application.Cseconds__timer_resolution;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttimer, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			implementation: new Ctimer.Dimplementation(init['implementation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'behaviour'?'timer'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinteger__set_type = {
};
export class Cinteger__set_type extends AlanNode {
	public readonly output:{
		set_type: () => application.Cnumber_set_type;
	} = {
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnumber_set_type.Pinteger).result!)
	}
	constructor(init:Tinteger__set_type, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'set type'?'integer'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpositive__set_type = {
};
export class Cpositive__set_type extends AlanNode {
	public readonly output:{
		set_type: () => application.Cnumber_set_type;
	} = {
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnumber_set_type.Ppositive).result!)
	}
	constructor(init:Tpositive__set_type, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'set type'?'positive'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__type__number = {
	'type':['causal', Tcausal]|'simple'|['simple', {}];
};
export class Cbase__type__number extends AlanNode {
	public readonly properties:{
		readonly type:Cbase__type__number.Dtype
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	public readonly inferences:{
		parent_source: () => application.Cbase_value_source
	} = {
		parent_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tbase__type__number, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			type: new Cbase__type__number.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcausal = {
	'type':'creation'|['creation', {}]|['destruction', Tdestruction]|['mutation', Tmutation];
};
export class Ccausal extends AlanNode {
	public readonly properties:{
		readonly type:Ccausal.Dtype
	};
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cbase_value_source.Pinternal)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication,
		no_behaviour: () => application.Cnone__behaviour
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__base__type__causal_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})
		,
		no_behaviour: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__base__type__causal_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.behaviour.state.name === 'none') {
						return resolve(context.properties.behaviour.state.node as application.Cnone__behaviour).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcausal, public parent:Cbase__type__number) {
		super();
		const $this = this;
		this.properties = {
			type: new Ccausal.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'causal'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcreation = {
};
export class Ccreation extends AlanNode {
	public readonly inferences:{
		no_behaviour: () => application.Cnone__behaviour
	} = {
		no_behaviour: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__base__type__causal__type__creation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.behaviour.state.name === 'none') {
						return resolve(context.properties.behaviour.state.node as application.Cnone__behaviour).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcreation, public parent:Ccausal) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'creation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdestruction = {
	'destruction operation':'add lifetime'|['add lifetime', {}]|'set to lifetime'|['set to lifetime', {}]|'subtract lifetime'|['subtract lifetime', {}];
	'watched state':string;
	'watched stategroup':string;
};
export class Cdestruction extends AlanNode {
	public readonly properties:{
		readonly destruction_operation:Cdestruction.Ddestruction_operation,
		readonly watched_state:Cdestruction.Dwatched_state,
		readonly watched_stategroup:Cdestruction.Dwatched_stategroup
	};
	public readonly inferences:{
		integer: () => application.Cinteger__number_set_type
	} = {
		integer: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__base__type__causal__type__destruction_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.set_type?.state.node.output.set_type())
				.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result!;
		})

	}
	constructor(init:Tdestruction, public parent:Ccausal) {
		super();
		const $this = this;
		this.properties = {
			destruction_operation: new Cdestruction.Ddestruction_operation(init['destruction operation'], $this),
			watched_state: new Cdestruction.Dwatched_state(init['watched state'], $this),
			watched_stategroup: new Cdestruction.Dwatched_stategroup(init['watched stategroup'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'destruction'`; }
	public get entity() { return this.parent.entity; }
}
export type Tadd_lifetime = {
};
export class Cadd_lifetime extends AlanNode {
	constructor(init:Tadd_lifetime, public parent:Cdestruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'destruction operation'?'add lifetime'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_to_lifetime = {
};
export class Cset_to_lifetime extends AlanNode {
	constructor(init:Tset_to_lifetime, public parent:Cdestruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'destruction operation'?'set to lifetime'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsubtract_lifetime = {
};
export class Csubtract_lifetime extends AlanNode {
	constructor(init:Tsubtract_lifetime, public parent:Cdestruction) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'destruction operation'?'subtract lifetime'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmutation = {
	'watched property':string;
};
export class Cmutation extends AlanNode {
	public readonly properties:{
		readonly watched_property:Cmutation.Dwatched_property
	};
	public readonly inferences:{
		no_behaviour: () => application.Cnone__behaviour
	} = {
		no_behaviour: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__base__type__causal__type__mutation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.behaviour.state.name === 'none') {
						return resolve(context.properties.behaviour.state.node as application.Cnone__behaviour).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tmutation, public parent:Ccausal) {
		super();
		const $this = this;
		this.properties = {
			watched_property: new Cmutation.Dwatched_property(init['watched property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'mutation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsimple__type__base = {
};
export class Csimple__type__base extends AlanNode {
	public readonly output:{
		value_source: () => application.Cvalue_source;
	} = {
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!)
	}
	constructor(init:Tsimple__type__base, public parent:Cbase__type__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'simple'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived__type__number = {
	'value source':['expression', Texpression__value_source__derived__type__number]|'parameter'|['parameter', {}];
};
export class Cderived__type__number extends AlanNode {
	public readonly properties:{
		readonly value_source:Cderived__type__number.Dvalue_source
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_source()).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__derived_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tderived__type__number, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			value_source: new Cderived__type__number.Dvalue_source(init['value source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'derived'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__value_source__derived__type__number = {
	'expression':Tderivation_expression;
	'recursion':Trecursion_annotation;
};
export class Cexpression__value_source__derived__type__number extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly recursion:Crecursion_annotation
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.recursion)
			.then(context => context?.component_root.output.location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	constructor(init:Texpression__value_source__derived__type__number, public parent:Cderived__type__number) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cexpression__value_source__derived__type__number.Dexpression(init['expression'], $this),
			recursion: new Cexpression__value_source__derived__type__number.Drecursion(init['recursion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter__value_source__derived__type__number = {
};
export class Cparameter__value_source__derived__type__number extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	}
	public readonly inferences:{
		initializer: () => application.Cnode_initializing_expression
	} = {
		initializer: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__number__type__derived__value_source__parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
				.then(context => context?.variant.name === 'initializer' ? context.variant.definition as application.Cnode_initializing_expression : undefined).result!;
		})

	}
	constructor(init:Tparameter__value_source__derived__type__number, public parent:Cderived__type__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group = {
	'first state':string;
	'states':Record<string, Tstates__state_group>;
	'type':'base'|['base', {}]|['derived', Tderived__type__state_group];
	'ui':Tui_state_group_property;
};
export class Cstate_group extends AlanNode {
	public definitions:{
		scalar_value: Cscalar_value;
		value_object: Cvalue_object;
	} = {
		scalar_value: new Cscalar_value({name:'state choice', definition: this}),
		value_object: new Cvalue_object({name:'choice', definition: this})
	}
	public readonly properties:{
		readonly first_state:Cstate_group.Dfirst_state,
		readonly states:Cstate_group.Dstates,
		readonly type:Cstate_group.Dtype,
		readonly ui:Cui_state_group_property
	};
	public readonly output:{
		value: () => application.Cvalue_object;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.value_object).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	constructor(init:Tstate_group, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			first_state: new Cstate_group.Dfirst_state(init['first state'], $this),
			states: new Cstate_group.Dstates(init['states'], $this),
			type: new Cstate_group.Dtype(init['type'], $this),
			ui: new Cstate_group.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates__state_group = {
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__states__state_group];
	'node':Tnode__application;
	'permissions':Titem_permissions_definition;
	'record lifetime':'no'|['no', {}]|['yes', Tyes__record_lifetime];
	'rules':Twhere_rules_definition;
	'ui':Tui_state;
};
export class Cstates__state_group extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		node_location: Cnode_location;
		where_rules_definer: Cwhere_rules_definer;
	} = {
		node_location: new Cnode_location({name:'state', definition: this}, {
			attribute: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent_attribute).result!),
			node: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root()).result!)
		}),
		where_rules_definer: new Cwhere_rules_definer({name:'state', definition: this})
	}
	public readonly properties:{
		readonly has_successor:Cstates__state_group.Dhas_successor,
		readonly node:Cnode__application,
		readonly permissions:Citem_permissions_definition,
		readonly record_lifetime:Cstates__state_group.Drecord_lifetime,
		readonly rules:Cwhere_rules_definition,
		readonly ui:Cui_state
	};
	constructor(key:string, init:Tstates__state_group, public parent:Cstate_group) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_successor: new Cstates__state_group.Dhas_successor(init['has successor'], $this),
			node: new Cstates__state_group.Dnode(init['node'], $this),
			permissions: new Cstates__state_group.Dpermissions(init['permissions'], $this),
			record_lifetime: new Cstates__state_group.Drecord_lifetime(init['record lifetime'], $this),
			rules: new Cstates__state_group.Drules(init['rules'], $this),
			ui: new Cstates__state_group.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor__states__state_group = {
};
export class Cno__has_successor__states__state_group extends AlanNode {
	constructor(init:Tno__has_successor__states__state_group, public parent:Cstates__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__states__state_group = {
	'successor':string;
};
export class Cyes__has_successor__states__state_group extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__states__state_group.Dsuccessor
	};
	constructor(init:Tyes__has_successor__states__state_group, public parent:Cstates__state_group) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__states__state_group.Dsuccessor(init['successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__record_lifetime = {
};
export class Cno__record_lifetime extends AlanNode {
	constructor(init:Tno__record_lifetime, public parent:Cstates__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'record lifetime'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__record_lifetime = {
	'creation timestamp':string;
	'meta property':string;
};
export class Cyes__record_lifetime extends AlanNode {
	public readonly properties:{
		readonly creation_timestamp:Cyes__record_lifetime.Dcreation_timestamp,
		readonly meta_property:Cyes__record_lifetime.Dmeta_property
	};
	public readonly inferences:{
		base_value: () => application.Cbase__type__state_group
	} = {
		base_value: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__state_group__states__record_lifetime__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__state_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__record_lifetime, public parent:Cstates__state_group) {
		super();
		const $this = this;
		this.properties = {
			creation_timestamp: new Cyes__record_lifetime.Dcreation_timestamp(init['creation timestamp'], $this),
			meta_property: new Cyes__record_lifetime.Dmeta_property(init['meta property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'record lifetime'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__type__state_group = {
};
export class Cbase__type__state_group extends AlanNode {
	public readonly output:{
		state_value_source: () => application.Cvalue_source;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		state_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node_source())
			.then(context => {
				switch (context?.variant.name) {
					case 'base': {
						const application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base_nval = context.cast('base');
						return resolve(context)
							.then(context => application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'external': {
										const application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base___external_nval = context.cast('external');
										return resolve(context)
											.then(context => application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base___external_nval)
											.then(context => {
												switch (context?.variant.name) {
													case 'imported interface': {
														const application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base___external___imported_interface_nval = context.cast('imported interface');
														return resolve(context)
															.then(context => application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base___external___imported_interface_nval)
															.then(context => context?.properties.connection_status)
															.then(context => {
																const state_group = context;
																switch (state_group?.state.name) {
																	case 'no': {
																		return resolve(context)
																			.then(() => this)
																			.then(context => context?.inferences.node_source()).result;
																	}
																	case 'yes': {
																		const application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base___external___imported_interface___yes_nval = state_group.cast('yes');
																		return resolve(context)
																			.then(match_context => { 
																				const expression_context = resolve(match_context)
																				.then(context => {
																					const left = resolve(context)
																						.then(context => application__node__attributes__type__property__type__state_group__type__base_sout___value_source___base___external___imported_interface___yes_nval)
																						.then(context => context?.properties.state_group_path)
																						.then(context => context?.properties.property?.ref)
																						.then(context => context?.parent).result;
																					const right = resolve(context)
																						.then(() => this)
																						.then(context => context?.parent)
																						.then(context => context?.parent)
																						.then(context => context?.parent).result;
																					return left?.is(right!) ? left : undefined
																				}).result;
																				if (expression_context !== undefined) {
																				return resolve(match_context)
																				.then(() => this)
																				.then(() => application.Cbase_value_source.Pinternal)
																				.then(context => context?.definitions.value_source).result
																				} else {
																					return resolve(match_context)
																					.then(() => this)
																					.then(context => context?.inferences.node_source()).result
																				}
																			}).result;
																	}
																	case undefined: return undefined;
																	default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
																};}).result;
													}
													case 'user': {
														return resolve(context)
															.then(() => this)
															.then(context => context?.inferences.node_source()).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
												};
											}).result;
									}
									case 'internal': {
										return resolve(context)
											.then(() => this)
											.then(context => context?.inferences.node_source()).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case 'derived': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.inferences.node_source()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	}
	public readonly inferences:{
		node_source: () => application.Cvalue_source,
		parent_source: () => application.Cbase_value_source
	} = {
		node_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__state_group__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.permissions_definition)
				.then(context => context?.component_root.output.value_source()).result!;
		})
		,
		parent_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__state_group__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tbase__type__state_group, public parent:Cstate_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived__type__state_group = {
	'value source':['expression', Texpression__value_source__derived__type__state_group]|'parameter'|['parameter', {}];
};
export class Cderived__type__state_group extends AlanNode {
	public definitions:{
		node_initializing_expression: Cnode_initializing_expression;
	} = {
		node_initializing_expression: new Cnode_initializing_expression({name:'state group', definition: this})
	}
	public readonly properties:{
		readonly value_source:Cderived__type__state_group.Dvalue_source
	};
	public readonly output:{
		state_value_source: () => application.Cvalue_source;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		state_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.node_initializing_expression)
			.then(context => context?.definitions.derived_value_source)
			.then(context => context?.definitions.value_source).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_source()).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__state_group__type__derived_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tderived__type__state_group, public parent:Cstate_group) {
		super();
		const $this = this;
		this.properties = {
			value_source: new Cderived__type__state_group.Dvalue_source(init['value source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'derived'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__value_source__derived__type__state_group = {
	'expression':Tderivation_expression;
	'recursion':Trecursion_annotation;
};
export class Cexpression__value_source__derived__type__state_group extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly recursion:Crecursion_annotation
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.recursion)
			.then(context => context?.component_root.output.location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	constructor(init:Texpression__value_source__derived__type__state_group, public parent:Cderived__type__state_group) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cexpression__value_source__derived__type__state_group.Dexpression(init['expression'], $this),
			recursion: new Cexpression__value_source__derived__type__state_group.Drecursion(init['recursion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter__value_source__derived__type__state_group = {
};
export class Cparameter__value_source__derived__type__state_group extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	}
	public readonly inferences:{
		initializer: () => application.Cnode_initializing_expression
	} = {
		initializer: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__state_group__type__derived__value_source__parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
				.then(context => context?.variant.name === 'initializer' ? context.variant.definition as application.Cnode_initializing_expression : undefined).result!;
		})

	}
	constructor(init:Tparameter__value_source__derived__type__state_group, public parent:Cderived__type__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__property = {
	'has reference':'no'|['no', {}]|['yes', Tyes__has_reference];
	'type':'base'|['base', {}]|['derived', Tderived__type__text];
	'ui':Tui_text_property;
};
export class Ctext__type__property extends AlanNode {
	public definitions:{
		scalar_value: Cscalar_value;
	} = {
		scalar_value: new Cscalar_value({name:'text', definition: this})
	}
	public readonly properties:{
		readonly has_reference:Ctext__type__property.Dhas_reference,
		readonly type:Ctext__type__property.Dtype,
		readonly ui:Cui_text_property
	};
	public readonly output:{
		value: () => application.Cvalue_object;
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.scalar_value)
			.then(context => context?.definitions.leaf_value)
			.then(context => context?.definitions.value_object).result!),
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value_source()).result!)
	}
	constructor(init:Ttext__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			has_reference: new Ctext__type__property.Dhas_reference(init['has reference'], $this),
			type: new Ctext__type__property.Dtype(init['type'], $this),
			ui: new Ctext__type__property.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_reference = {
};
export class Cno__has_reference extends AlanNode {
	constructor(init:Tno__has_reference, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has reference'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_reference = {
	'assignment':Toptional_named_object_assignment;
	'behaviour':Treference_behaviour;
	'behaviour specialization':'none'|['none', {}]|'propagate key to value'|['propagate key to value', {}];
	'direction':Tdirection_annotation;
	'expression':Tentry_reference_definition;
	'recursion':Trecursion_annotation;
	'rules':Twhere_rules_definition;
	'ui':Tui_reference_rule;
};
export class Cyes__has_reference extends AlanNode {
	public definitions:{
		entry_reference_definer: Centry_reference_definer;
		reference_definer: Creference_definer;
		where_rules_definer: Cwhere_rules_definer;
	} = {
		entry_reference_definer: new Centry_reference_definer({name:'text', definition: this}, {
			defining_property: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent).result!)
		}),
		reference_definer: new Creference_definer({name:'text', definition: this}, {
			behaviour: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.behaviour).result!),
			defining_property: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent).result!),
			dependency: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.expression)
				.then(context => context?.component_root.output.dependency()).result!),
			direction: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.direction).result!),
			location: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.expression)
				.then(context => context?.component_root.output.location()).result!),
			recursion: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.recursion).result!),
			referenced_node_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.expression)
				.then(context => context?.component_root.output.referenced_node_type()).result!),
			step_dependency: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.expression)
				.then(context => context?.component_root.output.step_dependency()).result!),
			value_source_dependence: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.expression)
				.then(context => context?.component_root.output.value_source_dependence()).result!)
		}),
		where_rules_definer: new Cwhere_rules_definer({name:'text', definition: this})
	}
	public readonly properties:{
		readonly assignment:Coptional_named_object_assignment,
		readonly behaviour:Creference_behaviour,
		readonly behaviour_specialization:Cyes__has_reference.Dbehaviour_specialization,
		readonly direction:Cdirection_annotation,
		readonly expression:Centry_reference_definition,
		readonly recursion:Crecursion_annotation,
		readonly rules:Cwhere_rules_definition,
		readonly ui:Cui_reference_rule
	};
	constructor(init:Tyes__has_reference, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			assignment: new Cyes__has_reference.Dassignment(init['assignment'], $this),
			behaviour: new Cyes__has_reference.Dbehaviour(init['behaviour'], $this),
			behaviour_specialization: new Cyes__has_reference.Dbehaviour_specialization(init['behaviour specialization'], $this),
			direction: new Cyes__has_reference.Ddirection(init['direction'], $this),
			expression: new Cyes__has_reference.Dexpression(init['expression'], $this),
			recursion: new Cyes__has_reference.Drecursion(init['recursion'], $this),
			rules: new Cyes__has_reference.Drules(init['rules'], $this),
			ui: new Cyes__has_reference.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has reference'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__behaviour_specialization = {
};
export class Cnone__behaviour_specialization extends AlanNode {
	constructor(init:Tnone__behaviour_specialization, public parent:Cyes__has_reference) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'behaviour specialization'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpropagate_key_to_value = {
};
export class Cpropagate_key_to_value extends AlanNode {
	public readonly inferences:{
		base_value: () => application.Cbase__type__text,
		optional_reference: () => application.Coptional__existence
	} = {
		base_value: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__has_reference__yes__behaviour_specialization__propagate_key_to_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		optional_reference: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__has_reference__yes__behaviour_specialization__propagate_key_to_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.behaviour)
				.then(context => context?.component_root.output.existence())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tpropagate_key_to_value, public parent:Cyes__has_reference) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'behaviour specialization'?'propagate key to value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__type__text = {
};
export class Cbase__type__text extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.properties.permissions_definition)
			.then(context => context?.component_root.output.value_source()).result!)
	}
	public readonly inferences:{
		parent_source: () => application.Cbase_value_source
	} = {
		parent_source: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tbase__type__text, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived__type__text = {
	'value source':'derived key'|['derived key', {}]|['expression', Texpression__value_source__derived__type__text]|'parameter'|['parameter', {}];
};
export class Cderived__type__text extends AlanNode {
	public readonly properties:{
		readonly value_source:Cderived__type__text.Dvalue_source
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_dependency_location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value_source?.state.node.output.value_source()).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__derived_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Tderived__type__text, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			value_source: new Cderived__type__text.Dvalue_source(init['value source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'derived'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderived_key = {
};
export class Cderived_key extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property,
		derived_collection: () => application.Cderived__type__collection,
		is_key_member: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__derived__value_source__derived_key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		derived_collection: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__derived__value_source__derived_key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => {
					if (context?.properties.type.state.name === 'derived') {
						return resolve(context.properties.type.state.node as application.Cderived__type__collection).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		is_key_member: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__derived__value_source__derived_key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.type?.state.node.output.attribute_type())
				.then(context => context?.variant.name === 'key' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tderived_key, public parent:Cderived__type__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'derived key'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__value_source__derived__type__text = {
	'expression':Tderivation_expression;
	'recursion':Trecursion_annotation;
};
export class Cexpression__value_source__derived__type__text extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly recursion:Crecursion_annotation
	};
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.recursion)
			.then(context => context?.component_root.output.location()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		is_simple_member: () => application.Csimple__attribute_type
	} = {
		is_simple_member: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__derived__value_source__expression_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.type?.state.node.output.attribute_type())
				.then(context => context?.variant.name === 'simple' ? context.variant.definition as application.Csimple__attribute_type : undefined).result!;
		})

	}
	constructor(init:Texpression__value_source__derived__type__text, public parent:Cderived__type__text) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cexpression__value_source__derived__type__text.Dexpression(init['expression'], $this),
			recursion: new Cexpression__value_source__derived__type__text.Drecursion(init['recursion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter__value_source__derived__type__text = {
};
export class Cparameter__value_source__derived__type__text extends AlanNode {
	public readonly output:{
		value_computation_phase: () => application.Ccomputation_phase;
		value_dependency_location: () => application.Crecursion_dependence;
		value_source: () => application.Cvalue_source;
	} = {
		value_computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		value_dependency_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Crecursion_dependence.Pnone).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	}
	public readonly inferences:{
		initializer: () => application.Cnode_initializing_expression
	} = {
		initializer: cache((detach:boolean) => {
			const application__node__attributes__type__property__type__text__type__derived__value_source__parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
				.then(context => context?.variant.name === 'initializer' ? context.variant.definition as application.Cnode_initializing_expression : undefined).result!;
		})

	}
	constructor(init:Tparameter__value_source__derived__type__text, public parent:Cderived__type__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_set__type__attributes = {
	'direction':Tdirection_annotation;
	'recursion':Trecursion_annotation;
	'referenced type path':Treferenced_type_path;
	'ui':Tui_reference_set_attribute;
	'value source':['reference', Treference__value_source]|['union branch', Tunion_branch];
};
export class Creference_set__type__attributes extends AlanNode {
	public definitions:{
		collection_attribute: Ccollection_attribute;
	} = {
		collection_attribute: new Ccollection_attribute({name:'reference set', definition: this}, {
			lower_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.value_source?.state.node.output.lower_bound_cardinality()).result!),
			upper_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.value_source?.state.node.output.upper_bound_cardinality()).result!),
			value_location: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.referenced_type_path)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.location()).result!),
			value_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.referenced_type_path)
				.then(context => context?.properties.path?.inferences.node_type()).result!)
		})
	}
	public readonly properties:{
		readonly direction:Cdirection_annotation,
		readonly recursion:Crecursion_annotation,
		readonly referenced_type_path:Creferenced_type_path,
		readonly ui:Cui_reference_set_attribute,
		readonly value_source:Creference_set__type__attributes.Dvalue_source
	};
	public readonly output:{
		attribute_type: () => application.Cattribute_type;
	} = {
		attribute_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cattribute_type.Psimple).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})

	}
	constructor(init:Treference_set__type__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			direction: new Creference_set__type__attributes.Ddirection(init['direction'], $this),
			recursion: new Creference_set__type__attributes.Drecursion(init['recursion'], $this),
			referenced_type_path: new Creferenced_type_path(init['referenced type path'], $this),
			ui: new Creference_set__type__attributes.Dui(init['ui'], $this),
			value_source: new Creference_set__type__attributes.Dvalue_source(init['value source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference set'`; }
	public get entity() { return this.parent.entity; }
}
export type Treferenced_type_path = {
	'head':Tobject_path_tail;
	'path':Tplural_object_path_tail;
	'root':'context'|['context', {}]|['sibling', Tsibling__root];
};
export class Creferenced_type_path extends AlanNode {
	public readonly properties:{
		readonly head:Cobject_path_tail,
		readonly path:Cplural_object_path_tail & { readonly inferences: {
			node_context: () => application.Cnode_navigation_context;
			node_type: () => application.Cnode__application;
			subset_dependency: () => application.Cdependency__application;
		} },
		readonly root:Creferenced_type_path.Droot
	};
	constructor(init:Treferenced_type_path, public parent:Creference_set__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			head: new Creferenced_type_path.Dhead(init['head'], $this),
			path: new Creferenced_type_path.Dpath(init['path'], $this),
			root: new Creferenced_type_path.Droot(init['root'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'referenced type path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext = {
};
export class Ccontext extends AlanNode {
	public readonly output:{
		context: () => application.Cnavigation_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.head)
			.then(context => context?.component_root.output.context()).result!)
	}
	constructor(init:Tcontext, public parent:Creferenced_type_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'root'?'context'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsibling__root = {
	'definition':Tsibling_reference_definition;
};
export class Csibling__root extends AlanNode {
	public readonly properties:{
		readonly definition:Csibling_reference_definition & { readonly inferences: {
			relative_location_of_referenced_node: () => application.Crelative_object_location;
			sibling_subset: () => application.Cderived_set;
		} }
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.definition?.inferences.sibling_subset())
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.collection_object)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.dependency()).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.existence()).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.location()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.value_source_dependence()).result!

				});
			}).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property,
		direction: () => application.Cupstream__direction__application,
		head_node_type: () => application.Cnode__application
	} = {
		collection: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__referenced_type_path__root__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.head_node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		direction: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__referenced_type_path__root__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.direction)
				.then(context => context?.component_root.output.direction())
				.then(context => context?.variant.name === 'upstream' ? context.variant.definition as application.Cupstream__direction__application : undefined).result!;
		})
		,
		head_node_type: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__referenced_type_path__root__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tsibling__root, public parent:Creferenced_type_path) {
		super();
		const $this = this;
		this.properties = {
			definition: new Csibling__root.Ddefinition(init['definition'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'root'?'sibling'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference__value_source = {
	'reference':Tdependency_step;
};
export class Creference__value_source extends AlanNode {
	public readonly properties:{
		readonly reference:Cdependency_step & { readonly inferences: {
			entry_step: () => application.Centry_step_definition;
			inverse_relative_node_location: () => application.Crelative_object_location;
			referenced_node_type: () => application.Cnode__application;
			relative_location_of_referenced_node: () => application.Crelative_object_location;
			root_dependency: () => application.Centity__application;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		reference_definer: () => application.Creference_definer;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
		value_source: () => application.Cvalue_source;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.referenced_type_path)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.reference)
					.then(context => context?.component_root.output.phase()).result!

				})).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pzero).result!),
		reference_definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.reference)
			.then(context => context?.component_root.output.definer()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.reference?.inferences.entry_step())
			.then(context => context?.component_root.output.iubc()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.reference)
			.then(context => context?.component_root.output.definer())
			.then(context => context?.component_root.output.value_source_dependence()).result!)
	}
	public readonly inferences:{
		collection_step_for_inversion: () => application.Ccollection__type__property
	} = {
		collection_step_for_inversion: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__value_source__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.referenced_type_path)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.dependency())
				.then(context => context?.variant.name === 'step' ? context.variant.definition as application.Cdependency_definition : undefined)
				.then(context => context?.variant.name === 'collection step' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Treference__value_source, public parent:Creference_set__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			reference: new Creference__value_source.Dreference(init['reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunion_branch = {
	'branch':string;
};
export class Cunion_branch extends AlanNode {
	public readonly properties:{
		readonly branch:Cunion_branch.Dbranch
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		reference_definer: () => application.Creference_definer;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
		value_source: () => application.Cvalue_source;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.branch?.ref)
			.then(context => context?.parent)
			.then(context => context?.properties.has_predecessor)
			.then(context => {
				const state_group = context;
				switch (state_group?.state.name) {
					case 'no': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.properties.branch?.ref)
							.then(context => context?.parent)
							.then(context => context?.properties.has_successor)
							.then(context => {
								const state_group = context;
								switch (state_group?.state.name) {
									case 'no': {
										return resolve(context)
											.then(() => this)
											.then(() => application.Clower_bound_cardinality.Pone).result;
									}
									case 'yes': {
										return resolve(context)
											.then(() => this)
											.then(() => application.Clower_bound_cardinality.Pzero).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
								};}).result;
					}
					case 'yes': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Clower_bound_cardinality.Pzero).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
				};}).result!),
		reference_definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.branch?.ref)
			.then(context => context?.properties.dependency)
			.then(context => context?.component_root.output.definer()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.branch?.inferences.upper_bound_cardinality()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cderived_value_source.Pexpression)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		derived_parent_collection: () => application.Cyes__key_constraint,
		parent_collection: () => application.Ccollection__type__property,
		union: () => application.Cunion
	} = {
		derived_parent_collection: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__value_source__union_branch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.parent_collection())
				.then(context => {
					if (context?.properties.type.state.name === 'derived') {
						return resolve(context.properties.type.state.node as application.Cderived__type__collection)
						.then(context => {
							if (context?.properties.key_constraint.state.name === 'yes') {
								return context.properties.key_constraint.state.node as application.Cyes__key_constraint;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		parent_collection: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__value_source__union_branch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		union: cache((detach:boolean) => {
			const application__node__attributes__type__reference_set__value_source__union_branch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.derived_parent_collection())
				.then(context => context?.properties.expression)
				.then(context => {
					if (context?.properties.type.state.name === 'union') {
						return resolve(context.properties.type.state.node as application.Cunion).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tunion_branch, public parent:Creference_set__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			branch: new Cunion_branch.Dbranch(init['branch'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value source'?'union branch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_attributes = {
};
export class Cno__has_attributes extends AlanNode {
	constructor(init:Tno__has_attributes, public parent:Cnode__application) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has attributes'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_attributes = {
	'first':string;
	'last':string;
};
export class Cyes__has_attributes extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_attributes.Dfirst,
		readonly last:Cyes__has_attributes.Dlast
	};
	constructor(init:Tyes__has_attributes, public parent:Cnode__application) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_attributes.Dfirst(init['first'], $this),
			last: new Cyes__has_attributes.Dlast(init['last'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has attributes'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vnavigation_support = { name: 'unbounded', definition: (typeof Cnavigation_support.Punbounded), widening_index: 0}|{ name: 'entity', definition: (typeof Cnavigation_support.Pentity), widening_index: 1}|{ name: 'descendant', definition: (typeof Cnavigation_support.Pdescendant), widening_index: 2}
export class Cnavigation_support extends AlanStruct {
	public static Punbounded:Cnavigation_support = new class PrimitiveInstance extends Cnavigation_support {
		constructor () {
			super({name: 'unbounded', definition: undefined as unknown as Cnavigation_support, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pentity:Cnavigation_support = new class PrimitiveInstance extends Cnavigation_support {
		constructor () {
			super({name: 'entity', definition: undefined as unknown as Cnavigation_support, widening_index: 1})
			this.variant.definition = this;
		}
	}
	public static Pdescendant:Cnavigation_support = new class PrimitiveInstance extends Cnavigation_support {
		constructor () {
			super({name: 'descendant', definition: undefined as unknown as Cnavigation_support, widening_index: 2})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnavigation_support) { super(); }
	public cast<K extends Vnavigation_support['name']>(_variant:K):Extract<Vnavigation_support, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnavigation_support['name']]:(($:Extract<Vnavigation_support, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'navigation support'`; }
	public is(other:Cnavigation_support):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cdescendant = application.Cnavigation_support;
export import Centity__navigation_support = application.Cnavigation_support;
export import Cunbounded__navigation_support = application.Cnavigation_support;
type Vnavigation_context = { name: 'attribute', definition: application.Cattributes}|{ name: 'value', definition: application.Cvalue_object}
export class Cnavigation_context extends AlanStruct {
	constructor(
		public readonly variant:Vnavigation_context, public input: {
			dependency: () => application.Cdependency__application,
			direction: () => application.Cdirection,
			existence: () => application.Cexistence__application,
			location: () => application.Crelative_object_location,
			value_source_dependence: () => application.Cvalue_source
		}) { super(); }
	public readonly output:{
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		existence: () => application.Cexistence__application;
		location: () => application.Crelative_object_location;
		node_context: () => application.Coptional_node_navigation_context;
		value: () => application.Cvalue_object;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.direction()).result!),
		existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.existence()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!),
		node_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'attribute': {
						const application__navigation_context_out___node_context___attribute_nval = context.cast('attribute');
						return resolve(context)
							.then(context => application__navigation_context_out___node_context___attribute_nval)
							.then(context => {
								const conv_context = resolve(context).result!;
								return new Cnode_navigation_context({name: 'attribute', definition: conv_context},{
								dependency: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.dependency()).result!
								, 
								direction: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.direction()).result!
								, 
								existence: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.existence()).result!
								, 
								location: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.location()).result!
								, 
								value_source_dependence: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.value_source_dependence()).result!

								});
							})
							.then(context => context?.definitions.optional_node_navigation_context).result;
					}
					case 'value': {
						const application__navigation_context_out___node_context___value_nval = context.cast('value');
						return resolve(context)
							.then(context => application__navigation_context_out___node_context___value_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'choice': {
										return resolve(context)
											.then(() => this)
											.then(() => application.Coptional_node_navigation_context.Pnone).result;
									}
									case 'collection': {
										return resolve(context)
											.then(() => this)
											.then(() => application.Coptional_node_navigation_context.Pnone).result;
									}
									case 'leaf': {
										return resolve(context)
											.then(() => this)
											.then(() => application.Coptional_node_navigation_context.Pnone).result;
									}
									case 'node': {
										const application__navigation_context_out___node_context___value___node_nval = context.cast('node');
										return resolve(context)
											.then(context => application__navigation_context_out___node_context___value___node_nval)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cnode_navigation_context({name: 'node', definition: conv_context},{
												dependency: () => resolve(this)
													.then(() => this)
													.then(context => context?.component_root.input.dependency()).result!
												, 
												direction: () => resolve(this)
													.then(() => this)
													.then(context => context?.component_root.input.direction()).result!
												, 
												existence: () => resolve(this)
													.then(() => this)
													.then(context => context?.component_root.input.existence()).result!
												, 
												location: () => resolve(this)
													.then(() => this)
													.then(context => context?.component_root.input.location()).result!
												, 
												value_source_dependence: () => resolve(this)
													.then(() => this)
													.then(context => context?.component_root.input.value_source_dependence()).result!

												});
											})
											.then(context => context?.definitions.optional_node_navigation_context).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'attribute': {
						const application__navigation_context_out___value___attribute_nval = context.cast('attribute');
						return resolve(context)
							.then(context => application__navigation_context_out___value___attribute_nval)
							.then(context => context?.parent)
							.then(context => context?.definitions.value_object).result;
					}
					case 'value': {
						const application__navigation_context_out___value___value_nval = context.cast('value');
						return resolve(context)
							.then(context => application__navigation_context_out___value___value_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source_dependence()).result!)
	};
	public cast<K extends Vnavigation_context['name']>(_variant:K):Extract<Vnavigation_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnavigation_context['name']]:(($:Extract<Vnavigation_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'navigation context'`; }
	public is(other:Cnavigation_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tnamed_object_step = {
	'name':['explicit', Texplicit__name__named_object_step]|'implicit'|['implicit', {}];
};
export class Cnamed_object_step extends AlanNode {
	public readonly properties:{
		readonly name:Cnamed_object_step.Dname
	};
	public readonly output:{
		assignment: () => application.Cnamed_object_assignment;
	} = {
		assignment: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.name?.state.node.output.assignment()).result!)
	};
	constructor(init:Tnamed_object_step, public location:AlanNode, public input: {
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			name: new Cnamed_object_step.Dname(init['name'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texplicit__name__named_object_step = {
	'head':Tancestor_named_object_path;
	'named object':string;
};
export class Cexplicit__name__named_object_step extends AlanNode {
	public readonly properties:{
		readonly head:Cancestor_named_object_path & { readonly inferences: {
			assignment: () => application.Cnamed_object_assignment;
			explicit_name: () => application.Cexplicit__name__named_object_assignment;
		} },
		readonly named_object:Cexplicit__name__named_object_step.Dnamed_object
	};
	public readonly output:{
		assignment: () => application.Cnamed_object_assignment;
	} = {
		assignment: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head?.inferences.assignment()).result!)
	}
	constructor(init:Texplicit__name__named_object_step, public parent:Cnamed_object_step) {
		super();
		const $this = this;
		this.properties = {
			head: new Cexplicit__name__named_object_step.Dhead(init['head'], $this),
			named_object: new Cexplicit__name__named_object_step.Dnamed_object(init['named object'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Timplicit__name__named_object_step = {
};
export class Cimplicit__name__named_object_step extends AlanNode {
	public readonly output:{
		assignment: () => application.Cnamed_object_assignment;
	} = {
		assignment: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.assignment()).result!)
	}
	public readonly inferences:{
		assignment: () => application.Cnamed_object_assignment,
		implicit_name: () => application.Cimplicit__name__named_object_assignment
	} = {
		assignment: cache((detach:boolean) => {
			const application__named_object_step__name__implicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.variable())
				.then(context => context?.variant.name === 'assigned' ? context.variant.definition as application.Cnamed_object_assignment : undefined).result!;
		})
		,
		implicit_name: cache((detach:boolean) => {
			const application__named_object_step__name__implicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.assignment())
				.then(context => {
					if (context?.properties.name.state.name === 'implicit') {
						return resolve(context.properties.name.state.node as application.Cimplicit__name__named_object_assignment).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Timplicit__name__named_object_step, public parent:Cnamed_object_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'implicit'`; }
	public get entity() { return this.parent.entity; }
}
type Vnamed_object = { name: 'assigned', definition: application.Cnamed_object_assignment}|{ name: 'unassigned', definition: (typeof Cnamed_object.Punassigned)}
export class Cnamed_object extends AlanStruct {
	public static Punassigned:Cnamed_object = new class PrimitiveInstance extends Cnamed_object {
		constructor () {
			super({name: 'unassigned', definition: undefined as unknown as Cnamed_object})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnamed_object) { super(); }
	public cast<K extends Vnamed_object['name']>(_variant:K):Extract<Vnamed_object, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnamed_object['name']]:(($:Extract<Vnamed_object, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'named object'`; }
	public is(other:Cnamed_object):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cunassigned__named_object = application.Cnamed_object;
export type Tnamed_object_assignment = {
	'name':['explicit', Texplicit__name__named_object_assignment]|'implicit'|['implicit', {}];
};
export class Cnamed_object_assignment extends AlanNode {
	public definitions:{
		named_object: Cnamed_object;
	} = {
		named_object: new Cnamed_object({name:'assigned', definition: this})
	}
	public readonly properties:{
		readonly name:Cnamed_object_assignment.Dname
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		definer: () => application.Cnamed_object_definer;
		parent_variable: () => application.Cnamed_object;
		phase: () => application.Ccomputation_phase;
		value_source: () => application.Cvalue_source;
		variable: () => application.Cnamed_object;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.definer()).result!),
		parent_variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_variable()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.phase()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!),
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.named_object).result!)
	};
	constructor(init:Tnamed_object_assignment, public location:AlanNode, public input: {
		context: () => application.Cnavigation_context,
		definer: () => application.Cnamed_object_definer,
		parent_variable: () => application.Cnamed_object,
		phase: () => application.Ccomputation_phase,
		value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			name: new Cnamed_object_assignment.Dname(init['name'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texplicit__name__named_object_assignment = {
	'name':string;
	'named objects':Record<string, Tnamed_objects__explicit__name__named_object_assignment>;
};
export class Cexplicit__name__named_object_assignment extends AlanNode {
	public readonly properties:{
		readonly name:Cexplicit__name__named_object_assignment.Dname,
		readonly named_objects:Cexplicit__name__named_object_assignment.Dnamed_objects
	};
	constructor(init:Texplicit__name__named_object_assignment, public parent:Cnamed_object_assignment) {
		super();
		const $this = this;
		this.properties = {
			name: new Cexplicit__name__named_object_assignment.Dname(init['name'], $this),
			named_objects: new Cexplicit__name__named_object_assignment.Dnamed_objects(init['named objects'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnamed_objects__explicit__name__named_object_assignment = {
	'has successor':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cnamed_objects__explicit__name__named_object_assignment extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_successor:Cnamed_objects__explicit__name__named_object_assignment.Dhas_successor
	};
	constructor(key:string, init:Tnamed_objects__explicit__name__named_object_assignment, public parent:Cexplicit__name__named_object_assignment) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_successor: new Cnamed_objects__explicit__name__named_object_assignment.Dhas_successor(init['has successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'named objects'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor__named_objects__explicit__name__named_object_assignment = {
};
export class Cno__has_successor__named_objects__explicit__name__named_object_assignment extends AlanNode {
	constructor(init:Tno__has_successor__named_objects__explicit__name__named_object_assignment, public parent:Cnamed_objects__explicit__name__named_object_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__named_objects__explicit__name__named_object_assignment = {
};
export class Cyes__has_successor__named_objects__explicit__name__named_object_assignment extends AlanNode {
	constructor(init:Tyes__has_successor__named_objects__explicit__name__named_object_assignment, public parent:Cnamed_objects__explicit__name__named_object_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Timplicit__name__named_object_assignment = {
};
export class Cimplicit__name__named_object_assignment extends AlanNode {
	constructor(init:Timplicit__name__named_object_assignment, public parent:Cnamed_object_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'implicit'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__merge_locations_impl(detach:boolean, application__merge_locations_impl_nval:application.Capplication, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location_support: () => application.Crecursion_dependence,
	reference_context_location: () => application.Crelative_object_location
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.context_location())
					.then(context => context?.component_root.output.entity()).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.reference_context_location())
					.then(context => context?.component_root.output.entity()).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(context => evaluate__merge_location_for_identical_entity(detach, 
				resolve(context)
					.then(context => application__merge_locations_impl_nval).result!, {
				context_location: () => resolve(self)
					.then(() => self)
					.then(context => context?.component_root.input.context_location()).result!
				, 
				reference_context_location: () => resolve(self)
					.then(context => evaluate__check_support_for_navigation_to_location(detach, 
						resolve(context)
							.then(context => application__merge_locations_impl_nval).result!, {
						dependency_location_support: () => resolve(self)
							.then(() => self)
							.then(context => context?.component_root.input.dependency_location_support()).result!
						, 
						reference_context_location: () => resolve(self)
							.then(() => self)
							.then(context => context?.component_root.input.reference_context_location()).result!

						})).result!

				})).result
			} else {
				return resolve(match_context)
				.then(context => evaluate__compute_most_significant_location(detach, 
					resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.reference_context_location())
						.then(context => context?.component_root.output.entity()).result!, {
					context_location: () => resolve(self)
						.then(() => self)
						.then(context => context?.component_root.input.context_location()).result!
					, 
					dependency_location_support: () => resolve(self)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_location_support()).result!
					, 
					reference_context_location: () => resolve(self)
						.then(() => self)
						.then(context => context?.component_root.input.reference_context_location()).result!
					, 
					root: () => resolve(self)
						.then(context => application__merge_locations_impl_nval).result!

					})).result
			}
		}).result!
}
function evaluate__merge_locations(detach:boolean, application__merge_locations_nval:application.Capplication, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location_support: () => application.Crecursion_dependence,
	dependency_root_location: () => application.Croot_location,
	reference_context_location: () => application.Crelative_object_location,
	target_root_location: () => application.Croot_location
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.target_root_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'dataset': {
					return resolve(context)
						.then(context => evaluate__merge_locations_impl(detach, 
							resolve(context)
								.then(context => application__merge_locations_nval).result!, {
							context_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.context_location()).result!
							, 
							dependency_location_support: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.dependency_location_support()).result!
							, 
							reference_context_location: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.reference_context_location()).result!

							})).result;
				}
				case 'operation': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_root_location())
						.then(context => {
							switch (context?.variant.name) {
								case 'dataset': {
									const application__merge_locations___operation___dataset_nval = context.cast('dataset');
									return resolve(context)
										.then(context => application__merge_locations___operation___dataset_nval)
										.then(context => context?.definitions.entity)
										.then(context => context?.definitions.relative_object_location).result;
								}
								case 'operation': {
									return resolve(context)
										.then(context => evaluate__merge_locations_impl(detach, 
											resolve(context)
												.then(context => application__merge_locations_nval).result!, {
											context_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.context_location()).result!
											, 
											dependency_location_support: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.dependency_location_support()).result!
											, 
											reference_context_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.reference_context_location()).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__merge_location_for_identical_entity(detach:boolean, application__merge_location_for_identical_entity_nval:application.Capplication, input: {
	context_location: () => application.Crelative_object_location,
	reference_context_location: () => application.Crelative_object_location
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.context_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'context entity': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.reference_context_location()).result;
				}
				case 'sibling entity': {
					const application__merge_location_for_identical_entity___sibling_entity_nval = context.cast('sibling entity');
					return resolve(context)
						.then(context => application__merge_location_for_identical_entity___sibling_entity_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'graph step': {
									const application__merge_location_for_identical_entity___sibling_entity___graph_step_nval = context.cast('graph step');
									return resolve(context)
										.then(match_context => { 
											const expression_context = resolve(match_context)
											.then(context => {
												const left = resolve(context)
													.then(() => self)
													.then(context => context?.component_root.input.context_location()).result;
												const right = resolve(context)
													.then(() => self)
													.then(context => context?.component_root.input.reference_context_location()).result;
												return left?.is(right!) ? left : undefined
											}).result;
											if (expression_context !== undefined) {
											const application__merge_location_for_identical_entity___sibling_entity___graph_step___struct_nval = expression_context;
											return resolve(match_context)
											.then(context => application__merge_location_for_identical_entity___sibling_entity___graph_step___struct_nval).result
											} else {
												return resolve(match_context)
												.then(() => self)
												.then(context => context?.component_root.input.reference_context_location())
												.then(context => {
													switch (context?.variant.name) {
														case 'context entity': {
															return resolve(context)
																.then(() => self)
																.then(context => context?.component_root.input.context_location()).result;
														}
														case 'sibling entity': {
															const application__merge_location_for_identical_entity___sibling_entity___graph_step___none___sibling_entity_nval = context.cast('sibling entity');
															return resolve(context)
																.then(context => application__merge_location_for_identical_entity___sibling_entity___graph_step___none___sibling_entity_nval)
																.then(context => context?.component_root.output.entity())
																.then(context => context?.definitions.sibling_dependency_retrieval)
																.then(context => context?.definitions.relative_object_location).result;
														}
														case undefined: return undefined;
														default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
													};
												}).result
											}
										}).result;
								}
								case 'sibling or self step': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.context_location()).result;
								}
								case 'sibling step': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.reference_context_location())
										.then(context => {
											switch (context?.variant.name) {
												case 'context entity': {
													return resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.context_location()).result;
												}
												case 'sibling entity': {
													const application__merge_location_for_identical_entity___sibling_entity___sibling_step___sibling_entity_nval = context.cast('sibling entity');
													return resolve(context)
														.then(context => application__merge_location_for_identical_entity___sibling_entity___sibling_step___sibling_entity_nval)
														.then(context => context?.component_root.output.entity())
														.then(context => context?.definitions.sibling_dependency_retrieval)
														.then(context => context?.definitions.relative_object_location).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tlower_bound_cardinality_constraint = {
	'constraint':'non empty'|['non empty', {}]|'none'|['none', {}];
};
export class Clower_bound_cardinality_constraint extends AlanNode {
	public readonly properties:{
		readonly constraint:Clower_bound_cardinality_constraint.Dconstraint
	};
	public readonly output:{
		cardinality: () => application.Clower_bound_cardinality;
	} = {
		cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.constraint?.state.node.output.cardinality()).result!)
	};
	constructor(init:Tlower_bound_cardinality_constraint, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			constraint: new Clower_bound_cardinality_constraint.Dconstraint(init['constraint'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tnon_empty = {
};
export class Cnon_empty extends AlanNode {
	public readonly output:{
		cardinality: () => application.Clower_bound_cardinality;
	} = {
		cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pone).result!)
	}
	constructor(init:Tnon_empty, public parent:Clower_bound_cardinality_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constraint'?'non empty'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__constraint = {
};
export class Cnone__constraint extends AlanNode {
	public readonly output:{
		cardinality: () => application.Clower_bound_cardinality;
	} = {
		cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Clower_bound_cardinality.Pzero).result!)
	}
	constructor(init:Tnone__constraint, public parent:Clower_bound_cardinality_constraint) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constraint'?'none'`; }
	public get entity() { return this.parent.entity; }
}
type Vlower_bound_cardinality = { name: 'zero', definition: (typeof Clower_bound_cardinality.Pzero), widening_index: 0}|{ name: 'one', definition: (typeof Clower_bound_cardinality.Pone), widening_index: 1}
export class Clower_bound_cardinality extends AlanStruct {
	public static Pzero:Clower_bound_cardinality = new class PrimitiveInstance extends Clower_bound_cardinality {
		constructor () {
			super({name: 'zero', definition: undefined as unknown as Clower_bound_cardinality, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pone:Clower_bound_cardinality = new class PrimitiveInstance extends Clower_bound_cardinality {
		constructor () {
			super({name: 'one', definition: undefined as unknown as Clower_bound_cardinality, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vlower_bound_cardinality) { super(); }
	public readonly output:{
		existence: () => application.Cexistence__application;
	} = {
		existence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'one': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cexistence__application.Pmandatory).result;
					}
					case 'zero': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cexistence__application.Poptional).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vlower_bound_cardinality['name']>(_variant:K):Extract<Vlower_bound_cardinality, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vlower_bound_cardinality['name']]:(($:Extract<Vlower_bound_cardinality, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'lower bound cardinality'`; }
	public is(other:Clower_bound_cardinality):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cone__lower_bound_cardinality = application.Clower_bound_cardinality;
export import Czero__lower_bound_cardinality = application.Clower_bound_cardinality;
export type Titem_permissions_definition = {
	'create permission':['explicit', Texplicit__create_permission]|'inherited'|['inherited', {}];
	'delete permission':['explicit', Texplicit__delete_permission]|'inherited'|['inherited', {}];
};
export class Citem_permissions_definition extends AlanNode {
	public readonly properties:{
		readonly create_permission:Citem_permissions_definition.Dcreate_permission,
		readonly delete_permission:Citem_permissions_definition.Ddelete_permission
	};
	public readonly output:{
		source: () => application.Cvalue_source;
	} = {
		source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.create_permission?.state.node.output.source()).result!)
	};
	constructor(init:Titem_permissions_definition, public location:AlanNode, public input: {
		item_node_type: () => application.Cnode__application,
		property: () => application.Cproperty__type__attributes,
		value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			create_permission: new Citem_permissions_definition.Dcreate_permission(init['create permission'], $this),
			delete_permission: new Citem_permissions_definition.Ddelete_permission(init['delete permission'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texplicit__create_permission = {
	'permission':Tpermission;
};
export class Cexplicit__create_permission extends AlanNode {
	public readonly properties:{
		readonly permission:Cpermission
	};
	public readonly output:{
		source: () => application.Cvalue_source;
	} = {
		source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.permission)
			.then(context => context?.component_root.output.source())
			.then(context => context?.definitions.base_value_source)
			.then(context => context?.definitions.value_source).result!)
	}
	public readonly inferences:{
		dataset_attribute: () => application.Capplication,
		user_value: () => application.Cuser__external_source
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__item_permissions_definition__create_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.property())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})
		,
		user_value: cache((detach:boolean) => {
			const application__item_permissions_definition__create_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
		})

	}
	constructor(init:Texplicit__create_permission, public parent:Citem_permissions_definition) {
		super();
		const $this = this;
		this.properties = {
			permission: new Cexplicit__create_permission.Dpermission(init['permission'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'create permission'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinherited__create_permission = {
};
export class Cinherited__create_permission extends AlanNode {
	public readonly output:{
		source: () => application.Cvalue_source;
	} = {
		source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	}
	constructor(init:Tinherited__create_permission, public parent:Citem_permissions_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'create permission'?'inherited'`; }
	public get entity() { return this.parent.entity; }
}
export type Texplicit__delete_permission = {
	'permission':Tpermission;
};
export class Cexplicit__delete_permission extends AlanNode {
	public readonly properties:{
		readonly permission:Cpermission & { readonly inferences: {
			valid_source: () => application.Cvalue_source;
		} }
	};
	public readonly inferences:{
		dataset_attribute: () => application.Capplication,
		user_value: () => application.Cuser__external_source
	} = {
		dataset_attribute: cache((detach:boolean) => {
			const application__item_permissions_definition__delete_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.property())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'dataset' ? context.variant.definition as application.Capplication : undefined).result!;
		})
		,
		user_value: cache((detach:boolean) => {
			const application__item_permissions_definition__delete_permission__explicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
		})

	}
	constructor(init:Texplicit__delete_permission, public parent:Citem_permissions_definition) {
		super();
		const $this = this;
		this.properties = {
			permission: new Cexplicit__delete_permission.Dpermission(init['permission'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'delete permission'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinherited__delete_permission = {
};
export class Cinherited__delete_permission extends AlanNode {
	constructor(init:Tinherited__delete_permission, public parent:Citem_permissions_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'delete permission'?'inherited'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__inverse_relative_object_location(detach:boolean, application__inverse_relative_object_location_nval:application.Crelative_object_location, ):application.Crelative_object_location {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => application__inverse_relative_object_location_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'context entity': {
					return resolve(context)
						.then(context => application__inverse_relative_object_location_nval).result;
				}
				case 'sibling entity': {
					const application__inverse_relative_object_location___sibling_entity_nval = context.cast('sibling entity');
					return resolve(context)
						.then(context => application__inverse_relative_object_location___sibling_entity_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'graph step': {
									const application__inverse_relative_object_location___sibling_entity___graph_step_nval = context.cast('graph step');
									return resolve(context)
										.then(context => application__inverse_relative_object_location___sibling_entity___graph_step_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'base order': {
													const application__inverse_relative_object_location___sibling_entity___graph_step___base_order_nval = context.cast('base order');
													return resolve(context)
														.then(context => application__inverse_relative_object_location___sibling_entity___graph_step___base_order_nval)
														.then(context => {
															const conv_context = resolve(context).result!;
															return new Cgraph_traversal_type({name: 'inverse order', definition: conv_context});
														})
														.then(context => context?.definitions.sibling_dependency_retrieval)
														.then(context => context?.definitions.relative_object_location).result;
												}
												case 'inverse order': {
													const application__inverse_relative_object_location___sibling_entity___graph_step___inverse_order_nval = context.cast('inverse order');
													return resolve(context)
														.then(context => application__inverse_relative_object_location___sibling_entity___graph_step___inverse_order_nval)
														.then(context => {
															const conv_context = resolve(context).result!;
															return new Cgraph_traversal_type({name: 'base order', definition: conv_context});
														})
														.then(context => context?.definitions.sibling_dependency_retrieval)
														.then(context => context?.definitions.relative_object_location).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'sibling or self step': {
									return resolve(context)
										.then(context => application__inverse_relative_object_location_nval).result;
								}
								case 'sibling step': {
									return resolve(context)
										.then(context => application__inverse_relative_object_location_nval).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export class Cinverse_reference_step extends AlanStruct {
	constructor(
		public readonly properties: {
			collection: () => application.Ccollection__type__property, 
			upper_bound_cardinality: () => application.Cupper_bound_cardinality
		}
	) { super(); }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'inverse reference step'`; }
	public is(other:Cinverse_reference_step):boolean {
		if (!this.properties.collection().is(other.properties.collection())) return false;
		if (!this.properties.upper_bound_cardinality().is(other.properties.upper_bound_cardinality())) return false;
		return true;
	}
}
export type Tinterface_instance_initializer = {
	'initializer':Tcommand_object_expression;
};
export class Cinterface_instance_initializer extends AlanNode {
	public definitions:{
		command_initialization_operation: Ccommand_initialization_operation;
	} = {
		command_initialization_operation: new Ccommand_initialization_operation({name:'interface instance', definition: this})
	}
	public readonly properties:{
		readonly initializer:Ccommand_object_expression
	};
	public readonly output:{
		interface_: () => application.Cmany__instances;
	} = {
		interface_: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.interface_()).result!)
	};
	constructor(init:Tinterface_instance_initializer, public location:AlanNode, public input: {
		interface_: () => application.Cmany__instances
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initializer: new Cinterface_instance_initializer.Dinitializer(init['initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
function evaluate__inspect_dependency(detach:boolean, application__inspect_dependency_nval:application.Cdependency__application, input: {
	dep: () => application.Cdependency__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => evaluate___inspect_dependency(detach, 
			resolve(context)
				.then(() => self)
				.then(context => context?.component_root.input.dep()).result!, )).result!
}
function evaluate__inline_dependency_step(detach:boolean, application__inline_dependency_step_nval:application.Cdependency_definition, input: {
	parent: () => application.Cdependency__application
}):application.Cdependency_definition {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__inline_dependency_step_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'collection step': {
					const application__inline_dependency_step___collection_step_nval = context.cast('collection step');
					return resolve(context)
						.then(context => application__inline_dependency_step___collection_step_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cdependency_definition({name: 'collection step', definition: conv_context},{
							location: () => resolve(self)
								.then(context => application__inline_dependency_step_nval)
								.then(context => context?.component_root.output.location()).result!
							, 
							parent: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.parent()).result!

							});
						}).result;
				}
				case 'entry step': {
					const application__inline_dependency_step___entry_step_nval = context.cast('entry step');
					return resolve(context)
						.then(context => application__inline_dependency_step___entry_step_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cdependency_definition({name: 'entry step', definition: conv_context},{
							location: () => resolve(self)
								.then(context => application__inline_dependency_step_nval)
								.then(context => context?.component_root.output.location()).result!
							, 
							parent: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.parent()).result!

							});
						}).result;
				}
				case 'inverse reference step': {
					const application__inline_dependency_step___inverse_reference_step_nval = context.cast('inverse reference step');
					return resolve(context)
						.then(context => application__inline_dependency_step___inverse_reference_step_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cdependency_definition({name: 'inverse reference step', definition: conv_context},{
							location: () => resolve(self)
								.then(context => application__inline_dependency_step_nval)
								.then(context => context?.component_root.output.location()).result!
							, 
							parent: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.parent()).result!

							});
						}).result;
				}
				case 'recursion start step': {
					const application__inline_dependency_step___recursion_start_step_nval = context.cast('recursion start step');
					return resolve(context)
						.then(context => application__inline_dependency_step___recursion_start_step_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cdependency_definition({name: 'recursion start step', definition: conv_context},{
							location: () => resolve(self)
								.then(context => application__inline_dependency_step_nval)
								.then(context => context?.component_root.output.location()).result!
							, 
							parent: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.parent()).result!

							});
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__inline_dependency_chain(detach:boolean, application__inline_dependency_chain_nval:application.Cdependency__application, input: {
	target: () => application.Cdependency__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.target())
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application__inline_dependency_chain_nval).result;
				}
				case 'step': {
					return resolve(context)
						.then(context => application__inline_dependency_chain_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'self': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.target()).result;
								}
								case 'step': {
									const application__inline_dependency_chain___step___step_nval = context.cast('step');
									return resolve(context)
										.then(context => evaluate__inline_dependency_step(detach, 
											resolve(context)
												.then(context => application__inline_dependency_chain___step___step_nval).result!, {
											parent: () => resolve(self)
												.then(context => evaluate__inline_dependency_chain(detach, 
													resolve(context)
														.then(context => application__inline_dependency_chain___step___step_nval)
														.then(context => context?.component_root.output.parent()).result!, {
													target: () => resolve(self)
														.then(() => self)
														.then(context => context?.component_root.input.target()).result!

													})).result!

											}))
										.then(context => context?.definitions.dependency).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tidentity_initializer = {
	'initializer':Tcommand_object_expression;
};
export class Cidentity_initializer extends AlanNode {
	public definitions:{
		command_initialization_operation: Ccommand_initialization_operation;
	} = {
		command_initialization_operation: new Ccommand_initialization_operation({name:'identity', definition: this})
	}
	public readonly properties:{
		readonly initializer:Ccommand_object_expression
	};
	public readonly output:{
		identity: () => application.Cproperty__type__attributes;
		username: () => application.Cproperty__type__attributes;
	} = {
		identity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.identities_collection())
			.then(context => context?.properties.key_property?.ref)
			.then(context => context?.parent).result!),
		username: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.username()).result!)
	};
	constructor(init:Tidentity_initializer, public location:AlanNode, public input: {
		identities_collection: () => application.Ccollection__type__property,
		username: () => application.Cproperty__type__attributes
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initializer: new Cidentity_initializer.Dinitializer(init['initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
function evaluate__identical_recursion_dependence(detach:boolean, application__identical_recursion_dependence_nval:application.Crecursion_dependence, input: {
	phase: () => application.Ccomputation_phase
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.phase()).result!
}
export type Tgroup_ancestor_node_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__group_ancestor_node_path];
};
export class Cgroup_ancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cgroup_ancestor_node_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tgroup_ancestor_node_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cgroup_ancestor_node_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__group_ancestor_node_path = {
};
export class Cno__has_steps__group_ancestor_node_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__group_ancestor_node_path, public parent:Cgroup_ancestor_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__group_ancestor_node_path = {
	'tail':Tgroup_ancestor_node_path;
};
export class Cyes__has_steps__group_ancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cgroup_ancestor_node_path
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const application__group_ancestor_node_path__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'group' ? context.variant.definition as application.Cgroup__type__property : undefined).result!;
		})

	}
	constructor(init:Tyes__has_steps__group_ancestor_node_path, public parent:Cgroup_ancestor_node_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__group_ancestor_node_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vgraph_traversal_type = { name: 'inverse order', definition: application.Cgraphs}|{ name: 'base order', definition: application.Cgraphs}
export class Cgraph_traversal_type extends AlanStruct {
	constructor(
		public readonly variant:Vgraph_traversal_type) { super(); }
	public definitions:{
		sibling_dependency_retrieval: Csibling_dependency_retrieval;
	} = {
		sibling_dependency_retrieval: new Csibling_dependency_retrieval({name:'graph step', definition: this})
	}
	public readonly output:{
		entity: () => application.Centity__application;
		graph: () => application.Cgraphs;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'base order': {
						const application__graph_traversal_type_out___entity___base_order_nval = context.cast('base order');
						return resolve(context)
							.then(context => application__graph_traversal_type_out___entity___base_order_nval)
							.then(context => context?.component_root.output.collection())
							.then(context => context?.definitions.entity).result;
					}
					case 'inverse order': {
						const application__graph_traversal_type_out___entity___inverse_order_nval = context.cast('inverse order');
						return resolve(context)
							.then(context => application__graph_traversal_type_out___entity___inverse_order_nval)
							.then(context => context?.component_root.output.collection())
							.then(context => context?.definitions.entity).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		graph: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'base order': {
						const application__graph_traversal_type_out___graph___base_order_nval = context.cast('base order');
						return resolve(context)
							.then(context => application__graph_traversal_type_out___graph___base_order_nval).result;
					}
					case 'inverse order': {
						const application__graph_traversal_type_out___graph___inverse_order_nval = context.cast('inverse order');
						return resolve(context)
							.then(context => application__graph_traversal_type_out___graph___inverse_order_nval).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vgraph_traversal_type['name']>(_variant:K):Extract<Vgraph_traversal_type, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vgraph_traversal_type['name']]:(($:Extract<Vgraph_traversal_type, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'graph traversal type'`; }
	public is(other:Cgraph_traversal_type):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tgraph_traversal_selector = {
	'ancestor path':Tancestor_node_path;
	'graph traversal':Tgraph_traversal_definition;
	'has tail':'no'|['no', {}]|['yes', Tyes__has_tail__graph_traversal_selector];
};
export class Cgraph_traversal_selector extends AlanNode {
	public readonly properties:{
		readonly ancestor_path:Cancestor_node_path & { readonly inferences: {
			base_collection: () => application.Cbase__type__collection;
			collection: () => application.Ccollection__type__property;
		} },
		readonly graph_traversal:Cgraph_traversal_definition,
		readonly has_tail:Cgraph_traversal_selector.Dhas_tail
	};
	public readonly output:{
		traversal: () => application.Cgraph_traversal;
	} = {
		traversal: cache((detach:boolean) => resolve(this).then(context => new Cgraph_traversal({
			parent: () => resolve(this)
				.then(() => this)
				.then(context => context?.properties.has_tail?.state.node.output.traversal()).result!, 
			traversal: () => resolve(this)
				.then(() => this)
				.then(context => context?.properties.graph_traversal)
				.then(context => context?.component_root.output.type()).result!}), ).result!)
	};
	constructor(init:Tgraph_traversal_selector, public location:AlanNode, public input: {
		this_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			ancestor_path: new Cgraph_traversal_selector.Dancestor_path(init['ancestor path'], $this),
			graph_traversal: new Cgraph_traversal_selector.Dgraph_traversal(init['graph traversal'], $this),
			has_tail: new Cgraph_traversal_selector.Dhas_tail(init['has tail'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_tail__graph_traversal_selector = {
};
export class Cno__has_tail__graph_traversal_selector extends AlanNode {
	public readonly output:{
		traversal: () => application.Coptional_graph_traversal;
	} = {
		traversal: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.ancestor_path?.inferences.collection())
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.component_root.output.entity())
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Coptional_graph_traversal({name: 'none', definition: conv_context});
			}).result!)
	}
	constructor(init:Tno__has_tail__graph_traversal_selector, public parent:Cgraph_traversal_selector) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_tail__graph_traversal_selector = {
	'tail':Tgraph_traversal_selector;
};
export class Cyes__has_tail__graph_traversal_selector extends AlanNode {
	public readonly properties:{
		readonly tail:Cgraph_traversal_selector
	};
	public readonly output:{
		traversal: () => application.Coptional_graph_traversal;
	} = {
		traversal: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.traversal())
			.then(context => context?.definitions.optional_graph_traversal).result!)
	}
	constructor(init:Tyes__has_tail__graph_traversal_selector, public parent:Cgraph_traversal_selector) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_tail__graph_traversal_selector.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgraph_traversal_definition = {
	'graph':string;
	'traversal type':'base order'|['base order', {}]|'inverse order'|['inverse order', {}];
};
export class Cgraph_traversal_definition extends AlanNode {
	public readonly properties:{
		readonly graph:Cgraph_traversal_definition.Dgraph,
		readonly traversal_type:Cgraph_traversal_definition.Dtraversal_type
	};
	public readonly output:{
		graph: () => application.Cgraphs;
		type: () => application.Cgraph_traversal_type;
	} = {
		graph: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.graph?.ref).result!),
		type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.traversal_type?.state.node.output.type()).result!)
	};
	constructor(init:Tgraph_traversal_definition, public location:AlanNode, public input: {
		collection: () => application.Ccollection__type__property,
		value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			graph: new Cgraph_traversal_definition.Dgraph(init['graph'], $this),
			traversal_type: new Cgraph_traversal_definition.Dtraversal_type(init['traversal type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tbase_order = {
};
export class Cbase_order extends AlanNode {
	public readonly output:{
		type: () => application.Cgraph_traversal_type;
	} = {
		type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.graph?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cgraph_traversal_type({name: 'base order', definition: conv_context});
			}).result!)
	}
	constructor(init:Tbase_order, public parent:Cgraph_traversal_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'traversal type'?'base order'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinverse_order = {
};
export class Cinverse_order extends AlanNode {
	public readonly output:{
		type: () => application.Cgraph_traversal_type;
	} = {
		type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.graph?.ref)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cgraph_traversal_type({name: 'inverse order', definition: conv_context});
			}).result!)
	}
	public readonly inferences:{
		derived_value_source: () => application.Cderived_value_source
	} = {
		derived_value_source: cache((detach:boolean) => {
			const application__graph_traversal_definition__traversal_type__inverse_order_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.value_source())
				.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined).result!;
		})

	}
	constructor(init:Tinverse_order, public parent:Cgraph_traversal_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'traversal type'?'inverse order'`; }
	public get entity() { return this.parent.entity; }
}
export class Cgraph_traversal extends AlanStruct {
	constructor(
		public readonly properties: {
			parent: () => application.Coptional_graph_traversal, 
			traversal: () => application.Cgraph_traversal_type
		}
	) { super(); }
	public definitions:{
		optional_graph_traversal: Coptional_graph_traversal;
		recursion_dependence: Crecursion_dependence;
	} = {
		optional_graph_traversal: new Coptional_graph_traversal({name:'graph', definition: this}),
		recursion_dependence: new Crecursion_dependence({name:'recursion', definition: this})
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'graph traversal'`; }
	public is(other:Cgraph_traversal):boolean {
		if (!this.properties.parent().is(other.properties.parent())) return false;
		if (!this.properties.traversal().is(other.properties.traversal())) return false;
		return true;
	}
}
export type Tgraph_constraints_definition = {
	'graphs':Record<string, Tgraphs>;
};
export class Cgraph_constraints_definition extends AlanNode {
	public readonly properties:{
		readonly graphs:Cgraph_constraints_definition.Dgraphs
	};
	public readonly output:{
		collection: () => application.Ccollection__type__property;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.collection()).result!)
	};
	constructor(init:Tgraph_constraints_definition, public location:AlanNode, public input: {
		collection: () => application.Ccollection__type__property
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			graphs: new Cgraph_constraints_definition.Dgraphs(init['graphs'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tgraphs = {
	'type':'acyclic'|['acyclic', {}]|['ordered', Tordered];
	'value type':'base'|['base', {}];
};
export class Cgraphs extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly type:Cgraphs.Dtype,
		readonly value_type:Cgraphs.Dvalue_type
	};
	constructor(key:string, init:Tgraphs, public parent:Cgraph_constraints_definition) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			type: new Cgraphs.Dtype(init['type'], $this),
			value_type: new Cgraphs.Dvalue_type(init['value type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'graphs'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tacyclic = {
};
export class Cacyclic extends AlanNode {
	constructor(init:Tacyclic, public parent:Cgraphs) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'acyclic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordered = {
	'ordering property path':Tordering_property_path;
};
export class Cordered extends AlanNode {
	public readonly properties:{
		readonly ordering_property_path:Cordering_property_path
	};
	constructor(init:Tordered, public parent:Cgraphs) {
		super();
		const $this = this;
		this.properties = {
			ordering_property_path: new Cordering_property_path(init['ordering property path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'ordered'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordering_property_path = {
	'ordering state group path':Tdescendant_base_property_path;
	'ordering states':Tordering_states;
};
export class Cordering_property_path extends AlanNode {
	public readonly properties:{
		readonly ordering_state_group_path:Cdescendant_base_property_path & { readonly inferences: {
			base_value: () => application.Cbase__type__state_group;
			state_group: () => application.Cstate_group;
		} },
		readonly ordering_states:Cordering_states
	};
	constructor(init:Tordering_property_path, public parent:Cordered) {
		super();
		const $this = this;
		this.properties = {
			ordering_state_group_path: new Cordering_property_path.Dordering_state_group_path(init['ordering state group path'], $this),
			ordering_states: new Cordering_states(init['ordering states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordering property path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordering_states = {
	'edge reference':string;
	'edge state':string;
	'sink state':string;
};
export class Cordering_states extends AlanNode {
	public readonly properties:{
		readonly edge_reference:Cordering_states.Dedge_reference,
		readonly edge_state:Cordering_states.Dedge_state,
		readonly sink_state:Cordering_states.Dsink_state
	};
	constructor(init:Tordering_states, public parent:Cordering_property_path) {
		super();
		const $this = this;
		this.properties = {
			edge_reference: new Cordering_states.Dedge_reference(init['edge reference'], $this),
			edge_state: new Cordering_states.Dedge_state(init['edge state'], $this),
			sink_state: new Cordering_states.Dsink_state(init['sink state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordering states'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbase__value_type = {
};
export class Cbase__value_type extends AlanNode {
	public readonly inferences:{
		base_collection: () => application.Cbase__type__collection
	} = {
		base_collection: cache((detach:boolean) => {
			const application__graph_constraints_definition__graphs__value_type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.collection())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__collection).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tbase__value_type, public parent:Cgraphs) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__generalize_dependency___on_match(detach:boolean, application__generalize_dependency___on_match_nval:application.Cdependency__application, input: {
	first_matching_step_from_actual: () => application.Cdependency_definition,
	gen: () => application.Cdependency_gen,
	gen_step: () => application.Cdependency__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.gen_step()).result;
				const right = resolve(context)
					.then(context => application__generalize_dependency___on_match_nval).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(() => self)
			.then(context => context?.component_root.input.gen_step())
			.then(context => {
				switch (context?.variant.name) {
					case 'self': {
						return resolve(context)
							.then(() => self)
							.then(context => context?.component_root.input.gen())
							.then(context => context?.properties.gen()).result;
					}
					case 'step': {
						const application__generalize_dependency___on_match___struct___step_nval = context.cast('step');
						return resolve(context)
							.then(context => application__generalize_dependency___on_match_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'self': {
										return resolve(context)
											.then(() => self)
											.then(context => context?.component_root.input.first_matching_step_from_actual())
											.then(context => context?.definitions.dependency).result;
									}
									case 'step': {
										const application__generalize_dependency___on_match___struct___step___step_nval = context.cast('step');
										return resolve(context)
											.then(context => evaluate__generalize_dependency___on_match(detach, 
												resolve(context)
													.then(context => application__generalize_dependency___on_match___struct___step___step_nval)
													.then(context => context?.component_root.output.parent()).result!, {
												first_matching_step_from_actual: () => resolve(self)
													.then(() => self)
													.then(context => context?.component_root.input.first_matching_step_from_actual()).result!
												, 
												gen: () => resolve(self)
													.then(() => self)
													.then(context => context?.component_root.input.gen()).result!
												, 
												gen_step: () => resolve(self)
													.then(context => application__generalize_dependency___on_match___struct___step_nval)
													.then(context => context?.component_root.output.parent()).result!

												})).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result
			} else {
				return resolve(match_context)
				.then(context => evaluate__inline_dependency_step(detach, 
					resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.first_matching_step_from_actual()).result!, {
					parent: () => resolve(self)
						.then(context => evaluate__generalize_dependency___before_match(detach, 
							resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.first_matching_step_from_actual())
								.then(context => context?.component_root.output.parent()).result!, {
							gen: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.gen()).result!

							})).result!

					}))
				.then(context => context?.definitions.dependency).result
			}
		}).result!
}
function evaluate__generalize_dependency___before_match(detach:boolean, application__generalize_dependency___before_match_nval:application.Cdependency__application, input: {
	gen: () => application.Cdependency_gen
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__generalize_dependency___before_match_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application__generalize_dependency___before_match_nval).result;
				}
				case 'step': {
					const application__generalize_dependency___before_match___step_nval = context.cast('step');
					return resolve(context)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(context => {
								const left = resolve(context)
									.then(context => application__generalize_dependency___before_match_nval).result;
								const right = resolve(context)
									.then(() => self)
									.then(context => context?.component_root.input.gen())
									.then(context => context?.properties.dependency()).result;
								return left?.is(right!) ? left : undefined
							}).result;
							if (expression_context !== undefined) {
							return resolve(match_context)
							.then(context => evaluate__generalize_dependency___on_match(detach, 
								resolve(context)
									.then(context => application__generalize_dependency___before_match_nval).result!, {
								first_matching_step_from_actual: () => resolve(self)
									.then(context => application__generalize_dependency___before_match___step_nval).result!
								, 
								gen: () => resolve(self)
									.then(() => self)
									.then(context => context?.component_root.input.gen()).result!
								, 
								gen_step: () => resolve(self)
									.then(() => self)
									.then(context => context?.component_root.input.gen())
									.then(context => context?.properties.dependency()).result!

								})).result
							} else {
								return resolve(match_context)
								.then(context => evaluate__inline_dependency_step(detach, 
									resolve(context)
										.then(context => application__generalize_dependency___before_match___step_nval).result!, {
									parent: () => resolve(self)
										.then(context => evaluate__generalize_dependency___before_match(detach, 
											resolve(context)
												.then(context => application__generalize_dependency___before_match___step_nval)
												.then(context => context?.component_root.output.parent()).result!, {
											gen: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.gen()).result!

											})).result!

									}))
								.then(context => context?.definitions.dependency).result
							}
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tflatten_expression = {
	'head':Tgroup_ancestor_node_path;
	'path':Tplural_descendant_node_path;
};
export class Cflatten_expression extends AlanNode {
	public readonly properties:{
		readonly head:Cgroup_ancestor_node_path,
		readonly path:Cplural_descendant_node_path
	};
	public readonly output:{
		context: () => application.Cnode_navigation_context;
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
		phase: () => application.Ccomputation_phase;
		upper_bound_cardinality: () => application.Cupper_bound_cardinality;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context()).result!),
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.lower_bound_cardinality()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!),
		upper_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.upper_bound_cardinality()).result!)
	};
	constructor(init:Tflatten_expression, public location:AlanNode, public input: {
		this_property: () => application.Cproperty__type__attributes
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			head: new Cflatten_expression.Dhead(init['head'], $this),
			path: new Cflatten_expression.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vbase_value_source = { name: 'internal', definition: (typeof Cbase_value_source.Pinternal)}|{ name: 'external', definition: application.Cexternal_source}
export class Cbase_value_source extends AlanStruct {
	public static Pinternal:Cbase_value_source = new class PrimitiveInstance extends Cbase_value_source {
		constructor () {
			super({name: 'internal', definition: undefined as unknown as Cbase_value_source})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vbase_value_source) { super(); }
	public definitions:{
		value_source: Cvalue_source;
	} = {
		value_source: new Cvalue_source({name:'base', definition: this})
	}
	public cast<K extends Vbase_value_source['name']>(_variant:K):Extract<Vbase_value_source, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vbase_value_source['name']]:(($:Extract<Vbase_value_source, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'base value source'`; }
	public is(other:Cbase_value_source):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cinternal__base_value_source = application.Cbase_value_source;
type Vexternal_source = { name: 'imported interface', definition: application.Cimported_interfaces}|{ name: 'user', definition: (typeof Cexternal_source.Puser)}
export class Cexternal_source extends AlanStruct {
	public static Puser:Cexternal_source = new class PrimitiveInstance extends Cexternal_source {
		constructor () {
			super({name: 'user', definition: undefined as unknown as Cexternal_source})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vexternal_source) { super(); }
	public definitions:{
		base_value_source: Cbase_value_source;
	} = {
		base_value_source: new Cbase_value_source({name:'external', definition: this})
	}
	public cast<K extends Vexternal_source['name']>(_variant:K):Extract<Vexternal_source, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vexternal_source['name']]:(($:Extract<Vexternal_source, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'external source'`; }
	public is(other:Cexternal_source):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cuser__external_source = application.Cexternal_source;
type Vexistence__application = { name: 'optional', definition: (typeof Cexistence__application.Poptional), widening_index: 0}|{ name: 'mandatory', definition: (typeof Cexistence__application.Pmandatory), widening_index: 1}
export class Cexistence__application extends AlanStruct {
	public static Poptional:Cexistence__application = new class PrimitiveInstance extends Cexistence__application {
		constructor () {
			super({name: 'optional', definition: undefined as unknown as Cexistence__application, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pmandatory:Cexistence__application = new class PrimitiveInstance extends Cexistence__application {
		constructor () {
			super({name: 'mandatory', definition: undefined as unknown as Cexistence__application, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vexistence__application) { super(); }
	public readonly output:{
		lower_bound_cardinality: () => application.Clower_bound_cardinality;
	} = {
		lower_bound_cardinality: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'mandatory': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Clower_bound_cardinality.Pone).result;
					}
					case 'optional': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Clower_bound_cardinality.Pzero).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vexistence__application['name']>(_variant:K):Extract<Vexistence__application, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vexistence__application['name']]:(($:Extract<Vexistence__application, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'existence'`; }
	public is(other:Cexistence__application):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cmandatory__existence = application.Cexistence__application;
export import Coptional__existence = application.Cexistence__application;
export type Tentry_reference_selector = {
	'definer':['property', Tproperty__definer]|['rule', Trule];
};
export class Centry_reference_selector extends AlanNode {
	public readonly properties:{
		readonly definer:Centry_reference_selector.Ddefiner
	};
	public readonly output:{
		definition: () => application.Centry_step_definition;
		property: () => application.Cyes__has_reference;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.definer?.state.node.output.definition()).result!),
		property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.definer?.state.node.output.property()).result!)
	};
	constructor(init:Tentry_reference_selector, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			definer: new Centry_reference_selector.Ddefiner(init['definer'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tproperty__definer = {
	'property':string;
};
export class Cproperty__definer extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty__definer.Dproperty
	};
	public readonly output:{
		definition: () => application.Centry_step_definition;
		property: () => application.Cyes__has_reference;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.ref)
			.then(context => context?.properties.expression)
			.then(context => context?.definitions.entry_step_definition).result!),
		property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.ref).result!)
	}
	constructor(init:Tproperty__definer, public parent:Centry_reference_selector) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty__definer.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'definer'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Trule = {
	'property':string;
	'rule':string;
};
export class Crule extends AlanNode {
	public readonly properties:{
		readonly property:Crule.Dproperty,
		readonly rule:Crule.Drule
	};
	public readonly output:{
		definition: () => application.Centry_step_definition;
		property: () => application.Cyes__has_reference;
	} = {
		definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.inferences.entry_step()).result!),
		property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.ref).result!)
	}
	constructor(init:Trule, public parent:Centry_reference_selector) {
		super();
		const $this = this;
		this.properties = {
			property: new Crule.Dproperty(init['property'], $this),
			rule: new Crule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'definer'?'rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_reference_definition = {
	'collection path':Tvariablized_object_path;
	'tail':Tdescendant_object_path;
	'type':['sibling', Tsibling__type__entry_reference_definition]|'simple'|['simple', {}];
};
export class Centry_reference_definition extends AlanNode {
	public definitions:{
		entry_step_definition: Centry_step_definition;
		reference_value: Creference_value;
	} = {
		entry_step_definition: new Centry_step_definition({name:'reference', definition: this}),
		reference_value: new Creference_value({name:'entry reference', definition: this}, {
			definer: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.definer())
				.then(context => context?.component_root.output.reference_definer()).result!)
		})
	}
	public readonly properties:{
		readonly collection_path:Cvariablized_object_path & { readonly inferences: {
			location: () => application.Crelative_object_location;
		} },
		readonly tail:Cdescendant_object_path & { readonly inferences: {
			node: () => application.Cnode__application;
		} },
		readonly type:Centry_reference_definition.Dtype
	};
	public readonly output:{
		collection: () => application.Ccollection__type__property;
		definer: () => application.Centry_reference_definer;
		dependency: () => application.Cdependency__application;
		direction: () => application.Cdirection;
		iubc: () => application.Cupper_bound_cardinality;
		key_value_source: () => application.Ctext__type__property;
		local_iubc: () => application.Cupper_bound_cardinality;
		location: () => application.Crelative_object_location;
		phase: () => application.Ccomputation_phase;
		referenced_node_type: () => application.Cnode__application;
		step_dependency: () => application.Cdependency__application;
		value_source_dependence: () => application.Cvalue_source;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.collection()).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.definer()).result!),
		dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.dependency()).result!),
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.direction()).result!),
		iubc: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.iubc()).result!),
		key_value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.collection())
			.then(context => context?.properties.type?.state.node.output.key_value_source()).result!),
		local_iubc: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.definer())
			.then(context => context?.component_root.output.defining_property())
			.then(context => context?.parent)
			.then(context => context?.properties.type?.state.node.output.attribute_type())
			.then(context => {
				switch (context?.variant.name) {
					case 'key': {
						return resolve(context)
							.then(() => this)
							.then(() => application.Cupper_bound_cardinality.Pone).result;
					}
					case 'simple': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.properties.type?.state.node.output.iubc()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.location()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.phase()).result!),
		referenced_node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail?.inferences.node()).result!),
		step_dependency: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.entry_step_definition).result!;
				return new Cdependency_definition({name: 'entry step', definition: conv_context},{
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.tail)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.dependency())
					.then(context => context?.component_root.output.location()).result!
				, 
				parent: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.dependency()).result!

				});
			})
			.then(context => context?.definitions.dependency).result!),
		value_source_dependence: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.context())
			.then(context => context?.component_root.output.value_source_dependence()).result!)
	};
	constructor(init:Tentry_reference_definition, public location:AlanNode, public input: {
		collection_existence: () => application.Cexistence__application,
		definer: () => application.Centry_reference_definer,
		required_value_source: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			collection_path: new Centry_reference_definition.Dcollection_path(init['collection path'], $this),
			tail: new Centry_reference_definition.Dtail(init['tail'], $this),
			type: new Centry_reference_definition.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tsibling__type__entry_reference_definition = {
	'definition':Tsibling_reference_definition;
};
export class Csibling__type__entry_reference_definition extends AlanNode {
	public readonly properties:{
		readonly definition:Csibling_reference_definition & { readonly inferences: {
			collection_managing_source: () => application.Cvalue_source;
			inversion_upper_bound_cardinality: () => application.Cupper_bound_cardinality;
		} }
	};
	public readonly output:{
		collection: () => application.Ccollection__type__property;
		entry_location: () => application.Crelative_object_location;
		iubc: () => application.Cupper_bound_cardinality;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection()).result!),
		entry_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.definition)
			.then(context => context?.component_root.output.location())
			.then(context => context?.definitions.relative_object_location).result!),
		iubc: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.definition?.inferences.inversion_upper_bound_cardinality()).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property,
		node_type: () => application.Cnode__application
	} = {
		collection: cache((detach:boolean) => {
			const application__entry_reference_definition__type__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		node_type: cache((detach:boolean) => {
			const application__entry_reference_definition__type__sibling_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.collection_path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tsibling__type__entry_reference_definition, public parent:Centry_reference_definition) {
		super();
		const $this = this;
		this.properties = {
			definition: new Csibling__type__entry_reference_definition.Ddefinition(init['definition'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sibling'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsimple__type__entry_reference_definition = {
};
export class Csimple__type__entry_reference_definition extends AlanNode {
	public readonly output:{
		collection: () => application.Ccollection__type__property;
		entry_location: () => application.Crelative_object_location;
		iubc: () => application.Cupper_bound_cardinality;
	} = {
		collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection()).result!),
		entry_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.definitions.entity)
			.then(context => context?.definitions.relative_object_location).result!),
		iubc: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__compute_entry_step_iubc(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.root).result!, {
				collection_path_context: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.context()).result!
				, 
				definition: () => resolve(this)
					.then(() => this)
					.then(context => context?.parent)
					.then(context => context?.definitions.entry_step_definition).result!
				, 
				local_step_iubc: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.attribute_type())
					.then(context => {
						switch (context?.variant.name) {
							case 'key': {
								return resolve(context)
									.then(() => this)
									.then(() => application.Cupper_bound_cardinality.Pone).result;
							}
							case 'simple': {
								return resolve(context)
									.then(() => this)
									.then(() => application.Cupper_bound_cardinality.Pmany).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!

				})).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const application__entry_reference_definition__type__simple_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.collection_path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined)
				.then(context => context?.variant.name === 'collection attribute' ? context.variant.definition as application.Ccollection_attribute : undefined)
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tsimple__type__entry_reference_definition, public parent:Centry_reference_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'simple'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__eliminate_dependency(detach:boolean, application__eliminate_dependency_nval:application.Centity__application, input: {
	dependency: () => application.Cdependency_definition,
	new_root: () => application.Centity__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(context => application__eliminate_dependency_nval).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.new_root()).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(context => evaluate__eliminate_dependencies(detach, 
				resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.new_root()).result!, {
				chain: () => resolve(self)
					.then(() => self)
					.then(context => context?.component_root.input.dependency())
					.then(context => context?.component_root.output.parent()).result!

				})).result
			} else {
				return resolve(match_context)
				.then(context => application__eliminate_dependency_nval)
				.then(context => {
					switch (context?.variant.name) {
						case 'collection': {
							const application__eliminate_dependency___none___collection_nval = context.cast('collection');
							return resolve(context)
								.then(context => evaluate__eliminate_dependency(detach, 
									resolve(context)
										.then(context => application__eliminate_dependency___none___collection_nval)
										.then(context => context?.component_root.output.entity()).result!, {
									dependency: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.dependency()).result!
									, 
									new_root: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.new_root()).result!

									})).result;
						}
						case 'root': {
							return resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.dependency())
								.then(context => context?.definitions.dependency).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result
			}
		}).result!
}
function evaluate__eliminate_dependencies(detach:boolean, application__eliminate_dependencies_nval:application.Centity__application, input: {
	chain: () => application.Cdependency__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.chain())
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application__eliminate_dependencies_nval)
						.then(context => context?.definitions.dependency).result;
				}
				case 'step': {
					const application__eliminate_dependencies___step_nval = context.cast('step');
					return resolve(context)
						.then(context => evaluate__eliminate_dependency(detach, 
							resolve(context)
								.then(context => application__eliminate_dependencies___step_nval)
								.then(context => context?.component_root.output.base_entity()).result!, {
							dependency: () => resolve(self)
								.then(context => application__eliminate_dependencies___step_nval).result!
							, 
							new_root: () => resolve(self)
								.then(context => application__eliminate_dependencies_nval).result!

							})).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tdirection_annotation = {
	'direction':'downstream'|['downstream', {}]|'upstream'|['upstream', {}];
};
export class Cdirection_annotation extends AlanNode {
	public readonly properties:{
		readonly direction:Cdirection_annotation.Ddirection
	};
	public readonly output:{
		direction: () => application.Cdirection;
	} = {
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.direction?.state.node.output.direction()).result!)
	};
	constructor(init:Tdirection_annotation, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			direction: new Cdirection_annotation.Ddirection(init['direction'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdownstream__direction__direction_annotation = {
};
export class Cdownstream__direction__direction_annotation extends AlanNode {
	public readonly output:{
		direction: () => application.Cdirection;
	} = {
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cdirection.Pdownstream).result!)
	}
	constructor(init:Tdownstream__direction__direction_annotation, public parent:Cdirection_annotation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'direction'?'downstream'`; }
	public get entity() { return this.parent.entity; }
}
export type Tupstream__direction__direction_annotation = {
};
export class Cupstream__direction__direction_annotation extends AlanNode {
	public readonly output:{
		direction: () => application.Cdirection;
	} = {
		direction: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cdirection.Pupstream).result!)
	}
	constructor(init:Tupstream__direction__direction_annotation, public parent:Cdirection_annotation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'direction'?'upstream'`; }
	public get entity() { return this.parent.entity; }
}
type Vdirection = { name: 'unbounded', definition: (typeof Cdirection.Punbounded), widening_index: 0}|{ name: 'downstream', definition: (typeof Cdirection.Pdownstream), widening_index: 1}|{ name: 'upstream', definition: (typeof Cdirection.Pupstream), widening_index: 2}
export class Cdirection extends AlanStruct {
	public static Punbounded:Cdirection = new class PrimitiveInstance extends Cdirection {
		constructor () {
			super({name: 'unbounded', definition: undefined as unknown as Cdirection, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pdownstream:Cdirection = new class PrimitiveInstance extends Cdirection {
		constructor () {
			super({name: 'downstream', definition: undefined as unknown as Cdirection, widening_index: 1})
			this.variant.definition = this;
		}
	}
	public static Pupstream:Cdirection = new class PrimitiveInstance extends Cdirection {
		constructor () {
			super({name: 'upstream', definition: undefined as unknown as Cdirection, widening_index: 2})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vdirection) { super(); }
	public cast<K extends Vdirection['name']>(_variant:K):Extract<Vdirection, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vdirection['name']]:(($:Extract<Vdirection, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'direction'`; }
	public is(other:Cdirection):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cdownstream__direction__application = application.Cdirection;
export import Cunbounded__direction = application.Cdirection;
export import Cupstream__direction__application = application.Cdirection;
export type Tdescendant_object_path = {
	'path':Tobject_path_tail;
};
export class Cdescendant_object_path extends AlanNode {
	public readonly properties:{
		readonly path:Cobject_path_tail
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!)
	};
	constructor(init:Tdescendant_object_path, public location:AlanNode, public input: {
		context: () => application.Cnavigation_context,
		dependency_location_support: () => application.Crecursion_dependence,
		existence_support: () => application.Cexistence__application,
		phase: () => application.Ccomputation_phase,
		target_root_location: () => application.Croot_location,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Cdescendant_object_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdescendant_base_property_path = {
	'head':Tobject_path_tail;
	'property':string;
};
export class Cdescendant_base_property_path extends AlanNode {
	public readonly properties:{
		readonly head:Cobject_path_tail & { readonly inferences: {
			node: () => application.Cnode__application;
		} },
		readonly property:Cdescendant_base_property_path.Dproperty
	};
	public readonly output:{
		property: () => application.Cproperty__type__attributes;
	} = {
		property: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.ref).result!)
	};
	constructor(init:Tdescendant_base_property_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application,
		existence_support: () => application.Cexistence__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			head: new Cdescendant_base_property_path.Dhead(init['head'], $this),
			property: new Cdescendant_base_property_path.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Cderived_set extends AlanStruct {
	constructor(
		public readonly properties: {
			lower_bound_cardinality: () => application.Clower_bound_cardinality, 
			upper_bound_cardinality: () => application.Cupper_bound_cardinality
		}, public input: {
			entity: () => application.Centity__application,
			source_collection: () => application.Ccollection_attribute,
			source_entry_existence: () => application.Cexistence__application,
			value_location: () => application.Crelative_object_location,
			value_type: () => application.Cvalue_object
		}
	) { super(); }
	public definitions:{
		collection_object: Ccollection_object;
	} = {
		collection_object: new Ccollection_object({name:'derived set', definition: this}, {
			lower_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.lower_bound_cardinality()).result!),
			source_entry_existence: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.source_entry_existence()).result!),
			upper_bound_cardinality: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.upper_bound_cardinality()).result!),
			value_location: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.value_location()).result!),
			value_type: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.value_type()).result!)
		})
	}
	public readonly output:{
		entity: () => application.Centity__application;
		source_collection: () => application.Ccollection_attribute;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.entity()).result!),
		source_collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.source_collection()).result!)
	};
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'derived set'`; }
	public is(other:Cderived_set):boolean {
		if (!this.properties.lower_bound_cardinality().is(other.properties.lower_bound_cardinality())) return false;
		if (!this.properties.upper_bound_cardinality().is(other.properties.upper_bound_cardinality())) return false;
		return true;
	}
}
export type Tderivation_expression_tail = {
	'type':['dynamic', Tdynamic__type__derivation_expression_tail]|['number', Tnumber__type__derivation_expression_tail]|['reference', Treference__type__derivation_expression_tail]|['state', Tstate__type__derivation_expression_tail]|['text', Ttext__type__derivation_expression_tail];
};
export class Cderivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly type:Cderivation_expression_tail.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	};
	constructor(init:Tderivation_expression_tail, public location:AlanNode, public input: {
		support: () => application.Cderivation_expression_support,
		target: () => application.Cvalue_object,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cderivation_expression_tail.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic__type__derivation_expression_tail = {
	'path':Tsingular_variablized_object_path;
};
export class Cdynamic__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly path:Csingular_variablized_object_path & { readonly inferences: {
			dependency: () => application.Cdependency__application;
			value: () => application.Cvalue_object;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	public readonly inferences:{
		leaf: () => application.Cleaf_value
	} = {
		leaf: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__dynamic_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined).result!;
		})

	}
	constructor(init:Tdynamic__type__derivation_expression_tail, public parent:Cderivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			path: new Cdynamic__type__derivation_expression_tail.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__derivation_expression_tail = {
	'expression':Tnumber_expression;
};
export class Cnumber__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly expression:Cnumber_expression
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	public readonly inferences:{
		number: () => application.Cnumber_value
	} = {
		number: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
				.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Cnumber_value : undefined).result!;
		})

	}
	constructor(init:Tnumber__type__derivation_expression_tail, public parent:Cderivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber__type__derivation_expression_tail.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference__type__derivation_expression_tail = {
	'type':['branch', Tbranch]|['ordered graph node', Tordered_graph_node];
};
export class Creference__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly type:Creference__type__derivation_expression_tail.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	}
	public readonly inferences:{
		reference: () => application.Centry_reference_definition
	} = {
		reference: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
				.then(context => context?.variant.name === 'reference' ? context.variant.definition as application.Creference_value : undefined)
				.then(context => context?.variant.name === 'entry reference' ? context.variant.definition as application.Centry_reference_definition : undefined).result!;
		})

	}
	constructor(init:Treference__type__derivation_expression_tail, public parent:Cderivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			type: new Creference__type__derivation_expression_tail.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbranch = {
	'branch':string;
	'expression':Tsingular_variablized_object_path;
};
export class Cbranch extends AlanNode {
	public readonly properties:{
		readonly branch:Cbranch.Dbranch,
		readonly expression:Csingular_variablized_object_path & { readonly inferences: {
			dependency: () => application.Cdependency__application;
			node: () => application.Cnode__application;
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.inferences.reference())
			.then(context => context?.properties.type)
			.then(context => {
				const state_group = context;
				switch (state_group?.state.name) {
					case 'sibling': {
						return resolve(context)
							.then(() => this)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.phase()).result;
					}
					case 'simple': {
						return resolve(context)
							.then(context => evaluate__computation_phase_merge(detach, 
								resolve(context)
									.then(() => this)
									.then(context => context?.properties.expression)
									.then(context => context?.component_root.output.phase()).result!, {
								other_computation_phase: () => resolve(this)
									.then(() => this)
									.then(context => context?.inferences.derived_collection_without_key_constraint())
									.then(context => context?.properties.branches?.inferences.computation_phase()).result!

								})).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
				};}).result!)
	}
	public readonly inferences:{
		derived_collection_without_key_constraint: () => application.Cno__key_constraint
	} = {
		derived_collection_without_key_constraint: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__reference__type__branch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.reference())
				.then(context => context?.component_root.output.collection())
				.then(context => {
					if (context?.properties.type.state.name === 'derived') {
						return resolve(context.properties.type.state.node as application.Cderived__type__collection)
						.then(context => {
							if (context?.properties.key_constraint.state.name === 'no') {
								return context.properties.key_constraint.state.node as application.Cno__key_constraint;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tbranch, public parent:Creference__type__derivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			branch: new Cbranch.Dbranch(init['branch'], $this),
			expression: new Cbranch.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'branch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordered_graph_node = {
	'collection path':Tsingular_variablized_object_path;
	'ordered graph':string;
	'type':'sink'|['sink', {}]|'source'|['source', {}];
};
export class Cordered_graph_node extends AlanNode {
	public readonly properties:{
		readonly collection_path:Csingular_variablized_object_path & { readonly inferences: {
			cardinality: () => application.Clower_bound_cardinality;
			collection: () => application.Ccollection_object;
			dependency: () => application.Cdependency__application;
			node_type: () => application.Cnode__application;
			source_collection: () => application.Ccollection__type__property;
			unfiltered_collection: () => application.Cmandatory__existence;
		} },
		readonly ordered_graph:Cordered_graph_node.Dordered_graph,
		readonly type:Cordered_graph_node.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tordered_graph_node, public parent:Creference__type__derivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			collection_path: new Cordered_graph_node.Dcollection_path(init['collection path'], $this),
			ordered_graph: new Cordered_graph_node.Dordered_graph(init['ordered graph'], $this),
			type: new Cordered_graph_node.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'ordered graph node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsink = {
};
export class Csink extends AlanNode {
	constructor(init:Tsink, public parent:Cordered_graph_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sink'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsource = {
};
export class Csource extends AlanNode {
	constructor(init:Tsource, public parent:Cordered_graph_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'source'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__derivation_expression_tail = {
	'initializer':Tstate_initializer;
};
export class Cstate__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly initializer:Cstate_initializer
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.initializer)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'choice' ? context.variant.definition as application.Cstate_group : undefined).result!;
		})

	}
	constructor(init:Tstate__type__derivation_expression_tail, public parent:Cderivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			initializer: new Cstate__type__derivation_expression_tail.Dinitializer(init['initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__derivation_expression_tail = {
	'type':['concatenation', Tconcatenation__type__text__type__derivation_expression_tail]|['static', Tstatic__type__text__type__derivation_expression_tail];
};
export class Ctext__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly type:Ctext__type__derivation_expression_tail.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	}
	public readonly inferences:{
		text: () => application.Ctext__type__property
	} = {
		text: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
				.then(context => context?.variant.name === 'text' ? context.variant.definition as application.Ctext__type__property : undefined).result!;
		})

	}
	constructor(init:Ttext__type__derivation_expression_tail, public parent:Cderivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext__type__derivation_expression_tail.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconcatenation__type__text__type__derivation_expression_tail = {
	'expression':Tderivation_expression_list;
};
export class Cconcatenation__type__text__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression_list
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	public readonly inferences:{
		no_nested_concatenation: () => application.Cunrestricted
	} = {
		no_nested_concatenation: cache((detach:boolean) => {
			const application__derivation_expression_tail__type__text__type__concatenation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.support())
				.then(context => context?.variant.name === 'unrestricted' ? context.variant.definition as application.Cunrestricted : undefined).result!;
		})

	}
	constructor(init:Tconcatenation__type__text__type__derivation_expression_tail, public parent:Ctext__type__derivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cconcatenation__type__text__type__derivation_expression_tail.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'concatenation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__text__type__derivation_expression_tail = {
	'value':string;
};
export class Cstatic__type__text__type__derivation_expression_tail extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	constructor(init:Tstatic__type__text__type__derivation_expression_tail, public parent:Ctext__type__derivation_expression_tail) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
type Vderivation_expression_support = { name: 'unrestricted', definition: (typeof Cderivation_expression_support.Punrestricted), widening_index: 0}|{ name: 'unary', definition: (typeof Cderivation_expression_support.Punary), widening_index: 1}
export class Cderivation_expression_support extends AlanStruct {
	public static Punrestricted:Cderivation_expression_support = new class PrimitiveInstance extends Cderivation_expression_support {
		constructor () {
			super({name: 'unrestricted', definition: undefined as unknown as Cderivation_expression_support, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Punary:Cderivation_expression_support = new class PrimitiveInstance extends Cderivation_expression_support {
		constructor () {
			super({name: 'unary', definition: undefined as unknown as Cderivation_expression_support, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vderivation_expression_support) { super(); }
	public cast<K extends Vderivation_expression_support['name']>(_variant:K):Extract<Vderivation_expression_support, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vderivation_expression_support['name']]:(($:Extract<Vderivation_expression_support, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'derivation expression support'`; }
	public is(other:Cderivation_expression_support):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cunary__derivation_expression_support = application.Cderivation_expression_support;
export import Cunrestricted = application.Cderivation_expression_support;
export type Tderivation_expression_list = {
	'has tail':'no'|['no', {}]|['yes', Tyes__has_tail__derivation_expression_list];
	'head':Tderivation_expression_tail;
};
export class Cderivation_expression_list extends AlanNode {
	public readonly properties:{
		readonly has_tail:Cderivation_expression_list.Dhas_tail,
		readonly head:Cderivation_expression_tail
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.computation_phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.has_tail?.state.node.output.computation_phase()).result!

				})).result!)
	};
	constructor(init:Tderivation_expression_list, public location:AlanNode, public input: {
		target: () => application.Cvalue_object,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_tail: new Cderivation_expression_list.Dhas_tail(init['has tail'], $this),
			head: new Cderivation_expression_list.Dhead(init['head'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_tail__derivation_expression_list = {
};
export class Cno__has_tail__derivation_expression_list extends AlanNode {
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	constructor(init:Tno__has_tail__derivation_expression_list, public parent:Cderivation_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_tail__derivation_expression_list = {
	'tail':Tderivation_expression_list;
};
export class Cyes__has_tail__derivation_expression_list extends AlanNode {
	public readonly properties:{
		readonly tail:Cderivation_expression_list
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	constructor(init:Tyes__has_tail__derivation_expression_list, public parent:Cderivation_expression_list) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_tail__derivation_expression_list.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has tail'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tderivation_expression = {
	'type':['node switch', Tnode_switch__type]|['number switch', Tnumber_switch]|['produce value', Tproduce_value__type]|['recurse', Trecurse]|['set switch', Tset_switch]|['state switch', Tstate_switch__type];
};
export class Cderivation_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cderivation_expression.Dtype
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.computation_phase()).result!)
	};
	constructor(init:Tderivation_expression, public location:AlanNode, public input: {
		target: () => application.Cvalue_object,
		this_property: () => application.Cproperty__type__attributes,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cderivation_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tnode_switch__type = {
	'cases':Tcases__node_switch__type;
	'node path':Tvariablized_object_path;
	'operation':['equality', Tequality__operation]|'existence'|['existence', {}];
};
export class Cnode_switch__type extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases__node_switch__type,
		readonly node_path:Cvariablized_object_path & { readonly inferences: {
			node: () => application.Cnode__application;
		} },
		readonly operation:Cnode_switch__type.Doperation
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.node_path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(context => evaluate__computation_phase_merge(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.properties.operation?.state.node.output.computation_phase()).result!, {
						other_computation_phase: () => resolve(this)
							.then(context => evaluate__computation_phase_merge(detach, 
								resolve(context)
									.then(() => this)
									.then(context => context?.properties.cases)
									.then(context => context?.properties.node)
									.then(context => context?.properties.expression)
									.then(context => context?.component_root.output.computation_phase()).result!, {
								other_computation_phase: () => resolve(this)
									.then(() => this)
									.then(context => context?.properties.cases)
									.then(context => context?.properties.none)
									.then(context => context?.component_root.output.computation_phase()).result!

								})).result!

						})).result!

				})).result!)
	}
	constructor(init:Tnode_switch__type, public parent:Cderivation_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases__node_switch__type(init['cases'], $this),
			node_path: new Cnode_switch__type.Dnode_path(init['node path'], $this),
			operation: new Cnode_switch__type.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases__node_switch__type = {
	'node':Tnode__cases;
	'none':Tderivation_expression;
};
export class Ccases__node_switch__type extends AlanNode {
	public readonly properties:{
		readonly node:Cnode__cases,
		readonly none:Cderivation_expression
	};
	constructor(init:Tcases__node_switch__type, public parent:Cnode_switch__type) {
		super();
		const $this = this;
		this.properties = {
			node: new Cnode__cases(init['node'], $this),
			none: new Ccases__node_switch__type.Dnone(init['none'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__cases = {
	'expression':Tderivation_expression;
	'variable assignment':Toptional_named_object_assignment;
};
export class Cnode__cases extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly variable_assignment:Coptional_named_object_assignment
	};
	constructor(init:Tnode__cases, public parent:Ccases__node_switch__type) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnode__cases.Dexpression(init['expression'], $this),
			variable_assignment: new Cnode__cases.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequality__operation = {
	'other node path':Tsingular_variablized_object_path;
};
export class Cequality__operation extends AlanNode {
	public readonly properties:{
		readonly other_node_path:Csingular_variablized_object_path & { readonly inferences: {
			node: () => application.Cnode__application;
			node_type: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.other_node_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tequality__operation, public parent:Cnode_switch__type) {
		super();
		const $this = this;
		this.properties = {
			other_node_path: new Cequality__operation.Dother_node_path(init['other node path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'equality'`; }
	public get entity() { return this.parent.entity; }
}
export type Texistence__operation = {
};
export class Cexistence__operation extends AlanNode {
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	constructor(init:Texistence__operation, public parent:Cnode_switch__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'existence'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_switch = {
	'cases':Record<string, Tcases__number_switch>;
	'compare to':['constant', Tconstant__compare_to]|['path', Tpath__compare_to];
	'last case':string;
	'number path':Tsingular_variablized_object_path;
};
export class Cnumber_switch extends AlanNode {
	public readonly properties:{
		readonly cases:Cnumber_switch.Dcases,
		readonly compare_to:Cnumber_switch.Dcompare_to,
		readonly last_case:Cnumber_switch.Dlast_case,
		readonly number_path:Csingular_variablized_object_path & { readonly inferences: {
			number: () => application.Cnumber_value;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.number_path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(context => evaluate__computation_phase_merge(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.properties.compare_to?.state.node.output.computation_phase()).result!, {
						other_computation_phase: () => resolve(this)
							.then(reduce_context => 
								resolve(reduce_context)
								.then(() => this)
								.then(context => context?.properties.cases)
								.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
								.then(set => set.map(entry => resolve(entry)
									.then(context => context?.properties.expression)
									.then(context => context?.component_root.output.computation_phase()).result))
									.then(set => set.reduce((prev:application.Ccomputation_phase, curr) => curr.variant.widening_index < prev!.variant.widening_index ? curr : prev, application.Ccomputation_phase.Pzero)).result).result!

						})).result!

				})).result!)
	}
	constructor(init:Tnumber_switch, public parent:Cderivation_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Cnumber_switch.Dcases(init['cases'], $this),
			compare_to: new Cnumber_switch.Dcompare_to(init['compare to'], $this),
			last_case: new Cnumber_switch.Dlast_case(init['last case'], $this),
			number_path: new Cnumber_switch.Dnumber_path(init['number path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number switch'`; }
	public get entity() { return this.parent.entity; }
}
export class Kkeyset___keycases__number_switch extends AlanObject {
	public is(other:Kkeyset___keycases__number_switch):boolean {
		return this === other;
	}
	public get path() { return `static key`; }
};
const keyset___keycases__number_switch: { [key: string]: Kkeyset___keycases__number_switch } = {
	'equals': new Kkeyset___keycases__number_switch(),
	'greater than': new Kkeyset___keycases__number_switch(),
	'greater than or equals': new Kkeyset___keycases__number_switch(),
	'less than': new Kkeyset___keycases__number_switch(),
	'less than or equals': new Kkeyset___keycases__number_switch(),
	'less than or greater than': new Kkeyset___keycases__number_switch()
};
export class Kcases__number_switch extends Reference<application.Kkeyset___keycases__number_switch, string> {
	constructor(key:string, $this:Ccases__number_switch) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => keyset___keycases__number_switch[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'::key`; }
}
export type Tcases__number_switch = {
	'expression':Tderivation_expression;
	'has predecessor':'no'|['no', {}]|['yes', Tyes__has_predecessor__cases];
	'variable assignment':Toptional_named_object_assignment;
};
export class Ccases__number_switch extends AlanDictionaryEntry {
	public key:Kcases__number_switch;
	public get key_value() { return this.key.entry; }
	public definitions:{
		number_comparison_case: Cnumber_comparison_case;
	} = {
		number_comparison_case: new Cnumber_comparison_case({name:'defined', definition: this})
	}
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly has_predecessor:Ccases__number_switch.Dhas_predecessor,
		readonly variable_assignment:Coptional_named_object_assignment
	};
	constructor(key:string, init:Tcases__number_switch, public parent:Cnumber_switch) {
		super();
		const $this = this;
		this.key = new Kcases__number_switch(key, $this);
		this.properties = {
			expression: new Ccases__number_switch.Dexpression(init['expression'], $this),
			has_predecessor: new Ccases__number_switch.Dhas_predecessor(init['has predecessor'], $this),
			variable_assignment: new Ccases__number_switch.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__has_predecessor__cases = {
};
export class Cno__has_predecessor__cases extends AlanNode {
	public readonly output:{
		equals_case: () => application.Cnumber_comparison_case;
		greater_than_case: () => application.Cnumber_comparison_case;
		less_than_case: () => application.Cnumber_comparison_case;
	} = {
		equals_case: cache((detach:boolean) => resolve(this)
			.then(switch_context => {
				return resolve(switch_context)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.key?.ref).then(key_value => {
					if (key_value === keyset___keycases__number_switch[`equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else {
					throw new Error(`Unexpected key in dictionary with static keys.`);
					}
				}).result
			}).result!),
		greater_than_case: cache((detach:boolean) => resolve(this)
			.then(switch_context => {
				return resolve(switch_context)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.key?.ref).then(key_value => {
					if (key_value === keyset___keycases__number_switch[`equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else {
					throw new Error(`Unexpected key in dictionary with static keys.`);
					}
				}).result
			}).result!),
		less_than_case: cache((detach:boolean) => resolve(this)
			.then(switch_context => {
				return resolve(switch_context)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.key?.ref).then(key_value => {
					if (key_value === keyset___keycases__number_switch[`equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(() => application.Cnumber_comparison_case.Pnone).result
					} else if (key_value === keyset___keycases__number_switch[`less than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else {
					throw new Error(`Unexpected key in dictionary with static keys.`);
					}
				}).result
			}).result!)
	}
	constructor(init:Tno__has_predecessor__cases, public parent:Ccases__number_switch) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_predecessor__cases = {
	'case':string;
};
export class Cyes__has_predecessor__cases extends AlanNode {
	public readonly properties:{
		readonly case_:Cyes__has_predecessor__cases.Dcase_
	};
	public readonly output:{
		equals_case: () => application.Cnumber_comparison_case;
		greater_than_case: () => application.Cnumber_comparison_case;
		less_than_case: () => application.Cnumber_comparison_case;
	} = {
		equals_case: cache((detach:boolean) => resolve(this)
			.then(switch_context => {
				return resolve(switch_context)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.key?.ref).then(key_value => {
					if (key_value === keyset___keycases__number_switch[`equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.equals_case()).result
					} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.equals_case()).result
					} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.equals_case()).result
					} else {
					throw new Error(`Unexpected key in dictionary with static keys.`);
					}
				}).result
			}).result!),
		greater_than_case: cache((detach:boolean) => resolve(this)
			.then(switch_context => {
				return resolve(switch_context)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.key?.ref).then(key_value => {
					if (key_value === keyset___keycases__number_switch[`equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.greater_than_case()).result
					} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.greater_than_case()).result
					} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.greater_than_case()).result
					} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else {
					throw new Error(`Unexpected key in dictionary with static keys.`);
					}
				}).result
			}).result!),
		less_than_case: cache((detach:boolean) => resolve(this)
			.then(switch_context => {
				return resolve(switch_context)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.key?.ref).then(key_value => {
					if (key_value === keyset___keycases__number_switch[`equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.less_than_case()).result
					} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.less_than_case()).result
					} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.properties.case_?.inferences.less_than_case()).result
					} else if (key_value === keyset___keycases__number_switch[`less than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
						return resolve(switch_context)
						.then(() => this)
						.then(context => context?.parent)
						.then(context => context?.definitions.number_comparison_case).result
					} else {
					throw new Error(`Unexpected key in dictionary with static keys.`);
					}
				}).result
			}).result!)
	}
	constructor(init:Tyes__has_predecessor__cases, public parent:Ccases__number_switch) {
		super();
		const $this = this;
		this.properties = {
			case_: new Cyes__has_predecessor__cases.Dcase_(init['case'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has predecessor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconstant__compare_to = {
	'value':Tconstant_number_value;
};
export class Cconstant__compare_to extends AlanNode {
	public readonly properties:{
		readonly value:Cconstant_number_value
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
		greater_than_set_type: () => application.Cnumber_set_type;
		set_type: () => application.Cnumber_set_type;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!),
		greater_than_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnumber_set_type.Ppositive).result!),
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value)
			.then(context => context?.component_root.output.set_type()).result!)
	}
	constructor(init:Tconstant__compare_to, public parent:Cnumber_switch) {
		super();
		const $this = this;
		this.properties = {
			value: new Cconstant__compare_to.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'compare to'?'constant'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpath__compare_to = {
	'right number path':Tsingular_variablized_object_path;
};
export class Cpath__compare_to extends AlanNode {
	public readonly properties:{
		readonly right_number_path:Csingular_variablized_object_path & { readonly inferences: {
			number: () => application.Cnumber_value;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
		greater_than_set_type: () => application.Cnumber_set_type;
		set_type: () => application.Cnumber_set_type;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.right_number_path)
			.then(context => context?.component_root.output.phase()).result!),
		greater_than_set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.right_number_path?.inferences.number())
			.then(context => context?.component_root.output.set_type()).result!),
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.right_number_path?.inferences.number())
			.then(context => context?.component_root.output.set_type()).result!)
	}
	constructor(init:Tpath__compare_to, public parent:Cnumber_switch) {
		super();
		const $this = this;
		this.properties = {
			right_number_path: new Cpath__compare_to.Dright_number_path(init['right number path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'compare to'?'path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduce_value__type = {
	'expression':Tderivation_expression_tail;
};
export class Cproduce_value__type extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression_tail
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	constructor(init:Tproduce_value__type, public parent:Cderivation_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cproduce_value__type.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'produce value'`; }
	public get entity() { return this.parent.entity; }
}
export type Trecurse = {
	'step':['repeat', Trepeat]|['start', Tstart];
};
export class Crecurse extends AlanNode {
	public readonly properties:{
		readonly step:Crecurse.Dstep
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.step?.state.node.output.phase()).result!)
	}
	constructor(init:Trecurse, public parent:Cderivation_expression) {
		super();
		const $this = this;
		this.properties = {
			step: new Crecurse.Dstep(init['step'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'recurse'`; }
	public get entity() { return this.parent.entity; }
}
export type Trepeat = {
	'recursion start':Tnamed_object_step;
	'sibling node path':Tsingular_variablized_object_path;
};
export class Crepeat extends AlanNode {
	public readonly properties:{
		readonly recursion_start:Cnamed_object_step & { readonly inferences: {
			closure: () => application.Cstart;
		} },
		readonly sibling_node_path:Csingular_variablized_object_path & { readonly inferences: {
			binding_node_type: () => application.Cnode__application;
			graph_traversing_step: () => application.Centry_step_definition;
			graph_traversing_steps: () => application.Cdependency__application;
			node: () => application.Cnode__application;
			sibling_of_recursion_start_object: () => application.Cdependency__application;
		} }
	};
	public readonly output:{
		phase: () => application.Ccomputation_phase;
	} = {
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.sibling_node_path)
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Trepeat, public parent:Crecurse) {
		super();
		const $this = this;
		this.properties = {
			recursion_start: new Crepeat.Drecursion_start(init['recursion start'], $this),
			sibling_node_path: new Crepeat.Dsibling_node_path(init['sibling node path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'step'?'repeat'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstart = {
	'binding node path':Tsingular_variablized_object_path;
	'expression':Tderivation_expression;
	'graph traversal':Tgraph_traversal_definition;
	'recursion start assignment':Tnamed_object_assignment;
};
export class Cstart extends AlanNode {
	public definitions:{
		named_object_definer: Cnamed_object_definer;
	} = {
		named_object_definer: new Cnamed_object_definer({name:'recursion start', definition: this})
	}
	public readonly properties:{
		readonly binding_node_path:Csingular_variablized_object_path & { readonly inferences: {
			collection: () => application.Ccollection__type__property;
			node: () => application.Cnode__application;
		} },
		readonly expression:Cderivation_expression,
		readonly graph_traversal:Cgraph_traversal_definition,
		readonly recursion_start_assignment:Cnamed_object_assignment
	};
	public readonly output:{
		phase: () => application.Ccomputation_phase;
	} = {
		phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.binding_node_path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.computation_phase()).result!

				})).result!)
	}
	constructor(init:Tstart, public parent:Crecurse) {
		super();
		const $this = this;
		this.properties = {
			binding_node_path: new Cstart.Dbinding_node_path(init['binding node path'], $this),
			expression: new Cstart.Dexpression(init['expression'], $this),
			graph_traversal: new Cstart.Dgraph_traversal(init['graph traversal'], $this),
			recursion_start_assignment: new Cstart.Drecursion_start_assignment(init['recursion start assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'step'?'start'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_switch = {
	'cases':Tcases__set_switch;
	'nodes path':Tobject_set_path;
};
export class Cset_switch extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases__set_switch,
		readonly nodes_path:Cobject_set_path & { readonly inferences: {
			node: () => application.Cnode__application;
		} }
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.nodes_path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(context => evaluate__computation_phase_merge(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.properties.cases)
							.then(context => context?.properties.match_none?.state.node.output.computation_phase()).result!, {
						other_computation_phase: () => resolve(this)
							.then(context => evaluate__computation_phase_merge(detach, 
								resolve(context)
									.then(() => this)
									.then(context => context?.properties.cases)
									.then(context => context?.properties.match_node?.state.node.output.computation_phase()).result!, {
								other_computation_phase: () => resolve(this)
									.then(() => this)
									.then(context => context?.properties.cases)
									.then(context => context?.properties.match_nodes?.state.node.output.computation_phase()).result!

								})).result!

						})).result!

				})).result!)
	}
	constructor(init:Tset_switch, public parent:Cderivation_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases__set_switch(init['cases'], $this),
			nodes_path: new Cset_switch.Dnodes_path(init['nodes path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'set switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases__set_switch = {
	'match node':'no'|['no', {}]|['yes', Tyes__match_node];
	'match nodes':'no'|['no', {}]|['yes', Tyes__match_nodes];
	'match none':'no'|['no', {}]|['yes', Tyes__match_none];
};
export class Ccases__set_switch extends AlanNode {
	public readonly properties:{
		readonly match_node:Ccases__set_switch.Dmatch_node,
		readonly match_nodes:Ccases__set_switch.Dmatch_nodes,
		readonly match_none:Ccases__set_switch.Dmatch_none
	};
	constructor(init:Tcases__set_switch, public parent:Cset_switch) {
		super();
		const $this = this;
		this.properties = {
			match_node: new Ccases__set_switch.Dmatch_node(init['match node'], $this),
			match_nodes: new Ccases__set_switch.Dmatch_nodes(init['match nodes'], $this),
			match_none: new Ccases__set_switch.Dmatch_none(init['match none'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__match_node = {
};
export class Cno__match_node extends AlanNode {
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	constructor(init:Tno__match_node, public parent:Ccases__set_switch) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'match node'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__match_node = {
	'expression':Tderivation_expression;
	'variable assignment':Toptional_named_object_assignment;
};
export class Cyes__match_node extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly variable_assignment:Coptional_named_object_assignment
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	constructor(init:Tyes__match_node, public parent:Ccases__set_switch) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cyes__match_node.Dexpression(init['expression'], $this),
			variable_assignment: new Cyes__match_node.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'match node'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__match_nodes = {
};
export class Cno__match_nodes extends AlanNode {
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	public readonly inferences:{
		upper_bound_cardinality_for_set: () => application.Cone__upper_bound_cardinality
	} = {
		upper_bound_cardinality_for_set: cache((detach:boolean) => {
			const application__derivation_expression__type__set_switch__cases__match_nodes__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.nodes_path)
				.then(context => context?.component_root.output.upper_bound_cardinality())
				.then(context => context?.variant.name === 'one' ? context.variant.definition as application.Cone__upper_bound_cardinality : undefined).result!;
		})

	}
	constructor(init:Tno__match_nodes, public parent:Ccases__set_switch) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'match nodes'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__match_nodes = {
	'expression':Tderivation_expression;
	'variable assignment':Toptional_named_object_assignment;
};
export class Cyes__match_nodes extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly variable_assignment:Coptional_named_object_assignment
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	public readonly inferences:{
		upper_bound_cardinality_for_set: () => application.Cmany__upper_bound_cardinality
	} = {
		upper_bound_cardinality_for_set: cache((detach:boolean) => {
			const application__derivation_expression__type__set_switch__cases__match_nodes__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.nodes_path)
				.then(context => context?.component_root.output.upper_bound_cardinality())
				.then(context => context?.variant.name === 'many' ? context.variant.definition as application.Cmany__upper_bound_cardinality : undefined).result!;
		})

	}
	constructor(init:Tyes__match_nodes, public parent:Ccases__set_switch) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cyes__match_nodes.Dexpression(init['expression'], $this),
			variable_assignment: new Cyes__match_nodes.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'match nodes'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__match_none = {
};
export class Cno__match_none extends AlanNode {
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	public readonly inferences:{
		lower_bound_cardinality_for_set: () => application.Cone__lower_bound_cardinality
	} = {
		lower_bound_cardinality_for_set: cache((detach:boolean) => {
			const application__derivation_expression__type__set_switch__cases__match_none__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.nodes_path)
				.then(context => context?.component_root.output.lower_bound_cardinality())
				.then(context => context?.variant.name === 'one' ? context.variant.definition as application.Cone__lower_bound_cardinality : undefined).result!;
		})

	}
	constructor(init:Tno__match_none, public parent:Ccases__set_switch) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'match none'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__match_none = {
	'expression':Tderivation_expression;
};
export class Cyes__match_none extends AlanNode {
	public readonly properties:{
		readonly expression:Cderivation_expression
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.computation_phase()).result!)
	}
	public readonly inferences:{
		lower_bound_cardinality_for_set: () => application.Czero__lower_bound_cardinality
	} = {
		lower_bound_cardinality_for_set: cache((detach:boolean) => {
			const application__derivation_expression__type__set_switch__cases__match_none__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.nodes_path)
				.then(context => context?.component_root.output.lower_bound_cardinality())
				.then(context => context?.variant.name === 'zero' ? context.variant.definition as application.Czero__lower_bound_cardinality : undefined).result!;
		})

	}
	constructor(init:Tyes__match_none, public parent:Ccases__set_switch) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cyes__match_none.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'match none'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_switch__type = {
	'state group path':Tsingular_variablized_object_path;
	'states':Record<string, Tstates__state_switch__type>;
};
export class Cstate_switch__type extends AlanNode {
	public readonly properties:{
		readonly state_group_path:Csingular_variablized_object_path & { readonly inferences: {
			state_group: () => application.Cstate_group;
		} },
		readonly states:Cstate_switch__type.Dstates
	};
	public readonly output:{
		computation_phase: () => application.Ccomputation_phase;
	} = {
		computation_phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.state_group_path)
					.then(context => context?.component_root.output.phase()).result!, {
				other_computation_phase: () => resolve(this)
					.then(reduce_context => 
						resolve(reduce_context)
						.then(() => this)
						.then(context => context?.properties.states)
						.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
						.then(set => set.map(entry => resolve(entry)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.computation_phase()).result))
							.then(set => set.reduce((prev:application.Ccomputation_phase, curr) => curr.variant.widening_index < prev!.variant.widening_index ? curr : prev, application.Ccomputation_phase.Pzero)).result).result!

				})).result!)
	}
	constructor(init:Tstate_switch__type, public parent:Cderivation_expression) {
		super();
		const $this = this;
		this.properties = {
			state_group_path: new Cstate_switch__type.Dstate_group_path(init['state group path'], $this),
			states: new Cstate_switch__type.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state switch'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_switch__type extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_switch__type) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.state_group_path?.inferences.state_group())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_switch__type = {
	'expression':Tderivation_expression;
	'variable assignment':Toptional_named_object_assignment;
};
export class Cstates__state_switch__type extends AlanDictionaryEntry {
	public key:Kstates__state_switch__type;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly expression:Cderivation_expression,
		readonly variable_assignment:Coptional_named_object_assignment
	};
	constructor(key:string, init:Tstates__state_switch__type, public parent:Cstate_switch__type) {
		super();
		const $this = this;
		this.key = new Kstates__state_switch__type(key, $this);
		this.properties = {
			expression: new Cstates__state_switch__type.Dexpression(init['expression'], $this),
			variable_assignment: new Cstates__state_switch__type.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
function evaluate__dependency_to_ubc(detach:boolean, application__dependency_to_ubc_nval:application.Cdependency__application, ):application.Cupper_bound_cardinality {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => application__dependency_to_ubc_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(() => self)
						.then(() => application.Cupper_bound_cardinality.Pone).result;
				}
				case 'step': {
					const application__dependency_to_ubc___step_nval = context.cast('step');
					return resolve(context)
						.then(context => application__dependency_to_ubc___step_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'collection step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case 'entry step': {
									return resolve(context)
										.then(context => evaluate__dependency_to_ubc(detach, 
											resolve(context)
												.then(context => application__dependency_to_ubc___step_nval)
												.then(context => context?.component_root.output.parent()).result!, )).result;
								}
								case 'inverse reference step': {
									const application__dependency_to_ubc___step___inverse_reference_step_nval = context.cast('inverse reference step');
									return resolve(context)
										.then(context => application__dependency_to_ubc___step___inverse_reference_step_nval)
										.then(context => context?.properties.upper_bound_cardinality())
										.then(context => {
											switch (context?.variant.name) {
												case 'many': {
													return resolve(context)
														.then(context => application__dependency_to_ubc___step___inverse_reference_step_nval)
														.then(context => context?.properties.upper_bound_cardinality()).result;
												}
												case 'one': {
													return resolve(context)
														.then(context => evaluate__dependency_to_ubc(detach, 
															resolve(context)
																.then(context => application__dependency_to_ubc___step_nval)
																.then(context => context?.component_root.output.parent()).result!, )).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'recursion start step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__dependency_to_iubc(detach:boolean, application__dependency_to_iubc_nval:application.Cdependency__application, ):application.Cupper_bound_cardinality {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => application__dependency_to_iubc_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(() => self)
						.then(() => application.Cupper_bound_cardinality.Pone).result;
				}
				case 'step': {
					const application__dependency_to_iubc___step_nval = context.cast('step');
					return resolve(context)
						.then(context => application__dependency_to_iubc___step_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'collection step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case 'entry step': {
									const application__dependency_to_iubc___step___entry_step_nval = context.cast('entry step');
									return resolve(context)
										.then(context => application__dependency_to_iubc___step___entry_step_nval)
										.then(context => context?.component_root.output.iubc())
										.then(context => {
											switch (context?.variant.name) {
												case 'many': {
													return resolve(context)
														.then(() => self)
														.then(() => application.Cupper_bound_cardinality.Pmany).result;
												}
												case 'one': {
													return resolve(context)
														.then(context => evaluate__dependency_to_iubc(detach, 
															resolve(context)
																.then(context => application__dependency_to_iubc___step_nval)
																.then(context => context?.component_root.output.parent()).result!, )).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'inverse reference step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case 'recursion start step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tdependency_step = {
	'type':['reference', Treference__type__dependency_step]|['reference rule', Treference_rule]|['state context rule', Tstate_context_rule__type__dependency_step];
};
export class Cdependency_step extends AlanNode {
	public readonly properties:{
		readonly type:Cdependency_step.Dtype
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		definer: () => application.Creference_definer;
		phase: () => application.Ccomputation_phase;
		step_type: () => application.Cstep_type;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.context()).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.definer()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.phase()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.step_type()).result!)
	};
	constructor(init:Tdependency_step, public location:AlanNode, public input: {
		context: () => application.Cnode_navigation_context,
		dependency_location_support: () => application.Crecursion_dependence,
		existence_support: () => application.Cexistence__application,
		phase: () => application.Ccomputation_phase,
		referenced_location_support: () => application.Crecursion_dependence,
		target_root_location: () => application.Croot_location,
		value_source_support: () => application.Cvalue_source
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cdependency_step.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Treference__type__dependency_step = {
	'property':Tproperty_step;
};
export class Creference__type__dependency_step extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty_step & { readonly inferences: {
			dependencies: () => application.Ccomputation_phase;
			existence: () => application.Cexistence__application;
			has_reference: () => application.Cyes__has_reference;
			reference: () => application.Creference_definer;
			relative_location_of_referenced_node: () => application.Crelative_object_location;
			text: () => application.Ctext__type__property;
			value_source: () => application.Cvalue_source;
		} }
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		definer: () => application.Creference_definer;
		phase: () => application.Ccomputation_phase;
		step_type: () => application.Cstep_type;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.inferences.has_reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.referenced_node_type())
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(context => evaluate__inline_dependency_chain(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.properties.property?.inferences.has_reference())
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.step_dependency()).result!, {
						target: () => resolve(this)
							.then(context => evaluate__eliminate_dependencies(detach, 
								resolve(context)
									.then(() => this)
									.then(context => context?.properties.property?.inferences.has_reference())
									.then(context => context?.properties.expression)
									.then(context => context?.component_root.output.location())
									.then(context => context?.component_root.output.entity()).result!, {
								chain: () => resolve(this)
									.then(() => this)
									.then(context => context?.component_root.input.context())
									.then(context => context?.component_root.output.dependency()).result!

								})).result!

						})).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.context())
					.then(context => context?.component_root.output.direction()).result!
				, 
				existence: () => resolve(this)
					.then(context => evaluate__step_existence(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.existence()).result!, {
						step_existence: () => resolve(this)
							.then(() => this)
							.then(context => context?.properties.property?.inferences.existence()).result!

						})).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.property?.inferences.relative_location_of_referenced_node()).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(context => evaluate__step_value_source_dependence(detach, 
						resolve(context)
							.then(() => this)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!, {
						step_value_source_dependence: () => resolve(this)
							.then(() => this)
							.then(context => context?.properties.property?.inferences.reference())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						})).result!

				});
			}).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.inferences.has_reference())
			.then(context => context?.definitions.reference_definer).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(context => evaluate__computation_phase_merge(detach, 
				resolve(context)
					.then(() => this)
					.then(context => context?.properties.property?.inferences.dependencies()).result!, {
				other_computation_phase: () => resolve(this)
					.then(() => this)
					.then(context => context?.properties.property)
					.then(context => context?.component_root.output.phase()).result!

				})).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.property?.inferences.has_reference())
			.then(context => context?.properties.expression)
			.then(context => context?.definitions.entry_step_definition)
			.then(context => context?.definitions.step_type).result!)
	}
	constructor(init:Treference__type__dependency_step, public parent:Cdependency_step) {
		super();
		const $this = this;
		this.properties = {
			property: new Creference__type__dependency_step.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_rule = {
	'property':Tproperty_step;
	'rule':Twhere_rule_step;
};
export class Creference_rule extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty_step & { readonly inferences: {
			has_reference: () => application.Cyes__has_reference;
			text: () => application.Ctext__type__property;
		} },
		readonly rule:Cwhere_rule_step
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		definer: () => application.Creference_definer;
		phase: () => application.Ccomputation_phase;
		step_type: () => application.Cstep_type;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.component_root.output.context()).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.component_root.output.definer()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.component_root.output.phase()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.step_type()).result!)
	}
	constructor(init:Treference_rule, public parent:Cdependency_step) {
		super();
		const $this = this;
		this.properties = {
			property: new Creference_rule.Dproperty(init['property'], $this),
			rule: new Creference_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_context_rule__type__dependency_step = {
	'rule':Twhere_rule_step;
};
export class Cstate_context_rule__type__dependency_step extends AlanNode {
	public readonly properties:{
		readonly rule:Cwhere_rule_step
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		definer: () => application.Creference_definer;
		phase: () => application.Ccomputation_phase;
		step_type: () => application.Cstep_type;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.component_root.output.context()).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.component_root.output.definer()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.component_root.output.phase()).result!),
		step_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.step_type()).result!)
	}
	public readonly inferences:{
		state: () => application.Cstates__state_group
	} = {
		state: cache((detach:boolean) => {
			const application__dependency_step__type__state_context_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as application.Cstates__state_group : undefined).result!;
		})

	}
	constructor(init:Tstate_context_rule__type__dependency_step, public parent:Cdependency_step) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cstate_context_rule__type__dependency_step.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state context rule'`; }
	public get entity() { return this.parent.entity; }
}
export class Cdependency_gen extends AlanStruct {
	constructor(
		public readonly properties: {
			dependency: () => application.Cdependency__application, 
			gen: () => application.Cdependency__application
		}
	) { super(); }
	public definitions:{
		optional_dependency_gen: Coptional_dependency_gen;
	} = {
		optional_dependency_gen: new Coptional_dependency_gen({name:'gen', definition: this})
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'dependency gen'`; }
	public is(other:Cdependency_gen):boolean {
		if (!this.properties.dependency().is(other.properties.dependency())) return false;
		if (!this.properties.gen().is(other.properties.gen())) return false;
		return true;
	}
}
type Vdependency_definition = { name: 'inverse reference step', definition: application.Cinverse_reference_step}|{ name: 'entry step', definition: application.Centry_step_definition}|{ name: 'collection step', definition: application.Ccollection__type__property}|{ name: 'recursion start step', definition: application.Cstart}
export class Cdependency_definition extends AlanStruct {
	constructor(
		public readonly variant:Vdependency_definition, public input: {
			location: () => application.Crelative_object_location,
			parent: () => application.Cdependency__application
		}) { super(); }
	public definitions:{
		dependency: Cdependency__application;
	} = {
		dependency: new Cdependency__application({name:'step', definition: this})
	}
	public readonly output:{
		base_entity: () => application.Centity__application;
		definer: () => application.Centity__application;
		entity: () => application.Centity__application;
		location: () => application.Crelative_object_location;
		parent: () => application.Cdependency__application;
	} = {
		base_entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection step': {
						const application__dependency_definition_out___base_entity___collection_step_nval = context.cast('collection step');
						return resolve(context)
							.then(context => application__dependency_definition_out___base_entity___collection_step_nval)
							.then(context => context?.component_root.output.entity()).result;
					}
					case 'entry step': {
						const application__dependency_definition_out___base_entity___entry_step_nval = context.cast('entry step');
						return resolve(context)
							.then(context => application__dependency_definition_out___base_entity___entry_step_nval)
							.then(context => context?.component_root.output.collection())
							.then(context => context?.component_root.output.entity()).result;
					}
					case 'inverse reference step': {
						const application__dependency_definition_out___base_entity___inverse_reference_step_nval = context.cast('inverse reference step');
						return resolve(context)
							.then(context => application__dependency_definition_out___base_entity___inverse_reference_step_nval)
							.then(context => context?.properties.collection())
							.then(context => context?.component_root.output.entity()).result;
					}
					case 'recursion start step': {
						const application__dependency_definition_out___base_entity___recursion_start_step_nval = context.cast('recursion start step');
						return resolve(context)
							.then(context => application__dependency_definition_out___base_entity___recursion_start_step_nval)
							.then(context => context?.properties.binding_node_path?.inferences.collection())
							.then(context => context?.component_root.output.entity()).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		definer: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection step': {
						const application__dependency_definition_out___definer___collection_step_nval = context.cast('collection step');
						return resolve(context)
							.then(context => application__dependency_definition_out___definer___collection_step_nval)
							.then(context => context?.definitions.entity).result;
					}
					case 'entry step': {
						const application__dependency_definition_out___definer___entry_step_nval = context.cast('entry step');
						return resolve(context)
							.then(context => application__dependency_definition_out___definer___entry_step_nval)
							.then(context => context?.component_root.output.definer())
							.then(context => context?.component_root.output.defining_property())
							.then(context => context?.component_root.output.entity()).result;
					}
					case 'inverse reference step': {
						const application__dependency_definition_out___definer___inverse_reference_step_nval = context.cast('inverse reference step');
						return resolve(context)
							.then(context => application__dependency_definition_out___definer___inverse_reference_step_nval)
							.then(context => context?.properties.collection())
							.then(context => context?.definitions.entity).result;
					}
					case 'recursion start step': {
						const application__dependency_definition_out___definer___recursion_start_step_nval = context.cast('recursion start step');
						return resolve(context)
							.then(context => application__dependency_definition_out___definer___recursion_start_step_nval)
							.then(context => context?.properties.binding_node_path?.inferences.collection())
							.then(context => context?.definitions.entity).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'collection step': {
						const application__dependency_definition_out___entity___collection_step_nval = context.cast('collection step');
						return resolve(context)
							.then(context => application__dependency_definition_out___entity___collection_step_nval)
							.then(context => context?.definitions.entity).result;
					}
					case 'entry step': {
						const application__dependency_definition_out___entity___entry_step_nval = context.cast('entry step');
						return resolve(context)
							.then(context => application__dependency_definition_out___entity___entry_step_nval)
							.then(context => context?.component_root.output.collection())
							.then(context => context?.definitions.entity).result;
					}
					case 'inverse reference step': {
						const application__dependency_definition_out___entity___inverse_reference_step_nval = context.cast('inverse reference step');
						return resolve(context)
							.then(context => application__dependency_definition_out___entity___inverse_reference_step_nval)
							.then(context => context?.properties.collection())
							.then(context => context?.definitions.entity).result;
					}
					case 'recursion start step': {
						const application__dependency_definition_out___entity___recursion_start_step_nval = context.cast('recursion start step');
						return resolve(context)
							.then(context => application__dependency_definition_out___entity___recursion_start_step_nval)
							.then(context => context?.properties.binding_node_path?.inferences.collection())
							.then(context => context?.definitions.entity).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!),
		parent: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent()).result!)
	};
	public cast<K extends Vdependency_definition['name']>(_variant:K):Extract<Vdependency_definition, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vdependency_definition['name']]:(($:Extract<Vdependency_definition, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'dependency definition'`; }
	public is(other:Cdependency_definition):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcurrent_value__application = { name: 'none', definition: (typeof Ccurrent_value__application.Pnone)}|{ name: 'maybe value', definition: (typeof Ccurrent_value__application.Pmaybe_value)}
export class Ccurrent_value__application extends AlanStruct {
	public static Pnone:Ccurrent_value__application = new class PrimitiveInstance extends Ccurrent_value__application {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Ccurrent_value__application})
			this.variant.definition = this;
		}
	}
	public static Pmaybe_value:Ccurrent_value__application = new class PrimitiveInstance extends Ccurrent_value__application {
		constructor () {
			super({name: 'maybe value', definition: undefined as unknown as Ccurrent_value__application})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vcurrent_value__application) { super(); }
	public cast<K extends Vcurrent_value__application['name']>(_variant:K):Extract<Vcurrent_value__application, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcurrent_value__application['name']]:(($:Extract<Vcurrent_value__application, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'current value'`; }
	public is(other:Ccurrent_value__application):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cmaybe_value = application.Ccurrent_value__application;
export import Cnone__current_value = application.Ccurrent_value__application;
function evaluate__convert_to_subset_dependency(detach:boolean, application__convert_to_subset_dependency_nval:application.Cdependency__application, input: {
	upper_bound_cardinality: () => application.Cupper_bound_cardinality
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__convert_to_subset_dependency_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application__convert_to_subset_dependency_nval).result;
				}
				case 'step': {
					const application__convert_to_subset_dependency___step_nval = context.cast('step');
					return resolve(context)
						.then(context => application__convert_to_subset_dependency___step_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'collection step': {
									const application__convert_to_subset_dependency___step___collection_step_nval = context.cast('collection step');
									return resolve(context).then(context => new Cinverse_reference_step({
										collection: () => resolve(self)
											.then(context => application__convert_to_subset_dependency___step___collection_step_nval).result!, 
										upper_bound_cardinality: () => resolve(self)
											.then(() => self)
											.then(context => context?.component_root.input.upper_bound_cardinality()).result!}), )
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cdependency_definition({name: 'inverse reference step', definition: conv_context},{
											location: () => resolve(self)
												.then(context => application__convert_to_subset_dependency___step_nval)
												.then(context => context?.component_root.output.location()).result!
											, 
											parent: () => resolve(self)
												.then(context => evaluate__convert_to_subset_dependency(detach, 
													resolve(context)
														.then(context => application__convert_to_subset_dependency___step_nval)
														.then(context => context?.component_root.output.parent()).result!, {
													upper_bound_cardinality: () => resolve(self)
														.then(() => self)
														.then(context => context?.component_root.input.upper_bound_cardinality()).result!

													})).result!

											});
										})
										.then(context => context?.definitions.dependency).result;
								}
								case 'entry step': {
									const application__convert_to_subset_dependency___step___entry_step_nval = context.cast('entry step');
									return resolve(context)
										.then(context => application__convert_to_subset_dependency___step___entry_step_nval)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cdependency_definition({name: 'entry step', definition: conv_context},{
											location: () => resolve(self)
												.then(context => application__convert_to_subset_dependency___step_nval)
												.then(context => context?.component_root.output.location()).result!
											, 
											parent: () => resolve(self)
												.then(context => evaluate__convert_to_subset_dependency(detach, 
													resolve(context)
														.then(context => application__convert_to_subset_dependency___step_nval)
														.then(context => context?.component_root.output.parent()).result!, {
													upper_bound_cardinality: () => resolve(self)
														.then(() => self)
														.then(context => context?.component_root.input.upper_bound_cardinality()).result!

													})).result!

											});
										})
										.then(context => context?.definitions.dependency).result;
								}
								case 'inverse reference step': {
									const application__convert_to_subset_dependency___step___inverse_reference_step_nval = context.cast('inverse reference step');
									return resolve(context).then(context => new Cinverse_reference_step({
										collection: () => resolve(self)
											.then(context => application__convert_to_subset_dependency___step___inverse_reference_step_nval)
											.then(context => context?.properties.collection()).result!, 
										upper_bound_cardinality: () => resolve(self)
											.then(() => self)
											.then(context => context?.component_root.input.upper_bound_cardinality()).result!}), )
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cdependency_definition({name: 'inverse reference step', definition: conv_context},{
											location: () => resolve(self)
												.then(context => application__convert_to_subset_dependency___step_nval)
												.then(context => context?.component_root.output.location()).result!
											, 
											parent: () => resolve(self)
												.then(context => evaluate__convert_to_subset_dependency(detach, 
													resolve(context)
														.then(context => application__convert_to_subset_dependency___step_nval)
														.then(context => context?.component_root.output.parent()).result!, {
													upper_bound_cardinality: () => resolve(self)
														.then(() => self)
														.then(context => context?.component_root.input.upper_bound_cardinality()).result!

													})).result!

											});
										})
										.then(context => context?.definitions.dependency).result;
								}
								case 'recursion start step': {
									const application__convert_to_subset_dependency___step___recursion_start_step_nval = context.cast('recursion start step');
									return resolve(context)
										.then(context => application__convert_to_subset_dependency___step___recursion_start_step_nval)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cdependency_definition({name: 'recursion start step', definition: conv_context},{
											location: () => resolve(self)
												.then(context => application__convert_to_subset_dependency___step_nval)
												.then(context => context?.component_root.output.location()).result!
											, 
											parent: () => resolve(self)
												.then(context => evaluate__convert_to_subset_dependency(detach, 
													resolve(context)
														.then(context => application__convert_to_subset_dependency___step_nval)
														.then(context => context?.component_root.output.parent()).result!, {
													upper_bound_cardinality: () => resolve(self)
														.then(() => self)
														.then(context => context?.component_root.input.upper_bound_cardinality()).result!

													})).result!

											});
										})
										.then(context => context?.definitions.dependency).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
export type Tcontext_object_step = {
	'context':'dynamic user'|['dynamic user', {}]|'this'|['this', {}]|['variable', Tvariable__context__context_object_step];
};
export class Ccontext_object_step extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_object_step.Dcontext
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.context?.state.node.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.context?.state.node.output.phase()).result!)
	};
	constructor(init:Tcontext_object_step, public location:AlanNode, public input: {
		definer_direction: () => application.Cdirection,
		defining_property: () => application.Cproperty__type__attributes,
		existence_support: () => application.Cexistence__application,
		modifier: () => application.Cvalue_source,
		value_source_support: () => application.Cvalue_source,
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			context: new Ccontext_object_step.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic_user__context__context_object_step = {
};
export class Cdynamic_user__context__context_object_step extends AlanNode {
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.user_node_type())
			.then(context => {
				const conv_context = resolve(context)
					.then(context => context?.definitions.value_object).result!;
				return new Cnavigation_context({name: 'value', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.inferences.user_node_type())
					.then(context => context?.component_root.output.entity())
					.then(context => context?.definitions.dependency).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(() => application.Cdirection.Punbounded).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(() => application.Cexistence__application.Pmandatory).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.inferences.user_node_type())
					.then(context => context?.component_root.output.entity())
					.then(context => context?.definitions.relative_object_location).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(() => this)
					.then(context => context?.inferences.user_node_type())
					.then(context => context?.component_root.output.value_source()).result!

				});
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	public readonly inferences:{
		existence: () => application.Coptional__existence,
		has_dynamic_users: () => application.Cyes__has_dynamic_users,
		user_modifier: () => application.Cuser__external_source,
		user_node_type: () => application.Cnode__application
	} = {
		existence: cache((detach:boolean) => {
			const application__context_object_step__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})
		,
		has_dynamic_users: cache((detach:boolean) => {
			const application__context_object_step__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		user_modifier: cache((detach:boolean) => {
			const application__context_object_step__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.modifier())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
		})
		,
		user_node_type: cache((detach:boolean) => {
			const application__context_object_step__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.has_dynamic_users())
				.then(context => context?.properties.users_collection_path?.inferences.collection())
				.then(context => context?.properties.node).result!;
		})

	}
	constructor(init:Tdynamic_user__context__context_object_step, public parent:Ccontext_object_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'dynamic user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tthis__context__context_object_step = {
};
export class Cthis__context__context_object_step extends AlanNode {
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.defining_property())
			.then(context => context?.parent)
			.then(context => {
				const conv_context = resolve(context).result!;
				return new Cnavigation_context({name: 'attribute', definition: conv_context},{
				dependency: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.defining_property())
					.then(context => context?.component_root.output.entity())
					.then(context => context?.definitions.dependency).result!
				, 
				direction: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.definer_direction()).result!
				, 
				existence: () => resolve(this)
					.then(() => this)
					.then(() => application.Cexistence__application.Pmandatory).result!
				, 
				location: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.defining_property())
					.then(context => context?.component_root.output.entity())
					.then(context => context?.definitions.relative_object_location).result!
				, 
				value_source_dependence: () => resolve(this)
					.then(() => this)
					.then(context => context?.component_root.input.defining_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!

				});
			}).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccomputation_phase.Pzero).result!)
	}
	constructor(init:Tthis__context__context_object_step, public parent:Ccontext_object_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'this'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvariable__context__context_object_step = {
	'step':Tnamed_object_step;
};
export class Cvariable__context__context_object_step extends AlanNode {
	public readonly properties:{
		readonly step:Cnamed_object_step & { readonly inferences: {
			existence: () => application.Cexistence__application;
			value_source: () => application.Cvalue_source;
		} }
	};
	public readonly output:{
		context: () => application.Cnavigation_context;
		phase: () => application.Ccomputation_phase;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.step)
			.then(context => context?.component_root.output.assignment())
			.then(context => context?.component_root.output.context()).result!),
		phase: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.step)
			.then(context => context?.component_root.output.assignment())
			.then(context => context?.component_root.output.phase()).result!)
	}
	constructor(init:Tvariable__context__context_object_step, public parent:Ccontext_object_step) {
		super();
		const $this = this;
		this.properties = {
			step: new Cvariable__context__context_object_step.Dstep(init['step'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'variable'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext_node_path = {
	'context':'dynamic user'|['dynamic user', {}]|'this'|['this', {}]|['variable', Tvariable__context__context_node_path];
};
export class Ccontext_node_path extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_node_path.Dcontext
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.context?.state.node.output.node_type()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.context?.state.node.output.parent_navigation_support()).result!)
	};
	constructor(init:Tcontext_node_path, public location:AlanNode, public input: {
		existence_support: () => application.Cexistence__application,
		modifier: () => application.Cbase_value_source,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			context: new Ccontext_node_path.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic_user__context__context_node_path = {
};
export class Cdynamic_user__context__context_node_path extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.has_dynamic_users())
			.then(context => context?.properties.users_collection_path?.inferences.collection())
			.then(context => context?.properties.node).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnavigation_support.Pentity).result!)
	}
	public readonly inferences:{
		existence: () => application.Coptional__existence,
		has_dynamic_users: () => application.Cyes__has_dynamic_users,
		user_modifier: () => application.Cuser__external_source
	} = {
		existence: cache((detach:boolean) => {
			const application__context_node_path__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})
		,
		has_dynamic_users: cache((detach:boolean) => {
			const application__context_node_path__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		user_modifier: cache((detach:boolean) => {
			const application__context_node_path__context__dynamic_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.modifier())
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
		})

	}
	constructor(init:Tdynamic_user__context__context_node_path, public parent:Ccontext_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'dynamic user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tthis__context__context_node_path = {
};
export class Cthis__context__context_node_path extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.this_node()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnavigation_support.Punbounded).result!)
	}
	constructor(init:Tthis__context__context_node_path, public parent:Ccontext_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'this'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvariable__context__context_node_path = {
	'name':['explicit', Texplicit__name__variable]|'implicit'|['implicit', {}];
};
export class Cvariable__context__context_node_path extends AlanNode {
	public readonly properties:{
		readonly name:Cvariable__context__context_node_path.Dname
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		parent_navigation_support: () => application.Cnavigation_support;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.name?.state.node.output.assigned_variable())
			.then(context => context?.component_root.output.node_type()).result!),
		parent_navigation_support: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Cnavigation_support.Pentity).result!)
	}
	constructor(init:Tvariable__context__context_node_path, public parent:Ccontext_node_path) {
		super();
		const $this = this;
		this.properties = {
			name: new Cvariable__context__context_node_path.Dname(init['name'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'variable'`; }
	public get entity() { return this.parent.entity; }
}
export type Texplicit__name__variable = {
	'head':Tancestor_variable_path;
	'variable':string;
};
export class Cexplicit__name__variable extends AlanNode {
	public readonly properties:{
		readonly head:Cancestor_variable_path & { readonly inferences: {
			assigned_variable: () => application.Cvariable_assignment;
			explicit_name: () => application.Cexplicit__name__variable_assignment;
		} },
		readonly variable:Cexplicit__name__variable.Dvariable
	};
	public readonly output:{
		assigned_variable: () => application.Cvariable_assignment;
	} = {
		assigned_variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.head?.inferences.assigned_variable()).result!)
	}
	constructor(init:Texplicit__name__variable, public parent:Cvariable__context__context_node_path) {
		super();
		const $this = this;
		this.properties = {
			head: new Cexplicit__name__variable.Dhead(init['head'], $this),
			variable: new Cexplicit__name__variable.Dvariable(init['variable'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Timplicit__name__variable = {
};
export class Cimplicit__name__variable extends AlanNode {
	public readonly output:{
		assigned_variable: () => application.Cvariable_assignment;
	} = {
		assigned_variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.assigned_variable()).result!)
	}
	public readonly inferences:{
		assigned_variable: () => application.Cvariable_assignment,
		implicit_name: () => application.Cimplicit__name__variable_assignment
	} = {
		assigned_variable: cache((detach:boolean) => {
			const application__context_node_path__context__variable__name__implicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.variable())
				.then(context => context?.variant.name === 'assigned' ? context.variant.definition as application.Cvariable_assignment : undefined).result!;
		})
		,
		implicit_name: cache((detach:boolean) => {
			const application__context_node_path__context__variable__name__implicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.assigned_variable())
				.then(context => {
					if (context?.properties.name.state.name === 'implicit') {
						return resolve(context.properties.name.state.node as application.Cimplicit__name__variable_assignment).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Timplicit__name__variable, public parent:Cvariable__context__context_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'name'?'implicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconstant_number_value = {
	'value':number;
};
export class Cconstant_number_value extends AlanNode {
	public readonly properties:{
		readonly value:Cconstant_number_value.Dvalue
	};
	public readonly output:{
		set_type: () => application.Cnumber_set_type;
	} = {
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.value?.inferences.set_type()).result!)
	};
	constructor(init:Tconstant_number_value, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			value: new Cconstant_number_value.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vconnection_status = { name: 'disconnected', definition: (typeof Cconnection_status.Pdisconnected)}|{ name: 'connecting', definition: (typeof Cconnection_status.Pconnecting)}|{ name: 'connected', definition: (typeof Cconnection_status.Pconnected)}
export class Cconnection_status extends AlanStruct {
	public static Pdisconnected:Cconnection_status = new class PrimitiveInstance extends Cconnection_status {
		constructor () {
			super({name: 'disconnected', definition: undefined as unknown as Cconnection_status})
			this.variant.definition = this;
		}
	}
	public static Pconnecting:Cconnection_status = new class PrimitiveInstance extends Cconnection_status {
		constructor () {
			super({name: 'connecting', definition: undefined as unknown as Cconnection_status})
			this.variant.definition = this;
		}
	}
	public static Pconnected:Cconnection_status = new class PrimitiveInstance extends Cconnection_status {
		constructor () {
			super({name: 'connected', definition: undefined as unknown as Cconnection_status})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vconnection_status) { super(); }
	public cast<K extends Vconnection_status['name']>(_variant:K):Extract<Vconnection_status, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vconnection_status['name']]:(($:Extract<Vconnection_status, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'connection status'`; }
	public is(other:Cconnection_status):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cconnected = application.Cconnection_status;
export import Cconnecting = application.Cconnection_status;
export import Cdisconnected = application.Cconnection_status;
export type Tconditional_node_path = {
	'path':Tnode_path;
};
export class Cconditional_node_path extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.node_type()).result!)
	};
	constructor(init:Tconditional_node_path, public location:AlanNode, public input: {
		modifier: () => application.Cbase_value_source,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Cconditional_node_path.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
function evaluate__compute_most_significant_location(detach:boolean, application__compute_most_significant_location_nval:application.Centity__application, input: {
	context_location: () => application.Crelative_object_location,
	dependency_location_support: () => application.Crecursion_dependence,
	reference_context_location: () => application.Crelative_object_location,
	root: () => application.Capplication
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(context => application__compute_most_significant_location_nval).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.context_location())
					.then(context => context?.component_root.output.entity()).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(() => self)
			.then(context => context?.component_root.input.context_location()).result
			} else {
				return resolve(match_context)
				.then(context => application__compute_most_significant_location_nval)
				.then(context => {
					switch (context?.variant.name) {
						case 'collection': {
							const application__compute_most_significant_location___none___collection_nval = context.cast('collection');
							return resolve(context)
								.then(context => evaluate__compute_most_significant_location(detach, 
									resolve(context)
										.then(context => application__compute_most_significant_location___none___collection_nval)
										.then(context => context?.component_root.output.entity()).result!, {
									context_location: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.context_location()).result!
									, 
									dependency_location_support: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.dependency_location_support()).result!
									, 
									reference_context_location: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.reference_context_location()).result!
									, 
									root: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.root()).result!

									})).result;
						}
						case 'root': {
							return resolve(context)
								.then(context => evaluate__check_support_for_navigation_to_location(detach, 
									resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.root()).result!, {
									dependency_location_support: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.dependency_location_support()).result!
									, 
									reference_context_location: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.reference_context_location()).result!

									})).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result
			}
		}).result!
}
function evaluate__compute_local_key_iubc___on_match(detach:boolean, application__compute_local_key_iubc___on_match_nval:application.Cdependency__application, input: {
	context_entity: () => application.Centity__application
}):application.Cupper_bound_cardinality {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__compute_local_key_iubc___on_match_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(() => self)
						.then(() => application.Cupper_bound_cardinality.Pone).result;
				}
				case 'step': {
					const application__compute_local_key_iubc___on_match___step_nval = context.cast('step');
					return resolve(context)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(context => {
								const left = resolve(context)
									.then(context => application__compute_local_key_iubc___on_match___step_nval)
									.then(context => context?.component_root.output.definer()).result;
								const right = resolve(context)
									.then(() => self)
									.then(context => context?.component_root.input.context_entity()).result;
								return left?.is(right!) ? left : undefined
							}).result;
							if (expression_context !== undefined) {
							return resolve(match_context)
							.then(context => application__compute_local_key_iubc___on_match___step_nval)
							.then(context => {
								switch (context?.variant.name) {
									case 'collection step': {
										return resolve(context)
											.then(() => self)
											.then(() => application.Cupper_bound_cardinality.Pmany).result;
									}
									case 'entry step': {
										const application__compute_local_key_iubc___on_match___step___struct___entry_step_nval = context.cast('entry step');
										return resolve(context)
											.then(context => application__compute_local_key_iubc___on_match___step___struct___entry_step_nval)
											.then(context => context?.component_root.output.local_iubc())
											.then(context => {
												switch (context?.variant.name) {
													case 'many': {
														return resolve(context)
															.then(() => self)
															.then(() => application.Cupper_bound_cardinality.Pmany).result;
													}
													case 'one': {
														return resolve(context)
															.then(context => evaluate__compute_local_key_iubc___on_match(detach, 
																resolve(context)
																	.then(context => application__compute_local_key_iubc___on_match___step_nval)
																	.then(context => context?.component_root.output.parent()).result!, {
																context_entity: () => resolve(self)
																	.then(() => self)
																	.then(context => context?.component_root.input.context_entity()).result!

																})).result;
													}
													case undefined: return undefined;
													default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
												};
											}).result;
									}
									case 'inverse reference step': {
										return resolve(context)
											.then(() => self)
											.then(() => application.Cupper_bound_cardinality.Pmany).result;
									}
									case 'recursion start step': {
										return resolve(context)
											.then(() => self)
											.then(() => application.Cupper_bound_cardinality.Pmany).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result
							} else {
								return resolve(match_context)
								.then(() => self)
								.then(() => application.Cupper_bound_cardinality.Pone).result
							}
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__compute_local_key_iubc(detach:boolean, application__compute_local_key_iubc_nval:application.Cdependency__application, input: {
	context_entity: () => application.Centity__application
}):application.Cupper_bound_cardinality {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__compute_local_key_iubc_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(() => self)
						.then(() => application.Cupper_bound_cardinality.Pmany).result;
				}
				case 'step': {
					const application__compute_local_key_iubc___step_nval = context.cast('step');
					return resolve(context)
						.then(context => application__compute_local_key_iubc___step_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'collection step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case 'entry step': {
									const application__compute_local_key_iubc___step___entry_step_nval = context.cast('entry step');
									return resolve(context)
										.then(context => application__compute_local_key_iubc___step___entry_step_nval)
										.then(context => context?.component_root.output.local_iubc())
										.then(context => {
											switch (context?.variant.name) {
												case 'many': {
													return resolve(context)
														.then(() => self)
														.then(() => application.Cupper_bound_cardinality.Pmany).result;
												}
												case 'one': {
													return resolve(context)
														.then(match_context => { 
															const expression_context = resolve(match_context)
															.then(context => {
																const left = resolve(context)
																	.then(context => application__compute_local_key_iubc___step_nval)
																	.then(context => context?.component_root.output.definer()).result;
																const right = resolve(context)
																	.then(() => self)
																	.then(context => context?.component_root.input.context_entity()).result;
																return left?.is(right!) ? left : undefined
															}).result;
															if (expression_context !== undefined) {
															return resolve(match_context)
															.then(context => evaluate__compute_local_key_iubc___on_match(detach, 
																resolve(context)
																	.then(context => application__compute_local_key_iubc___step_nval)
																	.then(context => context?.component_root.output.parent()).result!, {
																context_entity: () => resolve(self)
																	.then(() => self)
																	.then(context => context?.component_root.input.context_entity()).result!

																})).result
															} else {
																return resolve(match_context)
																.then(context => evaluate__compute_local_key_iubc(detach, 
																	resolve(context)
																		.then(context => application__compute_local_key_iubc___step_nval)
																		.then(context => context?.component_root.output.parent()).result!, {
																	context_entity: () => resolve(self)
																		.then(() => self)
																		.then(context => context?.component_root.input.context_entity()).result!

																	})).result
															}
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'inverse reference step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case 'recursion start step': {
									return resolve(context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__compute_entry_step_iubc_imp(detach:boolean, application__compute_entry_step_iubc_imp_nval:application.Capplication, input: {
	collection_path_context: () => application.Cnavigation_context,
	collection_path_iubc: () => application.Cupper_bound_cardinality,
	definition: () => application.Centry_step_definition,
	local_step_iubc: () => application.Cupper_bound_cardinality
}):application.Cupper_bound_cardinality {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.collection_path_context())
					.then(context => context?.component_root.output.dependency())
					.then(context => context?.component_root.output.definer()).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.definition())
					.then(context => context?.component_root.output.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.component_root.output.entity()).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(() => self)
			.then(context => context?.component_root.input.collection_path_iubc()).result
			} else {
				return resolve(match_context)
				.then(() => self)
				.then(context => context?.component_root.input.definition())
				.then(context => context?.component_root.output.definer())
				.then(context => context?.component_root.output.defining_property())
				.then(context => context?.component_root.output.entity())
				.then(context => {
					switch (context?.variant.name) {
						case 'collection': {
							const application__compute_entry_step_iubc_imp___none___collection_nval = context.cast('collection');
							return resolve(context)
								.then(match_context => { 
									const expression_context = resolve(match_context)
									.then(context => {
										const left = resolve(context)
											.then(() => self)
											.then(context => context?.component_root.input.collection_path_context())
											.then(context => context?.component_root.output.dependency())
											.then(context => context?.component_root.output.definer()).result;
										const right = resolve(context)
											.then(context => application__compute_entry_step_iubc_imp___none___collection_nval)
											.then(context => context?.component_root.output.entity()).result;
										return left?.is(right!) ? left : undefined
									}).result;
									if (expression_context !== undefined) {
									return resolve(match_context)
									.then(() => self)
									.then(context => context?.component_root.input.local_step_iubc())
									.then(context => {
										switch (context?.variant.name) {
											case 'many': {
												return resolve(context)
													.then(() => self)
													.then(() => application.Cupper_bound_cardinality.Pmany).result;
											}
											case 'one': {
												return resolve(context)
													.then(() => self)
													.then(context => context?.component_root.input.collection_path_iubc()).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
										};
									}).result
									} else {
										return resolve(match_context)
										.then(() => self)
										.then(() => application.Cupper_bound_cardinality.Pmany).result
									}
								}).result;
						}
						case 'root': {
							return resolve(context)
								.then(() => self)
								.then(() => application.Cupper_bound_cardinality.Pmany).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result
			}
		}).result!
}
function evaluate__compute_entry_step_iubc(detach:boolean, application__compute_entry_step_iubc_nval:application.Capplication, input: {
	collection_path_context: () => application.Cnavigation_context,
	definition: () => application.Centry_step_definition,
	local_step_iubc: () => application.Cupper_bound_cardinality
}):application.Cupper_bound_cardinality {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => evaluate__compute_entry_step_iubc_imp(detach, 
			resolve(context)
				.then(context => application__compute_entry_step_iubc_nval).result!, {
			collection_path_context: () => resolve(self)
				.then(() => self)
				.then(context => context?.component_root.input.collection_path_context()).result!
			, 
			collection_path_iubc: () => resolve(self)
				.then(context => evaluate__dependency_to_iubc(detach, 
					resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.collection_path_context())
						.then(context => context?.component_root.output.dependency()).result!, )).result!
			, 
			definition: () => resolve(self)
				.then(() => self)
				.then(context => context?.component_root.input.definition()).result!
			, 
			local_step_iubc: () => resolve(self)
				.then(() => self)
				.then(context => context?.component_root.input.local_step_iubc()).result!

			})).result!
}
function evaluate__computation_phase_merge(detach:boolean, application__computation_phase_merge_nval:application.Ccomputation_phase, input: {
	other_computation_phase: () => application.Ccomputation_phase
}):application.Ccomputation_phase {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.other_computation_phase()).result!
}
type Vcomputation_phase = { name: 'zero', definition: (typeof Ccomputation_phase.Pzero), widening_index: 0}
export class Ccomputation_phase extends AlanStruct {
	public static Pzero:Ccomputation_phase = new class PrimitiveInstance extends Ccomputation_phase {
		constructor () {
			super({name: 'zero', definition: undefined as unknown as Ccomputation_phase, widening_index: 0})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vcomputation_phase) { super(); }
	public cast<K extends Vcomputation_phase['name']>(_variant:K):Extract<Vcomputation_phase, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcomputation_phase['name']]:(($:Extract<Vcomputation_phase, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'computation phase'`; }
	public is(other:Ccomputation_phase):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Czero__computation_phase = application.Ccomputation_phase;
function evaluate__compare_entity_dependency(detach:boolean, application__compare_entity_dependency_nval:application.Centity__application, input: {
	actual: () => application.Cdependency__application,
	expected: () => application.Cdependency_definition,
	location_support: () => application.Crelative_object_location
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => evaluate__check_dependencies_wrapper(detach, 
			resolve(context)
				.then(context => application__compare_entity_dependency_nval).result!, {
			actual: () => resolve(self)
				.then(() => self)
				.then(context => context?.component_root.input.actual())
				.then(context => {
					switch (context?.variant.name) {
						case 'self': {
							return resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.actual()).result;
						}
						case 'step': {
							return resolve(context)
								.then(context => evaluate__unwind_sibling_steps(detach, 
									resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.actual()).result!, {
									location_support: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.location_support()).result!

									})).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!
			, 
			expected: () => resolve(self)
				.then(() => self)
				.then(context => context?.component_root.input.expected())
				.then(context => context?.component_root.output.parent()).result!

			})).result!
}
function evaluate__compare_dependency_definitions(detach:boolean, application__compare_dependency_definitions_nval:application.Cdependency__application, input: {
	expected: () => application.Cdependency_definition
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => application__compare_dependency_definitions_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => {
							const left = resolve(context)
								.then(context => application__compare_dependency_definitions_nval).result;
							const right = resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.expected())
								.then(context => context?.definitions.dependency).result;
							return left?.is(right!) ? left : undefined
						}).result;
				}
				case 'step': {
					const application__compare_dependency_definitions___step_nval = context.cast('step');
					return resolve(context)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(context => application__compare_dependency_definitions___step_nval)
								.then(context => context?.component_root.output.parent()).result!, {
							expected: () => resolve(self)
								.then(() => self)
								.then(context => context?.component_root.input.expected())
								.then(context => context?.component_root.output.parent()).result!

							})).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
type Vcommand_properties = { name: 'node', definition: application.Cnode__application}|{ name: 'collection', definition: application.Ccommand_collection}|{ name: 'stategroup', definition: application.Ccommand_stategroup}|{ name: 'scalar', definition: application.Ccommand_scalar}
export class Ccommand_properties extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_properties) { super(); }
	public cast<K extends Vcommand_properties['name']>(_variant:K):Extract<Vcommand_properties, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_properties['name']]:(($:Extract<Vcommand_properties, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command properties'`; }
	public is(other:Ccommand_properties):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcommand_scalar = { name: 'file', definition: application.Ccommand_file}|{ name: 'text', definition: application.Ccommand_text}|{ name: 'number', definition: application.Ccommand_number}
export class Ccommand_scalar extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_scalar) { super(); }
	public definitions:{
		command_properties: Ccommand_properties;
	} = {
		command_properties: new Ccommand_properties({name:'scalar', definition: this})
	}
	public cast<K extends Vcommand_scalar['name']>(_variant:K):Extract<Vcommand_scalar, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_scalar['name']]:(($:Extract<Vcommand_scalar, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command scalar'`; }
	public is(other:Ccommand_scalar):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcommand_text = { name: 'temporary', definition: (typeof Ccommand_text.Ptemporary)}|{ name: 'text property', definition: application.Ctext__type__property}
export class Ccommand_text extends AlanStruct {
	public static Ptemporary:Ccommand_text = new class PrimitiveInstance extends Ccommand_text {
		constructor () {
			super({name: 'temporary', definition: undefined as unknown as Ccommand_text})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vcommand_text) { super(); }
	public definitions:{
		command_scalar: Ccommand_scalar;
	} = {
		command_scalar: new Ccommand_scalar({name:'text', definition: this})
	}
	public cast<K extends Vcommand_text['name']>(_variant:K):Extract<Vcommand_text, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_text['name']]:(($:Extract<Vcommand_text, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command text'`; }
	public is(other:Ccommand_text):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Ctemporary = application.Ccommand_text;
type Vcommand_target = { name: 'command', definition: application.Ccommand__application}|{ name: 'properties', definition: application.Ccommand_properties}
export class Ccommand_target extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_target, public input: {
			current_value: () => application.Ccurrent_value__application,
			operation: () => application.Coptional_command_operation
		}) { super(); }
	public readonly output:{
		current_value: () => application.Ccurrent_value__application;
		operation: () => application.Coptional_command_operation;
	} = {
		current_value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.current_value()).result!),
		operation: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.operation()).result!)
	};
	public cast<K extends Vcommand_target['name']>(_variant:K):Extract<Vcommand_target, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_target['name']]:(($:Extract<Vcommand_target, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command target'`; }
	public is(other:Ccommand_target):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcommand_stategroup = { name: 'stategroup property', definition: application.Cstate_group}
export class Ccommand_stategroup extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_stategroup) { super(); }
	public definitions:{
		command_properties: Ccommand_properties;
	} = {
		command_properties: new Ccommand_properties({name:'stategroup', definition: this})
	}
	public cast<K extends Vcommand_stategroup['name']>(_variant:K):Extract<Vcommand_stategroup, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_stategroup['name']]:(($:Extract<Vcommand_stategroup, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command stategroup'`; }
	public is(other:Ccommand_stategroup):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcommand_state = { name: 'state', definition: application.Cstates__state_group}
export class Ccommand_state extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_state) { super(); }
	public cast<K extends Vcommand_state['name']>(_variant:K):Extract<Vcommand_state, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_state['name']]:(($:Extract<Vcommand_state, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command state'`; }
	public is(other:Ccommand_state):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
function evaluate__command_property_target_derivation(detach:boolean, application__command_property_target_derivation_nval:application.Cproperty__type__attributes, ):application.Ccommand_properties {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => application__command_property_target_derivation_nval)
		.then(context => context?.properties.type)
		.then(context => {
			const state_group = context;
			switch (state_group?.state.name) {
				case 'collection': {
					const application__command_property_target_derivation___collection_nval = state_group.cast('collection');
					return resolve(context)
						.then(context => application__command_property_target_derivation___collection_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ccommand_collection({name: 'collection property', definition: conv_context},{
							entry_object: () => resolve(self)
								.then(context => application__command_property_target_derivation___collection_nval)
								.then(context => context?.properties.node).result!
							, 
							value_source: () => resolve(self)
								.then(context => application__command_property_target_derivation___collection_nval)
								.then(context => context?.properties.type?.state.node.output.value_source()).result!

							});
						})
						.then(context => context?.definitions.command_properties).result;
				}
				case 'file': {
					const application__command_property_target_derivation___file_nval = state_group.cast('file');
					return resolve(context)
						.then(context => application__command_property_target_derivation___file_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ccommand_file({name: 'file property', definition: conv_context});
						})
						.then(context => context?.definitions.command_scalar)
						.then(context => context?.definitions.command_properties).result;
				}
				case 'group': {
					const application__command_property_target_derivation___group_nval = state_group.cast('group');
					return resolve(context)
						.then(context => application__command_property_target_derivation___group_nval)
						.then(context => context?.properties.node)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ccommand_properties({name: 'node', definition: conv_context});
						}).result;
				}
				case 'number': {
					const application__command_property_target_derivation___number_nval = state_group.cast('number');
					return resolve(context)
						.then(context => application__command_property_target_derivation___number_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ccommand_number({name: 'number property', definition: conv_context},{
							numerical_type: () => resolve(self)
								.then(context => application__command_property_target_derivation___number_nval)
								.then(context => context?.properties.numerical_type?.ref).result!
							, 
							set_type: () => resolve(self)
								.then(context => application__command_property_target_derivation___number_nval)
								.then(context => context?.properties.set_type?.state.node.output.set_type()).result!

							});
						})
						.then(context => context?.definitions.command_scalar)
						.then(context => context?.definitions.command_properties).result;
				}
				case 'state group': {
					const application__command_property_target_derivation___state_group_nval = state_group.cast('state group');
					return resolve(context)
						.then(context => application__command_property_target_derivation___state_group_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ccommand_stategroup({name: 'stategroup property', definition: conv_context});
						})
						.then(context => context?.definitions.command_properties).result;
				}
				case 'text': {
					const application__command_property_target_derivation___text_nval = state_group.cast('text');
					return resolve(context)
						.then(context => application__command_property_target_derivation___text_nval)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Ccommand_text({name: 'text property', definition: conv_context});
						})
						.then(context => context?.definitions.command_scalar)
						.then(context => context?.definitions.command_properties).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
			};}).result!
}
export type Tcommand_permission_definition = {
	'execute permission':['explicit', Texplicit__execute_permission]|'reachable'|['reachable', {}];
};
export class Ccommand_permission_definition extends AlanNode {
	public readonly properties:{
		readonly execute_permission:Ccommand_permission_definition.Dexecute_permission
	};
	constructor(init:Tcommand_permission_definition, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			execute_permission: new Ccommand_permission_definition.Dexecute_permission(init['execute permission'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texplicit__execute_permission = {
	'requirement':Tuser_requirement;
};
export class Cexplicit__execute_permission extends AlanNode {
	public readonly properties:{
		readonly requirement:Cuser_requirement
	};
	constructor(init:Texplicit__execute_permission, public parent:Ccommand_permission_definition) {
		super();
		const $this = this;
		this.properties = {
			requirement: new Cexplicit__execute_permission.Drequirement(init['requirement'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'execute permission'?'explicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Treachable = {
};
export class Creachable extends AlanNode {
	constructor(init:Treachable, public parent:Ccommand_permission_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'execute permission'?'reachable'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand_object_initialization_behaviour = {
	'behaviour':'ensure existence'|['ensure existence', {}]|'fail when exists'|['fail when exists', {}];
};
export class Ccommand_object_initialization_behaviour extends AlanNode {
	public readonly properties:{
		readonly behaviour:Ccommand_object_initialization_behaviour.Dbehaviour
	};
	public readonly output:{
		current_value: () => application.Ccurrent_value__application;
	} = {
		current_value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.behaviour?.state.node.output.current_value()).result!)
	};
	constructor(init:Tcommand_object_initialization_behaviour, public location:AlanNode, public input: {
		current_value: () => application.Ccurrent_value__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			behaviour: new Ccommand_object_initialization_behaviour.Dbehaviour(init['behaviour'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tensure_existence = {
};
export class Censure_existence extends AlanNode {
	public readonly output:{
		current_value: () => application.Ccurrent_value__application;
	} = {
		current_value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccurrent_value__application.Pmaybe_value).result!)
	}
	public readonly inferences:{
		maybe_value: () => application.Cmaybe_value
	} = {
		maybe_value: cache((detach:boolean) => {
			const application__command_object_initialization_behaviour__behaviour__ensure_existence_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.current_value())
				.then(context => context?.variant.name === 'maybe value' ? context.variant.definition as application.Cmaybe_value : undefined).result!;
		})

	}
	constructor(init:Tensure_existence, public parent:Ccommand_object_initialization_behaviour) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'behaviour'?'ensure existence'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfail_when_exists = {
};
export class Cfail_when_exists extends AlanNode {
	public readonly output:{
		current_value: () => application.Ccurrent_value__application;
	} = {
		current_value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => application.Ccurrent_value__application.Pnone).result!)
	}
	constructor(init:Tfail_when_exists, public parent:Ccommand_object_initialization_behaviour) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'behaviour'?'fail when exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand_object_expression = {
	'properties':Record<string, Tproperties__command_object_expression>;
};
export class Ccommand_object_expression extends AlanNode {
	public readonly properties:{
		readonly properties:Ccommand_object_expression.Dproperties
	};
	constructor(init:Tcommand_object_expression, public location:AlanNode, public input: {
		current_value: () => application.Ccurrent_value__application,
		operation: () => application.Ccommand_operation,
		source: () => application.Cbase_value_source,
		target: () => application.Cnode__application,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Ccommand_object_expression.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__command_object_expression extends Reference<application.Cproperty__type__attributes, string> {
	public readonly inferences:{
		current_value: () => application.Ccurrent_value__application,
		filter_identity: () => application.Cvalue_member_inclusion,
		filter_interface_dataset_status: () => application.Cvalue_member_inclusion,
		filter_interface_instance_key: () => application.Cvalue_member_inclusion,
		filter_password: () => application.Cvalue_member_inclusion,
		filter_password_status: () => application.Cvalue_member_inclusion,
		filter_username: () => application.Cvalue_member_inclusion,
		target: () => application.Ccommand_properties,
		value_source: () => application.Cbase_value_source
	}
	constructor(key:string, $this:Cproperties__command_object_expression) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.target())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as application.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
		this.inferences = {
			current_value: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => application__command_object_expression__properties_key_nval)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.attribute_type())
					.then(context => {
						switch (context?.variant.name) {
							case 'key': {
								return resolve(context)
									.then(() => $this.parent)
									.then(() => application.Ccurrent_value__application.Pnone).result;
							}
							case 'simple': {
								return resolve(context)
									.then(() => $this.parent)
									.then(context => context?.component_root.input.current_value()).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!;
			})
			,
			filter_identity: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(match_context => { 
						const expression_context = resolve(match_context)
						.then(() => $this.parent)
						.then(context => context?.component_root.input.operation())
						.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
						.then(context => context?.variant.name === 'identity' ? context.variant.definition as application.Cidentity_initializer : undefined).result;
						if (expression_context !== undefined) {
						const application__command_object_expression__properties_key_inf___filter_identity___struct_nval = expression_context;
						return resolve(match_context)
						.then(context => evaluate__value_member_filter(detach, 
							resolve(context)
								.then(context => application__command_object_expression__properties_key_nval).result!, {
							filter: () => resolve($this.parent)
								.then(context => application__command_object_expression__properties_key_inf___filter_identity___struct_nval)
								.then(context => context?.component_root.output.identity()).result!

							})).result
						} else {
							return resolve(match_context)
							.then(() => $this.parent)
							.then(() => application.Cvalue_member_inclusion.Pyes).result
						}
					}).result!;
			})
			,
			filter_interface_dataset_status: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(match_context => { 
						const expression_context = resolve(match_context)
						.then(() => $this.parent)
						.then(context => context?.component_root.input.operation())
						.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
						.then(context => context?.variant.name === 'interface connection status' ? context.variant.definition as application.Ccases__yes__connection_status : undefined).result;
						if (expression_context !== undefined) {
						const application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct_nval = expression_context;
						return resolve(match_context)
						.then(context => application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct_nval)
						.then(context => context?.properties.dataset_status)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'no': {
									return resolve(context)
										.then(() => $this.parent)
										.then(() => application.Cvalue_member_inclusion.Pyes).result;
								}
								case 'yes': {
									const application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct___yes_nval = state_group.cast('yes');
									return resolve(context)
										.then(context => evaluate__value_member_filter(detach, 
											resolve(context)
												.then(context => application__command_object_expression__properties_key_nval).result!, {
											filter: () => resolve($this.parent)
												.then(context => application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct___yes_nval)
												.then(context => context?.properties.state_group_path?.inferences.state_group())
												.then(context => context?.parent).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result
						} else {
							return resolve(match_context)
							.then(() => $this.parent)
							.then(() => application.Cvalue_member_inclusion.Pyes).result
						}
					}).result!;
			})
			,
			filter_interface_instance_key: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(match_context => { 
						const expression_context = resolve(match_context)
						.then(() => $this.parent)
						.then(context => context?.component_root.input.operation())
						.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
						.then(context => context?.variant.name === 'interface instance' ? context.variant.definition as application.Cinterface_instance_initializer : undefined).result;
						if (expression_context !== undefined) {
						const application__command_object_expression__properties_key_inf___filter_interface_instance_key___struct_nval = expression_context;
						return resolve(match_context)
						.then(context => evaluate__value_member_filter(detach, 
							resolve(context)
								.then(context => application__command_object_expression__properties_key_nval).result!, {
							filter: () => resolve($this.parent)
								.then(context => application__command_object_expression__properties_key_inf___filter_interface_instance_key___struct_nval)
								.then(context => context?.component_root.output.interface_())
								.then(context => context?.inferences.collection())
								.then(context => context?.properties.key_property?.ref)
								.then(context => context?.parent).result!

							})).result
						} else {
							return resolve(match_context)
							.then(() => $this.parent)
							.then(() => application.Cvalue_member_inclusion.Pyes).result
						}
					}).result!;
			})
			,
			filter_password: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(match_context => { 
						const expression_context = resolve(match_context)
						.then(() => $this.parent)
						.then(context => context?.component_root.input.operation())
						.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
						.then(context => context?.variant.name === 'password' ? context.variant.definition as application.Cpassword_initializer : undefined).result;
						if (expression_context !== undefined) {
						const application__command_object_expression__properties_key_inf___filter_password___struct_nval = expression_context;
						return resolve(match_context)
						.then(context => evaluate__value_member_filter(detach, 
							resolve(context)
								.then(context => application__command_object_expression__properties_key_nval).result!, {
							filter: () => resolve($this.parent)
								.then(context => application__command_object_expression__properties_key_inf___filter_password___struct_nval)
								.then(context => context?.component_root.output.password()).result!

							})).result
						} else {
							return resolve(match_context)
							.then(() => $this.parent)
							.then(() => application.Cvalue_member_inclusion.Pyes).result
						}
					}).result!;
			})
			,
			filter_password_status: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(match_context => { 
						const expression_context = resolve(match_context)
						.then(() => $this.parent)
						.then(context => context?.component_root.input.operation())
						.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
						.then(context => context?.variant.name === 'password' ? context.variant.definition as application.Cpassword_initializer : undefined).result;
						if (expression_context !== undefined) {
						const application__command_object_expression__properties_key_inf___filter_password_status___struct_nval = expression_context;
						return resolve(match_context)
						.then(context => evaluate__value_member_filter(detach, 
							resolve(context)
								.then(context => application__command_object_expression__properties_key_nval).result!, {
							filter: () => resolve($this.parent)
								.then(context => application__command_object_expression__properties_key_inf___filter_password_status___struct_nval)
								.then(context => context?.component_root.output.password_status()).result!

							})).result
						} else {
							return resolve(match_context)
							.then(() => $this.parent)
							.then(() => application.Cvalue_member_inclusion.Pyes).result
						}
					}).result!;
			})
			,
			filter_username: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(() => $this.parent)
					.then(context => context?.component_root.input.operation())
					.then(context => {
						switch (context?.variant.name) {
							case 'initialization': {
								const application__command_object_expression__properties_key_inf___filter_username___initialization_nval = context.cast('initialization');
								return resolve(context)
									.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization_nval)
									.then(context => {
										switch (context?.variant.name) {
											case 'identity': {
												const application__command_object_expression__properties_key_inf___filter_username___initialization___identity_nval = context.cast('identity');
												return resolve(context)
													.then(context => evaluate__value_member_filter(detach, 
														resolve(context)
															.then(context => application__command_object_expression__properties_key_nval).result!, {
														filter: () => resolve($this.parent)
															.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization___identity_nval)
															.then(context => context?.component_root.output.username()).result!

														})).result;
											}
											case 'interface connection status': {
												return resolve(context)
													.then(() => $this.parent)
													.then(() => application.Cvalue_member_inclusion.Pyes).result;
											}
											case 'interface instance': {
												return resolve(context)
													.then(() => $this.parent)
													.then(() => application.Cvalue_member_inclusion.Pyes).result;
											}
											case 'object': {
												return resolve(context)
													.then(() => $this.parent)
													.then(() => application.Cvalue_member_inclusion.Pyes).result;
											}
											case 'password': {
												const application__command_object_expression__properties_key_inf___filter_username___initialization___password_nval = context.cast('password');
												return resolve(context)
													.then(context => evaluate__value_member_filter(detach, 
														resolve(context)
															.then(context => application__command_object_expression__properties_key_nval).result!, {
														filter: () => resolve($this.parent)
															.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization___password_nval)
															.then(context => context?.component_root.output.username()).result!

														})).result;
											}
											case 'user': {
												const application__command_object_expression__properties_key_inf___filter_username___initialization___user_nval = context.cast('user');
												return resolve(context)
													.then(context => evaluate__value_member_filter(detach, 
														resolve(context)
															.then(context => application__command_object_expression__properties_key_nval).result!, {
														filter: () => resolve($this.parent)
															.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization___user_nval)
															.then(context => context?.component_root.output.username()).result!

														})).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
										};
									}).result;
							}
							case 'update': {
								return resolve(context)
									.then(() => $this.parent)
									.then(() => application.Cvalue_member_inclusion.Pyes).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!;
			})
			,
			target: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => evaluate__command_property_target_derivation(detach, 
						resolve(context)
							.then(context => application__command_object_expression__properties_key_nval).result!, )).result!;
			})
			,
			value_source: cache((detach:boolean) => {
				const application__command_object_expression__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(() => $this.parent)
					.then(context => context?.component_root.input.operation())
					.then(context => {
						switch (context?.variant.name) {
							case 'initialization': {
								return resolve(context)
									.then(() => $this.parent)
									.then(context => context?.component_root.input.current_value())
									.then(context => {
										switch (context?.variant.name) {
											case 'maybe value': {
												return resolve(context)
													.then(context => evaluate__user_source_checker(detach, 
														resolve(context)
															.then(context => application__command_object_expression__properties_key_nval).result!, )).result;
											}
											case 'none': {
												return resolve(context)
													.then(context => application__command_object_expression__properties_key_nval)
													.then(context => context?.properties.type?.state.node.output.value_source())
													.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
													.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
													.then(context => context?.definitions.base_value_source).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
										};
									}).result;
							}
							case 'update': {
								return resolve(context)
									.then(context => evaluate__user_source_checker(detach, 
										resolve(context)
											.then(context => application__command_object_expression__properties_key_nval).result!, )).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__command_object_expression = {
	'expression':Tcommand_expression;
};
export class Cproperties__command_object_expression extends AlanDictionaryEntry {
	public key:Kproperties__command_object_expression;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly expression:Ccommand_expression
	};
	constructor(key:string, init:Tproperties__command_object_expression, public parent:Ccommand_object_expression) {
		super();
		const $this = this;
		this.key = new Kproperties__command_object_expression(key, $this);
		this.properties = {
			expression: new Cproperties__command_object_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
type Vcommand_number = { name: 'intermediate result', definition: application.Capplication}|{ name: 'number property', definition: application.Cnumber__type__property}
export class Ccommand_number extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_number, public input: {
			numerical_type: () => application.Cnumerical_types,
			set_type: () => application.Cnumber_set_type
		}) { super(); }
	public definitions:{
		command_scalar: Ccommand_scalar;
	} = {
		command_scalar: new Ccommand_scalar({name:'number', definition: this})
	}
	public readonly output:{
		numerical_type: () => application.Cnumerical_types;
		set_type: () => application.Cnumber_set_type;
	} = {
		numerical_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.numerical_type()).result!),
		set_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.set_type()).result!)
	};
	public cast<K extends Vcommand_number['name']>(_variant:K):Extract<Vcommand_number, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_number['name']]:(($:Extract<Vcommand_number, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command number'`; }
	public is(other:Ccommand_number):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vcommand_file = { name: 'file property', definition: application.Cfile}
export class Ccommand_file extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_file) { super(); }
	public definitions:{
		command_scalar: Ccommand_scalar;
	} = {
		command_scalar: new Ccommand_scalar({name:'file', definition: this})
	}
	public cast<K extends Vcommand_file['name']>(_variant:K):Extract<Vcommand_file, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_file['name']]:(($:Extract<Vcommand_file, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command file'`; }
	public is(other:Ccommand_file):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tcommand_expression = {
	'operation':['entry', Tentry__operation__command_expression]|['execute operation', Texecute_operation__operation__command_expression]|'ignore'|['ignore', {}]|['node switch', Tnode_switch__operation__command_expression]|['produce value', Tproduce_value__operation__command_expression]|['state switch', Tstate_switch__operation__command_expression]|['update properties', Tupdate_properties__operation__command_expression]|['walk', Twalk__operation__command_expression];
};
export class Ccommand_expression extends AlanNode {
	public readonly properties:{
		readonly operation:Ccommand_expression.Doperation
	};
	constructor(init:Tcommand_expression, public location:AlanNode, public input: {
		source: () => application.Cbase_value_source,
		target: () => application.Ccommand_target,
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			operation: new Ccommand_expression.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tentry__operation__command_expression = {
	'type':['create', Tcreate__type__entry__operation__command_expression]|['delete', Tdelete__type__entry__operation__command_expression];
};
export class Centry__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly type:Centry__operation__command_expression.Dtype
	};
	public readonly inferences:{
		target_collection: () => application.Ccommand_collection
	} = {
		target_collection: cache((detach:boolean) => {
			const application__command_expression__operation__entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'properties' ? context.variant.definition as application.Ccommand_properties : undefined)
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccommand_collection : undefined).result!;
		})

	}
	constructor(init:Tentry__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Centry__operation__command_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcreate__type__entry__operation__command_expression = {
	'behaviour':Tcommand_object_initialization_behaviour;
	'expression':Tcommand_object_expression;
};
export class Ccreate__type__entry__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly behaviour:Ccommand_object_initialization_behaviour,
		readonly expression:Ccommand_object_expression & { readonly inferences: {
			key_initialization: () => application.Cproperties__command_object_expression;
		} }
	};
	public readonly inferences:{
		initialization_context: () => application.Ccommand_initialization_operation,
		key_member: () => application.Cproperty__type__attributes
	} = {
		initialization_context: cache((detach:boolean) => {
			const application__command_expression__operation__entry__type__create_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.operation())
				.then(context => {
					switch (context?.variant.name) {
						case 'none': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => application.Ccommand_initialization_operation.Pobject).result;
						}
						case 'operation': {
							const application__command_expression__operation__entry__type__create_inf___initialization_context___operation_nval = context.cast('operation');
							return resolve(context)
								.then(context => application__command_expression__operation__entry__type__create_inf___initialization_context___operation_nval)
								.then(context => {
									switch (context?.variant.name) {
										case 'initialization': {
											const application__command_expression__operation__entry__type__create_inf___initialization_context___operation___initialization_nval = context.cast('initialization');
											return resolve(context)
												.then(context => application__command_expression__operation__entry__type__create_inf___initialization_context___operation___initialization_nval).result;
										}
										case 'update': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => application.Ccommand_initialization_operation.Pobject).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		key_member: cache((detach:boolean) => {
			const application__command_expression__operation__entry__type__create_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_collection())
				.then(context => {
					switch (context?.variant.name) {
						case 'collection property': {
							const application__command_expression__operation__entry__type__create_inf___key_member___collection_property_nval = context.cast('collection property');
							return resolve(context)
								.then(context => application__command_expression__operation__entry__type__create_inf___key_member___collection_property_nval)
								.then(context => context?.properties.key_property?.ref)
								.then(context => context?.parent).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tcreate__type__entry__operation__command_expression, public parent:Centry__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			behaviour: new Ccreate__type__entry__operation__command_expression.Dbehaviour(init['behaviour'], $this),
			expression: new Ccreate__type__entry__operation__command_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'create'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdelete__type__entry__operation__command_expression = {
	'path':Tsingular_node_path;
};
export class Cdelete__type__entry__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly path:Csingular_node_path & { readonly inferences: {
			collection_node_type: () => application.Cnode__application;
		} }
	};
	public readonly inferences:{
		collection_property: () => application.Ccollection__type__property,
		update: () => application.Cupdate__command_operation
	} = {
		collection_property: cache((detach:boolean) => {
			const application__command_expression__operation__entry__type__delete_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.target_collection())
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		update: cache((detach:boolean) => {
			const application__command_expression__operation__entry__type__delete_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.operation())
				.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Ccommand_operation : undefined)
				.then(context => context?.variant.name === 'update' ? context.variant.definition as application.Cupdate__command_operation : undefined).result!;
		})

	}
	constructor(init:Tdelete__type__entry__operation__command_expression, public parent:Centry__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cdelete__type__entry__operation__command_expression.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'delete'`; }
	public get entity() { return this.parent.entity; }
}
export type Texecute_operation__operation__command_expression = {
	'command':string;
	'expression':Tcommand_object_expression;
	'path':Tsingular_node_path;
};
export class Cexecute_operation__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly command:Cexecute_operation__operation__command_expression.Dcommand,
		readonly expression:Ccommand_object_expression,
		readonly path:Csingular_node_path
	};
	public readonly inferences:{
		command_target: () => application.Ccommand__application
	} = {
		command_target: cache((detach:boolean) => {
			const application__command_expression__operation__execute_operation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'command' ? context.variant.definition as application.Ccommand__application : undefined).result!;
		})

	}
	constructor(init:Texecute_operation__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			command: new Cexecute_operation__operation__command_expression.Dcommand(init['command'], $this),
			expression: new Cexecute_operation__operation__command_expression.Dexpression(init['expression'], $this),
			path: new Cexecute_operation__operation__command_expression.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'execute operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tignore__operation__command_expression = {
};
export class Cignore__operation__command_expression extends AlanNode {
	public readonly inferences:{
		supported_operation: () => application.Coptional_command_operation
	} = {
		supported_operation: cache((detach:boolean) => {
			const application__command_expression__operation__ignore_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => {
					switch (context?.variant.name) {
						case 'command': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.target())
								.then(context => context?.component_root.output.operation()).result;
						}
						case 'properties': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.target())
								.then(context => context?.component_root.output.operation())
								.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Ccommand_operation : undefined)
								.then(context => context?.variant.name === 'update' ? context.variant.definition as application.Cupdate__command_operation : undefined)
								.then(context => context?.definitions.optional_command_operation).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tignore__operation__command_expression, public parent:Ccommand_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'ignore'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_switch__operation__command_expression = {
	'cases':Tcases__node_switch__operation__command_expression;
	'expression':Tnode_expression;
};
export class Cnode_switch__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases__node_switch__operation__command_expression,
		readonly expression:Cnode_expression
	};
	constructor(init:Tnode_switch__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases__node_switch__operation__command_expression(init['cases'], $this),
			expression: new Cnode_switch__operation__command_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'node switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases__node_switch__operation__command_expression = {
	'node case':Tnode_case__cases__node_switch__operation__command_expression;
	'none case':Tnone_case__cases__node_switch__operation__command_expression;
};
export class Ccases__node_switch__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly node_case:Cnode_case__cases__node_switch__operation__command_expression,
		readonly none_case:Cnone_case__cases__node_switch__operation__command_expression
	};
	constructor(init:Tcases__node_switch__operation__command_expression, public parent:Cnode_switch__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			node_case: new Cnode_case__cases__node_switch__operation__command_expression(init['node case'], $this),
			none_case: new Cnone_case__cases__node_switch__operation__command_expression(init['none case'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_case__cases__node_switch__operation__command_expression = {
	'expression':Tcommand_expression;
	'variable assignment':Toptional_variable_assignment;
};
export class Cnode_case__cases__node_switch__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_expression,
		readonly variable_assignment:Coptional_variable_assignment
	};
	constructor(init:Tnode_case__cases__node_switch__operation__command_expression, public parent:Ccases__node_switch__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnode_case__cases__node_switch__operation__command_expression.Dexpression(init['expression'], $this),
			variable_assignment: new Cnode_case__cases__node_switch__operation__command_expression.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'node case'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone_case__cases__node_switch__operation__command_expression = {
	'expression':Tcommand_expression;
};
export class Cnone_case__cases__node_switch__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_expression
	};
	constructor(init:Tnone_case__cases__node_switch__operation__command_expression, public parent:Ccases__node_switch__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnone_case__cases__node_switch__operation__command_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'none case'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduce_value__operation__command_expression = {
	'value':['current value', Tcurrent_value__value]|'empty collection'|['empty collection', {}]|['object', Tobject__value__produce_value__operation__command_expression]|['scalar', Tscalar__value__produce_value__operation__command_expression]|['state', Tstate__value__produce_value__operation__command_expression];
};
export class Cproduce_value__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly value:Cproduce_value__operation__command_expression.Dvalue
	};
	constructor(init:Tproduce_value__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cproduce_value__operation__command_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'produce value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_value__value = {
	'expression':Tcommand_expression;
};
export class Ccurrent_value__value extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_expression
	};
	public readonly inferences:{
		current_value: () => application.Cmaybe_value,
		initialization: () => application.Ccommand_initialization_operation,
		property_target: () => application.Ccommand_properties
	} = {
		current_value: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__current_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.current_value())
				.then(context => context?.variant.name === 'maybe value' ? context.variant.definition as application.Cmaybe_value : undefined).result!;
		})
		,
		initialization: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__current_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.operation())
				.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Ccommand_operation : undefined)
				.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined).result!;
		})
		,
		property_target: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__current_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'properties' ? context.variant.definition as application.Ccommand_properties : undefined).result!;
		})

	}
	constructor(init:Tcurrent_value__value, public parent:Cproduce_value__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ccurrent_value__value.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'current value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tempty_collection = {
};
export class Cempty_collection extends AlanNode {
	public readonly inferences:{
		collection: () => application.Ccommand_collection,
		initialization: () => application.Ccommand_initialization_operation,
		no_current_value: () => application.Cnone__current_value
	} = {
		collection: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__empty_collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'properties' ? context.variant.definition as application.Ccommand_properties : undefined)
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccommand_collection : undefined).result!;
		})
		,
		initialization: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__empty_collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.operation())
				.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Ccommand_operation : undefined)
				.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined).result!;
		})
		,
		no_current_value: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__empty_collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.current_value())
				.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__current_value : undefined).result!;
		})

	}
	constructor(init:Tempty_collection, public parent:Cproduce_value__operation__command_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'empty collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tobject__value__produce_value__operation__command_expression = {
	'expression':Tcommand_object_expression;
};
export class Cobject__value__produce_value__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_object_expression
	};
	public readonly inferences:{
		operation: () => application.Ccommand_operation,
		target_object: () => application.Cnode__application
	} = {
		operation: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__object_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.operation())
				.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Ccommand_operation : undefined).result!;
		})
		,
		target_object: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__object_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'properties' ? context.variant.definition as application.Ccommand_properties : undefined)
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tobject__value__produce_value__operation__command_expression, public parent:Cproduce_value__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cobject__value__produce_value__operation__command_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'object'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscalar__value__produce_value__operation__command_expression = {
	'expression':Tscalar_expression;
};
export class Cscalar__value__produce_value__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Cscalar_expression
	};
	public readonly inferences:{
		target_scalar: () => application.Ccommand_scalar
	} = {
		target_scalar: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__scalar_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'properties' ? context.variant.definition as application.Ccommand_properties : undefined)
				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Ccommand_scalar : undefined).result!;
		})

	}
	constructor(init:Tscalar__value__produce_value__operation__command_expression, public parent:Cproduce_value__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cscalar__value__produce_value__operation__command_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'scalar'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__value__produce_value__operation__command_expression = {
	'behaviour':Tcommand_object_initialization_behaviour;
	'expression':Tcommand_object_expression;
	'state':string;
};
export class Cstate__value__produce_value__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly behaviour:Ccommand_object_initialization_behaviour,
		readonly expression:Ccommand_object_expression,
		readonly state:Cstate__value__produce_value__operation__command_expression.Dstate
	};
	public readonly inferences:{
		initialization_context: () => application.Ccommand_initialization_operation,
		target_state_group: () => application.Ccommand_stategroup
	} = {
		initialization_context: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.component_root.output.operation())
				.then(context => {
					switch (context?.variant.name) {
						case 'none': {
							return resolve(context)
								.then(() => this.parent)
								.then(() => application.Ccommand_initialization_operation.Pobject).result;
						}
						case 'operation': {
							const application__command_expression__operation__produce_value__value__state_inf___initialization_context___operation_nval = context.cast('operation');
							return resolve(context)
								.then(context => application__command_expression__operation__produce_value__value__state_inf___initialization_context___operation_nval)
								.then(context => {
									switch (context?.variant.name) {
										case 'initialization': {
											const application__command_expression__operation__produce_value__value__state_inf___initialization_context___operation___initialization_nval = context.cast('initialization');
											return resolve(context)
												.then(context => application__command_expression__operation__produce_value__value__state_inf___initialization_context___operation___initialization_nval).result;
										}
										case 'update': {
											return resolve(context)
												.then(() => this.parent)
												.then(() => application.Ccommand_initialization_operation.Pobject).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})
		,
		target_state_group: cache((detach:boolean) => {
			const application__command_expression__operation__produce_value__value__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'properties' ? context.variant.definition as application.Ccommand_properties : undefined)
				.then(context => context?.variant.name === 'stategroup' ? context.variant.definition as application.Ccommand_stategroup : undefined).result!;
		})

	}
	constructor(init:Tstate__value__produce_value__operation__command_expression, public parent:Cproduce_value__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			behaviour: new Cstate__value__produce_value__operation__command_expression.Dbehaviour(init['behaviour'], $this),
			expression: new Cstate__value__produce_value__operation__command_expression.Dexpression(init['expression'], $this),
			state: new Cstate__value__produce_value__operation__command_expression.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_switch__operation__command_expression = {
	'path':Tpath__state_switch__operation__command_expression;
	'states':Record<string, Tstates__state_switch__operation__command_expression>;
};
export class Cstate_switch__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly path:Cpath__state_switch__operation__command_expression,
		readonly states:Cstate_switch__operation__command_expression.Dstates
	};
	constructor(init:Tstate_switch__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cpath__state_switch__operation__command_expression(init['path'], $this),
			states: new Cstate_switch__operation__command_expression.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'state switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpath__state_switch__operation__command_expression = {
	'path':Tsingular_node_path;
	'stategroup':string;
};
export class Cpath__state_switch__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly path:Csingular_node_path,
		readonly stategroup:Cpath__state_switch__operation__command_expression.Dstategroup
	};
	constructor(init:Tpath__state_switch__operation__command_expression, public parent:Cstate_switch__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cpath__state_switch__operation__command_expression.Dpath(init['path'], $this),
			stategroup: new Cpath__state_switch__operation__command_expression.Dstategroup(init['stategroup'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'path'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_switch__operation__command_expression extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_switch__operation__command_expression) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.path)
			.then(context => context?.properties.stategroup?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_switch__operation__command_expression = {
	'expression':Tcommand_expression;
	'variable assignment':Toptional_variable_assignment;
};
export class Cstates__state_switch__operation__command_expression extends AlanDictionaryEntry {
	public key:Kstates__state_switch__operation__command_expression;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly expression:Ccommand_expression,
		readonly variable_assignment:Coptional_variable_assignment
	};
	constructor(key:string, init:Tstates__state_switch__operation__command_expression, public parent:Cstate_switch__operation__command_expression) {
		super();
		const $this = this;
		this.key = new Kstates__state_switch__operation__command_expression(key, $this);
		this.properties = {
			expression: new Cstates__state_switch__operation__command_expression.Dexpression(init['expression'], $this),
			variable_assignment: new Cstates__state_switch__operation__command_expression.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tupdate_properties__operation__command_expression = {
	'path':Tsingular_node_path;
	'target':['node', Tnode__target__update_properties__operation__command_expression]|['property', Tproperty__target__update_properties__operation__command_expression];
};
export class Cupdate_properties__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly path:Csingular_node_path & { readonly inferences: {
			user_value: () => application.Cuser__external_source;
		} },
		readonly target:Cupdate_properties__operation__command_expression.Dtarget
	};
	public readonly inferences:{
		command_target: () => application.Ccommand__application
	} = {
		command_target: cache((detach:boolean) => {
			const application__command_expression__operation__update_properties_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'command' ? context.variant.definition as application.Ccommand__application : undefined).result!;
		})

	}
	constructor(init:Tupdate_properties__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cupdate_properties__operation__command_expression.Dpath(init['path'], $this),
			target: new Cupdate_properties__operation__command_expression.Dtarget(init['target'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'update properties'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__target__update_properties__operation__command_expression = {
	'expression':Tcommand_object_expression;
};
export class Cnode__target__update_properties__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_object_expression
	};
	constructor(init:Tnode__target__update_properties__operation__command_expression, public parent:Cupdate_properties__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnode__target__update_properties__operation__command_expression.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'target'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__target__update_properties__operation__command_expression = {
	'expression':Tcommand_expression;
	'property':string;
};
export class Cproperty__target__update_properties__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_expression,
		readonly property:Cproperty__target__update_properties__operation__command_expression.Dproperty
	};
	constructor(init:Tproperty__target__update_properties__operation__command_expression, public parent:Cupdate_properties__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cproperty__target__update_properties__operation__command_expression.Dexpression(init['expression'], $this),
			property: new Cproperty__target__update_properties__operation__command_expression.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'target'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Twalk__operation__command_expression = {
	'expression':Tcommand_expression;
	'path':Tsingular_node_path;
	'tail':Tnode_path_tail;
	'type':['collection', Tcollection__type__walk]|['reference set', Treference_set__type__walk];
	'variable assignment':Toptional_variable_assignment;
};
export class Cwalk__operation__command_expression extends AlanNode {
	public readonly properties:{
		readonly expression:Ccommand_expression,
		readonly path:Csingular_node_path,
		readonly tail:Cnode_path_tail,
		readonly type:Cwalk__operation__command_expression.Dtype,
		readonly variable_assignment:Coptional_variable_assignment
	};
	public readonly inferences:{
		supported_target: () => application.Ccommand_target
	} = {
		supported_target: cache((detach:boolean) => {
			const application__command_expression__operation__walk_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => {
					switch (context?.variant.name) {
						case 'command': {
							return resolve(context)
								.then(() => this.parent)
								.then(context => context?.component_root.input.target()).result;
						}
						case 'properties': {
							const application__command_expression__operation__walk_inf___supported_target___properties_nval = context.cast('properties');
							return resolve(context)
								.then(context => application__command_expression__operation__walk_inf___supported_target___properties_nval)
								.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccommand_collection : undefined)
								.then(context => {
									const conv_context = resolve(context)
										.then(context => context?.definitions.command_properties).result!;
									return new Ccommand_target({name: 'properties', definition: conv_context},{
									current_value: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.target())
										.then(context => context?.component_root.output.current_value()).result!
									, 
									operation: () => resolve(this.parent)
										.then(() => this.parent)
										.then(context => context?.component_root.input.target())
										.then(context => context?.component_root.output.operation()).result!

									});
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Twalk__operation__command_expression, public parent:Ccommand_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cwalk__operation__command_expression.Dexpression(init['expression'], $this),
			path: new Cwalk__operation__command_expression.Dpath(init['path'], $this),
			tail: new Cwalk__operation__command_expression.Dtail(init['tail'], $this),
			type: new Cwalk__operation__command_expression.Dtype(init['type'], $this),
			variable_assignment: new Cwalk__operation__command_expression.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'walk'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__walk = {
	'collection':string;
};
export class Ccollection__type__walk extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection__type__walk.Dcollection
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection__type__walk, public parent:Cwalk__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection__type__walk.Dcollection(init['collection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_set__type__walk = {
	'reference set':string;
};
export class Creference_set__type__walk extends AlanNode {
	public readonly properties:{
		readonly reference_set:Creference_set__type__walk.Dreference_set
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.reference_set?.ref)
			.then(context => context?.properties.referenced_type_path)
			.then(context => context?.properties.path?.inferences.node_type()).result!)
	}
	public readonly inferences:{
		command_target: () => application.Ccommand__application
	} = {
		command_target: cache((detach:boolean) => {
			const application__command_expression__operation__walk__type__reference_set_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'command' ? context.variant.definition as application.Ccommand__application : undefined).result!;
		})

	}
	constructor(init:Treference_set__type__walk, public parent:Cwalk__operation__command_expression) {
		super();
		const $this = this;
		this.properties = {
			reference_set: new Creference_set__type__walk.Dreference_set(init['reference set'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference set'`; }
	public get entity() { return this.parent.entity; }
}
type Vcommand_collection = { name: 'collection property', definition: application.Ccollection__type__property}
export class Ccommand_collection extends AlanStruct {
	constructor(
		public readonly variant:Vcommand_collection, public input: {
			entry_object: () => application.Cnode__application,
			value_source: () => application.Cvalue_source
		}) { super(); }
	public definitions:{
		command_properties: Ccommand_properties;
	} = {
		command_properties: new Ccommand_properties({name:'collection', definition: this})
	}
	public readonly output:{
		entry_object: () => application.Cnode__application;
		value_source: () => application.Cvalue_source;
	} = {
		entry_object: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.entry_object()).result!),
		value_source: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.value_source()).result!)
	};
	public cast<K extends Vcommand_collection['name']>(_variant:K):Extract<Vcommand_collection, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcommand_collection['name']]:(($:Extract<Vcommand_collection, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'command collection'`; }
	public is(other:Ccommand_collection):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tcommand__application = {
	'expression':Tcommand_expression;
	'has next command':'no'|['no', {}]|['yes', Tyes__has_next_command];
};
export class Ccommand__application extends AlanNode {
	public definitions:{
		command_target: Ccommand_target;
	} = {
		command_target: new Ccommand_target({name:'command', definition: this}, {
			current_value: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(() => application.Ccurrent_value__application.Pnone).result!),
			operation: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(() => application.Coptional_command_operation.Pnone).result!)
		})
	}
	public readonly properties:{
		readonly expression:Ccommand_expression,
		readonly has_next_command:Ccommand__application.Dhas_next_command
	};
	constructor(init:Tcommand__application, public location:AlanNode, public input: {
		this_node: () => application.Cnode__application,
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ccommand__application.Dexpression(init['expression'], $this),
			has_next_command: new Ccommand__application.Dhas_next_command(init['has next command'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next_command = {
};
export class Cno__has_next_command extends AlanNode {
	constructor(init:Tno__has_next_command, public parent:Ccommand__application) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next command'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next_command = {
	'command':Tcommand__application;
};
export class Cyes__has_next_command extends AlanNode {
	public readonly properties:{
		readonly command:Ccommand__application
	};
	constructor(init:Tyes__has_next_command, public parent:Ccommand__application) {
		super();
		const $this = this;
		this.properties = {
			command: new Cyes__has_next_command.Dcommand(init['command'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next command'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate__check_support_for_navigation_to_location(detach:boolean, application__check_support_for_navigation_to_location_nval:application.Capplication, input: {
	dependency_location_support: () => application.Crecursion_dependence,
	reference_context_location: () => application.Crelative_object_location
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.reference_context_location())
		.then(context => {
			switch (context?.variant.name) {
				case 'context entity': {
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.reference_context_location()).result;
				}
				case 'sibling entity': {
					const application__check_support_for_navigation_to_location___sibling_entity_nval = context.cast('sibling entity');
					return resolve(context)
						.then(() => self)
						.then(context => context?.component_root.input.dependency_location_support())
						.then(context => {
							switch (context?.variant.name) {
								case 'none': {
									return resolve(context)
										.then(() => self)
										.then(context => context?.component_root.input.reference_context_location()).result;
								}
								case 'recursion': {
									const application__check_support_for_navigation_to_location___sibling_entity___recursion_nval = context.cast('recursion');
									return resolve(context)
										.then(context => evaluate__check_graph_traversal_existence(detach, 
											resolve(context)
												.then(context => application__check_support_for_navigation_to_location_nval).result!, {
											graph_traversal_support: () => resolve(self)
												.then(context => application__check_support_for_navigation_to_location___sibling_entity___recursion_nval).result!
											, 
											reference_context_location: () => resolve(self)
												.then(() => self)
												.then(context => context?.component_root.input.reference_context_location()).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
function evaluate__check_graph_traversal_existence(detach:boolean, application__check_graph_traversal_existence_nval:application.Capplication, input: {
	graph_traversal_support: () => application.Cgraph_traversal,
	reference_context_location: () => application.Crelative_object_location
}):application.Crelative_object_location {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(match_context => { 
			const expression_context = resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.reference_context_location())
					.then(context => context?.component_root.output.entity()).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.graph_traversal_support())
					.then(context => context?.properties.traversal())
					.then(context => context?.component_root.output.entity()).result;
				return left?.is(right!) ? left : undefined
			}).result;
			if (expression_context !== undefined) {
			return resolve(match_context)
			.then(context => {
				const left = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.reference_context_location()).result;
				const right = resolve(context)
					.then(() => self)
					.then(context => context?.component_root.input.graph_traversal_support())
					.then(context => context?.properties.traversal())
					.then(context => context?.definitions.sibling_dependency_retrieval)
					.then(context => context?.definitions.relative_object_location).result;
				return left?.is(right!) ? left : undefined
			}).result
			} else {
				return resolve(match_context)
				.then(() => self)
				.then(context => context?.component_root.input.graph_traversal_support())
				.then(context => context?.properties.parent())
				.then(context => {
					switch (context?.variant.name) {
						case 'graph': {
							const application__check_graph_traversal_existence___none___graph_nval = context.cast('graph');
							return resolve(context)
								.then(context => evaluate__check_graph_traversal_existence(detach, 
									resolve(context)
										.then(context => application__check_graph_traversal_existence_nval).result!, {
									graph_traversal_support: () => resolve(self)
										.then(context => application__check_graph_traversal_existence___none___graph_nval).result!
									, 
									reference_context_location: () => resolve(self)
										.then(() => self)
										.then(context => context?.component_root.input.reference_context_location()).result!

									})).result;
						}
						case 'none': {
							return resolve(context)
								.then(() => self)
								.then(context => context?.component_root.input.reference_context_location())
								.then(context => context?.variant.name === 'context entity' ? context.variant.definition as application.Centity__application : undefined)
								.then(context => context?.definitions.relative_object_location).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result
			}
		}).result!
}
function evaluate__check_dependencies_wrapper(detach:boolean, application__check_dependencies_wrapper_nval:application.Centity__application, input: {
	actual: () => application.Cdependency__application,
	expected: () => application.Cdependency__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(context => evaluate__check_dependencies(detach, 
			resolve(context)
				.then(() => self)
				.then(context => context?.component_root.input.actual()).result!, {
			expected: () => resolve(self)
				.then(() => self)
				.then(context => context?.component_root.input.expected()).result!

			})).result!
}
function evaluate__check_dependencies(detach:boolean, application__check_dependencies_nval:application.Cdependency__application, input: {
	expected: () => application.Cdependency__application
}):application.Cdependency__application {
	const self = { component_root: { input: input } };
	return resolve(self)
		.then(() => self)
		.then(context => context?.component_root.input.expected())
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application__check_dependencies_nval)
						.then(context => context?.variant.name === 'self' ? context.variant.definition as application.Centity__application : undefined)
						.then(context => context?.definitions.dependency).result;
				}
				case 'step': {
					const application__check_dependencies___step_nval = context.cast('step');
					return resolve(context)
						.then(context => application__check_dependencies___step_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'collection step': {
									return resolve(context)
										.then(context => evaluate__compare_entity_dependency(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(context => application__check_dependencies_nval)
														.then(context => context?.component_root.output.location())
														.then(context => context?.component_root.output.entity()).result;
													const right = resolve(context)
														.then(context => application__check_dependencies___step_nval)
														.then(context => context?.component_root.output.location())
														.then(context => context?.component_root.output.entity()).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											actual: () => resolve(self)
												.then(context => application__check_dependencies_nval).result!
											, 
											expected: () => resolve(self)
												.then(context => application__check_dependencies___step_nval).result!
											, 
											location_support: () => resolve(self)
												.then(context => evaluate__relative_object_location_validator(detach, 
													resolve(context)
														.then(context => application__check_dependencies_nval)
														.then(context => context?.component_root.output.location()).result!, {
													expected: () => resolve(self)
														.then(context => application__check_dependencies___step_nval)
														.then(context => context?.component_root.output.location()).result!

													})).result!

											})).result;
								}
								case 'entry step': {
									return resolve(context)
										.then(context => evaluate__compare_dependency_definitions(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(context => application__check_dependencies_nval).result;
													const right = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.expected()).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											expected: () => resolve(self)
												.then(context => application__check_dependencies___step_nval).result!

											})).result;
								}
								case 'inverse reference step': {
									return resolve(context)
										.then(context => evaluate__compare_dependency_definitions(detach, 
											resolve(context)
												.then(context => {
													const left = resolve(context)
														.then(context => application__check_dependencies_nval).result;
													const right = resolve(context)
														.then(() => self)
														.then(context => context?.component_root.input.expected()).result;
													return left?.is(right!) ? left : undefined
												}).result!, {
											expected: () => resolve(self)
												.then(context => application__check_dependencies___step_nval).result!

											})).result;
								}
								case 'recursion start step': {
									return resolve(context)
										.then(context => evaluate__compare_dependency_definitions(detach, 
											resolve(context)
												.then(context => application__check_dependencies_nval).result!, {
											expected: () => resolve(self)
												.then(context => application__check_dependencies___step_nval).result!

											})).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}
type Vattribute_class = { name: 'value', definition: (typeof Cattribute_class.Pvalue)}|{ name: 'operation', definition: application.Cnode__application}
export class Cattribute_class extends AlanStruct {
	public static Pvalue:Cattribute_class = new class PrimitiveInstance extends Cattribute_class {
		constructor () {
			super({name: 'value', definition: undefined as unknown as Cattribute_class})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vattribute_class) { super(); }
	public cast<K extends Vattribute_class['name']>(_variant:K):Extract<Vattribute_class, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vattribute_class['name']]:(($:Extract<Vattribute_class, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'attribute class'`; }
	public is(other:Cattribute_class):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cvalue = application.Cattribute_class;
export type Tancestor_variable_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__ancestor_variable_path];
};
export class Cancestor_variable_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cancestor_variable_path.Dhas_steps
	};
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.variable()).result!)
	};
	constructor(init:Tancestor_variable_path, public location:AlanNode, public input: {
		variable: () => application.Cvariable__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cancestor_variable_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__ancestor_variable_path = {
};
export class Cno__has_steps__ancestor_variable_path extends AlanNode {
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.variable()).result!)
	}
	constructor(init:Tno__has_steps__ancestor_variable_path, public parent:Cancestor_variable_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__ancestor_variable_path = {
	'tail':Tancestor_variable_path;
};
export class Cyes__has_steps__ancestor_variable_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cancestor_variable_path
	};
	public readonly output:{
		variable: () => application.Cvariable__application;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.variable()).result!)
	}
	public readonly inferences:{
		variable_assignment: () => application.Cvariable_assignment
	} = {
		variable_assignment: cache((detach:boolean) => {
			const application__ancestor_variable_path__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.variable())
				.then(context => context?.variant.name === 'assigned' ? context.variant.definition as application.Cvariable_assignment : undefined).result!;
		})

	}
	constructor(init:Tyes__has_steps__ancestor_variable_path, public parent:Cancestor_variable_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__ancestor_variable_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tancestor_node_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__ancestor_node_path];
};
export class Cancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cancestor_node_path.Dhas_steps
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.node_type()).result!)
	};
	constructor(init:Tancestor_node_path, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cancestor_node_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__ancestor_node_path = {
};
export class Cno__has_steps__ancestor_node_path extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node_type()).result!)
	}
	constructor(init:Tno__has_steps__ancestor_node_path, public parent:Cancestor_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__ancestor_node_path = {
	'tail':Tancestor_node_path;
};
export class Cyes__has_steps__ancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cancestor_node_path
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node_type()).result!)
	}
	public readonly inferences:{
		parent_node_type: () => application.Cnode__application
	} = {
		parent_node_type: cache((detach:boolean) => {
			const application__ancestor_node_path__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tyes__has_steps__ancestor_node_path, public parent:Cancestor_node_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__ancestor_node_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tancestor_named_object_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__ancestor_named_object_path];
};
export class Cancestor_named_object_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cancestor_named_object_path.Dhas_steps
	};
	public readonly output:{
		variable: () => application.Cnamed_object;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.variable()).result!)
	};
	constructor(init:Tancestor_named_object_path, public location:AlanNode, public input: {
		variable: () => application.Cnamed_object
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cancestor_named_object_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__ancestor_named_object_path = {
};
export class Cno__has_steps__ancestor_named_object_path extends AlanNode {
	public readonly output:{
		variable: () => application.Cnamed_object;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.variable()).result!)
	}
	constructor(init:Tno__has_steps__ancestor_named_object_path, public parent:Cancestor_named_object_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__ancestor_named_object_path = {
	'tail':Tancestor_named_object_path;
};
export class Cyes__has_steps__ancestor_named_object_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cancestor_named_object_path
	};
	public readonly output:{
		variable: () => application.Cnamed_object;
	} = {
		variable: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.variable()).result!)
	}
	public readonly inferences:{
		variable_assignment: () => application.Cnamed_object_assignment
	} = {
		variable_assignment: cache((detach:boolean) => {
			const application__ancestor_named_object_path__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.variable())
				.then(context => context?.variant.name === 'assigned' ? context.variant.definition as application.Cnamed_object_assignment : undefined).result!;
		})

	}
	constructor(init:Tyes__has_steps__ancestor_named_object_path, public parent:Cancestor_named_object_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__ancestor_named_object_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
function evaluate___inspect_dependency(detach:boolean, application___inspect_dependency_nval:application.Cdependency__application, ):application.Cdependency__application {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => application___inspect_dependency_nval)
		.then(context => {
			switch (context?.variant.name) {
				case 'self': {
					return resolve(context)
						.then(context => application___inspect_dependency_nval).result;
				}
				case 'step': {
					const application___inspect_dependency___step_nval = context.cast('step');
					return resolve(context)
						.then(context => evaluate___inspect_dependency(detach, 
							resolve(context)
								.then(context => application___inspect_dependency___step_nval)
								.then(context => context?.component_root.output.parent()).result!, )).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
			};
		}).result!
}

export type Tapplication = {
	'allow anonymous user':'no'|['no', {}]|'yes'|['yes', {}];
	'has dynamic users':'no'|['no', {}]|['yes', Tyes__has_dynamic_users];
	'imported interfaces':Record<string, Timported_interfaces>;
	'numerical types':Record<string, Tnumerical_types>;
	'root':Tnode__application;
};
export class Capplication extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public definitions:{
		entity: Centity__application;
		root_location: Croot_location;
	} = {
		entity: new Centity__application({name:'root', definition: this}),
		root_location: new Croot_location({name:'dataset', definition: this})
	}
	public readonly properties:{
		readonly allow_anonymous_user:Capplication.Dallow_anonymous_user,
		readonly has_dynamic_users:Capplication.Dhas_dynamic_users,
		readonly imported_interfaces:Capplication.Dimported_interfaces,
		readonly numerical_types:Capplication.Dnumerical_types,
		readonly root:Cnode__application
	};
	constructor(init:Tapplication, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			allow_anonymous_user: new Capplication.Dallow_anonymous_user(init['allow anonymous user'], $this),
			has_dynamic_users: new Capplication.Dhas_dynamic_users(init['has dynamic users'], $this),
			imported_interfaces: new Capplication.Dimported_interfaces(init['imported interfaces'], $this),
			numerical_types: new Capplication.Dnumerical_types(init['numerical types'], $this),
			root: new Capplication.Droot(init['root'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tno__allow_anonymous_user = {
};
export class Cno__allow_anonymous_user extends AlanNode {
	constructor(init:Tno__allow_anonymous_user, public parent:Capplication) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'allow anonymous user'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__allow_anonymous_user = {
};
export class Cyes__allow_anonymous_user extends AlanNode {
	constructor(init:Tyes__allow_anonymous_user, public parent:Capplication) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'allow anonymous user'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_dynamic_users = {
};
export class Cno__has_dynamic_users extends AlanNode {
	public readonly inferences:{
		allow_anonymous: () => application.Cyes__allow_anonymous_user
	} = {
		allow_anonymous: cache((detach:boolean) => {
			const application__has_dynamic_users__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.allow_anonymous_user.state.name === 'yes') {
						return resolve(context.properties.allow_anonymous_user.state.node as application.Cyes__allow_anonymous_user).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_dynamic_users, public parent:Capplication) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has dynamic users'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_dynamic_users = {
	'has external authentication':'no'|['no', {}]|['yes', Tyes__has_external_authentication];
	'has password authentication':'no'|['no', {}]|['yes', Tyes__has_password_authentication];
	'supports user sign-up':'no'|['no', {}]|['yes', Tyes__supports_user_sign_up];
	'users collection path':Tdescendant_base_property_path;
};
export class Cyes__has_dynamic_users extends AlanNode {
	public readonly properties:{
		readonly has_external_authentication:Cyes__has_dynamic_users.Dhas_external_authentication,
		readonly has_password_authentication:Cyes__has_dynamic_users.Dhas_password_authentication,
		readonly supports_user_sign_up:Cyes__has_dynamic_users.Dsupports_user_sign_up,
		readonly users_collection_path:Cdescendant_base_property_path & { readonly inferences: {
			collection: () => application.Ccollection__type__property;
		} }
	};
	constructor(init:Tyes__has_dynamic_users, public parent:Capplication) {
		super();
		const $this = this;
		this.properties = {
			has_external_authentication: new Cyes__has_dynamic_users.Dhas_external_authentication(init['has external authentication'], $this),
			has_password_authentication: new Cyes__has_dynamic_users.Dhas_password_authentication(init['has password authentication'], $this),
			supports_user_sign_up: new Cyes__has_dynamic_users.Dsupports_user_sign_up(init['supports user sign-up'], $this),
			users_collection_path: new Cyes__has_dynamic_users.Dusers_collection_path(init['users collection path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has dynamic users'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_external_authentication = {
};
export class Cno__has_external_authentication extends AlanNode {
	constructor(init:Tno__has_external_authentication, public parent:Cyes__has_dynamic_users) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has external authentication'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_external_authentication = {
	'authorities path':Tdescendant_base_property_path;
	'identities path':Tdescendant_base_property_path;
	'identity initializer':Tidentity_initializer;
	'user reference':string;
};
export class Cyes__has_external_authentication extends AlanNode {
	public readonly properties:{
		readonly authorities_path:Cdescendant_base_property_path & { readonly inferences: {
			base_value: () => application.Cbase__type__collection;
			collection: () => application.Ccollection__type__property;
		} },
		readonly identities_path:Cdescendant_base_property_path & { readonly inferences: {
			collection: () => application.Ccollection__type__property;
		} },
		readonly identity_initializer:Cidentity_initializer,
		readonly user_reference:Cyes__has_external_authentication.Duser_reference
	};
	constructor(init:Tyes__has_external_authentication, public parent:Cyes__has_dynamic_users) {
		super();
		const $this = this;
		this.properties = {
			authorities_path: new Cyes__has_external_authentication.Dauthorities_path(init['authorities path'], $this),
			identities_path: new Cyes__has_external_authentication.Didentities_path(init['identities path'], $this),
			identity_initializer: new Cyes__has_external_authentication.Didentity_initializer(init['identity initializer'], $this),
			user_reference: new Cyes__has_external_authentication.Duser_reference(init['user reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has external authentication'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_password_authentication = {
};
export class Cno__has_password_authentication extends AlanNode {
	constructor(init:Tno__has_password_authentication, public parent:Cyes__has_dynamic_users) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has password authentication'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_password_authentication = {
	'password initializer':Tpassword_initializer;
	'password status':Tpassword_status;
	'password text path':Tdescendant_base_property_path;
	'passwords collection path':Tdescendant_base_property_path;
};
export class Cyes__has_password_authentication extends AlanNode {
	public readonly properties:{
		readonly password_initializer:Cpassword_initializer,
		readonly password_status:Cpassword_status,
		readonly password_text_path:Cdescendant_base_property_path & { readonly inferences: {
			text: () => application.Ctext__type__property;
		} },
		readonly passwords_collection_path:Cdescendant_base_property_path & { readonly inferences: {
			collection: () => application.Ccollection__type__property;
			user_constraint: () => application.Ccollection__type__property;
			user_reference: () => application.Cyes__has_reference;
		} }
	};
	constructor(init:Tyes__has_password_authentication, public parent:Cyes__has_dynamic_users) {
		super();
		const $this = this;
		this.properties = {
			password_initializer: new Cyes__has_password_authentication.Dpassword_initializer(init['password initializer'], $this),
			password_status: new Cpassword_status(init['password status'], $this),
			password_text_path: new Cyes__has_password_authentication.Dpassword_text_path(init['password text path'], $this),
			passwords_collection_path: new Cyes__has_password_authentication.Dpasswords_collection_path(init['passwords collection path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has password authentication'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpassword_status = {
	'cases':Record<string, Tcases__password_status>;
	'state group path':Tdescendant_base_property_path;
};
export class Cpassword_status extends AlanNode {
	public readonly properties:{
		readonly cases:Cpassword_status.Dcases,
		readonly state_group_path:Cdescendant_base_property_path & { readonly inferences: {
			state_group: () => application.Cstate_group;
		} }
	};
	constructor(init:Tpassword_status, public parent:Cyes__has_password_authentication) {
		super();
		const $this = this;
		this.properties = {
			cases: new Cpassword_status.Dcases(init['cases'], $this),
			state_group_path: new Cpassword_status.Dstate_group_path(init['state group path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'password status'`; }
	public get entity() { return this.parent.entity; }
}
export class Kkeyset___keycases__password_status extends AlanObject {
	public is(other:Kkeyset___keycases__password_status):boolean {
		return this === other;
	}
	public get path() { return `static key`; }
};
const keyset___keycases__password_status: { [key: string]: Kkeyset___keycases__password_status } = {
	'active': new Kkeyset___keycases__password_status(),
	'reset': new Kkeyset___keycases__password_status()
};
export class Kcases__password_status extends Reference<application.Kkeyset___keycases__password_status, string> {
	constructor(key:string, $this:Ccases__password_status) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => keyset___keycases__password_status[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'::key`; }
}
export type Tcases__password_status = {
	'initializer':Tcommand_object_expression;
	'state':string;
};
export class Ccases__password_status extends AlanDictionaryEntry {
	public key:Kcases__password_status;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly initializer:Ccommand_object_expression,
		readonly state:Ccases__password_status.Dstate
	};
	constructor(key:string, init:Tcases__password_status, public parent:Cpassword_status) {
		super();
		const $this = this;
		this.key = new Kcases__password_status(key, $this);
		this.properties = {
			initializer: new Ccases__password_status.Dinitializer(init['initializer'], $this),
			state: new Ccases__password_status.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__supports_user_sign_up = {
};
export class Cno__supports_user_sign_up extends AlanNode {
	constructor(init:Tno__supports_user_sign_up, public parent:Cyes__has_dynamic_users) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'supports user sign-up'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__supports_user_sign_up = {
	'user initializer':Tuser_initializer;
};
export class Cyes__supports_user_sign_up extends AlanNode {
	public readonly properties:{
		readonly user_initializer:Cuser_initializer
	};
	constructor(init:Tyes__supports_user_sign_up, public parent:Cyes__has_dynamic_users) {
		super();
		const $this = this;
		this.properties = {
			user_initializer: new Cyes__supports_user_sign_up.Duser_initializer(init['user initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'supports user sign-up'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Timported_interfaces = {
	'connection status':'no'|['no', {}]|['yes', Tyes__connection_status];
	'instances':['many', Tmany__instances]|'one'|['one', {}];
	'parameters':Record<string, Tparameters>;
	'path':Tobject_path_tail;
};
export class Cimported_interfaces extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		external_source: Cexternal_source;
	} = {
		external_source: new Cexternal_source({name:'imported interface', definition: this})
	}
	public readonly properties:{
		readonly connection_status:Cimported_interfaces.Dconnection_status,
		readonly instances:Cimported_interfaces.Dinstances,
		readonly parameters:Cimported_interfaces.Dparameters,
		readonly path:Cobject_path_tail
	};
	constructor(key:string, init:Timported_interfaces, public parent:Capplication) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			connection_status: new Cimported_interfaces.Dconnection_status(init['connection status'], $this),
			instances: new Cimported_interfaces.Dinstances(init['instances'], $this),
			parameters: new Cimported_interfaces.Dparameters(init['parameters'], $this),
			path: new Cimported_interfaces.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'imported interfaces'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__connection_status = {
};
export class Cno__connection_status extends AlanNode {
	constructor(init:Tno__connection_status, public parent:Cimported_interfaces) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'connection status'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__connection_status = {
	'cases':Record<string, Tcases__yes__connection_status>;
	'state group path':Tdescendant_base_property_path;
};
export class Cyes__connection_status extends AlanNode {
	public readonly properties:{
		readonly cases:Cyes__connection_status.Dcases,
		readonly state_group_path:Cdescendant_base_property_path & { readonly inferences: {
			base_value: () => application.Cbase__type__state_group;
			state_group: () => application.Cstate_group;
		} }
	};
	constructor(init:Tyes__connection_status, public parent:Cimported_interfaces) {
		super();
		const $this = this;
		this.properties = {
			cases: new Cyes__connection_status.Dcases(init['cases'], $this),
			state_group_path: new Cyes__connection_status.Dstate_group_path(init['state group path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'connection status'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kkeyset___keycases__yes__connection_status extends AlanObject {
	public is(other:Kkeyset___keycases__yes__connection_status):boolean {
		return this === other;
	}
	public get path() { return `static key`; }
};
const keyset___keycases__yes__connection_status: { [key: string]: Kkeyset___keycases__yes__connection_status } = {
	'connected': new Kkeyset___keycases__yes__connection_status(),
	'connecting': new Kkeyset___keycases__yes__connection_status(),
	'disconnected': new Kkeyset___keycases__yes__connection_status()
};
export class Kcases__yes__connection_status extends Reference<application.Kkeyset___keycases__yes__connection_status, string> {
	constructor(key:string, $this:Ccases__yes__connection_status) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => keyset___keycases__yes__connection_status[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'::key`; }
}
export type Tcases__yes__connection_status = {
	'dataset status':'no'|['no', {}]|['yes', Tyes__dataset_status];
	'initializer':Tcommand_object_expression;
	'state':string;
};
export class Ccases__yes__connection_status extends AlanDictionaryEntry {
	public key:Kcases__yes__connection_status;
	public get key_value() { return this.key.entry; }
	public definitions:{
		command_initialization_operation: Ccommand_initialization_operation;
	} = {
		command_initialization_operation: new Ccommand_initialization_operation({name:'interface connection status', definition: this})
	}
	public readonly properties:{
		readonly dataset_status:Ccases__yes__connection_status.Ddataset_status,
		readonly initializer:Ccommand_object_expression,
		readonly state:Ccases__yes__connection_status.Dstate
	};
	constructor(key:string, init:Tcases__yes__connection_status, public parent:Cyes__connection_status) {
		super();
		const $this = this;
		this.key = new Kcases__yes__connection_status(key, $this);
		this.properties = {
			dataset_status: new Ccases__yes__connection_status.Ddataset_status(init['dataset status'], $this),
			initializer: new Ccases__yes__connection_status.Dinitializer(init['initializer'], $this),
			state: new Ccases__yes__connection_status.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__dataset_status = {
};
export class Cno__dataset_status extends AlanNode {
	constructor(init:Tno__dataset_status, public parent:Ccases__yes__connection_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'dataset status'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__dataset_status = {
	'cases':Record<string, Tcases__yes__dataset_status>;
	'state group path':Tdescendant_base_property_path;
};
export class Cyes__dataset_status extends AlanNode {
	public readonly properties:{
		readonly cases:Cyes__dataset_status.Dcases,
		readonly state_group_path:Cdescendant_base_property_path & { readonly inferences: {
			base_value: () => application.Cbase__type__state_group;
			state_group: () => application.Cstate_group;
		} }
	};
	public readonly inferences:{
		connected_state: () => application.Cconnection_status
	} = {
		connected_state: cache((detach:boolean) => {
			const application__imported_interfaces__connection_status__yes__cases__dataset_status__yes_nval = this.parent;
			return resolve(this.parent)
				.then(switch_context => {
					return resolve(switch_context)
					.then(() => this.parent)
					.then(context => context?.key?.ref).then(key_value => {
						if (key_value === keyset___keycases__yes__connection_status[`connected`]) {
							return resolve(switch_context)
							.then(() => this.parent)
							.then(() => application.Cconnection_status.Pconnected).result
						} else if (key_value === keyset___keycases__yes__connection_status[`connecting`]) {
							return resolve(switch_context)
							.then(() => this.parent)
							.then(() => application.Cconnection_status.Pconnecting)
							.then(context => context?.variant.name === 'connected' ? context.variant.definition as application.Cconnected : undefined).result
						} else if (key_value === keyset___keycases__yes__connection_status[`disconnected`]) {
							return resolve(switch_context)
							.then(() => this.parent)
							.then(() => application.Cconnection_status.Pdisconnected)
							.then(context => context?.variant.name === 'connected' ? context.variant.definition as application.Cconnected : undefined).result
						} else {
						throw new Error(`Unexpected key in dictionary with static keys.`);
						}
					}).result
				}).result!;
		})

	}
	constructor(init:Tyes__dataset_status, public parent:Ccases__yes__connection_status) {
		super();
		const $this = this;
		this.properties = {
			cases: new Cyes__dataset_status.Dcases(init['cases'], $this),
			state_group_path: new Cyes__dataset_status.Dstate_group_path(init['state group path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'dataset status'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kkeyset___keycases__yes__dataset_status extends AlanObject {
	public is(other:Kkeyset___keycases__yes__dataset_status):boolean {
		return this === other;
	}
	public get path() { return `static key`; }
};
const keyset___keycases__yes__dataset_status: { [key: string]: Kkeyset___keycases__yes__dataset_status } = {
	'available': new Kkeyset___keycases__yes__dataset_status(),
	'unavailable': new Kkeyset___keycases__yes__dataset_status()
};
export class Kcases__yes__dataset_status extends Reference<application.Kkeyset___keycases__yes__dataset_status, string> {
	constructor(key:string, $this:Ccases__yes__dataset_status) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => keyset___keycases__yes__dataset_status[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'::key`; }
}
export type Tcases__yes__dataset_status = {
	'initializer':Tcommand_object_expression;
	'state':string;
};
export class Ccases__yes__dataset_status extends AlanDictionaryEntry {
	public key:Kcases__yes__dataset_status;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly initializer:Ccommand_object_expression,
		readonly state:Ccases__yes__dataset_status.Dstate
	};
	constructor(key:string, init:Tcases__yes__dataset_status, public parent:Cyes__dataset_status) {
		super();
		const $this = this;
		this.key = new Kcases__yes__dataset_status(key, $this);
		this.properties = {
			initializer: new Ccases__yes__dataset_status.Dinitializer(init['initializer'], $this),
			state: new Ccases__yes__dataset_status.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tmany__instances = {
	'initializer':Tinterface_instance_initializer;
};
export class Cmany__instances extends AlanNode {
	public readonly properties:{
		readonly initializer:Cinterface_instance_initializer
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		collection: () => application.Ccollection__type__property,
		collection_cardinality_constraint: () => application.Cnone__constraint,
		value_source_is_interface: () => application.Cimported_interfaces
	} = {
		collection: cache((detach:boolean) => {
			const application__imported_interfaces__instances__many_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.value())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined)
				.then(context => context?.variant.name === 'collection attribute' ? context.variant.definition as application.Ccollection_attribute : undefined)
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})
		,
		collection_cardinality_constraint: cache((detach:boolean) => {
			const application__imported_interfaces__instances__many_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => context?.properties.cardinality_constraint)
				.then(context => {
					if (context?.properties.constraint.state.name === 'none') {
						return resolve(context.properties.constraint.state.node as application.Cnone__constraint).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		value_source_is_interface: cache((detach:boolean) => {
			const application__imported_interfaces__instances__many_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection())
				.then(context => context?.properties.node)
				.then(context => context?.component_root.output.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'imported interface' ? context.variant.definition as application.Cimported_interfaces : undefined).result!;
		})

	}
	constructor(init:Tmany__instances, public parent:Cimported_interfaces) {
		super();
		const $this = this;
		this.properties = {
			initializer: new Cmany__instances.Dinitializer(init['initializer'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'instances'?'many'`; }
	public get entity() { return this.parent.entity; }
}
export type Tone__instances = {
};
export class Cone__instances extends AlanNode {
	public readonly output:{
		node_type: () => application.Cnode__application;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.root_node())
			.then(context => context?.component_root.output.node()).result!)
	}
	public readonly inferences:{
		root_node: () => application.Cnode_navigation_context,
		value_source_is_interface: () => application.Cimported_interfaces
	} = {
		root_node: cache((detach:boolean) => {
			const application__imported_interfaces__instances__one_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.context())
				.then(context => context?.component_root.output.node_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
		})
		,
		value_source_is_interface: cache((detach:boolean) => {
			const application__imported_interfaces__instances__one_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.root_node())
				.then(context => context?.component_root.output.node())
				.then(context => context?.component_root.output.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
				.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
				.then(context => context?.variant.name === 'imported interface' ? context.variant.definition as application.Cimported_interfaces : undefined).result!;
		})

	}
	constructor(init:Tone__instances, public parent:Cimported_interfaces) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'instances'?'one'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameters = {
	'value':string;
};
export class Cparameters extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly value:Cparameters.Dvalue
	};
	constructor(key:string, init:Tparameters, public parent:Cimported_interfaces) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			value: new Cparameters.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'parameters'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnumerical_types = {
	'singular conversions':Record<string, Tsingular_conversions>;
	'type':['quantity', Tquantity]|['scale', Tscale];
	'ui':Tui_numerical_type;
};
export class Cnumerical_types extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly singular_conversions:Cnumerical_types.Dsingular_conversions,
		readonly type:Cnumerical_types.Dtype,
		readonly ui:Cui_numerical_type
	};
	constructor(key:string, init:Tnumerical_types, public parent:Capplication) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			singular_conversions: new Cnumerical_types.Dsingular_conversions(init['singular conversions'], $this),
			type: new Cnumerical_types.Dtype(init['type'], $this),
			ui: new Cnumerical_types.Dui(init['ui'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'numerical types'['${this.key}']`; }
	public get entity() { return this; }
}
export class Ksingular_conversions extends Reference<application.Cnumerical_types, string> {
	constructor(key:string, $this:Csingular_conversions) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => {
				const entry = context?.properties.numerical_types.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'singular conversions'::key`; }
}
export type Tsingular_conversions = {
	'type':['base', Tbase__type__singular_conversions]|['factor', Tfactor];
};
export class Csingular_conversions extends AlanDictionaryEntry {
	public key:Ksingular_conversions;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Csingular_conversions.Dtype
	};
	constructor(key:string, init:Tsingular_conversions, public parent:Cnumerical_types) {
		super();
		const $this = this;
		this.key = new Ksingular_conversions(key, $this);
		this.properties = {
			type: new Csingular_conversions.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'singular conversions'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tbase__type__singular_conversions = {
	'base':number;
	'exponent':number;
	'offset':number;
	'unit conversion':'no'|['no', {}]|['yes', Tyes__unit_conversion];
};
export class Cbase__type__singular_conversions extends AlanNode {
	public readonly properties:{
		readonly base:Cbase__type__singular_conversions.Dbase,
		readonly exponent:Cbase__type__singular_conversions.Dexponent,
		readonly offset:Cbase__type__singular_conversions.Doffset,
		readonly unit_conversion:Cbase__type__singular_conversions.Dunit_conversion
	};
	public readonly inferences:{
		from: () => application.Cscale,
		to: () => application.Cscale
	} = {
		from: cache((detach:boolean) => {
			const application__numerical_types__singular_conversions__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'scale') {
						return resolve(context.properties.type.state.node as application.Cscale).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		to: cache((detach:boolean) => {
			const application__numerical_types__singular_conversions__type__base_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.type.state.name === 'scale') {
						return resolve(context.properties.type.state.node as application.Cscale).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tbase__type__singular_conversions, public parent:Csingular_conversions) {
		super();
		const $this = this;
		this.properties = {
			base: new Cbase__type__singular_conversions.Dbase(init['base'], $this),
			exponent: new Cbase__type__singular_conversions.Dexponent(init['exponent'], $this),
			offset: new Cbase__type__singular_conversions.Doffset(init['offset'], $this),
			unit_conversion: new Cbase__type__singular_conversions.Dunit_conversion(init['unit conversion'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'base'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__unit_conversion = {
};
export class Cno__unit_conversion extends AlanNode {
	public readonly inferences:{
		numerical_type: () => application.Cnumerical_types
	} = {
		numerical_type: cache((detach:boolean) => {
			const application__numerical_types__singular_conversions__type__base__unit_conversion__no_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.inferences.from())
						.then(context => context?.properties.range_type?.ref).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.inferences.to())
						.then(context => context?.properties.range_type?.ref).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tno__unit_conversion, public parent:Cbase__type__singular_conversions) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit conversion'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__unit_conversion = {
	'conversion rule':string;
};
export class Cyes__unit_conversion extends AlanNode {
	public readonly properties:{
		readonly conversion_rule:Cyes__unit_conversion.Dconversion_rule
	};
	constructor(init:Tyes__unit_conversion, public parent:Cbase__type__singular_conversions) {
		super();
		const $this = this;
		this.properties = {
			conversion_rule: new Cyes__unit_conversion.Dconversion_rule(init['conversion rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit conversion'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfactor = {
	'base':number;
	'exponent':number;
	'factor':number;
	'invert':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cfactor extends AlanNode {
	public readonly properties:{
		readonly base:Cfactor.Dbase,
		readonly exponent:Cfactor.Dexponent,
		readonly factor:Cfactor.Dfactor,
		readonly invert:Cfactor.Dinvert
	};
	public readonly inferences:{
		from: () => application.Cquantity,
		to: () => application.Cquantity
	} = {
		from: cache((detach:boolean) => {
			const application__numerical_types__singular_conversions__type__factor_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		to: cache((detach:boolean) => {
			const application__numerical_types__singular_conversions__type__factor_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.type.state.name === 'quantity') {
						return resolve(context.properties.type.state.node as application.Cquantity).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfactor, public parent:Csingular_conversions) {
		super();
		const $this = this;
		this.properties = {
			base: new Cfactor.Dbase(init['base'], $this),
			exponent: new Cfactor.Dexponent(init['exponent'], $this),
			factor: new Cfactor.Dfactor(init['factor'], $this),
			invert: new Cfactor.Dinvert(init['invert'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'factor'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__invert = {
};
export class Cno__invert extends AlanNode {
	constructor(init:Tno__invert, public parent:Cfactor) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'invert'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__invert = {
};
export class Cyes__invert extends AlanNode {
	constructor(init:Tyes__invert, public parent:Cfactor) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'invert'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquantity = {
	'division conversions':Record<string, Tdivision_conversions>;
	'product conversions':Record<string, Tproduct_conversions>;
};
export class Cquantity extends AlanNode {
	public readonly properties:{
		readonly division_conversions:Cquantity.Ddivision_conversions,
		readonly product_conversions:Cquantity.Dproduct_conversions
	};
	public readonly output:{
		quantity_type: () => application.Cnumerical_types;
	} = {
		quantity_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent).result!)
	}
	constructor(init:Tquantity, public parent:Cnumerical_types) {
		super();
		const $this = this;
		this.properties = {
			division_conversions: new Cquantity.Ddivision_conversions(init['division conversions'], $this),
			product_conversions: new Cquantity.Dproduct_conversions(init['product conversions'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'quantity'`; }
	public get entity() { return this.parent.entity; }
}
export class Kdivision_conversions extends Reference<application.Cnumerical_types, string> {
	public readonly inferences:{
		division_conversions: () => application.Cquantity
	}
	constructor(key:string, $this:Cdivision_conversions) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => {
				const entry = context?.properties.numerical_types.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			division_conversions: cache((detach:boolean) => {
				const application__numerical_types__type__quantity__division_conversions_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => application__numerical_types__type__quantity__division_conversions_key_nval)
					.then(context => {
						if (context?.properties.type.state.name === 'quantity') {
							return resolve(context.properties.type.state.node as application.Cquantity).result;
						} else {
							return undefined;
						}
					}).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'division conversions'::key`; }
}
export type Tdivision_conversions = {
	'denominator':string;
};
export class Cdivision_conversions extends AlanDictionaryEntry {
	public key:Kdivision_conversions;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly denominator:Cdivision_conversions.Ddenominator
	};
	constructor(key:string, init:Tdivision_conversions, public parent:Cquantity) {
		super();
		const $this = this;
		this.key = new Kdivision_conversions(key, $this);
		this.properties = {
			denominator: new Cdivision_conversions.Ddenominator(init['denominator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'division conversions'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export class Kproduct_conversions extends Reference<application.Cnumerical_types, string> {
	public readonly inferences:{
		product_conversions: () => application.Cquantity
	}
	constructor(key:string, $this:Cproduct_conversions) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => {
				const entry = context?.properties.numerical_types.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			product_conversions: cache((detach:boolean) => {
				const application__numerical_types__type__quantity__product_conversions_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => application__numerical_types__type__quantity__product_conversions_key_nval)
					.then(context => {
						if (context?.properties.type.state.name === 'quantity') {
							return resolve(context.properties.type.state.node as application.Cquantity).result;
						} else {
							return undefined;
						}
					}).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'product conversions'::key`; }
}
export type Tproduct_conversions = {
	'right':string;
};
export class Cproduct_conversions extends AlanDictionaryEntry {
	public key:Kproduct_conversions;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly right:Cproduct_conversions.Dright
	};
	constructor(key:string, init:Tproduct_conversions, public parent:Cquantity) {
		super();
		const $this = this;
		this.key = new Kproduct_conversions(key, $this);
		this.properties = {
			right: new Cproduct_conversions.Dright(init['right'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'product conversions'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tscale = {
	'range type':string;
	'timer resolution':'none'|['none', {}]|'seconds'|['seconds', {}];
};
export class Cscale extends AlanNode {
	public readonly properties:{
		readonly range_type:Cscale.Drange_type,
		readonly timer_resolution:Cscale.Dtimer_resolution
	};
	public readonly output:{
		quantity_type: () => application.Cnumerical_types;
	} = {
		quantity_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.range_type?.ref).result!)
	}
	constructor(init:Tscale, public parent:Cnumerical_types) {
		super();
		const $this = this;
		this.properties = {
			range_type: new Cscale.Drange_type(init['range type'], $this),
			timer_resolution: new Cscale.Dtimer_resolution(init['timer resolution'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'scale'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__timer_resolution = {
};
export class Cnone__timer_resolution extends AlanNode {
	constructor(init:Tnone__timer_resolution, public parent:Cscale) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'timer resolution'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tseconds__timer_resolution = {
};
export class Cseconds__timer_resolution extends AlanNode {
	constructor(init:Tseconds__timer_resolution, public parent:Cscale) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'timer resolution'?'seconds'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cancestor_named_object_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__ancestor_named_object_path, init:Tno__has_steps__ancestor_named_object_path}|
		{ name: 'yes', node:Cyes__has_steps__ancestor_named_object_path, init:Tyes__has_steps__ancestor_named_object_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__ancestor_named_object_path, parent:Cancestor_named_object_path) => new Cno__has_steps__ancestor_named_object_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__ancestor_named_object_path, parent:Cancestor_named_object_path) => new Cyes__has_steps__ancestor_named_object_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__ancestor_named_object_path;
				case 'yes': return finalize_yes__has_steps__ancestor_named_object_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tancestor_named_object_path['has steps'], parent:Cancestor_named_object_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__ancestor_named_object_path {
	export class Dtail extends Cancestor_named_object_path {
		constructor(data:Tyes__has_steps__ancestor_named_object_path['tail'], parent:Cyes__has_steps__ancestor_named_object_path) {
			super(data, parent, {
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.variable_assignment())
					.then(context => context?.component_root.output.parent_variable()).result!)
			}, 'tail')
		}
	}
}
export namespace Cancestor_node_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__ancestor_node_path, init:Tno__has_steps__ancestor_node_path}|
		{ name: 'yes', node:Cyes__has_steps__ancestor_node_path, init:Tyes__has_steps__ancestor_node_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__ancestor_node_path, parent:Cancestor_node_path) => new Cno__has_steps__ancestor_node_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__ancestor_node_path, parent:Cancestor_node_path) => new Cyes__has_steps__ancestor_node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__ancestor_node_path;
				case 'yes': return finalize_yes__has_steps__ancestor_node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tancestor_node_path['has steps'], parent:Cancestor_node_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__ancestor_node_path {
	export class Dtail extends Cancestor_node_path {
		constructor(data:Tyes__has_steps__ancestor_node_path['tail'], parent:Cyes__has_steps__ancestor_node_path) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.parent_node_type()).result!)
			}, 'tail')
		}
	}
}
export namespace Cancestor_variable_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__ancestor_variable_path, init:Tno__has_steps__ancestor_variable_path}|
		{ name: 'yes', node:Cyes__has_steps__ancestor_variable_path, init:Tyes__has_steps__ancestor_variable_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__ancestor_variable_path, parent:Cancestor_variable_path) => new Cno__has_steps__ancestor_variable_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__ancestor_variable_path, parent:Cancestor_variable_path) => new Cyes__has_steps__ancestor_variable_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__ancestor_variable_path;
				case 'yes': return finalize_yes__has_steps__ancestor_variable_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tancestor_variable_path['has steps'], parent:Cancestor_variable_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__ancestor_variable_path {
	export class Dtail extends Cancestor_variable_path {
		constructor(data:Tyes__has_steps__ancestor_variable_path['tail'], parent:Cyes__has_steps__ancestor_variable_path) {
			super(data, parent, {
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.variable_assignment())
					.then(context => context?.component_root.output.parent_variable()).result!)
			}, 'tail')
		}
	}
}
export namespace Ccommand__application {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tcommand__application['expression'], parent:Ccommand__application) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.command_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dhas_next_command extends StateGroup<
		{ name: 'no', node:Cno__has_next_command, init:Tno__has_next_command}|
		{ name: 'yes', node:Cyes__has_next_command, init:Tyes__has_next_command}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next_command, parent:Ccommand__application) => new Cno__has_next_command(init, parent);
				case 'yes': return (init:Tyes__has_next_command, parent:Ccommand__application) => new Cyes__has_next_command(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next_command;
				case 'yes': return finalize_yes__has_next_command;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand__application['has next command'], parent:Ccommand__application) {
			super(data, parent, 'has next command');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next command'`; }
	}
}
export namespace Cyes__has_next_command {
	export class Dcommand extends Ccommand__application {
		constructor(data:Tyes__has_next_command['command'], parent:Cyes__has_next_command) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'command')
		}
	}
}
export namespace Ccommand_expression {
	export class Doperation extends StateGroup<
		{ name: 'entry', node:Centry__operation__command_expression, init:Tentry__operation__command_expression}|
		{ name: 'execute operation', node:Cexecute_operation__operation__command_expression, init:Texecute_operation__operation__command_expression}|
		{ name: 'ignore', node:Cignore__operation__command_expression, init:Tignore__operation__command_expression}|
		{ name: 'node switch', node:Cnode_switch__operation__command_expression, init:Tnode_switch__operation__command_expression}|
		{ name: 'produce value', node:Cproduce_value__operation__command_expression, init:Tproduce_value__operation__command_expression}|
		{ name: 'state switch', node:Cstate_switch__operation__command_expression, init:Tstate_switch__operation__command_expression}|
		{ name: 'update properties', node:Cupdate_properties__operation__command_expression, init:Tupdate_properties__operation__command_expression}|
		{ name: 'walk', node:Cwalk__operation__command_expression, init:Twalk__operation__command_expression}> {
		protected initializer(state: 'entry'|'execute operation'|'ignore'|'node switch'|'produce value'|'state switch'|'update properties'|'walk') {
			switch (state) {
				case 'entry': return (init:Tentry__operation__command_expression, parent:Ccommand_expression) => new Centry__operation__command_expression(init, parent);
				case 'execute operation': return (init:Texecute_operation__operation__command_expression, parent:Ccommand_expression) => new Cexecute_operation__operation__command_expression(init, parent);
				case 'ignore': return (init:Tignore__operation__command_expression, parent:Ccommand_expression) => new Cignore__operation__command_expression(init, parent);
				case 'node switch': return (init:Tnode_switch__operation__command_expression, parent:Ccommand_expression) => new Cnode_switch__operation__command_expression(init, parent);
				case 'produce value': return (init:Tproduce_value__operation__command_expression, parent:Ccommand_expression) => new Cproduce_value__operation__command_expression(init, parent);
				case 'state switch': return (init:Tstate_switch__operation__command_expression, parent:Ccommand_expression) => new Cstate_switch__operation__command_expression(init, parent);
				case 'update properties': return (init:Tupdate_properties__operation__command_expression, parent:Ccommand_expression) => new Cupdate_properties__operation__command_expression(init, parent);
				case 'walk': return (init:Twalk__operation__command_expression, parent:Ccommand_expression) => new Cwalk__operation__command_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry'|'execute operation'|'ignore'|'node switch'|'produce value'|'state switch'|'update properties'|'walk') {
			switch (state) {
				case 'entry': return finalize_entry__operation__command_expression;
				case 'execute operation': return finalize_execute_operation__operation__command_expression;
				case 'ignore': return finalize_ignore__operation__command_expression;
				case 'node switch': return finalize_node_switch__operation__command_expression;
				case 'produce value': return finalize_produce_value__operation__command_expression;
				case 'state switch': return finalize_state_switch__operation__command_expression;
				case 'update properties': return finalize_update_properties__operation__command_expression;
				case 'walk': return finalize_walk__operation__command_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand_expression['operation'], parent:Ccommand_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Centry__operation__command_expression {
	export class Dtype extends StateGroup<
		{ name: 'create', node:Ccreate__type__entry__operation__command_expression, init:Tcreate__type__entry__operation__command_expression}|
		{ name: 'delete', node:Cdelete__type__entry__operation__command_expression, init:Tdelete__type__entry__operation__command_expression}> {
		protected initializer(state: 'create'|'delete') {
			switch (state) {
				case 'create': return (init:Tcreate__type__entry__operation__command_expression, parent:Centry__operation__command_expression) => new Ccreate__type__entry__operation__command_expression(init, parent);
				case 'delete': return (init:Tdelete__type__entry__operation__command_expression, parent:Centry__operation__command_expression) => new Cdelete__type__entry__operation__command_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'create'|'delete') {
			switch (state) {
				case 'create': return finalize_create__type__entry__operation__command_expression;
				case 'delete': return finalize_delete__type__entry__operation__command_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry__operation__command_expression['type'], parent:Centry__operation__command_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccreate__type__entry__operation__command_expression {
	export class Dbehaviour extends Ccommand_object_initialization_behaviour {
		constructor(data:Tcreate__type__entry__operation__command_expression['behaviour'], parent:Ccreate__type__entry__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.current_value()).result!)
			}, 'behaviour')
		}
	}
	export class Dexpression extends Ccommand_object_expression {
		public readonly inferences:{
			key_initialization: () => application.Cproperties__command_object_expression
		}
		constructor(data:Tcreate__type__entry__operation__command_expression['expression'], parent:Ccreate__type__entry__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.current_value()).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.initialization_context())
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.target_collection())
					.then(context => context?.component_root.output.entry_object()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
			this.inferences = {
				key_initialization: cache((detach:boolean) => {
					const application__command_expression__operation__entry__type__create__expression_nval = this;
					return resolve(this)
						.then(context => {
							const col_object = resolve(context)
								.then(context => application__command_expression__operation__entry__type__create__expression_nval).result;
							const key_object = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.key_member()).result;
							return resolve(col_object.properties.properties.get(key_object?.entity.key)).result;
						}).result!;
				})

			}
		}
	}
}
export namespace Cdelete__type__entry__operation__command_expression {
	export class Dpath extends Csingular_node_path {
		public readonly inferences:{
			collection_node_type: () => application.Cnode__application
		}
		constructor(data:Tdelete__type__entry__operation__command_expression['path'], parent:Cdelete__type__entry__operation__command_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
			this.inferences = {
				collection_node_type: cache((detach:boolean) => {
					const application__command_expression__operation__entry__type__delete__path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__command_expression__operation__entry__type__delete__path_nval)
								.then(context => context?.component_root.output.node_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.collection_property())
								.then(context => context?.properties.node).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cexecute_operation__operation__command_expression {
	export class Dcommand extends Reference<application.Ccommand__type,string> {

		constructor(data:string, $this:Cexecute_operation__operation__command_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'command') {
							return context.properties.type.state.node as application.Ccommand__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'command'`; }
	}
	export class Dexpression extends Ccommand_object_expression {
		constructor(data:Texecute_operation__operation__command_expression['expression'], parent:Cexecute_operation__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccommand_initialization_operation.Pobject)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.command?.ref)
					.then(context => context?.properties.parameters).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dpath extends Csingular_node_path {
		constructor(data:Texecute_operation__operation__command_expression['path'], parent:Cexecute_operation__operation__command_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Cnode_switch__operation__command_expression {
	export class Dexpression extends Cnode_expression {
		constructor(data:Tnode_switch__operation__command_expression['expression'], parent:Cnode_switch__operation__command_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Ccases__node_switch__operation__command_expression {
}
export namespace Cnode_case__cases__node_switch__operation__command_expression {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tnode_case__cases__node_switch__operation__command_expression['expression'], parent:Cnode_case__cases__node_switch__operation__command_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Tnode_case__cases__node_switch__operation__command_expression['variable assignment'], parent:Cnode_case__cases__node_switch__operation__command_expression) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cnone_case__cases__node_switch__operation__command_expression {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tnone_case__cases__node_switch__operation__command_expression['expression'], parent:Cnone_case__cases__node_switch__operation__command_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cproduce_value__operation__command_expression {
	export class Dvalue extends StateGroup<
		{ name: 'current value', node:Ccurrent_value__value, init:Tcurrent_value__value}|
		{ name: 'empty collection', node:Cempty_collection, init:Tempty_collection}|
		{ name: 'object', node:Cobject__value__produce_value__operation__command_expression, init:Tobject__value__produce_value__operation__command_expression}|
		{ name: 'scalar', node:Cscalar__value__produce_value__operation__command_expression, init:Tscalar__value__produce_value__operation__command_expression}|
		{ name: 'state', node:Cstate__value__produce_value__operation__command_expression, init:Tstate__value__produce_value__operation__command_expression}> {
		protected initializer(state: 'current value'|'empty collection'|'object'|'scalar'|'state') {
			switch (state) {
				case 'current value': return (init:Tcurrent_value__value, parent:Cproduce_value__operation__command_expression) => new Ccurrent_value__value(init, parent);
				case 'empty collection': return (init:Tempty_collection, parent:Cproduce_value__operation__command_expression) => new Cempty_collection(init, parent);
				case 'object': return (init:Tobject__value__produce_value__operation__command_expression, parent:Cproduce_value__operation__command_expression) => new Cobject__value__produce_value__operation__command_expression(init, parent);
				case 'scalar': return (init:Tscalar__value__produce_value__operation__command_expression, parent:Cproduce_value__operation__command_expression) => new Cscalar__value__produce_value__operation__command_expression(init, parent);
				case 'state': return (init:Tstate__value__produce_value__operation__command_expression, parent:Cproduce_value__operation__command_expression) => new Cstate__value__produce_value__operation__command_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'current value'|'empty collection'|'object'|'scalar'|'state') {
			switch (state) {
				case 'current value': return finalize_current_value__value;
				case 'empty collection': return finalize_empty_collection;
				case 'object': return finalize_object__value__produce_value__operation__command_expression;
				case 'scalar': return finalize_scalar__value__produce_value__operation__command_expression;
				case 'state': return finalize_state__value__produce_value__operation__command_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproduce_value__operation__command_expression['value'], parent:Cproduce_value__operation__command_expression) {
			super(data, parent, 'value');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Ccurrent_value__value {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tcurrent_value__value['expression'], parent:Ccurrent_value__value) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.property_target())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_target({name: 'properties', definition: conv_context},{
						current_value: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Ccurrent_value__application.Pnone).result!
						, 
						operation: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.target())
							.then(context => context?.component_root.output.operation()).result!

						});
					}).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cobject__value__produce_value__operation__command_expression {
	export class Dexpression extends Ccommand_object_expression {
		constructor(data:Tobject__value__produce_value__operation__command_expression['expression'], parent:Cobject__value__produce_value__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.current_value()).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.operation()).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.target_object()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cscalar__value__produce_value__operation__command_expression {
	export class Dexpression extends Cscalar_expression {
		constructor(data:Tscalar__value__produce_value__operation__command_expression['expression'], parent:Cscalar__value__produce_value__operation__command_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.target_scalar()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cstate__value__produce_value__operation__command_expression {
	export class Dbehaviour extends Ccommand_object_initialization_behaviour {
		constructor(data:Tstate__value__produce_value__operation__command_expression['behaviour'], parent:Cstate__value__produce_value__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.current_value()).result!)
			}, 'behaviour')
		}
	}
	export class Dexpression extends Ccommand_object_expression {
		constructor(data:Tstate__value__produce_value__operation__command_expression['expression'], parent:Cstate__value__produce_value__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.current_value()).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.initialization_context())
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.inferences.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dstate extends Reference<application.Ccommand_state,string> {
		public readonly inferences:{
			target: () => application.Cnode__application
		}

		constructor(data:string, $this:Cstate__value__produce_value__operation__command_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.target_state_group())
				.then(context => {
					switch (context?.variant.name) {
						case 'stategroup property': {
							const application__command_expression__operation__produce_value__value__state__state___stategroup_property_nval = context.cast('stategroup property');
							return resolve(context)
								.then(context => application__command_expression__operation__produce_value__value__state__state___stategroup_property_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Ccommand_state({name: 'state', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
			this.inferences = {
				target: cache((detach:boolean) => {
					const application__command_expression__operation__produce_value__value__state__state_nval = $this.properties.state.ref;
					return resolve($this.properties.state.ref)
						.then(context => application__command_expression__operation__produce_value__value__state__state_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'state': {
									const application__command_expression__operation__produce_value__value__state__state_inf___target___state_nval = context.cast('state');
									return resolve(context)
										.then(context => application__command_expression__operation__produce_value__value__state__state_inf___target___state_nval)
										.then(context => context?.properties.node).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstate_switch__operation__command_expression {
	export class Dstates extends AlanDictionary<{ node:Cstates__state_switch__operation__command_expression, init:Tstates__state_switch__operation__command_expression},Cstate_switch__operation__command_expression> {
		protected initialize(parent:Cstate_switch__operation__command_expression, key:string, entry_init:Tstates__state_switch__operation__command_expression) { return new Cstates__state_switch__operation__command_expression(key, entry_init, parent); }
		protected finalize = finalize_states__state_switch__operation__command_expression
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__command_expression__operation__state_switch__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.path)
			.then(context => context?.properties.stategroup?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_switch__operation__command_expression['states'], parent:Cstate_switch__operation__command_expression) {
			super(data, parent);
		}
	}
}
export namespace Cpath__state_switch__operation__command_expression {
	export class Dpath extends Csingular_node_path {
		constructor(data:Tpath__state_switch__operation__command_expression['path'], parent:Cpath__state_switch__operation__command_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dstategroup extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cpath__state_switch__operation__command_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'stategroup'`; }
	}
}
export namespace Cstates__state_switch__operation__command_expression {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tstates__state_switch__operation__command_expression['expression'], parent:Cstates__state_switch__operation__command_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Tstates__state_switch__operation__command_expression['variable assignment'], parent:Cstates__state_switch__operation__command_expression) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cupdate_properties__operation__command_expression {
	export class Dpath extends Csingular_node_path {
		public readonly inferences:{
			user_value: () => application.Cuser__external_source
		}
		constructor(data:Tupdate_properties__operation__command_expression['path'], parent:Cupdate_properties__operation__command_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
			this.inferences = {
				user_value: cache((detach:boolean) => {
					const application__command_expression__operation__update_properties__path_nval = this;
					return resolve(this)
						.then(context => application__command_expression__operation__update_properties__path_nval)
						.then(context => context?.component_root.output.node_type())
						.then(context => context?.component_root.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
						.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
						.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
				})

			}
		}
	}
	export class Dtarget extends StateGroup<
		{ name: 'node', node:Cnode__target__update_properties__operation__command_expression, init:Tnode__target__update_properties__operation__command_expression}|
		{ name: 'property', node:Cproperty__target__update_properties__operation__command_expression, init:Tproperty__target__update_properties__operation__command_expression}> {
		protected initializer(state: 'node'|'property') {
			switch (state) {
				case 'node': return (init:Tnode__target__update_properties__operation__command_expression, parent:Cupdate_properties__operation__command_expression) => new Cnode__target__update_properties__operation__command_expression(init, parent);
				case 'property': return (init:Tproperty__target__update_properties__operation__command_expression, parent:Cupdate_properties__operation__command_expression) => new Cproperty__target__update_properties__operation__command_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'property') {
			switch (state) {
				case 'node': return finalize_node__target__update_properties__operation__command_expression;
				case 'property': return finalize_property__target__update_properties__operation__command_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tupdate_properties__operation__command_expression['target'], parent:Cupdate_properties__operation__command_expression) {
			super(data, parent, 'target');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'target'`; }
	}
}
export namespace Cnode__target__update_properties__operation__command_expression {
	export class Dexpression extends Ccommand_object_expression {
		constructor(data:Tnode__target__update_properties__operation__command_expression['expression'], parent:Cnode__target__update_properties__operation__command_expression) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pmaybe_value).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccommand_operation.Pupdate).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.node_type()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cproperty__target__update_properties__operation__command_expression {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tproperty__target__update_properties__operation__command_expression['expression'], parent:Cproperty__target__update_properties__operation__command_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.property?.inferences.target())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_target({name: 'properties', definition: conv_context},{
						current_value: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Ccurrent_value__application.Pmaybe_value).result!
						, 
						operation: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Ccommand_operation.Pupdate)
							.then(context => context?.definitions.optional_command_operation).result!

						});
					}).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			target: () => application.Ccommand_properties,
			user_value: () => application.Cuser__external_source
		}

		constructor(data:string, $this:Cproperty__target__update_properties__operation__command_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				target: cache((detach:boolean) => {
					const application__command_expression__operation__update_properties__target__property__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => evaluate__command_property_target_derivation(detach, 
							resolve(context)
								.then(context => application__command_expression__operation__update_properties__target__property__property_nval).result!, )).result!;
				})
				,
				user_value: cache((detach:boolean) => {
					const application__command_expression__operation__update_properties__target__property__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => application__command_expression__operation__update_properties__target__property__property_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
						.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
						.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Cwalk__operation__command_expression {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Twalk__operation__command_expression['expression'], parent:Cwalk__operation__command_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dpath extends Csingular_node_path {
		constructor(data:Twalk__operation__command_expression['path'], parent:Cwalk__operation__command_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dtail extends Cnode_path_tail {
		constructor(data:Twalk__operation__command_expression['tail'], parent:Cwalk__operation__command_expression) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.node_type()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__walk, init:Tcollection__type__walk}|
		{ name: 'reference set', node:Creference_set__type__walk, init:Treference_set__type__walk}> {
		protected initializer(state: 'collection'|'reference set') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__walk, parent:Cwalk__operation__command_expression) => new Ccollection__type__walk(init, parent);
				case 'reference set': return (init:Treference_set__type__walk, parent:Cwalk__operation__command_expression) => new Creference_set__type__walk(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'reference set') {
			switch (state) {
				case 'collection': return finalize_collection__type__walk;
				case 'reference set': return finalize_reference_set__type__walk;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twalk__operation__command_expression['type'], parent:Cwalk__operation__command_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Twalk__operation__command_expression['variable assignment'], parent:Cwalk__operation__command_expression) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.tail)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Ccollection__type__walk {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection__type__walk) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Creference_set__type__walk {
	export class Dreference_set extends Reference<application.Creference_set__type__attributes,string> {

		constructor(data:string, $this:Creference_set__type__walk) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'reference set') {
							return context.properties.type.state.node as application.Creference_set__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference set'`; }
	}
}
export namespace Ccommand_object_expression {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__command_object_expression, init:Tproperties__command_object_expression},Ccommand_object_expression> {
		protected initialize(parent:Ccommand_object_expression, key:string, entry_init:Tproperties__command_object_expression) { return new Cproperties__command_object_expression(key, entry_init, parent); }
		protected finalize = finalize_properties__command_object_expression
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__command_object_expression__properties_key_nval:application.Cproperty__type__attributes):boolean {
				const inferences:{
					current_value: (detach?:boolean) => application.Ccurrent_value__application|undefined;
					filter_identity: (detach?:boolean) => application.Cvalue_member_inclusion|undefined;
					filter_interface_dataset_status: (detach?:boolean) => application.Cvalue_member_inclusion|undefined;
					filter_interface_instance_key: (detach?:boolean) => application.Cvalue_member_inclusion|undefined;
					filter_password: (detach?:boolean) => application.Cvalue_member_inclusion|undefined;
					filter_password_status: (detach?:boolean) => application.Cvalue_member_inclusion|undefined;
					filter_username: (detach?:boolean) => application.Cvalue_member_inclusion|undefined;
					target: (detach?:boolean) => application.Ccommand_properties|undefined;
					value_source: (detach?:boolean) => application.Cbase_value_source|undefined;} = {

					current_value: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(context => application__command_object_expression__properties_key_nval)
						.then(context => context?.parent)
						.then(context => context?.properties.type?.state.node.output.attribute_type())
						.then(context => {
							switch (context?.variant.name) {
								case 'key': {
									return resolve(context)
										.then(() => this_obj)
										.then(() => application.Ccurrent_value__application.Pnone).result;
								}
								case 'simple': {
									return resolve(context)
										.then(() => this_obj)
										.then(context => context?.component_root.input.current_value()).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						})?.result),
					filter_identity: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(() => this_obj)
							.then(context => context?.component_root.input.operation())
							.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
							.then(context => context?.variant.name === 'identity' ? context.variant.definition as application.Cidentity_initializer : undefined).result;
							if (expression_context !== undefined) {
							const application__command_object_expression__properties_key_inf___filter_identity___struct_nval = expression_context;
							return resolve(match_context)
							.then(context => evaluate__value_member_filter(detach, 
								resolve(context)
									.then(context => application__command_object_expression__properties_key_nval).result!, {
								filter: () => resolve(this_obj)
									.then(context => application__command_object_expression__properties_key_inf___filter_identity___struct_nval)
									.then(context => context?.component_root.output.identity()).result!

								})).result
							} else {
								return resolve(match_context)
								.then(() => this_obj)
								.then(() => application.Cvalue_member_inclusion.Pyes).result
							}
						})?.result),
					filter_interface_dataset_status: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(() => this_obj)
							.then(context => context?.component_root.input.operation())
							.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
							.then(context => context?.variant.name === 'interface connection status' ? context.variant.definition as application.Ccases__yes__connection_status : undefined).result;
							if (expression_context !== undefined) {
							const application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct_nval = expression_context;
							return resolve(match_context)
							.then(context => application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct_nval)
							.then(context => context?.properties.dataset_status)
							.then(context => {
								const state_group = context;
								switch (state_group?.state.name) {
									case 'no': {
										return resolve(context)
											.then(() => this_obj)
											.then(() => application.Cvalue_member_inclusion.Pyes).result;
									}
									case 'yes': {
										const application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct___yes_nval = state_group.cast('yes');
										return resolve(context)
											.then(context => evaluate__value_member_filter(detach, 
												resolve(context)
													.then(context => application__command_object_expression__properties_key_nval).result!, {
												filter: () => resolve(this_obj)
													.then(context => application__command_object_expression__properties_key_inf___filter_interface_dataset_status___struct___yes_nval)
													.then(context => context?.properties.state_group_path?.inferences.state_group())
													.then(context => context?.parent).result!

												})).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
								};}).result
							} else {
								return resolve(match_context)
								.then(() => this_obj)
								.then(() => application.Cvalue_member_inclusion.Pyes).result
							}
						})?.result),
					filter_interface_instance_key: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(() => this_obj)
							.then(context => context?.component_root.input.operation())
							.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
							.then(context => context?.variant.name === 'interface instance' ? context.variant.definition as application.Cinterface_instance_initializer : undefined).result;
							if (expression_context !== undefined) {
							const application__command_object_expression__properties_key_inf___filter_interface_instance_key___struct_nval = expression_context;
							return resolve(match_context)
							.then(context => evaluate__value_member_filter(detach, 
								resolve(context)
									.then(context => application__command_object_expression__properties_key_nval).result!, {
								filter: () => resolve(this_obj)
									.then(context => application__command_object_expression__properties_key_inf___filter_interface_instance_key___struct_nval)
									.then(context => context?.component_root.output.interface_())
									.then(context => context?.inferences.collection())
									.then(context => context?.properties.key_property?.ref)
									.then(context => context?.parent).result!

								})).result
							} else {
								return resolve(match_context)
								.then(() => this_obj)
								.then(() => application.Cvalue_member_inclusion.Pyes).result
							}
						})?.result),
					filter_password: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(() => this_obj)
							.then(context => context?.component_root.input.operation())
							.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
							.then(context => context?.variant.name === 'password' ? context.variant.definition as application.Cpassword_initializer : undefined).result;
							if (expression_context !== undefined) {
							const application__command_object_expression__properties_key_inf___filter_password___struct_nval = expression_context;
							return resolve(match_context)
							.then(context => evaluate__value_member_filter(detach, 
								resolve(context)
									.then(context => application__command_object_expression__properties_key_nval).result!, {
								filter: () => resolve(this_obj)
									.then(context => application__command_object_expression__properties_key_inf___filter_password___struct_nval)
									.then(context => context?.component_root.output.password()).result!

								})).result
							} else {
								return resolve(match_context)
								.then(() => this_obj)
								.then(() => application.Cvalue_member_inclusion.Pyes).result
							}
						})?.result),
					filter_password_status: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(match_context => { 
							const expression_context = resolve(match_context)
							.then(() => this_obj)
							.then(context => context?.component_root.input.operation())
							.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined)
							.then(context => context?.variant.name === 'password' ? context.variant.definition as application.Cpassword_initializer : undefined).result;
							if (expression_context !== undefined) {
							const application__command_object_expression__properties_key_inf___filter_password_status___struct_nval = expression_context;
							return resolve(match_context)
							.then(context => evaluate__value_member_filter(detach, 
								resolve(context)
									.then(context => application__command_object_expression__properties_key_nval).result!, {
								filter: () => resolve(this_obj)
									.then(context => application__command_object_expression__properties_key_inf___filter_password_status___struct_nval)
									.then(context => context?.component_root.output.password_status()).result!

								})).result
							} else {
								return resolve(match_context)
								.then(() => this_obj)
								.then(() => application.Cvalue_member_inclusion.Pyes).result
							}
						})?.result),
					filter_username: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(() => this_obj)
						.then(context => context?.component_root.input.operation())
						.then(context => {
							switch (context?.variant.name) {
								case 'initialization': {
									const application__command_object_expression__properties_key_inf___filter_username___initialization_nval = context.cast('initialization');
									return resolve(context)
										.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'identity': {
													const application__command_object_expression__properties_key_inf___filter_username___initialization___identity_nval = context.cast('identity');
													return resolve(context)
														.then(context => evaluate__value_member_filter(detach, 
															resolve(context)
																.then(context => application__command_object_expression__properties_key_nval).result!, {
															filter: () => resolve(this_obj)
																.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization___identity_nval)
																.then(context => context?.component_root.output.username()).result!

															})).result;
												}
												case 'interface connection status': {
													return resolve(context)
														.then(() => this_obj)
														.then(() => application.Cvalue_member_inclusion.Pyes).result;
												}
												case 'interface instance': {
													return resolve(context)
														.then(() => this_obj)
														.then(() => application.Cvalue_member_inclusion.Pyes).result;
												}
												case 'object': {
													return resolve(context)
														.then(() => this_obj)
														.then(() => application.Cvalue_member_inclusion.Pyes).result;
												}
												case 'password': {
													const application__command_object_expression__properties_key_inf___filter_username___initialization___password_nval = context.cast('password');
													return resolve(context)
														.then(context => evaluate__value_member_filter(detach, 
															resolve(context)
																.then(context => application__command_object_expression__properties_key_nval).result!, {
															filter: () => resolve(this_obj)
																.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization___password_nval)
																.then(context => context?.component_root.output.username()).result!

															})).result;
												}
												case 'user': {
													const application__command_object_expression__properties_key_inf___filter_username___initialization___user_nval = context.cast('user');
													return resolve(context)
														.then(context => evaluate__value_member_filter(detach, 
															resolve(context)
																.then(context => application__command_object_expression__properties_key_nval).result!, {
															filter: () => resolve(this_obj)
																.then(context => application__command_object_expression__properties_key_inf___filter_username___initialization___user_nval)
																.then(context => context?.component_root.output.username()).result!

															})).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'update': {
									return resolve(context)
										.then(() => this_obj)
										.then(() => application.Cvalue_member_inclusion.Pyes).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						})?.result),
					target: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(context => evaluate__command_property_target_derivation(detach, 
							resolve(context)
								.then(context => application__command_object_expression__properties_key_nval).result!, ))?.result),
					value_source: maybe_cache((detach:boolean) => resolve(application__command_object_expression__properties_key_nval)
						.then(() => this_obj)
						.then(context => context?.component_root.input.operation())
						.then(context => {
							switch (context?.variant.name) {
								case 'initialization': {
									return resolve(context)
										.then(() => this_obj)
										.then(context => context?.component_root.input.current_value())
										.then(context => {
											switch (context?.variant.name) {
												case 'maybe value': {
													return resolve(context)
														.then(context => evaluate__user_source_checker(detach, 
															resolve(context)
																.then(context => application__command_object_expression__properties_key_nval).result!, )).result;
												}
												case 'none': {
													return resolve(context)
														.then(context => application__command_object_expression__properties_key_nval)
														.then(context => context?.properties.type?.state.node.output.value_source())
														.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
														.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
														.then(context => context?.definitions.base_value_source).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'update': {
									return resolve(context)
										.then(context => evaluate__user_source_checker(detach, 
											resolve(context)
												.then(context => application__command_object_expression__properties_key_nval).result!, )).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						})?.result)
				}
				const $this = {
					inferences: inferences
				}
				if (inferences.current_value() === undefined)
					return false;
				if (inferences.filter_identity() === undefined)
					return false;
				if (inferences.filter_interface_dataset_status() === undefined)
					return false;
				if (inferences.filter_interface_instance_key() === undefined)
					return false;
				if (inferences.filter_password() === undefined)
					return false;
				if (inferences.filter_password_status() === undefined)
					return false;
				if (inferences.filter_username() === undefined)
					return false;
				if (inferences.target() === undefined)
					return false;
				if (inferences.value_source() === undefined)
					return false;
				const application__command_object_expression__properties_cond_nval = application__command_object_expression__properties_key_nval;
				const condition_context = resolve(application__command_object_expression__properties_cond_nval)
				.then(() => this.parent)
				.then(context => context?.component_root.input.operation())
				.then(context => context?.variant.name === 'initialization' ? context.variant.definition as application.Ccommand_initialization_operation : undefined).result;
				if (condition_context === undefined) {
					return false;
				}
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.target())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tcommand_object_expression['properties'], parent:Ccommand_object_expression) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__command_object_expression {
	export class Dexpression extends Ccommand_expression {
		constructor(data:Tproperties__command_object_expression['expression'], parent:Cproperties__command_object_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.target())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_target({name: 'properties', definition: conv_context},{
						current_value: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.current_value()).result!
						, 
						operation: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.operation())
							.then(context => context?.definitions.optional_command_operation).result!

						});
					}).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Ccommand_object_initialization_behaviour {
	export class Dbehaviour extends StateGroup<
		{ name: 'ensure existence', node:Censure_existence, init:Tensure_existence}|
		{ name: 'fail when exists', node:Cfail_when_exists, init:Tfail_when_exists}> {
		protected initializer(state: 'ensure existence'|'fail when exists') {
			switch (state) {
				case 'ensure existence': return (init:Tensure_existence, parent:Ccommand_object_initialization_behaviour) => new Censure_existence(init, parent);
				case 'fail when exists': return (init:Tfail_when_exists, parent:Ccommand_object_initialization_behaviour) => new Cfail_when_exists(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ensure existence'|'fail when exists') {
			switch (state) {
				case 'ensure existence': return finalize_ensure_existence;
				case 'fail when exists': return finalize_fail_when_exists;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand_object_initialization_behaviour['behaviour'], parent:Ccommand_object_initialization_behaviour) {
			super(data, parent, 'behaviour');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'behaviour'`; }
	}
}
export namespace Ccommand_permission_definition {
	export class Dexecute_permission extends StateGroup<
		{ name: 'explicit', node:Cexplicit__execute_permission, init:Texplicit__execute_permission}|
		{ name: 'reachable', node:Creachable, init:Treachable}> {
		protected initializer(state: 'explicit'|'reachable') {
			switch (state) {
				case 'explicit': return (init:Texplicit__execute_permission, parent:Ccommand_permission_definition) => new Cexplicit__execute_permission(init, parent);
				case 'reachable': return (init:Treachable, parent:Ccommand_permission_definition) => new Creachable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'reachable') {
			switch (state) {
				case 'explicit': return finalize_explicit__execute_permission;
				case 'reachable': return finalize_reachable;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand_permission_definition['execute permission'], parent:Ccommand_permission_definition) {
			super(data, parent, 'execute permission');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'execute permission'`; }
	}
}
export namespace Cexplicit__execute_permission {
	export class Drequirement extends Cuser_requirement {
		constructor(data:Texplicit__execute_permission['requirement'], parent:Cexplicit__execute_permission) {
			super(data, parent, {
				definition_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'requirement')
		}
	}
}
export namespace Cconditional_node_path {
	export class Dpath extends Cnode_path {
		constructor(data:Tconditional_node_path['path'], parent:Cconditional_node_path) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Cconstant_number_value {
	export class Dvalue extends AlanInteger {
		public readonly inferences:{
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tconstant_number_value['value'], parent:Cconstant_number_value) {
			super(data, parent);this.inferences = {
				set_type: cache((detach:boolean) => {
					const application__constant_number_value__value_nval = this;
					return resolve(this)
						.then(switch_context => { 
							const value = resolve(switch_context)
								.then(context => application__constant_number_value__value_nval).result.value;
							if (value < 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(() => application.Cnumber_set_type.Pinteger).result;
							}
							else if (value > 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(() => application.Cnumber_set_type.Ppositive).result;
							}
							else if (value == 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(() => application.Cnumber_set_type.Pinteger).result;
							}
							else {
								throw new Error(`Missing case handler.`);
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Ccontext_node_path {
	export class Dcontext extends StateGroup<
		{ name: 'dynamic user', node:Cdynamic_user__context__context_node_path, init:Tdynamic_user__context__context_node_path}|
		{ name: 'this', node:Cthis__context__context_node_path, init:Tthis__context__context_node_path}|
		{ name: 'variable', node:Cvariable__context__context_node_path, init:Tvariable__context__context_node_path}> {
		protected initializer(state: 'dynamic user'|'this'|'variable') {
			switch (state) {
				case 'dynamic user': return (init:Tdynamic_user__context__context_node_path, parent:Ccontext_node_path) => new Cdynamic_user__context__context_node_path(init, parent);
				case 'this': return (init:Tthis__context__context_node_path, parent:Ccontext_node_path) => new Cthis__context__context_node_path(init, parent);
				case 'variable': return (init:Tvariable__context__context_node_path, parent:Ccontext_node_path) => new Cvariable__context__context_node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic user'|'this'|'variable') {
			switch (state) {
				case 'dynamic user': return finalize_dynamic_user__context__context_node_path;
				case 'this': return finalize_this__context__context_node_path;
				case 'variable': return finalize_variable__context__context_node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontext_node_path['context'], parent:Ccontext_node_path) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cvariable__context__context_node_path {
	export class Dname extends StateGroup<
		{ name: 'explicit', node:Cexplicit__name__variable, init:Texplicit__name__variable}|
		{ name: 'implicit', node:Cimplicit__name__variable, init:Timplicit__name__variable}> {
		protected initializer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return (init:Texplicit__name__variable, parent:Cvariable__context__context_node_path) => new Cexplicit__name__variable(init, parent);
				case 'implicit': return (init:Timplicit__name__variable, parent:Cvariable__context__context_node_path) => new Cimplicit__name__variable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return finalize_explicit__name__variable;
				case 'implicit': return finalize_implicit__name__variable;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvariable__context__context_node_path['name'], parent:Cvariable__context__context_node_path) {
			super(data, parent, 'name');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
}
export namespace Cexplicit__name__variable {
	export class Dhead extends Cancestor_variable_path {
		public readonly inferences:{
			assigned_variable: () => application.Cvariable_assignment,
			explicit_name: () => application.Cexplicit__name__variable_assignment
		}
		constructor(data:Texplicit__name__variable['head'], parent:Cexplicit__name__variable) {
			super(data, parent, {
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
			this.inferences = {
				assigned_variable: cache((detach:boolean) => {
					const application__context_node_path__context__variable__name__explicit__head_nval = this;
					return resolve(this)
						.then(context => application__context_node_path__context__variable__name__explicit__head_nval)
						.then(context => context?.component_root.output.variable())
						.then(context => context?.variant.name === 'assigned' ? context.variant.definition as application.Cvariable_assignment : undefined).result!;
				})
				,
				explicit_name: cache((detach:boolean) => {
					const application__context_node_path__context__variable__name__explicit__head_nval = this;
					return resolve(this)
						.then(() => parent.properties.head.inferences.assigned_variable())
						.then(context => {
							if (context?.properties.name.state.name === 'explicit') {
								return resolve(context.properties.name.state.node as application.Cexplicit__name__variable_assignment).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
	export class Dvariable extends Reference<application.Cnamed_objects__explicit__name__variable_assignment,string> {

		constructor(data:string, $this:Cexplicit__name__variable) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head?.inferences.explicit_name())
				.then(context => {
					const entry = context?.properties.named_objects.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'variable'`; }
	}
}
export namespace Ccontext_object_step {
	export class Dcontext extends StateGroup<
		{ name: 'dynamic user', node:Cdynamic_user__context__context_object_step, init:Tdynamic_user__context__context_object_step}|
		{ name: 'this', node:Cthis__context__context_object_step, init:Tthis__context__context_object_step}|
		{ name: 'variable', node:Cvariable__context__context_object_step, init:Tvariable__context__context_object_step}> {
		protected initializer(state: 'dynamic user'|'this'|'variable') {
			switch (state) {
				case 'dynamic user': return (init:Tdynamic_user__context__context_object_step, parent:Ccontext_object_step) => new Cdynamic_user__context__context_object_step(init, parent);
				case 'this': return (init:Tthis__context__context_object_step, parent:Ccontext_object_step) => new Cthis__context__context_object_step(init, parent);
				case 'variable': return (init:Tvariable__context__context_object_step, parent:Ccontext_object_step) => new Cvariable__context__context_object_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic user'|'this'|'variable') {
			switch (state) {
				case 'dynamic user': return finalize_dynamic_user__context__context_object_step;
				case 'this': return finalize_this__context__context_object_step;
				case 'variable': return finalize_variable__context__context_object_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontext_object_step['context'], parent:Ccontext_object_step) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cvariable__context__context_object_step {
	export class Dstep extends Cnamed_object_step {
		public readonly inferences:{
			existence: () => application.Cexistence__application,
			value_source: () => application.Cvalue_source
		}
		constructor(data:Tvariable__context__context_object_step['step'], parent:Cvariable__context__context_object_step) {
			super(data, parent, {
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'step')
			this.inferences = {
				existence: cache((detach:boolean) => {
					const application__context_object_step__context__variable__step_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__context_object_step__context__variable__step_nval)
								.then(context => context?.component_root.output.assignment())
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				value_source: cache((detach:boolean) => {
					const application__context_object_step__context__variable__step_nval = this;
					return resolve(this)
						.then(context => evaluate__value_source_checker(detach, 
							resolve(context)
								.then(context => application__context_object_step__context__variable__step_nval)
								.then(context => context?.component_root.output.assignment())
								.then(context => context?.component_root.output.value_source()).result!, {
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.defining_property())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.defining_property())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							value_source_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.value_source_support()).result!

							})).result!;
				})

			}
		}
	}
}
export namespace Cdependency_step {
	export class Dtype extends StateGroup<
		{ name: 'reference', node:Creference__type__dependency_step, init:Treference__type__dependency_step}|
		{ name: 'reference rule', node:Creference_rule, init:Treference_rule}|
		{ name: 'state context rule', node:Cstate_context_rule__type__dependency_step, init:Tstate_context_rule__type__dependency_step}> {
		protected initializer(state: 'reference'|'reference rule'|'state context rule') {
			switch (state) {
				case 'reference': return (init:Treference__type__dependency_step, parent:Cdependency_step) => new Creference__type__dependency_step(init, parent);
				case 'reference rule': return (init:Treference_rule, parent:Cdependency_step) => new Creference_rule(init, parent);
				case 'state context rule': return (init:Tstate_context_rule__type__dependency_step, parent:Cdependency_step) => new Cstate_context_rule__type__dependency_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'reference'|'reference rule'|'state context rule') {
			switch (state) {
				case 'reference': return finalize_reference__type__dependency_step;
				case 'reference rule': return finalize_reference_rule;
				case 'state context rule': return finalize_state_context_rule__type__dependency_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdependency_step['type'], parent:Cdependency_step) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Creference__type__dependency_step {
	export class Dproperty extends Cproperty_step {
		public readonly inferences:{
			dependencies: () => application.Ccomputation_phase,
			existence: () => application.Cexistence__application,
			has_reference: () => application.Cyes__has_reference,
			reference: () => application.Creference_definer,
			relative_location_of_referenced_node: () => application.Crelative_object_location,
			text: () => application.Ctext__type__property,
			value_source: () => application.Cvalue_source
		}
		constructor(data:Treference__type__dependency_step['property'], parent:Creference__type__dependency_step) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'property')
			this.inferences = {
				dependencies: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(context => evaluate__reference_dependencies_checker(detach, 
							resolve(context)
								.then(() => parent.properties.property.inferences.reference()).result!, {
							context_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location: () => resolve(parent)
								.then(() => parent.properties.property.inferences.reference())
								.then(context => context?.component_root.output.recursion())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.dependency_location_support()).result!
							, 
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				existence: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.property.inferences.reference())
								.then(context => context?.component_root.output.behaviour())
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				has_reference: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(() => parent.properties.property.inferences.text())
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(() => parent.properties.property.inferences.has_reference())
						.then(context => context?.definitions.reference_definer).result!;
				})
				,
				relative_location_of_referenced_node: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(context => evaluate__merge_locations(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.root).result!, {
							context_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.referenced_location_support()).result!
							, 
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							reference_context_location: () => resolve(parent)
								.then(() => parent.properties.property.inferences.reference())
								.then(context => context?.component_root.output.location()).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				text: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(context => application__dependency_step__type__reference__property_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				value_source: cache((detach:boolean) => {
					const application__dependency_step__type__reference__property_nval = this;
					return resolve(this)
						.then(context => evaluate__value_source_checker(detach, 
							resolve(context)
								.then(() => parent.properties.property.inferences.reference())
								.then(context => context?.component_root.output.behaviour())
								.then(context => context?.component_root.output.value_source()).result!, {
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.target_root_location()).result!
							, 
							value_source_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.value_source_support()).result!

							})).result!;
				})

			}
		}
	}
}
export namespace Creference_rule {
	export class Dproperty extends Cproperty_step {
		public readonly inferences:{
			has_reference: () => application.Cyes__has_reference,
			text: () => application.Ctext__type__property
		}
		constructor(data:Treference_rule['property'], parent:Creference_rule) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'property')
			this.inferences = {
				has_reference: cache((detach:boolean) => {
					const application__dependency_step__type__reference_rule__property_nval = this;
					return resolve(this)
						.then(() => parent.properties.property.inferences.text())
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				text: cache((detach:boolean) => {
					const application__dependency_step__type__reference_rule__property_nval = this;
					return resolve(this)
						.then(context => application__dependency_step__type__reference_rule__property_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
	export class Drule extends Cwhere_rule_step {
		constructor(data:Treference_rule['rule'], parent:Creference_rule) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.property?.inferences.has_reference())
					.then(context => context?.properties.rules).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.property)
					.then(context => context?.component_root.output.phase()).result!),
				referenced_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.referenced_location_support()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'rule')
		}
	}
}
export namespace Cstate_context_rule__type__dependency_step {
	export class Drule extends Cwhere_rule_step {
		constructor(data:Tstate_context_rule__type__dependency_step['rule'], parent:Cstate_context_rule__type__dependency_step) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.state())
					.then(context => context?.properties.rules).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				referenced_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.referenced_location_support()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'rule')
		}
	}
}
export namespace Cderivation_expression {
	export class Dtype extends StateGroup<
		{ name: 'node switch', node:Cnode_switch__type, init:Tnode_switch__type}|
		{ name: 'number switch', node:Cnumber_switch, init:Tnumber_switch}|
		{ name: 'produce value', node:Cproduce_value__type, init:Tproduce_value__type}|
		{ name: 'recurse', node:Crecurse, init:Trecurse}|
		{ name: 'set switch', node:Cset_switch, init:Tset_switch}|
		{ name: 'state switch', node:Cstate_switch__type, init:Tstate_switch__type}> {
		protected initializer(state: 'node switch'|'number switch'|'produce value'|'recurse'|'set switch'|'state switch') {
			switch (state) {
				case 'node switch': return (init:Tnode_switch__type, parent:Cderivation_expression) => new Cnode_switch__type(init, parent);
				case 'number switch': return (init:Tnumber_switch, parent:Cderivation_expression) => new Cnumber_switch(init, parent);
				case 'produce value': return (init:Tproduce_value__type, parent:Cderivation_expression) => new Cproduce_value__type(init, parent);
				case 'recurse': return (init:Trecurse, parent:Cderivation_expression) => new Crecurse(init, parent);
				case 'set switch': return (init:Tset_switch, parent:Cderivation_expression) => new Cset_switch(init, parent);
				case 'state switch': return (init:Tstate_switch__type, parent:Cderivation_expression) => new Cstate_switch__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node switch'|'number switch'|'produce value'|'recurse'|'set switch'|'state switch') {
			switch (state) {
				case 'node switch': return finalize_node_switch__type;
				case 'number switch': return finalize_number_switch;
				case 'produce value': return finalize_produce_value__type;
				case 'recurse': return finalize_recurse;
				case 'set switch': return finalize_set_switch;
				case 'state switch': return finalize_state_switch__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderivation_expression['type'], parent:Cderivation_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnode_switch__type {
	export class Dnode_path extends Cvariablized_object_path {
		public readonly inferences:{
			node: () => application.Cnode__application
		}
		constructor(data:Tnode_switch__type['node path'], parent:Cnode_switch__type) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cdirection.Punbounded).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'node path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__derivation_expression__type__node_switch__node_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__node_switch__node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
	export class Doperation extends StateGroup<
		{ name: 'equality', node:Cequality__operation, init:Tequality__operation}|
		{ name: 'existence', node:Cexistence__operation, init:Texistence__operation}> {
		protected initializer(state: 'equality'|'existence') {
			switch (state) {
				case 'equality': return (init:Tequality__operation, parent:Cnode_switch__type) => new Cequality__operation(init, parent);
				case 'existence': return (init:Texistence__operation, parent:Cnode_switch__type) => new Cexistence__operation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'equality'|'existence') {
			switch (state) {
				case 'equality': return finalize_equality__operation;
				case 'existence': return finalize_existence__operation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_switch__type['operation'], parent:Cnode_switch__type) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Ccases__node_switch__type {
	export class Dnone extends Cderivation_expression {
		constructor(data:Tcases__node_switch__type['none'], parent:Ccases__node_switch__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'none')
		}
	}
}
export namespace Cnode__cases {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tnode__cases['expression'], parent:Cnode__cases) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_named_object_assignment {
		constructor(data:Tnode__cases['variable assignment'], parent:Cnode__cases) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.node_path)
					.then(context => context?.component_root.output.context())
					.then(context => {
						switch (context?.variant.name) {
							case 'attribute': {
								const application__derivation_expression__type__node_switch__cases__node__variable_assignment_in___context___attribute_nval = context.cast('attribute');
								return resolve(context)
									.then(context => application__derivation_expression__type__node_switch__cases__node__variable_assignment_in___context___attribute_nval)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cnavigation_context({name: 'attribute', definition: conv_context},{
										dependency: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.dependency()).result!
										, 
										direction: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.direction()).result!
										, 
										existence: () => resolve(parent)
											.then(() => parent)
											.then(() => application.Cexistence__application.Pmandatory).result!
										, 
										location: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.location()).result!
										, 
										value_source_dependence: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.value_source_dependence()).result!

										});
									}).result;
							}
							case 'value': {
								const application__derivation_expression__type__node_switch__cases__node__variable_assignment_in___context___value_nval = context.cast('value');
								return resolve(context)
									.then(context => application__derivation_expression__type__node_switch__cases__node__variable_assignment_in___context___value_nval)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cnavigation_context({name: 'value', definition: conv_context},{
										dependency: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.dependency()).result!
										, 
										direction: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.direction()).result!
										, 
										existence: () => resolve(parent)
											.then(() => parent)
											.then(() => application.Cexistence__application.Pmandatory).result!
										, 
										location: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.location()).result!
										, 
										value_source_dependence: () => resolve(parent)
											.then(() => parent)
											.then(context => context?.parent)
											.then(context => context?.parent)
											.then(context => context?.properties.node_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.value_source_dependence()).result!

										});
									}).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.node_path)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cequality__operation {
	export class Dother_node_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			node: () => application.Cnode__application,
			node_type: () => application.Cnode__application
		}
		constructor(data:Tequality__operation['other node path'], parent:Cequality__operation) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'other node path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__derivation_expression__type__node_switch__operation__equality__other_node_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__node_switch__operation__equality__other_node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__derivation_expression__type__node_switch__operation__equality__other_node_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.other_node_path.inferences.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.node_path?.inferences.node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cnumber_switch {
	export class Dcases extends AlanDictionary<{ node:Ccases__number_switch, init:Tcases__number_switch},Cnumber_switch> {
		protected initialize(parent:Cnumber_switch, key:string, entry_init:Tcases__number_switch) { return new Ccases__number_switch(key, entry_init, parent); }
		protected finalize = finalize_cases__number_switch
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
		constructor(data:Tnumber_switch['cases'], parent:Cnumber_switch) {
			super(data, parent);
		}
	}
	export class Dcompare_to extends StateGroup<
		{ name: 'constant', node:Cconstant__compare_to, init:Tconstant__compare_to}|
		{ name: 'path', node:Cpath__compare_to, init:Tpath__compare_to}> {
		protected initializer(state: 'constant'|'path') {
			switch (state) {
				case 'constant': return (init:Tconstant__compare_to, parent:Cnumber_switch) => new Cconstant__compare_to(init, parent);
				case 'path': return (init:Tpath__compare_to, parent:Cnumber_switch) => new Cpath__compare_to(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'constant'|'path') {
			switch (state) {
				case 'constant': return finalize_constant__compare_to;
				case 'path': return finalize_path__compare_to;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_switch['compare to'], parent:Cnumber_switch) {
			super(data, parent, 'compare to');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'compare to'`; }
	}
	export class Dlast_case extends Reference<application.Ccases__number_switch,string> {
		public readonly inferences:{
			equals_case: () => application.Ccases__number_switch,
			greater_than_case: () => application.Ccases__number_switch,
			less_than_case: () => application.Ccases__number_switch
		}

		constructor(data:string, $this:Cnumber_switch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.cases.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				equals_case: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__last_case_nval = $this.properties.last_case.ref;
					return resolve($this.properties.last_case.ref)
						.then(context => application__derivation_expression__type__number_switch__last_case_nval)
						.then(context => context?.properties.has_predecessor?.state.node.output.equals_case())
						.then(context => context?.variant.name === 'defined' ? context.variant.definition as application.Ccases__number_switch : undefined).result!;
				})
				,
				greater_than_case: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__last_case_nval = $this.properties.last_case.ref;
					return resolve($this.properties.last_case.ref)
						.then(context => application__derivation_expression__type__number_switch__last_case_nval)
						.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case())
						.then(context => context?.variant.name === 'defined' ? context.variant.definition as application.Ccases__number_switch : undefined).result!;
				})
				,
				less_than_case: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__last_case_nval = $this.properties.last_case.ref;
					return resolve($this.properties.last_case.ref)
						.then(context => application__derivation_expression__type__number_switch__last_case_nval)
						.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case())
						.then(context => context?.variant.name === 'defined' ? context.variant.definition as application.Ccases__number_switch : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'last case'`; }
	}
	export class Dnumber_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			number: () => application.Cnumber_value
		}
		constructor(data:Tnumber_switch['number path'], parent:Cnumber_switch) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'number path')
			this.inferences = {
				number: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__number_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__number_switch__number_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
						.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
						.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Cnumber_value : undefined).result!;
				})

			}
		}
	}
}
export namespace Ccases__number_switch {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tcases__number_switch['expression'], parent:Ccases__number_switch) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dhas_predecessor extends StateGroup<
		{ name: 'no', node:Cno__has_predecessor__cases, init:Tno__has_predecessor__cases}|
		{ name: 'yes', node:Cyes__has_predecessor__cases, init:Tyes__has_predecessor__cases}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_predecessor__cases, parent:Ccases__number_switch) => new Cno__has_predecessor__cases(init, parent);
				case 'yes': return (init:Tyes__has_predecessor__cases, parent:Ccases__number_switch) => new Cyes__has_predecessor__cases(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_predecessor__cases;
				case 'yes': return finalize_yes__has_predecessor__cases;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcases__number_switch['has predecessor'], parent:Ccases__number_switch) {
			super(data, parent, 'has predecessor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has predecessor'`; }
	}
	export class Dvariable_assignment extends Coptional_named_object_assignment {
		constructor(data:Tcases__number_switch['variable assignment'], parent:Ccases__number_switch) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.properties.number_path?.inferences.number())
						.then(context => context?.component_root.output.numerical_type()).result!, 
					set_type: () => resolve(parent)
						.then(switch_context => {
							return resolve(switch_context)
							.then(() => parent)
							.then(context => context?.key?.ref).then(key_value => {
								if (key_value === keyset___keycases__number_switch[`equals`]) {
									return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.properties.number_path?.inferences.number())
									.then(context => context?.component_root.output.set_type())
									.then(context => {
										switch (context?.variant.name) {
											case 'integer': {
												return resolve(context)
													.then(() => parent)
													.then(context => context?.parent)
													.then(context => context?.properties.compare_to?.state.node.output.set_type()).result;
											}
											case 'positive': {
												return resolve(context)
													.then(() => parent)
													.then(() => application.Cnumber_set_type.Ppositive).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
										};
									}).result
								} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
									return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.properties.number_path?.inferences.number())
									.then(context => context?.component_root.output.set_type())
									.then(context => {
										switch (context?.variant.name) {
											case 'integer': {
												return resolve(context)
													.then(() => parent)
													.then(context => context?.parent)
													.then(context => context?.properties.compare_to?.state.node.output.greater_than_set_type()).result;
											}
											case 'positive': {
												return resolve(context)
													.then(() => parent)
													.then(() => application.Cnumber_set_type.Ppositive).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
										};
									}).result
								} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
									return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.properties.number_path?.inferences.number())
									.then(context => context?.component_root.output.set_type())
									.then(context => {
										switch (context?.variant.name) {
											case 'integer': {
												return resolve(context)
													.then(() => parent)
													.then(context => context?.parent)
													.then(context => context?.properties.compare_to?.state.node.output.set_type()).result;
											}
											case 'positive': {
												return resolve(context)
													.then(() => parent)
													.then(() => application.Cnumber_set_type.Ppositive).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
										};
									}).result
								} else if (key_value === keyset___keycases__number_switch[`less than`]) {
									return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.properties.number_path?.inferences.number())
									.then(context => context?.component_root.output.set_type()).result
								} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
									return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.properties.number_path?.inferences.number())
									.then(context => context?.component_root.output.set_type()).result
								} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
									return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.properties.number_path?.inferences.number())
									.then(context => context?.component_root.output.set_type()).result
								} else {
								throw new Error(`Unexpected key in dictionary with static keys.`);
								}
							}).result
						}).result!}), )
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.number_value)
							.then(context => context?.definitions.scalar_value)
							.then(context => context?.definitions.leaf_value)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.number_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.number_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.number_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.number_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.number_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.number_path)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cyes__has_predecessor__cases {
	export class Dcase_ extends Reference<application.Ccases__number_switch,string> {
		public readonly inferences:{
			equals_case: () => application.Cnumber_comparison_case,
			greater_than_case: () => application.Cnumber_comparison_case,
			less_than_case: () => application.Cnumber_comparison_case
		}

		constructor(data:string, $this:Cyes__has_predecessor__cases) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.cases.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				equals_case: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval = $this.properties.case_.ref;
					return resolve($this.properties.case_.ref)
						.then(switch_context => {
							return resolve(switch_context)
							.then(() => $this)
							.then(context => context?.parent)
							.then(context => context?.key?.ref).then(key_value => {
								if (key_value === keyset___keycases__number_switch[`equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.equals_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.equals_case()).result
								} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.equals_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`less than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.equals_case()).result
								} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.equals_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.equals_case()).result
								} else {
								throw new Error(`Unexpected key in dictionary with static keys.`);
								}
							}).result
						}).result!;
				})
				,
				greater_than_case: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval = $this.properties.case_.ref;
					return resolve($this.properties.case_.ref)
						.then(switch_context => {
							return resolve(switch_context)
							.then(() => $this)
							.then(context => context?.parent)
							.then(context => context?.key?.ref).then(key_value => {
								if (key_value === keyset___keycases__number_switch[`equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case()).result
								} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`less than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case()).result
								} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case()).result
								} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.greater_than_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else {
								throw new Error(`Unexpected key in dictionary with static keys.`);
								}
							}).result
						}).result!;
				})
				,
				less_than_case: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval = $this.properties.case_.ref;
					return resolve($this.properties.case_.ref)
						.then(switch_context => {
							return resolve(switch_context)
							.then(() => $this)
							.then(context => context?.parent)
							.then(context => context?.key?.ref).then(key_value => {
								if (key_value === keyset___keycases__number_switch[`equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case()).result
								} else if (key_value === keyset___keycases__number_switch[`greater than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case()).result
								} else if (key_value === keyset___keycases__number_switch[`greater than or equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case()).result
								} else if (key_value === keyset___keycases__number_switch[`less than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`less than or equals`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else if (key_value === keyset___keycases__number_switch[`less than or greater than`]) {
									return resolve(switch_context)
									.then(context => application__derivation_expression__type__number_switch__cases__has_predecessor__yes__case_nval)
									.then(context => context?.properties.has_predecessor?.state.node.output.less_than_case())
									.then(context => context?.variant.name === 'none' ? context.variant.definition as application.Cnone__number_comparison_case : undefined).result
								} else {
								throw new Error(`Unexpected key in dictionary with static keys.`);
								}
							}).result
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'case'`; }
	}
}
export namespace Cconstant__compare_to {
	export class Dvalue extends Cconstant_number_value {
		constructor(data:Tconstant__compare_to['value'], parent:Cconstant__compare_to) {
			super(data, parent, 'value')
		}
	}
}
export namespace Cpath__compare_to {
	export class Dright_number_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			number: () => application.Cnumber_value
		}
		constructor(data:Tpath__compare_to['right number path'], parent:Cpath__compare_to) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'right number path')
			this.inferences = {
				number: cache((detach:boolean) => {
					const application__derivation_expression__type__number_switch__compare_to__path__right_number_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__number_switch__compare_to__path__right_number_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
						.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
						.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Cnumber_value : undefined).result!;
				})

			}
		}
	}
}
export namespace Cproduce_value__type {
	export class Dexpression extends Cderivation_expression_tail {
		constructor(data:Tproduce_value__type['expression'], parent:Cproduce_value__type) {
			super(data, parent, {
				support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderivation_expression_support.Punrestricted).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Crecurse {
	export class Dstep extends StateGroup<
		{ name: 'repeat', node:Crepeat, init:Trepeat}|
		{ name: 'start', node:Cstart, init:Tstart}> {
		protected initializer(state: 'repeat'|'start') {
			switch (state) {
				case 'repeat': return (init:Trepeat, parent:Crecurse) => new Crepeat(init, parent);
				case 'start': return (init:Tstart, parent:Crecurse) => new Cstart(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'repeat'|'start') {
			switch (state) {
				case 'repeat': return finalize_repeat;
				case 'start': return finalize_start;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trecurse['step'], parent:Crecurse) {
			super(data, parent, 'step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'step'`; }
	}
}
export namespace Crepeat {
	export class Drecursion_start extends Cnamed_object_step {
		public readonly inferences:{
			closure: () => application.Cstart
		}
		constructor(data:Trepeat['recursion start'], parent:Crepeat) {
			super(data, parent, {
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'recursion start')
			this.inferences = {
				closure: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__repeat__recursion_start_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__recurse__step__repeat__recursion_start_nval)
						.then(context => context?.component_root.output.assignment())
						.then(context => context?.component_root.output.definer())
						.then(context => context?.variant.name === 'recursion start' ? context.variant.definition as application.Cstart : undefined).result!;
				})

			}
		}
	}
	export class Dsibling_node_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			binding_node_type: () => application.Cnode__application,
			graph_traversing_step: () => application.Centry_step_definition,
			graph_traversing_steps: () => application.Cdependency__application,
			node: () => application.Cnode__application,
			sibling_of_recursion_start_object: () => application.Cdependency__application
		}
		constructor(data:Trepeat['sibling node path'], parent:Crepeat) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'sibling node path')
			this.inferences = {
				binding_node_type: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.sibling_node_path.inferences.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.properties.recursion_start?.inferences.closure())
								.then(context => context?.properties.binding_node_path?.inferences.node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				graph_traversing_step: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.dependency())
						.then(context => context?.variant.name === 'step' ? context.variant.definition as application.Cdependency_definition : undefined)
						.then(context => context?.variant.name === 'entry step' ? context.variant.definition as application.Centry_step_definition : undefined).result!;
				})
				,
				graph_traversing_steps: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval = this;
					return resolve(this)
						.then(context => evaluate__unwind_graph_traversing_steps(detach, 
							resolve(context)
								.then(context => application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.location()).result!, {
							dependency: () => resolve(parent)
								.then(context => application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!
							, 
							recursion_start: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.recursion_start?.inferences.closure()).result!

							})).result!;
				})
				,
				node: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})
				,
				sibling_of_recursion_start_object: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__repeat__sibling_node_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.sibling_node_path.inferences.graph_traversing_steps()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.properties.recursion_start?.inferences.closure())
								.then(context => context?.properties.recursion_start_assignment)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cstart {
	export class Dbinding_node_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property,
			node: () => application.Cnode__application
		}
		constructor(data:Tstart['binding node path'], parent:Cstart) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'binding node path')
			this.inferences = {
				collection: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__start__binding_node_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.binding_node_path.inferences.node())
						.then(context => context?.component_root.output.entity())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
				})
				,
				node: cache((detach:boolean) => {
					const application__derivation_expression__type__recurse__step__start__binding_node_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__recurse__step__start__binding_node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tstart['expression'], parent:Cstart) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.recursion_start_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dgraph_traversal extends Cgraph_traversal_definition {
		constructor(data:Tstart['graph traversal'], parent:Cstart) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.binding_node_path?.inferences.collection()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'graph traversal')
		}
	}
	export class Drecursion_start_assignment extends Cnamed_object_assignment {
		constructor(data:Tstart['recursion start assignment'], parent:Cstart) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.binding_node_path)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.value())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => {
								const conv_context = resolve(context).result!;
								return new Cdependency_definition({name: 'recursion start step', definition: conv_context},{
								location: () => resolve(parent)
									.then(() => parent)
									.then(context => context?.properties.binding_node_path?.inferences.node())
									.then(context => context?.component_root.output.entity())
									.then(context => context?.definitions.relative_object_location).result!
								, 
								parent: () => resolve(parent)
									.then(() => parent)
									.then(context => context?.properties.binding_node_path)
									.then(context => context?.component_root.output.context())
									.then(context => context?.component_root.output.dependency()).result!

								});
							})
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.binding_node_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.binding_node_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.binding_node_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.binding_node_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.named_object_definer).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.binding_node_path)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'recursion start assignment')
		}
	}
}
export namespace Cset_switch {
	export class Dnodes_path extends Cobject_set_path {
		public readonly inferences:{
			node: () => application.Cnode__application
		}
		constructor(data:Tset_switch['nodes path'], parent:Cset_switch) {
			super(data, parent, {
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Clower_bound_cardinality.Pzero).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'nodes path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__derivation_expression__type__set_switch__nodes_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__set_switch__nodes_path_nval)
						.then(context => context?.component_root.output.object_context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
}
export namespace Ccases__set_switch {
	export class Dmatch_node extends StateGroup<
		{ name: 'no', node:Cno__match_node, init:Tno__match_node}|
		{ name: 'yes', node:Cyes__match_node, init:Tyes__match_node}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__match_node, parent:Ccases__set_switch) => new Cno__match_node(init, parent);
				case 'yes': return (init:Tyes__match_node, parent:Ccases__set_switch) => new Cyes__match_node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__match_node;
				case 'yes': return finalize_yes__match_node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcases__set_switch['match node'], parent:Ccases__set_switch) {
			super(data, parent, 'match node');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'match node'`; }
	}
	export class Dmatch_nodes extends StateGroup<
		{ name: 'no', node:Cno__match_nodes, init:Tno__match_nodes}|
		{ name: 'yes', node:Cyes__match_nodes, init:Tyes__match_nodes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__match_nodes, parent:Ccases__set_switch) => new Cno__match_nodes(init, parent);
				case 'yes': return (init:Tyes__match_nodes, parent:Ccases__set_switch) => new Cyes__match_nodes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__match_nodes;
				case 'yes': return finalize_yes__match_nodes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcases__set_switch['match nodes'], parent:Ccases__set_switch) {
			super(data, parent, 'match nodes');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'match nodes'`; }
	}
	export class Dmatch_none extends StateGroup<
		{ name: 'no', node:Cno__match_none, init:Tno__match_none}|
		{ name: 'yes', node:Cyes__match_none, init:Tyes__match_none}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__match_none, parent:Ccases__set_switch) => new Cno__match_none(init, parent);
				case 'yes': return (init:Tyes__match_none, parent:Ccases__set_switch) => new Cyes__match_none(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__match_none;
				case 'yes': return finalize_yes__match_none;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcases__set_switch['match none'], parent:Ccases__set_switch) {
			super(data, parent, 'match none');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'match none'`; }
	}
}
export namespace Cyes__match_node {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tyes__match_node['expression'], parent:Cyes__match_node) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_named_object_assignment {
		constructor(data:Tyes__match_node['variable assignment'], parent:Cyes__match_node) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.nodes_path?.inferences.node())
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.component_root.output.object_context())
							.then(context => context?.component_root.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.component_root.output.object_context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.component_root.output.object_context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.component_root.output.object_context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.nodes_path)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cyes__match_nodes {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tyes__match_nodes['expression'], parent:Cyes__match_nodes) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_named_object_assignment {
		constructor(data:Tyes__match_nodes['variable assignment'], parent:Cyes__match_nodes) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this).then(context => new Cderived_set({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => application.Clower_bound_cardinality.Pone).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => application.Cupper_bound_cardinality.Pmany).result!},{
					entity: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.nodes_path?.inferences.node())
						.then(context => context?.component_root.output.entity()).result!
					, 
					source_collection: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.nodes_path)
						.then(context => context?.component_root.output.collection())
						.then(context => context?.component_root.output.source_collection()).result!
					, 
					source_entry_existence: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.nodes_path)
						.then(context => context?.component_root.output.object_context())
						.then(context => context?.component_root.output.existence()).result!
					, 
					value_location: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.nodes_path)
						.then(context => context?.component_root.output.collection())
						.then(context => context?.component_root.output.value_location()).result!
					, 
					value_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.properties.nodes_path)
						.then(context => context?.component_root.output.object_context())
						.then(context => context?.component_root.output.value()).result!

					}), )
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.collection_object)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.properties.nodes_path)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.nodes_path)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cyes__match_none {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tyes__match_none['expression'], parent:Cyes__match_none) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cstate_switch__type {
	export class Dstate_group_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			state_group: () => application.Cstate_group
		}
		constructor(data:Tstate_switch__type['state group path'], parent:Cstate_switch__type) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'state group path')
			this.inferences = {
				state_group: cache((detach:boolean) => {
					const application__derivation_expression__type__state_switch__state_group_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression__type__state_switch__state_group_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'choice' ? context.variant.definition as application.Cstate_group : undefined).result!;
				})

			}
		}
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__state_switch__type, init:Tstates__state_switch__type},Cstate_switch__type> {
		protected initialize(parent:Cstate_switch__type, key:string, entry_init:Tstates__state_switch__type) { return new Cstates__state_switch__type(key, entry_init, parent); }
		protected finalize = finalize_states__state_switch__type
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__derivation_expression__type__state_switch__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.state_group_path?.inferences.state_group())
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_switch__type['states'], parent:Cstate_switch__type) {
			super(data, parent);
		}
	}
}
export namespace Cstates__state_switch__type {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Tstates__state_switch__type['expression'], parent:Cstates__state_switch__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_named_object_assignment {
		constructor(data:Tstates__state_switch__type['variable assignment'], parent:Cstates__state_switch__type) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node)
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.state_group_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.state_group_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.state_group_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.state_group_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.state_group_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.state_group_path)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cderivation_expression_list {
	export class Dhas_tail extends StateGroup<
		{ name: 'no', node:Cno__has_tail__derivation_expression_list, init:Tno__has_tail__derivation_expression_list}|
		{ name: 'yes', node:Cyes__has_tail__derivation_expression_list, init:Tyes__has_tail__derivation_expression_list}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_tail__derivation_expression_list, parent:Cderivation_expression_list) => new Cno__has_tail__derivation_expression_list(init, parent);
				case 'yes': return (init:Tyes__has_tail__derivation_expression_list, parent:Cderivation_expression_list) => new Cyes__has_tail__derivation_expression_list(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_tail__derivation_expression_list;
				case 'yes': return finalize_yes__has_tail__derivation_expression_list;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderivation_expression_list['has tail'], parent:Cderivation_expression_list) {
			super(data, parent, 'has tail');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has tail'`; }
	}
	export class Dhead extends Cderivation_expression_tail {
		constructor(data:Tderivation_expression_list['head'], parent:Cderivation_expression_list) {
			super(data, parent, {
				support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderivation_expression_support.Punary).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
		}
	}
}
export namespace Cyes__has_tail__derivation_expression_list {
	export class Dtail extends Cderivation_expression_list {
		constructor(data:Tyes__has_tail__derivation_expression_list['tail'], parent:Cyes__has_tail__derivation_expression_list) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'tail')
		}
	}
}
export namespace Cderivation_expression_tail {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic__type__derivation_expression_tail, init:Tdynamic__type__derivation_expression_tail}|
		{ name: 'number', node:Cnumber__type__derivation_expression_tail, init:Tnumber__type__derivation_expression_tail}|
		{ name: 'reference', node:Creference__type__derivation_expression_tail, init:Treference__type__derivation_expression_tail}|
		{ name: 'state', node:Cstate__type__derivation_expression_tail, init:Tstate__type__derivation_expression_tail}|
		{ name: 'text', node:Ctext__type__derivation_expression_tail, init:Ttext__type__derivation_expression_tail}> {
		protected initializer(state: 'dynamic'|'number'|'reference'|'state'|'text') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic__type__derivation_expression_tail, parent:Cderivation_expression_tail) => new Cdynamic__type__derivation_expression_tail(init, parent);
				case 'number': return (init:Tnumber__type__derivation_expression_tail, parent:Cderivation_expression_tail) => new Cnumber__type__derivation_expression_tail(init, parent);
				case 'reference': return (init:Treference__type__derivation_expression_tail, parent:Cderivation_expression_tail) => new Creference__type__derivation_expression_tail(init, parent);
				case 'state': return (init:Tstate__type__derivation_expression_tail, parent:Cderivation_expression_tail) => new Cstate__type__derivation_expression_tail(init, parent);
				case 'text': return (init:Ttext__type__derivation_expression_tail, parent:Cderivation_expression_tail) => new Ctext__type__derivation_expression_tail(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'number'|'reference'|'state'|'text') {
			switch (state) {
				case 'dynamic': return finalize_dynamic__type__derivation_expression_tail;
				case 'number': return finalize_number__type__derivation_expression_tail;
				case 'reference': return finalize_reference__type__derivation_expression_tail;
				case 'state': return finalize_state__type__derivation_expression_tail;
				case 'text': return finalize_text__type__derivation_expression_tail;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderivation_expression_tail['type'], parent:Cderivation_expression_tail) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic__type__derivation_expression_tail {
	export class Dpath extends Csingular_variablized_object_path {
		public readonly inferences:{
			dependency: () => application.Cdependency__application,
			value: () => application.Cvalue_object
		}
		constructor(data:Tdynamic__type__derivation_expression_tail['path'], parent:Cdynamic__type__derivation_expression_tail) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
			this.inferences = {
				dependency: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__dynamic__path_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => {
							switch (context?.variant.name) {
								case 'choice': {
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.dependency()).result;
								}
								case 'collection': {
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.dependency()).result;
								}
								case 'leaf': {
									const application__derivation_expression_tail__type__dynamic__path_inf___dependency___leaf_nval = context.cast('leaf');
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_inf___dependency___leaf_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'file': {
													return resolve(context)
														.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
														.then(context => context?.component_root.output.context())
														.then(context => context?.component_root.output.dependency()).result;
												}
												case 'reference': {
													const application__derivation_expression_tail__type__dynamic__path_inf___dependency___leaf___reference_nval = context.cast('reference');
													return resolve(context)
														.then(context => evaluate__check_dependencies(detach, 
															resolve(context)
																.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
																.then(context => context?.component_root.output.context())
																.then(context => context?.component_root.output.dependency()).result!, {
															expected: () => resolve(parent)
																.then(context => application__derivation_expression_tail__type__dynamic__path_inf___dependency___leaf___reference_nval)
																.then(context => context?.component_root.output.definer())
																.then(context => context?.component_root.output.dependency()).result!

															})).result;
												}
												case 'scalar': {
													return resolve(context)
														.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
														.then(context => context?.component_root.output.context())
														.then(context => context?.component_root.output.dependency()).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'node': {
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.dependency()).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})
				,
				value: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__dynamic__path_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => {
							switch (context?.variant.name) {
								case 'choice': {
									const application__derivation_expression_tail__type__dynamic__path_inf___value___choice_nval = context.cast('choice');
									return resolve(context)
										.then(context => evaluate__state_group_checker(detach, 
											resolve(context)
												.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
												.then(context => context?.component_root.output.context())
												.then(context => context?.component_root.output.value())
												.then(context => context?.variant.name === 'choice' ? context.variant.definition as application.Cstate_group : undefined).result!, {
											expected: () => resolve(parent)
												.then(context => application__derivation_expression_tail__type__dynamic__path_inf___value___choice_nval).result!

											}))
										.then(context => context?.definitions.value_object).result;
								}
								case 'collection': {
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.value())
										.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined)
										.then(context => context?.definitions.value_object).result;
								}
								case 'leaf': {
									const application__derivation_expression_tail__type__dynamic__path_inf___value___leaf_nval = context.cast('leaf');
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_inf___value___leaf_nval)
										.then(context => {
											switch (context?.variant.name) {
												case 'file': {
													return resolve(context)
														.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
														.then(context => context?.component_root.output.context())
														.then(context => context?.component_root.output.value())
														.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
														.then(context => context?.variant.name === 'file' ? context.variant.definition as application.Cfile : undefined)
														.then(context => context?.definitions.leaf_value)
														.then(context => context?.definitions.value_object).result;
												}
												case 'reference': {
													const application__derivation_expression_tail__type__dynamic__path_inf___value___leaf___reference_nval = context.cast('reference');
													return resolve(context)
														.then(context => evaluate__node_type_validator(detach, 
															resolve(context)
																.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
																.then(context => context?.component_root.output.context())
																.then(context => context?.component_root.output.value())
																.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!, {
															expected: () => resolve(parent)
																.then(context => application__derivation_expression_tail__type__dynamic__path_inf___value___leaf___reference_nval)
																.then(context => context?.component_root.output.definer())
																.then(context => context?.component_root.output.referenced_node_type()).result!

															}))
														.then(context => context?.definitions.value_object).result;
												}
												case 'scalar': {
													const application__derivation_expression_tail__type__dynamic__path_inf___value___leaf___scalar_nval = context.cast('scalar');
													return resolve(context)
														.then(context => application__derivation_expression_tail__type__dynamic__path_inf___value___leaf___scalar_nval)
														.then(context => {
															switch (context?.variant.name) {
																case 'number': {
																	const application__derivation_expression_tail__type__dynamic__path_inf___value___leaf___scalar___number_nval = context.cast('number');
																	return resolve(context)
																		.then(context => evaluate__number_checker(detach, 
																			resolve(context)
																				.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
																				.then(context => context?.component_root.output.context())
																				.then(context => context?.component_root.output.value())
																				.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
																				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
																				.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Cnumber_value : undefined).result!, {
																			target: () => resolve(parent)
																				.then(context => application__derivation_expression_tail__type__dynamic__path_inf___value___leaf___scalar___number_nval).result!

																			}))
																		.then(context => context?.definitions.number_value)
																		.then(context => context?.definitions.scalar_value)
																		.then(context => context?.definitions.leaf_value)
																		.then(context => context?.definitions.value_object).result;
																}
																case 'state choice': {
																	return resolve(context)
																		.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
																		.then(context => context?.component_root.output.context())
																		.then(context => context?.component_root.output.value())
																		.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
																		.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
																		.then(context => context?.variant.name === 'state choice' ? context.variant.definition as application.Cstate_group : undefined)
																		.then(context => context?.definitions.scalar_value)
																		.then(context => context?.definitions.leaf_value)
																		.then(context => context?.definitions.value_object).result;
																}
																case 'text': {
																	return resolve(context)
																		.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
																		.then(context => context?.component_root.output.context())
																		.then(context => context?.component_root.output.value())
																		.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
																		.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
																		.then(context => context?.variant.name === 'text' ? context.variant.definition as application.Ctext__type__property : undefined)
																		.then(context => context?.definitions.scalar_value)
																		.then(context => context?.definitions.leaf_value)
																		.then(context => context?.definitions.value_object).result;
																}
																case undefined: return undefined;
																default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
															};
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case 'node': {
									return resolve(context)
										.then(context => application__derivation_expression_tail__type__dynamic__path_nval)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.value())
										.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined)
										.then(context => context?.definitions.value_object).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
	}
}
export namespace Cnumber__type__derivation_expression_tail {
	export class Dexpression extends Cnumber_expression {
		constructor(data:Tnumber__type__derivation_expression_tail['expression'], parent:Cnumber__type__derivation_expression_tail) {
			super(data, parent, {
				support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.support()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.number()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Creference__type__derivation_expression_tail {
	export class Dtype extends StateGroup<
		{ name: 'branch', node:Cbranch, init:Tbranch}|
		{ name: 'ordered graph node', node:Cordered_graph_node, init:Tordered_graph_node}> {
		protected initializer(state: 'branch'|'ordered graph node') {
			switch (state) {
				case 'branch': return (init:Tbranch, parent:Creference__type__derivation_expression_tail) => new Cbranch(init, parent);
				case 'ordered graph node': return (init:Tordered_graph_node, parent:Creference__type__derivation_expression_tail) => new Cordered_graph_node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'branch'|'ordered graph node') {
			switch (state) {
				case 'branch': return finalize_branch;
				case 'ordered graph node': return finalize_ordered_graph_node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference__type__derivation_expression_tail['type'], parent:Creference__type__derivation_expression_tail) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbranch {
	export class Dbranch extends Reference<application.Cbranches__no,string> {
		public readonly inferences:{
			normalized_dependency_chain: () => application.Cdependency__application
		}

		constructor(data:string, $this:Cbranch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.derived_collection_without_key_constraint())
				.then(context => {
					const entry = context?.properties.branches.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				normalized_dependency_chain: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__branch__branch_nval = $this.properties.branch.ref;
					return resolve($this.properties.branch.ref)
						.then(context => evaluate__inline_dependency_chain(detach, 
							resolve(context)
								.then(context => application__derivation_expression_tail__type__reference__type__branch__branch_nval)
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							target: () => resolve($this)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.inferences.reference())
								.then(context => context?.properties.collection_path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!

							})).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'branch'`; }
	}
	export class Dexpression extends Csingular_variablized_object_path {
		public readonly inferences:{
			dependency: () => application.Cdependency__application,
			node: () => application.Cnode__application,
			node_type: () => application.Cnode__application
		}
		constructor(data:Tbranch['expression'], parent:Cbranch) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
			this.inferences = {
				dependency: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__branch__expression_nval = this;
					return resolve(this)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(context => application__derivation_expression_tail__type__reference__type__branch__expression_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.branch?.inferences.normalized_dependency_chain()).result!

							})).result!;
				})
				,
				node: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__branch__expression_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression_tail__type__reference__type__branch__expression_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__branch__expression_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.expression.inferences.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.properties.branch?.ref)
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cordered_graph_node {
	export class Dcollection_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			cardinality: () => application.Clower_bound_cardinality,
			collection: () => application.Ccollection_object,
			dependency: () => application.Cdependency__application,
			node_type: () => application.Cnode__application,
			source_collection: () => application.Ccollection__type__property,
			unfiltered_collection: () => application.Cmandatory__existence
		}
		constructor(data:Tordered_graph_node['collection path'], parent:Cordered_graph_node) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'collection path')
			this.inferences = {
				cardinality: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.collection_path.inferences.collection())
								.then(context => context?.component_root.output.lower_bound_cardinality()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(() => application.Clower_bound_cardinality.Pone).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				collection: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval = this;
					return resolve(this)
						.then(context => application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined).result!;
				})
				,
				dependency: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval = this;
					return resolve(this)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(context => application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.inferences.reference())
								.then(context => context?.properties.collection_path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!

							})).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.collection_path.inferences.source_collection())
								.then(context => context?.properties.node).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.inferences.reference())
								.then(context => context?.component_root.output.referenced_node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				source_collection: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.collection_path.inferences.collection())
						.then(context => context?.component_root.output.source_collection())
						.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
				})
				,
				unfiltered_collection: cache((detach:boolean) => {
					const application__derivation_expression_tail__type__reference__type__ordered_graph_node__collection_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.collection_path.inferences.collection())
						.then(context => context?.component_root.output.source_entry_existence())
						.then(context => context?.variant.name === 'mandatory' ? context.variant.definition as application.Cmandatory__existence : undefined).result!;
				})

			}
		}
	}
	export class Dordered_graph extends Reference<application.Cordered,string> {

		constructor(data:string, $this:Cordered_graph_node) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.collection_path?.inferences.source_collection())
				.then(context => context?.properties.graph_constraints)
				.then(context => {
					const entry = context?.properties.graphs.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'ordered') {
							return context.properties.type.state.node as application.Cordered;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ordered graph'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'sink', node:Csink, init:Tsink}|
		{ name: 'source', node:Csource, init:Tsource}> {
		protected initializer(state: 'sink'|'source') {
			switch (state) {
				case 'sink': return (init:Tsink, parent:Cordered_graph_node) => new Csink(init, parent);
				case 'source': return (init:Tsource, parent:Cordered_graph_node) => new Csource(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'sink'|'source') {
			switch (state) {
				case 'sink': return finalize_sink;
				case 'source': return finalize_source;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tordered_graph_node['type'], parent:Cordered_graph_node) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate__type__derivation_expression_tail {
	export class Dinitializer extends Cstate_initializer {
		constructor(data:Tstate__type__derivation_expression_tail['initializer'], parent:Cstate__type__derivation_expression_tail) {
			super(data, parent, {
				state_group: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.state_group()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'initializer')
		}
	}
}
export namespace Ctext__type__derivation_expression_tail {
	export class Dtype extends StateGroup<
		{ name: 'concatenation', node:Cconcatenation__type__text__type__derivation_expression_tail, init:Tconcatenation__type__text__type__derivation_expression_tail}|
		{ name: 'static', node:Cstatic__type__text__type__derivation_expression_tail, init:Tstatic__type__text__type__derivation_expression_tail}> {
		protected initializer(state: 'concatenation'|'static') {
			switch (state) {
				case 'concatenation': return (init:Tconcatenation__type__text__type__derivation_expression_tail, parent:Ctext__type__derivation_expression_tail) => new Cconcatenation__type__text__type__derivation_expression_tail(init, parent);
				case 'static': return (init:Tstatic__type__text__type__derivation_expression_tail, parent:Ctext__type__derivation_expression_tail) => new Cstatic__type__text__type__derivation_expression_tail(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'concatenation'|'static') {
			switch (state) {
				case 'concatenation': return finalize_concatenation__type__text__type__derivation_expression_tail;
				case 'static': return finalize_static__type__text__type__derivation_expression_tail;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__derivation_expression_tail['type'], parent:Ctext__type__derivation_expression_tail) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cconcatenation__type__text__type__derivation_expression_tail {
	export class Dexpression extends Cderivation_expression_list {
		constructor(data:Tconcatenation__type__text__type__derivation_expression_tail['expression'], parent:Cconcatenation__type__text__type__derivation_expression_tail) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cstatic__type__text__type__derivation_expression_tail {
}
export namespace Cdescendant_base_property_path {
	export class Dhead extends Cobject_path_tail {
		public readonly inferences:{
			node: () => application.Cnode__application
		}
		constructor(data:Tdescendant_base_property_path['head'], parent:Cdescendant_base_property_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node())
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context_node())
							.then(context => context?.component_root.output.entity())
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cdirection.Punbounded).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context_node())
							.then(context => context?.component_root.output.entity())
							.then(context => context?.definitions.relative_object_location).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context_node())
							.then(context => context?.properties.permissions_definition)
							.then(context => context?.component_root.output.value_source()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Crecursion_dependence.Pnone).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccomputation_phase.Pzero).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pinference)
					.then(context => context?.definitions.value_source).result!)
			}, 'head')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__descendant_base_property_path__head_nval = this;
					return resolve(this)
						.then(context => application__descendant_base_property_path__head_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			base_value: () => application.Cbase_value_source
		}

		constructor(data:string, $this:Cdescendant_base_property_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head?.inferences.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__descendant_base_property_path__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => application__descendant_base_property_path__property_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Cdescendant_object_path {
	export class Dpath extends Cobject_path_tail {
		constructor(data:Tdescendant_object_path['path'], parent:Cdescendant_object_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'path')
		}
	}
}
export namespace Cdirection_annotation {
	export class Ddirection extends StateGroup<
		{ name: 'downstream', node:Cdownstream__direction__direction_annotation, init:Tdownstream__direction__direction_annotation}|
		{ name: 'upstream', node:Cupstream__direction__direction_annotation, init:Tupstream__direction__direction_annotation}> {
		protected initializer(state: 'downstream'|'upstream') {
			switch (state) {
				case 'downstream': return (init:Tdownstream__direction__direction_annotation, parent:Cdirection_annotation) => new Cdownstream__direction__direction_annotation(init, parent);
				case 'upstream': return (init:Tupstream__direction__direction_annotation, parent:Cdirection_annotation) => new Cupstream__direction__direction_annotation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'downstream'|'upstream') {
			switch (state) {
				case 'downstream': return finalize_downstream__direction__direction_annotation;
				case 'upstream': return finalize_upstream__direction__direction_annotation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdirection_annotation['direction'], parent:Cdirection_annotation) {
			super(data, parent, 'direction');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'direction'`; }
	}
}
export namespace Centry_reference_definition {
	export class Dcollection_path extends Cvariablized_object_path {
		public readonly inferences:{
			location: () => application.Crelative_object_location
		}
		constructor(data:Tentry_reference_definition['collection path'], parent:Centry_reference_definition) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.reference_definer())
					.then(context => context?.component_root.output.recursion())
					.then(context => context?.component_root.output.location()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.reference_definer())
					.then(context => context?.component_root.output.direction())
					.then(context => context?.component_root.output.direction()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection_existence()).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.required_value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'collection path')
			this.inferences = {
				location: cache((detach:boolean) => {
					const application__entry_reference_definition__collection_path_nval = this;
					return resolve(this)
						.then(context => evaluate__merge_locations(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.root).result!, {
							context_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.collection_path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.reference_definer())
								.then(context => context?.component_root.output.recursion())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.type?.state.node.output.collection())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							reference_context_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.type?.state.node.output.entry_location()).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.defining_property())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!

							})).result!;
				})

			}
		}
	}
	export class Dtail extends Cdescendant_object_path {
		public readonly inferences:{
			node: () => application.Cnode__application
		}
		constructor(data:Tentry_reference_definition['tail'], parent:Centry_reference_definition) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.collection())
					.then(context => context?.properties.node)
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.type?.state.node.output.collection())
							.then(context => {
								const conv_context = resolve(context).result!;
								return new Cdependency_definition({name: 'collection step', definition: conv_context},{
								location: () => resolve(parent)
									.then(() => parent)
									.then(context => context?.properties.type?.state.node.output.entry_location()).result!
								, 
								parent: () => resolve(parent)
									.then(() => parent)
									.then(context => context?.properties.collection_path)
									.then(context => context?.component_root.output.context())
									.then(context => context?.component_root.output.dependency()).result!

								});
							})
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.definer())
							.then(context => context?.component_root.output.reference_definer())
							.then(context => context?.component_root.output.direction())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.definer())
							.then(context => context?.component_root.output.reference_definer())
							.then(context => context?.component_root.output.behaviour())
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path?.inferences.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.reference_definer())
					.then(context => context?.component_root.output.recursion())
					.then(context => context?.component_root.output.location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.required_value_source()).result!)
			}, 'tail')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__entry_reference_definition__tail_nval = this;
					return resolve(this)
						.then(context => application__entry_reference_definition__tail_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'sibling', node:Csibling__type__entry_reference_definition, init:Tsibling__type__entry_reference_definition}|
		{ name: 'simple', node:Csimple__type__entry_reference_definition, init:Tsimple__type__entry_reference_definition}> {
		protected initializer(state: 'sibling'|'simple') {
			switch (state) {
				case 'sibling': return (init:Tsibling__type__entry_reference_definition, parent:Centry_reference_definition) => new Csibling__type__entry_reference_definition(init, parent);
				case 'simple': return (init:Tsimple__type__entry_reference_definition, parent:Centry_reference_definition) => new Csimple__type__entry_reference_definition(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'sibling'|'simple') {
			switch (state) {
				case 'sibling': return finalize_sibling__type__entry_reference_definition;
				case 'simple': return finalize_simple__type__entry_reference_definition;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry_reference_definition['type'], parent:Centry_reference_definition) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Csibling__type__entry_reference_definition {
	export class Ddefinition extends Csibling_reference_definition {
		public readonly inferences:{
			collection_managing_source: () => application.Cvalue_source,
			inversion_upper_bound_cardinality: () => application.Cupper_bound_cardinality
		}
		constructor(data:Tsibling__type__entry_reference_definition['definition'], parent:Csibling__type__entry_reference_definition) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.collection()).result!),
				definer_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				definer_value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				existence: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.reference_definer())
					.then(context => context?.component_root.output.behaviour())
					.then(context => context?.component_root.output.existence()).result!)
			}, 'definition')
			this.inferences = {
				collection_managing_source: cache((detach:boolean) => {
					const application__entry_reference_definition__type__sibling__definition_nval = this;
					return resolve(this)
						.then(context => evaluate__value_source_checker(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.collection())
								.then(context => context?.parent)
								.then(context => context?.properties.type?.state.node.output.value_source()).result!, {
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.inferences.collection())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.defining_property())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							value_source_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.required_value_source()).result!

							})).result!;
				})
				,
				inversion_upper_bound_cardinality: cache((detach:boolean) => {
					const application__entry_reference_definition__type__sibling__definition_nval = this;
					return resolve(this)
						.then(context => application__entry_reference_definition__type__sibling__definition_nval)
						.then(context => context?.component_root.output.location())
						.then(context => {
							switch (context?.variant.name) {
								case 'graph step': {
									const application__entry_reference_definition__type__sibling__definition_inf___inversion_upper_bound_cardinality___graph_step_nval = context.cast('graph step');
									return resolve(context)
										.then(context => application__entry_reference_definition__type__sibling__definition_inf___inversion_upper_bound_cardinality___graph_step_nval)
										.then(context => context?.component_root.output.graph())
										.then(context => context?.properties.type)
										.then(context => {
											const state_group = context;
											switch (state_group?.state.name) {
												case 'acyclic': {
													return resolve(context)
														.then(() => parent)
														.then(() => application.Cupper_bound_cardinality.Pmany).result;
												}
												case 'ordered': {
													const application__entry_reference_definition__type__sibling__definition_inf___inversion_upper_bound_cardinality___graph_step___ordered_nval = state_group.cast('ordered');
													return resolve(context)
														.then(match_context => { 
															const expression_context = resolve(match_context)
															.then(context => {
																const left = resolve(context)
																	.then(context => application__entry_reference_definition__type__sibling__definition_inf___inversion_upper_bound_cardinality___graph_step___ordered_nval)
																	.then(context => context?.properties.ordering_property_path)
																	.then(context => context?.properties.ordering_states)
																	.then(context => context?.properties.edge_reference?.ref)
																	.then(context => context?.properties.expression).result;
																const right = resolve(context)
																	.then(() => parent)
																	.then(context => context?.parent).result;
																return left?.is(right!) ? left : undefined
															}).result;
															if (expression_context !== undefined) {
															return resolve(match_context)
															.then(() => parent)
															.then(() => application.Cupper_bound_cardinality.Pone).result
															} else {
																return resolve(match_context)
																.then(() => parent)
																.then(() => application.Cupper_bound_cardinality.Pmany).result
															}
														}).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
											};}).result;
								}
								case 'sibling or self step': {
									return resolve(context)
										.then(() => parent)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case 'sibling step': {
									return resolve(context)
										.then(() => parent)
										.then(() => application.Cupper_bound_cardinality.Pmany).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
	}
}
export namespace Centry_reference_selector {
	export class Ddefiner extends StateGroup<
		{ name: 'property', node:Cproperty__definer, init:Tproperty__definer}|
		{ name: 'rule', node:Crule, init:Trule}> {
		protected initializer(state: 'property'|'rule') {
			switch (state) {
				case 'property': return (init:Tproperty__definer, parent:Centry_reference_selector) => new Cproperty__definer(init, parent);
				case 'rule': return (init:Trule, parent:Centry_reference_selector) => new Crule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'property'|'rule') {
			switch (state) {
				case 'property': return finalize_property__definer;
				case 'rule': return finalize_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry_reference_selector['definer'], parent:Centry_reference_selector) {
			super(data, parent, 'definer');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'definer'`; }
	}
}
export namespace Cproperty__definer {
	export class Dproperty extends Reference<application.Cyes__has_reference,string> {

		constructor(data:string, $this:Cproperty__definer) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.has_reference.state.name === 'yes') {
							return context.properties.has_reference.state.node as application.Cyes__has_reference;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Crule {
	export class Dproperty extends Reference<application.Cyes__has_reference,string> {

		constructor(data:string, $this:Crule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.has_reference.state.name === 'yes') {
							return context.properties.has_reference.state.node as application.Cyes__has_reference;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			entry_step: () => application.Centry_step_definition
		}

		constructor(data:string, $this:Crule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property?.ref)
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				entry_step: cache((detach:boolean) => {
					const application__entry_reference_selector__definer__rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => application__entry_reference_selector__definer__rule__rule_nval)
						.then(context => context?.properties.type?.state.node.output.step_type())
						.then(context => context?.variant.name === 'entry step' ? context.variant.definition as application.Centry_step_definition : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cflatten_expression {
	export class Dhead extends Cgroup_ancestor_node_path {
		constructor(data:Tflatten_expression['head'], parent:Cflatten_expression) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'head')
		}
	}
	export class Dpath extends Cplural_descendant_node_path {
		constructor(data:Tflatten_expression['path'], parent:Cflatten_expression) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.result_node())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnode_navigation_context({name: 'node', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.head)
							.then(context => context?.component_root.output.result_node())
							.then(context => context?.component_root.output.entity())
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cdirection.Punbounded).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.head)
							.then(context => context?.component_root.output.result_node())
							.then(context => context?.component_root.output.entity())
							.then(context => context?.definitions.relative_object_location).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.this_property())
							.then(context => context?.properties.type?.state.node.output.value_source()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				lower_bound_cardinality: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Clower_bound_cardinality.Pone).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccomputation_phase.Pzero).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				upper_bound_cardinality: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cupper_bound_cardinality.Pone).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'path')
		}
	}
}
export namespace Cgraph_constraints_definition {
	export class Dgraphs extends AlanDictionary<{ node:Cgraphs, init:Tgraphs},Cgraph_constraints_definition> {
		protected initialize(parent:Cgraph_constraints_definition, key:string, entry_init:Tgraphs) { return new Cgraphs(key, entry_init, parent); }
		protected finalize = finalize_graphs
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graphs'`; }
		constructor(data:Tgraph_constraints_definition['graphs'], parent:Cgraph_constraints_definition) {
			super(data, parent);
		}
	}
}
export namespace Cgraphs {
	export class Dtype extends StateGroup<
		{ name: 'acyclic', node:Cacyclic, init:Tacyclic}|
		{ name: 'ordered', node:Cordered, init:Tordered}> {
		protected initializer(state: 'acyclic'|'ordered') {
			switch (state) {
				case 'acyclic': return (init:Tacyclic, parent:Cgraphs) => new Cacyclic(init, parent);
				case 'ordered': return (init:Tordered, parent:Cgraphs) => new Cordered(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'acyclic'|'ordered') {
			switch (state) {
				case 'acyclic': return finalize_acyclic;
				case 'ordered': return finalize_ordered;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgraphs['type'], parent:Cgraphs) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dvalue_type extends StateGroup<
		{ name: 'base', node:Cbase__value_type, init:Tbase__value_type}> {
		protected initializer(state: 'base') {
			switch (state) {
				case 'base': return (init:Tbase__value_type, parent:Cgraphs) => new Cbase__value_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base') {
			switch (state) {
				case 'base': return finalize_base__value_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgraphs['value type'], parent:Cgraphs) {
			super(data, parent, 'value type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value type'`; }
	}
}
export namespace Cordered {
}
export namespace Cordering_property_path {
	export class Dordering_state_group_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			base_value: () => application.Cbase__type__state_group,
			state_group: () => application.Cstate_group
		}
		constructor(data:Tordering_property_path['ordering state group path'], parent:Cordering_property_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.properties.node).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'ordering state group path')
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_state_group_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.ordering_state_group_path.inferences.state_group())
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__state_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				state_group: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_state_group_path_nval = this;
					return resolve(this)
						.then(context => application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_state_group_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return resolve(context.properties.type.state.node as application.Cstate_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Cordering_states {
	export class Dedge_reference extends Reference<application.Cyes__has_reference,string> {
		public readonly inferences:{
			base_value: () => application.Cbase__type__text,
			graph_edge: () => application.Cyes__graph_participation,
			mandatory_reference: () => application.Cmandatory__type,
			ordered_graph_edge: () => application.Cordered,
			sibling_edge: () => application.Csibling__type__entry_reference_definition
		}

		constructor(data:string, $this:Cordering_states) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.edge_state?.ref)
				.then(context => context?.properties.node)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.has_reference.state.name === 'yes') {
							return context.properties.has_reference.state.node as application.Cyes__has_reference;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval = $this.properties.edge_reference.ref;
					return resolve($this.properties.edge_reference.ref)
						.then(context => application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval)
						.then(context => context?.parent)
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				graph_edge: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval = $this.properties.edge_reference.ref;
					return resolve($this.properties.edge_reference.ref)
						.then(() => $this.properties.edge_reference.inferences.sibling_edge())
						.then(context => context?.properties.definition)
						.then(context => {
							if (context?.properties.graph_participation.state.name === 'yes') {
								return resolve(context.properties.graph_participation.state.node as application.Cyes__graph_participation).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				mandatory_reference: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval = $this.properties.edge_reference.ref;
					return resolve($this.properties.edge_reference.ref)
						.then(context => application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval)
						.then(context => context?.properties.behaviour)
						.then(context => {
							if (context?.properties.type.state.name === 'mandatory') {
								return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				ordered_graph_edge: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval = $this.properties.edge_reference.ref;
					return resolve($this.properties.edge_reference.ref)
						.then(() => $this.properties.edge_reference.inferences.graph_edge())
						.then(context => context?.properties.graph_traversal)
						.then(context => context?.component_root.output.graph())
						.then(context => {
							if (context?.properties.type.state.name === 'ordered') {
								return resolve(context.properties.type.state.node as application.Cordered).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				sibling_edge: cache((detach:boolean) => {
					const application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval = $this.properties.edge_reference.ref;
					return resolve($this.properties.edge_reference.ref)
						.then(context => application__graph_constraints_definition__graphs__type__ordered__ordering_property_path__ordering_states__edge_reference_nval)
						.then(context => context?.properties.expression)
						.then(context => {
							if (context?.properties.type.state.name === 'sibling') {
								return resolve(context.properties.type.state.node as application.Csibling__type__entry_reference_definition).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edge reference'`; }
	}
	export class Dedge_state extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cordering_states) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.sink_state?.ref)
				.then(context => {
					const entry = context?.parent.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edge state'`; }
	}
	export class Dsink_state extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cordering_states) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.ordering_state_group_path?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'sink state'`; }
	}
}
export namespace Cgraph_traversal_definition {
	export class Dgraph extends Reference<application.Cgraphs,string> {

		constructor(data:string, $this:Cgraph_traversal_definition) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.collection())
				.then(context => context?.properties.graph_constraints)
				.then(context => {
					const entry = context?.properties.graphs.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph'`; }
	}
	export class Dtraversal_type extends StateGroup<
		{ name: 'base order', node:Cbase_order, init:Tbase_order}|
		{ name: 'inverse order', node:Cinverse_order, init:Tinverse_order}> {
		protected initializer(state: 'base order'|'inverse order') {
			switch (state) {
				case 'base order': return (init:Tbase_order, parent:Cgraph_traversal_definition) => new Cbase_order(init, parent);
				case 'inverse order': return (init:Tinverse_order, parent:Cgraph_traversal_definition) => new Cinverse_order(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base order'|'inverse order') {
			switch (state) {
				case 'base order': return finalize_base_order;
				case 'inverse order': return finalize_inverse_order;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgraph_traversal_definition['traversal type'], parent:Cgraph_traversal_definition) {
			super(data, parent, 'traversal type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'traversal type'`; }
	}
}
export namespace Cgraph_traversal_selector {
	export class Dancestor_path extends Cancestor_node_path {
		public readonly inferences:{
			base_collection: () => application.Cbase__type__collection,
			collection: () => application.Ccollection__type__property
		}
		constructor(data:Tgraph_traversal_selector['ancestor path'], parent:Cgraph_traversal_selector) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!)
			}, 'ancestor path')
			this.inferences = {
				base_collection: cache((detach:boolean) => {
					const application__graph_traversal_selector__ancestor_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.ancestor_path.inferences.collection())
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__collection).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				collection: cache((detach:boolean) => {
					const application__graph_traversal_selector__ancestor_path_nval = this;
					return resolve(this)
						.then(context => application__graph_traversal_selector__ancestor_path_nval)
						.then(context => context?.component_root.output.node_type())
						.then(context => context?.component_root.output.location())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
				})

			}
		}
	}
	export class Dgraph_traversal extends Cgraph_traversal_definition {
		constructor(data:Tgraph_traversal_selector['graph traversal'], parent:Cgraph_traversal_selector) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.ancestor_path?.inferences.collection()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'graph traversal')
		}
	}
	export class Dhas_tail extends StateGroup<
		{ name: 'no', node:Cno__has_tail__graph_traversal_selector, init:Tno__has_tail__graph_traversal_selector}|
		{ name: 'yes', node:Cyes__has_tail__graph_traversal_selector, init:Tyes__has_tail__graph_traversal_selector}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_tail__graph_traversal_selector, parent:Cgraph_traversal_selector) => new Cno__has_tail__graph_traversal_selector(init, parent);
				case 'yes': return (init:Tyes__has_tail__graph_traversal_selector, parent:Cgraph_traversal_selector) => new Cyes__has_tail__graph_traversal_selector(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_tail__graph_traversal_selector;
				case 'yes': return finalize_yes__has_tail__graph_traversal_selector;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgraph_traversal_selector['has tail'], parent:Cgraph_traversal_selector) {
			super(data, parent, 'has tail');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has tail'`; }
	}
}
export namespace Cyes__has_tail__graph_traversal_selector {
	export class Dtail extends Cgraph_traversal_selector {
		constructor(data:Tyes__has_tail__graph_traversal_selector['tail'], parent:Cyes__has_tail__graph_traversal_selector) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.ancestor_path?.inferences.base_collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'tail')
		}
	}
}
export namespace Cgroup_ancestor_node_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__group_ancestor_node_path, init:Tno__has_steps__group_ancestor_node_path}|
		{ name: 'yes', node:Cyes__has_steps__group_ancestor_node_path, init:Tyes__has_steps__group_ancestor_node_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__group_ancestor_node_path, parent:Cgroup_ancestor_node_path) => new Cno__has_steps__group_ancestor_node_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__group_ancestor_node_path, parent:Cgroup_ancestor_node_path) => new Cyes__has_steps__group_ancestor_node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__group_ancestor_node_path;
				case 'yes': return finalize_yes__has_steps__group_ancestor_node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgroup_ancestor_node_path['has steps'], parent:Cgroup_ancestor_node_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__group_ancestor_node_path {
	export class Dtail extends Cgroup_ancestor_node_path {
		constructor(data:Tyes__has_steps__group_ancestor_node_path['tail'], parent:Cyes__has_steps__group_ancestor_node_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'tail')
		}
	}
}
export namespace Cidentity_initializer {
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tidentity_initializer['initializer'], parent:Cidentity_initializer) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.command_initialization_operation)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cbase_value_source.Pinternal).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.identities_collection())
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
}
export namespace Cinterface_instance_initializer {
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tinterface_instance_initializer['initializer'], parent:Cinterface_instance_initializer) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.command_initialization_operation)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.interface_())
					.then(context => context?.parent)
					.then(context => context?.definitions.external_source)
					.then(context => context?.definitions.base_value_source).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.interface_())
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
}
export namespace Citem_permissions_definition {
	export class Dcreate_permission extends StateGroup<
		{ name: 'explicit', node:Cexplicit__create_permission, init:Texplicit__create_permission}|
		{ name: 'inherited', node:Cinherited__create_permission, init:Tinherited__create_permission}> {
		protected initializer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return (init:Texplicit__create_permission, parent:Citem_permissions_definition) => new Cexplicit__create_permission(init, parent);
				case 'inherited': return (init:Tinherited__create_permission, parent:Citem_permissions_definition) => new Cinherited__create_permission(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return finalize_explicit__create_permission;
				case 'inherited': return finalize_inherited__create_permission;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Titem_permissions_definition['create permission'], parent:Citem_permissions_definition) {
			super(data, parent, 'create permission');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'create permission'`; }
	}
	export class Ddelete_permission extends StateGroup<
		{ name: 'explicit', node:Cexplicit__delete_permission, init:Texplicit__delete_permission}|
		{ name: 'inherited', node:Cinherited__delete_permission, init:Tinherited__delete_permission}> {
		protected initializer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return (init:Texplicit__delete_permission, parent:Citem_permissions_definition) => new Cexplicit__delete_permission(init, parent);
				case 'inherited': return (init:Tinherited__delete_permission, parent:Citem_permissions_definition) => new Cinherited__delete_permission(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return finalize_explicit__delete_permission;
				case 'inherited': return finalize_inherited__delete_permission;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Titem_permissions_definition['delete permission'], parent:Citem_permissions_definition) {
			super(data, parent, 'delete permission');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'delete permission'`; }
	}
}
export namespace Cexplicit__create_permission {
	export class Dpermission extends Cpermission {
		constructor(data:Texplicit__create_permission['permission'], parent:Cexplicit__create_permission) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.property())
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'permission')
		}
	}
}
export namespace Cexplicit__delete_permission {
	export class Dpermission extends Cpermission {
		public readonly inferences:{
			valid_source: () => application.Cvalue_source
		}
		constructor(data:Texplicit__delete_permission['permission'], parent:Cexplicit__delete_permission) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.item_node_type()).result!)
			}, 'permission')
			this.inferences = {
				valid_source: cache((detach:boolean) => {
					const application__item_permissions_definition__delete_permission__explicit__permission_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__item_permissions_definition__delete_permission__explicit__permission_nval)
								.then(context => context?.component_root.output.source())
								.then(context => context?.definitions.base_value_source)
								.then(context => context?.definitions.value_source).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.create_permission?.state.node.output.source()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Clower_bound_cardinality_constraint {
	export class Dconstraint extends StateGroup<
		{ name: 'non empty', node:Cnon_empty, init:Tnon_empty}|
		{ name: 'none', node:Cnone__constraint, init:Tnone__constraint}> {
		protected initializer(state: 'non empty'|'none') {
			switch (state) {
				case 'non empty': return (init:Tnon_empty, parent:Clower_bound_cardinality_constraint) => new Cnon_empty(init, parent);
				case 'none': return (init:Tnone__constraint, parent:Clower_bound_cardinality_constraint) => new Cnone__constraint(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'non empty'|'none') {
			switch (state) {
				case 'non empty': return finalize_non_empty;
				case 'none': return finalize_none__constraint;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlower_bound_cardinality_constraint['constraint'], parent:Clower_bound_cardinality_constraint) {
			super(data, parent, 'constraint');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'constraint'`; }
	}
}
export namespace Cnamed_object_assignment {
	export class Dname extends StateGroup<
		{ name: 'explicit', node:Cexplicit__name__named_object_assignment, init:Texplicit__name__named_object_assignment}|
		{ name: 'implicit', node:Cimplicit__name__named_object_assignment, init:Timplicit__name__named_object_assignment}> {
		protected initializer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return (init:Texplicit__name__named_object_assignment, parent:Cnamed_object_assignment) => new Cexplicit__name__named_object_assignment(init, parent);
				case 'implicit': return (init:Timplicit__name__named_object_assignment, parent:Cnamed_object_assignment) => new Cimplicit__name__named_object_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return finalize_explicit__name__named_object_assignment;
				case 'implicit': return finalize_implicit__name__named_object_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnamed_object_assignment['name'], parent:Cnamed_object_assignment) {
			super(data, parent, 'name');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
}
export namespace Cexplicit__name__named_object_assignment {
	export class Dname extends Reference<application.Cnamed_objects__explicit__name__named_object_assignment,string> {
		public readonly inferences:{
			single_name: () => application.Cno__has_successor__named_objects__explicit__name__named_object_assignment
		}

		constructor(data:string, $this:Cexplicit__name__named_object_assignment) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.named_objects.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				single_name: cache((detach:boolean) => {
					const application__named_object_assignment__name__explicit__name_nval = $this.properties.name.ref;
					return resolve($this.properties.name.ref)
						.then(context => application__named_object_assignment__name__explicit__name_nval)
						.then(context => {
							if (context?.properties.has_successor.state.name === 'no') {
								return resolve(context.properties.has_successor.state.node as application.Cno__has_successor__named_objects__explicit__name__named_object_assignment).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
	export class Dnamed_objects extends AlanDictionary<{ node:Cnamed_objects__explicit__name__named_object_assignment, init:Tnamed_objects__explicit__name__named_object_assignment},Cexplicit__name__named_object_assignment> {
		protected initialize(parent:Cexplicit__name__named_object_assignment, key:string, entry_init:Tnamed_objects__explicit__name__named_object_assignment) { return new Cnamed_objects__explicit__name__named_object_assignment(key, entry_init, parent); }
		protected finalize = finalize_named_objects__explicit__name__named_object_assignment
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'named objects'`; }
		constructor(data:Texplicit__name__named_object_assignment['named objects'], parent:Cexplicit__name__named_object_assignment) {
			super(data, parent);
		}
	}
}
export namespace Cnamed_objects__explicit__name__named_object_assignment {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__named_objects__explicit__name__named_object_assignment, init:Tno__has_successor__named_objects__explicit__name__named_object_assignment}|
		{ name: 'yes', node:Cyes__has_successor__named_objects__explicit__name__named_object_assignment, init:Tyes__has_successor__named_objects__explicit__name__named_object_assignment}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__named_objects__explicit__name__named_object_assignment, parent:Cnamed_objects__explicit__name__named_object_assignment) => new Cno__has_successor__named_objects__explicit__name__named_object_assignment(init, parent);
				case 'yes': return (init:Tyes__has_successor__named_objects__explicit__name__named_object_assignment, parent:Cnamed_objects__explicit__name__named_object_assignment) => new Cyes__has_successor__named_objects__explicit__name__named_object_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__named_objects__explicit__name__named_object_assignment;
				case 'yes': return finalize_yes__has_successor__named_objects__explicit__name__named_object_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnamed_objects__explicit__name__named_object_assignment['has successor'], parent:Cnamed_objects__explicit__name__named_object_assignment) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
}
export namespace Cnamed_object_step {
	export class Dname extends StateGroup<
		{ name: 'explicit', node:Cexplicit__name__named_object_step, init:Texplicit__name__named_object_step}|
		{ name: 'implicit', node:Cimplicit__name__named_object_step, init:Timplicit__name__named_object_step}> {
		protected initializer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return (init:Texplicit__name__named_object_step, parent:Cnamed_object_step) => new Cexplicit__name__named_object_step(init, parent);
				case 'implicit': return (init:Timplicit__name__named_object_step, parent:Cnamed_object_step) => new Cimplicit__name__named_object_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return finalize_explicit__name__named_object_step;
				case 'implicit': return finalize_implicit__name__named_object_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnamed_object_step['name'], parent:Cnamed_object_step) {
			super(data, parent, 'name');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
}
export namespace Cexplicit__name__named_object_step {
	export class Dhead extends Cancestor_named_object_path {
		public readonly inferences:{
			assignment: () => application.Cnamed_object_assignment,
			explicit_name: () => application.Cexplicit__name__named_object_assignment
		}
		constructor(data:Texplicit__name__named_object_step['head'], parent:Cexplicit__name__named_object_step) {
			super(data, parent, {
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
			this.inferences = {
				assignment: cache((detach:boolean) => {
					const application__named_object_step__name__explicit__head_nval = this;
					return resolve(this)
						.then(context => application__named_object_step__name__explicit__head_nval)
						.then(context => context?.component_root.output.variable())
						.then(context => context?.variant.name === 'assigned' ? context.variant.definition as application.Cnamed_object_assignment : undefined).result!;
				})
				,
				explicit_name: cache((detach:boolean) => {
					const application__named_object_step__name__explicit__head_nval = this;
					return resolve(this)
						.then(() => parent.properties.head.inferences.assignment())
						.then(context => {
							if (context?.properties.name.state.name === 'explicit') {
								return resolve(context.properties.name.state.node as application.Cexplicit__name__named_object_assignment).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
	export class Dnamed_object extends Reference<application.Cnamed_objects__explicit__name__named_object_assignment,string> {

		constructor(data:string, $this:Cexplicit__name__named_object_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head?.inferences.explicit_name())
				.then(context => {
					const entry = context?.properties.named_objects.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'named object'`; }
	}
}
export namespace Cnode__application {
	export class Dattributes extends AlanTopology<{ node:Cattributes, init:Tattributes},Cnode__application,'downstream attributes'|'upstream attributes'> {
		protected graph_iterator(graph:('downstream attributes'|'upstream attributes')):(node:Cattributes) => Cattributes {
			switch (graph) {
				case 'downstream attributes': return (entry:Cattributes) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as application.Cyes__has_successor__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.attribute.ref).result!
				case 'upstream attributes': return (entry:Cattributes) => resolve(entry)
					.then(context => {
						if (context?.properties.has_predecessor.state.name === 'yes') {
							return context.properties.has_predecessor.state.node as application.Cyes__has_predecessor__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.attribute.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cnode__application, key:string, entry_init:Tattributes) { return new Cattributes(key, entry_init, parent); }
		protected finalize = finalize_attributes
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tnode__application['attributes'], parent:Cnode__application) {
			super(data, parent);
		}
	}
	export class Dhas_attributes extends StateGroup<
		{ name: 'no', node:Cno__has_attributes, init:Tno__has_attributes}|
		{ name: 'yes', node:Cyes__has_attributes, init:Tyes__has_attributes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_attributes, parent:Cnode__application) => new Cno__has_attributes(init, parent);
				case 'yes': return (init:Tyes__has_attributes, parent:Cnode__application) => new Cyes__has_attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_attributes;
				case 'yes': return finalize_yes__has_attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode__application['has attributes'], parent:Cnode__application) {
			super(data, parent, 'has attributes');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has attributes'`; }
	}
	export class Dpermissions_definition extends Cnode_permissions_definition {
		constructor(data:Tnode__application['permissions definition'], parent:Cnode__application) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source()).result!)
			}, 'permissions definition')
		}
	}
	export class Dtodo_definition extends Ctodo_definition {
		constructor(data:Tnode__application['todo definition'], parent:Cnode__application) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'todo definition')
		}
	}
}
export namespace Cattributes {
	export class Dhas_predecessor extends StateGroup<
		{ name: 'no', node:Cno__has_predecessor__attributes, init:Tno__has_predecessor__attributes}|
		{ name: 'yes', node:Cyes__has_predecessor__attributes, init:Tyes__has_predecessor__attributes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_predecessor__attributes, parent:Cattributes) => new Cno__has_predecessor__attributes(init, parent);
				case 'yes': return (init:Tyes__has_predecessor__attributes, parent:Cattributes) => new Cyes__has_predecessor__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_predecessor__attributes;
				case 'yes': return finalize_yes__has_predecessor__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['has predecessor'], parent:Cattributes) {
			super(data, parent, 'has predecessor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has predecessor'`; }
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__attributes, init:Tno__has_successor__attributes}|
		{ name: 'yes', node:Cyes__has_successor__attributes, init:Tyes__has_successor__attributes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__attributes, parent:Cattributes) => new Cno__has_successor__attributes(init, parent);
				case 'yes': return (init:Tyes__has_successor__attributes, parent:Cattributes) => new Cyes__has_successor__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__attributes;
				case 'yes': return finalize_yes__has_successor__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['has successor'], parent:Cattributes) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'action', node:Caction, init:Taction}|
		{ name: 'command', node:Ccommand__type, init:Tcommand__type}|
		{ name: 'property', node:Cproperty__type__attributes, init:Tproperty__type__attributes}|
		{ name: 'reference set', node:Creference_set__type__attributes, init:Treference_set__type__attributes}> {
		protected initializer(state: 'action'|'command'|'property'|'reference set') {
			switch (state) {
				case 'action': return (init:Taction, parent:Cattributes) => new Caction(init, parent);
				case 'command': return (init:Tcommand__type, parent:Cattributes) => new Ccommand__type(init, parent);
				case 'property': return (init:Tproperty__type__attributes, parent:Cattributes) => new Cproperty__type__attributes(init, parent);
				case 'reference set': return (init:Treference_set__type__attributes, parent:Cattributes) => new Creference_set__type__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'action'|'command'|'property'|'reference set') {
			switch (state) {
				case 'action': return finalize_action;
				case 'command': return finalize_command__type;
				case 'property': return finalize_property__type__attributes;
				case 'reference set': return finalize_reference_set__type__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['type'], parent:Cattributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_predecessor__attributes {
	export class Dattribute extends Reference<application.Cattributes,string> {

		constructor(data:string, $this:Cyes__has_predecessor__attributes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
}
export namespace Cyes__has_successor__attributes {
	export class Dattribute extends Reference<application.Cattributes,string> {

		constructor(data:string, $this:Cyes__has_successor__attributes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
}
export namespace Caction {
	export class Daction extends Cui_action {
		constructor(data:Taction['action'], parent:Caction) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'action')
		}
	}
	export class Dparameters extends Cnode__application {
		constructor(data:Taction['parameters'], parent:Caction) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.operation)
					.then(context => context?.definitions.root_location)
					.then(context => context?.definitions.node_location).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source)
					.then(context => context?.definitions.value_source).result!)
			}, 'parameters')
		}
	}
	export class Dui extends Cui_action_attribute {
		constructor(data:Taction['ui'], parent:Caction) {
			super(data, parent, 'ui')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Taction['variable assignment'], parent:Caction) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.parameters).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Ccommand__type {
	export class Dimplementation extends StateGroup<
		{ name: 'external', node:Cexternal, init:Texternal}|
		{ name: 'internal', node:Cinternal__implementation, init:Tinternal__implementation}> {
		protected initializer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return (init:Texternal, parent:Ccommand__type) => new Cexternal(init, parent);
				case 'internal': return (init:Tinternal__implementation, parent:Ccommand__type) => new Cinternal__implementation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'external'|'internal') {
			switch (state) {
				case 'external': return finalize_external;
				case 'internal': return finalize_internal__implementation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand__type['implementation'], parent:Ccommand__type) {
			super(data, parent, 'implementation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'implementation'`; }
	}
	export class Dparameters extends Cnode__application {
		constructor(data:Tcommand__type['parameters'], parent:Ccommand__type) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.operation)
					.then(context => context?.definitions.root_location)
					.then(context => context?.definitions.node_location).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source)
					.then(context => context?.definitions.value_source).result!)
			}, 'parameters')
		}
	}
	export class Dpermission_definition extends Ccommand_permission_definition {
		constructor(data:Tcommand__type['permission definition'], parent:Ccommand__type) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'permission definition')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'component', node:Ccomponent, init:Tcomponent}|
		{ name: 'global', node:Cglobal, init:Tglobal}> {
		protected initializer(state: 'component'|'global') {
			switch (state) {
				case 'component': return (init:Tcomponent, parent:Ccommand__type) => new Ccomponent(init, parent);
				case 'global': return (init:Tglobal, parent:Ccommand__type) => new Cglobal(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'component'|'global') {
			switch (state) {
				case 'component': return finalize_component;
				case 'global': return finalize_global;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand__type['type'], parent:Ccommand__type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cinternal__implementation {
	export class Dimplementation extends Ccommand__application {
		constructor(data:Tinternal__implementation['implementation'], parent:Cinternal__implementation) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'implementation')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Tinternal__implementation['variable assignment'], parent:Cinternal__implementation) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.parameters).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cglobal {
	export class Dui extends Cui_command_attribute {
		constructor(data:Tglobal['ui'], parent:Cglobal) {
			super(data, parent, 'ui')
		}
	}
}
export namespace Cproperty__type__attributes {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property, init:Tcollection__type__property}|
		{ name: 'file', node:Cfile, init:Tfile}|
		{ name: 'group', node:Cgroup__type__property, init:Tgroup__type__property}|
		{ name: 'number', node:Cnumber__type__property, init:Tnumber__type__property}|
		{ name: 'state group', node:Cstate_group, init:Tstate_group}|
		{ name: 'text', node:Ctext__type__property, init:Ttext__type__property}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property, parent:Cproperty__type__attributes) => new Ccollection__type__property(init, parent);
				case 'file': return (init:Tfile, parent:Cproperty__type__attributes) => new Cfile(init, parent);
				case 'group': return (init:Tgroup__type__property, parent:Cproperty__type__attributes) => new Cgroup__type__property(init, parent);
				case 'number': return (init:Tnumber__type__property, parent:Cproperty__type__attributes) => new Cnumber__type__property(init, parent);
				case 'state group': return (init:Tstate_group, parent:Cproperty__type__attributes) => new Cstate_group(init, parent);
				case 'text': return (init:Ttext__type__property, parent:Cproperty__type__attributes) => new Ctext__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__property;
				case 'file': return finalize_file;
				case 'group': return finalize_group__type__property;
				case 'number': return finalize_number__type__property;
				case 'state group': return finalize_state_group;
				case 'text': return finalize_text__type__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__attributes['type'], parent:Cproperty__type__attributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property {
	export class Dcardinality_constraint extends Clower_bound_cardinality_constraint {
		constructor(data:Tcollection__type__property['cardinality constraint'], parent:Ccollection__type__property) {
			super(data, parent, 'cardinality constraint')
		}
	}
	export class Dgraph_constraints extends Cgraph_constraints_definition {
		constructor(data:Tcollection__type__property['graph constraints'], parent:Ccollection__type__property) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'graph constraints')
		}
	}
	export class Dkey_property extends Reference<application.Ctext__type__property,string> {

		constructor(data:string, $this:Ccollection__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.node)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'key property'`; }
	}
	export class Dnode extends Cnode__application {
		constructor(data:Tcollection__type__property['node'], parent:Ccollection__type__property) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entity).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_location).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.entry_value_source()).result!)
			}, 'node')
		}
	}
	export class Dpermissions extends Citem_permissions_definition {
		constructor(data:Tcollection__type__property['permissions'], parent:Ccollection__type__property) {
			super(data, parent, {
				item_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.node).result!),
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.entry_value_source()).result!)
			}, 'permissions')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__collection, init:Tbase__type__collection}|
		{ name: 'derived', node:Cderived__type__collection, init:Tderived__type__collection}> {
		protected initializer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return (init:Tbase__type__collection, parent:Ccollection__type__property) => new Cbase__type__collection(init, parent);
				case 'derived': return (init:Tderived__type__collection, parent:Ccollection__type__property) => new Cderived__type__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return finalize_base__type__collection;
				case 'derived': return finalize_derived__type__collection;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['type'], parent:Ccollection__type__property) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_collection_property {
		constructor(data:Tcollection__type__property['ui'], parent:Ccollection__type__property) {
			super(data, parent, {
				collection_property: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Cderived__type__collection {
	export class Dkey_constraint extends StateGroup<
		{ name: 'no', node:Cno__key_constraint, init:Tno__key_constraint}|
		{ name: 'yes', node:Cyes__key_constraint, init:Tyes__key_constraint}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__key_constraint, parent:Cderived__type__collection) => new Cno__key_constraint(init, parent);
				case 'yes': return (init:Tyes__key_constraint, parent:Cderived__type__collection) => new Cyes__key_constraint(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__key_constraint;
				case 'yes': return finalize_yes__key_constraint;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderived__type__collection['key constraint'], parent:Cderived__type__collection) {
			super(data, parent, 'key constraint');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'key constraint'`; }
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Tderived__type__collection['recursion'], parent:Cderived__type__collection) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
}
export namespace Cno__key_constraint {
	export class Dbranches extends AlanDictionary<{ node:Cbranches__no, init:Tbranches__no},Cno__key_constraint> {
		public readonly inferences:{
			combined_lower_bound_cardinality: () => application.Clower_bound_cardinality,
			computation_phase: () => application.Ccomputation_phase,
			lower_bound_cardinality: () => application.Clower_bound_cardinality
		}
		protected initialize(parent:Cno__key_constraint, key:string, entry_init:Tbranches__no) { return new Cbranches__no(key, entry_init, parent); }
		protected finalize = finalize_branches__no
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'branches'`; }
		constructor(data:Tno__key_constraint['branches'], parent:Cno__key_constraint) {
			super(data, parent);
			this.inferences = {
				combined_lower_bound_cardinality: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches_nval = this;
					return resolve(this)
						.then(reduce_context => 
							resolve(reduce_context)
							.then(context => application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches_nval)
							.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
							.then(set => set.map(entry => resolve(entry)
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.lower_bound_cardinality()).result))
								.then(set => set.reduce((prev:application.Clower_bound_cardinality, curr) => curr.variant.widening_index > prev!.variant.widening_index ? curr : prev, application.Clower_bound_cardinality.Pzero)).result).result!;
				})
				,
				computation_phase: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches_nval = this;
					return resolve(this)
						.then(reduce_context => 
							resolve(reduce_context)
							.then(context => application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches_nval)
							.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
							.then(set => set.map(entry => resolve(entry)
								.then(context => context?.properties.node_initializer?.inferences.computation_phase()).result))
								.then(set => set.reduce((prev:application.Ccomputation_phase, curr) => curr.variant.widening_index < prev!.variant.widening_index ? curr : prev, application.Ccomputation_phase.Pzero)).result).result!;
				})
				,
				lower_bound_cardinality: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.branches.inferences.combined_lower_bound_cardinality()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.properties.cardinality_constraint)
								.then(context => context?.component_root.output.cardinality()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
	}
	export class Dseparator extends StateGroup<
		{ name: 'colon', node:Ccolon, init:Tcolon}|
		{ name: 'dash', node:Cdash, init:Tdash}|
		{ name: 'dot', node:Cdot, init:Tdot}|
		{ name: 'greater than', node:Cgreater_than, init:Tgreater_than}|
		{ name: 'space', node:Cspace, init:Tspace}> {
		protected initializer(state: 'colon'|'dash'|'dot'|'greater than'|'space') {
			switch (state) {
				case 'colon': return (init:Tcolon, parent:Cno__key_constraint) => new Ccolon(init, parent);
				case 'dash': return (init:Tdash, parent:Cno__key_constraint) => new Cdash(init, parent);
				case 'dot': return (init:Tdot, parent:Cno__key_constraint) => new Cdot(init, parent);
				case 'greater than': return (init:Tgreater_than, parent:Cno__key_constraint) => new Cgreater_than(init, parent);
				case 'space': return (init:Tspace, parent:Cno__key_constraint) => new Cspace(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'colon'|'dash'|'dot'|'greater than'|'space') {
			switch (state) {
				case 'colon': return finalize_colon;
				case 'dash': return finalize_dash;
				case 'dot': return finalize_dot;
				case 'greater than': return finalize_greater_than;
				case 'space': return finalize_space;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__key_constraint['separator'], parent:Cno__key_constraint) {
			super(data, parent, 'separator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'separator'`; }
	}
}
export namespace Cbranches__no {
	export class Dexpression extends Cflatten_expression {
		constructor(data:Tbranches__no['expression'], parent:Cbranches__no) {
			super(data, parent, {
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'expression')
		}
	}
	export class Dnode_initializer extends Cnode_initializer {
		public readonly inferences:{
			computation_phase: () => application.Ccomputation_phase
		}
		constructor(data:Tbranches__no['node initializer'], parent:Cbranches__no) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.node).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'node initializer')
			this.inferences = {
				computation_phase: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches__node_initializer_nval = this;
					return resolve(this)
						.then(context => evaluate__computation_phase_merge(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.phase()).result!, {
							other_computation_phase: () => resolve(parent)
								.then(context => application__node__attributes__type__property__type__collection__type__derived__key_constraint__no__branches__node_initializer_nval)
								.then(context => context?.component_root.output.computation_phase()).result!

							})).result!;
				})

			}
		}
	}
	export class Dvariable_assignment extends Coptional_named_object_assignment {
		constructor(data:Tbranches__no['variable assignment'], parent:Cbranches__no) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.context())
					.then(context => context?.component_root.output.node())
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cdirection.Punbounded).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cderived_value_source.Pexpression)
							.then(context => context?.definitions.value_source).result!

						});
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cyes__key_constraint {
	export class Dexpression extends Cplural_reference_expression {
		constructor(data:Tyes__key_constraint['expression'], parent:Cyes__key_constraint) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!),
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.cardinality_constraint)
					.then(context => context?.component_root.output.cardinality()).result!)
			}, 'expression')
		}
	}
}
export namespace Cfile {
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__file, init:Tbase__type__file}|
		{ name: 'derived', node:Cderived__type__file, init:Tderived__type__file}> {
		protected initializer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return (init:Tbase__type__file, parent:Cfile) => new Cbase__type__file(init, parent);
				case 'derived': return (init:Tderived__type__file, parent:Cfile) => new Cderived__type__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return finalize_base__type__file;
				case 'derived': return finalize_derived__type__file;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile['type'], parent:Cfile) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_file_property {
		constructor(data:Tfile['ui'], parent:Cfile) {
			super(data, parent, {
				file_property: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Cderived__type__file {
	export class Dvalue_source extends StateGroup<
		{ name: 'expression', node:Cexpression__value_source__derived__type__file, init:Texpression__value_source__derived__type__file}|
		{ name: 'parameter', node:Cparameter__value_source__derived__type__file, init:Tparameter__value_source__derived__type__file}> {
		protected initializer(state: 'expression'|'parameter') {
			switch (state) {
				case 'expression': return (init:Texpression__value_source__derived__type__file, parent:Cderived__type__file) => new Cexpression__value_source__derived__type__file(init, parent);
				case 'parameter': return (init:Tparameter__value_source__derived__type__file, parent:Cderived__type__file) => new Cparameter__value_source__derived__type__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'parameter') {
			switch (state) {
				case 'expression': return finalize_expression__value_source__derived__type__file;
				case 'parameter': return finalize_parameter__value_source__derived__type__file;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderived__type__file['value source'], parent:Cderived__type__file) {
			super(data, parent, 'value source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value source'`; }
	}
}
export namespace Cexpression__value_source__derived__type__file {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Texpression__value_source__derived__type__file['expression'], parent:Cexpression__value_source__derived__type__file) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.leaf_value)
					.then(context => context?.definitions.value_object).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'expression')
		}
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Texpression__value_source__derived__type__file['recursion'], parent:Cexpression__value_source__derived__type__file) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
}
export namespace Cgroup__type__property {
	export class Dnode extends Cnode__application {
		constructor(data:Tgroup__type__property['node'], parent:Cgroup__type__property) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_location).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'node')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__group, init:Tbase__type__group}|
		{ name: 'derived', node:Cderived__type__group, init:Tderived__type__group}> {
		protected initializer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return (init:Tbase__type__group, parent:Cgroup__type__property) => new Cbase__type__group(init, parent);
				case 'derived': return (init:Tderived__type__group, parent:Cgroup__type__property) => new Cderived__type__group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return finalize_base__type__group;
				case 'derived': return finalize_derived__type__group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgroup__type__property['type'], parent:Cgroup__type__property) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_group_property {
		constructor(data:Tgroup__type__property['ui'], parent:Cgroup__type__property) {
			super(data, parent, {
				group_property: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Cnumber__type__property {
	export class Dbehaviour extends StateGroup<
		{ name: 'none', node:Cnone__behaviour, init:Tnone__behaviour}|
		{ name: 'timer', node:Ctimer, init:Ttimer}> {
		protected initializer(state: 'none'|'timer') {
			switch (state) {
				case 'none': return (init:Tnone__behaviour, parent:Cnumber__type__property) => new Cnone__behaviour(init, parent);
				case 'timer': return (init:Ttimer, parent:Cnumber__type__property) => new Ctimer(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'timer') {
			switch (state) {
				case 'none': return finalize_none__behaviour;
				case 'timer': return finalize_timer;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['behaviour'], parent:Cnumber__type__property) {
			super(data, parent, 'behaviour');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'behaviour'`; }
	}
	export class Dnumerical_type extends Reference<application.Cnumerical_types,string> {

		constructor(data:string, $this:Cnumber__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'numerical type'`; }
	}
	export class Dset_type extends StateGroup<
		{ name: 'integer', node:Cinteger__set_type, init:Tinteger__set_type}|
		{ name: 'positive', node:Cpositive__set_type, init:Tpositive__set_type}> {
		protected initializer(state: 'integer'|'positive') {
			switch (state) {
				case 'integer': return (init:Tinteger__set_type, parent:Cnumber__type__property) => new Cinteger__set_type(init, parent);
				case 'positive': return (init:Tpositive__set_type, parent:Cnumber__type__property) => new Cpositive__set_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'integer'|'positive') {
			switch (state) {
				case 'integer': return finalize_integer__set_type;
				case 'positive': return finalize_positive__set_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['set type'], parent:Cnumber__type__property) {
			super(data, parent, 'set type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'set type'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__number, init:Tbase__type__number}|
		{ name: 'derived', node:Cderived__type__number, init:Tderived__type__number}> {
		protected initializer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return (init:Tbase__type__number, parent:Cnumber__type__property) => new Cbase__type__number(init, parent);
				case 'derived': return (init:Tderived__type__number, parent:Cnumber__type__property) => new Cderived__type__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return finalize_base__type__number;
				case 'derived': return finalize_derived__type__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['type'], parent:Cnumber__type__property) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_number_property {
		constructor(data:Tnumber__type__property['ui'], parent:Cnumber__type__property) {
			super(data, parent, {
				number_property: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Ctimer {
	export class Dimplementation extends Ccommand__application {
		constructor(data:Ttimer['implementation'], parent:Ctimer) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'implementation')
		}
	}
}
export namespace Cbase__type__number {
	export class Dtype extends StateGroup<
		{ name: 'causal', node:Ccausal, init:Tcausal}|
		{ name: 'simple', node:Csimple__type__base, init:Tsimple__type__base}> {
		protected initializer(state: 'causal'|'simple') {
			switch (state) {
				case 'causal': return (init:Tcausal, parent:Cbase__type__number) => new Ccausal(init, parent);
				case 'simple': return (init:Tsimple__type__base, parent:Cbase__type__number) => new Csimple__type__base(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'causal'|'simple') {
			switch (state) {
				case 'causal': return finalize_causal;
				case 'simple': return finalize_simple__type__base;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbase__type__number['type'], parent:Cbase__type__number) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccausal {
	export class Dtype extends StateGroup<
		{ name: 'creation', node:Ccreation, init:Tcreation}|
		{ name: 'destruction', node:Cdestruction, init:Tdestruction}|
		{ name: 'mutation', node:Cmutation, init:Tmutation}> {
		protected initializer(state: 'creation'|'destruction'|'mutation') {
			switch (state) {
				case 'creation': return (init:Tcreation, parent:Ccausal) => new Ccreation(init, parent);
				case 'destruction': return (init:Tdestruction, parent:Ccausal) => new Cdestruction(init, parent);
				case 'mutation': return (init:Tmutation, parent:Ccausal) => new Cmutation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'creation'|'destruction'|'mutation') {
			switch (state) {
				case 'creation': return finalize_creation;
				case 'destruction': return finalize_destruction;
				case 'mutation': return finalize_mutation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcausal['type'], parent:Ccausal) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdestruction {
	export class Ddestruction_operation extends StateGroup<
		{ name: 'add lifetime', node:Cadd_lifetime, init:Tadd_lifetime}|
		{ name: 'set to lifetime', node:Cset_to_lifetime, init:Tset_to_lifetime}|
		{ name: 'subtract lifetime', node:Csubtract_lifetime, init:Tsubtract_lifetime}> {
		protected initializer(state: 'add lifetime'|'set to lifetime'|'subtract lifetime') {
			switch (state) {
				case 'add lifetime': return (init:Tadd_lifetime, parent:Cdestruction) => new Cadd_lifetime(init, parent);
				case 'set to lifetime': return (init:Tset_to_lifetime, parent:Cdestruction) => new Cset_to_lifetime(init, parent);
				case 'subtract lifetime': return (init:Tsubtract_lifetime, parent:Cdestruction) => new Csubtract_lifetime(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'add lifetime'|'set to lifetime'|'subtract lifetime') {
			switch (state) {
				case 'add lifetime': return finalize_add_lifetime;
				case 'set to lifetime': return finalize_set_to_lifetime;
				case 'subtract lifetime': return finalize_subtract_lifetime;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdestruction['destruction operation'], parent:Cdestruction) {
			super(data, parent, 'destruction operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'destruction operation'`; }
	}
	export class Dwatched_state extends Reference<application.Cstates__state_group,string> {
		public readonly inferences:{
			meta_property: () => application.Cnumber__type__property,
			watched_state: () => application.Cyes__record_lifetime
		}

		constructor(data:string, $this:Cdestruction) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.watched_stategroup?.inferences.stategroup())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				meta_property: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__number__type__base__type__causal__type__destruction__watched_state_nval = $this.properties.watched_state.ref;
					return resolve($this.properties.watched_state.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.watched_state.inferences.watched_state())
								.then(context => context?.properties.meta_property?.ref).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.parent).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				watched_state: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__number__type__base__type__causal__type__destruction__watched_state_nval = $this.properties.watched_state.ref;
					return resolve($this.properties.watched_state.ref)
						.then(context => application__node__attributes__type__property__type__number__type__base__type__causal__type__destruction__watched_state_nval)
						.then(context => {
							if (context?.properties.record_lifetime.state.name === 'yes') {
								return resolve(context.properties.record_lifetime.state.node as application.Cyes__record_lifetime).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'watched state'`; }
	}
	export class Dwatched_stategroup extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			stategroup: () => application.Cstate_group
		}

		constructor(data:string, $this:Cdestruction) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				stategroup: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__number__type__base__type__causal__type__destruction__watched_stategroup_nval = $this.properties.watched_stategroup.ref;
					return resolve($this.properties.watched_stategroup.ref)
						.then(context => application__node__attributes__type__property__type__number__type__base__type__causal__type__destruction__watched_stategroup_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'state group') {
										return context.properties.type.state.node as application.Cstate_group;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'watched stategroup'`; }
	}
}
export namespace Cmutation {
	export class Dwatched_property extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			base_value: () => application.Cbase_value_source,
			scalar_value: () => application.Cleaf_value
		}

		constructor(data:string, $this:Cmutation) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__number__type__base__type__causal__type__mutation__watched_property_nval = $this.properties.watched_property.ref;
					return resolve($this.properties.watched_property.ref)
						.then(context => application__node__attributes__type__property__type__number__type__base__type__causal__type__mutation__watched_property_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
				})
				,
				scalar_value: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__number__type__base__type__causal__type__mutation__watched_property_nval = $this.properties.watched_property.ref;
					return resolve($this.properties.watched_property.ref)
						.then(context => application__node__attributes__type__property__type__number__type__base__type__causal__type__mutation__watched_property_nval)
						.then(context => context?.properties.type?.state.node.output.value())
						.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'watched property'`; }
	}
}
export namespace Cderived__type__number {
	export class Dvalue_source extends StateGroup<
		{ name: 'expression', node:Cexpression__value_source__derived__type__number, init:Texpression__value_source__derived__type__number}|
		{ name: 'parameter', node:Cparameter__value_source__derived__type__number, init:Tparameter__value_source__derived__type__number}> {
		protected initializer(state: 'expression'|'parameter') {
			switch (state) {
				case 'expression': return (init:Texpression__value_source__derived__type__number, parent:Cderived__type__number) => new Cexpression__value_source__derived__type__number(init, parent);
				case 'parameter': return (init:Tparameter__value_source__derived__type__number, parent:Cderived__type__number) => new Cparameter__value_source__derived__type__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'parameter') {
			switch (state) {
				case 'expression': return finalize_expression__value_source__derived__type__number;
				case 'parameter': return finalize_parameter__value_source__derived__type__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderived__type__number['value source'], parent:Cderived__type__number) {
			super(data, parent, 'value source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value source'`; }
	}
}
export namespace Cexpression__value_source__derived__type__number {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Texpression__value_source__derived__type__number['expression'], parent:Cexpression__value_source__derived__type__number) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.number_value)
					.then(context => context?.definitions.scalar_value)
					.then(context => context?.definitions.leaf_value)
					.then(context => context?.definitions.value_object).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'expression')
		}
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Texpression__value_source__derived__type__number['recursion'], parent:Cexpression__value_source__derived__type__number) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
}
export namespace Cstate_group {
	export class Dfirst_state extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first state'`; }
	}
	export class Dstates extends AlanTopology<{ node:Cstates__state_group, init:Tstates__state_group},Cstate_group,'view order'> {
		protected graph_iterator(graph:('view order')):(node:Cstates__state_group) => Cstates__state_group {
			switch (graph) {
				case 'view order': return (entry:Cstates__state_group) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as application.Cyes__has_successor__states__state_group;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cstate_group, key:string, entry_init:Tstates__state_group) { return new Cstates__state_group(key, entry_init, parent); }
		protected finalize = finalize_states__state_group
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group['states'], parent:Cstate_group) {
			super(data, parent);
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__state_group, init:Tbase__type__state_group}|
		{ name: 'derived', node:Cderived__type__state_group, init:Tderived__type__state_group}> {
		protected initializer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return (init:Tbase__type__state_group, parent:Cstate_group) => new Cbase__type__state_group(init, parent);
				case 'derived': return (init:Tderived__type__state_group, parent:Cstate_group) => new Cderived__type__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return finalize_base__type__state_group;
				case 'derived': return finalize_derived__type__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group['type'], parent:Cstate_group) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_state_group_property {
		constructor(data:Tstate_group['ui'], parent:Cstate_group) {
			super(data, parent, {
				state_group_property: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Cstates__state_group {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__states__state_group, init:Tno__has_successor__states__state_group}|
		{ name: 'yes', node:Cyes__has_successor__states__state_group, init:Tyes__has_successor__states__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__states__state_group, parent:Cstates__state_group) => new Cno__has_successor__states__state_group(init, parent);
				case 'yes': return (init:Tyes__has_successor__states__state_group, parent:Cstates__state_group) => new Cyes__has_successor__states__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__states__state_group;
				case 'yes': return finalize_yes__has_successor__states__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group['has successor'], parent:Cstates__state_group) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dnode extends Cnode__application {
		constructor(data:Tstates__state_group['node'], parent:Cstates__state_group) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_location).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.permissions)
					.then(context => context?.component_root.output.source()).result!)
			}, 'node')
		}
	}
	export class Dpermissions extends Citem_permissions_definition {
		constructor(data:Tstates__state_group['permissions'], parent:Cstates__state_group) {
			super(data, parent, {
				item_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.node).result!),
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.state_value_source()).result!)
			}, 'permissions')
		}
	}
	export class Drecord_lifetime extends StateGroup<
		{ name: 'no', node:Cno__record_lifetime, init:Tno__record_lifetime}|
		{ name: 'yes', node:Cyes__record_lifetime, init:Tyes__record_lifetime}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__record_lifetime, parent:Cstates__state_group) => new Cno__record_lifetime(init, parent);
				case 'yes': return (init:Tyes__record_lifetime, parent:Cstates__state_group) => new Cyes__record_lifetime(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__record_lifetime;
				case 'yes': return finalize_yes__record_lifetime;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group['record lifetime'], parent:Cstates__state_group) {
			super(data, parent, 'record lifetime');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'record lifetime'`; }
	}
	export class Drules extends Cwhere_rules_definition {
		constructor(data:Tstates__state_group['rules'], parent:Cstates__state_group) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.where_rules_definer).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'rules')
		}
	}
	export class Dui extends Cui_state {
		constructor(data:Tstates__state_group['ui'], parent:Cstates__state_group) {
			super(data, parent, {
				state: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Cyes__has_successor__states__state_group {
	export class Dsuccessor extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cyes__has_successor__states__state_group) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Cyes__record_lifetime {
	export class Dcreation_timestamp extends Reference<application.Cnumber__type__property,string> {
		public readonly inferences:{
			creation_timestamp: () => application.Ccreation
		}

		constructor(data:string, $this:Cyes__record_lifetime) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.node)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as application.Cnumber__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				creation_timestamp: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__state_group__states__record_lifetime__yes__creation_timestamp_nval = $this.properties.creation_timestamp.ref;
					return resolve($this.properties.creation_timestamp.ref)
						.then(context => application__node__attributes__type__property__type__state_group__states__record_lifetime__yes__creation_timestamp_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__number)
								.then(context => {
									if (context?.properties.type.state.name === 'causal') {
										return context.properties.type.state.node as application.Ccausal;
									} else {
										return undefined;
									}
								})
								.then(context => {
									if (context?.properties.type.state.name === 'creation') {
										return context.properties.type.state.node as application.Ccreation;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'creation timestamp'`; }
	}
	export class Dmeta_property extends Reference<application.Cnumber__type__property,string> {
		public readonly inferences:{
			meta_property: () => application.Cdestruction,
			watches_this_state: () => application.Cstates__state_group
		}

		constructor(data:string, $this:Cyes__record_lifetime) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as application.Cnumber__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				meta_property: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__state_group__states__record_lifetime__yes__meta_property_nval = $this.properties.meta_property.ref;
					return resolve($this.properties.meta_property.ref)
						.then(context => application__node__attributes__type__property__type__state_group__states__record_lifetime__yes__meta_property_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__number)
								.then(context => {
									if (context?.properties.type.state.name === 'causal') {
										return context.properties.type.state.node as application.Ccausal;
									} else {
										return undefined;
									}
								})
								.then(context => {
									if (context?.properties.type.state.name === 'destruction') {
										return context.properties.type.state.node as application.Cdestruction;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				watches_this_state: cache((detach:boolean) => {
					const application__node__attributes__type__property__type__state_group__states__record_lifetime__yes__meta_property_nval = $this.properties.meta_property.ref;
					return resolve($this.properties.meta_property.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.meta_property.inferences.meta_property())
								.then(context => context?.properties.watched_state?.ref).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'meta property'`; }
	}
}
export namespace Cderived__type__state_group {
	export class Dvalue_source extends StateGroup<
		{ name: 'expression', node:Cexpression__value_source__derived__type__state_group, init:Texpression__value_source__derived__type__state_group}|
		{ name: 'parameter', node:Cparameter__value_source__derived__type__state_group, init:Tparameter__value_source__derived__type__state_group}> {
		protected initializer(state: 'expression'|'parameter') {
			switch (state) {
				case 'expression': return (init:Texpression__value_source__derived__type__state_group, parent:Cderived__type__state_group) => new Cexpression__value_source__derived__type__state_group(init, parent);
				case 'parameter': return (init:Tparameter__value_source__derived__type__state_group, parent:Cderived__type__state_group) => new Cparameter__value_source__derived__type__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'parameter') {
			switch (state) {
				case 'expression': return finalize_expression__value_source__derived__type__state_group;
				case 'parameter': return finalize_parameter__value_source__derived__type__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderived__type__state_group['value source'], parent:Cderived__type__state_group) {
			super(data, parent, 'value source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value source'`; }
	}
}
export namespace Cexpression__value_source__derived__type__state_group {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Texpression__value_source__derived__type__state_group['expression'], parent:Cexpression__value_source__derived__type__state_group) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.value_object).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'expression')
		}
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Texpression__value_source__derived__type__state_group['recursion'], parent:Cexpression__value_source__derived__type__state_group) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
}
export namespace Ctext__type__property {
	export class Dhas_reference extends StateGroup<
		{ name: 'no', node:Cno__has_reference, init:Tno__has_reference}|
		{ name: 'yes', node:Cyes__has_reference, init:Tyes__has_reference}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_reference, parent:Ctext__type__property) => new Cno__has_reference(init, parent);
				case 'yes': return (init:Tyes__has_reference, parent:Ctext__type__property) => new Cyes__has_reference(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_reference;
				case 'yes': return finalize_yes__has_reference;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['has reference'], parent:Ctext__type__property) {
			super(data, parent, 'has reference');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has reference'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__text, init:Tbase__type__text}|
		{ name: 'derived', node:Cderived__type__text, init:Tderived__type__text}> {
		protected initializer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return (init:Tbase__type__text, parent:Ctext__type__property) => new Cbase__type__text(init, parent);
				case 'derived': return (init:Tderived__type__text, parent:Ctext__type__property) => new Cderived__type__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'derived') {
			switch (state) {
				case 'base': return finalize_base__type__text;
				case 'derived': return finalize_derived__type__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['type'], parent:Ctext__type__property) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_text_property {
		constructor(data:Ttext__type__property['ui'], parent:Ctext__type__property) {
			super(data, parent, {
				text_property: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Cyes__has_reference {
	export class Dassignment extends Coptional_named_object_assignment {
		constructor(data:Tyes__has_reference['assignment'], parent:Cyes__has_reference) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.referenced_node_type())
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.step_dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.behaviour)
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.expression)
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.value_source()).result!)
			}, 'assignment')
		}
	}
	export class Dbehaviour extends Creference_behaviour {
		constructor(data:Tyes__has_reference['behaviour'], parent:Cyes__has_reference) {
			super(data, parent, {
				target_value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'behaviour')
		}
	}
	export class Dbehaviour_specialization extends StateGroup<
		{ name: 'none', node:Cnone__behaviour_specialization, init:Tnone__behaviour_specialization}|
		{ name: 'propagate key to value', node:Cpropagate_key_to_value, init:Tpropagate_key_to_value}> {
		protected initializer(state: 'none'|'propagate key to value') {
			switch (state) {
				case 'none': return (init:Tnone__behaviour_specialization, parent:Cyes__has_reference) => new Cnone__behaviour_specialization(init, parent);
				case 'propagate key to value': return (init:Tpropagate_key_to_value, parent:Cyes__has_reference) => new Cpropagate_key_to_value(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'propagate key to value') {
			switch (state) {
				case 'none': return finalize_none__behaviour_specialization;
				case 'propagate key to value': return finalize_propagate_key_to_value;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_reference['behaviour specialization'], parent:Cyes__has_reference) {
			super(data, parent, 'behaviour specialization');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'behaviour specialization'`; }
	}
	export class Ddirection extends Cdirection_annotation {
		constructor(data:Tyes__has_reference['direction'], parent:Cyes__has_reference) {
			super(data, parent, 'direction')
		}
	}
	export class Dexpression extends Centry_reference_definition {
		constructor(data:Tyes__has_reference['expression'], parent:Cyes__has_reference) {
			super(data, parent, {
				collection_existence: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_source())
					.then(context => {
						switch (context?.variant.name) {
							case 'base': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.properties.behaviour)
									.then(context => context?.component_root.output.existence()).result;
							}
							case 'derived': {
								return resolve(context)
									.then(() => parent)
									.then(() => application.Cexistence__application.Poptional).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!),
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entry_reference_definer).result!),
				required_value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.value_source()).result!)
			}, 'expression')
		}
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Tyes__has_reference['recursion'], parent:Cyes__has_reference) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
	export class Drules extends Cwhere_rules_definition {
		constructor(data:Tyes__has_reference['rules'], parent:Cyes__has_reference) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.where_rules_definer).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'rules')
		}
	}
	export class Dui extends Cui_reference_rule {
		constructor(data:Tyes__has_reference['ui'], parent:Cyes__has_reference) {
			super(data, parent, 'ui')
		}
	}
}
export namespace Cderived__type__text {
	export class Dvalue_source extends StateGroup<
		{ name: 'derived key', node:Cderived_key, init:Tderived_key}|
		{ name: 'expression', node:Cexpression__value_source__derived__type__text, init:Texpression__value_source__derived__type__text}|
		{ name: 'parameter', node:Cparameter__value_source__derived__type__text, init:Tparameter__value_source__derived__type__text}> {
		protected initializer(state: 'derived key'|'expression'|'parameter') {
			switch (state) {
				case 'derived key': return (init:Tderived_key, parent:Cderived__type__text) => new Cderived_key(init, parent);
				case 'expression': return (init:Texpression__value_source__derived__type__text, parent:Cderived__type__text) => new Cexpression__value_source__derived__type__text(init, parent);
				case 'parameter': return (init:Tparameter__value_source__derived__type__text, parent:Cderived__type__text) => new Cparameter__value_source__derived__type__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'derived key'|'expression'|'parameter') {
			switch (state) {
				case 'derived key': return finalize_derived_key;
				case 'expression': return finalize_expression__value_source__derived__type__text;
				case 'parameter': return finalize_parameter__value_source__derived__type__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tderived__type__text['value source'], parent:Cderived__type__text) {
			super(data, parent, 'value source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value source'`; }
	}
}
export namespace Cexpression__value_source__derived__type__text {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Texpression__value_source__derived__type__text['expression'], parent:Cexpression__value_source__derived__type__text) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.has_reference)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'no': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.parent)
									.then(context => context?.parent)
									.then(context => context?.definitions.scalar_value)
									.then(context => context?.definitions.leaf_value)
									.then(context => context?.definitions.value_object).result;
							}
							case 'yes': {
								const application__node__attributes__type__property__type__text__type__derived__value_source__expression__expression_in___target___yes_nval = state_group.cast('yes');
								return resolve(context)
									.then(context => application__node__attributes__type__property__type__text__type__derived__value_source__expression__expression_in___target___yes_nval)
									.then(context => context?.properties.behaviour)
									.then(context => context?.properties.type)
									.then(context => {
										const state_group = context;
										switch (state_group?.state.name) {
											case 'mandatory': {
												return resolve(context)
													.then(context => application__node__attributes__type__property__type__text__type__derived__value_source__expression__expression_in___target___yes_nval)
													.then(context => context?.properties.expression)
													.then(context => context?.definitions.reference_value)
													.then(context => context?.definitions.leaf_value).result;
											}
											case 'optional': {
												return resolve(context)
													.then(() => parent)
													.then(context => context?.parent)
													.then(context => context?.parent)
													.then(context => context?.definitions.scalar_value)
													.then(context => context?.definitions.leaf_value).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
										};})
									.then(context => context?.definitions.value_object).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'expression')
		}
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Texpression__value_source__derived__type__text['recursion'], parent:Cexpression__value_source__derived__type__text) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
}
export namespace Creference_set__type__attributes {
	export class Ddirection extends Cdirection_annotation {
		constructor(data:Treference_set__type__attributes['direction'], parent:Creference_set__type__attributes) {
			super(data, parent, 'direction')
		}
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Treference_set__type__attributes['recursion'], parent:Creference_set__type__attributes) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
	export class Dui extends Cui_reference_set_attribute {
		constructor(data:Treference_set__type__attributes['ui'], parent:Creference_set__type__attributes) {
			super(data, parent, 'ui')
		}
	}
	export class Dvalue_source extends StateGroup<
		{ name: 'reference', node:Creference__value_source, init:Treference__value_source}|
		{ name: 'union branch', node:Cunion_branch, init:Tunion_branch}> {
		protected initializer(state: 'reference'|'union branch') {
			switch (state) {
				case 'reference': return (init:Treference__value_source, parent:Creference_set__type__attributes) => new Creference__value_source(init, parent);
				case 'union branch': return (init:Tunion_branch, parent:Creference_set__type__attributes) => new Cunion_branch(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'reference'|'union branch') {
			switch (state) {
				case 'reference': return finalize_reference__value_source;
				case 'union branch': return finalize_union_branch;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_set__type__attributes['value source'], parent:Creference_set__type__attributes) {
			super(data, parent, 'value source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value source'`; }
	}
}
export namespace Creferenced_type_path {
	export class Dhead extends Cobject_path_tail {
		constructor(data:Treferenced_type_path['head'], parent:Creferenced_type_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnavigation_context({name: 'attribute', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.entity())
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.direction)
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.entity())
							.then(context => context?.definitions.relative_object_location).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.value_source()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.recursion)
					.then(context => context?.component_root.output.location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccomputation_phase.Pzero).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.value_source)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'reference': {
								return resolve(context)
									.then(() => parent)
									.then(() => application.Cnavigation_support.Pdescendant).result;
							}
							case 'union branch': {
								return resolve(context)
									.then(() => parent)
									.then(() => application.Cnavigation_support.Punbounded).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'head')
		}
	}
	export class Dpath extends Cplural_object_path_tail {
		public readonly inferences:{
			node_context: () => application.Cnode_navigation_context,
			node_type: () => application.Cnode__application,
			subset_dependency: () => application.Cdependency__application
		}
		constructor(data:Treferenced_type_path['path'], parent:Creferenced_type_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.root?.state.node.output.context()).result!),
				dependency_gen: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Coptional_dependency_gen.Pnone).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.recursion)
					.then(context => context?.component_root.output.location()).result!),
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Clower_bound_cardinality.Pzero).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.location())
					.then(context => context?.component_root.output.root()).result!),
				upper_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cupper_bound_cardinality.Pone).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'path')
			this.inferences = {
				node_context: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__referenced_type_path__path_nval = this;
					return resolve(this)
						.then(context => application__node__attributes__type__reference_set__referenced_type_path__path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.node_context())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__referenced_type_path__path_nval = this;
					return resolve(this)
						.then(() => parent.properties.path.inferences.node_context())
						.then(context => context?.component_root.output.node()).result!;
				})
				,
				subset_dependency: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__referenced_type_path__path_nval = this;
					return resolve(this)
						.then(context => evaluate__convert_to_subset_dependency(detach, 
							resolve(context)
								.then(context => application__node__attributes__type__reference_set__referenced_type_path__path_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							upper_bound_cardinality: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.value_source?.state.node.output.upper_bound_cardinality()).result!

							})).result!;
				})

			}
		}
	}
	export class Droot extends StateGroup<
		{ name: 'context', node:Ccontext, init:Tcontext}|
		{ name: 'sibling', node:Csibling__root, init:Tsibling__root}> {
		protected initializer(state: 'context'|'sibling') {
			switch (state) {
				case 'context': return (init:Tcontext, parent:Creferenced_type_path) => new Ccontext(init, parent);
				case 'sibling': return (init:Tsibling__root, parent:Creferenced_type_path) => new Csibling__root(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'context'|'sibling') {
			switch (state) {
				case 'context': return finalize_context;
				case 'sibling': return finalize_sibling__root;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treferenced_type_path['root'], parent:Creferenced_type_path) {
			super(data, parent, 'root');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'root'`; }
	}
}
export namespace Csibling__root {
	export class Ddefinition extends Csibling_reference_definition {
		public readonly inferences:{
			relative_location_of_referenced_node: () => application.Crelative_object_location,
			sibling_subset: () => application.Cderived_set
		}
		constructor(data:Tsibling__root['definition'], parent:Csibling__root) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.collection()).result!),
				definer_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.location())
					.then(context => context?.component_root.output.root()).result!),
				definer_value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pinference)
					.then(context => context?.definitions.value_source).result!),
				existence: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'definition')
			this.inferences = {
				relative_location_of_referenced_node: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__referenced_type_path__root__sibling__definition_nval = this;
					return resolve(this)
						.then(context => evaluate__merge_locations(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.root).result!, {
							context_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.head)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.properties.recursion)
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.inferences.head_node_type())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							reference_context_location: () => resolve(parent)
								.then(context => application__node__attributes__type__reference_set__referenced_type_path__root__sibling__definition_nval)
								.then(context => context?.component_root.output.location())
								.then(context => context?.definitions.relative_object_location).result!
							, 
							target_root_location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.location())
								.then(context => context?.component_root.output.root()).result!

							})).result!;
				})
				,
				sibling_subset: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__referenced_type_path__root__sibling__definition_nval = this;
					return resolve(this).then(context => new Cderived_set({
						lower_bound_cardinality: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Clower_bound_cardinality.Pzero).result!, 
						upper_bound_cardinality: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cupper_bound_cardinality.Pmany).result!},{
						entity: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.collection())
							.then(context => context?.definitions.entity).result!
						, 
						source_collection: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.collection())
							.then(context => context?.definitions.collection_attribute).result!
						, 
						source_entry_existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Poptional).result!
						, 
						value_location: () => resolve(parent)
							.then(() => parent.properties.definition.inferences.relative_location_of_referenced_node()).result!
						, 
						value_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.collection())
							.then(context => context?.properties.node)
							.then(context => context?.definitions.value_object).result!

						}), ).result!;
				})

			}
		}
	}
}
export namespace Creference__value_source {
	export class Dreference extends Cdependency_step {
		public readonly inferences:{
			entry_step: () => application.Centry_step_definition,
			inverse_relative_node_location: () => application.Crelative_object_location,
			referenced_node_type: () => application.Cnode__application,
			relative_location_of_referenced_node: () => application.Crelative_object_location,
			root_dependency: () => application.Centity__application
		}
		constructor(data:Treference__value_source['reference'], parent:Creference__value_source) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.referenced_type_path)
					.then(context => context?.properties.path?.inferences.node_context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.recursion)
					.then(context => context?.component_root.output.location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.referenced_type_path)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.phase()).result!),
				referenced_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Crecursion_dependence.Pnone).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!)
			}, 'reference')
			this.inferences = {
				entry_step: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__reference__reference_nval = this;
					return resolve(this)
						.then(context => application__node__attributes__type__reference_set__value_source__reference__reference_nval)
						.then(context => context?.component_root.output.step_type())
						.then(context => context?.variant.name === 'entry step' ? context.variant.definition as application.Centry_step_definition : undefined).result!;
				})
				,
				inverse_relative_node_location: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__reference__reference_nval = this;
					return resolve(this)
						.then(context => evaluate__inverse_relative_object_location(detach, 
							resolve(context)
								.then(context => application__node__attributes__type__reference_set__value_source__reference__reference_nval)
								.then(context => context?.component_root.output.definer())
								.then(context => context?.component_root.output.location()).result!, )).result!;
				})
				,
				referenced_node_type: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__reference__reference_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__node__attributes__type__reference_set__value_source__reference__reference_nval)
								.then(context => context?.component_root.output.definer())
								.then(context => context?.component_root.output.referenced_node_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.parent).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				relative_location_of_referenced_node: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__reference__reference_nval = this;
					return resolve(this)
						.then(context => evaluate__relative_object_location_validator(detach, 
							resolve(context)
								.then(() => parent.properties.reference.inferences.inverse_relative_node_location()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.referenced_type_path)
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.location()).result!

							})).result!;
				})
				,
				root_dependency: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__reference__reference_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.reference.inferences.inverse_relative_node_location())
								.then(context => context?.component_root.output.entity()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.referenced_type_path)
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.entity()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cunion_branch {
	export class Dbranch extends Reference<application.Cdependency__source,string> {
		public readonly inferences:{
			dependency: () => application.Cdependency__application,
			local_key_iubc: () => application.Cupper_bound_cardinality,
			node_type: () => application.Cnode__application,
			upper_bound_cardinality: () => application.Cupper_bound_cardinality
		}

		constructor(data:string, $this:Cunion_branch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.union())
				.then(context => {
					const entry = context?.properties.branches.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.source.state.name === 'dependency') {
							return context.properties.source.state.node as application.Cdependency__source;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				dependency: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__union_branch__branch_nval = $this.properties.branch.ref;
					return resolve($this.properties.branch.ref)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_nval)
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							expected: () => resolve($this)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.properties.referenced_type_path)
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!

							})).result!;
				})
				,
				local_key_iubc: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__union_branch__branch_nval = $this.properties.branch.ref;
					return resolve($this.properties.branch.ref)
						.then(context => evaluate__compute_local_key_iubc(detach, 
							resolve(context)
								.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_nval)
								.then(context => context?.properties.dependency)
								.then(context => context?.component_root.output.definer())
								.then(context => context?.component_root.output.step_dependency()).result!, {
							context_entity: () => resolve($this)
								.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_nval)
								.then(context => context?.properties.path?.inferences.node())
								.then(context => context?.component_root.output.entity()).result!

							})).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__union_branch__branch_nval = $this.properties.branch.ref;
					return resolve($this.properties.branch.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_nval)
								.then(context => context?.properties.path?.inferences.node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.properties.referenced_type_path)
								.then(context => context?.properties.path?.inferences.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				upper_bound_cardinality: cache((detach:boolean) => {
					const application__node__attributes__type__reference_set__value_source__union_branch__branch_nval = $this.properties.branch.ref;
					return resolve($this.properties.branch.ref)
						.then(() => $this.properties.branch.inferences.local_key_iubc())
						.then(context => {
							switch (context?.variant.name) {
								case 'many': {
									return resolve(context)
										.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_nval)
										.then(context => context?.properties.path)
										.then(context => context?.component_root.output.upper_bound_cardinality()).result;
								}
								case 'one': {
									return resolve(context)
										.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_nval)
										.then(context => context?.properties.path)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.dependency())
										.then(context => {
											switch (context?.variant.name) {
												case 'self': {
													return resolve(context)
														.then(() => $this)
														.then(() => application.Cupper_bound_cardinality.Pone).result;
												}
												case 'step': {
													const application__node__attributes__type__reference_set__value_source__union_branch__branch_inf___upper_bound_cardinality___one___step_nval = context.cast('step');
													return resolve(context)
														.then(context => evaluate__dependency_to_ubc(detach, 
															resolve(context)
																.then(context => application__node__attributes__type__reference_set__value_source__union_branch__branch_inf___upper_bound_cardinality___one___step_nval)
																.then(context => context?.component_root.output.parent()).result!, )).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'branch'`; }
	}
}
export namespace Cyes__has_attributes {
	export class Dfirst extends Reference<application.Cattributes,string> {

		constructor(data:string, $this:Cyes__has_attributes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
	export class Dlast extends Reference<application.Cattributes,string> {

		constructor(data:string, $this:Cyes__has_attributes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'last'`; }
	}
}
export namespace Cnode_expression {
	export class Dnode_path extends Cconditional_node_path {
		constructor(data:Tnode_expression['node path'], parent:Cnode_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'node path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'containment', node:Ccontainment__type__node_expression, init:Tcontainment__type__node_expression}|
		{ name: 'equality', node:Cequality__type__node_expression, init:Tequality__type__node_expression}|
		{ name: 'existence', node:Cexistence__type__node_expression, init:Texistence__type__node_expression}> {
		protected initializer(state: 'containment'|'equality'|'existence') {
			switch (state) {
				case 'containment': return (init:Tcontainment__type__node_expression, parent:Cnode_expression) => new Ccontainment__type__node_expression(init, parent);
				case 'equality': return (init:Tequality__type__node_expression, parent:Cnode_expression) => new Cequality__type__node_expression(init, parent);
				case 'existence': return (init:Texistence__type__node_expression, parent:Cnode_expression) => new Cexistence__type__node_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'containment'|'equality'|'existence') {
			switch (state) {
				case 'containment': return finalize_containment__type__node_expression;
				case 'equality': return finalize_equality__type__node_expression;
				case 'existence': return finalize_existence__type__node_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_expression['type'], parent:Cnode_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccontainment__type__node_expression {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccontainment__type__node_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.node_path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Ckey_path {
	export class Dpath extends Cconditional_node_path {
		constructor(data:Tkey_path['path'], parent:Ckey_path) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dtext extends Reference<application.Ctext__type__property,string> {

		constructor(data:string, $this:Ckey_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cequality__type__node_expression {
	export class Dexpected_node_path extends Cconditional_node_path {
		public readonly inferences:{
			expected_node_type: () => application.Cnode__application
		}
		constructor(data:Tequality__type__node_expression['expected node path'], parent:Cequality__type__node_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expected node path')
			this.inferences = {
				expected_node_type: cache((detach:boolean) => {
					const application__node_expression__type__equality__expected_node_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__node_expression__type__equality__expected_node_path_nval)
								.then(context => context?.component_root.output.node_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.node_path)
								.then(context => context?.component_root.output.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cnode_initializer {
	export class Darguments extends AlanDictionary<{ node:Carguments, init:Targuments},Cnode_initializer> {
		protected initialize(parent:Cnode_initializer, key:string, entry_init:Targuments) { return new Carguments(key, entry_init, parent); }
		protected finalize = finalize_arguments
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__node_initializer__arguments_key_nval:application.Cproperty__type__attributes):boolean {
				const inferences:{
					parameter: (detach?:boolean) => application.Cnode_initializing_expression|undefined;} = {

					parameter: maybe_cache((detach:boolean) => resolve(application__node_initializer__arguments_key_nval)
						.then(context => application__node_initializer__arguments_key_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'derived' ? context.variant.definition as application.Cderived_value_source : undefined)
						.then(context => context?.variant.name === 'initializer' ? context.variant.definition as application.Cnode_initializing_expression : undefined)?.result)
				}
				const $this = {
					inferences: inferences
				}
				if (inferences.parameter() === undefined)
					return false;
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'arguments'`; }
		constructor(data:Tnode_initializer['arguments'], parent:Cnode_initializer) {
			super(data, parent);
		}
	}
}
export namespace Carguments {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Targuments['expression'], parent:Carguments) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.type)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'collection': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.key?.ref)
									.then(context => context?.properties.type?.state.node.output.value()).result;
							}
							case 'file': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.key?.ref)
									.then(context => context?.properties.type?.state.node.output.value()).result;
							}
							case 'group': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.key?.ref)
									.then(context => context?.properties.type?.state.node.output.value()).result;
							}
							case 'number': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.key?.ref)
									.then(context => context?.properties.type?.state.node.output.value()).result;
							}
							case 'state group': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.key?.ref)
									.then(context => context?.properties.type?.state.node.output.value()).result;
							}
							case 'text': {
								const application__node_initializer__arguments__expression_in___target___text_nval = state_group.cast('text');
								return resolve(context)
									.then(context => application__node_initializer__arguments__expression_in___target___text_nval)
									.then(context => context?.properties.has_reference)
									.then(context => {
										const state_group = context;
										switch (state_group?.state.name) {
											case 'no': {
												return resolve(context)
													.then(() => parent)
													.then(context => context?.key?.ref)
													.then(context => context?.properties.type?.state.node.output.value()).result;
											}
											case 'yes': {
												const application__node_initializer__arguments__expression_in___target___text___yes_nval = state_group.cast('yes');
												return resolve(context)
													.then(context => application__node_initializer__arguments__expression_in___target___text___yes_nval)
													.then(context => context?.properties.behaviour)
													.then(context => context?.properties.type)
													.then(context => {
														const state_group = context;
														switch (state_group?.state.name) {
															case 'mandatory': {
																return resolve(context)
																	.then(context => application__node_initializer__arguments__expression_in___target___text___yes_nval)
																	.then(context => context?.properties.expression)
																	.then(context => context?.definitions.reference_value)
																	.then(context => context?.definitions.leaf_value)
																	.then(context => context?.definitions.value_object).result;
															}
															case 'optional': {
																return resolve(context)
																	.then(() => parent)
																	.then(context => context?.key?.ref)
																	.then(context => context?.properties.type?.state.node.output.value()).result;
															}
															case undefined: return undefined;
															default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
														};}).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
										};}).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cnode_path {
	export class Dhead extends Ccontext_node_path {
		constructor(data:Tnode_path['head'], parent:Cnode_path) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
		}
	}
	export class Dtail extends Cnode_path_tail {
		constructor(data:Tnode_path['tail'], parent:Cnode_path) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.parent_navigation_support()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!)
			}, 'tail')
		}
	}
}
export namespace Cnode_path_tail {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__node_path_tail, init:Tno__has_steps__node_path_tail}|
		{ name: 'yes', node:Cyes__has_steps__node_path_tail, init:Tyes__has_steps__node_path_tail}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__node_path_tail, parent:Cnode_path_tail) => new Cno__has_steps__node_path_tail(init, parent);
				case 'yes': return (init:Tyes__has_steps__node_path_tail, parent:Cnode_path_tail) => new Cyes__has_steps__node_path_tail(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__node_path_tail;
				case 'yes': return finalize_yes__has_steps__node_path_tail;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_path_tail['has steps'], parent:Cnode_path_tail) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__node_path_tail {
	export class Dstep extends Cnode_step__application {
		constructor(data:Tyes__has_steps__node_path_tail['step'], parent:Cyes__has_steps__node_path_tail) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.reference_navigation_support()).result!)
			}, 'step')
		}
	}
	export class Dtail extends Cnode_path_tail {
		constructor(data:Tyes__has_steps__node_path_tail['tail'], parent:Cyes__has_steps__node_path_tail) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.step)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.step)
					.then(context => context?.component_root.output.parent_navigation_support()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.reference_navigation_support()).result!)
			}, 'tail')
		}
	}
}
export namespace Cnode_permissions_definition {
	export class Dread_permission extends StateGroup<
		{ name: 'explicit', node:Cexplicit__read_permission, init:Texplicit__read_permission}|
		{ name: 'inherited', node:Cinherited__read_permission, init:Tinherited__read_permission}> {
		protected initializer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return (init:Texplicit__read_permission, parent:Cnode_permissions_definition) => new Cexplicit__read_permission(init, parent);
				case 'inherited': return (init:Tinherited__read_permission, parent:Cnode_permissions_definition) => new Cinherited__read_permission(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return finalize_explicit__read_permission;
				case 'inherited': return finalize_inherited__read_permission;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_permissions_definition['read permission'], parent:Cnode_permissions_definition) {
			super(data, parent, 'read permission');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'read permission'`; }
	}
	export class Dupdate_permission extends StateGroup<
		{ name: 'explicit', node:Cexplicit__update_permission, init:Texplicit__update_permission}|
		{ name: 'inherited', node:Cinherited__update_permission, init:Tinherited__update_permission}> {
		protected initializer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return (init:Texplicit__update_permission, parent:Cnode_permissions_definition) => new Cexplicit__update_permission(init, parent);
				case 'inherited': return (init:Tinherited__update_permission, parent:Cnode_permissions_definition) => new Cinherited__update_permission(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'inherited') {
			switch (state) {
				case 'explicit': return finalize_explicit__update_permission;
				case 'inherited': return finalize_inherited__update_permission;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_permissions_definition['update permission'], parent:Cnode_permissions_definition) {
			super(data, parent, 'update permission');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'update permission'`; }
	}
}
export namespace Cexplicit__read_permission {
	export class Dpermission extends Cpermission {
		constructor(data:Texplicit__read_permission['permission'], parent:Cexplicit__read_permission) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!)
			}, 'permission')
		}
	}
}
export namespace Cexplicit__update_permission {
	export class Dpermission extends Cpermission {
		constructor(data:Texplicit__update_permission['permission'], parent:Cexplicit__update_permission) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!)
			}, 'permission')
		}
	}
}
export namespace Cnode_step__application {
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__node_step, init:Tgroup__type__node_step}|
		{ name: 'parent', node:Cparent__type__node_step, init:Tparent__type__node_step}|
		{ name: 'property rule', node:Cproperty_rule, init:Tproperty_rule}|
		{ name: 'reference', node:Creference__type__node_step, init:Treference__type__node_step}|
		{ name: 'state', node:Cstate__type__node_step, init:Tstate__type__node_step}|
		{ name: 'state context rule', node:Cstate_context_rule__type__node_step, init:Tstate_context_rule__type__node_step}> {
		protected initializer(state: 'group'|'parent'|'property rule'|'reference'|'state'|'state context rule') {
			switch (state) {
				case 'group': return (init:Tgroup__type__node_step, parent:Cnode_step__application) => new Cgroup__type__node_step(init, parent);
				case 'parent': return (init:Tparent__type__node_step, parent:Cnode_step__application) => new Cparent__type__node_step(init, parent);
				case 'property rule': return (init:Tproperty_rule, parent:Cnode_step__application) => new Cproperty_rule(init, parent);
				case 'reference': return (init:Treference__type__node_step, parent:Cnode_step__application) => new Creference__type__node_step(init, parent);
				case 'state': return (init:Tstate__type__node_step, parent:Cnode_step__application) => new Cstate__type__node_step(init, parent);
				case 'state context rule': return (init:Tstate_context_rule__type__node_step, parent:Cnode_step__application) => new Cstate_context_rule__type__node_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'parent'|'property rule'|'reference'|'state'|'state context rule') {
			switch (state) {
				case 'group': return finalize_group__type__node_step;
				case 'parent': return finalize_parent__type__node_step;
				case 'property rule': return finalize_property_rule;
				case 'reference': return finalize_reference__type__node_step;
				case 'state': return finalize_state__type__node_step;
				case 'state context rule': return finalize_state_context_rule__type__node_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_step__application['type'], parent:Cnode_step__application) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__node_step {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__node_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cproperty_rule {
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			existence_support: () => application.Cexistence__application
		}

		constructor(data:string, $this:Cproperty_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.text?.inferences.reference())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				existence_support: cache((detach:boolean) => {
					const application__node_step__type__property_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__node_step__type__property_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
	export class Dtext extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Cproperty_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				reference: cache((detach:boolean) => {
					const application__node_step__type__property_rule__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => application__node_step__type__property_rule__text_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Creference__type__node_step {
	export class Dtext extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			existence_support: () => application.Cexistence__application,
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Creference__type__node_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				existence_support: cache((detach:boolean) => {
					const application__node_step__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.text.inferences.reference())
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const application__node_step__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => application__node_step__type__reference__text_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cstate__type__node_step {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__node_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate__type__node_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cstate_context_rule__type__node_step {
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			existence_support: () => application.Cexistence__application
		}

		constructor(data:string, $this:Cstate_context_rule__type__node_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				existence_support: cache((detach:boolean) => {
					const application__node_step__type__state_context_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__node_step__type__state_context_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cnode_type_id {
	export class Dsteps extends Cnode_type_id_path {
		constructor(data:Tnode_type_id['steps'], parent:Cnode_type_id) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnode_type_id_context({name: 'node', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.root)
							.then(context => context?.definitions.entity)
							.then(context => context?.definitions.dependency).result!

						});
					}).result!)
			}, 'steps')
		}
	}
}
export namespace Cnode_type_id_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__node_type_id_path, init:Tno__has_steps__node_type_id_path}|
		{ name: 'yes', node:Cyes__has_steps__node_type_id_path, init:Tyes__has_steps__node_type_id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__node_type_id_path, parent:Cnode_type_id_path) => new Cno__has_steps__node_type_id_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__node_type_id_path, parent:Cnode_type_id_path) => new Cyes__has_steps__node_type_id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__node_type_id_path;
				case 'yes': return finalize_yes__has_steps__node_type_id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_type_id_path['has steps'], parent:Cnode_type_id_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__node_type_id_path {
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {

		constructor(data:string, $this:Cyes__has_steps__node_type_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context())
				.then(context => {
					switch (context?.variant.name) {
						case 'attribute': {
							const application__node_type_id_path__has_steps__yes__property___attribute_nval = context.cast('attribute');
							return resolve(context)
								.then(context => application__node_type_id_path__has_steps__yes__property___attribute_nval)
								.then(context => {
									const entry = context?.parent.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										if (context?.properties.type.state.name === 'property') {
											return context.properties.type.state.node as application.Cproperty__type__attributes;
										} else {
											return undefined;
										}
									}).result;
								}).result;
						}
						case 'node': {
							const application__node_type_id_path__has_steps__yes__property___node_nval = context.cast('node');
							return resolve(context)
								.then(context => application__node_type_id_path__has_steps__yes__property___node_nval)
								.then(context => {
									const entry = context?.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										if (context?.properties.type.state.name === 'property') {
											return context.properties.type.state.node as application.Cproperty__type__attributes;
										} else {
											return undefined;
										}
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dtail extends Cnode_type_id_path {
		constructor(data:Tyes__has_steps__node_type_id_path['tail'], parent:Cyes__has_steps__node_type_id_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.value_type?.state.node.output.node_type())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnode_type_id_context({name: 'node', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.value_type?.state.node.output.dependency()).result!

						});
					}).result!)
			}, 'tail')
		}
	}
	export class Dvalue_type extends StateGroup<
		{ name: 'choice', node:Cchoice__value_type__yes__has_steps__node_type_id_path, init:Tchoice__value_type__yes__has_steps__node_type_id_path}|
		{ name: 'collection', node:Ccollection__value_type__yes__has_steps__node_type_id_path, init:Tcollection__value_type__yes__has_steps__node_type_id_path}|
		{ name: 'node', node:Cnode__value_type__yes__has_steps__node_type_id_path, init:Tnode__value_type__yes__has_steps__node_type_id_path}> {
		protected initializer(state: 'choice'|'collection'|'node') {
			switch (state) {
				case 'choice': return (init:Tchoice__value_type__yes__has_steps__node_type_id_path, parent:Cyes__has_steps__node_type_id_path) => new Cchoice__value_type__yes__has_steps__node_type_id_path(init, parent);
				case 'collection': return (init:Tcollection__value_type__yes__has_steps__node_type_id_path, parent:Cyes__has_steps__node_type_id_path) => new Ccollection__value_type__yes__has_steps__node_type_id_path(init, parent);
				case 'node': return (init:Tnode__value_type__yes__has_steps__node_type_id_path, parent:Cyes__has_steps__node_type_id_path) => new Cnode__value_type__yes__has_steps__node_type_id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'choice'|'collection'|'node') {
			switch (state) {
				case 'choice': return finalize_choice__value_type__yes__has_steps__node_type_id_path;
				case 'collection': return finalize_collection__value_type__yes__has_steps__node_type_id_path;
				case 'node': return finalize_node__value_type__yes__has_steps__node_type_id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__node_type_id_path['value type'], parent:Cyes__has_steps__node_type_id_path) {
			super(data, parent, 'value type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value type'`; }
	}
}
export namespace Cchoice__value_type__yes__has_steps__node_type_id_path {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cchoice__value_type__yes__has_steps__node_type_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cnumber_expression {
	export class Dtype extends StateGroup<
		{ name: 'aggregate', node:Caggregate, init:Taggregate}|
		{ name: 'constant', node:Cconstant__type__number_expression, init:Tconstant__type__number_expression}|
		{ name: 'unary', node:Cunary__type, init:Tunary__type}> {
		protected initializer(state: 'aggregate'|'constant'|'unary') {
			switch (state) {
				case 'aggregate': return (init:Taggregate, parent:Cnumber_expression) => new Caggregate(init, parent);
				case 'constant': return (init:Tconstant__type__number_expression, parent:Cnumber_expression) => new Cconstant__type__number_expression(init, parent);
				case 'unary': return (init:Tunary__type, parent:Cnumber_expression) => new Cunary__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'aggregate'|'constant'|'unary') {
			switch (state) {
				case 'aggregate': return finalize_aggregate;
				case 'constant': return finalize_constant__type__number_expression;
				case 'unary': return finalize_unary__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_expression['type'], parent:Cnumber_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Caggregate {
	export class Dtype extends StateGroup<
		{ name: 'addition', node:Caddition, init:Taddition}|
		{ name: 'collection operation', node:Ccollection_operation, init:Tcollection_operation}|
		{ name: 'difference', node:Cdifference, init:Tdifference}|
		{ name: 'division', node:Cdivision__type, init:Tdivision__type}|
		{ name: 'list operation', node:Clist_operation, init:Tlist_operation}|
		{ name: 'product', node:Cproduct__type, init:Tproduct__type}|
		{ name: 'remainder', node:Cremainder__type, init:Tremainder__type}> {
		protected initializer(state: 'addition'|'collection operation'|'difference'|'division'|'list operation'|'product'|'remainder') {
			switch (state) {
				case 'addition': return (init:Taddition, parent:Caggregate) => new Caddition(init, parent);
				case 'collection operation': return (init:Tcollection_operation, parent:Caggregate) => new Ccollection_operation(init, parent);
				case 'difference': return (init:Tdifference, parent:Caggregate) => new Cdifference(init, parent);
				case 'division': return (init:Tdivision__type, parent:Caggregate) => new Cdivision__type(init, parent);
				case 'list operation': return (init:Tlist_operation, parent:Caggregate) => new Clist_operation(init, parent);
				case 'product': return (init:Tproduct__type, parent:Caggregate) => new Cproduct__type(init, parent);
				case 'remainder': return (init:Tremainder__type, parent:Caggregate) => new Cremainder__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'addition'|'collection operation'|'difference'|'division'|'list operation'|'product'|'remainder') {
			switch (state) {
				case 'addition': return finalize_addition;
				case 'collection operation': return finalize_collection_operation;
				case 'difference': return finalize_difference;
				case 'division': return finalize_division__type;
				case 'list operation': return finalize_list_operation;
				case 'product': return finalize_product__type;
				case 'remainder': return finalize_remainder__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Taggregate['type'], parent:Caggregate) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Caddition {
	export class Dleft extends Cunary_number_expression {
		constructor(data:Taddition['left'], parent:Caddition) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'left')
		}
	}
	export class Dright extends Cunary_number_expression {
		constructor(data:Taddition['right'], parent:Caddition) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.inferences.relative_type())
						.then(context => context?.properties.range_type?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'right')
		}
	}
}
export namespace Ccollection_operation {
	export class Dtype extends StateGroup<
		{ name: 'count', node:Ccount, init:Tcount}|
		{ name: 'property', node:Cproperty__type__collection_operation, init:Tproperty__type__collection_operation}> {
		protected initializer(state: 'count'|'property') {
			switch (state) {
				case 'count': return (init:Tcount, parent:Ccollection_operation) => new Ccount(init, parent);
				case 'property': return (init:Tproperty__type__collection_operation, parent:Ccollection_operation) => new Cproperty__type__collection_operation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'count'|'property') {
			switch (state) {
				case 'count': return finalize_count;
				case 'property': return finalize_property__type__collection_operation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_operation['type'], parent:Ccollection_operation) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccount {
	export class Dnodes_path extends Cobject_set_path {
		public readonly inferences:{
			node: () => application.Cnode__application
		}
		constructor(data:Tcount['nodes path'], parent:Ccount) {
			super(data, parent, {
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.set_type())
					.then(context => {
						switch (context?.variant.name) {
							case 'integer': {
								return resolve(context)
									.then(() => parent)
									.then(() => application.Clower_bound_cardinality.Pzero).result;
							}
							case 'positive': {
								return resolve(context)
									.then(() => parent)
									.then(() => application.Clower_bound_cardinality.Pone).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'nodes path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__number_expression__type__aggregate__type__collection_operation__type__count__nodes_path_nval = this;
					return resolve(this)
						.then(context => application__number_expression__type__aggregate__type__collection_operation__type__count__nodes_path_nval)
						.then(context => context?.component_root.output.object_context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
}
export namespace Cproperty__type__collection_operation {
	export class Dnumbers_path extends Cobject_set_path {
		public readonly inferences:{
			number: () => application.Cnumber_value,
			numerical_type: () => application.Cnumerical_type_requirement,
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tproperty__type__collection_operation['numbers path'], parent:Cproperty__type__collection_operation) {
			super(data, parent, {
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.component_root.output.set_type())
					.then(context => {
						switch (context?.variant.name) {
							case 'integer': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.properties.operation?.state.node.output.required_cardinality()).result;
							}
							case 'positive': {
								return resolve(context)
									.then(() => parent)
									.then(() => application.Clower_bound_cardinality.Pone).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
						};
					}).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'numbers path')
			this.inferences = {
				number: cache((detach:boolean) => {
					const application__number_expression__type__aggregate__type__collection_operation__type__property__numbers_path_nval = this;
					return resolve(this)
						.then(context => application__number_expression__type__aggregate__type__collection_operation__type__property__numbers_path_nval)
						.then(context => context?.component_root.output.object_context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
						.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined)
						.then(context => context?.variant.name === 'number' ? context.variant.definition as application.Cnumber_value : undefined).result!;
				})
				,
				numerical_type: cache((detach:boolean) => {
					const application__number_expression__type__aggregate__type__collection_operation__type__property__numbers_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.numbers_path.inferences.number())
								.then(context => context?.component_root.output.numerical_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.target())
								.then(context => context?.component_root.output.numerical_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				set_type: cache((detach:boolean) => {
					const application__number_expression__type__aggregate__type__collection_operation__type__property__numbers_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.numbers_path.inferences.number())
								.then(context => context?.component_root.output.set_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.target())
								.then(context => context?.component_root.output.set_type()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
	}
	export class Doperation extends StateGroup<
		{ name: 'maximum', node:Cmaximum__operation__property, init:Tmaximum__operation__property}|
		{ name: 'minimum', node:Cminimum__operation__property, init:Tminimum__operation__property}|
		{ name: 'standard deviation', node:Cstandard_deviation, init:Tstandard_deviation}|
		{ name: 'sum', node:Csum__operation__property, init:Tsum__operation__property}> {
		protected initializer(state: 'maximum'|'minimum'|'standard deviation'|'sum') {
			switch (state) {
				case 'maximum': return (init:Tmaximum__operation__property, parent:Cproperty__type__collection_operation) => new Cmaximum__operation__property(init, parent);
				case 'minimum': return (init:Tminimum__operation__property, parent:Cproperty__type__collection_operation) => new Cminimum__operation__property(init, parent);
				case 'standard deviation': return (init:Tstandard_deviation, parent:Cproperty__type__collection_operation) => new Cstandard_deviation(init, parent);
				case 'sum': return (init:Tsum__operation__property, parent:Cproperty__type__collection_operation) => new Csum__operation__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'maximum'|'minimum'|'standard deviation'|'sum') {
			switch (state) {
				case 'maximum': return finalize_maximum__operation__property;
				case 'minimum': return finalize_minimum__operation__property;
				case 'standard deviation': return finalize_standard_deviation;
				case 'sum': return finalize_sum__operation__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__collection_operation['operation'], parent:Cproperty__type__collection_operation) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cdifference {
	export class Dscale_type extends Reference<application.Cscale,string> {
		public readonly inferences:{
			range_type: () => application.Cnumerical_types
		}

		constructor(data:string, $this:Cdifference) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'scale') {
							return context.properties.type.state.node as application.Cscale;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				range_type: cache((detach:boolean) => {
					const application__number_expression__type__aggregate__type__difference__scale_type_nval = $this.properties.scale_type.ref;
					return resolve($this.properties.scale_type.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__number_expression__type__aggregate__type__difference__scale_type_nval)
								.then(context => context?.properties.range_type?.ref).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.quantity_type())
								.then(context => context?.parent).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'scale type'`; }
	}
}
export namespace Cexpressions__difference {
	export class Dleft extends Cunary_number_expression {
		constructor(data:Texpressions__difference['left'], parent:Cexpressions__difference) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.properties.scale_type?.ref)
						.then(context => context?.parent)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'left')
		}
	}
	export class Dright extends Cunary_number_expression {
		constructor(data:Texpressions__difference['right'], parent:Cexpressions__difference) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.properties.scale_type?.ref)
						.then(context => context?.parent)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'right')
		}
	}
}
export namespace Cdivision__type {
	export class Drounding extends StateGroup<
		{ name: 'ceil', node:Cceil__rounding__division__type, init:Tceil__rounding__division__type}|
		{ name: 'floor', node:Cfloor__rounding__division__type, init:Tfloor__rounding__division__type}|
		{ name: 'ordinary', node:Cordinary__rounding__division__type, init:Tordinary__rounding__division__type}> {
		protected initializer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return (init:Tceil__rounding__division__type, parent:Cdivision__type) => new Cceil__rounding__division__type(init, parent);
				case 'floor': return (init:Tfloor__rounding__division__type, parent:Cdivision__type) => new Cfloor__rounding__division__type(init, parent);
				case 'ordinary': return (init:Tordinary__rounding__division__type, parent:Cdivision__type) => new Cordinary__rounding__division__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return finalize_ceil__rounding__division__type;
				case 'floor': return finalize_floor__rounding__division__type;
				case 'ordinary': return finalize_ordinary__rounding__division__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdivision__type['rounding'], parent:Cdivision__type) {
			super(data, parent, 'rounding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rounding'`; }
	}
}
export namespace Cexpressions__division {
	export class Dconversion_rule extends Reference<application.Cdivision_conversions,string> {

		constructor(data:string, $this:Cexpressions__division) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.inferences.quantity_type())
				.then(context => {
					const entry = context?.properties.division_conversions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion rule'`; }
	}
	export class Ddenominator extends Cunary_number_expression {
		constructor(data:Texpressions__division['denominator'], parent:Cexpressions__division) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.properties.conversion_rule?.ref)
						.then(context => context?.properties.denominator?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(() => application.Cnumber_set_type.Ppositive).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'denominator')
		}
	}
	export class Dnumerator extends Cunary_number_expression {
		constructor(data:Texpressions__division['numerator'], parent:Cexpressions__division) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.properties.conversion_rule?.ref)
						.then(context => context?.key?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'numerator')
		}
	}
}
export namespace Clist_operation {
	export class Dnumbers extends Cderivation_expression_list {
		constructor(data:Tlist_operation['numbers'], parent:Clist_operation) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.numerical_type()).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value)
					.then(context => context?.definitions.scalar_value)
					.then(context => context?.definitions.leaf_value)
					.then(context => context?.definitions.value_object).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'numbers')
		}
	}
	export class Doperation extends StateGroup<
		{ name: 'maximum', node:Cmaximum__operation__list_operation, init:Tmaximum__operation__list_operation}|
		{ name: 'minimum', node:Cminimum__operation__list_operation, init:Tminimum__operation__list_operation}|
		{ name: 'sum', node:Csum__operation__list_operation, init:Tsum__operation__list_operation}> {
		protected initializer(state: 'maximum'|'minimum'|'sum') {
			switch (state) {
				case 'maximum': return (init:Tmaximum__operation__list_operation, parent:Clist_operation) => new Cmaximum__operation__list_operation(init, parent);
				case 'minimum': return (init:Tminimum__operation__list_operation, parent:Clist_operation) => new Cminimum__operation__list_operation(init, parent);
				case 'sum': return (init:Tsum__operation__list_operation, parent:Clist_operation) => new Csum__operation__list_operation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'maximum'|'minimum'|'sum') {
			switch (state) {
				case 'maximum': return finalize_maximum__operation__list_operation;
				case 'minimum': return finalize_minimum__operation__list_operation;
				case 'sum': return finalize_sum__operation__list_operation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlist_operation['operation'], parent:Clist_operation) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cproduct__type {
	export class Dconversion_rule extends Reference<application.Cproduct_conversions,string> {

		constructor(data:string, $this:Cproduct__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.quantity_type())
				.then(context => {
					const entry = context?.properties.product_conversions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion rule'`; }
	}
	export class Dleft extends Cunary_number_expression {
		constructor(data:Tproduct__type['left'], parent:Cproduct__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.properties.conversion_rule?.ref)
						.then(context => context?.key?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'left')
		}
	}
	export class Dright extends Cunary_number_expression {
		constructor(data:Tproduct__type['right'], parent:Cproduct__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.properties.conversion_rule?.ref)
						.then(context => context?.properties.right?.ref)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cnumerical_type_requirement({name: 'known', definition: conv_context});
						}).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'right')
		}
	}
}
export namespace Cremainder__type {
	export class Ddenominator extends Cunary_number_expression {
		constructor(data:Tremainder__type['denominator'], parent:Cremainder__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.numerical_type()).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(() => application.Cnumber_set_type.Ppositive).result!}), )
					.then(context => context?.definitions.number_value).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'denominator')
		}
	}
	export class Dnumerator extends Cunary_number_expression {
		constructor(data:Tremainder__type['numerator'], parent:Cremainder__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'numerator')
		}
	}
}
export namespace Cconstant__type__number_expression {
	export class Dvalue extends Cconstant_number_value {
		public readonly inferences:{
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tconstant__type__number_expression['value'], parent:Cconstant__type__number_expression) {
			super(data, parent, 'value')
			this.inferences = {
				set_type: cache((detach:boolean) => {
					const application__number_expression__type__constant__value_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__number_expression__type__constant__value_nval)
								.then(context => context?.component_root.output.set_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.target())
								.then(context => context?.component_root.output.set_type()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cunary__type {
	export class Dexpression extends Cderivation_expression_tail {
		constructor(data:Tunary__type['expression'], parent:Cunary__type) {
			super(data, parent, {
				support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.support()).result!),
				target: cache((detach:boolean) => resolve(this).then(context => new Cnumber__application({
					numerical_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.properties.type?.state.node.output.numerical_type()).result!, 
					set_type: () => resolve(parent)
						.then(() => parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.set_type()).result!}), )
					.then(context => context?.definitions.number_value)
					.then(context => context?.definitions.scalar_value)
					.then(context => context?.definitions.leaf_value)
					.then(context => context?.definitions.value_object).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'absolute value', node:Cabsolute_value__type__unary, init:Tabsolute_value__type__unary}|
		{ name: 'numerical type conversion', node:Cnumerical_type_conversion__type__unary, init:Tnumerical_type_conversion__type__unary}|
		{ name: 'sign inversion', node:Csign_inversion__type__unary, init:Tsign_inversion__type__unary}> {
		protected initializer(state: 'absolute value'|'numerical type conversion'|'sign inversion') {
			switch (state) {
				case 'absolute value': return (init:Tabsolute_value__type__unary, parent:Cunary__type) => new Cabsolute_value__type__unary(init, parent);
				case 'numerical type conversion': return (init:Tnumerical_type_conversion__type__unary, parent:Cunary__type) => new Cnumerical_type_conversion__type__unary(init, parent);
				case 'sign inversion': return (init:Tsign_inversion__type__unary, parent:Cunary__type) => new Csign_inversion__type__unary(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'absolute value'|'numerical type conversion'|'sign inversion') {
			switch (state) {
				case 'absolute value': return finalize_absolute_value__type__unary;
				case 'numerical type conversion': return finalize_numerical_type_conversion__type__unary;
				case 'sign inversion': return finalize_sign_inversion__type__unary;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tunary__type['type'], parent:Cunary__type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumerical_type_conversion__type__unary {
	export class Dconversion extends Reference<application.Csingular_conversions,string> {

		constructor(data:string, $this:Cnumerical_type_conversion__type__unary) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.known_target_numerical_type())
				.then(context => {
					const entry = context?.properties.singular_conversions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion'`; }
	}
}
export namespace Cobject_path_tail {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__object_path_tail, init:Tno__has_steps__object_path_tail}|
		{ name: 'yes', node:Cyes__has_steps__object_path_tail, init:Tyes__has_steps__object_path_tail}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__object_path_tail, parent:Cobject_path_tail) => new Cno__has_steps__object_path_tail(init, parent);
				case 'yes': return (init:Tyes__has_steps__object_path_tail, parent:Cobject_path_tail) => new Cyes__has_steps__object_path_tail(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__object_path_tail;
				case 'yes': return finalize_yes__has_steps__object_path_tail;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tobject_path_tail['has steps'], parent:Cobject_path_tail) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__object_path_tail {
	export class Dtail extends Cobject_path_tail {
		constructor(data:Tyes__has_steps__object_path_tail['tail'], parent:Cyes__has_steps__object_path_tail) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.reference_navigation_support()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'dependency', node:Cdependency__type, init:Tdependency__type}|
		{ name: 'parent', node:Cparent__type__yes, init:Tparent__type__yes}|
		{ name: 'property value', node:Cproperty_value, init:Tproperty_value}|
		{ name: 'reference set', node:Creference_set__type__yes, init:Treference_set__type__yes}|
		{ name: 'state', node:Cstate__type__yes, init:Tstate__type__yes}> {
		protected initializer(state: 'dependency'|'parent'|'property value'|'reference set'|'state') {
			switch (state) {
				case 'dependency': return (init:Tdependency__type, parent:Cyes__has_steps__object_path_tail) => new Cdependency__type(init, parent);
				case 'parent': return (init:Tparent__type__yes, parent:Cyes__has_steps__object_path_tail) => new Cparent__type__yes(init, parent);
				case 'property value': return (init:Tproperty_value, parent:Cyes__has_steps__object_path_tail) => new Cproperty_value(init, parent);
				case 'reference set': return (init:Treference_set__type__yes, parent:Cyes__has_steps__object_path_tail) => new Creference_set__type__yes(init, parent);
				case 'state': return (init:Tstate__type__yes, parent:Cyes__has_steps__object_path_tail) => new Cstate__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dependency'|'parent'|'property value'|'reference set'|'state') {
			switch (state) {
				case 'dependency': return finalize_dependency__type;
				case 'parent': return finalize_parent__type__yes;
				case 'property value': return finalize_property_value;
				case 'reference set': return finalize_reference_set__type__yes;
				case 'state': return finalize_state__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__object_path_tail['type'], parent:Cyes__has_steps__object_path_tail) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdependency__type {
	export class Ddependency extends Cdependency_step {
		constructor(data:Tdependency__type['dependency'], parent:Cdependency__type) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node_context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				referenced_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'dependency')
		}
	}
}
export namespace Cproperty_value {
	export class Dproperty extends Cproperty_step {
		constructor(data:Tproperty_value['property'], parent:Cproperty_value) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node_context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'property')
		}
	}
}
export namespace Creference_set__type__yes {
	export class Dstep extends Creference_set_step {
		constructor(data:Treference_set__type__yes['step'], parent:Creference_set__type__yes) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node_context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'step')
		}
	}
}
export namespace Cstate__type__yes {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cobject_set_path {
	export class Dcollection_path extends Csingular_variablized_object_path {
		public readonly inferences:{
			cardinality: () => application.Clower_bound_cardinality,
			collection: () => application.Ccollection_object
		}
		constructor(data:Tobject_set_path['collection path'], parent:Cobject_set_path) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'collection path')
			this.inferences = {
				cardinality: cache((detach:boolean) => {
					const application__object_set_path__collection_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.collection_path.inferences.collection())
								.then(context => context?.component_root.output.lower_bound_cardinality()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.lower_bound_cardinality_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				collection: cache((detach:boolean) => {
					const application__object_set_path__collection_path_nval = this;
					return resolve(this)
						.then(context => application__object_set_path__collection_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined).result!;
				})

			}
		}
	}
	export class Dvalue_path extends Cobject_path_tail {
		public readonly inferences:{
			lower_bound_cardinality: () => application.Clower_bound_cardinality
		}
		constructor(data:Tobject_set_path['value path'], parent:Cobject_set_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection_path?.inferences.collection())
					.then(context => context?.component_root.output.value_type())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path?.inferences.collection())
							.then(context => context?.component_root.output.source_collection())
							.then(context => {
								switch (context?.variant.name) {
									case 'collection property': {
										const application__object_set_path__value_path_in___context___dependency___collection_property_nval = context.cast('collection property');
										return resolve(context)
											.then(context => application__object_set_path__value_path_in___context___dependency___collection_property_nval)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cdependency_definition({name: 'collection step', definition: conv_context},{
												location: () => resolve(parent)
													.then(() => parent)
													.then(context => context?.properties.collection_path?.inferences.collection())
													.then(context => context?.component_root.output.value_location()).result!
												, 
												parent: () => resolve(parent)
													.then(() => parent)
													.then(context => context?.properties.collection_path)
													.then(context => context?.component_root.output.context())
													.then(context => context?.component_root.output.dependency()).result!

												});
											})
											.then(context => context?.definitions.dependency).result;
									}
									case 'reference set': {
										const application__object_set_path__value_path_in___context___dependency___reference_set_nval = context.cast('reference set');
										return resolve(context)
											.then(context => evaluate__inline_dependency_chain(detach, 
												resolve(context)
													.then(context => application__object_set_path__value_path_in___context___dependency___reference_set_nval)
													.then(context => context?.properties.referenced_type_path)
													.then(context => context?.properties.path?.inferences.subset_dependency()).result!, {
												target: () => resolve(parent)
													.then(context => evaluate__eliminate_dependencies(detach, 
														resolve(context)
															.then(context => application__object_set_path__value_path_in___context___dependency___reference_set_nval)
															.then(context => context?.properties.referenced_type_path)
															.then(context => context?.properties.path)
															.then(context => context?.component_root.output.context())
															.then(context => context?.component_root.output.location())
															.then(context => context?.component_root.output.entity()).result!, {
														chain: () => resolve(parent)
															.then(() => parent)
															.then(context => context?.properties.collection_path)
															.then(context => context?.component_root.output.context())
															.then(context => context?.component_root.output.dependency()).result!

														})).result!

												})).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path?.inferences.collection())
							.then(context => context?.component_root.output.source_entry_existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support())
					.then(context => context?.component_root.output.existence()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'value path')
			this.inferences = {
				lower_bound_cardinality: cache((detach:boolean) => {
					const application__object_set_path__value_path_nval = this;
					return resolve(this)
						.then(context => application__object_set_path__value_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.existence())
						.then(context => {
							switch (context?.variant.name) {
								case 'mandatory': {
									return resolve(context)
										.then(() => parent)
										.then(context => context?.properties.collection_path?.inferences.cardinality()).result;
								}
								case 'optional': {
									return resolve(context)
										.then(() => parent)
										.then(() => application.Clower_bound_cardinality.Pzero).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
	}
}
export namespace Coptional_named_object_assignment {
	export class Dhas_assignment extends StateGroup<
		{ name: 'no', node:Cno__has_assignment__optional_named_object_assignment, init:Tno__has_assignment__optional_named_object_assignment}|
		{ name: 'yes', node:Cyes__has_assignment__optional_named_object_assignment, init:Tyes__has_assignment__optional_named_object_assignment}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_assignment__optional_named_object_assignment, parent:Coptional_named_object_assignment) => new Cno__has_assignment__optional_named_object_assignment(init, parent);
				case 'yes': return (init:Tyes__has_assignment__optional_named_object_assignment, parent:Coptional_named_object_assignment) => new Cyes__has_assignment__optional_named_object_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_assignment__optional_named_object_assignment;
				case 'yes': return finalize_yes__has_assignment__optional_named_object_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Toptional_named_object_assignment['has assignment'], parent:Coptional_named_object_assignment) {
			super(data, parent, 'has assignment');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has assignment'`; }
	}
}
export namespace Cyes__has_assignment__optional_named_object_assignment {
	export class Dassignment extends Cnamed_object_assignment {
		constructor(data:Tyes__has_assignment__optional_named_object_assignment['assignment'], parent:Cyes__has_assignment__optional_named_object_assignment) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.named_object_definer).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_variable()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source()).result!)
			}, 'assignment')
		}
	}
}
export namespace Coptional_variable_assignment {
	export class Dhas_assignment extends StateGroup<
		{ name: 'no', node:Cno__has_assignment__optional_variable_assignment, init:Tno__has_assignment__optional_variable_assignment}|
		{ name: 'yes', node:Cyes__has_assignment__optional_variable_assignment, init:Tyes__has_assignment__optional_variable_assignment}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_assignment__optional_variable_assignment, parent:Coptional_variable_assignment) => new Cno__has_assignment__optional_variable_assignment(init, parent);
				case 'yes': return (init:Tyes__has_assignment__optional_variable_assignment, parent:Coptional_variable_assignment) => new Cyes__has_assignment__optional_variable_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_assignment__optional_variable_assignment;
				case 'yes': return finalize_yes__has_assignment__optional_variable_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Toptional_variable_assignment['has assignment'], parent:Coptional_variable_assignment) {
			super(data, parent, 'has assignment');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has assignment'`; }
	}
}
export namespace Cyes__has_assignment__optional_variable_assignment {
	export class Dassignment extends Cvariable_assignment {
		constructor(data:Tyes__has_assignment__optional_variable_assignment['assignment'], parent:Cyes__has_assignment__optional_variable_assignment) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_variable()).result!)
			}, 'assignment')
		}
	}
}
export namespace Cpassword_initializer {
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tpassword_initializer['initializer'], parent:Cpassword_initializer) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.command_initialization_operation)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cbase_value_source.Pinternal).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.password_collection())
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
}
export namespace Cpermission {
	export class Dsource extends StateGroup<
		{ name: 'imported interface', node:Cimported_interface, init:Timported_interface}|
		{ name: 'user', node:Cuser__source, init:Tuser__source}> {
		protected initializer(state: 'imported interface'|'user') {
			switch (state) {
				case 'imported interface': return (init:Timported_interface, parent:Cpermission) => new Cimported_interface(init, parent);
				case 'user': return (init:Tuser__source, parent:Cpermission) => new Cuser__source(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'imported interface'|'user') {
			switch (state) {
				case 'imported interface': return finalize_imported_interface;
				case 'user': return finalize_user__source;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tpermission['source'], parent:Cpermission) {
			super(data, parent, 'source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'source'`; }
	}
}
export namespace Cimported_interface {
	export class Dinterface_ extends Reference<application.Cimported_interfaces,string> {
		public readonly inferences:{
			node_type: () => application.Cnode__application
		}

		constructor(data:string, $this:Cimported_interface) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.imported_interfaces.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				node_type: cache((detach:boolean) => {
					const application__permission__source__imported_interface__interface_nval = $this.properties.interface_.ref;
					return resolve($this.properties.interface_.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__permission__source__imported_interface__interface_nval)
								.then(context => context?.properties.instances?.state.node.output.node_type()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'interface'`; }
	}
}
export namespace Cuser__source {
	export class Drequirement extends Cuser_requirement {
		constructor(data:Tuser__source['requirement'], parent:Cuser__source) {
			super(data, parent, {
				definition_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'requirement')
		}
	}
}
export namespace Cplural_descendant_node_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__plural_descendant_node_path, init:Tno__has_steps__plural_descendant_node_path}|
		{ name: 'yes', node:Cyes__has_steps__plural_descendant_node_path, init:Tyes__has_steps__plural_descendant_node_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__plural_descendant_node_path, parent:Cplural_descendant_node_path) => new Cno__has_steps__plural_descendant_node_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__plural_descendant_node_path, parent:Cplural_descendant_node_path) => new Cyes__has_steps__plural_descendant_node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__plural_descendant_node_path;
				case 'yes': return finalize_yes__has_steps__plural_descendant_node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tplural_descendant_node_path['has steps'], parent:Cplural_descendant_node_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__plural_descendant_node_path {
	export class Dproperty extends Cproperty_step {
		constructor(data:Tyes__has_steps__plural_descendant_node_path['property'], parent:Cyes__has_steps__plural_descendant_node_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'property')
		}
	}
	export class Dtail extends Cplural_descendant_node_path {
		constructor(data:Tyes__has_steps__plural_descendant_node_path['tail'], parent:Cyes__has_steps__plural_descendant_node_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.value_type?.state.node.output.node_type())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnode_navigation_context({name: 'node', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.value_type?.state.node.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(context => evaluate__step_value_source_dependence(detach, 
								resolve(context)
									.then(() => parent)
									.then(context => context?.component_root.input.context())
									.then(context => context?.component_root.output.value_source_dependence()).result!, {
								step_value_source_dependence: () => resolve(parent)
									.then(() => parent)
									.then(context => context?.properties.property)
									.then(context => context?.component_root.output.property())
									.then(context => context?.properties.type?.state.node.output.value_source()).result!

								})).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				lower_bound_cardinality: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.value_type?.state.node.output.lower_bound_cardinality()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.property)
					.then(context => context?.component_root.output.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				upper_bound_cardinality: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.value_type?.state.node.output.upper_bound_cardinality()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'tail')
		}
	}
	export class Dvalue_type extends StateGroup<
		{ name: 'choice', node:Cchoice__value_type__yes__has_steps__plural_descendant_node_path, init:Tchoice__value_type__yes__has_steps__plural_descendant_node_path}|
		{ name: 'collection', node:Ccollection__value_type__yes__has_steps__plural_descendant_node_path, init:Tcollection__value_type__yes__has_steps__plural_descendant_node_path}|
		{ name: 'node', node:Cnode__value_type__yes__has_steps__plural_descendant_node_path, init:Tnode__value_type__yes__has_steps__plural_descendant_node_path}> {
		protected initializer(state: 'choice'|'collection'|'node') {
			switch (state) {
				case 'choice': return (init:Tchoice__value_type__yes__has_steps__plural_descendant_node_path, parent:Cyes__has_steps__plural_descendant_node_path) => new Cchoice__value_type__yes__has_steps__plural_descendant_node_path(init, parent);
				case 'collection': return (init:Tcollection__value_type__yes__has_steps__plural_descendant_node_path, parent:Cyes__has_steps__plural_descendant_node_path) => new Ccollection__value_type__yes__has_steps__plural_descendant_node_path(init, parent);
				case 'node': return (init:Tnode__value_type__yes__has_steps__plural_descendant_node_path, parent:Cyes__has_steps__plural_descendant_node_path) => new Cnode__value_type__yes__has_steps__plural_descendant_node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'choice'|'collection'|'node') {
			switch (state) {
				case 'choice': return finalize_choice__value_type__yes__has_steps__plural_descendant_node_path;
				case 'collection': return finalize_collection__value_type__yes__has_steps__plural_descendant_node_path;
				case 'node': return finalize_node__value_type__yes__has_steps__plural_descendant_node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__plural_descendant_node_path['value type'], parent:Cyes__has_steps__plural_descendant_node_path) {
			super(data, parent, 'value type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value type'`; }
	}
}
export namespace Cchoice__value_type__yes__has_steps__plural_descendant_node_path {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cchoice__value_type__yes__has_steps__plural_descendant_node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cplural_object_path {
	export class Dhead extends Cvariablized_object_path {
		constructor(data:Tplural_object_path['head'], parent:Cplural_object_path) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.direction_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support())
					.then(context => context?.component_root.output.existence()).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support_for_head()).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
		}
	}
	export class Dpath extends Cplural_object_path_tail {
		constructor(data:Tplural_object_path['path'], parent:Cplural_object_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context()).result!),
				dependency_gen: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Coptional_dependency_gen.Pnone).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support_for_path()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				upper_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.upper_bound_cardinality_support()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'path')
		}
	}
}
export namespace Cplural_object_path_tail {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__plural_object_path_tail, init:Tno__has_steps__plural_object_path_tail}|
		{ name: 'yes', node:Cyes__has_steps__plural_object_path_tail, init:Tyes__has_steps__plural_object_path_tail}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__plural_object_path_tail, parent:Cplural_object_path_tail) => new Cno__has_steps__plural_object_path_tail(init, parent);
				case 'yes': return (init:Tyes__has_steps__plural_object_path_tail, parent:Cplural_object_path_tail) => new Cyes__has_steps__plural_object_path_tail(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__plural_object_path_tail;
				case 'yes': return finalize_yes__has_steps__plural_object_path_tail;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tplural_object_path_tail['has steps'], parent:Cplural_object_path_tail) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__plural_object_path_tail {
	export class Dpath extends Cobject_path_tail {
		constructor(data:Tyes__has_steps__plural_object_path_tail['path'], parent:Cyes__has_steps__plural_object_path_tail) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.component_root.output.value_type())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.collection())
							.then(context => context?.component_root.output.source_collection())
							.then(context => {
								switch (context?.variant.name) {
									case 'collection property': {
										const application__plural_object_path_tail__has_steps__yes__path_in___context___dependency___collection_property_nval = context.cast('collection property');
										return resolve(context)
											.then(context => application__plural_object_path_tail__has_steps__yes__path_in___context___dependency___collection_property_nval)
											.then(context => {
												const conv_context = resolve(context).result!;
												return new Cdependency_definition({name: 'collection step', definition: conv_context},{
												location: () => resolve(parent)
													.then(() => parent)
													.then(context => context?.inferences.collection())
													.then(context => context?.component_root.output.value_location()).result!
												, 
												parent: () => resolve(parent)
													.then(() => parent)
													.then(context => context?.component_root.input.context())
													.then(context => context?.component_root.output.dependency()).result!

												});
											})
											.then(context => context?.definitions.dependency).result;
									}
									case 'reference set': {
										const application__plural_object_path_tail__has_steps__yes__path_in___context___dependency___reference_set_nval = context.cast('reference set');
										return resolve(context)
											.then(context => evaluate__inline_dependency_chain(detach, 
												resolve(context)
													.then(context => application__plural_object_path_tail__has_steps__yes__path_in___context___dependency___reference_set_nval)
													.then(context => context?.properties.referenced_type_path)
													.then(context => context?.properties.path?.inferences.subset_dependency()).result!, {
												target: () => resolve(parent)
													.then(context => evaluate__eliminate_dependencies(detach, 
														resolve(context)
															.then(context => application__plural_object_path_tail__has_steps__yes__path_in___context___dependency___reference_set_nval)
															.then(context => context?.properties.referenced_type_path)
															.then(context => context?.properties.path)
															.then(context => context?.component_root.output.context())
															.then(context => context?.component_root.output.location())
															.then(context => context?.component_root.output.entity()).result!, {
														chain: () => resolve(parent)
															.then(() => parent)
															.then(context => context?.component_root.input.context())
															.then(context => context?.component_root.output.dependency()).result!

														})).result!

												})).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.collection())
							.then(context => context?.component_root.output.lower_bound_cardinality())
							.then(context => {
								switch (context?.variant.name) {
									case 'one': {
										return resolve(context)
											.then(() => parent)
											.then(context => context?.component_root.input.context())
											.then(context => context?.component_root.output.existence()).result;
									}
									case 'zero': {
										return resolve(context)
											.then(() => parent)
											.then(() => application.Cexistence__application.Poptional).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.inferences.entry_location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support())
					.then(context => context?.component_root.output.existence()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.reference_navigation_support()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'path')
		}
	}
	export class Dtail extends Cplural_object_path_tail {
		constructor(data:Tyes__has_steps__plural_object_path_tail['tail'], parent:Cyes__has_steps__plural_object_path_tail) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.context()).result!),
				dependency_gen: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.dependency_gen()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.reference_navigation_support()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target_root_location()).result!),
				upper_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.upper_bound_cardinality_support()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'tail')
		}
	}
}
export namespace Cplural_reference_expression {
	export class Dtype extends StateGroup<
		{ name: 'augment', node:Caugment, init:Taugment}|
		{ name: 'union', node:Cunion, init:Tunion}> {
		protected initializer(state: 'augment'|'union') {
			switch (state) {
				case 'augment': return (init:Taugment, parent:Cplural_reference_expression) => new Caugment(init, parent);
				case 'union': return (init:Tunion, parent:Cplural_reference_expression) => new Cunion(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'augment'|'union') {
			switch (state) {
				case 'augment': return finalize_augment;
				case 'union': return finalize_union;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tplural_reference_expression['type'], parent:Cplural_reference_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Caugment {
	export class Dcollection_path extends Cvariablized_object_path {
		public readonly inferences:{
			collection: () => application.Ccollection_object,
			collection_property: () => application.Ccollection__type__property,
			dependency: () => application.Cdependency__application,
			lower_bound_cardinality: () => application.Clower_bound_cardinality
		}
		constructor(data:Taugment['collection path'], parent:Caugment) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cdirection.Punbounded).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support())
					.then(context => context?.component_root.output.existence()).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'collection path')
			this.inferences = {
				collection: cache((detach:boolean) => {
					const application__plural_reference_expression__type__augment__collection_path_nval = this;
					return resolve(this)
						.then(context => application__plural_reference_expression__type__augment__collection_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined).result!;
				})
				,
				collection_property: cache((detach:boolean) => {
					const application__plural_reference_expression__type__augment__collection_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.collection_path.inferences.collection())
						.then(context => context?.variant.name === 'collection attribute' ? context.variant.definition as application.Ccollection_attribute : undefined)
						.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
				})
				,
				dependency: cache((detach:boolean) => {
					const application__plural_reference_expression__type__augment__collection_path_nval = this;
					return resolve(this)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(context => application__plural_reference_expression__type__augment__collection_path_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.collection())
								.then(context => context?.inferences.key_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.properties.collection_path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!

							})).result!;
				})
				,
				lower_bound_cardinality: cache((detach:boolean) => {
					const application__plural_reference_expression__type__augment__collection_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.collection_path.inferences.collection())
								.then(context => context?.component_root.output.lower_bound_cardinality()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.lower_bound_cardinality_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
	}
	export class Dfilter_path extends Cdescendant_object_path {
		public readonly inferences:{
			node: () => application.Cnode__application,
			node_type: () => application.Cnode__application
		}
		constructor(data:Taugment['filter path'], parent:Caugment) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection_path?.inferences.collection_property())
					.then(context => context?.properties.node)
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path?.inferences.collection_property())
							.then(context => context?.definitions.entity)
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.existence())
							.then(context => {
								switch (context?.variant.name) {
									case 'mandatory': {
										return resolve(context)
											.then(() => parent)
											.then(context => context?.properties.collection_path?.inferences.lower_bound_cardinality())
											.then(context => context?.component_root.output.existence()).result;
									}
									case 'optional': {
										return resolve(context)
											.then(() => parent)
											.then(context => context?.properties.collection_path)
											.then(context => context?.component_root.output.context())
											.then(context => context?.component_root.output.existence()).result;
									}
									case undefined: return undefined;
									default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
								};
							}).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.collection_path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.properties.recursion)
					.then(context => context?.component_root.output.location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.lower_bound_cardinality_support())
					.then(context => context?.component_root.output.existence()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.phase()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'filter path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__plural_reference_expression__type__augment__filter_path_nval = this;
					return resolve(this)
						.then(context => application__plural_reference_expression__type__augment__filter_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__plural_reference_expression__type__augment__filter_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.filter_path.inferences.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.collection())
								.then(context => context?.inferences.key_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.referenced_node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cunion {
	export class Dbranches extends AlanDictionary<{ node:Cbranches__union, init:Tbranches__union},Cunion> {
		public readonly inferences:{
			computation_phase: () => application.Ccomputation_phase
		}
		protected initialize(parent:Cunion, key:string, entry_init:Tbranches__union) { return new Cbranches__union(key, entry_init, parent); }
		protected finalize = finalize_branches__union
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'branches'`; }
		constructor(data:Tunion['branches'], parent:Cunion) {
			super(data, parent);
			this.inferences = {
				computation_phase: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches_nval = this;
					return resolve(this)
						.then(reduce_context => 
							resolve(reduce_context)
							.then(context => application__plural_reference_expression__type__union__branches_nval)
							.then(col => col ? Array.from(col.entries()).map(entry => entry[1]) : [])
							.then(set => set.map(entry => resolve(entry)
								.then(context => context?.properties.source?.state.node.output.phase()).result))
								.then(set => set.reduce((prev:application.Ccomputation_phase, curr) => curr.variant.widening_index < prev!.variant.widening_index ? curr : prev, application.Ccomputation_phase.Pzero)).result).result!;
				})

			}
		}
	}
	export class Dfirst_branch extends Reference<application.Cbranches__union,string> {
		public readonly inferences:{
			no_predecessor: () => application.Cno__has_predecessor__branches
		}

		constructor(data:string, $this:Cunion) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.branches.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				no_predecessor: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__first_branch_nval = $this.properties.first_branch.ref;
					return resolve($this.properties.first_branch.ref)
						.then(context => application__plural_reference_expression__type__union__first_branch_nval)
						.then(context => {
							if (context?.properties.has_predecessor.state.name === 'no') {
								return resolve(context.properties.has_predecessor.state.node as application.Cno__has_predecessor__branches).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first branch'`; }
	}
}
export namespace Cbranches__union {
	export class Dhas_predecessor extends StateGroup<
		{ name: 'no', node:Cno__has_predecessor__branches, init:Tno__has_predecessor__branches}|
		{ name: 'yes', node:Cyes__has_predecessor__branches, init:Tyes__has_predecessor__branches}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_predecessor__branches, parent:Cbranches__union) => new Cno__has_predecessor__branches(init, parent);
				case 'yes': return (init:Tyes__has_predecessor__branches, parent:Cbranches__union) => new Cyes__has_predecessor__branches(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_predecessor__branches;
				case 'yes': return finalize_yes__has_predecessor__branches;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbranches__union['has predecessor'], parent:Cbranches__union) {
			super(data, parent, 'has predecessor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has predecessor'`; }
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__branches, init:Tno__has_successor__branches}|
		{ name: 'yes', node:Cyes__has_successor__branches, init:Tyes__has_successor__branches}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__branches, parent:Cbranches__union) => new Cno__has_successor__branches(init, parent);
				case 'yes': return (init:Tyes__has_successor__branches, parent:Cbranches__union) => new Cyes__has_successor__branches(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__branches;
				case 'yes': return finalize_yes__has_successor__branches;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbranches__union['has successor'], parent:Cbranches__union) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dsource extends StateGroup<
		{ name: 'dependency', node:Cdependency__source, init:Tdependency__source}|
		{ name: 'dependency inversion', node:Cdependency_inversion, init:Tdependency_inversion}> {
		protected initializer(state: 'dependency'|'dependency inversion') {
			switch (state) {
				case 'dependency': return (init:Tdependency__source, parent:Cbranches__union) => new Cdependency__source(init, parent);
				case 'dependency inversion': return (init:Tdependency_inversion, parent:Cbranches__union) => new Cdependency_inversion(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dependency'|'dependency inversion') {
			switch (state) {
				case 'dependency': return finalize_dependency__source;
				case 'dependency inversion': return finalize_dependency_inversion;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbranches__union['source'], parent:Cbranches__union) {
			super(data, parent, 'source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'source'`; }
	}
}
export namespace Cdependency__source {
	export class Ddependency extends Cdependency_step {
		public readonly inferences:{
			_inspect_dependency_step_dependency: () => application.Cdependency__application,
			dependency: () => application.Cdependency__application,
			dependency_step: () => application.Cdependency_definition,
			entry_step: () => application.Centry_step_definition,
			node: () => application.Cnode__application,
			node_type: () => application.Cnode__application,
			unified_dependency: () => application.Cdependency__application
		}
		constructor(data:Tdependency__source['dependency'], parent:Cdependency__source) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.path?.inferences.node())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnode_navigation_context({name: 'node', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.dependency()).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.direction()).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.existence()).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.location()).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.value_source_dependence()).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.phase()).result!),
				referenced_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_source()).result!)
			}, 'dependency')
			this.inferences = {
				_inspect_dependency_step_dependency: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(context => evaluate___inspect_dependency(detach, 
							resolve(context)
								.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, )).result!;
				})
				,
				dependency: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(() => parent.properties.dependency.inferences.unified_dependency()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.collection())
								.then(context => context?.inferences.key_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.dependency()).result!

							})).result!;
				})
				,
				dependency_step: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.dependency())
						.then(context => context?.variant.name === 'step' ? context.variant.definition as application.Cdependency_definition : undefined).result!;
				})
				,
				entry_step: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(() => parent.properties.dependency.inferences.dependency_step())
						.then(context => context?.variant.name === 'entry step' ? context.variant.definition as application.Centry_step_definition : undefined).result!;
				})
				,
				node: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.dependency.inferences.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.collection())
								.then(context => context?.inferences.key_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.referenced_node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				unified_dependency: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.path)
						.then(context => context?.component_root.output.dependency_gen())
						.then(context => {
							switch (context?.variant.name) {
								case 'gen': {
									const application__plural_reference_expression__type__union__branches__source__dependency__dependency_inf___unified_dependency___gen_nval = context.cast('gen');
									return resolve(context)
										.then(context => evaluate__generalize_dependency___before_match(detach, 
											resolve(context)
												.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval)
												.then(context => context?.component_root.output.context())
												.then(context => context?.component_root.output.dependency()).result!, {
											gen: () => resolve(parent).then(context => new Cdependency_gen({
												dependency: () => resolve(parent)
													.then(context => evaluate__eliminate_dependencies(detach, 
														resolve(context)
															.then(() => parent.properties.dependency.inferences.dependency_step())
															.then(context => context?.component_root.output.parent())
															.then(context => context?.component_root.output.location())
															.then(context => context?.component_root.output.entity()).result!, {
														chain: () => resolve(parent)
															.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_inf___unified_dependency___gen_nval)
															.then(context => context?.properties.dependency()).result!

														})).result!, 
												gen: () => resolve(parent)
													.then(context => evaluate__eliminate_dependencies(detach, 
														resolve(context)
															.then(() => parent.properties.dependency.inferences.dependency_step())
															.then(context => context?.component_root.output.parent())
															.then(context => context?.component_root.output.location())
															.then(context => context?.component_root.output.entity()).result!, {
														chain: () => resolve(parent)
															.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_inf___unified_dependency___gen_nval)
															.then(context => context?.properties.gen()).result!

														})).result!}), ).result!

											})).result;
								}
								case 'none': {
									return resolve(context)
										.then(context => application__plural_reference_expression__type__union__branches__source__dependency__dependency_nval)
										.then(context => context?.component_root.output.context())
										.then(context => context?.component_root.output.dependency()).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
	}
	export class Dpath extends Cplural_object_path {
		public readonly inferences:{
			node: () => application.Cnode__application
		}
		constructor(data:Tdependency__source['path'], parent:Cdependency__source) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.has_predecessor?.state.node.output.dependency_location_support()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cdirection.Punbounded).result!),
				lower_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Clower_bound_cardinality.Pzero).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				parent_navigation_support_for_head: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				parent_navigation_support_for_path: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				upper_bound_cardinality_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cupper_bound_cardinality.Pone).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency__path_nval = this;
					return resolve(this)
						.then(context => application__plural_reference_expression__type__union__branches__source__dependency__path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
	}
}
export namespace Cdependency_inversion {
	export class Dreference_set_path extends Cvariablized_object_path {
		public readonly inferences:{
			reference_set: () => application.Creference_set__type__attributes
		}
		constructor(data:Tdependency_inversion['reference set path'], parent:Cdependency_inversion) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.has_predecessor?.state.node.output.dependency_location_support()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cdirection.Punbounded).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'reference set path')
			this.inferences = {
				reference_set: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency_inversion__reference_set_path_nval = this;
					return resolve(this)
						.then(context => application__plural_reference_expression__type__union__branches__source__dependency_inversion__reference_set_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection_object : undefined)
						.then(context => context?.variant.name === 'collection attribute' ? context.variant.definition as application.Ccollection_attribute : undefined)
						.then(context => context?.variant.name === 'reference set' ? context.variant.definition as application.Creference_set__type__attributes : undefined).result!;
				})

			}
		}
	}
	export class Dsubset_path extends Creference_set_subset_step {
		public readonly inferences:{
			dependency_required_by_key: () => application.Cdependency__application,
			node_type: () => application.Cnode__application
		}
		constructor(data:Tdependency_inversion['subset path'], parent:Cdependency_inversion) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.reference_set_path)
					.then(context => context?.component_root.output.context()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.reference_set_path)
					.then(context => context?.component_root.output.phase()).result!),
				reference_set: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.reference_set_path?.inferences.reference_set()).result!),
				reference_set_dependency: cache((detach:boolean) => resolve(this)
					.then(context => evaluate__inline_dependency_chain(detach, 
						resolve(context)
							.then(() => parent)
							.then(context => context?.properties.reference_set_path?.inferences.reference_set())
							.then(context => context?.properties.referenced_type_path)
							.then(context => context?.properties.path)
							.then(context => context?.component_root.output.context())
							.then(context => context?.component_root.output.dependency()).result!, {
						target: () => resolve(parent)
							.then(context => evaluate__eliminate_dependencies(detach, 
								resolve(context)
									.then(() => parent)
									.then(context => context?.properties.reference_set_path?.inferences.reference_set())
									.then(context => context?.properties.referenced_type_path)
									.then(context => context?.properties.path)
									.then(context => context?.component_root.output.context())
									.then(context => context?.component_root.output.location())
									.then(context => context?.component_root.output.entity()).result!, {
								chain: () => resolve(parent)
									.then(() => parent)
									.then(context => context?.properties.reference_set_path)
									.then(context => context?.component_root.output.context())
									.then(context => context?.component_root.output.dependency()).result!

								})).result!

						})).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'subset path')
			this.inferences = {
				dependency_required_by_key: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency_inversion__subset_path_nval = this;
					return resolve(this)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(context => application__plural_reference_expression__type__union__branches__source__dependency_inversion__subset_path_nval)
								.then(context => context?.component_root.output.dependency()).result!, {
							expected: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.collection())
								.then(context => context?.inferences.key_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.dependency()).result!

							})).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__plural_reference_expression__type__union__branches__source__dependency_inversion__subset_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__plural_reference_expression__type__union__branches__source__dependency_inversion__subset_path_nval)
								.then(context => context?.component_root.output.node_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.collection())
								.then(context => context?.inferences.key_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.referenced_node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cproperty_step {
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			dependencies: () => application.Ccomputation_phase,
			value_source: () => application.Cvalue_source
		}

		constructor(data:string, $this:Cproperty_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context())
				.then(context => {
					switch (context?.variant.name) {
						case 'attribute': {
							const application__property_step__property___attribute_nval = context.cast('attribute');
							return resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.direction())
								.then(context => {
									switch (context?.variant.name) {
										case 'downstream': {
											return resolve(context)
												.then(context => application__property_step__property___attribute_nval)
												.then(context => {
													const entry = context?.parent.properties.attributes.get(this.entry)!;
													return resolve(entry)
													.then(context => {
														if (context?.properties.type.state.name === 'property') {
															return context.properties.type.state.node as application.Cproperty__type__attributes;
														} else {
															return undefined;
														}
													}).result;
												}).result;
										}
										case 'unbounded': {
											return resolve(context)
												.then(context => application__property_step__property___attribute_nval)
												.then(context => {
													const entry = context?.parent.properties.attributes.get(this.entry)!;
													return resolve(entry)
													.then(context => {
														if (context?.properties.type.state.name === 'property') {
															return context.properties.type.state.node as application.Cproperty__type__attributes;
														} else {
															return undefined;
														}
													}).result;
												}).result;
										}
										case 'upstream': {
											return resolve(context)
												.then(context => application__property_step__property___attribute_nval)
												.then(context => {
													const entry = context?.parent.properties.attributes.get(this.entry)!;
													return resolve(entry)
													.then(context => {
														if (context?.properties.type.state.name === 'property') {
															return context.properties.type.state.node as application.Cproperty__type__attributes;
														} else {
															return undefined;
														}
													}).result;
												}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case 'node': {
							const application__property_step__property___node_nval = context.cast('node');
							return resolve(context)
								.then(context => application__property_step__property___node_nval)
								.then(context => {
									const entry = context?.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										if (context?.properties.type.state.name === 'property') {
											return context.properties.type.state.node as application.Cproperty__type__attributes;
										} else {
											return undefined;
										}
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
			this.inferences = {
				dependencies: cache((detach:boolean) => {
					const application__property_step__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => evaluate__property_value_dependencies_checker(detach, 
							resolve(context)
								.then(context => application__property_step__property_nval).result!, {
							context_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location: () => resolve($this)
								.then(context => application__property_step__property_nval)
								.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!
							, 
							dependency_location_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.dependency_location_support()).result!
							, 
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				value_source: cache((detach:boolean) => {
					const application__property_step__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => evaluate__value_source_checker(detach, 
							resolve(context)
								.then(context => application__property_step__property_nval)
								.then(context => context?.properties.type?.state.node.output.value_source()).result!, {
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!
							, 
							value_source_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.value_source_support()).result!

							})).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Crecursion_annotation {
	export class Drecursive_graph_traversal extends StateGroup<
		{ name: 'no', node:Cno__recursive_graph_traversal, init:Tno__recursive_graph_traversal}|
		{ name: 'yes', node:Cyes__recursive_graph_traversal, init:Tyes__recursive_graph_traversal}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__recursive_graph_traversal, parent:Crecursion_annotation) => new Cno__recursive_graph_traversal(init, parent);
				case 'yes': return (init:Tyes__recursive_graph_traversal, parent:Crecursion_annotation) => new Cyes__recursive_graph_traversal(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__recursive_graph_traversal;
				case 'yes': return finalize_yes__recursive_graph_traversal;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trecursion_annotation['recursive graph traversal'], parent:Crecursion_annotation) {
			super(data, parent, 'recursive graph traversal');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'recursive graph traversal'`; }
	}
}
export namespace Cyes__recursive_graph_traversal {
	export class Dselector extends Cgraph_traversal_selector {
		constructor(data:Tyes__recursive_graph_traversal['selector'], parent:Cyes__recursive_graph_traversal) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!)
			}, 'selector')
		}
	}
}
export namespace Creference_behaviour {
	export class Dtype extends StateGroup<
		{ name: 'mandatory', node:Cmandatory__type, init:Tmandatory__type}|
		{ name: 'optional', node:Coptional__type, init:Toptional__type}> {
		protected initializer(state: 'mandatory'|'optional') {
			switch (state) {
				case 'mandatory': return (init:Tmandatory__type, parent:Creference_behaviour) => new Cmandatory__type(init, parent);
				case 'optional': return (init:Toptional__type, parent:Creference_behaviour) => new Coptional__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'mandatory'|'optional') {
			switch (state) {
				case 'mandatory': return finalize_mandatory__type;
				case 'optional': return finalize_optional__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_behaviour['type'], parent:Creference_behaviour) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Creference_set_step {
	export class Dreference_set extends Reference<application.Creference_set__type__attributes,string> {
		public readonly inferences:{
			dependencies: () => application.Ccomputation_phase,
			relative_location_of_referenced_nodes: () => application.Crelative_object_location,
			value_source: () => application.Cvalue_source
		}

		constructor(data:string, $this:Creference_set_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context())
				.then(context => {
					switch (context?.variant.name) {
						case 'attribute': {
							const application__reference_set_step__reference_set___attribute_nval = context.cast('attribute');
							return resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.direction())
								.then(context => {
									switch (context?.variant.name) {
										case 'downstream': {
											return resolve(context)
												.then(context => application__reference_set_step__reference_set___attribute_nval)
												.then(context => {
													const entry = context?.parent.properties.attributes.get(this.entry)!;
													return resolve(entry)
													.then(context => {
														if (context?.properties.type.state.name === 'reference set') {
															return context.properties.type.state.node as application.Creference_set__type__attributes;
														} else {
															return undefined;
														}
													}).result;
												}).result;
										}
										case 'unbounded': {
											return resolve(context)
												.then(context => application__reference_set_step__reference_set___attribute_nval)
												.then(context => {
													const entry = context?.parent.properties.attributes.get(this.entry)!;
													return resolve(entry)
													.then(context => {
														if (context?.properties.type.state.name === 'reference set') {
															return context.properties.type.state.node as application.Creference_set__type__attributes;
														} else {
															return undefined;
														}
													}).result;
												}).result;
										}
										case 'upstream': {
											return resolve(context)
												.then(context => application__reference_set_step__reference_set___attribute_nval)
												.then(context => {
													const entry = context?.parent.properties.attributes.get(this.entry)!;
													return resolve(entry)
													.then(context => {
														if (context?.properties.type.state.name === 'reference set') {
															return context.properties.type.state.node as application.Creference_set__type__attributes;
														} else {
															return undefined;
														}
													}).result;
												}).result;
										}
										case undefined: return undefined;
										default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
									};
								}).result;
						}
						case 'node': {
							const application__reference_set_step__reference_set___node_nval = context.cast('node');
							return resolve(context)
								.then(context => application__reference_set_step__reference_set___node_nval)
								.then(context => {
									const entry = context?.properties.attributes.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										if (context?.properties.type.state.name === 'reference set') {
											return context.properties.type.state.node as application.Creference_set__type__attributes;
										} else {
											return undefined;
										}
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
			this.inferences = {
				dependencies: cache((detach:boolean) => {
					const application__reference_set_step__reference_set_nval = $this.properties.reference_set.ref;
					return resolve($this.properties.reference_set.ref)
						.then(context => evaluate__reference_set_dependencies_checker(detach, 
							resolve(context)
								.then(context => application__reference_set_step__reference_set_nval).result!, {
							context_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location: () => resolve($this)
								.then(context => application__reference_set_step__reference_set_nval)
								.then(context => context?.properties.recursion)
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.dependency_location_support()).result!
							, 
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				relative_location_of_referenced_nodes: cache((detach:boolean) => {
					const application__reference_set_step__reference_set_nval = $this.properties.reference_set.ref;
					return resolve($this.properties.reference_set.ref)
						.then(context => evaluate__merge_locations(detach, 
							resolve(context)
								.then(() => $this)
								.then(context => context?.root).result!, {
							context_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.dependency_location_support()).result!
							, 
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							reference_context_location: () => resolve($this)
								.then(context => application__reference_set_step__reference_set_nval)
								.then(context => context?.properties.referenced_type_path)
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				value_source: cache((detach:boolean) => {
					const application__reference_set_step__reference_set_nval = $this.properties.reference_set.ref;
					return resolve($this.properties.reference_set.ref)
						.then(context => evaluate__value_source_checker(detach, 
							resolve(context)
								.then(context => application__reference_set_step__reference_set_nval)
								.then(context => context?.properties.value_source?.state.node.output.value_source()).result!, {
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!
							, 
							value_source_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.value_source_support()).result!

							})).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference set'`; }
	}
}
export namespace Creference_set_subset_step {
	export class Dsubset extends StateGroup<
		{ name: 'no', node:Cno__subset, init:Tno__subset}|
		{ name: 'yes', node:Cyes__subset, init:Tyes__subset}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__subset, parent:Creference_set_subset_step) => new Cno__subset(init, parent);
				case 'yes': return (init:Tyes__subset, parent:Creference_set_subset_step) => new Cyes__subset(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__subset;
				case 'yes': return finalize_yes__subset;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_set_subset_step['subset'], parent:Creference_set_subset_step) {
			super(data, parent, 'subset');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'subset'`; }
	}
}
export namespace Cyes__subset {
	export class Dhead extends Cvariablized_object_path {
		public readonly inferences:{
			dependency_of_reference_set: () => application.Cdependency__application,
			subset_dependency: () => application.Cdependency__application,
			type_dependency: () => application.Cdependency__application
		}
		constructor(data:Tyes__subset['head'], parent:Cyes__subset) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cdirection.Punbounded).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnamed_object.Punassigned).result!)
			}, 'head')
			this.inferences = {
				dependency_of_reference_set: cache((detach:boolean) => {
					const application__reference_set_subset_step__subset__yes__head_nval = this;
					return resolve(this)
						.then(context => evaluate__check_dependencies(detach, 
							resolve(context)
								.then(() => parent.properties.head.inferences.subset_dependency()).result!, {
							expected: () => resolve(parent)
								.then(context => evaluate__eliminate_dependencies(detach, 
									resolve(context)
										.then(() => parent.properties.head.inferences.type_dependency())
										.then(context => context?.component_root.output.location())
										.then(context => context?.component_root.output.entity()).result!, {
									chain: () => resolve(parent)
										.then(() => parent)
										.then(context => context?.component_root.input.reference_set_dependency()).result!

									})).result!

							})).result!;
				})
				,
				subset_dependency: cache((detach:boolean) => {
					const application__reference_set_subset_step__subset__yes__head_nval = this;
					return resolve(this)
						.then(context => evaluate__convert_to_subset_dependency(detach, 
							resolve(context)
								.then(() => parent.properties.head.inferences.type_dependency()).result!, {
							upper_bound_cardinality: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.reference_set())
								.then(context => context?.properties.value_source?.state.node.output.upper_bound_cardinality()).result!

							})).result!;
				})
				,
				type_dependency: cache((detach:boolean) => {
					const application__reference_set_subset_step__subset__yes__head_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.type?.state.node.output.collection())
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Cdependency_definition({name: 'collection step', definition: conv_context},{
							location: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.type?.state.node.output.entry_location()).result!
							, 
							parent: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.properties.head)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!

							});
						})
						.then(context => context?.definitions.dependency).result!;
				})

			}
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'sibling', node:Csibling__type__yes, init:Tsibling__type__yes}|
		{ name: 'simple', node:Csimple__type__yes, init:Tsimple__type__yes}> {
		protected initializer(state: 'sibling'|'simple') {
			switch (state) {
				case 'sibling': return (init:Tsibling__type__yes, parent:Cyes__subset) => new Csibling__type__yes(init, parent);
				case 'simple': return (init:Tsimple__type__yes, parent:Cyes__subset) => new Csimple__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'sibling'|'simple') {
			switch (state) {
				case 'sibling': return finalize_sibling__type__yes;
				case 'simple': return finalize_simple__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__subset['type'], parent:Cyes__subset) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cscalar_expression {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic__type__scalar_expression, init:Tdynamic__type__scalar_expression}|
		{ name: 'number', node:Cnumber__type__scalar_expression, init:Tnumber__type__scalar_expression}|
		{ name: 'text', node:Ctext__type__scalar_expression, init:Ttext__type__scalar_expression}> {
		protected initializer(state: 'dynamic'|'number'|'text') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic__type__scalar_expression, parent:Cscalar_expression) => new Cdynamic__type__scalar_expression(init, parent);
				case 'number': return (init:Tnumber__type__scalar_expression, parent:Cscalar_expression) => new Cnumber__type__scalar_expression(init, parent);
				case 'text': return (init:Ttext__type__scalar_expression, parent:Cscalar_expression) => new Ctext__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'number'|'text') {
			switch (state) {
				case 'dynamic': return finalize_dynamic__type__scalar_expression;
				case 'number': return finalize_number__type__scalar_expression;
				case 'text': return finalize_text__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscalar_expression['type'], parent:Cscalar_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic__type__scalar_expression {
	export class Dpath extends Csingular_node_path {
		constructor(data:Tdynamic__type__scalar_expression['path'], parent:Cdynamic__type__scalar_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			value: () => application.Ccommand_scalar
		}

		constructor(data:string, $this:Cdynamic__type__scalar_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				value: cache((detach:boolean) => {
					const application__scalar_expression__type__dynamic__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(() => $this)
						.then(context => context?.component_root.input.target())
						.then(context => {
							switch (context?.variant.name) {
								case 'file': {
									return resolve(context)
										.then(() => $this)
										.then(context => context?.properties.property?.ref)
										.then(context => {
											if (context?.properties.type.state.name === 'file') {
												return resolve(context.properties.type.state.node as application.Cfile).result;
											} else {
												return undefined;
											}
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Ccommand_file({name: 'file property', definition: conv_context});
										})
										.then(context => context?.definitions.command_scalar).result;
								}
								case 'number': {
									const application__scalar_expression__type__dynamic__property_inf___value___number_nval = context.cast('number');
									return resolve(context)
										.then(() => $this)
										.then(context => context?.properties.property?.ref)
										.then(context => {
											if (context?.properties.type.state.name === 'number') {
												return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
											} else {
												return undefined;
											}
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											const application__scalar_expression__type__dynamic__property_inf___value___number_vcon_nval = context;
											return new Ccommand_number({name: 'number property', definition: conv_context},{
											numerical_type: () => resolve($this)
												.then(context => application__scalar_expression__type__dynamic__property_inf___value___number_vcon_nval)
												.then(context => context?.properties.numerical_type?.ref).result!
											, 
											set_type: () => resolve($this)
												.then(context => {
													const left = resolve(context)
														.then(context => application__scalar_expression__type__dynamic__property_inf___value___number_vcon_nval)
														.then(context => context?.properties.set_type?.state.node.output.set_type()).result;
													const right = resolve(context)
														.then(context => application__scalar_expression__type__dynamic__property_inf___value___number_nval)
														.then(context => context?.component_root.output.set_type()).result;
													return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
												}).result!

											});
										})
										.then(context => context?.definitions.command_scalar).result;
								}
								case 'text': {
									return resolve(context)
										.then(() => $this)
										.then(context => context?.properties.property?.ref)
										.then(context => {
											if (context?.properties.type.state.name === 'text') {
												return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
											} else {
												return undefined;
											}
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Ccommand_text({name: 'text property', definition: conv_context});
										})
										.then(context => context?.definitions.command_scalar).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Cnumber__type__scalar_expression {
	export class Dtype extends StateGroup<
		{ name: 'binary expression', node:Cbinary_expression__type__number__type__scalar_expression, init:Tbinary_expression__type__number__type__scalar_expression}|
		{ name: 'constant', node:Cconstant__type__number__type__scalar_expression, init:Tconstant__type__number__type__scalar_expression}|
		{ name: 'list expression', node:Clist_expression__type__number__type__scalar_expression, init:Tlist_expression__type__number__type__scalar_expression}|
		{ name: 'now in seconds', node:Cnow_in_seconds__type__number__type__scalar_expression, init:Tnow_in_seconds__type__number__type__scalar_expression}|
		{ name: 'unary expression', node:Cunary_expression__type__number__type__scalar_expression, init:Tunary_expression__type__number__type__scalar_expression}> {
		protected initializer(state: 'binary expression'|'constant'|'list expression'|'now in seconds'|'unary expression') {
			switch (state) {
				case 'binary expression': return (init:Tbinary_expression__type__number__type__scalar_expression, parent:Cnumber__type__scalar_expression) => new Cbinary_expression__type__number__type__scalar_expression(init, parent);
				case 'constant': return (init:Tconstant__type__number__type__scalar_expression, parent:Cnumber__type__scalar_expression) => new Cconstant__type__number__type__scalar_expression(init, parent);
				case 'list expression': return (init:Tlist_expression__type__number__type__scalar_expression, parent:Cnumber__type__scalar_expression) => new Clist_expression__type__number__type__scalar_expression(init, parent);
				case 'now in seconds': return (init:Tnow_in_seconds__type__number__type__scalar_expression, parent:Cnumber__type__scalar_expression) => new Cnow_in_seconds__type__number__type__scalar_expression(init, parent);
				case 'unary expression': return (init:Tunary_expression__type__number__type__scalar_expression, parent:Cnumber__type__scalar_expression) => new Cunary_expression__type__number__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binary expression'|'constant'|'list expression'|'now in seconds'|'unary expression') {
			switch (state) {
				case 'binary expression': return finalize_binary_expression__type__number__type__scalar_expression;
				case 'constant': return finalize_constant__type__number__type__scalar_expression;
				case 'list expression': return finalize_list_expression__type__number__type__scalar_expression;
				case 'now in seconds': return finalize_now_in_seconds__type__number__type__scalar_expression;
				case 'unary expression': return finalize_unary_expression__type__number__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__scalar_expression['type'], parent:Cnumber__type__scalar_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbinary_expression__type__number__type__scalar_expression {
	export class Doperation extends StateGroup<
		{ name: 'division', node:Cdivision__operation__binary_expression__type__number__type__scalar_expression, init:Tdivision__operation__binary_expression__type__number__type__scalar_expression}|
		{ name: 'remainder', node:Cremainder__operation__binary_expression__type__number__type__scalar_expression, init:Tremainder__operation__binary_expression__type__number__type__scalar_expression}> {
		protected initializer(state: 'division'|'remainder') {
			switch (state) {
				case 'division': return (init:Tdivision__operation__binary_expression__type__number__type__scalar_expression, parent:Cbinary_expression__type__number__type__scalar_expression) => new Cdivision__operation__binary_expression__type__number__type__scalar_expression(init, parent);
				case 'remainder': return (init:Tremainder__operation__binary_expression__type__number__type__scalar_expression, parent:Cbinary_expression__type__number__type__scalar_expression) => new Cremainder__operation__binary_expression__type__number__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'division'|'remainder') {
			switch (state) {
				case 'division': return finalize_division__operation__binary_expression__type__number__type__scalar_expression;
				case 'remainder': return finalize_remainder__operation__binary_expression__type__number__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinary_expression__type__number__type__scalar_expression['operation'], parent:Cbinary_expression__type__number__type__scalar_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cexpressions__binary_expression__type__number__type__scalar_expression {
	export class Dleft extends Cscalar_expression {
		constructor(data:Texpressions__binary_expression__type__number__type__scalar_expression['left'], parent:Cexpressions__binary_expression__type__number__type__scalar_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.properties.operation?.state.node.output.left_set_type()).result!

						});
					})
					.then(context => context?.definitions.command_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'left')
		}
	}
	export class Dright extends Cscalar_expression {
		constructor(data:Texpressions__binary_expression__type__number__type__scalar_expression['right'], parent:Cexpressions__binary_expression__type__number__type__scalar_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cnumber_set_type.Ppositive).result!

						});
					})
					.then(context => context?.definitions.command_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'right')
		}
	}
}
export namespace Cdivision__operation__binary_expression__type__number__type__scalar_expression {
	export class Drounding extends StateGroup<
		{ name: 'ceil', node:Cceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression, init:Tceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression}|
		{ name: 'floor', node:Cfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression, init:Tfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression}|
		{ name: 'ordinary', node:Cordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression, init:Tordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression}> {
		protected initializer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return (init:Tceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression, parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) => new Cceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression(init, parent);
				case 'floor': return (init:Tfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression, parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) => new Cfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression(init, parent);
				case 'ordinary': return (init:Tordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression, parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) => new Cordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return finalize_ceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression;
				case 'floor': return finalize_floor__rounding__division__operation__binary_expression__type__number__type__scalar_expression;
				case 'ordinary': return finalize_ordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdivision__operation__binary_expression__type__number__type__scalar_expression['rounding'], parent:Cdivision__operation__binary_expression__type__number__type__scalar_expression) {
			super(data, parent, 'rounding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rounding'`; }
	}
}
export namespace Cconstant__type__number__type__scalar_expression {
	export class Dvalue extends Cconstant_number_value {
		public readonly inferences:{
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tconstant__type__number__type__scalar_expression['value'], parent:Cconstant__type__number__type__scalar_expression) {
			super(data, parent, 'value')
			this.inferences = {
				set_type: cache((detach:boolean) => {
					const application__scalar_expression__type__number__type__constant__value_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__scalar_expression__type__number__type__constant__value_nval)
								.then(context => context?.component_root.output.set_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.inferences.number_target())
								.then(context => context?.component_root.output.set_type()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Clist_expression__type__number__type__scalar_expression {
	export class Dexpression extends Cscalar_expression_list {
		constructor(data:Tlist_expression__type__number__type__scalar_expression['expression'], parent:Clist_expression__type__number__type__scalar_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.command_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Doperation extends StateGroup<
		{ name: 'maximum', node:Cmaximum__operation__list_expression__type__number__type__scalar_expression, init:Tmaximum__operation__list_expression__type__number__type__scalar_expression}|
		{ name: 'minimum', node:Cminimum__operation__list_expression__type__number__type__scalar_expression, init:Tminimum__operation__list_expression__type__number__type__scalar_expression}|
		{ name: 'product', node:Cproduct__operation__list_expression__type__number__type__scalar_expression, init:Tproduct__operation__list_expression__type__number__type__scalar_expression}|
		{ name: 'sum', node:Csum__operation__list_expression__type__number__type__scalar_expression, init:Tsum__operation__list_expression__type__number__type__scalar_expression}> {
		protected initializer(state: 'maximum'|'minimum'|'product'|'sum') {
			switch (state) {
				case 'maximum': return (init:Tmaximum__operation__list_expression__type__number__type__scalar_expression, parent:Clist_expression__type__number__type__scalar_expression) => new Cmaximum__operation__list_expression__type__number__type__scalar_expression(init, parent);
				case 'minimum': return (init:Tminimum__operation__list_expression__type__number__type__scalar_expression, parent:Clist_expression__type__number__type__scalar_expression) => new Cminimum__operation__list_expression__type__number__type__scalar_expression(init, parent);
				case 'product': return (init:Tproduct__operation__list_expression__type__number__type__scalar_expression, parent:Clist_expression__type__number__type__scalar_expression) => new Cproduct__operation__list_expression__type__number__type__scalar_expression(init, parent);
				case 'sum': return (init:Tsum__operation__list_expression__type__number__type__scalar_expression, parent:Clist_expression__type__number__type__scalar_expression) => new Csum__operation__list_expression__type__number__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'maximum'|'minimum'|'product'|'sum') {
			switch (state) {
				case 'maximum': return finalize_maximum__operation__list_expression__type__number__type__scalar_expression;
				case 'minimum': return finalize_minimum__operation__list_expression__type__number__type__scalar_expression;
				case 'product': return finalize_product__operation__list_expression__type__number__type__scalar_expression;
				case 'sum': return finalize_sum__operation__list_expression__type__number__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlist_expression__type__number__type__scalar_expression['operation'], parent:Clist_expression__type__number__type__scalar_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cunary_expression__type__number__type__scalar_expression {
	export class Dexpression extends Cscalar_expression {
		constructor(data:Tunary_expression__type__number__type__scalar_expression['expression'], parent:Cunary_expression__type__number__type__scalar_expression) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ccommand_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.type?.state.node.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.command_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'absolute value', node:Cabsolute_value__type__unary_expression__type__number__type__scalar_expression, init:Tabsolute_value__type__unary_expression__type__number__type__scalar_expression}|
		{ name: 'numerical type conversion', node:Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression, init:Tnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression}|
		{ name: 'sign inversion', node:Csign_inversion__type__unary_expression__type__number__type__scalar_expression, init:Tsign_inversion__type__unary_expression__type__number__type__scalar_expression}> {
		protected initializer(state: 'absolute value'|'numerical type conversion'|'sign inversion') {
			switch (state) {
				case 'absolute value': return (init:Tabsolute_value__type__unary_expression__type__number__type__scalar_expression, parent:Cunary_expression__type__number__type__scalar_expression) => new Cabsolute_value__type__unary_expression__type__number__type__scalar_expression(init, parent);
				case 'numerical type conversion': return (init:Tnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression, parent:Cunary_expression__type__number__type__scalar_expression) => new Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression(init, parent);
				case 'sign inversion': return (init:Tsign_inversion__type__unary_expression__type__number__type__scalar_expression, parent:Cunary_expression__type__number__type__scalar_expression) => new Csign_inversion__type__unary_expression__type__number__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'absolute value'|'numerical type conversion'|'sign inversion') {
			switch (state) {
				case 'absolute value': return finalize_absolute_value__type__unary_expression__type__number__type__scalar_expression;
				case 'numerical type conversion': return finalize_numerical_type_conversion__type__unary_expression__type__number__type__scalar_expression;
				case 'sign inversion': return finalize_sign_inversion__type__unary_expression__type__number__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tunary_expression__type__number__type__scalar_expression['type'], parent:Cunary_expression__type__number__type__scalar_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression {
	export class Dconversion extends Reference<application.Csingular_conversions,string> {

		constructor(data:string, $this:Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.numerical_type())
				.then(context => {
					const entry = context?.properties.singular_conversions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion'`; }
	}
}
export namespace Ctext__type__scalar_expression {
	export class Dtype extends StateGroup<
		{ name: 'static', node:Cstatic__type__text__type__scalar_expression, init:Tstatic__type__text__type__scalar_expression}> {
		protected initializer(state: 'static') {
			switch (state) {
				case 'static': return (init:Tstatic__type__text__type__scalar_expression, parent:Ctext__type__scalar_expression) => new Cstatic__type__text__type__scalar_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'static') {
			switch (state) {
				case 'static': return finalize_static__type__text__type__scalar_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__scalar_expression['type'], parent:Ctext__type__scalar_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstatic__type__text__type__scalar_expression {
}
export namespace Cscalar_expression_list {
	export class Dhas_tail extends StateGroup<
		{ name: 'no', node:Cno__has_tail__scalar_expression_list, init:Tno__has_tail__scalar_expression_list}|
		{ name: 'yes', node:Cyes__has_tail__scalar_expression_list, init:Tyes__has_tail__scalar_expression_list}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_tail__scalar_expression_list, parent:Cscalar_expression_list) => new Cno__has_tail__scalar_expression_list(init, parent);
				case 'yes': return (init:Tyes__has_tail__scalar_expression_list, parent:Cscalar_expression_list) => new Cyes__has_tail__scalar_expression_list(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_tail__scalar_expression_list;
				case 'yes': return finalize_yes__has_tail__scalar_expression_list;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscalar_expression_list['has tail'], parent:Cscalar_expression_list) {
			super(data, parent, 'has tail');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has tail'`; }
	}
	export class Dhead extends Cscalar_expression {
		constructor(data:Tscalar_expression_list['head'], parent:Cscalar_expression_list) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
		}
	}
}
export namespace Cyes__has_tail__scalar_expression_list {
	export class Dtail extends Cscalar_expression_list {
		constructor(data:Tyes__has_tail__scalar_expression_list['tail'], parent:Cyes__has_tail__scalar_expression_list) {
			super(data, parent, {
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.source()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'tail')
		}
	}
}
export namespace Csibling_reference_definition {
	export class Dgraph_participation extends StateGroup<
		{ name: 'no', node:Cno__graph_participation, init:Tno__graph_participation}|
		{ name: 'yes', node:Cyes__graph_participation, init:Tyes__graph_participation}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__graph_participation, parent:Csibling_reference_definition) => new Cno__graph_participation(init, parent);
				case 'yes': return (init:Tyes__graph_participation, parent:Csibling_reference_definition) => new Cyes__graph_participation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__graph_participation;
				case 'yes': return finalize_yes__graph_participation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsibling_reference_definition['graph participation'], parent:Csibling_reference_definition) {
			super(data, parent, 'graph participation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph participation'`; }
	}
}
export namespace Cno__graph_participation {
	export class Dsupport_self_reference extends StateGroup<
		{ name: 'no', node:Cno__support_self_reference, init:Tno__support_self_reference}|
		{ name: 'yes', node:Cyes__support_self_reference, init:Tyes__support_self_reference}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__support_self_reference, parent:Cno__graph_participation) => new Cno__support_self_reference(init, parent);
				case 'yes': return (init:Tyes__support_self_reference, parent:Cno__graph_participation) => new Cyes__support_self_reference(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__support_self_reference;
				case 'yes': return finalize_yes__support_self_reference;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__graph_participation['support self reference'], parent:Cno__graph_participation) {
			super(data, parent, 'support self reference');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'support self reference'`; }
	}
}
export namespace Cyes__graph_participation {
	export class Dgraph_traversal extends Cgraph_traversal_definition {
		constructor(data:Tyes__graph_participation['graph traversal'], parent:Cyes__graph_participation) {
			super(data, parent, {
				collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection()).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer_value_source()).result!)
			}, 'graph traversal')
		}
	}
}
export namespace Csingular_node_path {
	export class Dpath extends Cnode_path {
		constructor(data:Tsingular_node_path['path'], parent:Csingular_node_path) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Csingular_variablized_object_path {
	export class Dpath extends Cvariablized_object_path {
		constructor(data:Tsingular_variablized_object_path['path'], parent:Csingular_variablized_object_path) {
			super(data, parent, {
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_dependency_location()).result!),
				direction_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cdirection.Punbounded).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.properties.type?.state.node.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Cstate_initializer {
	export class Dnode_initializer extends Cnode_initializer {
		constructor(data:Tstate_initializer['node initializer'], parent:Cstate_initializer) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'node initializer')
		}
	}
	export class Drule_arguments extends AlanDictionary<{ node:Crule_arguments, init:Trule_arguments},Cstate_initializer> {
		protected initialize(parent:Cstate_initializer, key:string, entry_init:Trule_arguments) { return new Crule_arguments(key, entry_init, parent); }
		protected finalize = finalize_rule_arguments
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__state_initializer__rule_arguments_key_nval:application.Crules):boolean {
				const inferences:{
					mandatory: (detach?:boolean) => application.Cmandatory__existence|undefined;} = {

					mandatory: maybe_cache((detach:boolean) => resolve(application__state_initializer__rule_arguments_key_nval)
						.then(context => application__state_initializer__rule_arguments_key_nval)
						.then(context => context?.properties.behaviour)
						.then(context => context?.component_root.output.existence())
						.then(context => context?.variant.name === 'mandatory' ? context.variant.definition as application.Cmandatory__existence : undefined)?.result)
				}
				const $this = {
					inferences: inferences
				}
				if (inferences.mandatory() === undefined)
					return false;
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.rules)
			.then(context => {
				for (let [key,val] of context?.properties.rules) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule arguments'`; }
		constructor(data:Tstate_initializer['rule arguments'], parent:Cstate_initializer) {
			super(data, parent);
		}
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_initializer) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Crule_arguments {
	export class Dexpression extends Cderivation_expression {
		constructor(data:Trule_arguments['expression'], parent:Crule_arguments) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.type)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'entry reference': {
								const application__state_initializer__rule_arguments__expression_in___target___entry_reference_nval = state_group.cast('entry reference');
								return resolve(context)
									.then(context => application__state_initializer__rule_arguments__expression_in___target___entry_reference_nval)
									.then(context => context?.properties.expression)
									.then(context => context?.definitions.reference_value)
									.then(context => context?.definitions.leaf_value)
									.then(context => context?.definitions.value_object).result;
							}
							case 'node reference': {
								const application__state_initializer__rule_arguments__expression_in___target___node_reference_nval = state_group.cast('node reference');
								return resolve(context)
									.then(context => application__state_initializer__rule_arguments__expression_in___target___node_reference_nval)
									.then(context => context?.definitions.reference_value)
									.then(context => context?.definitions.leaf_value)
									.then(context => context?.definitions.value_object).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Ctodo_definition {
	export class Dtodo extends StateGroup<
		{ name: 'no', node:Cno__todo, init:Tno__todo}|
		{ name: 'yes', node:Cyes__todo, init:Tyes__todo}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__todo, parent:Ctodo_definition) => new Cno__todo(init, parent);
				case 'yes': return (init:Tyes__todo, parent:Ctodo_definition) => new Cyes__todo(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__todo;
				case 'yes': return finalize_yes__todo;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttodo_definition['todo'], parent:Ctodo_definition) {
			super(data, parent, 'todo');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'todo'`; }
	}
}
export namespace Cyes__todo {
	export class Drequirement extends Cuser_requirement {
		constructor(data:Tyes__todo['requirement'], parent:Cyes__todo) {
			super(data, parent, {
				definition_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'requirement')
		}
	}
	export class Dui extends Cui_todo {
		constructor(data:Tyes__todo['ui'], parent:Cyes__todo) {
			super(data, parent, 'ui')
		}
	}
}
export namespace Cui_action {
	export class Dexpression extends Cui_expression {
		constructor(data:Tui_action['expression'], parent:Cui_action) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dhas_next_action extends StateGroup<
		{ name: 'no', node:Cno__has_next_action, init:Tno__has_next_action}|
		{ name: 'yes', node:Cyes__has_next_action, init:Tyes__has_next_action}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next_action, parent:Cui_action) => new Cno__has_next_action(init, parent);
				case 'yes': return (init:Tyes__has_next_action, parent:Cui_action) => new Cyes__has_next_action(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next_action;
				case 'yes': return finalize_yes__has_next_action;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_action['has next action'], parent:Cui_action) {
			super(data, parent, 'has next action');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next action'`; }
	}
}
export namespace Cyes__has_next_action {
	export class Daction extends Cui_action {
		constructor(data:Tyes__has_next_action['action'], parent:Cyes__has_next_action) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'action')
		}
	}
}
export namespace Cui_action_attribute {
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_action_attribute, init:Tno__has_description__ui_action_attribute}|
		{ name: 'yes', node:Cyes__has_description__ui_action_attribute, init:Tyes__has_description__ui_action_attribute}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_action_attribute, parent:Cui_action_attribute) => new Cno__has_description__ui_action_attribute(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_action_attribute, parent:Cui_action_attribute) => new Cyes__has_description__ui_action_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_action_attribute;
				case 'yes': return finalize_yes__has_description__ui_action_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_action_attribute['has description'], parent:Cui_action_attribute) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
}
export namespace Cyes__has_description__ui_action_attribute {
}
export namespace Cui_action_interaction {
	export class Dinteractive extends StateGroup<
		{ name: 'no', node:Cno__interactive, init:Tno__interactive}|
		{ name: 'yes', node:Cyes__interactive, init:Tyes__interactive}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__interactive, parent:Cui_action_interaction) => new Cno__interactive(init, parent);
				case 'yes': return (init:Tyes__interactive, parent:Cui_action_interaction) => new Cyes__interactive(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__interactive;
				case 'yes': return finalize_yes__interactive;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_action_interaction['interactive'], parent:Cui_action_interaction) {
			super(data, parent, 'interactive');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'interactive'`; }
	}
}
export namespace Cui_action_visualization {
	export class Dshow_target extends StateGroup<
		{ name: 'no', node:Cno__show_target, init:Tno__show_target}|
		{ name: 'yes', node:Cyes__show_target, init:Tyes__show_target}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__show_target, parent:Cui_action_visualization) => new Cno__show_target(init, parent);
				case 'yes': return (init:Tyes__show_target, parent:Cui_action_visualization) => new Cyes__show_target(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__show_target;
				case 'yes': return finalize_yes__show_target;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_action_visualization['show target'], parent:Cui_action_visualization) {
			super(data, parent, 'show target');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'show target'`; }
	}
}
export namespace Cui_collection_property {
	export class Dbreak_out extends StateGroup<
		{ name: 'no', node:Cno__break_out__ui_collection_property, init:Tno__break_out__ui_collection_property}|
		{ name: 'yes', node:Cyes__break_out__ui_collection_property, init:Tyes__break_out__ui_collection_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__break_out__ui_collection_property, parent:Cui_collection_property) => new Cno__break_out__ui_collection_property(init, parent);
				case 'yes': return (init:Tyes__break_out__ui_collection_property, parent:Cui_collection_property) => new Cyes__break_out__ui_collection_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__break_out__ui_collection_property;
				case 'yes': return finalize_yes__break_out__ui_collection_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['break out'], parent:Cui_collection_property) {
			super(data, parent, 'break out');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'break out'`; }
	}
	export class Dcan_be_dormant extends StateGroup<
		{ name: 'no', node:Cno__can_be_dormant, init:Tno__can_be_dormant}|
		{ name: 'yes', node:Cyes__can_be_dormant, init:Tyes__can_be_dormant}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_be_dormant, parent:Cui_collection_property) => new Cno__can_be_dormant(init, parent);
				case 'yes': return (init:Tyes__can_be_dormant, parent:Cui_collection_property) => new Cyes__can_be_dormant(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__can_be_dormant;
				case 'yes': return finalize_yes__can_be_dormant;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['can be dormant'], parent:Cui_collection_property) {
			super(data, parent, 'can be dormant');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can be dormant'`; }
	}
	export class Dclassification extends Cui_property_classification {
		constructor(data:Tui_collection_property['classification'], parent:Cui_collection_property) {
			super(data, parent, {
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection_property())
					.then(context => context?.parent).result!)
			}, 'classification')
		}
	}
	export class Ddefault_ extends StateGroup<
		{ name: 'no', node:Cno__default__ui_collection_property, init:Tno__default__ui_collection_property}|
		{ name: 'yes', node:Cyes__default__ui_collection_property, init:Tyes__default__ui_collection_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default__ui_collection_property, parent:Cui_collection_property) => new Cno__default__ui_collection_property(init, parent);
				case 'yes': return (init:Tyes__default__ui_collection_property, parent:Cui_collection_property) => new Cyes__default__ui_collection_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default__ui_collection_property;
				case 'yes': return finalize_yes__default__ui_collection_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['default'], parent:Cui_collection_property) {
			super(data, parent, 'default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default'`; }
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_collection_property, init:Tno__has_description__ui_collection_property}|
		{ name: 'yes', node:Cyes__has_description__ui_collection_property, init:Tyes__has_description__ui_collection_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_collection_property, parent:Cui_collection_property) => new Cno__has_description__ui_collection_property(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_collection_property, parent:Cui_collection_property) => new Cyes__has_description__ui_collection_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_collection_property;
				case 'yes': return finalize_yes__has_description__ui_collection_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['has description'], parent:Cui_collection_property) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dhas_style extends StateGroup<
		{ name: 'no', node:Cno__has_style__ui_collection_property, init:Tno__has_style__ui_collection_property}|
		{ name: 'yes', node:Cyes__has_style__ui_collection_property, init:Tyes__has_style__ui_collection_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_style__ui_collection_property, parent:Cui_collection_property) => new Cno__has_style__ui_collection_property(init, parent);
				case 'yes': return (init:Tyes__has_style__ui_collection_property, parent:Cui_collection_property) => new Cyes__has_style__ui_collection_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_style__ui_collection_property;
				case 'yes': return finalize_yes__has_style__ui_collection_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['has style'], parent:Cui_collection_property) {
			super(data, parent, 'has style');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has style'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_collection_property, init:Tno__icon__ui_collection_property}|
		{ name: 'yes', node:Cyes__icon__ui_collection_property, init:Tyes__icon__ui_collection_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_collection_property, parent:Cui_collection_property) => new Cno__icon__ui_collection_property(init, parent);
				case 'yes': return (init:Tyes__icon__ui_collection_property, parent:Cui_collection_property) => new Cyes__icon__ui_collection_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_collection_property;
				case 'yes': return finalize_yes__icon__ui_collection_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['icon'], parent:Cui_collection_property) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
	export class Dsize extends StateGroup<
		{ name: 'large', node:Clarge, init:Tlarge}|
		{ name: 'small', node:Csmall, init:Tsmall}> {
		protected initializer(state: 'large'|'small') {
			switch (state) {
				case 'large': return (init:Tlarge, parent:Cui_collection_property) => new Clarge(init, parent);
				case 'small': return (init:Tsmall, parent:Cui_collection_property) => new Csmall(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'large'|'small') {
			switch (state) {
				case 'large': return finalize_large;
				case 'small': return finalize_small;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['size'], parent:Cui_collection_property) {
			super(data, parent, 'size');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'size'`; }
	}
	export class Dsort extends StateGroup<
		{ name: 'no', node:Cno__sort, init:Tno__sort}|
		{ name: 'yes', node:Cyes__sort, init:Tyes__sort}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__sort, parent:Cui_collection_property) => new Cno__sort(init, parent);
				case 'yes': return (init:Tyes__sort, parent:Cui_collection_property) => new Cyes__sort(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__sort;
				case 'yes': return finalize_yes__sort;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_collection_property['sort'], parent:Cui_collection_property) {
			super(data, parent, 'sort');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'sort'`; }
	}
}
export namespace Cyes__can_be_dormant {
	export class Dexpression extends Cnode_path_tail {
		public readonly inferences:{
			state_node_type: () => application.Cstates__state_group
		}
		constructor(data:Tyes__can_be_dormant['expression'], parent:Cyes__can_be_dormant) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection_property())
					.then(context => context?.properties.node).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!)
			}, 'expression')
			this.inferences = {
				state_node_type: cache((detach:boolean) => {
					const application__ui_collection_property__can_be_dormant__yes__expression_nval = this;
					return resolve(this)
						.then(context => application__ui_collection_property__can_be_dormant__yes__expression_nval)
						.then(context => context?.component_root.output.node_type())
						.then(context => context?.component_root.output.location())
						.then(context => context?.variant.name === 'state' ? context.variant.definition as application.Cstates__state_group : undefined).result!;
				})

			}
		}
	}
}
export namespace Cyes__default__ui_collection_property {
	export class Dentry_filter extends Cnode_path_tail {
		constructor(data:Tyes__default__ui_collection_property['entry filter'], parent:Cyes__default__ui_collection_property) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.key_reference)
					.then(context => context?.component_root.output.definition())
					.then(context => context?.component_root.output.definer())
					.then(context => context?.component_root.output.referenced_node_type()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!)
			}, 'entry filter')
		}
	}
	export class Dkey_reference extends Centry_reference_selector {
		public readonly inferences:{
			key: () => application.Ctext__type__property
		}
		constructor(data:Tyes__default__ui_collection_property['key reference'], parent:Cyes__default__ui_collection_property) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection_property())
					.then(context => context?.properties.node).result!)
			}, 'key reference')
			this.inferences = {
				key: cache((detach:boolean) => {
					const application__ui_collection_property__default__yes__key_reference_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__ui_collection_property__default__yes__key_reference_nval)
								.then(context => context?.component_root.output.property())
								.then(context => context?.parent).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.collection_property())
								.then(context => context?.properties.key_property?.ref).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_description__ui_collection_property {
}
export namespace Cyes__has_style__ui_collection_property {
	export class Dstyle_expression extends Cui_expression {
		constructor(data:Tyes__has_style__ui_collection_property['style expression'], parent:Cyes__has_style__ui_collection_property) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cui_scalar.Pstyle)
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection_property())
					.then(context => context?.properties.node).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'style expression')
		}
	}
}
export namespace Cyes__icon__ui_collection_property {
}
export namespace Cyes__sort {
	export class Ddirection extends StateGroup<
		{ name: 'ascending', node:Cascending, init:Tascending}|
		{ name: 'descending', node:Cdescending, init:Tdescending}> {
		protected initializer(state: 'ascending'|'descending') {
			switch (state) {
				case 'ascending': return (init:Tascending, parent:Cyes__sort) => new Cascending(init, parent);
				case 'descending': return (init:Tdescending, parent:Cyes__sort) => new Cdescending(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ascending'|'descending') {
			switch (state) {
				case 'ascending': return finalize_ascending;
				case 'descending': return finalize_descending;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__sort['direction'], parent:Cyes__sort) {
			super(data, parent, 'direction');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'direction'`; }
	}
	export class Dpath extends Cui_node_path {
		constructor(data:Tyes__sort['path'], parent:Cyes__sort) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.collection_property())
					.then(context => context?.properties.node).result!)
			}, 'path')
		}
	}
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			scalar_value: () => application.Cscalar_value
		}

		constructor(data:string, $this:Cyes__sort) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				scalar_value: cache((detach:boolean) => {
					const application__ui_collection_property__sort__yes__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => application__ui_collection_property__sort__yes__property_nval)
						.then(context => context?.properties.type?.state.node.output.value())
						.then(context => context?.variant.name === 'leaf' ? context.variant.definition as application.Cleaf_value : undefined)
						.then(context => context?.variant.name === 'scalar' ? context.variant.definition as application.Cscalar_value : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Cui_command_attribute {
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_command_attribute, init:Tno__has_description__ui_command_attribute}|
		{ name: 'yes', node:Cyes__has_description__ui_command_attribute, init:Tyes__has_description__ui_command_attribute}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_command_attribute, parent:Cui_command_attribute) => new Cno__has_description__ui_command_attribute(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_command_attribute, parent:Cui_command_attribute) => new Cyes__has_description__ui_command_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_command_attribute;
				case 'yes': return finalize_yes__has_description__ui_command_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_command_attribute['has description'], parent:Cui_command_attribute) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dvisible extends StateGroup<
		{ name: 'false', node:Cfalse__visible__ui_command_attribute, init:Tfalse__visible__ui_command_attribute}|
		{ name: 'true', node:Ctrue__visible__ui_command_attribute, init:Ttrue__visible__ui_command_attribute}> {
		protected initializer(state: 'false'|'true') {
			switch (state) {
				case 'false': return (init:Tfalse__visible__ui_command_attribute, parent:Cui_command_attribute) => new Cfalse__visible__ui_command_attribute(init, parent);
				case 'true': return (init:Ttrue__visible__ui_command_attribute, parent:Cui_command_attribute) => new Ctrue__visible__ui_command_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'false'|'true') {
			switch (state) {
				case 'false': return finalize_false__visible__ui_command_attribute;
				case 'true': return finalize_true__visible__ui_command_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_command_attribute['visible'], parent:Cui_command_attribute) {
			super(data, parent, 'visible');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'visible'`; }
	}
}
export namespace Cyes__has_description__ui_command_attribute {
}
export namespace Cui_entry_expression_list {
	export class Dentry_expression extends Cui_expression {
		constructor(data:Tui_entry_expression_list['entry expression'], parent:Cui_entry_expression_list) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'entry expression')
		}
	}
	export class Dmore_entries extends StateGroup<
		{ name: 'no', node:Cno__more_entries, init:Tno__more_entries}|
		{ name: 'yes', node:Cyes__more_entries, init:Tyes__more_entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__more_entries, parent:Cui_entry_expression_list) => new Cno__more_entries(init, parent);
				case 'yes': return (init:Tyes__more_entries, parent:Cui_entry_expression_list) => new Cyes__more_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__more_entries;
				case 'yes': return finalize_yes__more_entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_entry_expression_list['more entries'], parent:Cui_entry_expression_list) {
			super(data, parent, 'more entries');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'more entries'`; }
	}
}
export namespace Cyes__more_entries {
	export class Dtail extends Cui_entry_expression_list {
		constructor(data:Tyes__more_entries['tail'], parent:Cyes__more_entries) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'tail')
		}
	}
}
export namespace Cui_expression {
	export class Doperation extends StateGroup<
		{ name: 'entry', node:Centry__operation__ui_expression, init:Tentry__operation__ui_expression}|
		{ name: 'entry list', node:Centry_list, init:Tentry_list}|
		{ name: 'execute operation', node:Cexecute_operation__operation__ui_expression, init:Texecute_operation__operation__ui_expression}|
		{ name: 'ignore', node:Cignore__operation__ui_expression, init:Tignore__operation__ui_expression}|
		{ name: 'node switch', node:Cnode_switch__operation__ui_expression, init:Tnode_switch__operation__ui_expression}|
		{ name: 'produce value', node:Cproduce_value__operation__ui_expression, init:Tproduce_value__operation__ui_expression}|
		{ name: 'state switch', node:Cstate_switch__operation__ui_expression, init:Tstate_switch__operation__ui_expression}|
		{ name: 'update properties', node:Cupdate_properties__operation__ui_expression, init:Tupdate_properties__operation__ui_expression}|
		{ name: 'walk', node:Cwalk__operation__ui_expression, init:Twalk__operation__ui_expression}> {
		protected initializer(state: 'entry'|'entry list'|'execute operation'|'ignore'|'node switch'|'produce value'|'state switch'|'update properties'|'walk') {
			switch (state) {
				case 'entry': return (init:Tentry__operation__ui_expression, parent:Cui_expression) => new Centry__operation__ui_expression(init, parent);
				case 'entry list': return (init:Tentry_list, parent:Cui_expression) => new Centry_list(init, parent);
				case 'execute operation': return (init:Texecute_operation__operation__ui_expression, parent:Cui_expression) => new Cexecute_operation__operation__ui_expression(init, parent);
				case 'ignore': return (init:Tignore__operation__ui_expression, parent:Cui_expression) => new Cignore__operation__ui_expression(init, parent);
				case 'node switch': return (init:Tnode_switch__operation__ui_expression, parent:Cui_expression) => new Cnode_switch__operation__ui_expression(init, parent);
				case 'produce value': return (init:Tproduce_value__operation__ui_expression, parent:Cui_expression) => new Cproduce_value__operation__ui_expression(init, parent);
				case 'state switch': return (init:Tstate_switch__operation__ui_expression, parent:Cui_expression) => new Cstate_switch__operation__ui_expression(init, parent);
				case 'update properties': return (init:Tupdate_properties__operation__ui_expression, parent:Cui_expression) => new Cupdate_properties__operation__ui_expression(init, parent);
				case 'walk': return (init:Twalk__operation__ui_expression, parent:Cui_expression) => new Cwalk__operation__ui_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry'|'entry list'|'execute operation'|'ignore'|'node switch'|'produce value'|'state switch'|'update properties'|'walk') {
			switch (state) {
				case 'entry': return finalize_entry__operation__ui_expression;
				case 'entry list': return finalize_entry_list;
				case 'execute operation': return finalize_execute_operation__operation__ui_expression;
				case 'ignore': return finalize_ignore__operation__ui_expression;
				case 'node switch': return finalize_node_switch__operation__ui_expression;
				case 'produce value': return finalize_produce_value__operation__ui_expression;
				case 'state switch': return finalize_state_switch__operation__ui_expression;
				case 'update properties': return finalize_update_properties__operation__ui_expression;
				case 'walk': return finalize_walk__operation__ui_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_expression['operation'], parent:Cui_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Centry__operation__ui_expression {
	export class Dtype extends StateGroup<
		{ name: 'create', node:Ccreate__type__entry__operation__ui_expression, init:Tcreate__type__entry__operation__ui_expression}|
		{ name: 'delete', node:Cdelete__type__entry__operation__ui_expression, init:Tdelete__type__entry__operation__ui_expression}> {
		protected initializer(state: 'create'|'delete') {
			switch (state) {
				case 'create': return (init:Tcreate__type__entry__operation__ui_expression, parent:Centry__operation__ui_expression) => new Ccreate__type__entry__operation__ui_expression(init, parent);
				case 'delete': return (init:Tdelete__type__entry__operation__ui_expression, parent:Centry__operation__ui_expression) => new Cdelete__type__entry__operation__ui_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'create'|'delete') {
			switch (state) {
				case 'create': return finalize_create__type__entry__operation__ui_expression;
				case 'delete': return finalize_delete__type__entry__operation__ui_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry__operation__ui_expression['type'], parent:Centry__operation__ui_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccreate__type__entry__operation__ui_expression {
	export class Dexpression extends Cui_object_expression {
		constructor(data:Tcreate__type__entry__operation__ui_expression['expression'], parent:Ccreate__type__entry__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.target_collection())
					.then(context => context?.component_root.output.entry_object()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dinteraction extends Cui_action_interaction {
		constructor(data:Tcreate__type__entry__operation__ui_expression['interaction'], parent:Ccreate__type__entry__operation__ui_expression) {
			super(data, parent, 'interaction')
		}
	}
	export class Dvisualization extends Cui_action_visualization {
		constructor(data:Tcreate__type__entry__operation__ui_expression['visualization'], parent:Ccreate__type__entry__operation__ui_expression) {
			super(data, parent, 'visualization')
		}
	}
}
export namespace Cdelete__type__entry__operation__ui_expression {
	export class Dinteraction extends Cui_action_interaction {
		constructor(data:Tdelete__type__entry__operation__ui_expression['interaction'], parent:Cdelete__type__entry__operation__ui_expression) {
			super(data, parent, 'interaction')
		}
	}
	export class Dpath extends Csingular_node_path {
		public readonly inferences:{
			collection_node_type: () => application.Cnode__application
		}
		constructor(data:Tdelete__type__entry__operation__ui_expression['path'], parent:Cdelete__type__entry__operation__ui_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
			this.inferences = {
				collection_node_type: cache((detach:boolean) => {
					const application__ui_expression__operation__entry__type__delete__path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__ui_expression__operation__entry__type__delete__path_nval)
								.then(context => context?.component_root.output.node_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.collection_property())
								.then(context => context?.properties.node).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Centry_list {
	export class Dentries extends Cui_entry_expression_list {
		constructor(data:Tentry_list['entries'], parent:Centry_list) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.collection()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'entries')
		}
	}
}
export namespace Cexecute_operation__operation__ui_expression {
	export class Dexpression extends Cui_object_expression {
		constructor(data:Texecute_operation__operation__ui_expression['expression'], parent:Cexecute_operation__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.operation?.inferences.parameter_definition())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_object({name: 'node', definition: conv_context},{
						operation: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cui_properties_operation.Pinitialization).result!

						});
					}).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dinteraction extends Cui_action_interaction {
		constructor(data:Texecute_operation__operation__ui_expression['interaction'], parent:Cexecute_operation__operation__ui_expression) {
			super(data, parent, 'interaction')
		}
	}
	export class Doperation extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			attribute_class: () => application.Cattribute_class,
			parameter_definition: () => application.Cnode__application
		}

		constructor(data:string, $this:Cexecute_operation__operation__ui_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				attribute_class: cache((detach:boolean) => {
					const application__ui_expression__operation__execute_operation__operation_nval = $this.properties.operation.ref;
					return resolve($this.properties.operation.ref)
						.then(context => application__ui_expression__operation__execute_operation__operation_nval)
						.then(context => context?.properties.type)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'action': {
									const application__ui_expression__operation__execute_operation__operation_inf___attribute_class___action_nval = state_group.cast('action');
									return resolve(context)
										.then(context => application__ui_expression__operation__execute_operation__operation_inf___attribute_class___action_nval)
										.then(context => context?.properties.parameters)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cattribute_class({name: 'operation', definition: conv_context});
										}).result;
								}
								case 'command': {
									const application__ui_expression__operation__execute_operation__operation_inf___attribute_class___command_nval = state_group.cast('command');
									return resolve(context)
										.then(context => application__ui_expression__operation__execute_operation__operation_inf___attribute_class___command_nval)
										.then(context => context?.properties.parameters)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cattribute_class({name: 'operation', definition: conv_context});
										}).result;
								}
								case 'property': {
									return resolve(context)
										.then(() => $this)
										.then(() => application.Cattribute_class.Pvalue)
										.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Cnode__application : undefined)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cattribute_class({name: 'operation', definition: conv_context});
										}).result;
								}
								case 'reference set': {
									return resolve(context)
										.then(() => $this)
										.then(() => application.Cattribute_class.Pvalue)
										.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Cnode__application : undefined)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cattribute_class({name: 'operation', definition: conv_context});
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result!;
				})
				,
				parameter_definition: cache((detach:boolean) => {
					const application__ui_expression__operation__execute_operation__operation_nval = $this.properties.operation.ref;
					return resolve($this.properties.operation.ref)
						.then(() => $this.properties.operation.inferences.attribute_class())
						.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
	export class Dpath extends Csingular_node_path {
		constructor(data:Texecute_operation__operation__ui_expression['path'], parent:Cexecute_operation__operation__ui_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Cnode_switch__operation__ui_expression {
	export class Dexpression extends Cnode_expression {
		constructor(data:Tnode_switch__operation__ui_expression['expression'], parent:Cnode_switch__operation__ui_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Ccases__node_switch__operation__ui_expression {
}
export namespace Cnode_case__cases__node_switch__operation__ui_expression {
	export class Dexpression extends Cui_expression {
		constructor(data:Tnode_case__cases__node_switch__operation__ui_expression['expression'], parent:Cnode_case__cases__node_switch__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Tnode_case__cases__node_switch__operation__ui_expression['variable assignment'], parent:Cnode_case__cases__node_switch__operation__ui_expression) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cnone_case__cases__node_switch__operation__ui_expression {
	export class Dexpression extends Cui_expression {
		constructor(data:Tnone_case__cases__node_switch__operation__ui_expression['expression'], parent:Cnone_case__cases__node_switch__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cproduce_value__operation__ui_expression {
	export class Dvalue extends StateGroup<
		{ name: 'object', node:Cobject__value__produce_value__operation__ui_expression, init:Tobject__value__produce_value__operation__ui_expression}|
		{ name: 'scalar', node:Cscalar__value__produce_value__operation__ui_expression, init:Tscalar__value__produce_value__operation__ui_expression}|
		{ name: 'state', node:Cstate__value__produce_value__operation__ui_expression, init:Tstate__value__produce_value__operation__ui_expression}> {
		protected initializer(state: 'object'|'scalar'|'state') {
			switch (state) {
				case 'object': return (init:Tobject__value__produce_value__operation__ui_expression, parent:Cproduce_value__operation__ui_expression) => new Cobject__value__produce_value__operation__ui_expression(init, parent);
				case 'scalar': return (init:Tscalar__value__produce_value__operation__ui_expression, parent:Cproduce_value__operation__ui_expression) => new Cscalar__value__produce_value__operation__ui_expression(init, parent);
				case 'state': return (init:Tstate__value__produce_value__operation__ui_expression, parent:Cproduce_value__operation__ui_expression) => new Cstate__value__produce_value__operation__ui_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'object'|'scalar'|'state') {
			switch (state) {
				case 'object': return finalize_object__value__produce_value__operation__ui_expression;
				case 'scalar': return finalize_scalar__value__produce_value__operation__ui_expression;
				case 'state': return finalize_state__value__produce_value__operation__ui_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproduce_value__operation__ui_expression['value'], parent:Cproduce_value__operation__ui_expression) {
			super(data, parent, 'value');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cobject__value__produce_value__operation__ui_expression {
	export class Dexpression extends Cui_object_expression {
		constructor(data:Tobject__value__produce_value__operation__ui_expression['expression'], parent:Cobject__value__produce_value__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.target_object()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cscalar__value__produce_value__operation__ui_expression {
	export class Dexpression extends Cui_scalar_value_expression {
		constructor(data:Tscalar__value__produce_value__operation__ui_expression['expression'], parent:Cscalar__value__produce_value__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.target_scalar()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cstate__value__produce_value__operation__ui_expression {
	export class Dexpression extends Cui_object_expression {
		constructor(data:Tstate__value__produce_value__operation__ui_expression['expression'], parent:Cstate__value__produce_value__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.inferences.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dstate extends Reference<application.Cui_target_state,string> {
		public readonly inferences:{
			target: () => application.Cui_object
		}

		constructor(data:string, $this:Cstate__value__produce_value__operation__ui_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.target_state_group())
				.then(context => {
					switch (context?.variant.name) {
						case 'stategroup property': {
							const application__ui_expression__operation__produce_value__value__state__state___stategroup_property_nval = context.cast('stategroup property');
							return resolve(context)
								.then(context => application__ui_expression__operation__produce_value__value__state__state___stategroup_property_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cui_target_state({name: 'state', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
			this.inferences = {
				target: cache((detach:boolean) => {
					const application__ui_expression__operation__produce_value__value__state__state_nval = $this.properties.state.ref;
					return resolve($this.properties.state.ref)
						.then(context => application__ui_expression__operation__produce_value__value__state__state_nval)
						.then(context => {
							switch (context?.variant.name) {
								case 'state': {
									const application__ui_expression__operation__produce_value__value__state__state_inf___target___state_nval = context.cast('state');
									return resolve(context)
										.then(context => application__ui_expression__operation__produce_value__value__state__state_inf___target___state_nval)
										.then(context => context?.properties.node)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cui_object({name: 'node', definition: conv_context},{
											operation: () => resolve($this)
												.then(() => $this)
												.then(() => application.Cui_properties_operation.Pinitialization).result!

											});
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstate_switch__operation__ui_expression {
	export class Dstates extends AlanDictionary<{ node:Cstates__state_switch__operation__ui_expression, init:Tstates__state_switch__operation__ui_expression},Cstate_switch__operation__ui_expression> {
		protected initialize(parent:Cstate_switch__operation__ui_expression, key:string, entry_init:Tstates__state_switch__operation__ui_expression) { return new Cstates__state_switch__operation__ui_expression(key, entry_init, parent); }
		protected finalize = finalize_states__state_switch__operation__ui_expression
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__ui_expression__operation__state_switch__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.path)
			.then(context => context?.properties.stategroup?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_switch__operation__ui_expression['states'], parent:Cstate_switch__operation__ui_expression) {
			super(data, parent);
		}
	}
}
export namespace Cpath__state_switch__operation__ui_expression {
	export class Dpath extends Cui_parametrized_node_path {
		constructor(data:Tpath__state_switch__operation__ui_expression['path'], parent:Cpath__state_switch__operation__ui_expression) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dstategroup extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cpath__state_switch__operation__ui_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'stategroup'`; }
	}
}
export namespace Cstates__state_switch__operation__ui_expression {
	export class Dexpression extends Cui_expression {
		constructor(data:Tstates__state_switch__operation__ui_expression['expression'], parent:Cstates__state_switch__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Tstates__state_switch__operation__ui_expression['variable assignment'], parent:Cstates__state_switch__operation__ui_expression) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cupdate_properties__operation__ui_expression {
	export class Dinteraction extends Cui_action_interaction {
		constructor(data:Tupdate_properties__operation__ui_expression['interaction'], parent:Cupdate_properties__operation__ui_expression) {
			super(data, parent, 'interaction')
		}
	}
	export class Dpath extends Csingular_node_path {
		public readonly inferences:{
			user_value: () => application.Cuser__external_source
		}
		constructor(data:Tupdate_properties__operation__ui_expression['path'], parent:Cupdate_properties__operation__ui_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
			this.inferences = {
				user_value: cache((detach:boolean) => {
					const application__ui_expression__operation__update_properties__path_nval = this;
					return resolve(this)
						.then(context => application__ui_expression__operation__update_properties__path_nval)
						.then(context => context?.component_root.output.node_type())
						.then(context => context?.component_root.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
						.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
						.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
				})

			}
		}
	}
	export class Dtarget extends StateGroup<
		{ name: 'node', node:Cnode__target__update_properties__operation__ui_expression, init:Tnode__target__update_properties__operation__ui_expression}|
		{ name: 'property', node:Cproperty__target__update_properties__operation__ui_expression, init:Tproperty__target__update_properties__operation__ui_expression}> {
		protected initializer(state: 'node'|'property') {
			switch (state) {
				case 'node': return (init:Tnode__target__update_properties__operation__ui_expression, parent:Cupdate_properties__operation__ui_expression) => new Cnode__target__update_properties__operation__ui_expression(init, parent);
				case 'property': return (init:Tproperty__target__update_properties__operation__ui_expression, parent:Cupdate_properties__operation__ui_expression) => new Cproperty__target__update_properties__operation__ui_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'property') {
			switch (state) {
				case 'node': return finalize_node__target__update_properties__operation__ui_expression;
				case 'property': return finalize_property__target__update_properties__operation__ui_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tupdate_properties__operation__ui_expression['target'], parent:Cupdate_properties__operation__ui_expression) {
			super(data, parent, 'target');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'target'`; }
	}
	export class Dvisualization extends Cui_action_visualization {
		constructor(data:Tupdate_properties__operation__ui_expression['visualization'], parent:Cupdate_properties__operation__ui_expression) {
			super(data, parent, 'visualization')
		}
	}
}
export namespace Cnode__target__update_properties__operation__ui_expression {
	export class Dexpression extends Cui_object_expression {
		constructor(data:Tnode__target__update_properties__operation__ui_expression['expression'], parent:Cnode__target__update_properties__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cproperty__target__update_properties__operation__ui_expression {
	export class Dexpression extends Cui_expression {
		constructor(data:Tproperty__target__update_properties__operation__ui_expression['expression'], parent:Cproperty__target__update_properties__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.property?.inferences.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			target: () => application.Cui_target,
			user_value: () => application.Cuser__external_source
		}

		constructor(data:string, $this:Cproperty__target__update_properties__operation__ui_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				target: cache((detach:boolean) => {
					const application__ui_expression__operation__update_properties__target__property__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => evaluate__ui_property_target_derivation(detach, 
							resolve(context)
								.then(context => application__ui_expression__operation__update_properties__target__property__property_nval).result!, {
							operation: () => resolve($this)
								.then(() => $this)
								.then(() => application.Cui_properties_operation.Pupdate).result!

							})).result!;
				})
				,
				user_value: cache((detach:boolean) => {
					const application__ui_expression__operation__update_properties__target__property__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => application__ui_expression__operation__update_properties__target__property__property_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
						.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
						.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Cwalk__operation__ui_expression {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Cwalk__operation__ui_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dexpression extends Cui_expression {
		constructor(data:Twalk__operation__ui_expression['expression'], parent:Cwalk__operation__ui_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'expression')
		}
	}
	export class Dpath extends Csingular_node_path {
		constructor(data:Twalk__operation__ui_expression['path'], parent:Cwalk__operation__ui_expression) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dtail extends Cnode_path_tail {
		constructor(data:Twalk__operation__ui_expression['tail'], parent:Cwalk__operation__ui_expression) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pentity).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!)
			}, 'tail')
		}
	}
	export class Dvariable_assignment extends Coptional_variable_assignment {
		constructor(data:Twalk__operation__ui_expression['variable assignment'], parent:Cwalk__operation__ui_expression) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.tail)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cui_file_property {
	export class Dclassification extends Cui_property_classification {
		constructor(data:Tui_file_property['classification'], parent:Cui_file_property) {
			super(data, parent, {
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.file_property())
					.then(context => context?.parent).result!)
			}, 'classification')
		}
	}
	export class Dfile_name_expression extends StateGroup<
		{ name: 'no', node:Cno__file_name_expression, init:Tno__file_name_expression}|
		{ name: 'yes', node:Cyes__file_name_expression, init:Tyes__file_name_expression}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__file_name_expression, parent:Cui_file_property) => new Cno__file_name_expression(init, parent);
				case 'yes': return (init:Tyes__file_name_expression, parent:Cui_file_property) => new Cyes__file_name_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__file_name_expression;
				case 'yes': return finalize_yes__file_name_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_file_property['file name expression'], parent:Cui_file_property) {
			super(data, parent, 'file name expression');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'file name expression'`; }
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_file_property, init:Tno__has_description__ui_file_property}|
		{ name: 'yes', node:Cyes__has_description__ui_file_property, init:Tyes__has_description__ui_file_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_file_property, parent:Cui_file_property) => new Cno__has_description__ui_file_property(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_file_property, parent:Cui_file_property) => new Cyes__has_description__ui_file_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_file_property;
				case 'yes': return finalize_yes__has_description__ui_file_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_file_property['has description'], parent:Cui_file_property) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_file_property, init:Tno__icon__ui_file_property}|
		{ name: 'yes', node:Cyes__icon__ui_file_property, init:Tyes__icon__ui_file_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_file_property, parent:Cui_file_property) => new Cno__icon__ui_file_property(init, parent);
				case 'yes': return (init:Tyes__icon__ui_file_property, parent:Cui_file_property) => new Cyes__icon__ui_file_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_file_property;
				case 'yes': return finalize_yes__icon__ui_file_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_file_property['icon'], parent:Cui_file_property) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
}
export namespace Cyes__file_name_expression {
	export class Dfile_name_expression extends Cui_scalar_default {
		constructor(data:Tyes__file_name_expression['file name expression'], parent:Cyes__file_name_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_text({name: 'intermediate result', definition: conv_context});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.file_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'file name expression')
		}
	}
}
export namespace Cyes__has_description__ui_file_property {
}
export namespace Cyes__icon__ui_file_property {
}
export namespace Cui_group_property {
	export class Dbreak_out extends StateGroup<
		{ name: 'no', node:Cno__break_out__ui_group_property, init:Tno__break_out__ui_group_property}|
		{ name: 'yes', node:Cyes__break_out__ui_group_property, init:Tyes__break_out__ui_group_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__break_out__ui_group_property, parent:Cui_group_property) => new Cno__break_out__ui_group_property(init, parent);
				case 'yes': return (init:Tyes__break_out__ui_group_property, parent:Cui_group_property) => new Cyes__break_out__ui_group_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__break_out__ui_group_property;
				case 'yes': return finalize_yes__break_out__ui_group_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_group_property['break out'], parent:Cui_group_property) {
			super(data, parent, 'break out');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'break out'`; }
	}
	export class Dclassification extends Cui_property_classification {
		constructor(data:Tui_group_property['classification'], parent:Cui_group_property) {
			super(data, parent, {
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.group_property())
					.then(context => context?.parent).result!)
			}, 'classification')
		}
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_group_property, init:Tno__has_description__ui_group_property}|
		{ name: 'yes', node:Cyes__has_description__ui_group_property, init:Tyes__has_description__ui_group_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_group_property, parent:Cui_group_property) => new Cno__has_description__ui_group_property(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_group_property, parent:Cui_group_property) => new Cyes__has_description__ui_group_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_group_property;
				case 'yes': return finalize_yes__has_description__ui_group_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_group_property['has description'], parent:Cui_group_property) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_group_property, init:Tno__icon__ui_group_property}|
		{ name: 'yes', node:Cyes__icon__ui_group_property, init:Tyes__icon__ui_group_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_group_property, parent:Cui_group_property) => new Cno__icon__ui_group_property(init, parent);
				case 'yes': return (init:Tyes__icon__ui_group_property, parent:Cui_group_property) => new Cyes__icon__ui_group_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_group_property;
				case 'yes': return finalize_yes__icon__ui_group_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_group_property['icon'], parent:Cui_group_property) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
}
export namespace Cyes__has_description__ui_group_property {
}
export namespace Cyes__icon__ui_group_property {
}
export namespace Cui_identifying_property_selection {
	export class Dhas_properties extends StateGroup<
		{ name: 'no', node:Cno__has_properties, init:Tno__has_properties}|
		{ name: 'yes', node:Cyes__has_properties, init:Tyes__has_properties}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_properties, parent:Cui_identifying_property_selection) => new Cno__has_properties(init, parent);
				case 'yes': return (init:Tyes__has_properties, parent:Cui_identifying_property_selection) => new Cyes__has_properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_properties;
				case 'yes': return finalize_yes__has_properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_identifying_property_selection['has properties'], parent:Cui_identifying_property_selection) {
			super(data, parent, 'has properties');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has properties'`; }
	}
	export class Dproperties extends AlanTopology<{ node:Cproperties__ui_identifying_property_selection, init:Tproperties__ui_identifying_property_selection},Cui_identifying_property_selection,'view order'> {
		protected graph_iterator(graph:('view order')):(node:Cproperties__ui_identifying_property_selection) => Cproperties__ui_identifying_property_selection {
			switch (graph) {
				case 'view order': return (entry:Cproperties__ui_identifying_property_selection) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as application.Cyes__has_successor__properties;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cui_identifying_property_selection, key:string, entry_init:Tproperties__ui_identifying_property_selection) { return new Cproperties__ui_identifying_property_selection(key, entry_init, parent); }
		protected finalize = finalize_properties__ui_identifying_property_selection
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tui_identifying_property_selection['properties'], parent:Cui_identifying_property_selection) {
			super(data, parent);
		}
	}
}
export namespace Cyes__has_properties {
	export class Dfirst extends Reference<application.Cproperties__ui_identifying_property_selection,string> {

		constructor(data:string, $this:Cyes__has_properties) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cproperties__ui_identifying_property_selection {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__properties, init:Tno__has_successor__properties}|
		{ name: 'yes', node:Cyes__has_successor__properties, init:Tyes__has_successor__properties}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__properties, parent:Cproperties__ui_identifying_property_selection) => new Cno__has_successor__properties(init, parent);
				case 'yes': return (init:Tyes__has_successor__properties, parent:Cproperties__ui_identifying_property_selection) => new Cyes__has_successor__properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__properties;
				case 'yes': return finalize_yes__has_successor__properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__ui_identifying_property_selection['has successor'], parent:Cproperties__ui_identifying_property_selection) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dvalue_type extends StateGroup<
		{ name: 'choice', node:Cchoice__value_type__properties, init:Tchoice__value_type__properties}|
		{ name: 'node', node:Cnode__value_type__properties, init:Tnode__value_type__properties}|
		{ name: 'scalar', node:Cscalar__value_type, init:Tscalar__value_type}> {
		protected initializer(state: 'choice'|'node'|'scalar') {
			switch (state) {
				case 'choice': return (init:Tchoice__value_type__properties, parent:Cproperties__ui_identifying_property_selection) => new Cchoice__value_type__properties(init, parent);
				case 'node': return (init:Tnode__value_type__properties, parent:Cproperties__ui_identifying_property_selection) => new Cnode__value_type__properties(init, parent);
				case 'scalar': return (init:Tscalar__value_type, parent:Cproperties__ui_identifying_property_selection) => new Cscalar__value_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'choice'|'node'|'scalar') {
			switch (state) {
				case 'choice': return finalize_choice__value_type__properties;
				case 'node': return finalize_node__value_type__properties;
				case 'scalar': return finalize_scalar__value_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__ui_identifying_property_selection['value type'], parent:Cproperties__ui_identifying_property_selection) {
			super(data, parent, 'value type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value type'`; }
	}
}
export namespace Cyes__has_successor__properties {
	export class Dsuccessor extends Reference<application.Cproperties__ui_identifying_property_selection,string> {

		constructor(data:string, $this:Cyes__has_successor__properties) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Cchoice__value_type__properties {
	export class Dfirst_state extends Reference<application.Cstates__choice,string> {

		constructor(data:string, $this:Cchoice__value_type__properties) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first state'`; }
	}
	export class Dstate_key_is_identifying extends StateGroup<
		{ name: 'no', node:Cno__state_key_is_identifying, init:Tno__state_key_is_identifying}|
		{ name: 'yes', node:Cyes__state_key_is_identifying, init:Tyes__state_key_is_identifying}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__state_key_is_identifying, parent:Cchoice__value_type__properties) => new Cno__state_key_is_identifying(init, parent);
				case 'yes': return (init:Tyes__state_key_is_identifying, parent:Cchoice__value_type__properties) => new Cyes__state_key_is_identifying(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__state_key_is_identifying;
				case 'yes': return finalize_yes__state_key_is_identifying;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tchoice__value_type__properties['state key is identifying'], parent:Cchoice__value_type__properties) {
			super(data, parent, 'state key is identifying');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state key is identifying'`; }
	}
	export class Dstates extends AlanTopology<{ node:Cstates__choice, init:Tstates__choice},Cchoice__value_type__properties,'view order'> {
		protected graph_iterator(graph:('view order')):(node:Cstates__choice) => Cstates__choice {
			switch (graph) {
				case 'view order': return (entry:Cstates__choice) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as application.Cyes__has_successor__states__choice;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cchoice__value_type__properties, key:string, entry_init:Tstates__choice) { return new Cstates__choice(key, entry_init, parent); }
		protected finalize = finalize_states__choice
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tchoice__value_type__properties['states'], parent:Cchoice__value_type__properties) {
			super(data, parent);
		}
	}
}
export namespace Cstates__choice {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__states__choice, init:Tno__has_successor__states__choice}|
		{ name: 'yes', node:Cyes__has_successor__states__choice, init:Tyes__has_successor__states__choice}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__states__choice, parent:Cstates__choice) => new Cno__has_successor__states__choice(init, parent);
				case 'yes': return (init:Tyes__has_successor__states__choice, parent:Cstates__choice) => new Cyes__has_successor__states__choice(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__states__choice;
				case 'yes': return finalize_yes__has_successor__states__choice;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__choice['has successor'], parent:Cstates__choice) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dselection extends Cui_identifying_property_selection {
		constructor(data:Tstates__choice['selection'], parent:Cstates__choice) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!)
			}, 'selection')
		}
	}
}
export namespace Cyes__has_successor__states__choice {
	export class Dsuccessor extends Reference<application.Cstates__choice,string> {

		constructor(data:string, $this:Cyes__has_successor__states__choice) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Cnode__value_type__properties {
	export class Dselection extends Cui_identifying_property_selection {
		constructor(data:Tnode__value_type__properties['selection'], parent:Cnode__value_type__properties) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group_property())
					.then(context => context?.properties.node).result!)
			}, 'selection')
		}
	}
}
export namespace Cui_node_path {
	export class Dpath extends Cnode_path {
		constructor(data:Tui_node_path['path'], parent:Cui_node_path) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'path')
		}
	}
}
export namespace Cui_number_property {
	export class Dclassification extends Cui_property_classification {
		constructor(data:Tui_number_property['classification'], parent:Cui_number_property) {
			super(data, parent, {
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => context?.parent).result!)
			}, 'classification')
		}
	}
	export class Ddefault_ extends StateGroup<
		{ name: 'no', node:Cno__default__ui_number_property, init:Tno__default__ui_number_property}|
		{ name: 'yes', node:Cyes__default__ui_number_property, init:Tyes__default__ui_number_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default__ui_number_property, parent:Cui_number_property) => new Cno__default__ui_number_property(init, parent);
				case 'yes': return (init:Tyes__default__ui_number_property, parent:Cui_number_property) => new Cyes__default__ui_number_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default__ui_number_property;
				case 'yes': return finalize_yes__default__ui_number_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_number_property['default'], parent:Cui_number_property) {
			super(data, parent, 'default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default'`; }
	}
	export class Ddynamic_numerical_type extends StateGroup<
		{ name: 'no', node:Cno__dynamic_numerical_type, init:Tno__dynamic_numerical_type}|
		{ name: 'yes', node:Cyes__dynamic_numerical_type, init:Tyes__dynamic_numerical_type}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__dynamic_numerical_type, parent:Cui_number_property) => new Cno__dynamic_numerical_type(init, parent);
				case 'yes': return (init:Tyes__dynamic_numerical_type, parent:Cui_number_property) => new Cyes__dynamic_numerical_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__dynamic_numerical_type;
				case 'yes': return finalize_yes__dynamic_numerical_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_number_property['dynamic numerical type'], parent:Cui_number_property) {
			super(data, parent, 'dynamic numerical type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'dynamic numerical type'`; }
	}
	export class Demphasis extends StateGroup<
		{ name: 'no', node:Cno__emphasis__ui_number_property, init:Tno__emphasis__ui_number_property}|
		{ name: 'yes', node:Cyes__emphasis__ui_number_property, init:Tyes__emphasis__ui_number_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__emphasis__ui_number_property, parent:Cui_number_property) => new Cno__emphasis__ui_number_property(init, parent);
				case 'yes': return (init:Tyes__emphasis__ui_number_property, parent:Cui_number_property) => new Cyes__emphasis__ui_number_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__emphasis__ui_number_property;
				case 'yes': return finalize_yes__emphasis__ui_number_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_number_property['emphasis'], parent:Cui_number_property) {
			super(data, parent, 'emphasis');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'emphasis'`; }
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_number_property, init:Tno__has_description__ui_number_property}|
		{ name: 'yes', node:Cyes__has_description__ui_number_property, init:Tyes__has_description__ui_number_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_number_property, parent:Cui_number_property) => new Cno__has_description__ui_number_property(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_number_property, parent:Cui_number_property) => new Cyes__has_description__ui_number_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_number_property;
				case 'yes': return finalize_yes__has_description__ui_number_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_number_property['has description'], parent:Cui_number_property) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_number_property, init:Tno__icon__ui_number_property}|
		{ name: 'yes', node:Cyes__icon__ui_number_property, init:Tyes__icon__ui_number_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_number_property, parent:Cui_number_property) => new Cno__icon__ui_number_property(init, parent);
				case 'yes': return (init:Tyes__icon__ui_number_property, parent:Cui_number_property) => new Cyes__icon__ui_number_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_number_property;
				case 'yes': return finalize_yes__icon__ui_number_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_number_property['icon'], parent:Cui_number_property) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
	export class Dmetadata extends StateGroup<
		{ name: 'no', node:Cno__metadata, init:Tno__metadata}|
		{ name: 'yes', node:Cyes__metadata, init:Tyes__metadata}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__metadata, parent:Cui_number_property) => new Cno__metadata(init, parent);
				case 'yes': return (init:Tyes__metadata, parent:Cui_number_property) => new Cyes__metadata(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__metadata;
				case 'yes': return finalize_yes__metadata;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_number_property['metadata'], parent:Cui_number_property) {
			super(data, parent, 'metadata');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'metadata'`; }
	}
}
export namespace Cyes__default__ui_number_property {
	export class Ddefault_ extends Cui_scalar_default {
		constructor(data:Tyes__default__ui_number_property['default'], parent:Cyes__default__ui_number_property) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'number property', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.number_property())
							.then(context => context?.properties.numerical_type?.ref).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.number_property())
							.then(context => context?.properties.set_type?.state.node.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'default')
		}
	}
}
export namespace Cyes__dynamic_numerical_type {
	export class Dbinding_path extends Cnode_path {
		public readonly inferences:{
			node_type: () => application.Cnode__application
		}
		constructor(data:Tyes__dynamic_numerical_type['binding path'], parent:Cyes__dynamic_numerical_type) {
			super(data, parent, {
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'binding path')
			this.inferences = {
				node_type: cache((detach:boolean) => {
					const application__ui_number_property__dynamic_numerical_type__yes__binding_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__ui_number_property__dynamic_numerical_type__yes__binding_path_nval)
								.then(context => context?.component_root.output.node_type()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.dynamic_numerical_type())
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_description__ui_number_property {
}
export namespace Cyes__icon__ui_number_property {
}
export namespace Cvalidation {
	export class Dhas_maximum extends StateGroup<
		{ name: 'no', node:Cno__has_maximum, init:Tno__has_maximum}|
		{ name: 'yes', node:Cyes__has_maximum, init:Tyes__has_maximum}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_maximum, parent:Cvalidation) => new Cno__has_maximum(init, parent);
				case 'yes': return (init:Tyes__has_maximum, parent:Cvalidation) => new Cyes__has_maximum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_maximum;
				case 'yes': return finalize_yes__has_maximum;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalidation['has maximum'], parent:Cvalidation) {
			super(data, parent, 'has maximum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has maximum'`; }
	}
	export class Dhas_minimum extends StateGroup<
		{ name: 'no', node:Cno__has_minimum, init:Tno__has_minimum}|
		{ name: 'yes', node:Cyes__has_minimum, init:Tyes__has_minimum}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_minimum, parent:Cvalidation) => new Cno__has_minimum(init, parent);
				case 'yes': return (init:Tyes__has_minimum, parent:Cvalidation) => new Cyes__has_minimum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_minimum;
				case 'yes': return finalize_yes__has_minimum;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalidation['has minimum'], parent:Cvalidation) {
			super(data, parent, 'has minimum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has minimum'`; }
	}
}
export namespace Cyes__has_maximum {
	export class Dmaximum extends Cui_expression {
		constructor(data:Tyes__has_maximum['maximum'], parent:Cyes__has_maximum) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'number property', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.number_property())
							.then(context => context?.properties.numerical_type?.ref).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.number_property())
							.then(context => context?.properties.set_type?.state.node.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar)
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'maximum')
		}
	}
}
export namespace Cyes__has_minimum {
	export class Dminimum extends Cui_expression {
		constructor(data:Tyes__has_minimum['minimum'], parent:Cyes__has_minimum) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'number property', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.number_property())
							.then(context => context?.properties.numerical_type?.ref).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.number_property())
							.then(context => context?.properties.set_type?.state.node.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar)
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.number_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'minimum')
		}
	}
}
export namespace Cui_numerical_type {
	export class Drepresent_as extends StateGroup<
		{ name: 'custom type', node:Ccustom_type, init:Tcustom_type}|
		{ name: 'date', node:Cdate, init:Tdate}|
		{ name: 'date and time', node:Cdate_and_time, init:Tdate_and_time}|
		{ name: 'duration', node:Cduration, init:Tduration}|
		{ name: 'model', node:Cmodel, init:Tmodel}> {
		protected initializer(state: 'custom type'|'date'|'date and time'|'duration'|'model') {
			switch (state) {
				case 'custom type': return (init:Tcustom_type, parent:Cui_numerical_type) => new Ccustom_type(init, parent);
				case 'date': return (init:Tdate, parent:Cui_numerical_type) => new Cdate(init, parent);
				case 'date and time': return (init:Tdate_and_time, parent:Cui_numerical_type) => new Cdate_and_time(init, parent);
				case 'duration': return (init:Tduration, parent:Cui_numerical_type) => new Cduration(init, parent);
				case 'model': return (init:Tmodel, parent:Cui_numerical_type) => new Cmodel(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'custom type'|'date'|'date and time'|'duration'|'model') {
			switch (state) {
				case 'custom type': return finalize_custom_type;
				case 'date': return finalize_date;
				case 'date and time': return finalize_date_and_time;
				case 'duration': return finalize_duration;
				case 'model': return finalize_model;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_numerical_type['represent as'], parent:Cui_numerical_type) {
			super(data, parent, 'represent as');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'represent as'`; }
	}
}
export namespace Ccustom_type {
	export class Dbinding extends StateGroup<
		{ name: 'dynamic', node:Cdynamic__binding, init:Tdynamic__binding}|
		{ name: 'root', node:Croot, init:Troot}> {
		protected initializer(state: 'dynamic'|'root') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic__binding, parent:Ccustom_type) => new Cdynamic__binding(init, parent);
				case 'root': return (init:Troot, parent:Ccustom_type) => new Croot(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'root') {
			switch (state) {
				case 'dynamic': return finalize_dynamic__binding;
				case 'root': return finalize_root;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcustom_type['binding'], parent:Ccustom_type) {
			super(data, parent, 'binding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
}
export namespace Cdynamic__binding {
	export class Dassignment extends Cvariable_assignment {
		constructor(data:Tdynamic__binding['assignment'], parent:Cdynamic__binding) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'assignment')
		}
	}
	export class Dpath extends Cnode_type_id {
		constructor(data:Tdynamic__binding['path'], parent:Cdynamic__binding) {
			super(data, parent, 'path')
		}
	}
}
export namespace Cproperties__custom_type {
	export class Dconversion extends StateGroup<
		{ name: 'none', node:Cnone__conversion, init:Tnone__conversion}|
		{ name: 'point translation', node:Cpoint_translation, init:Tpoint_translation}> {
		protected initializer(state: 'none'|'point translation') {
			switch (state) {
				case 'none': return (init:Tnone__conversion, parent:Cproperties__custom_type) => new Cnone__conversion(init, parent);
				case 'point translation': return (init:Tpoint_translation, parent:Cproperties__custom_type) => new Cpoint_translation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'point translation') {
			switch (state) {
				case 'none': return finalize_none__conversion;
				case 'point translation': return finalize_point_translation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__custom_type['conversion'], parent:Cproperties__custom_type) {
			super(data, parent, 'conversion');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion'`; }
	}
	export class Dlabel extends Cui_scalar_value_expression {
		constructor(data:Tproperties__custom_type['label'], parent:Cproperties__custom_type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_text({name: 'intermediate result', definition: conv_context});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.binding?.state.node.output.variable()).result!)
			}, 'label')
		}
	}
}
export namespace Cpoint_translation {
	export class Ddecimals extends Cui_scalar_value_expression {
		constructor(data:Tpoint_translation['decimals'], parent:Cpoint_translation) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.component_root.input.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cnumber_set_type.Ppositive).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.binding?.state.node.output.variable()).result!)
			}, 'decimals')
		}
	}
}
export namespace Cduration {
	export class Dunit extends StateGroup<
		{ name: 'hours', node:Chours, init:Thours}|
		{ name: 'minutes', node:Cminutes, init:Tminutes}|
		{ name: 'seconds', node:Cseconds__unit, init:Tseconds__unit}> {
		protected initializer(state: 'hours'|'minutes'|'seconds') {
			switch (state) {
				case 'hours': return (init:Thours, parent:Cduration) => new Chours(init, parent);
				case 'minutes': return (init:Tminutes, parent:Cduration) => new Cminutes(init, parent);
				case 'seconds': return (init:Tseconds__unit, parent:Cduration) => new Cseconds__unit(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'hours'|'minutes'|'seconds') {
			switch (state) {
				case 'hours': return finalize_hours;
				case 'minutes': return finalize_minutes;
				case 'seconds': return finalize_seconds__unit;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tduration['unit'], parent:Cduration) {
			super(data, parent, 'unit');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'unit'`; }
	}
}
export namespace Cui_object_expression {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__ui_object_expression, init:Tproperties__ui_object_expression},Cui_object_expression> {
		protected initialize(parent:Cui_object_expression, key:string, entry_init:Tproperties__ui_object_expression) { return new Cproperties__ui_object_expression(key, entry_init, parent); }
		protected finalize = finalize_properties__ui_object_expression
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tui_object_expression['properties'], parent:Cui_object_expression) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__ui_object_expression {
	export class Ddefault_ extends Cui_expression {
		constructor(data:Tproperties__ui_object_expression['default'], parent:Cproperties__ui_object_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'default')
		}
	}
}
export namespace Cui_parametrized_node_path {
	export class Dpath extends Cconditional_node_path {
		constructor(data:Tui_parametrized_node_path['path'], parent:Cui_parametrized_node_path) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Cui_property_classification {
	export class Dclassification extends StateGroup<
		{ name: 'hidden', node:Chidden, init:Thidden}|
		{ name: 'identifying', node:Cidentifying, init:Tidentifying}|
		{ name: 'outlining', node:Coutlining, init:Toutlining}|
		{ name: 'standard', node:Cstandard, init:Tstandard}> {
		protected initializer(state: 'hidden'|'identifying'|'outlining'|'standard') {
			switch (state) {
				case 'hidden': return (init:Thidden, parent:Cui_property_classification) => new Chidden(init, parent);
				case 'identifying': return (init:Tidentifying, parent:Cui_property_classification) => new Cidentifying(init, parent);
				case 'outlining': return (init:Toutlining, parent:Cui_property_classification) => new Coutlining(init, parent);
				case 'standard': return (init:Tstandard, parent:Cui_property_classification) => new Cstandard(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'hidden'|'identifying'|'outlining'|'standard') {
			switch (state) {
				case 'hidden': return finalize_hidden;
				case 'identifying': return finalize_identifying;
				case 'outlining': return finalize_outlining;
				case 'standard': return finalize_standard;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_property_classification['classification'], parent:Cui_property_classification) {
			super(data, parent, 'classification');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'classification'`; }
	}
}
export namespace Cui_reference_rule {
	export class Dhas_validation extends StateGroup<
		{ name: 'no', node:Cno__has_validation__ui_reference_rule, init:Tno__has_validation__ui_reference_rule}|
		{ name: 'yes', node:Cyes__has_validation__ui_reference_rule, init:Tyes__has_validation__ui_reference_rule}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_validation__ui_reference_rule, parent:Cui_reference_rule) => new Cno__has_validation__ui_reference_rule(init, parent);
				case 'yes': return (init:Tyes__has_validation__ui_reference_rule, parent:Cui_reference_rule) => new Cyes__has_validation__ui_reference_rule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_validation__ui_reference_rule;
				case 'yes': return finalize_yes__has_validation__ui_reference_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_reference_rule['has validation'], parent:Cui_reference_rule) {
			super(data, parent, 'has validation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has validation'`; }
	}
}
export namespace Cui_reference_set_attribute {
	export class Dbreak_out extends StateGroup<
		{ name: 'no', node:Cno__break_out__ui_reference_set_attribute, init:Tno__break_out__ui_reference_set_attribute}|
		{ name: 'yes', node:Cyes__break_out__ui_reference_set_attribute, init:Tyes__break_out__ui_reference_set_attribute}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__break_out__ui_reference_set_attribute, parent:Cui_reference_set_attribute) => new Cno__break_out__ui_reference_set_attribute(init, parent);
				case 'yes': return (init:Tyes__break_out__ui_reference_set_attribute, parent:Cui_reference_set_attribute) => new Cyes__break_out__ui_reference_set_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__break_out__ui_reference_set_attribute;
				case 'yes': return finalize_yes__break_out__ui_reference_set_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_reference_set_attribute['break out'], parent:Cui_reference_set_attribute) {
			super(data, parent, 'break out');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'break out'`; }
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_reference_set_attribute, init:Tno__has_description__ui_reference_set_attribute}|
		{ name: 'yes', node:Cyes__has_description__ui_reference_set_attribute, init:Tyes__has_description__ui_reference_set_attribute}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_reference_set_attribute, parent:Cui_reference_set_attribute) => new Cno__has_description__ui_reference_set_attribute(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_reference_set_attribute, parent:Cui_reference_set_attribute) => new Cyes__has_description__ui_reference_set_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_reference_set_attribute;
				case 'yes': return finalize_yes__has_description__ui_reference_set_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_reference_set_attribute['has description'], parent:Cui_reference_set_attribute) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dvisible extends StateGroup<
		{ name: 'false', node:Cfalse__visible__ui_reference_set_attribute, init:Tfalse__visible__ui_reference_set_attribute}|
		{ name: 'true', node:Ctrue__visible__ui_reference_set_attribute, init:Ttrue__visible__ui_reference_set_attribute}> {
		protected initializer(state: 'false'|'true') {
			switch (state) {
				case 'false': return (init:Tfalse__visible__ui_reference_set_attribute, parent:Cui_reference_set_attribute) => new Cfalse__visible__ui_reference_set_attribute(init, parent);
				case 'true': return (init:Ttrue__visible__ui_reference_set_attribute, parent:Cui_reference_set_attribute) => new Ctrue__visible__ui_reference_set_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'false'|'true') {
			switch (state) {
				case 'false': return finalize_false__visible__ui_reference_set_attribute;
				case 'true': return finalize_true__visible__ui_reference_set_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_reference_set_attribute['visible'], parent:Cui_reference_set_attribute) {
			super(data, parent, 'visible');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'visible'`; }
	}
}
export namespace Cyes__has_description__ui_reference_set_attribute {
}
export namespace Cui_scalar_default {
	export class Dexpression extends Cui_expression {
		constructor(data:Tui_scalar_default['expression'], parent:Cui_scalar_default) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dhas_fallback extends StateGroup<
		{ name: 'no', node:Cno__has_fallback, init:Tno__has_fallback}|
		{ name: 'yes', node:Cyes__has_fallback, init:Tyes__has_fallback}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_fallback, parent:Cui_scalar_default) => new Cno__has_fallback(init, parent);
				case 'yes': return (init:Tyes__has_fallback, parent:Cui_scalar_default) => new Cyes__has_fallback(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_fallback;
				case 'yes': return finalize_yes__has_fallback;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_scalar_default['has fallback'], parent:Cui_scalar_default) {
			super(data, parent, 'has fallback');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has fallback'`; }
	}
}
export namespace Cyes__has_fallback {
	export class Dfallback extends Cui_scalar_default {
		constructor(data:Tyes__has_fallback['fallback'], parent:Cyes__has_fallback) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'fallback')
		}
	}
}
export namespace Cui_scalar_value_expression {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic__type__ui_scalar_value_expression, init:Tdynamic__type__ui_scalar_value_expression}|
		{ name: 'number', node:Cnumber__type__ui_scalar_value_expression, init:Tnumber__type__ui_scalar_value_expression}|
		{ name: 'state', node:Cstate__type__ui_scalar_value_expression, init:Tstate__type__ui_scalar_value_expression}|
		{ name: 'sticky', node:Csticky, init:Tsticky}|
		{ name: 'style', node:Cstyle__type, init:Tstyle__type}|
		{ name: 'text', node:Ctext__type__ui_scalar_value_expression, init:Ttext__type__ui_scalar_value_expression}> {
		protected initializer(state: 'dynamic'|'number'|'state'|'sticky'|'style'|'text') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic__type__ui_scalar_value_expression, parent:Cui_scalar_value_expression) => new Cdynamic__type__ui_scalar_value_expression(init, parent);
				case 'number': return (init:Tnumber__type__ui_scalar_value_expression, parent:Cui_scalar_value_expression) => new Cnumber__type__ui_scalar_value_expression(init, parent);
				case 'state': return (init:Tstate__type__ui_scalar_value_expression, parent:Cui_scalar_value_expression) => new Cstate__type__ui_scalar_value_expression(init, parent);
				case 'sticky': return (init:Tsticky, parent:Cui_scalar_value_expression) => new Csticky(init, parent);
				case 'style': return (init:Tstyle__type, parent:Cui_scalar_value_expression) => new Cstyle__type(init, parent);
				case 'text': return (init:Ttext__type__ui_scalar_value_expression, parent:Cui_scalar_value_expression) => new Ctext__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'number'|'state'|'sticky'|'style'|'text') {
			switch (state) {
				case 'dynamic': return finalize_dynamic__type__ui_scalar_value_expression;
				case 'number': return finalize_number__type__ui_scalar_value_expression;
				case 'state': return finalize_state__type__ui_scalar_value_expression;
				case 'sticky': return finalize_sticky;
				case 'style': return finalize_style__type;
				case 'text': return finalize_text__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_scalar_value_expression['type'], parent:Cui_scalar_value_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic__type__ui_scalar_value_expression {
	export class Dpath extends Cui_parametrized_node_path {
		constructor(data:Tdynamic__type__ui_scalar_value_expression['path'], parent:Cdynamic__type__ui_scalar_value_expression) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'path')
		}
	}
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {
		public readonly inferences:{
			value: () => application.Cui_scalar
		}

		constructor(data:string, $this:Cdynamic__type__ui_scalar_value_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				value: cache((detach:boolean) => {
					const application__ui_scalar_value_expression__type__dynamic__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(() => $this)
						.then(context => context?.inferences.property_target())
						.then(context => {
							switch (context?.variant.name) {
								case 'file': {
									const application__ui_scalar_value_expression__type__dynamic__property_inf___value___file_nval = context.cast('file');
									return resolve(context)
										.then(context => application__ui_scalar_value_expression__type__dynamic__property_nval)
										.then(context => {
											if (context?.properties.type.state.name === 'file') {
												return resolve(context.properties.type.state.node as application.Cfile).result;
											} else {
												return undefined;
											}
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cui_property_value({name: 'file', definition: conv_context});
										})
										.then(context => context?.definitions.ui_scalar).result;
								}
								case 'number': {
									const application__ui_scalar_value_expression__type__dynamic__property_inf___value___number_nval = context.cast('number');
									return resolve(context)
										.then(context => application__ui_scalar_value_expression__type__dynamic__property_nval)
										.then(context => {
											if (context?.properties.type.state.name === 'number') {
												return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
											} else {
												return undefined;
											}
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											const application__ui_scalar_value_expression__type__dynamic__property_inf___value___number_vcon_nval = context;
											return new Cui_number({name: 'number property', definition: conv_context},{
											numerical_type: () => resolve($this)
												.then(context => application__ui_scalar_value_expression__type__dynamic__property_inf___value___number_nval)
												.then(context => context?.component_root.output.numerical_type()).result!
											, 
											set_type: () => resolve($this)
												.then(context => application__ui_scalar_value_expression__type__dynamic__property_inf___value___number_nval)
												.then(context => context?.component_root.output.set_type()).result!

											});
										})
										.then(context => context?.definitions.ui_property_value)
										.then(context => context?.definitions.ui_scalar).result;
								}
								case 'state choice': {
									const application__ui_scalar_value_expression__type__dynamic__property_inf___value___state_choice_nval = context.cast('state choice');
									return resolve(context)
										.then(context => evaluate__ui_dynamic_state_choice_check(detach, 
											resolve(context)
												.then(context => application__ui_scalar_value_expression__type__dynamic__property_inf___value___state_choice_nval).result!, {
											selected_state_group: () => resolve($this)
												.then(context => application__ui_scalar_value_expression__type__dynamic__property_nval)
												.then(context => {
													if (context?.properties.type.state.name === 'state group') {
														return resolve(context.properties.type.state.node as application.Cstate_group).result;
													} else {
														return undefined;
													}
												})
												.then(context => {
													const conv_context = resolve(context).result!;
													return new Cui_state_choice({name: 'state node choice', definition: conv_context});
												}).result!

											}))
										.then(context => context?.definitions.ui_property_value)
										.then(context => context?.definitions.ui_scalar).result;
								}
								case 'text': {
									const application__ui_scalar_value_expression__type__dynamic__property_inf___value___text_nval = context.cast('text');
									return resolve(context)
										.then(context => application__ui_scalar_value_expression__type__dynamic__property_nval)
										.then(context => {
											if (context?.properties.type.state.name === 'text') {
												return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
											} else {
												return undefined;
											}
										})
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Cui_text({name: 'text property', definition: conv_context});
										})
										.then(context => context?.definitions.ui_property_value)
										.then(context => context?.definitions.ui_scalar).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
}
export namespace Cnumber__type__ui_scalar_value_expression {
	export class Dtype extends StateGroup<
		{ name: 'binary expression', node:Cbinary_expression__type__number__type__ui_scalar_value_expression, init:Tbinary_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'constant', node:Cconstant__type__number__type__ui_scalar_value_expression, init:Tconstant__type__number__type__ui_scalar_value_expression}|
		{ name: 'list expression', node:Clist_expression__type__number__type__ui_scalar_value_expression, init:Tlist_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'now in seconds', node:Cnow_in_seconds__type__number__type__ui_scalar_value_expression, init:Tnow_in_seconds__type__number__type__ui_scalar_value_expression}|
		{ name: 'unary expression', node:Cunary_expression__type__number__type__ui_scalar_value_expression, init:Tunary_expression__type__number__type__ui_scalar_value_expression}> {
		protected initializer(state: 'binary expression'|'constant'|'list expression'|'now in seconds'|'unary expression') {
			switch (state) {
				case 'binary expression': return (init:Tbinary_expression__type__number__type__ui_scalar_value_expression, parent:Cnumber__type__ui_scalar_value_expression) => new Cbinary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'constant': return (init:Tconstant__type__number__type__ui_scalar_value_expression, parent:Cnumber__type__ui_scalar_value_expression) => new Cconstant__type__number__type__ui_scalar_value_expression(init, parent);
				case 'list expression': return (init:Tlist_expression__type__number__type__ui_scalar_value_expression, parent:Cnumber__type__ui_scalar_value_expression) => new Clist_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'now in seconds': return (init:Tnow_in_seconds__type__number__type__ui_scalar_value_expression, parent:Cnumber__type__ui_scalar_value_expression) => new Cnow_in_seconds__type__number__type__ui_scalar_value_expression(init, parent);
				case 'unary expression': return (init:Tunary_expression__type__number__type__ui_scalar_value_expression, parent:Cnumber__type__ui_scalar_value_expression) => new Cunary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binary expression'|'constant'|'list expression'|'now in seconds'|'unary expression') {
			switch (state) {
				case 'binary expression': return finalize_binary_expression__type__number__type__ui_scalar_value_expression;
				case 'constant': return finalize_constant__type__number__type__ui_scalar_value_expression;
				case 'list expression': return finalize_list_expression__type__number__type__ui_scalar_value_expression;
				case 'now in seconds': return finalize_now_in_seconds__type__number__type__ui_scalar_value_expression;
				case 'unary expression': return finalize_unary_expression__type__number__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__ui_scalar_value_expression['type'], parent:Cnumber__type__ui_scalar_value_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbinary_expression__type__number__type__ui_scalar_value_expression {
	export class Doperation extends StateGroup<
		{ name: 'division', node:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression, init:Tdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'remainder', node:Cremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression, init:Tremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression}> {
		protected initializer(state: 'division'|'remainder') {
			switch (state) {
				case 'division': return (init:Tdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression, parent:Cbinary_expression__type__number__type__ui_scalar_value_expression) => new Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'remainder': return (init:Tremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression, parent:Cbinary_expression__type__number__type__ui_scalar_value_expression) => new Cremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'division'|'remainder') {
			switch (state) {
				case 'division': return finalize_division__operation__binary_expression__type__number__type__ui_scalar_value_expression;
				case 'remainder': return finalize_remainder__operation__binary_expression__type__number__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinary_expression__type__number__type__ui_scalar_value_expression['operation'], parent:Cbinary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression {
	export class Dleft extends Cui_scalar_value_expression {
		constructor(data:Texpressions__binary_expression__type__number__type__ui_scalar_value_expression['left'], parent:Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'left')
		}
	}
	export class Dright extends Cui_scalar_value_expression {
		constructor(data:Texpressions__binary_expression__type__number__type__ui_scalar_value_expression['right'], parent:Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cnumber_set_type.Ppositive).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'right')
		}
	}
}
export namespace Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression {
	export class Drounding extends StateGroup<
		{ name: 'ceil', node:Cceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, init:Tceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'floor', node:Cfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, init:Tfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'ordinary', node:Cordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, init:Tordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression}> {
		protected initializer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return (init:Tceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) => new Cceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'floor': return (init:Tfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) => new Cfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'ordinary': return (init:Tordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) => new Cordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return finalize_ceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression;
				case 'floor': return finalize_floor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression;
				case 'ordinary': return finalize_ordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression['rounding'], parent:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, 'rounding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rounding'`; }
	}
}
export namespace Cconstant__type__number__type__ui_scalar_value_expression {
	export class Dvalue extends Cconstant_number_value {
		constructor(data:Tconstant__type__number__type__ui_scalar_value_expression['value'], parent:Cconstant__type__number__type__ui_scalar_value_expression) {
			super(data, parent, 'value')
		}
	}
}
export namespace Clist_expression__type__number__type__ui_scalar_value_expression {
	export class Doperation extends StateGroup<
		{ name: 'maximum', node:Cmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression, init:Tmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'minimum', node:Cminimum__operation__list_expression__type__number__type__ui_scalar_value_expression, init:Tminimum__operation__list_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'product', node:Cproduct__operation__list_expression__type__number__type__ui_scalar_value_expression, init:Tproduct__operation__list_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'sum', node:Csum__operation__list_expression__type__number__type__ui_scalar_value_expression, init:Tsum__operation__list_expression__type__number__type__ui_scalar_value_expression}> {
		protected initializer(state: 'maximum'|'minimum'|'product'|'sum') {
			switch (state) {
				case 'maximum': return (init:Tmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression, parent:Clist_expression__type__number__type__ui_scalar_value_expression) => new Cmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'minimum': return (init:Tminimum__operation__list_expression__type__number__type__ui_scalar_value_expression, parent:Clist_expression__type__number__type__ui_scalar_value_expression) => new Cminimum__operation__list_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'product': return (init:Tproduct__operation__list_expression__type__number__type__ui_scalar_value_expression, parent:Clist_expression__type__number__type__ui_scalar_value_expression) => new Cproduct__operation__list_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'sum': return (init:Tsum__operation__list_expression__type__number__type__ui_scalar_value_expression, parent:Clist_expression__type__number__type__ui_scalar_value_expression) => new Csum__operation__list_expression__type__number__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'maximum'|'minimum'|'product'|'sum') {
			switch (state) {
				case 'maximum': return finalize_maximum__operation__list_expression__type__number__type__ui_scalar_value_expression;
				case 'minimum': return finalize_minimum__operation__list_expression__type__number__type__ui_scalar_value_expression;
				case 'product': return finalize_product__operation__list_expression__type__number__type__ui_scalar_value_expression;
				case 'sum': return finalize_sum__operation__list_expression__type__number__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlist_expression__type__number__type__ui_scalar_value_expression['operation'], parent:Clist_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
	export class Dvalue extends Cui_scalar_value_expression_list {
		constructor(data:Tlist_expression__type__number__type__ui_scalar_value_expression['value'], parent:Clist_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'value')
		}
	}
}
export namespace Cunary_expression__type__number__type__ui_scalar_value_expression {
	export class Dexpression extends Cui_scalar_value_expression {
		constructor(data:Tunary_expression__type__number__type__ui_scalar_value_expression['expression'], parent:Cunary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.type?.state.node.output.numerical_type()).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.inferences.number_target())
							.then(context => context?.component_root.output.set_type()).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'absolute value', node:Cabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression, init:Tabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'numerical type conversion', node:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression, init:Tnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression}|
		{ name: 'sign inversion', node:Csign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression, init:Tsign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression}> {
		protected initializer(state: 'absolute value'|'numerical type conversion'|'sign inversion') {
			switch (state) {
				case 'absolute value': return (init:Tabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression, parent:Cunary_expression__type__number__type__ui_scalar_value_expression) => new Cabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'numerical type conversion': return (init:Tnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression, parent:Cunary_expression__type__number__type__ui_scalar_value_expression) => new Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				case 'sign inversion': return (init:Tsign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression, parent:Cunary_expression__type__number__type__ui_scalar_value_expression) => new Csign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'absolute value'|'numerical type conversion'|'sign inversion') {
			switch (state) {
				case 'absolute value': return finalize_absolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression;
				case 'numerical type conversion': return finalize_numerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression;
				case 'sign inversion': return finalize_sign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tunary_expression__type__number__type__ui_scalar_value_expression['type'], parent:Cunary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression {
	export class Dconversion extends Reference<application.Csingular_conversions,string> {

		constructor(data:string, $this:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number_target())
				.then(context => context?.component_root.output.numerical_type())
				.then(context => {
					const entry = context?.properties.singular_conversions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion'`; }
	}
	export class Drepresentation_based extends StateGroup<
		{ name: 'no', node:Cno__representation_based, init:Tno__representation_based}|
		{ name: 'ui date and time', node:Cui_date_and_time, init:Tui_date_and_time}> {
		protected initializer(state: 'no'|'ui date and time') {
			switch (state) {
				case 'no': return (init:Tno__representation_based, parent:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression) => new Cno__representation_based(init, parent);
				case 'ui date and time': return (init:Tui_date_and_time, parent:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression) => new Cui_date_and_time(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'ui date and time') {
			switch (state) {
				case 'no': return finalize_no__representation_based;
				case 'ui date and time': return finalize_ui_date_and_time;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression['representation based'], parent:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression) {
			super(data, parent, 'representation based');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'representation based'`; }
	}
}
export namespace Cstate__type__ui_scalar_value_expression {
	export class Dstate extends Reference<application.Cui_target_state,string> {

		constructor(data:string, $this:Cstate__type__ui_scalar_value_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_choice_target())
				.then(context => {
					switch (context?.variant.name) {
						case 'state node choice': {
							const application__ui_scalar_value_expression__type__state__state___state_node_choice_nval = context.cast('state node choice');
							return resolve(context)
								.then(context => application__ui_scalar_value_expression__type__state__state___state_node_choice_nval)
								.then(context => {
									const entry = context?.properties.states.get(this.entry)!;
									return resolve(entry)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Cui_target_state({name: 'state', definition: conv_context});
									}).result;
								}).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstyle__type {
	export class Dtype extends StateGroup<
		{ name: 'fixed', node:Cfixed, init:Tfixed}|
		{ name: 'property', node:Cproperty__type__style, init:Tproperty__type__style}> {
		protected initializer(state: 'fixed'|'property') {
			switch (state) {
				case 'fixed': return (init:Tfixed, parent:Cstyle__type) => new Cfixed(init, parent);
				case 'property': return (init:Tproperty__type__style, parent:Cstyle__type) => new Cproperty__type__style(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'fixed'|'property') {
			switch (state) {
				case 'fixed': return finalize_fixed;
				case 'property': return finalize_property__type__style;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstyle__type['type'], parent:Cstyle__type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cfixed {
	export class Dstyle extends Cui_style {
		constructor(data:Tfixed['style'], parent:Cfixed) {
			super(data, parent, 'style')
		}
	}
}
export namespace Cproperty__type__style {
	export class Dexpression extends Cui_scalar_value_expression {
		constructor(data:Tproperty__type__style['expression'], parent:Cproperty__type__style) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_text({name: 'intermediate result', definition: conv_context});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Ctext__type__ui_scalar_value_expression {
	export class Dtype extends StateGroup<
		{ name: 'auto increment', node:Cauto_increment, init:Tauto_increment}|
		{ name: 'concatenation', node:Cconcatenation__type__text__type__ui_scalar_value_expression, init:Tconcatenation__type__text__type__ui_scalar_value_expression}|
		{ name: 'guid', node:Cguid, init:Tguid}|
		{ name: 'number to text', node:Cnumber_to_text, init:Tnumber_to_text}|
		{ name: 'static', node:Cstatic__type__text__type__ui_scalar_value_expression, init:Tstatic__type__text__type__ui_scalar_value_expression}> {
		protected initializer(state: 'auto increment'|'concatenation'|'guid'|'number to text'|'static') {
			switch (state) {
				case 'auto increment': return (init:Tauto_increment, parent:Ctext__type__ui_scalar_value_expression) => new Cauto_increment(init, parent);
				case 'concatenation': return (init:Tconcatenation__type__text__type__ui_scalar_value_expression, parent:Ctext__type__ui_scalar_value_expression) => new Cconcatenation__type__text__type__ui_scalar_value_expression(init, parent);
				case 'guid': return (init:Tguid, parent:Ctext__type__ui_scalar_value_expression) => new Cguid(init, parent);
				case 'number to text': return (init:Tnumber_to_text, parent:Ctext__type__ui_scalar_value_expression) => new Cnumber_to_text(init, parent);
				case 'static': return (init:Tstatic__type__text__type__ui_scalar_value_expression, parent:Ctext__type__ui_scalar_value_expression) => new Cstatic__type__text__type__ui_scalar_value_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'auto increment'|'concatenation'|'guid'|'number to text'|'static') {
			switch (state) {
				case 'auto increment': return finalize_auto_increment;
				case 'concatenation': return finalize_concatenation__type__text__type__ui_scalar_value_expression;
				case 'guid': return finalize_guid;
				case 'number to text': return finalize_number_to_text;
				case 'static': return finalize_static__type__text__type__ui_scalar_value_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__ui_scalar_value_expression['type'], parent:Ctext__type__ui_scalar_value_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cauto_increment {
	export class Dscope extends Cancestor_node_path {
		public readonly inferences:{
			root_location: () => application.Croot_location,
			valid_increment_scope: () => application.Ccollection__type__property
		}
		constructor(data:Tauto_increment['scope'], parent:Cauto_increment) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!)
			}, 'scope')
			this.inferences = {
				root_location: cache((detach:boolean) => {
					const application__ui_scalar_value_expression__type__text__type__auto_increment__scope_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => application__ui_scalar_value_expression__type__text__type__auto_increment__scope_nval)
								.then(context => context?.component_root.output.node_type())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.this_node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				valid_increment_scope: cache((detach:boolean) => {
					const application__ui_scalar_value_expression__type__text__type__auto_increment__scope_nval = this;
					return resolve(this)
						.then(context => application__ui_scalar_value_expression__type__text__type__auto_increment__scope_nval)
						.then(context => context?.component_root.output.node_type())
						.then(context => context?.component_root.output.entity())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
				})

			}
		}
	}
}
export namespace Cconcatenation__type__text__type__ui_scalar_value_expression {
	export class Dlist extends Cui_scalar_value_expression_list {
		constructor(data:Tconcatenation__type__text__type__ui_scalar_value_expression['list'], parent:Cconcatenation__type__text__type__ui_scalar_value_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'list')
		}
	}
}
export namespace Cnumber_to_text {
	export class Dexpression extends Cui_scalar_value_expression {
		constructor(data:Tnumber_to_text['expression'], parent:Cnumber_to_text) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_number({name: 'intermediate result', definition: conv_context},{
						numerical_type: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.properties.numerical_type?.ref).result!
						, 
						set_type: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cnumber_set_type.Pinteger).result!

						});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dnumerical_type extends Reference<application.Cnumerical_types,string> {

		constructor(data:string, $this:Cnumber_to_text) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'numerical type'`; }
	}
	export class Dpad extends StateGroup<
		{ name: 'no', node:Cno__pad, init:Tno__pad}|
		{ name: 'yes', node:Cyes__pad, init:Tyes__pad}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__pad, parent:Cnumber_to_text) => new Cno__pad(init, parent);
				case 'yes': return (init:Tyes__pad, parent:Cnumber_to_text) => new Cyes__pad(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__pad;
				case 'yes': return finalize_yes__pad;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_to_text['pad'], parent:Cnumber_to_text) {
			super(data, parent, 'pad');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'pad'`; }
	}
}
export namespace Cyes__pad {
	export class Dsize extends AlanInteger {
		constructor(data:Tyes__pad['size'], parent:Cyes__pad) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'size'`; }
	}
}
export namespace Cstatic__type__text__type__ui_scalar_value_expression {
}
export namespace Cui_scalar_value_expression_list {
	export class Dhas_tail extends StateGroup<
		{ name: 'no', node:Cno__has_tail__ui_scalar_value_expression_list, init:Tno__has_tail__ui_scalar_value_expression_list}|
		{ name: 'yes', node:Cyes__has_tail__ui_scalar_value_expression_list, init:Tyes__has_tail__ui_scalar_value_expression_list}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_tail__ui_scalar_value_expression_list, parent:Cui_scalar_value_expression_list) => new Cno__has_tail__ui_scalar_value_expression_list(init, parent);
				case 'yes': return (init:Tyes__has_tail__ui_scalar_value_expression_list, parent:Cui_scalar_value_expression_list) => new Cyes__has_tail__ui_scalar_value_expression_list(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_tail__ui_scalar_value_expression_list;
				case 'yes': return finalize_yes__has_tail__ui_scalar_value_expression_list;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_scalar_value_expression_list['has tail'], parent:Cui_scalar_value_expression_list) {
			super(data, parent, 'has tail');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has tail'`; }
	}
	export class Dvalue extends Cui_scalar_value_expression {
		constructor(data:Tui_scalar_value_expression_list['value'], parent:Cui_scalar_value_expression_list) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'value')
		}
	}
}
export namespace Cyes__has_tail__ui_scalar_value_expression_list {
	export class Dtail extends Cui_scalar_value_expression_list {
		constructor(data:Tyes__has_tail__ui_scalar_value_expression_list['tail'], parent:Cyes__has_tail__ui_scalar_value_expression_list) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_node()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'tail')
		}
	}
}
export namespace Cui_state {
	export class Ddesired_state extends StateGroup<
		{ name: 'no', node:Cno__desired_state, init:Tno__desired_state}|
		{ name: 'yes', node:Cyes__desired_state, init:Tyes__desired_state}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__desired_state, parent:Cui_state) => new Cno__desired_state(init, parent);
				case 'yes': return (init:Tyes__desired_state, parent:Cui_state) => new Cyes__desired_state(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__desired_state;
				case 'yes': return finalize_yes__desired_state;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state['desired state'], parent:Cui_state) {
			super(data, parent, 'desired state');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'desired state'`; }
	}
	export class Dhas_style extends StateGroup<
		{ name: 'no', node:Cno__has_style__ui_state, init:Tno__has_style__ui_state}|
		{ name: 'yes', node:Cyes__has_style__ui_state, init:Tyes__has_style__ui_state}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_style__ui_state, parent:Cui_state) => new Cno__has_style__ui_state(init, parent);
				case 'yes': return (init:Tyes__has_style__ui_state, parent:Cui_state) => new Cyes__has_style__ui_state(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_style__ui_state;
				case 'yes': return finalize_yes__has_style__ui_state;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state['has style'], parent:Cui_state) {
			super(data, parent, 'has style');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has style'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_state, init:Tno__icon__ui_state}|
		{ name: 'yes', node:Cyes__icon__ui_state, init:Tyes__icon__ui_state}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_state, parent:Cui_state) => new Cno__icon__ui_state(init, parent);
				case 'yes': return (init:Tyes__icon__ui_state, parent:Cui_state) => new Cyes__icon__ui_state(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_state;
				case 'yes': return finalize_yes__icon__ui_state;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state['icon'], parent:Cui_state) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
	export class Dtransitions extends AlanDictionary<{ node:Ctransitions, init:Ttransitions},Cui_state> {
		protected initialize(parent:Cui_state, key:string, entry_init:Ttransitions) { return new Ctransitions(key, entry_init, parent); }
		protected finalize = finalize_transitions
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'transitions'`; }
		constructor(data:Tui_state['transitions'], parent:Cui_state) {
			super(data, parent);
		}
	}
	export class Dverified_state extends StateGroup<
		{ name: 'no', node:Cno__verified_state, init:Tno__verified_state}|
		{ name: 'yes', node:Cyes__verified_state, init:Tyes__verified_state}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__verified_state, parent:Cui_state) => new Cno__verified_state(init, parent);
				case 'yes': return (init:Tyes__verified_state, parent:Cui_state) => new Cyes__verified_state(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__verified_state;
				case 'yes': return finalize_yes__verified_state;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state['verified state'], parent:Cui_state) {
			super(data, parent, 'verified state');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'verified state'`; }
	}
}
export namespace Cyes__has_style__ui_state {
	export class Dstyle_expression extends Cui_expression {
		constructor(data:Tyes__has_style__ui_state['style expression'], parent:Cyes__has_style__ui_state) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cui_scalar.Pstyle)
					.then(context => context?.definitions.ui_target).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.state())
					.then(context => context?.properties.node).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'style expression')
		}
	}
}
export namespace Cyes__icon__ui_state {
}
export namespace Ctransitions {
	export class Daction extends Reference<application.Caction,string> {

		constructor(data:string, $this:Ctransitions) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.state())
				.then(context => context?.properties.node)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'action') {
							return context.properties.type.state.node as application.Caction;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'action'`; }
	}
}
export namespace Cui_state_group_property {
	export class Dclassification extends Cui_property_classification {
		constructor(data:Tui_state_group_property['classification'], parent:Cui_state_group_property) {
			super(data, parent, {
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.state_group_property())
					.then(context => context?.parent).result!)
			}, 'classification')
		}
	}
	export class Ddefault_state extends StateGroup<
		{ name: 'no', node:Cno__default_state, init:Tno__default_state}|
		{ name: 'yes', node:Cyes__default_state, init:Tyes__default_state}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default_state, parent:Cui_state_group_property) => new Cno__default_state(init, parent);
				case 'yes': return (init:Tyes__default_state, parent:Cui_state_group_property) => new Cyes__default_state(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default_state;
				case 'yes': return finalize_yes__default_state;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state_group_property['default state'], parent:Cui_state_group_property) {
			super(data, parent, 'default state');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default state'`; }
	}
	export class Demphasis extends StateGroup<
		{ name: 'no', node:Cno__emphasis__ui_state_group_property, init:Tno__emphasis__ui_state_group_property}|
		{ name: 'yes', node:Cyes__emphasis__ui_state_group_property, init:Tyes__emphasis__ui_state_group_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__emphasis__ui_state_group_property, parent:Cui_state_group_property) => new Cno__emphasis__ui_state_group_property(init, parent);
				case 'yes': return (init:Tyes__emphasis__ui_state_group_property, parent:Cui_state_group_property) => new Cyes__emphasis__ui_state_group_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__emphasis__ui_state_group_property;
				case 'yes': return finalize_yes__emphasis__ui_state_group_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state_group_property['emphasis'], parent:Cui_state_group_property) {
			super(data, parent, 'emphasis');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'emphasis'`; }
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_state_group_property, init:Tno__has_description__ui_state_group_property}|
		{ name: 'yes', node:Cyes__has_description__ui_state_group_property, init:Tyes__has_description__ui_state_group_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_state_group_property, parent:Cui_state_group_property) => new Cno__has_description__ui_state_group_property(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_state_group_property, parent:Cui_state_group_property) => new Cyes__has_description__ui_state_group_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_state_group_property;
				case 'yes': return finalize_yes__has_description__ui_state_group_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state_group_property['has description'], parent:Cui_state_group_property) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_state_group_property, init:Tno__icon__ui_state_group_property}|
		{ name: 'yes', node:Cyes__icon__ui_state_group_property, init:Tyes__icon__ui_state_group_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_state_group_property, parent:Cui_state_group_property) => new Cno__icon__ui_state_group_property(init, parent);
				case 'yes': return (init:Tyes__icon__ui_state_group_property, parent:Cui_state_group_property) => new Cyes__icon__ui_state_group_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_state_group_property;
				case 'yes': return finalize_yes__icon__ui_state_group_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_state_group_property['icon'], parent:Cui_state_group_property) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
}
export namespace Cyes__default_state {
	export class Ddefault_ extends Cui_scalar_default {
		constructor(data:Tyes__default_state['default'], parent:Cyes__default_state) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.state_group_property())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_state_choice({name: 'state node choice', definition: conv_context});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.state_group_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'default')
		}
	}
}
export namespace Cyes__has_description__ui_state_group_property {
}
export namespace Cyes__icon__ui_state_group_property {
}
export namespace Cui_style {
	export class Dstyle extends StateGroup<
		{ name: 'accent', node:Caccent, init:Taccent}|
		{ name: 'background', node:Cbackground, init:Tbackground}|
		{ name: 'brand', node:Cbrand, init:Tbrand}|
		{ name: 'error', node:Cerror, init:Terror}|
		{ name: 'foreground', node:Cforeground, init:Tforeground}|
		{ name: 'link', node:Clink, init:Tlink}|
		{ name: 'success', node:Csuccess, init:Tsuccess}|
		{ name: 'warning', node:Cwarning, init:Twarning}> {
		protected initializer(state: 'accent'|'background'|'brand'|'error'|'foreground'|'link'|'success'|'warning') {
			switch (state) {
				case 'accent': return (init:Taccent, parent:Cui_style) => new Caccent(init, parent);
				case 'background': return (init:Tbackground, parent:Cui_style) => new Cbackground(init, parent);
				case 'brand': return (init:Tbrand, parent:Cui_style) => new Cbrand(init, parent);
				case 'error': return (init:Terror, parent:Cui_style) => new Cerror(init, parent);
				case 'foreground': return (init:Tforeground, parent:Cui_style) => new Cforeground(init, parent);
				case 'link': return (init:Tlink, parent:Cui_style) => new Clink(init, parent);
				case 'success': return (init:Tsuccess, parent:Cui_style) => new Csuccess(init, parent);
				case 'warning': return (init:Twarning, parent:Cui_style) => new Cwarning(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'accent'|'background'|'brand'|'error'|'foreground'|'link'|'success'|'warning') {
			switch (state) {
				case 'accent': return finalize_accent;
				case 'background': return finalize_background;
				case 'brand': return finalize_brand;
				case 'error': return finalize_error;
				case 'foreground': return finalize_foreground;
				case 'link': return finalize_link;
				case 'success': return finalize_success;
				case 'warning': return finalize_warning;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_style['style'], parent:Cui_style) {
			super(data, parent, 'style');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'style'`; }
	}
}
export namespace Cui_text_property {
	export class Dclassification extends Cui_property_classification {
		constructor(data:Tui_text_property['classification'], parent:Cui_text_property) {
			super(data, parent, {
				property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.text_property())
					.then(context => context?.parent).result!)
			}, 'classification')
		}
	}
	export class Ddefault_value extends StateGroup<
		{ name: 'no', node:Cno__default_value, init:Tno__default_value}|
		{ name: 'yes', node:Cyes__default_value, init:Tyes__default_value}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default_value, parent:Cui_text_property) => new Cno__default_value(init, parent);
				case 'yes': return (init:Tyes__default_value, parent:Cui_text_property) => new Cyes__default_value(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default_value;
				case 'yes': return finalize_yes__default_value;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['default value'], parent:Cui_text_property) {
			super(data, parent, 'default value');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default value'`; }
	}
	export class Demphasis extends StateGroup<
		{ name: 'no', node:Cno__emphasis__ui_text_property, init:Tno__emphasis__ui_text_property}|
		{ name: 'yes', node:Cyes__emphasis__ui_text_property, init:Tyes__emphasis__ui_text_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__emphasis__ui_text_property, parent:Cui_text_property) => new Cno__emphasis__ui_text_property(init, parent);
				case 'yes': return (init:Tyes__emphasis__ui_text_property, parent:Cui_text_property) => new Cyes__emphasis__ui_text_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__emphasis__ui_text_property;
				case 'yes': return finalize_yes__emphasis__ui_text_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['emphasis'], parent:Cui_text_property) {
			super(data, parent, 'emphasis');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'emphasis'`; }
	}
	export class Dhas_custom_identifying_properties extends StateGroup<
		{ name: 'no', node:Cno__has_custom_identifying_properties, init:Tno__has_custom_identifying_properties}|
		{ name: 'yes', node:Cyes__has_custom_identifying_properties, init:Tyes__has_custom_identifying_properties}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_custom_identifying_properties, parent:Cui_text_property) => new Cno__has_custom_identifying_properties(init, parent);
				case 'yes': return (init:Tyes__has_custom_identifying_properties, parent:Cui_text_property) => new Cyes__has_custom_identifying_properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_custom_identifying_properties;
				case 'yes': return finalize_yes__has_custom_identifying_properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['has custom identifying properties'], parent:Cui_text_property) {
			super(data, parent, 'has custom identifying properties');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has custom identifying properties'`; }
	}
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_text_property, init:Tno__has_description__ui_text_property}|
		{ name: 'yes', node:Cyes__has_description__ui_text_property, init:Tyes__has_description__ui_text_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_text_property, parent:Cui_text_property) => new Cno__has_description__ui_text_property(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_text_property, parent:Cui_text_property) => new Cyes__has_description__ui_text_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_text_property;
				case 'yes': return finalize_yes__has_description__ui_text_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['has description'], parent:Cui_text_property) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
	export class Dhas_validation extends StateGroup<
		{ name: 'no', node:Cno__has_validation__ui_text_property, init:Tno__has_validation__ui_text_property}|
		{ name: 'yes', node:Cyes__has_validation__ui_text_property, init:Tyes__has_validation__ui_text_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_validation__ui_text_property, parent:Cui_text_property) => new Cno__has_validation__ui_text_property(init, parent);
				case 'yes': return (init:Tyes__has_validation__ui_text_property, parent:Cui_text_property) => new Cyes__has_validation__ui_text_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_validation__ui_text_property;
				case 'yes': return finalize_yes__has_validation__ui_text_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['has validation'], parent:Cui_text_property) {
			super(data, parent, 'has validation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has validation'`; }
	}
	export class Dicon extends StateGroup<
		{ name: 'no', node:Cno__icon__ui_text_property, init:Tno__icon__ui_text_property}|
		{ name: 'yes', node:Cyes__icon__ui_text_property, init:Tyes__icon__ui_text_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__icon__ui_text_property, parent:Cui_text_property) => new Cno__icon__ui_text_property(init, parent);
				case 'yes': return (init:Tyes__icon__ui_text_property, parent:Cui_text_property) => new Cyes__icon__ui_text_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__icon__ui_text_property;
				case 'yes': return finalize_yes__icon__ui_text_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['icon'], parent:Cui_text_property) {
			super(data, parent, 'icon');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'icon'`; }
	}
	export class Dis_label extends StateGroup<
		{ name: 'no', node:Cno__is_label, init:Tno__is_label}|
		{ name: 'yes', node:Cyes__is_label, init:Tyes__is_label}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_label, parent:Cui_text_property) => new Cno__is_label(init, parent);
				case 'yes': return (init:Tyes__is_label, parent:Cui_text_property) => new Cyes__is_label(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__is_label;
				case 'yes': return finalize_yes__is_label;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['is label'], parent:Cui_text_property) {
			super(data, parent, 'is label');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is label'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'color', node:Ccolor, init:Tcolor}|
		{ name: 'default', node:Cdefault, init:Tdefault}|
		{ name: 'html', node:Chtml, init:Thtml}|
		{ name: 'markdown', node:Cmarkdown, init:Tmarkdown}|
		{ name: 'multi-line', node:Cmulti_line, init:Tmulti_line}|
		{ name: 'url', node:Curl, init:Turl}> {
		protected initializer(state: 'color'|'default'|'html'|'markdown'|'multi-line'|'url') {
			switch (state) {
				case 'color': return (init:Tcolor, parent:Cui_text_property) => new Ccolor(init, parent);
				case 'default': return (init:Tdefault, parent:Cui_text_property) => new Cdefault(init, parent);
				case 'html': return (init:Thtml, parent:Cui_text_property) => new Chtml(init, parent);
				case 'markdown': return (init:Tmarkdown, parent:Cui_text_property) => new Cmarkdown(init, parent);
				case 'multi-line': return (init:Tmulti_line, parent:Cui_text_property) => new Cmulti_line(init, parent);
				case 'url': return (init:Turl, parent:Cui_text_property) => new Curl(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'color'|'default'|'html'|'markdown'|'multi-line'|'url') {
			switch (state) {
				case 'color': return finalize_color;
				case 'default': return finalize_default;
				case 'html': return finalize_html;
				case 'markdown': return finalize_markdown;
				case 'multi-line': return finalize_multi_line;
				case 'url': return finalize_url;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_text_property['type'], parent:Cui_text_property) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__default_value {
	export class Ddefault_ extends Cui_scalar_default {
		constructor(data:Tyes__default_value['default'], parent:Cyes__default_value) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.text_property())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cui_text({name: 'text property', definition: conv_context});
					})
					.then(context => context?.definitions.ui_property_value)
					.then(context => context?.definitions.ui_scalar).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.text_property())
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'default')
		}
	}
}
export namespace Cyes__has_custom_identifying_properties {
	export class Dselection extends Cui_identifying_property_selection {
		constructor(data:Tyes__has_custom_identifying_properties['selection'], parent:Cyes__has_custom_identifying_properties) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.reference())
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.collection())
					.then(context => context?.properties.node).result!)
			}, 'selection')
		}
	}
}
export namespace Cyes__has_description__ui_text_property {
}
export namespace Cyes__has_validation__ui_text_property {
	export class Drules extends Cui_text_validation {
		constructor(data:Tyes__has_validation__ui_text_property['rules'], parent:Cyes__has_validation__ui_text_property) {
			super(data, parent, 'rules')
		}
	}
}
export namespace Cyes__icon__ui_text_property {
}
export namespace Cui_text_validation {
}
export namespace Cui_todo {
	export class Dhas_description extends StateGroup<
		{ name: 'no', node:Cno__has_description__ui_todo, init:Tno__has_description__ui_todo}|
		{ name: 'yes', node:Cyes__has_description__ui_todo, init:Tyes__has_description__ui_todo}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_description__ui_todo, parent:Cui_todo) => new Cno__has_description__ui_todo(init, parent);
				case 'yes': return (init:Tyes__has_description__ui_todo, parent:Cui_todo) => new Cyes__has_description__ui_todo(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_description__ui_todo;
				case 'yes': return finalize_yes__has_description__ui_todo;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tui_todo['has description'], parent:Cui_todo) {
			super(data, parent, 'has description');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has description'`; }
	}
}
export namespace Cyes__has_description__ui_todo {
}
export namespace Cunary_number_expression {
	export class Dexpression extends Cderivation_expression_tail {
		constructor(data:Tunary_number_expression['expression'], parent:Cunary_number_expression) {
			super(data, parent, {
				support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderivation_expression_support.Punary).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target())
					.then(context => context?.definitions.scalar_value)
					.then(context => context?.definitions.leaf_value)
					.then(context => context?.definitions.value_object).result!),
				this_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
}
export namespace Cuser_initializer {
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tuser_initializer['initializer'], parent:Cuser_initializer) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.command_initialization_operation)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cbase_value_source.Pinternal).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.users_collection())
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
}
export namespace Cuser_requirement {
	export class Dexpression extends Cnode_expression {
		constructor(data:Tuser_requirement['expression'], parent:Cuser_requirement) {
			super(data, parent, {
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definition_node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'expression')
		}
	}
	export class Dhas_alternative extends StateGroup<
		{ name: 'no', node:Cno__has_alternative, init:Tno__has_alternative}|
		{ name: 'yes', node:Cyes__has_alternative, init:Tyes__has_alternative}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_alternative, parent:Cuser_requirement) => new Cno__has_alternative(init, parent);
				case 'yes': return (init:Tyes__has_alternative, parent:Cuser_requirement) => new Cyes__has_alternative(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_alternative;
				case 'yes': return finalize_yes__has_alternative;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tuser_requirement['has alternative'], parent:Cuser_requirement) {
			super(data, parent, 'has alternative');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has alternative'`; }
	}
	export class Dhas_filter extends StateGroup<
		{ name: 'no', node:Cno__has_filter, init:Tno__has_filter}|
		{ name: 'yes', node:Cyes__has_filter, init:Tyes__has_filter}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_filter, parent:Cuser_requirement) => new Cno__has_filter(init, parent);
				case 'yes': return (init:Tyes__has_filter, parent:Cuser_requirement) => new Cyes__has_filter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_filter;
				case 'yes': return finalize_yes__has_filter;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tuser_requirement['has filter'], parent:Cuser_requirement) {
			super(data, parent, 'has filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has filter'`; }
	}
}
export namespace Cyes__has_alternative {
	export class Dalternative extends Cuser_requirement {
		constructor(data:Tyes__has_alternative['alternative'], parent:Cyes__has_alternative) {
			super(data, parent, {
				definition_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definition_node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'alternative')
		}
	}
}
export namespace Cyes__has_filter {
	export class Dassignment extends Coptional_variable_assignment {
		constructor(data:Tyes__has_filter['assignment'], parent:Cyes__has_filter) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.node_type()).result!),
				parent_variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'assignment')
		}
	}
	export class Dpath extends Cuser_requirement {
		constructor(data:Tyes__has_filter['path'], parent:Cyes__has_filter) {
			super(data, parent, {
				definition_node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definition_node_type()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.assignment)
					.then(context => context?.component_root.output.variable()).result!)
			}, 'path')
		}
	}
}
export namespace Cvariable_assignment {
	export class Dname extends StateGroup<
		{ name: 'explicit', node:Cexplicit__name__variable_assignment, init:Texplicit__name__variable_assignment}|
		{ name: 'implicit', node:Cimplicit__name__variable_assignment, init:Timplicit__name__variable_assignment}> {
		protected initializer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return (init:Texplicit__name__variable_assignment, parent:Cvariable_assignment) => new Cexplicit__name__variable_assignment(init, parent);
				case 'implicit': return (init:Timplicit__name__variable_assignment, parent:Cvariable_assignment) => new Cimplicit__name__variable_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'explicit'|'implicit') {
			switch (state) {
				case 'explicit': return finalize_explicit__name__variable_assignment;
				case 'implicit': return finalize_implicit__name__variable_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvariable_assignment['name'], parent:Cvariable_assignment) {
			super(data, parent, 'name');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
}
export namespace Cexplicit__name__variable_assignment {
	export class Dname extends Reference<application.Cnamed_objects__explicit__name__variable_assignment,string> {
		public readonly inferences:{
			single_name: () => application.Cno__has_successor__named_objects__explicit__name__variable_assignment
		}

		constructor(data:string, $this:Cexplicit__name__variable_assignment) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.named_objects.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				single_name: cache((detach:boolean) => {
					const application__variable_assignment__name__explicit__name_nval = $this.properties.name.ref;
					return resolve($this.properties.name.ref)
						.then(context => application__variable_assignment__name__explicit__name_nval)
						.then(context => {
							if (context?.properties.has_successor.state.name === 'no') {
								return resolve(context.properties.has_successor.state.node as application.Cno__has_successor__named_objects__explicit__name__variable_assignment).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
	export class Dnamed_objects extends AlanDictionary<{ node:Cnamed_objects__explicit__name__variable_assignment, init:Tnamed_objects__explicit__name__variable_assignment},Cexplicit__name__variable_assignment> {
		protected initialize(parent:Cexplicit__name__variable_assignment, key:string, entry_init:Tnamed_objects__explicit__name__variable_assignment) { return new Cnamed_objects__explicit__name__variable_assignment(key, entry_init, parent); }
		protected finalize = finalize_named_objects__explicit__name__variable_assignment
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'named objects'`; }
		constructor(data:Texplicit__name__variable_assignment['named objects'], parent:Cexplicit__name__variable_assignment) {
			super(data, parent);
		}
	}
}
export namespace Cnamed_objects__explicit__name__variable_assignment {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__named_objects__explicit__name__variable_assignment, init:Tno__has_successor__named_objects__explicit__name__variable_assignment}|
		{ name: 'yes', node:Cyes__has_successor__named_objects__explicit__name__variable_assignment, init:Tyes__has_successor__named_objects__explicit__name__variable_assignment}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__named_objects__explicit__name__variable_assignment, parent:Cnamed_objects__explicit__name__variable_assignment) => new Cno__has_successor__named_objects__explicit__name__variable_assignment(init, parent);
				case 'yes': return (init:Tyes__has_successor__named_objects__explicit__name__variable_assignment, parent:Cnamed_objects__explicit__name__variable_assignment) => new Cyes__has_successor__named_objects__explicit__name__variable_assignment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor__named_objects__explicit__name__variable_assignment;
				case 'yes': return finalize_yes__has_successor__named_objects__explicit__name__variable_assignment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnamed_objects__explicit__name__variable_assignment['has successor'], parent:Cnamed_objects__explicit__name__variable_assignment) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
}
export namespace Cvariablized_object_path {
	export class Dhead extends Ccontext_object_step {
		constructor(data:Tvariablized_object_path['head'], parent:Cvariablized_object_path) {
			super(data, parent, {
				definer_direction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.direction_support()).result!),
				defining_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.modifier()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
		}
	}
	export class Dpath extends Cobject_path_tail {
		constructor(data:Tvariablized_object_path['path'], parent:Cvariablized_object_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.dependency_location_support()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.parent_navigation_support()).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.head)
					.then(context => context?.component_root.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.this_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'path')
		}
	}
}
export namespace Cwhere_rule_object_path {
	export class Dcontext extends StateGroup<
		{ name: 'sibling rule', node:Csibling_rule, init:Tsibling_rule}|
		{ name: 'this', node:Cthis__context__where_rule_object_path, init:Tthis__context__where_rule_object_path}> {
		protected initializer(state: 'sibling rule'|'this') {
			switch (state) {
				case 'sibling rule': return (init:Tsibling_rule, parent:Cwhere_rule_object_path) => new Csibling_rule(init, parent);
				case 'this': return (init:Tthis__context__where_rule_object_path, parent:Cwhere_rule_object_path) => new Cthis__context__where_rule_object_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'sibling rule'|'this') {
			switch (state) {
				case 'sibling rule': return finalize_sibling_rule;
				case 'this': return finalize_this__context__where_rule_object_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twhere_rule_object_path['context'], parent:Cwhere_rule_object_path) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
	export class Dpath extends Cobject_path_tail {
		constructor(data:Twhere_rule_object_path['path'], parent:Cwhere_rule_object_path) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context?.state.node.output.context()).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.rule())
					.then(context => context?.properties.recursion)
					.then(context => context?.component_root.output.location()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context?.state.node.output.phase()).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Punbounded).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.component_root.output.location())
					.then(context => context?.component_root.output.root()).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!)
			}, 'path')
		}
	}
}
export namespace Csibling_rule {
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			existence: () => application.Cexistence__application
		}

		constructor(data:string, $this:Csibling_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.rule())
				.then(context => {
					const entry = context?.parent.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				existence: cache((detach:boolean) => {
					const application__where_rule_object_path__context__sibling_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__where_rule_object_path__context__sibling_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cthis__context__where_rule_object_path {
	export class Dhead extends Ccontext_object_step {
		constructor(data:Tthis__context__where_rule_object_path['head'], parent:Cthis__context__where_rule_object_path) {
			super(data, parent, {
				definer_direction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.rule())
					.then(context => context?.properties.direction)
					.then(context => context?.component_root.output.direction()).result!),
				defining_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!),
				modifier: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pexpression)
					.then(context => context?.definitions.value_source).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.value_source_support()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'head')
		}
	}
}
export namespace Cwhere_rule_step {
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			dependencies: () => application.Ccomputation_phase,
			existence: () => application.Cexistence__application,
			reference: () => application.Creference_definer,
			relative_location_of_referenced_node: () => application.Crelative_object_location,
			value_source: () => application.Cvalue_source
		}

		constructor(data:string, $this:Cwhere_rule_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.definition())
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				dependencies: cache((detach:boolean) => {
					const application__where_rule_step__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => evaluate__reference_dependencies_checker(detach, 
							resolve(context)
								.then(() => $this.properties.rule.inferences.reference()).result!, {
							context_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location: () => resolve($this)
								.then(() => $this.properties.rule.inferences.reference())
								.then(context => context?.component_root.output.recursion())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.dependency_location_support()).result!
							, 
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				existence: cache((detach:boolean) => {
					const application__where_rule_step__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.rule.inferences.reference())
								.then(context => context?.component_root.output.behaviour())
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const application__where_rule_step__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => application__where_rule_step__rule_nval)
						.then(context => context?.definitions.reference_definer).result!;
				})
				,
				relative_location_of_referenced_node: cache((detach:boolean) => {
					const application__where_rule_step__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => evaluate__merge_locations(detach, 
							resolve(context)
								.then(() => $this)
								.then(context => context?.root).result!, {
							context_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.location()).result!
							, 
							dependency_location_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.referenced_location_support()).result!
							, 
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							reference_context_location: () => resolve($this)
								.then(() => $this.properties.rule.inferences.reference())
								.then(context => context?.component_root.output.location()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!

							})).result!;
				})
				,
				value_source: cache((detach:boolean) => {
					const application__where_rule_step__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => evaluate__value_source_checker(detach, 
							resolve(context)
								.then(() => $this.properties.rule.inferences.reference())
								.then(context => context?.component_root.output.behaviour())
								.then(context => context?.component_root.output.value_source()).result!, {
							dependency_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.context())
								.then(context => context?.component_root.output.node())
								.then(context => context?.component_root.output.location())
								.then(context => context?.component_root.output.root()).result!
							, 
							target_root_location: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.target_root_location()).result!
							, 
							value_source_support: () => resolve($this)
								.then(() => $this)
								.then(context => context?.component_root.input.value_source_support()).result!

							})).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cwhere_rules_definition {
	export class Dhas_rule extends StateGroup<
		{ name: 'no', node:Cno__has_rule, init:Tno__has_rule}|
		{ name: 'yes', node:Cyes__has_rule, init:Tyes__has_rule}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_rule, parent:Cwhere_rules_definition) => new Cno__has_rule(init, parent);
				case 'yes': return (init:Tyes__has_rule, parent:Cwhere_rules_definition) => new Cyes__has_rule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_rule;
				case 'yes': return finalize_yes__has_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twhere_rules_definition['has rule'], parent:Cwhere_rules_definition) {
			super(data, parent, 'has rule');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has rule'`; }
	}
	export class Drules extends AlanTopology<{ node:Crules, init:Trules},Cwhere_rules_definition,'order'> {
		protected graph_iterator(graph:('order')):(node:Crules) => Crules {
			switch (graph) {
				case 'order': return (entry:Crules) => resolve(entry)
					.then(context => {
						if (context?.properties.has_predecessor.state.name === 'yes') {
							return context.properties.has_predecessor.state.node as application.Cyes__has_predecessor__rules;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.rule.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cwhere_rules_definition, key:string, entry_init:Trules) { return new Crules(key, entry_init, parent); }
		protected finalize = finalize_rules
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rules'`; }
		constructor(data:Twhere_rules_definition['rules'], parent:Cwhere_rules_definition) {
			super(data, parent);
		}
	}
}
export namespace Cyes__has_rule {
	export class Dfirst extends Reference<application.Crules,string> {

		constructor(data:string, $this:Cyes__has_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Crules {
	export class Dbehaviour extends Creference_behaviour {
		public readonly inferences:{
			existence_requirement_for_path: () => application.Cexistence__application
		}
		constructor(data:Trules['behaviour'], parent:Crules) {
			super(data, parent, {
				target_value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.value_source()).result!)
			}, 'behaviour')
			this.inferences = {
				existence_requirement_for_path: cache((detach:boolean) => {
					const application__where_rules_definition__rules__behaviour_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.component_root.input.definer())
						.then(context => context?.component_root.output.value_source())
						.then(context => {
							switch (context?.variant.name) {
								case 'base': {
									return resolve(context)
										.then(() => parent)
										.then(() => application.Cexistence__application.Poptional).result;
								}
								case 'derived': {
									return resolve(context)
										.then(() => parent)
										.then(context => context?.component_root.input.definer())
										.then(context => {
											switch (context?.variant.name) {
												case 'state': {
													return resolve(context)
														.then(() => parent)
														.then(() => application.Cexistence__application.Poptional).result;
												}
												case 'text': {
													return resolve(context)
														.then(() => parent)
														.then(context => context?.properties.behaviour)
														.then(context => context?.component_root.output.existence()).result;
												}
												case undefined: return undefined;
												default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
											};
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
							};
						}).result!;
				})

			}
		}
	}
	export class Ddirection extends Cdirection_annotation {
		constructor(data:Trules['direction'], parent:Crules) {
			super(data, parent, 'direction')
		}
	}
	export class Dhas_predecessor extends StateGroup<
		{ name: 'no', node:Cno__has_predecessor__rules, init:Tno__has_predecessor__rules}|
		{ name: 'yes', node:Cyes__has_predecessor__rules, init:Tyes__has_predecessor__rules}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_predecessor__rules, parent:Crules) => new Cno__has_predecessor__rules(init, parent);
				case 'yes': return (init:Tyes__has_predecessor__rules, parent:Crules) => new Cyes__has_predecessor__rules(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_predecessor__rules;
				case 'yes': return finalize_yes__has_predecessor__rules;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trules['has predecessor'], parent:Crules) {
			super(data, parent, 'has predecessor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has predecessor'`; }
	}
	export class Drecursion extends Crecursion_annotation {
		constructor(data:Trules['recursion'], parent:Crules) {
			super(data, parent, {
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer())
					.then(context => context?.component_root.output.defining_property())
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'recursion')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'entry reference', node:Centry_reference, init:Tentry_reference}|
		{ name: 'node reference', node:Cnode_reference, init:Tnode_reference}> {
		protected initializer(state: 'entry reference'|'node reference') {
			switch (state) {
				case 'entry reference': return (init:Tentry_reference, parent:Crules) => new Centry_reference(init, parent);
				case 'node reference': return (init:Tnode_reference, parent:Crules) => new Cnode_reference(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry reference'|'node reference') {
			switch (state) {
				case 'entry reference': return finalize_entry_reference;
				case 'node reference': return finalize_node_reference;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trules['type'], parent:Crules) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_reference_rule {
		constructor(data:Trules['ui'], parent:Crules) {
			super(data, parent, 'ui')
		}
	}
}
export namespace Cyes__has_predecessor__rules {
	export class Drule extends Reference<application.Crules,string> {

		constructor(data:string, $this:Cyes__has_predecessor__rules) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Centry_reference {
	export class Dexpression extends Centry_reference_definition {
		constructor(data:Tentry_reference['expression'], parent:Centry_reference) {
			super(data, parent, {
				collection_existence: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour?.inferences.existence_requirement_for_path()).result!),
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entry_reference_definer).result!),
				required_value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.value_source()).result!)
			}, 'expression')
		}
	}
}
export namespace Cnode_reference {
	export class Dobject_path extends Cwhere_rule_object_path {
		constructor(data:Tnode_reference['object path'], parent:Cnode_reference) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour?.inferences.existence_requirement_for_path()).result!),
				rule: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'object path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'containment', node:Ccontainment__type__node_reference, init:Tcontainment__type__node_reference}|
		{ name: 'equality', node:Cequality__type__node_reference, init:Tequality__type__node_reference}|
		{ name: 'existence', node:Cexistence__type__node_reference, init:Texistence__type__node_reference}> {
		protected initializer(state: 'containment'|'equality'|'existence') {
			switch (state) {
				case 'containment': return (init:Tcontainment__type__node_reference, parent:Cnode_reference) => new Ccontainment__type__node_reference(init, parent);
				case 'equality': return (init:Tequality__type__node_reference, parent:Cnode_reference) => new Cequality__type__node_reference(init, parent);
				case 'existence': return (init:Texistence__type__node_reference, parent:Cnode_reference) => new Cexistence__type__node_reference(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'containment'|'equality'|'existence') {
			switch (state) {
				case 'containment': return finalize_containment__type__node_reference;
				case 'equality': return finalize_equality__type__node_reference;
				case 'existence': return finalize_existence__type__node_reference;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_reference['type'], parent:Cnode_reference) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccontainment__type__node_reference {
	export class Dkey_node_path extends Cwhere_rule_object_path {
		public readonly inferences:{
			containment_iubc: () => application.Cupper_bound_cardinality,
			local_key_node_iubc: () => application.Cupper_bound_cardinality,
			node_context: () => application.Cnode_navigation_context,
			node_type: () => application.Cnode__application
		}
		constructor(data:Tcontainment__type__node_reference['key node path'], parent:Ccontainment__type__node_reference) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.existence()).result!),
				rule: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'key node path')
			this.inferences = {
				containment_iubc: cache((detach:boolean) => {
					const application__where_rules_definition__rules__type__node_reference__type__containment__key_node_path_nval = this;
					return resolve(this)
						.then(context => evaluate__compute_entry_step_iubc(detach, 
							resolve(context)
								.then(() => parent)
								.then(context => context?.root).result!, {
							collection_path_context: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.object_path)
								.then(context => context?.component_root.output.context()).result!
							, 
							definition: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.definitions.entry_step_definition).result!
							, 
							local_step_iubc: () => resolve(parent)
								.then(() => parent.properties.key_node_path.inferences.local_key_node_iubc()).result!

							})).result!;
				})
				,
				local_key_node_iubc: cache((detach:boolean) => {
					const application__where_rules_definition__rules__type__node_reference__type__containment__key_node_path_nval = this;
					return resolve(this)
						.then(context => evaluate__compute_local_key_iubc(detach, 
							resolve(context)
								.then(context => application__where_rules_definition__rules__type__node_reference__type__containment__key_node_path_nval)
								.then(context => context?.component_root.output.context())
								.then(context => context?.component_root.output.dependency()).result!, {
							context_entity: () => resolve(parent)
								.then(() => parent)
								.then(context => context?.component_root.input.definer())
								.then(context => context?.component_root.output.defining_property())
								.then(context => context?.component_root.output.entity()).result!

							})).result!;
				})
				,
				node_context: cache((detach:boolean) => {
					const application__where_rules_definition__rules__type__node_reference__type__containment__key_node_path_nval = this;
					return resolve(this)
						.then(context => application__where_rules_definition__rules__type__node_reference__type__containment__key_node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.node_context())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode_navigation_context : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__where_rules_definition__rules__type__node_reference__type__containment__key_node_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.key_node_path.inferences.node_context())
								.then(context => context?.component_root.output.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.key_reference_definer())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.referenced_node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cequality__type__node_reference {
	export class Dnode_path extends Cwhere_rule_object_path {
		public readonly inferences:{
			node: () => application.Cnode__application,
			node_type: () => application.Cnode__application
		}
		constructor(data:Tequality__type__node_reference['node path'], parent:Cequality__type__node_reference) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.definer()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.existence()).result!),
				rule: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.behaviour)
					.then(context => context?.component_root.output.value_source()).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.variable()).result!)
			}, 'node path')
			this.inferences = {
				node: cache((detach:boolean) => {
					const application__where_rules_definition__rules__type__node_reference__type__equality__node_path_nval = this;
					return resolve(this)
						.then(context => application__where_rules_definition__rules__type__node_reference__type__equality__node_path_nval)
						.then(context => context?.component_root.output.context())
						.then(context => context?.component_root.output.value())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})
				,
				node_type: cache((detach:boolean) => {
					const application__where_rules_definition__rules__type__node_reference__type__equality__node_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.node_path.inferences.node())
								.then(context => context?.component_root.output.node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Capplication {
	export class Dallow_anonymous_user extends StateGroup<
		{ name: 'no', node:Cno__allow_anonymous_user, init:Tno__allow_anonymous_user}|
		{ name: 'yes', node:Cyes__allow_anonymous_user, init:Tyes__allow_anonymous_user}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__allow_anonymous_user, parent:Capplication) => new Cno__allow_anonymous_user(init, parent);
				case 'yes': return (init:Tyes__allow_anonymous_user, parent:Capplication) => new Cyes__allow_anonymous_user(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__allow_anonymous_user;
				case 'yes': return finalize_yes__allow_anonymous_user;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tapplication['allow anonymous user'], parent:Capplication) {
			super(data, parent, 'allow anonymous user');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'allow anonymous user'`; }
	}
	export class Dhas_dynamic_users extends StateGroup<
		{ name: 'no', node:Cno__has_dynamic_users, init:Tno__has_dynamic_users}|
		{ name: 'yes', node:Cyes__has_dynamic_users, init:Tyes__has_dynamic_users}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_dynamic_users, parent:Capplication) => new Cno__has_dynamic_users(init, parent);
				case 'yes': return (init:Tyes__has_dynamic_users, parent:Capplication) => new Cyes__has_dynamic_users(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_dynamic_users;
				case 'yes': return finalize_yes__has_dynamic_users;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tapplication['has dynamic users'], parent:Capplication) {
			super(data, parent, 'has dynamic users');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has dynamic users'`; }
	}
	export class Dimported_interfaces extends AlanDictionary<{ node:Cimported_interfaces, init:Timported_interfaces},Capplication> {
		protected initialize(parent:Capplication, key:string, entry_init:Timported_interfaces) { return new Cimported_interfaces(key, entry_init, parent); }
		protected finalize = finalize_imported_interfaces
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'imported interfaces'`; }
		constructor(data:Tapplication['imported interfaces'], parent:Capplication) {
			super(data, parent);
		}
	}
	export class Dnumerical_types extends AlanDictionary<{ node:Cnumerical_types, init:Tnumerical_types},Capplication> {
		protected initialize(parent:Capplication, key:string, entry_init:Tnumerical_types) { return new Cnumerical_types(key, entry_init, parent); }
		protected finalize = finalize_numerical_types
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'numerical types'`; }
		constructor(data:Tapplication['numerical types'], parent:Capplication) {
			super(data, parent);
		}
	}
	export class Droot extends Cnode__application {
		constructor(data:Tapplication['root'], parent:Capplication) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entity).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.root_location)
					.then(context => context?.definitions.node_location).result!),
				value_source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source)
					.then(context => context?.definitions.value_source).result!)
			}, 'root')
		}
	}
}
export namespace Cyes__has_dynamic_users {
	export class Dhas_external_authentication extends StateGroup<
		{ name: 'no', node:Cno__has_external_authentication, init:Tno__has_external_authentication}|
		{ name: 'yes', node:Cyes__has_external_authentication, init:Tyes__has_external_authentication}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_external_authentication, parent:Cyes__has_dynamic_users) => new Cno__has_external_authentication(init, parent);
				case 'yes': return (init:Tyes__has_external_authentication, parent:Cyes__has_dynamic_users) => new Cyes__has_external_authentication(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_external_authentication;
				case 'yes': return finalize_yes__has_external_authentication;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_dynamic_users['has external authentication'], parent:Cyes__has_dynamic_users) {
			super(data, parent, 'has external authentication');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has external authentication'`; }
	}
	export class Dhas_password_authentication extends StateGroup<
		{ name: 'no', node:Cno__has_password_authentication, init:Tno__has_password_authentication}|
		{ name: 'yes', node:Cyes__has_password_authentication, init:Tyes__has_password_authentication}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_password_authentication, parent:Cyes__has_dynamic_users) => new Cno__has_password_authentication(init, parent);
				case 'yes': return (init:Tyes__has_password_authentication, parent:Cyes__has_dynamic_users) => new Cyes__has_password_authentication(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_password_authentication;
				case 'yes': return finalize_yes__has_password_authentication;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_dynamic_users['has password authentication'], parent:Cyes__has_dynamic_users) {
			super(data, parent, 'has password authentication');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has password authentication'`; }
	}
	export class Dsupports_user_sign_up extends StateGroup<
		{ name: 'no', node:Cno__supports_user_sign_up, init:Tno__supports_user_sign_up}|
		{ name: 'yes', node:Cyes__supports_user_sign_up, init:Tyes__supports_user_sign_up}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__supports_user_sign_up, parent:Cyes__has_dynamic_users) => new Cno__supports_user_sign_up(init, parent);
				case 'yes': return (init:Tyes__supports_user_sign_up, parent:Cyes__has_dynamic_users) => new Cyes__supports_user_sign_up(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__supports_user_sign_up;
				case 'yes': return finalize_yes__supports_user_sign_up;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_dynamic_users['supports user sign-up'], parent:Cyes__has_dynamic_users) {
			super(data, parent, 'supports user sign-up');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'supports user sign-up'`; }
	}
	export class Dusers_collection_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property
		}
		constructor(data:Tyes__has_dynamic_users['users collection path'], parent:Cyes__has_dynamic_users) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'users collection path')
			this.inferences = {
				collection: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__users_collection_path_nval = this;
					return resolve(this)
						.then(context => application__has_dynamic_users__yes__users_collection_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'collection') {
								return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_external_authentication {
	export class Dauthorities_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			base_value: () => application.Cbase__type__collection,
			collection: () => application.Ccollection__type__property
		}
		constructor(data:Tyes__has_external_authentication['authorities path'], parent:Cyes__has_external_authentication) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'authorities path')
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__authorities_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.authorities_path.inferences.collection())
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__collection).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				collection: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__authorities_path_nval = this;
					return resolve(this)
						.then(context => application__has_dynamic_users__yes__has_external_authentication__yes__authorities_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'collection') {
								return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
	export class Didentities_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property
		}
		constructor(data:Tyes__has_external_authentication['identities path'], parent:Cyes__has_external_authentication) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.authorities_path?.inferences.collection())
					.then(context => context?.properties.node).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'identities path')
			this.inferences = {
				collection: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__identities_path_nval = this;
					return resolve(this)
						.then(context => application__has_dynamic_users__yes__has_external_authentication__yes__identities_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'collection') {
								return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
	export class Didentity_initializer extends Cidentity_initializer {
		constructor(data:Tyes__has_external_authentication['identity initializer'], parent:Cyes__has_external_authentication) {
			super(data, parent, {
				identities_collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.identities_path?.inferences.collection()).result!),
				username: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.user_reference?.ref)
					.then(context => context?.parent).result!)
			}, 'identity initializer')
		}
	}
	export class Duser_reference extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			base_value: () => application.Cbase__type__text,
			collection: () => application.Ccollection__type__property,
			mandatory: () => application.Cmandatory__type,
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Cyes__has_external_authentication) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.identities_path?.inferences.collection())
				.then(context => context?.properties.node)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__user_reference_nval = $this.properties.user_reference.ref;
					return resolve($this.properties.user_reference.ref)
						.then(context => application__has_dynamic_users__yes__has_external_authentication__yes__user_reference_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				collection: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__user_reference_nval = $this.properties.user_reference.ref;
					return resolve($this.properties.user_reference.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.user_reference.inferences.reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.collection()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.properties.users_collection_path?.inferences.collection()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				mandatory: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__user_reference_nval = $this.properties.user_reference.ref;
					return resolve($this.properties.user_reference.ref)
						.then(() => $this.properties.user_reference.inferences.reference())
						.then(context => context?.properties.behaviour)
						.then(context => {
							if (context?.properties.type.state.name === 'mandatory') {
								return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_external_authentication__yes__user_reference_nval = $this.properties.user_reference.ref;
					return resolve($this.properties.user_reference.ref)
						.then(context => application__has_dynamic_users__yes__has_external_authentication__yes__user_reference_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'user reference'`; }
	}
}
export namespace Cyes__has_password_authentication {
	export class Dpassword_initializer extends Cpassword_initializer {
		constructor(data:Tyes__has_password_authentication['password initializer'], parent:Cyes__has_password_authentication) {
			super(data, parent, {
				password_collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.passwords_collection_path?.inferences.collection()).result!),
				password_property: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.password_text_path?.inferences.text())
					.then(context => context?.parent).result!),
				password_status: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.password_status)
					.then(context => context?.properties.state_group_path?.inferences.state_group())
					.then(context => context?.parent).result!)
			}, 'password initializer')
		}
	}
	export class Dpassword_text_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			text: () => application.Ctext__type__property
		}
		constructor(data:Tyes__has_password_authentication['password text path'], parent:Cyes__has_password_authentication) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.passwords_collection_path?.inferences.collection())
					.then(context => context?.properties.node).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'password text path')
			this.inferences = {
				text: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_password_authentication__yes__password_text_path_nval = this;
					return resolve(this)
						.then(context => application__has_dynamic_users__yes__has_password_authentication__yes__password_text_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
	export class Dpasswords_collection_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property,
			user_constraint: () => application.Ccollection__type__property,
			user_reference: () => application.Cyes__has_reference
		}
		constructor(data:Tyes__has_password_authentication['passwords collection path'], parent:Cyes__has_password_authentication) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'passwords collection path')
			this.inferences = {
				collection: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_password_authentication__yes__passwords_collection_path_nval = this;
					return resolve(this)
						.then(context => application__has_dynamic_users__yes__has_password_authentication__yes__passwords_collection_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'collection') {
								return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				user_constraint: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_password_authentication__yes__passwords_collection_path_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(() => parent.properties.passwords_collection_path.inferences.user_reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.collection()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.parent)
								.then(context => context?.properties.users_collection_path?.inferences.collection()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				user_reference: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_password_authentication__yes__passwords_collection_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.passwords_collection_path.inferences.collection())
						.then(context => context?.properties.key_property?.ref)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Cpassword_status {
	export class Dcases extends AlanDictionary<{ node:Ccases__password_status, init:Tcases__password_status},Cpassword_status> {
		protected initialize(parent:Cpassword_status, key:string, entry_init:Tcases__password_status) { return new Ccases__password_status(key, entry_init, parent); }
		protected finalize = finalize_cases__password_status
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__has_dynamic_users__yes__has_password_authentication__yes__password_status__cases_key_nval:application.Kkeyset___keycases__password_status):boolean {
				return true;
			};
			if (do_include(keyset___keycases__password_status[`active`])) {
				assert(this.get(`active`) !== undefined);
			}
			if (do_include(keyset___keycases__password_status[`reset`])) {
				assert(this.get(`reset`) !== undefined);
			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
		constructor(data:Tpassword_status['cases'], parent:Cpassword_status) {
			super(data, parent);
		}
	}
	export class Dstate_group_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			state_group: () => application.Cstate_group
		}
		constructor(data:Tpassword_status['state group path'], parent:Cpassword_status) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.passwords_collection_path?.inferences.collection())
					.then(context => context?.properties.node).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'state group path')
			this.inferences = {
				state_group: cache((detach:boolean) => {
					const application__has_dynamic_users__yes__has_password_authentication__yes__password_status__state_group_path_nval = this;
					return resolve(this)
						.then(context => application__has_dynamic_users__yes__has_password_authentication__yes__password_status__state_group_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return resolve(context.properties.type.state.node as application.Cstate_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Ccases__password_status {
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tcases__password_status['initializer'], parent:Ccases__password_status) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccommand_initialization_operation.Pobject)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexternal_source.Puser)
					.then(context => context?.definitions.base_value_source).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Ccases__password_status) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.state_group_path?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cyes__supports_user_sign_up {
	export class Duser_initializer extends Cuser_initializer {
		constructor(data:Tyes__supports_user_sign_up['user initializer'], parent:Cyes__supports_user_sign_up) {
			super(data, parent, {
				users_collection: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.users_collection_path?.inferences.collection()).result!)
			}, 'user initializer')
		}
	}
}
export namespace Cimported_interfaces {
	export class Dconnection_status extends StateGroup<
		{ name: 'no', node:Cno__connection_status, init:Tno__connection_status}|
		{ name: 'yes', node:Cyes__connection_status, init:Tyes__connection_status}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__connection_status, parent:Cimported_interfaces) => new Cno__connection_status(init, parent);
				case 'yes': return (init:Tyes__connection_status, parent:Cimported_interfaces) => new Cyes__connection_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__connection_status;
				case 'yes': return finalize_yes__connection_status;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Timported_interfaces['connection status'], parent:Cimported_interfaces) {
			super(data, parent, 'connection status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'connection status'`; }
	}
	export class Dinstances extends StateGroup<
		{ name: 'many', node:Cmany__instances, init:Tmany__instances}|
		{ name: 'one', node:Cone__instances, init:Tone__instances}> {
		protected initializer(state: 'many'|'one') {
			switch (state) {
				case 'many': return (init:Tmany__instances, parent:Cimported_interfaces) => new Cmany__instances(init, parent);
				case 'one': return (init:Tone__instances, parent:Cimported_interfaces) => new Cone__instances(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'many'|'one') {
			switch (state) {
				case 'many': return finalize_many__instances;
				case 'one': return finalize_one__instances;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Timported_interfaces['instances'], parent:Cimported_interfaces) {
			super(data, parent, 'instances');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instances'`; }
	}
	export class Dparameters extends AlanDictionary<{ node:Cparameters, init:Tparameters},Cimported_interfaces> {
		protected initialize(parent:Cimported_interfaces, key:string, entry_init:Tparameters) { return new Cparameters(key, entry_init, parent); }
		protected finalize = finalize_parameters
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'parameters'`; }
		constructor(data:Timported_interfaces['parameters'], parent:Cimported_interfaces) {
			super(data, parent);
		}
	}
	export class Dpath extends Cobject_path_tail {
		constructor(data:Timported_interfaces['path'], parent:Cimported_interfaces) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root)
					.then(context => {
						const conv_context = resolve(context)
							.then(context => context?.definitions.value_object).result!;
						return new Cnavigation_context({name: 'value', definition: conv_context},{
						dependency: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.definitions.entity)
							.then(context => context?.definitions.dependency).result!
						, 
						direction: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cdirection.Punbounded).result!
						, 
						existence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexistence__application.Pmandatory).result!
						, 
						location: () => resolve(parent)
							.then(() => parent)
							.then(context => context?.parent)
							.then(context => context?.definitions.entity)
							.then(context => context?.definitions.relative_object_location).result!
						, 
						value_source_dependence: () => resolve(parent)
							.then(() => parent)
							.then(() => application.Cexternal_source.Puser)
							.then(context => context?.definitions.base_value_source)
							.then(context => context?.definitions.value_source).result!

						});
					}).result!),
				dependency_location_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Crecursion_dependence.Pnone).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!),
				parent_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				phase: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccomputation_phase.Pzero).result!),
				reference_navigation_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cnavigation_support.Pdescendant).result!),
				target_root_location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.root_location).result!),
				value_source_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cderived_value_source.Pinference)
					.then(context => context?.definitions.value_source).result!)
			}, 'path')
		}
	}
}
export namespace Cyes__connection_status {
	export class Dcases extends AlanDictionary<{ node:Ccases__yes__connection_status, init:Tcases__yes__connection_status},Cyes__connection_status> {
		protected initialize(parent:Cyes__connection_status, key:string, entry_init:Tcases__yes__connection_status) { return new Ccases__yes__connection_status(key, entry_init, parent); }
		protected finalize = finalize_cases__yes__connection_status
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__imported_interfaces__connection_status__yes__cases_key_nval:application.Kkeyset___keycases__yes__connection_status):boolean {
				return true;
			};
			if (do_include(keyset___keycases__yes__connection_status[`connected`])) {
				assert(this.get(`connected`) !== undefined);
			}
			if (do_include(keyset___keycases__yes__connection_status[`connecting`])) {
				assert(this.get(`connecting`) !== undefined);
			}
			if (do_include(keyset___keycases__yes__connection_status[`disconnected`])) {
				assert(this.get(`disconnected`) !== undefined);
			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
		constructor(data:Tyes__connection_status['cases'], parent:Cyes__connection_status) {
			super(data, parent);
		}
	}
	export class Dstate_group_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			base_value: () => application.Cbase__type__state_group,
			state_group: () => application.Cstate_group
		}
		constructor(data:Tyes__connection_status['state group path'], parent:Cyes__connection_status) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.instances?.state.node.output.node_type()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'state group path')
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__imported_interfaces__connection_status__yes__state_group_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.state_group_path.inferences.state_group())
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__state_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				state_group: cache((detach:boolean) => {
					const application__imported_interfaces__connection_status__yes__state_group_path_nval = this;
					return resolve(this)
						.then(context => application__imported_interfaces__connection_status__yes__state_group_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return resolve(context.properties.type.state.node as application.Cstate_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Ccases__yes__connection_status {
	export class Ddataset_status extends StateGroup<
		{ name: 'no', node:Cno__dataset_status, init:Tno__dataset_status}|
		{ name: 'yes', node:Cyes__dataset_status, init:Tyes__dataset_status}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__dataset_status, parent:Ccases__yes__connection_status) => new Cno__dataset_status(init, parent);
				case 'yes': return (init:Tyes__dataset_status, parent:Ccases__yes__connection_status) => new Cyes__dataset_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__dataset_status;
				case 'yes': return finalize_yes__dataset_status;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcases__yes__connection_status['dataset status'], parent:Ccases__yes__connection_status) {
			super(data, parent, 'dataset status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'dataset status'`; }
	}
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tcases__yes__connection_status['initializer'], parent:Ccases__yes__connection_status) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.command_initialization_operation)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.external_source)
					.then(context => context?.definitions.base_value_source).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Ccases__yes__connection_status) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.state_group_path?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cyes__dataset_status {
	export class Dcases extends AlanDictionary<{ node:Ccases__yes__dataset_status, init:Tcases__yes__dataset_status},Cyes__dataset_status> {
		protected initialize(parent:Cyes__dataset_status, key:string, entry_init:Tcases__yes__dataset_status) { return new Ccases__yes__dataset_status(key, entry_init, parent); }
		protected finalize = finalize_cases__yes__dataset_status
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(application__imported_interfaces__connection_status__yes__cases__dataset_status__yes__cases_key_nval:application.Kkeyset___keycases__yes__dataset_status):boolean {
				return true;
			};
			if (do_include(keyset___keycases__yes__dataset_status[`available`])) {
				assert(this.get(`available`) !== undefined);
			}
			if (do_include(keyset___keycases__yes__dataset_status[`unavailable`])) {
				assert(this.get(`unavailable`) !== undefined);
			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
		constructor(data:Tyes__dataset_status['cases'], parent:Cyes__dataset_status) {
			super(data, parent);
		}
	}
	export class Dstate_group_path extends Cdescendant_base_property_path {
		public readonly inferences:{
			base_value: () => application.Cbase__type__state_group,
			state_group: () => application.Cstate_group
		}
		constructor(data:Tyes__dataset_status['state group path'], parent:Cyes__dataset_status) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'state group path')
			this.inferences = {
				base_value: cache((detach:boolean) => {
					const application__imported_interfaces__connection_status__yes__cases__dataset_status__yes__state_group_path_nval = this;
					return resolve(this)
						.then(() => parent.properties.state_group_path.inferences.state_group())
						.then(context => {
							if (context?.properties.type.state.name === 'base') {
								return resolve(context.properties.type.state.node as application.Cbase__type__state_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				state_group: cache((detach:boolean) => {
					const application__imported_interfaces__connection_status__yes__cases__dataset_status__yes__state_group_path_nval = this;
					return resolve(this)
						.then(context => application__imported_interfaces__connection_status__yes__cases__dataset_status__yes__state_group_path_nval)
						.then(context => context?.component_root.output.property())
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return resolve(context.properties.type.state.node as application.Cstate_group).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Ccases__yes__dataset_status {
	export class Dinitializer extends Ccommand_object_expression {
		constructor(data:Tcases__yes__dataset_status['initializer'], parent:Ccases__yes__dataset_status) {
			super(data, parent, {
				current_value: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccurrent_value__application.Pnone).result!),
				operation: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Ccommand_initialization_operation.Pobject)
					.then(context => context?.definitions.command_operation).result!),
				source: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.external_source)
					.then(context => context?.definitions.base_value_source).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				this_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!),
				variable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cvariable__application.Punassigned).result!)
			}, 'initializer')
		}
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Ccases__yes__dataset_status) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.state_group_path?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cmany__instances {
	export class Dinitializer extends Cinterface_instance_initializer {
		constructor(data:Tmany__instances['initializer'], parent:Cmany__instances) {
			super(data, parent, {
				interface_: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'initializer')
		}
	}
}
export namespace Cparameters {
	export class Dvalue extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			value: () => application.Ctext__type__property
		}

		constructor(data:string, $this:Cparameters) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.root)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				value: cache((detach:boolean) => {
					const application__imported_interfaces__parameters__value_nval = $this.properties.value.ref;
					return resolve($this.properties.value.ref)
						.then(context => application__imported_interfaces__parameters__value_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'text') {
										return context.properties.type.state.node as application.Ctext__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cnumerical_types {
	export class Dsingular_conversions extends AlanDictionary<{ node:Csingular_conversions, init:Tsingular_conversions},Cnumerical_types> {
		protected initialize(parent:Cnumerical_types, key:string, entry_init:Tsingular_conversions) { return new Csingular_conversions(key, entry_init, parent); }
		protected finalize = finalize_singular_conversions
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'singular conversions'`; }
		constructor(data:Tnumerical_types['singular conversions'], parent:Cnumerical_types) {
			super(data, parent);
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'quantity', node:Cquantity, init:Tquantity}|
		{ name: 'scale', node:Cscale, init:Tscale}> {
		protected initializer(state: 'quantity'|'scale') {
			switch (state) {
				case 'quantity': return (init:Tquantity, parent:Cnumerical_types) => new Cquantity(init, parent);
				case 'scale': return (init:Tscale, parent:Cnumerical_types) => new Cscale(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'quantity'|'scale') {
			switch (state) {
				case 'quantity': return finalize_quantity;
				case 'scale': return finalize_scale;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumerical_types['type'], parent:Cnumerical_types) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dui extends Cui_numerical_type {
		constructor(data:Tnumerical_types['ui'], parent:Cnumerical_types) {
			super(data, parent, {
				numerical_type: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'ui')
		}
	}
}
export namespace Csingular_conversions {
	export class Dtype extends StateGroup<
		{ name: 'base', node:Cbase__type__singular_conversions, init:Tbase__type__singular_conversions}|
		{ name: 'factor', node:Cfactor, init:Tfactor}> {
		protected initializer(state: 'base'|'factor') {
			switch (state) {
				case 'base': return (init:Tbase__type__singular_conversions, parent:Csingular_conversions) => new Cbase__type__singular_conversions(init, parent);
				case 'factor': return (init:Tfactor, parent:Csingular_conversions) => new Cfactor(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'base'|'factor') {
			switch (state) {
				case 'base': return finalize_base__type__singular_conversions;
				case 'factor': return finalize_factor;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsingular_conversions['type'], parent:Csingular_conversions) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbase__type__singular_conversions {
	export class Dbase extends AlanInteger {
		constructor(data:Tbase__type__singular_conversions['base'], parent:Cbase__type__singular_conversions) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'base'`; }
	}
	export class Dexponent extends AlanInteger {
		constructor(data:Tbase__type__singular_conversions['exponent'], parent:Cbase__type__singular_conversions) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'exponent'`; }
	}
	export class Doffset extends AlanInteger {
		constructor(data:Tbase__type__singular_conversions['offset'], parent:Cbase__type__singular_conversions) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'offset'`; }
	}
	export class Dunit_conversion extends StateGroup<
		{ name: 'no', node:Cno__unit_conversion, init:Tno__unit_conversion}|
		{ name: 'yes', node:Cyes__unit_conversion, init:Tyes__unit_conversion}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__unit_conversion, parent:Cbase__type__singular_conversions) => new Cno__unit_conversion(init, parent);
				case 'yes': return (init:Tyes__unit_conversion, parent:Cbase__type__singular_conversions) => new Cyes__unit_conversion(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__unit_conversion;
				case 'yes': return finalize_yes__unit_conversion;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbase__type__singular_conversions['unit conversion'], parent:Cbase__type__singular_conversions) {
			super(data, parent, 'unit conversion');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'unit conversion'`; }
	}
}
export namespace Cyes__unit_conversion {
	export class Dconversion_rule extends Reference<application.Csingular_conversions,string> {
		public readonly inferences:{
			conversion_rule: () => application.Cfactor,
			range_type: () => application.Cnumerical_types
		}

		constructor(data:string, $this:Cyes__unit_conversion) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.inferences.to())
				.then(context => context?.properties.range_type?.ref)
				.then(context => {
					const entry = context?.properties.singular_conversions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				conversion_rule: cache((detach:boolean) => {
					const application__numerical_types__singular_conversions__type__base__unit_conversion__yes__conversion_rule_nval = $this.properties.conversion_rule.ref;
					return resolve($this.properties.conversion_rule.ref)
						.then(context => application__numerical_types__singular_conversions__type__base__unit_conversion__yes__conversion_rule_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'factor') {
								return resolve(context.properties.type.state.node as application.Cfactor).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				range_type: cache((detach:boolean) => {
					const application__numerical_types__singular_conversions__type__base__unit_conversion__yes__conversion_rule_nval = $this.properties.conversion_rule.ref;
					return resolve($this.properties.conversion_rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => application__numerical_types__singular_conversions__type__base__unit_conversion__yes__conversion_rule_nval)
								.then(context => context?.key?.ref).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.inferences.from())
								.then(context => context?.properties.range_type?.ref).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'conversion rule'`; }
	}
}
export namespace Cfactor {
	export class Dbase extends AlanInteger {
		constructor(data:Tfactor['base'], parent:Cfactor) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'base'`; }
	}
	export class Dexponent extends AlanInteger {
		constructor(data:Tfactor['exponent'], parent:Cfactor) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'exponent'`; }
	}
	export class Dfactor extends AlanInteger {
		constructor(data:Tfactor['factor'], parent:Cfactor) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'factor'`; }
	}
	export class Dinvert extends StateGroup<
		{ name: 'no', node:Cno__invert, init:Tno__invert}|
		{ name: 'yes', node:Cyes__invert, init:Tyes__invert}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__invert, parent:Cfactor) => new Cno__invert(init, parent);
				case 'yes': return (init:Tyes__invert, parent:Cfactor) => new Cyes__invert(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__invert;
				case 'yes': return finalize_yes__invert;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfactor['invert'], parent:Cfactor) {
			super(data, parent, 'invert');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'invert'`; }
	}
}
export namespace Cquantity {
	export class Ddivision_conversions extends AlanDictionary<{ node:Cdivision_conversions, init:Tdivision_conversions},Cquantity> {
		protected initialize(parent:Cquantity, key:string, entry_init:Tdivision_conversions) { return new Cdivision_conversions(key, entry_init, parent); }
		protected finalize = finalize_division_conversions
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'division conversions'`; }
		constructor(data:Tquantity['division conversions'], parent:Cquantity) {
			super(data, parent);
		}
	}
	export class Dproduct_conversions extends AlanDictionary<{ node:Cproduct_conversions, init:Tproduct_conversions},Cquantity> {
		protected initialize(parent:Cquantity, key:string, entry_init:Tproduct_conversions) { return new Cproduct_conversions(key, entry_init, parent); }
		protected finalize = finalize_product_conversions
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'product conversions'`; }
		constructor(data:Tquantity['product conversions'], parent:Cquantity) {
			super(data, parent);
		}
	}
}
export namespace Cdivision_conversions {
	export class Ddenominator extends Reference<application.Cnumerical_types,string> {
		public readonly inferences:{
			denominator: () => application.Cquantity
		}

		constructor(data:string, $this:Cdivision_conversions) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				denominator: cache((detach:boolean) => {
					const application__numerical_types__type__quantity__division_conversions__denominator_nval = $this.properties.denominator.ref;
					return resolve($this.properties.denominator.ref)
						.then(context => application__numerical_types__type__quantity__division_conversions__denominator_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'quantity') {
								return resolve(context.properties.type.state.node as application.Cquantity).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'denominator'`; }
	}
}
export namespace Cproduct_conversions {
	export class Dright extends Reference<application.Cnumerical_types,string> {
		public readonly inferences:{
			right: () => application.Cquantity
		}

		constructor(data:string, $this:Cproduct_conversions) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				right: cache((detach:boolean) => {
					const application__numerical_types__type__quantity__product_conversions__right_nval = $this.properties.right.ref;
					return resolve($this.properties.right.ref)
						.then(context => application__numerical_types__type__quantity__product_conversions__right_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'quantity') {
								return resolve(context.properties.type.state.node as application.Cquantity).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'right'`; }
	}
}
export namespace Cscale {
	export class Drange_type extends Reference<application.Cnumerical_types,string> {
		public readonly inferences:{
			quantity: () => application.Cquantity
		}

		constructor(data:string, $this:Cscale) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				quantity: cache((detach:boolean) => {
					const application__numerical_types__type__scale__range_type_nval = $this.properties.range_type.ref;
					return resolve($this.properties.range_type.ref)
						.then(context => application__numerical_types__type__scale__range_type_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'quantity') {
								return resolve(context.properties.type.state.node as application.Cquantity).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'range type'`; }
	}
	export class Dtimer_resolution extends StateGroup<
		{ name: 'none', node:Cnone__timer_resolution, init:Tnone__timer_resolution}|
		{ name: 'seconds', node:Cseconds__timer_resolution, init:Tseconds__timer_resolution}> {
		protected initializer(state: 'none'|'seconds') {
			switch (state) {
				case 'none': return (init:Tnone__timer_resolution, parent:Cscale) => new Cnone__timer_resolution(init, parent);
				case 'seconds': return (init:Tseconds__timer_resolution, parent:Cscale) => new Cseconds__timer_resolution(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'seconds') {
			switch (state) {
				case 'none': return finalize_none__timer_resolution;
				case 'seconds': return finalize_seconds__timer_resolution;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscale['timer resolution'], parent:Cscale) {
			super(data, parent, 'timer resolution');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'timer resolution'`; }
	}
}
function finalize_no__has_steps__ancestor_named_object_path(obj:Cno__has_steps__ancestor_named_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__ancestor_named_object_path(obj:Cyes__has_steps__ancestor_named_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object_assignment>obj.inferences.variable_assignment)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
	finalize_ancestor_named_object_path(obj.properties.tail, detach);
}
function finalize_ancestor_named_object_path(obj:Cancestor_named_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__ancestor_named_object_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__ancestor_named_object_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__ancestor_node_path(obj:Cno__has_steps__ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__ancestor_node_path(obj:Cyes__has_steps__ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.parent_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_ancestor_node_path(obj.properties.tail, detach);
}
function finalize_ancestor_node_path(obj:Cancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__ancestor_node_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__ancestor_node_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__ancestor_variable_path(obj:Cno__has_steps__ancestor_variable_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__ancestor_variable_path(obj:Cyes__has_steps__ancestor_variable_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable_assignment>obj.inferences.variable_assignment)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
	finalize_ancestor_variable_path(obj.properties.tail, detach);
}
function finalize_ancestor_variable_path(obj:Cancestor_variable_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__ancestor_variable_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__ancestor_variable_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_attribute_class(obj:Cattribute_class, detach:boolean = false) {
}
function finalize_attribute_type(obj:Cattribute_type, detach:boolean = false) {
}
function finalize_base_value_source(obj:Cbase_value_source, detach:boolean = false) {
	finalize_value_source(obj.definitions.value_source, detach);
}
function finalize_collection_attribute(obj:Ccollection_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.input.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.value_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.value_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.output.root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.value_type)(detach) !== undefined || detach);
	finalize_collection_object(obj.definitions.collection_object, detach);
}
function finalize_collection_object(obj:Ccollection_object, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.source_entry_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.input.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.value_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.input.value_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection_attribute>obj.output.source_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.source_entry_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.value_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value_type)(detach) !== undefined || detach);
	finalize_value_object(obj.definitions.value_object, detach);
}
function finalize_no__has_next_command(obj:Cno__has_next_command, detach:boolean = false) {
}
function finalize_yes__has_next_command(obj:Cyes__has_next_command, detach:boolean = false) {
	finalize_command__application(obj.properties.command, detach);
}
function finalize_command__application(obj:Ccommand__application, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	finalize_command_target(obj.definitions.command_target, detach);
	finalize_command_expression(obj.properties.expression, detach);
	switch (obj.properties.has_next_command.state.name) {
		case 'no': finalize_no__has_next_command(obj.properties.has_next_command.state.node, detach); break;
		case 'yes': finalize_yes__has_next_command(obj.properties.has_next_command.state.node, detach); break;
	}
}
function finalize_command_collection(obj:Ccommand_collection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.entry_object)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.entry_object)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_command_properties(obj.definitions.command_properties, detach);
}
function finalize_create__type__entry__operation__command_expression(obj:Ccreate__type__entry__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_initialization_operation>obj.inferences.initialization_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.inferences.key_member)(detach) !== undefined || detach);
	finalize_command_object_initialization_behaviour(obj.properties.behaviour, detach);
	finalize_command_object_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cproperties__command_object_expression>obj.properties.expression.inferences.key_initialization)(detach) !== undefined || detach);
}
function finalize_delete__type__entry__operation__command_expression(obj:Cdelete__type__entry__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupdate__command_operation>obj.inferences.update)(detach) !== undefined || detach);
	finalize_singular_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.path.inferences.collection_node_type)(detach) !== undefined || detach);
}
function finalize_entry__operation__command_expression(obj:Centry__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_collection>obj.inferences.target_collection)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'create': finalize_create__type__entry__operation__command_expression(obj.properties.type.state.node, detach); break;
		case 'delete': finalize_delete__type__entry__operation__command_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_execute_operation__operation__command_expression(obj:Cexecute_operation__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand__application>obj.inferences.command_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand__type>(obj.properties.command as any).resolve)(detach) !== undefined || detach);
	finalize_command_object_expression(obj.properties.expression, detach);
	finalize_singular_node_path(obj.properties.path, detach);
}
function finalize_ignore__operation__command_expression(obj:Cignore__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional_command_operation>obj.inferences.supported_operation)(detach) !== undefined || detach);
}
function finalize_node_case__cases__node_switch__operation__command_expression(obj:Cnode_case__cases__node_switch__operation__command_expression, detach:boolean = false) {
	finalize_command_expression(obj.properties.expression, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_none_case__cases__node_switch__operation__command_expression(obj:Cnone_case__cases__node_switch__operation__command_expression, detach:boolean = false) {
	finalize_command_expression(obj.properties.expression, detach);
}
function finalize_cases__node_switch__operation__command_expression(obj:Ccases__node_switch__operation__command_expression, detach:boolean = false) {
	finalize_node_case__cases__node_switch__operation__command_expression(obj.properties.node_case, detach);
	finalize_none_case__cases__node_switch__operation__command_expression(obj.properties.none_case, detach);
}
function finalize_node_switch__operation__command_expression(obj:Cnode_switch__operation__command_expression, detach:boolean = false) {
	finalize_cases__node_switch__operation__command_expression(obj.properties.cases, detach);
	finalize_node_expression(obj.properties.expression, detach);
}
function finalize_current_value__value(obj:Ccurrent_value__value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cmaybe_value>obj.inferences.current_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_initialization_operation>obj.inferences.initialization)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_properties>obj.inferences.property_target)(detach) !== undefined || detach);
	finalize_command_expression(obj.properties.expression, detach);
}
function finalize_empty_collection(obj:Cempty_collection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_collection>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_initialization_operation>obj.inferences.initialization)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnone__current_value>obj.inferences.no_current_value)(detach) !== undefined || detach);
}
function finalize_object__value__produce_value__operation__command_expression(obj:Cobject__value__produce_value__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_operation>obj.inferences.operation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.target_object)(detach) !== undefined || detach);
	finalize_command_object_expression(obj.properties.expression, detach);
}
function finalize_scalar__value__produce_value__operation__command_expression(obj:Cscalar__value__produce_value__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_scalar>obj.inferences.target_scalar)(detach) !== undefined || detach);
	finalize_scalar_expression(obj.properties.expression, detach);
}
function finalize_state__value__produce_value__operation__command_expression(obj:Cstate__value__produce_value__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_initialization_operation>obj.inferences.initialization_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_stategroup>obj.inferences.target_state_group)(detach) !== undefined || detach);
	finalize_command_object_initialization_behaviour(obj.properties.behaviour, detach);
	finalize_command_object_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Ccommand_state>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.state.inferences.target)(detach) !== undefined || detach);
}
function finalize_produce_value__operation__command_expression(obj:Cproduce_value__operation__command_expression, detach:boolean = false) {
	switch (obj.properties.value.state.name) {
		case 'current value': finalize_current_value__value(obj.properties.value.state.node, detach); break;
		case 'empty collection': finalize_empty_collection(obj.properties.value.state.node, detach); break;
		case 'object': finalize_object__value__produce_value__operation__command_expression(obj.properties.value.state.node, detach); break;
		case 'scalar': finalize_scalar__value__produce_value__operation__command_expression(obj.properties.value.state.node, detach); break;
		case 'state': finalize_state__value__produce_value__operation__command_expression(obj.properties.value.state.node, detach); break;
	}
}
function finalize_path__state_switch__operation__command_expression(obj:Cpath__state_switch__operation__command_expression, detach:boolean = false) {
	finalize_singular_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.stategroup as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__state_switch__operation__command_expression(obj:Cstates__state_switch__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_command_expression(obj.properties.expression, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_state_switch__operation__command_expression(obj:Cstate_switch__operation__command_expression, detach:boolean = false) {
	finalize_path__state_switch__operation__command_expression(obj.properties.path, detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_switch__operation__command_expression(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_node__target__update_properties__operation__command_expression(obj:Cnode__target__update_properties__operation__command_expression, detach:boolean = false) {
	finalize_command_object_expression(obj.properties.expression, detach);
}
function finalize_property__target__update_properties__operation__command_expression(obj:Cproperty__target__update_properties__operation__command_expression, detach:boolean = false) {
	finalize_command_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_properties>obj.properties.property.inferences.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.properties.property.inferences.user_value)(detach) !== undefined || detach);
}
function finalize_update_properties__operation__command_expression(obj:Cupdate_properties__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand__application>obj.inferences.command_target)(detach) !== undefined || detach);
	finalize_singular_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.properties.path.inferences.user_value)(detach) !== undefined || detach);
	switch (obj.properties.target.state.name) {
		case 'node': finalize_node__target__update_properties__operation__command_expression(obj.properties.target.state.node, detach); break;
		case 'property': finalize_property__target__update_properties__operation__command_expression(obj.properties.target.state.node, detach); break;
	}
}
function finalize_collection__type__walk(obj:Ccollection__type__walk, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_reference_set__type__walk(obj:Creference_set__type__walk, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand__application>obj.inferences.command_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_set__type__attributes>(obj.properties.reference_set as any).resolve)(detach) !== undefined || detach);
}
function finalize_walk__operation__command_expression(obj:Cwalk__operation__command_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_target>obj.inferences.supported_target)(detach) !== undefined || detach);
	finalize_command_expression(obj.properties.expression, detach);
	finalize_singular_node_path(obj.properties.path, detach);
	finalize_node_path_tail(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__walk(obj.properties.type.state.node, detach); break;
		case 'reference set': finalize_reference_set__type__walk(obj.properties.type.state.node, detach); break;
	}
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_command_expression(obj:Ccommand_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_target>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.operation.state.name) {
		case 'entry': finalize_entry__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'execute operation': finalize_execute_operation__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'ignore': finalize_ignore__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'node switch': finalize_node_switch__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'produce value': finalize_produce_value__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'state switch': finalize_state_switch__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'update properties': finalize_update_properties__operation__command_expression(obj.properties.operation.state.node, detach); break;
		case 'walk': finalize_walk__operation__command_expression(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_command_file(obj:Ccommand_file, detach:boolean = false) {
	finalize_command_scalar(obj.definitions.command_scalar, detach);
}
function finalize_command_initialization_operation(obj:Ccommand_initialization_operation, detach:boolean = false) {
	finalize_command_operation(obj.definitions.command_operation, detach);
}
function finalize_command_number(obj:Ccommand_number, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.input.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.input.set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
	finalize_command_scalar(obj.definitions.command_scalar, detach);
}
function finalize_properties__command_object_expression(obj:Cproperties__command_object_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.key.inferences.current_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_member_inclusion>obj.key.inferences.filter_identity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_member_inclusion>obj.key.inferences.filter_interface_dataset_status)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_member_inclusion>obj.key.inferences.filter_interface_instance_key)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_member_inclusion>obj.key.inferences.filter_password)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_member_inclusion>obj.key.inferences.filter_password_status)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_member_inclusion>obj.key.inferences.filter_username)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_properties>obj.key.inferences.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.key.inferences.value_source)(detach) !== undefined || detach);
	finalize_command_expression(obj.properties.expression, detach);
}
function finalize_command_object_expression(obj:Ccommand_object_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.input.current_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_operation>obj.input.operation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__command_object_expression(entry, detach);
	}
	if (!detach) {
		(obj.properties.properties as any).eval_required_keys(detach);
	}
}
function finalize_ensure_existence(obj:Censure_existence, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cmaybe_value>obj.inferences.maybe_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.output.current_value)(detach) !== undefined || detach);
}
function finalize_fail_when_exists(obj:Cfail_when_exists, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.output.current_value)(detach) !== undefined || detach);
}
function finalize_command_object_initialization_behaviour(obj:Ccommand_object_initialization_behaviour, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.input.current_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.output.current_value)(detach) !== undefined || detach);
	switch (obj.properties.behaviour.state.name) {
		case 'ensure existence': finalize_ensure_existence(obj.properties.behaviour.state.node, detach); break;
		case 'fail when exists': finalize_fail_when_exists(obj.properties.behaviour.state.node, detach); break;
	}
}
function finalize_command_operation(obj:Ccommand_operation, detach:boolean = false) {
	finalize_optional_command_operation(obj.definitions.optional_command_operation, detach);
}
function finalize_explicit__execute_permission(obj:Cexplicit__execute_permission, detach:boolean = false) {
	finalize_user_requirement(obj.properties.requirement, detach);
}
function finalize_reachable(obj:Creachable, detach:boolean = false) {
}
function finalize_command_permission_definition(obj:Ccommand_permission_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	switch (obj.properties.execute_permission.state.name) {
		case 'explicit': finalize_explicit__execute_permission(obj.properties.execute_permission.state.node, detach); break;
		case 'reachable': finalize_reachable(obj.properties.execute_permission.state.node, detach); break;
	}
}
function finalize_command_properties(obj:Ccommand_properties, detach:boolean = false) {
}
function finalize_command_scalar(obj:Ccommand_scalar, detach:boolean = false) {
	finalize_command_properties(obj.definitions.command_properties, detach);
}
function finalize_command_state(obj:Ccommand_state, detach:boolean = false) {
}
function finalize_command_stategroup(obj:Ccommand_stategroup, detach:boolean = false) {
	finalize_command_properties(obj.definitions.command_properties, detach);
}
function finalize_command_target(obj:Ccommand_target, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.input.current_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_command_operation>obj.input.operation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccurrent_value__application>obj.output.current_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_command_operation>obj.output.operation)(detach) !== undefined || detach);
}
function finalize_command_text(obj:Ccommand_text, detach:boolean = false) {
	finalize_command_scalar(obj.definitions.command_scalar, detach);
}
function finalize_computation_phase(obj:Ccomputation_phase, detach:boolean = false) {
}
function finalize_conditional_node_path(obj:Cconditional_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.path, detach);
}
function finalize_connection_status(obj:Cconnection_status, detach:boolean = false) {
}
function finalize_constant_number_value(obj:Cconstant_number_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.value.inferences.set_type)(detach) !== undefined || detach);
}
function finalize_dynamic_user__context__context_node_path(obj:Cdynamic_user__context__context_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_dynamic_users>obj.inferences.has_dynamic_users)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.inferences.user_modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
}
function finalize_this__context__context_node_path(obj:Cthis__context__context_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
}
function finalize_explicit__name__variable(obj:Cexplicit__name__variable, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable_assignment>obj.output.assigned_variable)(detach) !== undefined || detach);
	finalize_ancestor_variable_path(obj.properties.head, detach);
	assert((<(detach?:boolean) => application.Cvariable_assignment>obj.properties.head.inferences.assigned_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexplicit__name__variable_assignment>obj.properties.head.inferences.explicit_name)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_objects__explicit__name__variable_assignment>(obj.properties.variable as any).resolve)(detach) !== undefined || detach);
}
function finalize_implicit__name__variable(obj:Cimplicit__name__variable, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable_assignment>obj.inferences.assigned_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cimplicit__name__variable_assignment>obj.inferences.implicit_name)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable_assignment>obj.output.assigned_variable)(detach) !== undefined || detach);
}
function finalize_variable__context__context_node_path(obj:Cvariable__context__context_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	switch (obj.properties.name.state.name) {
		case 'explicit': finalize_explicit__name__variable(obj.properties.name.state.node, detach); break;
		case 'implicit': finalize_implicit__name__variable(obj.properties.name.state.node, detach); break;
	}
}
function finalize_context_node_path(obj:Ccontext_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'dynamic user': finalize_dynamic_user__context__context_node_path(obj.properties.context.state.node, detach); break;
		case 'this': finalize_this__context__context_node_path(obj.properties.context.state.node, detach); break;
		case 'variable': finalize_variable__context__context_node_path(obj.properties.context.state.node, detach); break;
	}
}
function finalize_dynamic_user__context__context_object_step(obj:Cdynamic_user__context__context_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_dynamic_users>obj.inferences.has_dynamic_users)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.inferences.user_modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.user_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
}
function finalize_this__context__context_object_step(obj:Cthis__context__context_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
}
function finalize_variable__context__context_object_step(obj:Cvariable__context__context_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_named_object_step(obj.properties.step, detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.step.inferences.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.step.inferences.value_source)(detach) !== undefined || detach);
}
function finalize_context_object_step(obj:Ccontext_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdirection>obj.input.definer_direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.defining_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'dynamic user': finalize_dynamic_user__context__context_object_step(obj.properties.context.state.node, detach); break;
		case 'this': finalize_this__context__context_object_step(obj.properties.context.state.node, detach); break;
		case 'variable': finalize_variable__context__context_object_step(obj.properties.context.state.node, detach); break;
	}
}
function finalize_current_value__application(obj:Ccurrent_value__application, detach:boolean = false) {
}
function finalize_dependency__application(obj:Cdependency__application, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
}
function finalize_dependency_definition(obj:Cdependency_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.base_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.parent)(detach) !== undefined || detach);
	finalize_dependency__application(obj.definitions.dependency, detach);
}
function finalize_dependency_gen(obj:Cdependency_gen, detach:boolean = false) {
	finalize_optional_dependency_gen(obj.definitions.optional_dependency_gen, detach);
}
function finalize_reference__type__dependency_step(obj:Creference__type__dependency_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	finalize_property_step(obj.properties.property, detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.property.inferences.dependencies)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.property.inferences.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.property.inferences.has_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.properties.property.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.property.inferences.relative_location_of_referenced_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.properties.property.inferences.text)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.property.inferences.value_source)(detach) !== undefined || detach);
}
function finalize_reference_rule(obj:Creference_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	finalize_property_step(obj.properties.property, detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.property.inferences.has_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.properties.property.inferences.text)(detach) !== undefined || detach);
	finalize_where_rule_step(obj.properties.rule, detach);
}
function finalize_state_context_rule__type__dependency_step(obj:Cstate_context_rule__type__dependency_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	finalize_where_rule_step(obj.properties.rule, detach);
}
function finalize_dependency_step(obj:Cdependency_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.referenced_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'reference': finalize_reference__type__dependency_step(obj.properties.type.state.node, detach); break;
		case 'reference rule': finalize_reference_rule(obj.properties.type.state.node, detach); break;
		case 'state context rule': finalize_state_context_rule__type__dependency_step(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node__cases(obj:Cnode__cases, detach:boolean = false) {
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_optional_named_object_assignment(obj.properties.variable_assignment, detach);
}
function finalize_cases__node_switch__type(obj:Ccases__node_switch__type, detach:boolean = false) {
	finalize_node__cases(obj.properties.node, detach);
	finalize_derivation_expression(obj.properties.none, detach);
}
function finalize_equality__operation(obj:Cequality__operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.other_node_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.other_node_path.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.other_node_path.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_existence__operation(obj:Cexistence__operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
}
function finalize_node_switch__type(obj:Cnode_switch__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_cases__node_switch__type(obj.properties.cases, detach);
	finalize_variablized_object_path(obj.properties.node_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.node_path.inferences.node)(detach) !== undefined || detach);
	switch (obj.properties.operation.state.name) {
		case 'equality': finalize_equality__operation(obj.properties.operation.state.node, detach); break;
		case 'existence': finalize_existence__operation(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_no__has_predecessor__cases(obj:Cno__has_predecessor__cases, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.output.equals_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.output.greater_than_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.output.less_than_case)(detach) !== undefined || detach);
}
function finalize_yes__has_predecessor__cases(obj:Cyes__has_predecessor__cases, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.output.equals_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.output.greater_than_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.output.less_than_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccases__number_switch>(obj.properties.case_ as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.properties.case_.inferences.equals_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.properties.case_.inferences.greater_than_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_comparison_case>obj.properties.case_.inferences.less_than_case)(detach) !== undefined || detach);
}
function finalize_cases__number_switch(obj:Ccases__number_switch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Kkeyset___keycases__number_switch>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_number_comparison_case(obj.definitions.number_comparison_case, detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	switch (obj.properties.has_predecessor.state.name) {
		case 'no': finalize_no__has_predecessor__cases(obj.properties.has_predecessor.state.node, detach); break;
		case 'yes': finalize_yes__has_predecessor__cases(obj.properties.has_predecessor.state.node, detach); break;
	}
	finalize_optional_named_object_assignment(obj.properties.variable_assignment, detach);
}
function finalize_constant__compare_to(obj:Cconstant__compare_to, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.greater_than_set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
	finalize_constant_number_value(obj.properties.value, detach);
}
function finalize_path__compare_to(obj:Cpath__compare_to, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.greater_than_set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.right_number_path, detach);
	assert((<(detach?:boolean) => application.Cnumber_value>obj.properties.right_number_path.inferences.number)(detach) !== undefined || detach);
}
function finalize_number_switch(obj:Cnumber_switch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.cases) {
		finalize_cases__number_switch(entry, detach);
	}
	if (!detach) {
		if (obj.properties.cases.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
	switch (obj.properties.compare_to.state.name) {
		case 'constant': finalize_constant__compare_to(obj.properties.compare_to.state.node, detach); break;
		case 'path': finalize_path__compare_to(obj.properties.compare_to.state.node, detach); break;
	}
	assert((<(detach?:boolean) => application.Ccases__number_switch>(obj.properties.last_case as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccases__number_switch>obj.properties.last_case.inferences.equals_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccases__number_switch>obj.properties.last_case.inferences.greater_than_case)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccases__number_switch>obj.properties.last_case.inferences.less_than_case)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.number_path, detach);
	assert((<(detach?:boolean) => application.Cnumber_value>obj.properties.number_path.inferences.number)(detach) !== undefined || detach);
}
function finalize_produce_value__type(obj:Cproduce_value__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression_tail(obj.properties.expression, detach);
}
function finalize_repeat(obj:Crepeat, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_named_object_step(obj.properties.recursion_start, detach);
	assert((<(detach?:boolean) => application.Cstart>obj.properties.recursion_start.inferences.closure)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.sibling_node_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.sibling_node_path.inferences.binding_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.properties.sibling_node_path.inferences.graph_traversing_step)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.sibling_node_path.inferences.graph_traversing_steps)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.sibling_node_path.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.sibling_node_path.inferences.sibling_of_recursion_start_object)(detach) !== undefined || detach);
}
function finalize_start(obj:Cstart, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_named_object_definer(obj.definitions.named_object_definer, detach);
	finalize_singular_variablized_object_path(obj.properties.binding_node_path, detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.binding_node_path.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.binding_node_path.inferences.node)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_graph_traversal_definition(obj.properties.graph_traversal, detach);
	finalize_named_object_assignment(obj.properties.recursion_start_assignment, detach);
}
function finalize_recurse(obj:Crecurse, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.step.state.name) {
		case 'repeat': finalize_repeat(obj.properties.step.state.node, detach); break;
		case 'start': finalize_start(obj.properties.step.state.node, detach); break;
	}
}
function finalize_no__match_node(obj:Cno__match_node, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
}
function finalize_yes__match_node(obj:Cyes__match_node, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_optional_named_object_assignment(obj.properties.variable_assignment, detach);
}
function finalize_no__match_nodes(obj:Cno__match_nodes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cone__upper_bound_cardinality>obj.inferences.upper_bound_cardinality_for_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
}
function finalize_yes__match_nodes(obj:Cyes__match_nodes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cmany__upper_bound_cardinality>obj.inferences.upper_bound_cardinality_for_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_optional_named_object_assignment(obj.properties.variable_assignment, detach);
}
function finalize_no__match_none(obj:Cno__match_none, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cone__lower_bound_cardinality>obj.inferences.lower_bound_cardinality_for_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
}
function finalize_yes__match_none(obj:Cyes__match_none, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Czero__lower_bound_cardinality>obj.inferences.lower_bound_cardinality_for_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
}
function finalize_cases__set_switch(obj:Ccases__set_switch, detach:boolean = false) {
	switch (obj.properties.match_node.state.name) {
		case 'no': finalize_no__match_node(obj.properties.match_node.state.node, detach); break;
		case 'yes': finalize_yes__match_node(obj.properties.match_node.state.node, detach); break;
	}
	switch (obj.properties.match_nodes.state.name) {
		case 'no': finalize_no__match_nodes(obj.properties.match_nodes.state.node, detach); break;
		case 'yes': finalize_yes__match_nodes(obj.properties.match_nodes.state.node, detach); break;
	}
	switch (obj.properties.match_none.state.name) {
		case 'no': finalize_no__match_none(obj.properties.match_none.state.node, detach); break;
		case 'yes': finalize_yes__match_none(obj.properties.match_none.state.node, detach); break;
	}
}
function finalize_set_switch(obj:Cset_switch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_cases__set_switch(obj.properties.cases, detach);
	finalize_object_set_path(obj.properties.nodes_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.nodes_path.inferences.node)(detach) !== undefined || detach);
}
function finalize_states__state_switch__type(obj:Cstates__state_switch__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_optional_named_object_assignment(obj.properties.variable_assignment, detach);
}
function finalize_state_switch__type(obj:Cstate_switch__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.state_group_path, detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.state_group_path.inferences.state_group)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_switch__type(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_derivation_expression(obj:Cderivation_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'node switch': finalize_node_switch__type(obj.properties.type.state.node, detach); break;
		case 'number switch': finalize_number_switch(obj.properties.type.state.node, detach); break;
		case 'produce value': finalize_produce_value__type(obj.properties.type.state.node, detach); break;
		case 'recurse': finalize_recurse(obj.properties.type.state.node, detach); break;
		case 'set switch': finalize_set_switch(obj.properties.type.state.node, detach); break;
		case 'state switch': finalize_state_switch__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_tail__derivation_expression_list(obj:Cno__has_tail__derivation_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
}
function finalize_yes__has_tail__derivation_expression_list(obj:Cyes__has_tail__derivation_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression_list(obj.properties.tail, detach);
}
function finalize_derivation_expression_list(obj:Cderivation_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.has_tail.state.name) {
		case 'no': finalize_no__has_tail__derivation_expression_list(obj.properties.has_tail.state.node, detach); break;
		case 'yes': finalize_yes__has_tail__derivation_expression_list(obj.properties.has_tail.state.node, detach); break;
	}
	finalize_derivation_expression_tail(obj.properties.head, detach);
}
function finalize_derivation_expression_support(obj:Cderivation_expression_support, detach:boolean = false) {
}
function finalize_dynamic__type__derivation_expression_tail(obj:Cdynamic__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cleaf_value>obj.inferences.leaf)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.path.inferences.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.properties.path.inferences.value)(detach) !== undefined || detach);
}
function finalize_number__type__derivation_expression_tail(obj:Cnumber__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_value>obj.inferences.number)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_number_expression(obj.properties.expression, detach);
}
function finalize_branch(obj:Cbranch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cno__key_constraint>obj.inferences.derived_collection_without_key_constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbranches__no>(obj.properties.branch as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.branch.inferences.normalized_dependency_chain)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.expression.inferences.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.expression.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.expression.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_sink(obj:Csink, detach:boolean = false) {
}
function finalize_source(obj:Csource, detach:boolean = false) {
}
function finalize_ordered_graph_node(obj:Cordered_graph_node, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.collection_path, detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.properties.collection_path.inferences.cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection_object>obj.properties.collection_path.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.collection_path.inferences.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.collection_path.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.collection_path.inferences.source_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__existence>obj.properties.collection_path.inferences.unfiltered_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cordered>(obj.properties.ordered_graph as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'sink': finalize_sink(obj.properties.type.state.node, detach); break;
		case 'source': finalize_source(obj.properties.type.state.node, detach); break;
	}
}
function finalize_reference__type__derivation_expression_tail(obj:Creference__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centry_reference_definition>obj.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'branch': finalize_branch(obj.properties.type.state.node, detach); break;
		case 'ordered graph node': finalize_ordered_graph_node(obj.properties.type.state.node, detach); break;
	}
}
function finalize_state__type__derivation_expression_tail(obj:Cstate__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_state_initializer(obj.properties.initializer, detach);
}
function finalize_concatenation__type__text__type__derivation_expression_tail(obj:Cconcatenation__type__text__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cunrestricted>obj.inferences.no_nested_concatenation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression_list(obj.properties.expression, detach);
}
function finalize_static__type__text__type__derivation_expression_tail(obj:Cstatic__type__text__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
}
function finalize_text__type__derivation_expression_tail(obj:Ctext__type__derivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'concatenation': finalize_concatenation__type__text__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type__text__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
	}
}
function finalize_derivation_expression_tail(obj:Cderivation_expression_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cderivation_expression_support>obj.input.support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__derivation_expression_tail(obj.properties.type.state.node, detach); break;
	}
}
function finalize_derived_set(obj:Cderived_set, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.input.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection_attribute>obj.input.source_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.source_entry_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.value_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.input.value_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection_attribute>obj.output.source_collection)(detach) !== undefined || detach);
	finalize_collection_object(obj.definitions.collection_object, detach);
}
function finalize_derived_value_source(obj:Cderived_value_source, detach:boolean = false) {
	finalize_value_source(obj.definitions.value_source, detach);
}
function finalize_descendant_base_property_path(obj:Cdescendant_base_property_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.property)(detach) !== undefined || detach);
	finalize_object_path_tail(obj.properties.head, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.head.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.properties.property.inferences.base_value)(detach) !== undefined || detach);
}
function finalize_descendant_object_path(obj:Cdescendant_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_object_path_tail(obj.properties.path, detach);
}
function finalize_direction(obj:Cdirection, detach:boolean = false) {
}
function finalize_downstream__direction__direction_annotation(obj:Cdownstream__direction__direction_annotation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
}
function finalize_upstream__direction__direction_annotation(obj:Cupstream__direction__direction_annotation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
}
function finalize_direction_annotation(obj:Cdirection_annotation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	switch (obj.properties.direction.state.name) {
		case 'downstream': finalize_downstream__direction__direction_annotation(obj.properties.direction.state.node, detach); break;
		case 'upstream': finalize_upstream__direction__direction_annotation(obj.properties.direction.state.node, detach); break;
	}
}
function finalize_entity__application(obj:Centity__application, detach:boolean = false) {
	finalize_dependency__application(obj.definitions.dependency, detach);
	finalize_relative_object_location(obj.definitions.relative_object_location, detach);
	finalize_sibling_dependency_retrieval(obj.definitions.sibling_dependency_retrieval, detach);
}
function finalize_entry_reference_definer(obj:Centry_reference_definer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.defining_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.defining_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.reference_definer)(detach) !== undefined || detach);
}
function finalize_sibling__type__entry_reference_definition(obj:Csibling__type__entry_reference_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.entry_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.iubc)(detach) !== undefined || detach);
	finalize_sibling_reference_definition(obj.properties.definition, detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.definition.inferences.collection_managing_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.properties.definition.inferences.inversion_upper_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_simple__type__entry_reference_definition(obj:Csimple__type__entry_reference_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.entry_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.iubc)(detach) !== undefined || detach);
}
function finalize_entry_reference_definition(obj:Centry_reference_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.collection_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centry_reference_definer>obj.input.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.required_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centry_reference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.iubc)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.output.key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.local_iubc)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
	finalize_entry_step_definition(obj.definitions.entry_step_definition, detach);
	finalize_reference_value(obj.definitions.reference_value, detach);
	finalize_variablized_object_path(obj.properties.collection_path, detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.collection_path.inferences.location)(detach) !== undefined || detach);
	finalize_descendant_object_path(obj.properties.tail, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.tail.inferences.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'sibling': finalize_sibling__type__entry_reference_definition(obj.properties.type.state.node, detach); break;
		case 'simple': finalize_simple__type__entry_reference_definition(obj.properties.type.state.node, detach); break;
	}
}
function finalize_property__definer(obj:Cproperty__definer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.output.property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
}
function finalize_rule(obj:Crule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.output.property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.properties.rule.inferences.entry_step)(detach) !== undefined || detach);
}
function finalize_entry_reference_selector(obj:Centry_reference_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.output.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.output.property)(detach) !== undefined || detach);
	switch (obj.properties.definer.state.name) {
		case 'property': finalize_property__definer(obj.properties.definer.state.node, detach); break;
		case 'rule': finalize_rule(obj.properties.definer.state.node, detach); break;
	}
}
function finalize_entry_step_definition(obj:Centry_step_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.iubc)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.local_iubc)(detach) !== undefined || detach);
	finalize_step_type(obj.definitions.step_type, detach);
}
function finalize_existence__application(obj:Cexistence__application, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_external_source(obj:Cexternal_source, detach:boolean = false) {
	finalize_base_value_source(obj.definitions.base_value_source, detach);
}
function finalize_flatten_expression(obj:Cflatten_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	finalize_group_ancestor_node_path(obj.properties.head, detach);
	finalize_plural_descendant_node_path(obj.properties.path, detach);
}
function finalize_acyclic(obj:Cacyclic, detach:boolean = false) {
}
function finalize_ordering_states(obj:Cordering_states, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_reference>(obj.properties.edge_reference as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.properties.edge_reference.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__graph_participation>obj.properties.edge_reference.inferences.graph_edge)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.properties.edge_reference.inferences.mandatory_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cordered>obj.properties.edge_reference.inferences.ordered_graph_edge)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csibling__type__entry_reference_definition>obj.properties.edge_reference.inferences.sibling_edge)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.edge_state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.sink_state as any).resolve)(detach) !== undefined || detach);
}
function finalize_ordering_property_path(obj:Cordering_property_path, detach:boolean = false) {
	finalize_descendant_base_property_path(obj.properties.ordering_state_group_path, detach);
	assert((<(detach?:boolean) => application.Cbase__type__state_group>obj.properties.ordering_state_group_path.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.ordering_state_group_path.inferences.state_group)(detach) !== undefined || detach);
	finalize_ordering_states(obj.properties.ordering_states, detach);
}
function finalize_ordered(obj:Cordered, detach:boolean = false) {
	finalize_ordering_property_path(obj.properties.ordering_property_path, detach);
}
function finalize_base__value_type(obj:Cbase__value_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__collection>obj.inferences.base_collection)(detach) !== undefined || detach);
}
function finalize_graphs(obj:Cgraphs, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'acyclic': finalize_acyclic(obj.properties.type.state.node, detach); break;
		case 'ordered': finalize_ordered(obj.properties.type.state.node, detach); break;
	}
	switch (obj.properties.value_type.state.name) {
		case 'base': finalize_base__value_type(obj.properties.value_type.state.node, detach); break;
	}
}
function finalize_graph_constraints_definition(obj:Cgraph_constraints_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.graphs) {
		finalize_graphs(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_graph_traversal(obj:Cgraph_traversal, detach:boolean = false) {
	finalize_optional_graph_traversal(obj.definitions.optional_graph_traversal, detach);
	finalize_recursion_dependence(obj.definitions.recursion_dependence, detach);
}
function finalize_base_order(obj:Cbase_order, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgraph_traversal_type>obj.output.type)(detach) !== undefined || detach);
}
function finalize_inverse_order(obj:Cinverse_order, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cderived_value_source>obj.inferences.derived_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgraph_traversal_type>obj.output.type)(detach) !== undefined || detach);
}
function finalize_graph_traversal_definition(obj:Cgraph_traversal_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgraphs>obj.output.graph)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgraph_traversal_type>obj.output.type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgraphs>(obj.properties.graph as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.traversal_type.state.name) {
		case 'base order': finalize_base_order(obj.properties.traversal_type.state.node, detach); break;
		case 'inverse order': finalize_inverse_order(obj.properties.traversal_type.state.node, detach); break;
	}
}
function finalize_no__has_tail__graph_traversal_selector(obj:Cno__has_tail__graph_traversal_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional_graph_traversal>obj.output.traversal)(detach) !== undefined || detach);
}
function finalize_yes__has_tail__graph_traversal_selector(obj:Cyes__has_tail__graph_traversal_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional_graph_traversal>obj.output.traversal)(detach) !== undefined || detach);
	finalize_graph_traversal_selector(obj.properties.tail, detach);
}
function finalize_graph_traversal_selector(obj:Cgraph_traversal_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgraph_traversal>obj.output.traversal)(detach) !== undefined || detach);
	finalize_ancestor_node_path(obj.properties.ancestor_path, detach);
	assert((<(detach?:boolean) => application.Cbase__type__collection>obj.properties.ancestor_path.inferences.base_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.ancestor_path.inferences.collection)(detach) !== undefined || detach);
	finalize_graph_traversal_definition(obj.properties.graph_traversal, detach);
	switch (obj.properties.has_tail.state.name) {
		case 'no': finalize_no__has_tail__graph_traversal_selector(obj.properties.has_tail.state.node, detach); break;
		case 'yes': finalize_yes__has_tail__graph_traversal_selector(obj.properties.has_tail.state.node, detach); break;
	}
}
function finalize_graph_traversal_type(obj:Cgraph_traversal_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgraphs>obj.output.graph)(detach) !== undefined || detach);
	finalize_sibling_dependency_retrieval(obj.definitions.sibling_dependency_retrieval, detach);
}
function finalize_no__has_steps__group_ancestor_node_path(obj:Cno__has_steps__group_ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__group_ancestor_node_path(obj:Cyes__has_steps__group_ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_group_ancestor_node_path(obj.properties.tail, detach);
}
function finalize_group_ancestor_node_path(obj:Cgroup_ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__group_ancestor_node_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__group_ancestor_node_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_identity_initializer(obj:Cidentity_initializer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.identities_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.username)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.identity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.username)(detach) !== undefined || detach);
	finalize_command_initialization_operation(obj.definitions.command_initialization_operation, detach);
	finalize_command_object_expression(obj.properties.initializer, detach);
}
function finalize_interface_instance_initializer(obj:Cinterface_instance_initializer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cmany__instances>obj.input.interface_)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmany__instances>obj.output.interface_)(detach) !== undefined || detach);
	finalize_command_initialization_operation(obj.definitions.command_initialization_operation, detach);
	finalize_command_object_expression(obj.properties.initializer, detach);
}
function finalize_inverse_reference_step(obj:Cinverse_reference_step, detach:boolean = false) {
}
function finalize_explicit__create_permission(obj:Cexplicit__create_permission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.inferences.user_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.source)(detach) !== undefined || detach);
	finalize_permission(obj.properties.permission, detach);
}
function finalize_inherited__create_permission(obj:Cinherited__create_permission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.source)(detach) !== undefined || detach);
}
function finalize_explicit__delete_permission(obj:Cexplicit__delete_permission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.inferences.user_value)(detach) !== undefined || detach);
	finalize_permission(obj.properties.permission, detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.permission.inferences.valid_source)(detach) !== undefined || detach);
}
function finalize_inherited__delete_permission(obj:Cinherited__delete_permission, detach:boolean = false) {
}
function finalize_item_permissions_definition(obj:Citem_permissions_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.item_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.source)(detach) !== undefined || detach);
	switch (obj.properties.create_permission.state.name) {
		case 'explicit': finalize_explicit__create_permission(obj.properties.create_permission.state.node, detach); break;
		case 'inherited': finalize_inherited__create_permission(obj.properties.create_permission.state.node, detach); break;
	}
	switch (obj.properties.delete_permission.state.name) {
		case 'explicit': finalize_explicit__delete_permission(obj.properties.delete_permission.state.node, detach); break;
		case 'inherited': finalize_inherited__delete_permission(obj.properties.delete_permission.state.node, detach); break;
	}
}
function finalize_leaf_value(obj:Cleaf_value, detach:boolean = false) {
	finalize_value_object(obj.definitions.value_object, detach);
}
function finalize_lower_bound_cardinality(obj:Clower_bound_cardinality, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.existence)(detach) !== undefined || detach);
}
function finalize_non_empty(obj:Cnon_empty, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.cardinality)(detach) !== undefined || detach);
}
function finalize_none__constraint(obj:Cnone__constraint, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.cardinality)(detach) !== undefined || detach);
}
function finalize_lower_bound_cardinality_constraint(obj:Clower_bound_cardinality_constraint, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.cardinality)(detach) !== undefined || detach);
	switch (obj.properties.constraint.state.name) {
		case 'non empty': finalize_non_empty(obj.properties.constraint.state.node, detach); break;
		case 'none': finalize_none__constraint(obj.properties.constraint.state.node, detach); break;
	}
}
function finalize_named_object(obj:Cnamed_object, detach:boolean = false) {
}
function finalize_no__has_successor__named_objects__explicit__name__named_object_assignment(obj:Cno__has_successor__named_objects__explicit__name__named_object_assignment, detach:boolean = false) {
}
function finalize_yes__has_successor__named_objects__explicit__name__named_object_assignment(obj:Cyes__has_successor__named_objects__explicit__name__named_object_assignment, detach:boolean = false) {
}
function finalize_named_objects__explicit__name__named_object_assignment(obj:Cnamed_objects__explicit__name__named_object_assignment, detach:boolean = false) {
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__named_objects__explicit__name__named_object_assignment(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__named_objects__explicit__name__named_object_assignment(obj.properties.has_successor.state.node, detach); break;
	}
}
function finalize_explicit__name__named_object_assignment(obj:Cexplicit__name__named_object_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_objects__explicit__name__named_object_assignment>(obj.properties.name as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cno__has_successor__named_objects__explicit__name__named_object_assignment>obj.properties.name.inferences.single_name)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.named_objects) {
		finalize_named_objects__explicit__name__named_object_assignment(entry, detach);
	}
	if (!detach) {
		if (obj.properties.named_objects.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_implicit__name__named_object_assignment(obj:Cimplicit__name__named_object_assignment, detach:boolean = false) {
}
function finalize_named_object_assignment(obj:Cnamed_object_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object_definer>obj.input.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.parent_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.parent_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
	finalize_named_object(obj.definitions.named_object, detach);
	switch (obj.properties.name.state.name) {
		case 'explicit': finalize_explicit__name__named_object_assignment(obj.properties.name.state.node, detach); break;
		case 'implicit': finalize_implicit__name__named_object_assignment(obj.properties.name.state.node, detach); break;
	}
}
function finalize_named_object_definer(obj:Cnamed_object_definer, detach:boolean = false) {
}
function finalize_explicit__name__named_object_step(obj:Cexplicit__name__named_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object_assignment>obj.output.assignment)(detach) !== undefined || detach);
	finalize_ancestor_named_object_path(obj.properties.head, detach);
	assert((<(detach?:boolean) => application.Cnamed_object_assignment>obj.properties.head.inferences.assignment)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexplicit__name__named_object_assignment>obj.properties.head.inferences.explicit_name)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_objects__explicit__name__named_object_assignment>(obj.properties.named_object as any).resolve)(detach) !== undefined || detach);
}
function finalize_implicit__name__named_object_step(obj:Cimplicit__name__named_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object_assignment>obj.inferences.assignment)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cimplicit__name__named_object_assignment>obj.inferences.implicit_name)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object_assignment>obj.output.assignment)(detach) !== undefined || detach);
}
function finalize_named_object_step(obj:Cnamed_object_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object_assignment>obj.output.assignment)(detach) !== undefined || detach);
	switch (obj.properties.name.state.name) {
		case 'explicit': finalize_explicit__name__named_object_step(obj.properties.name.state.node, detach); break;
		case 'implicit': finalize_implicit__name__named_object_step(obj.properties.name.state.node, detach); break;
	}
}
function finalize_navigation_context(obj:Cnavigation_context, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.input.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_dependence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_node_navigation_context>obj.output.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
}
function finalize_navigation_support(obj:Cnavigation_support, detach:boolean = false) {
}
function finalize_no__has_predecessor__attributes(obj:Cno__has_predecessor__attributes, detach:boolean = false) {
}
function finalize_yes__has_predecessor__attributes(obj:Cyes__has_predecessor__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_successor__attributes(obj:Cno__has_successor__attributes, detach:boolean = false) {
}
function finalize_yes__has_successor__attributes(obj:Cyes__has_successor__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
}
function finalize_action(obj:Caction, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattribute_type>obj.output.attribute_type)(detach) !== undefined || detach);
	finalize_operation(obj.definitions.operation, detach);
	finalize_ui_action(obj.properties.action, detach);
	finalize_node__application(obj.properties.parameters, detach);
	finalize_ui_action_attribute(obj.properties.ui, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_external(obj:Cexternal, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cimported_interfaces>obj.inferences.imported_interface_source)(detach) !== undefined || detach);
}
function finalize_internal__implementation(obj:Cinternal__implementation, detach:boolean = false) {
	finalize_command__application(obj.properties.implementation, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_component(obj:Ccomponent, detach:boolean = false) {
}
function finalize_global(obj:Cglobal, detach:boolean = false) {
	finalize_ui_command_attribute(obj.properties.ui, detach);
}
function finalize_command__type(obj:Ccommand__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattribute_type>obj.output.attribute_type)(detach) !== undefined || detach);
	finalize_operation(obj.definitions.operation, detach);
	switch (obj.properties.implementation.state.name) {
		case 'external': finalize_external(obj.properties.implementation.state.node, detach); break;
		case 'internal': finalize_internal__implementation(obj.properties.implementation.state.node, detach); break;
	}
	finalize_node__application(obj.properties.parameters, detach);
	finalize_command_permission_definition(obj.properties.permission_definition, detach);
	switch (obj.properties.type.state.name) {
		case 'component': finalize_component(obj.properties.type.state.node, detach); break;
		case 'global': finalize_global(obj.properties.type.state.node, detach); break;
	}
}
function finalize_base__type__collection(obj:Cbase__type__collection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.inferences.key_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.parent_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.entry_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.output.key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_branches__no(obj:Cbranches__no, detach:boolean = false) {
	finalize_flatten_expression(obj.properties.expression, detach);
	finalize_node_initializer(obj.properties.node_initializer, detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.node_initializer.inferences.computation_phase)(detach) !== undefined || detach);
	finalize_optional_named_object_assignment(obj.properties.variable_assignment, detach);
}
function finalize_colon(obj:Ccolon, detach:boolean = false) {
}
function finalize_dash(obj:Cdash, detach:boolean = false) {
}
function finalize_dot(obj:Cdot, detach:boolean = false) {
}
function finalize_greater_than(obj:Cgreater_than, detach:boolean = false) {
}
function finalize_space(obj:Cspace, detach:boolean = false) {
}
function finalize_no__key_constraint(obj:Cno__key_constraint, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cno__has_reference>obj.inferences.no_key_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cderived_value_source>obj.output.entry_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.output.key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	finalize_node_initializing_expression(obj.definitions.node_initializing_expression, detach);
	for (const [_key, entry] of obj.properties.branches) {
		finalize_branches__no(entry, detach);
	}
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.properties.branches.inferences.combined_lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.branches.inferences.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.properties.branches.inferences.lower_bound_cardinality)(detach) !== undefined || detach);
	if (!detach) {
		if (obj.properties.branches.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
	switch (obj.properties.separator.state.name) {
		case 'colon': finalize_colon(obj.properties.separator.state.node, detach); break;
		case 'dash': finalize_dash(obj.properties.separator.state.node, detach); break;
		case 'dot': finalize_dot(obj.properties.separator.state.node, detach); break;
		case 'greater than': finalize_greater_than(obj.properties.separator.state.node, detach); break;
		case 'space': finalize_space(obj.properties.separator.state.node, detach); break;
	}
}
function finalize_yes__key_constraint(obj:Cyes__key_constraint, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.inferences.key_constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.key_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cderived_value_source>obj.output.entry_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.output.key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	finalize_plural_reference_expression(obj.properties.expression, detach);
}
function finalize_derived__type__collection(obj:Cderived__type__collection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.entry_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.output.key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.key_constraint.state.name) {
		case 'no': finalize_no__key_constraint(obj.properties.key_constraint.state.node, detach); break;
		case 'yes': finalize_yes__key_constraint(obj.properties.key_constraint.state.node, detach); break;
	}
	finalize_recursion_annotation(obj.properties.recursion, detach);
}
function finalize_collection__type__property(obj:Ccollection__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_attribute_type(obj.definitions.attribute_type, detach);
	finalize_collection_attribute(obj.definitions.collection_attribute, detach);
	finalize_entity__application(obj.definitions.entity, detach);
	finalize_node_location(obj.definitions.node_location, detach);
	finalize_lower_bound_cardinality_constraint(obj.properties.cardinality_constraint, detach);
	finalize_graph_constraints_definition(obj.properties.graph_constraints, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.key_property as any).resolve)(detach) !== undefined || detach);
	finalize_node__application(obj.properties.node, detach);
	finalize_item_permissions_definition(obj.properties.permissions, detach);
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__collection(obj.properties.type.state.node, detach); break;
		case 'derived': finalize_derived__type__collection(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_collection_property(obj.properties.ui, detach);
}
function finalize_base__type__file(obj:Cbase__type__file, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.parent_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_expression__value_source__derived__type__file(obj:Cexpression__value_source__derived__type__file, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_recursion_annotation(obj.properties.recursion, detach);
}
function finalize_parameter__value_source__derived__type__file(obj:Cparameter__value_source__derived__type__file, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_initializing_expression>obj.inferences.initializer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_derived__type__file(obj:Cderived__type__file, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.value_source.state.name) {
		case 'expression': finalize_expression__value_source__derived__type__file(obj.properties.value_source.state.node, detach); break;
		case 'parameter': finalize_parameter__value_source__derived__type__file(obj.properties.value_source.state.node, detach); break;
	}
}
function finalize_file(obj:Cfile, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_leaf_value(obj.definitions.leaf_value, detach);
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__file(obj.properties.type.state.node, detach); break;
		case 'derived': finalize_derived__type__file(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_file_property(obj.properties.ui, detach);
}
function finalize_base__type__group(obj:Cbase__type__group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.parent_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_derived__type__group(obj:Cderived__type__group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_group__type__property(obj:Cgroup__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_node_location(obj.definitions.node_location, detach);
	finalize_node__application(obj.properties.node, detach);
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__group(obj.properties.type.state.node, detach); break;
		case 'derived': finalize_derived__type__group(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_group_property(obj.properties.ui, detach);
}
function finalize_none__behaviour(obj:Cnone__behaviour, detach:boolean = false) {
}
function finalize_timer(obj:Ctimer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cseconds__timer_resolution>obj.inferences.timer_resolution)(detach) !== undefined || detach);
	finalize_command__application(obj.properties.implementation, detach);
}
function finalize_integer__set_type(obj:Cinteger__set_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
}
function finalize_positive__set_type(obj:Cpositive__set_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
}
function finalize_creation(obj:Ccreation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnone__behaviour>obj.inferences.no_behaviour)(detach) !== undefined || detach);
}
function finalize_add_lifetime(obj:Cadd_lifetime, detach:boolean = false) {
}
function finalize_set_to_lifetime(obj:Cset_to_lifetime, detach:boolean = false) {
}
function finalize_subtract_lifetime(obj:Csubtract_lifetime, detach:boolean = false) {
}
function finalize_destruction(obj:Cdestruction, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer)(detach) !== undefined || detach);
	switch (obj.properties.destruction_operation.state.name) {
		case 'add lifetime': finalize_add_lifetime(obj.properties.destruction_operation.state.node, detach); break;
		case 'set to lifetime': finalize_set_to_lifetime(obj.properties.destruction_operation.state.node, detach); break;
		case 'subtract lifetime': finalize_subtract_lifetime(obj.properties.destruction_operation.state.node, detach); break;
	}
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.watched_state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.properties.watched_state.inferences.meta_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__record_lifetime>obj.properties.watched_state.inferences.watched_state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.watched_stategroup as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.watched_stategroup.inferences.stategroup)(detach) !== undefined || detach);
}
function finalize_mutation(obj:Cmutation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnone__behaviour>obj.inferences.no_behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.watched_property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.properties.watched_property.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cleaf_value>obj.properties.watched_property.inferences.scalar_value)(detach) !== undefined || detach);
}
function finalize_causal(obj:Ccausal, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnone__behaviour>obj.inferences.no_behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'creation': finalize_creation(obj.properties.type.state.node, detach); break;
		case 'destruction': finalize_destruction(obj.properties.type.state.node, detach); break;
		case 'mutation': finalize_mutation(obj.properties.type.state.node, detach); break;
	}
}
function finalize_simple__type__base(obj:Csimple__type__base, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_base__type__number(obj:Cbase__type__number, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.parent_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'causal': finalize_causal(obj.properties.type.state.node, detach); break;
		case 'simple': finalize_simple__type__base(obj.properties.type.state.node, detach); break;
	}
}
function finalize_expression__value_source__derived__type__number(obj:Cexpression__value_source__derived__type__number, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_recursion_annotation(obj.properties.recursion, detach);
}
function finalize_parameter__value_source__derived__type__number(obj:Cparameter__value_source__derived__type__number, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_initializing_expression>obj.inferences.initializer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_derived__type__number(obj:Cderived__type__number, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.value_source.state.name) {
		case 'expression': finalize_expression__value_source__derived__type__number(obj.properties.value_source.state.node, detach); break;
		case 'parameter': finalize_parameter__value_source__derived__type__number(obj.properties.value_source.state.node, detach); break;
	}
}
function finalize_number__type__property(obj:Cnumber__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_number_value(obj.definitions.number_value, detach);
	switch (obj.properties.behaviour.state.name) {
		case 'none': finalize_none__behaviour(obj.properties.behaviour.state.node, detach); break;
		case 'timer': finalize_timer(obj.properties.behaviour.state.node, detach); break;
	}
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.properties.numerical_type as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.set_type.state.name) {
		case 'integer': finalize_integer__set_type(obj.properties.set_type.state.node, detach); break;
		case 'positive': finalize_positive__set_type(obj.properties.set_type.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__number(obj.properties.type.state.node, detach); break;
		case 'derived': finalize_derived__type__number(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_number_property(obj.properties.ui, detach);
}
function finalize_no__has_successor__states__state_group(obj:Cno__has_successor__states__state_group, detach:boolean = false) {
}
function finalize_yes__has_successor__states__state_group(obj:Cyes__has_successor__states__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.successor as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__record_lifetime(obj:Cno__record_lifetime, detach:boolean = false) {
}
function finalize_yes__record_lifetime(obj:Cyes__record_lifetime, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__state_group>obj.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>(obj.properties.creation_timestamp as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccreation>obj.properties.creation_timestamp.inferences.creation_timestamp)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>(obj.properties.meta_property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdestruction>obj.properties.meta_property.inferences.meta_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.properties.meta_property.inferences.watches_this_state)(detach) !== undefined || detach);
}
function finalize_states__state_group(obj:Cstates__state_group, detach:boolean = false) {
	finalize_node_location(obj.definitions.node_location, detach);
	finalize_where_rules_definer(obj.definitions.where_rules_definer, detach);
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__states__state_group(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__states__state_group(obj.properties.has_successor.state.node, detach); break;
	}
	finalize_node__application(obj.properties.node, detach);
	finalize_item_permissions_definition(obj.properties.permissions, detach);
	switch (obj.properties.record_lifetime.state.name) {
		case 'no': finalize_no__record_lifetime(obj.properties.record_lifetime.state.node, detach); break;
		case 'yes': finalize_yes__record_lifetime(obj.properties.record_lifetime.state.node, detach); break;
	}
	finalize_where_rules_definition(obj.properties.rules, detach);
	finalize_ui_state(obj.properties.ui, detach);
}
function finalize_base__type__state_group(obj:Cbase__type__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_source>obj.inferences.node_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.parent_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.state_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_expression__value_source__derived__type__state_group(obj:Cexpression__value_source__derived__type__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_recursion_annotation(obj.properties.recursion, detach);
}
function finalize_parameter__value_source__derived__type__state_group(obj:Cparameter__value_source__derived__type__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_initializing_expression>obj.inferences.initializer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_derived__type__state_group(obj:Cderived__type__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.state_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_node_initializing_expression(obj.definitions.node_initializing_expression, detach);
	switch (obj.properties.value_source.state.name) {
		case 'expression': finalize_expression__value_source__derived__type__state_group(obj.properties.value_source.state.node, detach); break;
		case 'parameter': finalize_parameter__value_source__derived__type__state_group(obj.properties.value_source.state.node, detach); break;
	}
}
function finalize_state_group(obj:Cstate_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_scalar_value(obj.definitions.scalar_value, detach);
	finalize_value_object(obj.definitions.value_object, detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.first_state as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_group(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__state_group(obj.properties.type.state.node, detach); break;
		case 'derived': finalize_derived__type__state_group(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_state_group_property(obj.properties.ui, detach);
}
function finalize_no__has_reference(obj:Cno__has_reference, detach:boolean = false) {
}
function finalize_none__behaviour_specialization(obj:Cnone__behaviour_specialization, detach:boolean = false) {
}
function finalize_propagate_key_to_value(obj:Cpropagate_key_to_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_reference)(detach) !== undefined || detach);
}
function finalize_yes__has_reference(obj:Cyes__has_reference, detach:boolean = false) {
	finalize_entry_reference_definer(obj.definitions.entry_reference_definer, detach);
	finalize_reference_definer(obj.definitions.reference_definer, detach);
	finalize_where_rules_definer(obj.definitions.where_rules_definer, detach);
	finalize_optional_named_object_assignment(obj.properties.assignment, detach);
	finalize_reference_behaviour(obj.properties.behaviour, detach);
	switch (obj.properties.behaviour_specialization.state.name) {
		case 'none': finalize_none__behaviour_specialization(obj.properties.behaviour_specialization.state.node, detach); break;
		case 'propagate key to value': finalize_propagate_key_to_value(obj.properties.behaviour_specialization.state.node, detach); break;
	}
	finalize_direction_annotation(obj.properties.direction, detach);
	finalize_entry_reference_definition(obj.properties.expression, detach);
	finalize_recursion_annotation(obj.properties.recursion, detach);
	finalize_where_rules_definition(obj.properties.rules, detach);
	finalize_ui_reference_rule(obj.properties.ui, detach);
}
function finalize_base__type__text(obj:Cbase__type__text, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.parent_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_derived_key(obj:Cderived_key, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cderived__type__collection>obj.inferences.derived_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.is_key_member)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_expression__value_source__derived__type__text(obj:Cexpression__value_source__derived__type__text, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Csimple__attribute_type>obj.inferences.is_simple_member)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
	finalize_recursion_annotation(obj.properties.recursion, detach);
}
function finalize_parameter__value_source__derived__type__text(obj:Cparameter__value_source__derived__type__text, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_initializing_expression>obj.inferences.initializer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_derived__type__text(obj:Cderived__type__text, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.value_source.state.name) {
		case 'derived key': finalize_derived_key(obj.properties.value_source.state.node, detach); break;
		case 'expression': finalize_expression__value_source__derived__type__text(obj.properties.value_source.state.node, detach); break;
		case 'parameter': finalize_parameter__value_source__derived__type__text(obj.properties.value_source.state.node, detach); break;
	}
}
function finalize_text__type__property(obj:Ctext__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_object>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.value_dependency_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_scalar_value(obj.definitions.scalar_value, detach);
	switch (obj.properties.has_reference.state.name) {
		case 'no': finalize_no__has_reference(obj.properties.has_reference.state.node, detach); break;
		case 'yes': finalize_yes__has_reference(obj.properties.has_reference.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__text(obj.properties.type.state.node, detach); break;
		case 'derived': finalize_derived__type__text(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_text_property(obj.properties.ui, detach);
}
function finalize_property__type__attributes(obj:Cproperty__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattribute_type>obj.output.attribute_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__property(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__property(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_context(obj:Ccontext, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
}
function finalize_sibling__root(obj:Csibling__root, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupstream__direction__application>obj.inferences.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.head_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	finalize_sibling_reference_definition(obj.properties.definition, detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.definition.inferences.relative_location_of_referenced_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cderived_set>obj.properties.definition.inferences.sibling_subset)(detach) !== undefined || detach);
}
function finalize_referenced_type_path(obj:Creferenced_type_path, detach:boolean = false) {
	finalize_object_path_tail(obj.properties.head, detach);
	finalize_plural_object_path_tail(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.properties.path.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.path.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.path.inferences.subset_dependency)(detach) !== undefined || detach);
	switch (obj.properties.root.state.name) {
		case 'context': finalize_context(obj.properties.root.state.node, detach); break;
		case 'sibling': finalize_sibling__root(obj.properties.root.state.node, detach); break;
	}
}
function finalize_reference__value_source(obj:Creference__value_source, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection_step_for_inversion)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.reference_definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_dependency_step(obj.properties.reference, detach);
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.properties.reference.inferences.entry_step)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.reference.inferences.inverse_relative_node_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.reference.inferences.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.reference.inferences.relative_location_of_referenced_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.properties.reference.inferences.root_dependency)(detach) !== undefined || detach);
}
function finalize_union_branch(obj:Cunion_branch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__key_constraint>obj.inferences.derived_parent_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.parent_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cunion>obj.inferences.union)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.reference_definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__source>(obj.properties.branch as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.branch.inferences.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.properties.branch.inferences.local_key_iubc)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.branch.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.properties.branch.inferences.upper_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_reference_set__type__attributes(obj:Creference_set__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattribute_type>obj.output.attribute_type)(detach) !== undefined || detach);
	finalize_collection_attribute(obj.definitions.collection_attribute, detach);
	finalize_direction_annotation(obj.properties.direction, detach);
	finalize_recursion_annotation(obj.properties.recursion, detach);
	finalize_referenced_type_path(obj.properties.referenced_type_path, detach);
	finalize_ui_reference_set_attribute(obj.properties.ui, detach);
	switch (obj.properties.value_source.state.name) {
		case 'reference': finalize_reference__value_source(obj.properties.value_source.state.node, detach); break;
		case 'union branch': finalize_union_branch(obj.properties.value_source.state.node, detach); break;
	}
}
function finalize_attributes(obj:Cattributes, detach:boolean = false) {
	finalize_node_parent_attribute(obj.definitions.node_parent_attribute, detach);
	switch (obj.properties.has_predecessor.state.name) {
		case 'no': finalize_no__has_predecessor__attributes(obj.properties.has_predecessor.state.node, detach); break;
		case 'yes': finalize_yes__has_predecessor__attributes(obj.properties.has_predecessor.state.node, detach); break;
	}
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__attributes(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__attributes(obj.properties.has_successor.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'action': finalize_action(obj.properties.type.state.node, detach); break;
		case 'command': finalize_command__type(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'reference set': finalize_reference_set__type__attributes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_attributes(obj:Cno__has_attributes, detach:boolean = false) {
}
function finalize_yes__has_attributes(obj:Cyes__has_attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.last as any).resolve)(detach) !== undefined || detach);
}
function finalize_node__application(obj:Cnode__application, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.input.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_node_parent(obj.definitions.node_parent, detach);
	finalize_value_object(obj.definitions.value_object, detach);
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.has_attributes.state.name) {
		case 'no': finalize_no__has_attributes(obj.properties.has_attributes.state.node, detach); break;
		case 'yes': finalize_yes__has_attributes(obj.properties.has_attributes.state.node, detach); break;
	}
	finalize_node_permissions_definition(obj.properties.permissions_definition, detach);
	finalize_todo_definition(obj.properties.todo_definition, detach);
}
function finalize_key_path(obj:Ckey_path, detach:boolean = false) {
	finalize_conditional_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_containment__type__node_expression(obj:Ccontainment__type__node_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_key_path(obj.properties.key_path, detach);
}
function finalize_equality__type__node_expression(obj:Cequality__type__node_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_conditional_node_path(obj.properties.expected_node_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.expected_node_path.inferences.expected_node_type)(detach) !== undefined || detach);
}
function finalize_existence__type__node_expression(obj:Cexistence__type__node_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_node_expression(obj:Cnode_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_conditional_node_path(obj.properties.node_path, detach);
	switch (obj.properties.type.state.name) {
		case 'containment': finalize_containment__type__node_expression(obj.properties.type.state.node, detach); break;
		case 'equality': finalize_equality__type__node_expression(obj.properties.type.state.node, detach); break;
		case 'existence': finalize_existence__type__node_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_arguments(obj:Carguments, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_initializing_expression>obj.key.inferences.parameter)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
}
function finalize_node_initializer(obj:Cnode_initializer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.arguments) {
		finalize_arguments(entry, detach);
	}
	if (!detach) {
		(obj.properties.arguments as any).eval_required_keys(detach);
	}
}
function finalize_node_initializing_expression(obj:Cnode_initializing_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derived_value_source(obj.definitions.derived_value_source, detach);
}
function finalize_node_location(obj:Cnode_location, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_parent_attribute>obj.input.attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_parent>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.root)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_parent_attribute>obj.output.attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_parent>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.output.root)(detach) !== undefined || detach);
}
function finalize_node_navigation_context(obj:Cnode_navigation_context, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.input.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_dependence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
	finalize_optional_node_navigation_context(obj.definitions.optional_node_navigation_context, detach);
}
function finalize_node_parent(obj:Cnode_parent, detach:boolean = false) {
}
function finalize_node_parent_attribute(obj:Cnode_parent_attribute, detach:boolean = false) {
}
function finalize_node_path(obj:Cnode_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_context_node_path(obj.properties.head, detach);
	finalize_node_path_tail(obj.properties.tail, detach);
}
function finalize_no__has_steps__node_path_tail(obj:Cno__has_steps__node_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__node_path_tail(obj:Cyes__has_steps__node_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_node_step__application(obj.properties.step, detach);
	finalize_node_path_tail(obj.properties.tail, detach);
}
function finalize_node_path_tail(obj:Cnode_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.reference_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__node_path_tail(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__node_path_tail(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_explicit__read_permission(obj:Cexplicit__read_permission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_node_type)(detach) !== undefined || detach);
	finalize_permission(obj.properties.permission, detach);
}
function finalize_inherited__read_permission(obj:Cinherited__read_permission, detach:boolean = false) {
}
function finalize_explicit__update_permission(obj:Cexplicit__update_permission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.inferences.user_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_permission(obj.properties.permission, detach);
}
function finalize_inherited__update_permission(obj:Cinherited__update_permission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_node_permissions_definition(obj:Cnode_permissions_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.read_permission.state.name) {
		case 'explicit': finalize_explicit__read_permission(obj.properties.read_permission.state.node, detach); break;
		case 'inherited': finalize_inherited__read_permission(obj.properties.read_permission.state.node, detach); break;
	}
	switch (obj.properties.update_permission.state.name) {
		case 'explicit': finalize_explicit__update_permission(obj.properties.update_permission.state.node, detach); break;
		case 'inherited': finalize_inherited__update_permission(obj.properties.update_permission.state.node, detach); break;
	}
}
function finalize_group__type__node_step(obj:Cgroup__type__node_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_parent__type__node_step(obj:Cparent__type__node_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.inferences.context_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.inferences.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.parent_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
}
function finalize_property_rule(obj:Cproperty_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cunbounded__navigation_support>obj.inferences.navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_reference__type__node_step(obj:Creference__type__node_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.text.inferences.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_state__type__node_step(obj:Cstate__type__node_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_context_rule__type__node_step(obj:Cstate_context_rule__type__node_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cunbounded__navigation_support>obj.inferences.navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.existence_support)(detach) !== undefined || detach);
}
function finalize_node_step__application(obj:Cnode_step__application, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.reference_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.output.parent_navigation_support)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__node_step(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent__type__node_step(obj.properties.type.state.node, detach); break;
		case 'property rule': finalize_property_rule(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference__type__node_step(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__node_step(obj.properties.type.state.node, detach); break;
		case 'state context rule': finalize_state_context_rule__type__node_step(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_type_id(obj:Cnode_type_id, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_node_type_id_path(obj.properties.steps, detach);
}
function finalize_node_type_id_context(obj:Cnode_type_id_context, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_no__has_steps__node_type_id_path(obj:Cno__has_steps__node_type_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_type_id_context>obj.output.context)(detach) !== undefined || detach);
}
function finalize_choice__value_type__yes__has_steps__node_type_id_path(obj:Cchoice__value_type__yes__has_steps__node_type_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_collection__value_type__yes__has_steps__node_type_id_path(obj:Ccollection__value_type__yes__has_steps__node_type_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_node__value_type__yes__has_steps__node_type_id_path(obj:Cnode__value_type__yes__has_steps__node_type_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__node_type_id_path(obj:Cyes__has_steps__node_type_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_type_id_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	finalize_node_type_id_path(obj.properties.tail, detach);
	switch (obj.properties.value_type.state.name) {
		case 'choice': finalize_choice__value_type__yes__has_steps__node_type_id_path(obj.properties.value_type.state.node, detach); break;
		case 'collection': finalize_collection__value_type__yes__has_steps__node_type_id_path(obj.properties.value_type.state.node, detach); break;
		case 'node': finalize_node__value_type__yes__has_steps__node_type_id_path(obj.properties.value_type.state.node, detach); break;
	}
}
function finalize_node_type_id_path(obj:Cnode_type_id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_type_id_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_type_id_context>obj.output.context)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__node_type_id_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__node_type_id_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_number__application(obj:Cnumber__application, detach:boolean = false) {
	finalize_number_value(obj.definitions.number_value, detach);
}
function finalize_number_comparison_case(obj:Cnumber_comparison_case, detach:boolean = false) {
}
function finalize_addition(obj:Caddition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cscale>obj.inferences.relative_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_unary_number_expression(obj.properties.left, detach);
	finalize_unary_number_expression(obj.properties.right, detach);
}
function finalize_count(obj:Ccount, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_object_set_path(obj.properties.nodes_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.nodes_path.inferences.node)(detach) !== undefined || detach);
}
function finalize_maximum__operation__property(obj:Cmaximum__operation__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.required_cardinality)(detach) !== undefined || detach);
}
function finalize_minimum__operation__property(obj:Cminimum__operation__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.required_cardinality)(detach) !== undefined || detach);
}
function finalize_standard_deviation(obj:Cstandard_deviation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.expected_set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.required_cardinality)(detach) !== undefined || detach);
}
function finalize_sum__operation__property(obj:Csum__operation__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.required_cardinality)(detach) !== undefined || detach);
}
function finalize_property__type__collection_operation(obj:Cproperty__type__collection_operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_object_set_path(obj.properties.numbers_path, detach);
	assert((<(detach?:boolean) => application.Cnumber_value>obj.properties.numbers_path.inferences.number)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_type_requirement>obj.properties.numbers_path.inferences.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.numbers_path.inferences.set_type)(detach) !== undefined || detach);
	switch (obj.properties.operation.state.name) {
		case 'maximum': finalize_maximum__operation__property(obj.properties.operation.state.node, detach); break;
		case 'minimum': finalize_minimum__operation__property(obj.properties.operation.state.node, detach); break;
		case 'standard deviation': finalize_standard_deviation(obj.properties.operation.state.node, detach); break;
		case 'sum': finalize_sum__operation__property(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_collection_operation(obj:Ccollection_operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'count': finalize_count(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__collection_operation(obj.properties.type.state.node, detach); break;
	}
}
function finalize_expressions__difference(obj:Cexpressions__difference, detach:boolean = false) {
	finalize_unary_number_expression(obj.properties.left, detach);
	finalize_unary_number_expression(obj.properties.right, detach);
}
function finalize_difference(obj:Cdifference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_expressions__difference(obj.properties.expressions, detach);
	assert((<(detach?:boolean) => application.Cscale>(obj.properties.scale_type as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.properties.scale_type.inferences.range_type)(detach) !== undefined || detach);
}
function finalize_expressions__division(obj:Cexpressions__division, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdivision_conversions>(obj.properties.conversion_rule as any).resolve)(detach) !== undefined || detach);
	finalize_unary_number_expression(obj.properties.denominator, detach);
	finalize_unary_number_expression(obj.properties.numerator, detach);
}
function finalize_ceil__rounding__division__type(obj:Cceil__rounding__division__type, detach:boolean = false) {
}
function finalize_floor__rounding__division__type(obj:Cfloor__rounding__division__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
}
function finalize_ordinary__rounding__division__type(obj:Cordinary__rounding__division__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
}
function finalize_division__type(obj:Cdivision__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_expressions__division(obj.properties.expressions, detach);
	switch (obj.properties.rounding.state.name) {
		case 'ceil': finalize_ceil__rounding__division__type(obj.properties.rounding.state.node, detach); break;
		case 'floor': finalize_floor__rounding__division__type(obj.properties.rounding.state.node, detach); break;
		case 'ordinary': finalize_ordinary__rounding__division__type(obj.properties.rounding.state.node, detach); break;
	}
}
function finalize_maximum__operation__list_operation(obj:Cmaximum__operation__list_operation, detach:boolean = false) {
}
function finalize_minimum__operation__list_operation(obj:Cminimum__operation__list_operation, detach:boolean = false) {
}
function finalize_sum__operation__list_operation(obj:Csum__operation__list_operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
}
function finalize_list_operation(obj:Clist_operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression_list(obj.properties.numbers, detach);
	switch (obj.properties.operation.state.name) {
		case 'maximum': finalize_maximum__operation__list_operation(obj.properties.operation.state.node, detach); break;
		case 'minimum': finalize_minimum__operation__list_operation(obj.properties.operation.state.node, detach); break;
		case 'sum': finalize_sum__operation__list_operation(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_product__type(obj:Cproduct__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproduct_conversions>(obj.properties.conversion_rule as any).resolve)(detach) !== undefined || detach);
	finalize_unary_number_expression(obj.properties.left, detach);
	finalize_unary_number_expression(obj.properties.right, detach);
}
function finalize_remainder__type(obj:Cremainder__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_unary_number_expression(obj.properties.denominator, detach);
	finalize_unary_number_expression(obj.properties.numerator, detach);
}
function finalize_aggregate(obj:Caggregate, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cunrestricted>obj.inferences.no_nested_aggregation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.inferences.target_numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'addition': finalize_addition(obj.properties.type.state.node, detach); break;
		case 'collection operation': finalize_collection_operation(obj.properties.type.state.node, detach); break;
		case 'difference': finalize_difference(obj.properties.type.state.node, detach); break;
		case 'division': finalize_division__type(obj.properties.type.state.node, detach); break;
		case 'list operation': finalize_list_operation(obj.properties.type.state.node, detach); break;
		case 'product': finalize_product__type(obj.properties.type.state.node, detach); break;
		case 'remainder': finalize_remainder__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_constant__type__number_expression(obj:Cconstant__type__number_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_constant_number_value(obj.properties.value, detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.value.inferences.set_type)(detach) !== undefined || detach);
}
function finalize_absolute_value__type__unary(obj:Cabsolute_value__type__unary, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_type_requirement>obj.output.numerical_type)(detach) !== undefined || detach);
}
function finalize_numerical_type_conversion__type__unary(obj:Cnumerical_type_conversion__type__unary, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.inferences.known_target_numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_type_requirement>obj.output.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csingular_conversions>(obj.properties.conversion as any).resolve)(detach) !== undefined || detach);
}
function finalize_sign_inversion__type__unary(obj:Csign_inversion__type__unary, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.inferences.predefined_numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_type_requirement>obj.output.numerical_type)(detach) !== undefined || detach);
}
function finalize_unary__type(obj:Cunary__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression_tail(obj.properties.expression, detach);
	switch (obj.properties.type.state.name) {
		case 'absolute value': finalize_absolute_value__type__unary(obj.properties.type.state.node, detach); break;
		case 'numerical type conversion': finalize_numerical_type_conversion__type__unary(obj.properties.type.state.node, detach); break;
		case 'sign inversion': finalize_sign_inversion__type__unary(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number_expression(obj:Cnumber_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cderivation_expression_support>obj.input.support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_value>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'aggregate': finalize_aggregate(obj.properties.type.state.node, detach); break;
		case 'constant': finalize_constant__type__number_expression(obj.properties.type.state.node, detach); break;
		case 'unary': finalize_unary__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number_set_type(obj:Cnumber_set_type, detach:boolean = false) {
}
function finalize_number_value(obj:Cnumber_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_type_requirement>obj.input.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.input.set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_type_requirement>obj.output.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
	finalize_scalar_value(obj.definitions.scalar_value, detach);
}
function finalize_numerical_type_requirement(obj:Cnumerical_type_requirement, detach:boolean = false) {
}
function finalize_no__has_steps__object_path_tail(obj:Cno__has_steps__object_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
}
function finalize_dependency__type(obj:Cdependency__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cunbounded__navigation_support>obj.inferences.reference_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_dependency_step(obj.properties.dependency, detach);
}
function finalize_parent__type__yes(obj:Cparent__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.inferences.context_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.inferences.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.inferences.parent_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.inferences.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
}
function finalize_property_value(obj:Cproperty_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_property_step(obj.properties.property, detach);
}
function finalize_reference_set__type__yes(obj:Creference_set__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cunbounded__navigation_support>obj.inferences.reference_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_reference_set_step(obj.properties.step, detach);
}
function finalize_state__type__yes(obj:Cstate__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__object_path_tail(obj:Cyes__has_steps__object_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_object_path_tail(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'dependency': finalize_dependency__type(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent__type__yes(obj.properties.type.state.node, detach); break;
		case 'property value': finalize_property_value(obj.properties.type.state.node, detach); break;
		case 'reference set': finalize_reference_set__type__yes(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_object_path_tail(obj:Cobject_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.reference_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__object_path_tail(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__object_path_tail(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_object_set_path(obj:Cobject_set_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection_object>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.object_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	finalize_singular_variablized_object_path(obj.properties.collection_path, detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.properties.collection_path.inferences.cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection_object>obj.properties.collection_path.inferences.collection)(detach) !== undefined || detach);
	finalize_object_path_tail(obj.properties.value_path, detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.properties.value_path.inferences.lower_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_operation(obj:Coperation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>obj.output.attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.root_node_type)(detach) !== undefined || detach);
	finalize_root_location(obj.definitions.root_location, detach);
}
function finalize_optional_command_operation(obj:Coptional_command_operation, detach:boolean = false) {
}
function finalize_optional_dependency_gen(obj:Coptional_dependency_gen, detach:boolean = false) {
}
function finalize_optional_graph_traversal(obj:Coptional_graph_traversal, detach:boolean = false) {
}
function finalize_no__has_assignment__optional_named_object_assignment(obj:Cno__has_assignment__optional_named_object_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
}
function finalize_yes__has_assignment__optional_named_object_assignment(obj:Cyes__has_assignment__optional_named_object_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
	finalize_named_object_definer(obj.definitions.named_object_definer, detach);
	finalize_named_object_assignment(obj.properties.assignment, detach);
}
function finalize_optional_named_object_assignment(obj:Coptional_named_object_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.parent_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.output.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_assignment.state.name) {
		case 'no': finalize_no__has_assignment__optional_named_object_assignment(obj.properties.has_assignment.state.node, detach); break;
		case 'yes': finalize_yes__has_assignment__optional_named_object_assignment(obj.properties.has_assignment.state.node, detach); break;
	}
}
function finalize_optional_node_navigation_context(obj:Coptional_node_navigation_context, detach:boolean = false) {
}
function finalize_no__has_assignment__optional_variable_assignment(obj:Cno__has_assignment__optional_variable_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
}
function finalize_yes__has_assignment__optional_variable_assignment(obj:Cyes__has_assignment__optional_variable_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
	finalize_variable_assignment(obj.properties.assignment, detach);
}
function finalize_optional_variable_assignment(obj:Coptional_variable_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.parent_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_assignment.state.name) {
		case 'no': finalize_no__has_assignment__optional_variable_assignment(obj.properties.has_assignment.state.node, detach); break;
		case 'yes': finalize_yes__has_assignment__optional_variable_assignment(obj.properties.has_assignment.state.node, detach); break;
	}
}
function finalize_password_initializer(obj:Cpassword_initializer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.password_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.password_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.password_status)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.password)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.password_status)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.username)(detach) !== undefined || detach);
	finalize_command_initialization_operation(obj.definitions.command_initialization_operation, detach);
	finalize_command_object_expression(obj.properties.initializer, detach);
}
function finalize_imported_interface(obj:Cimported_interface, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexternal_source>obj.output.source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cimported_interfaces>(obj.properties.interface_ as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.interface_.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_user__source(obj:Cuser__source, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexternal_source>obj.output.source)(detach) !== undefined || detach);
	finalize_user_requirement(obj.properties.requirement, detach);
}
function finalize_permission(obj:Cpermission, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexternal_source>obj.output.source)(detach) !== undefined || detach);
	switch (obj.properties.source.state.name) {
		case 'imported interface': finalize_imported_interface(obj.properties.source.state.node, detach); break;
		case 'user': finalize_user__source(obj.properties.source.state.node, detach); break;
	}
}
function finalize_no__has_steps__plural_descendant_node_path(obj:Cno__has_steps__plural_descendant_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_choice__value_type__yes__has_steps__plural_descendant_node_path(obj:Cchoice__value_type__yes__has_steps__plural_descendant_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_collection__value_type__yes__has_steps__plural_descendant_node_path(obj:Ccollection__value_type__yes__has_steps__plural_descendant_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.inferences.valid_key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_node__value_type__yes__has_steps__plural_descendant_node_path(obj:Cnode__value_type__yes__has_steps__plural_descendant_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__plural_descendant_node_path(obj:Cyes__has_steps__plural_descendant_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	finalize_property_step(obj.properties.property, detach);
	finalize_plural_descendant_node_path(obj.properties.tail, detach);
	switch (obj.properties.value_type.state.name) {
		case 'choice': finalize_choice__value_type__yes__has_steps__plural_descendant_node_path(obj.properties.value_type.state.node, detach); break;
		case 'collection': finalize_collection__value_type__yes__has_steps__plural_descendant_node_path(obj.properties.value_type.state.node, detach); break;
		case 'node': finalize_node__value_type__yes__has_steps__plural_descendant_node_path(obj.properties.value_type.state.node, detach); break;
	}
}
function finalize_plural_descendant_node_path(obj:Cplural_descendant_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.input.upper_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__plural_descendant_node_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__plural_descendant_node_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_plural_object_path(obj:Cplural_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.input.direction_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support_for_head)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support_for_path)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.input.upper_bound_cardinality_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_dependency_gen>obj.output.dependency_gen)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	finalize_variablized_object_path(obj.properties.head, detach);
	finalize_plural_object_path_tail(obj.properties.path, detach);
}
function finalize_no__has_steps__plural_object_path_tail(obj:Cno__has_steps__plural_object_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_dependency_gen>obj.output.dependency_gen)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__plural_object_path_tail(obj:Cyes__has_steps__plural_object_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection_object>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_dependency_gen>obj.inferences.dependency_gen)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.inferences.entry_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.inferences.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_dependency_gen>obj.output.dependency_gen)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	finalize_object_path_tail(obj.properties.path, detach);
	finalize_plural_object_path_tail(obj.properties.tail, detach);
}
function finalize_plural_object_path_tail(obj:Cplural_object_path_tail, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_dependency_gen>obj.input.dependency_gen)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.reference_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.input.upper_bound_cardinality_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional_dependency_gen>obj.output.dependency_gen)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.output.upper_bound_cardinality)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__plural_object_path_tail(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__plural_object_path_tail(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_augment(obj:Caugment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	finalize_variablized_object_path(obj.properties.collection_path, detach);
	assert((<(detach?:boolean) => application.Ccollection_object>obj.properties.collection_path.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.collection_path.inferences.collection_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.collection_path.inferences.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.properties.collection_path.inferences.lower_bound_cardinality)(detach) !== undefined || detach);
	finalize_descendant_object_path(obj.properties.filter_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.filter_path.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.filter_path.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_no__has_predecessor__branches(obj:Cno__has_predecessor__branches, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.inferences.lower_bound_cardinality_for_first_branch)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.dependency_location_support)(detach) !== undefined || detach);
}
function finalize_yes__has_predecessor__branches(obj:Cyes__has_predecessor__branches, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.dependency_location_support)(detach) !== undefined || detach);
}
function finalize_no__has_successor__branches(obj:Cno__has_successor__branches, detach:boolean = false) {
}
function finalize_yes__has_successor__branches(obj:Cyes__has_successor__branches, detach:boolean = false) {
}
function finalize_dependency__source(obj:Cdependency__source, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_dependency_step(obj.properties.dependency, detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.dependency.inferences._inspect_dependency_step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.dependency.inferences.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency_definition>obj.properties.dependency.inferences.dependency_step)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Centry_step_definition>obj.properties.dependency.inferences.entry_step)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.dependency.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.dependency.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.dependency.inferences.unified_dependency)(detach) !== undefined || detach);
	finalize_plural_object_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.path.inferences.node)(detach) !== undefined || detach);
}
function finalize_dependency_inversion(obj:Cdependency_inversion, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_variablized_object_path(obj.properties.reference_set_path, detach);
	assert((<(detach?:boolean) => application.Creference_set__type__attributes>obj.properties.reference_set_path.inferences.reference_set)(detach) !== undefined || detach);
	finalize_reference_set_subset_step(obj.properties.subset_path, detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.subset_path.inferences.dependency_required_by_key)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.subset_path.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_branches__union(obj:Cbranches__union, detach:boolean = false) {
	switch (obj.properties.has_predecessor.state.name) {
		case 'no': finalize_no__has_predecessor__branches(obj.properties.has_predecessor.state.node, detach); break;
		case 'yes': finalize_yes__has_predecessor__branches(obj.properties.has_predecessor.state.node, detach); break;
	}
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__branches(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__branches(obj.properties.has_successor.state.node, detach); break;
	}
	switch (obj.properties.source.state.name) {
		case 'dependency': finalize_dependency__source(obj.properties.source.state.node, detach); break;
		case 'dependency inversion': finalize_dependency_inversion(obj.properties.source.state.node, detach); break;
	}
}
function finalize_union(obj:Cunion, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.branches) {
		finalize_branches__union(entry, detach);
	}
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.branches.inferences.computation_phase)(detach) !== undefined || detach);
	if (!detach) {
		if (obj.properties.branches.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
	assert((<(detach?:boolean) => application.Cbranches__union>(obj.properties.first_branch as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cno__has_predecessor__branches>obj.properties.first_branch.inferences.no_predecessor)(detach) !== undefined || detach);
}
function finalize_plural_reference_expression(obj:Cplural_reference_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__key_constraint>obj.input.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.input.lower_bound_cardinality_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__key_constraint>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.value_computation_phase)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'augment': finalize_augment(obj.properties.type.state.node, detach); break;
		case 'union': finalize_union(obj.properties.type.state.node, detach); break;
	}
}
function finalize_property_step(obj:Cproperty_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.property.inferences.dependencies)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.property.inferences.value_source)(detach) !== undefined || detach);
}
function finalize_no__recursive_graph_traversal(obj:Cno__recursive_graph_traversal, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.location)(detach) !== undefined || detach);
}
function finalize_yes__recursive_graph_traversal(obj:Cyes__recursive_graph_traversal, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.location)(detach) !== undefined || detach);
	finalize_graph_traversal_selector(obj.properties.selector, detach);
}
function finalize_recursion_annotation(obj:Crecursion_annotation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.output.location)(detach) !== undefined || detach);
	switch (obj.properties.recursive_graph_traversal.state.name) {
		case 'no': finalize_no__recursive_graph_traversal(obj.properties.recursive_graph_traversal.state.node, detach); break;
		case 'yes': finalize_yes__recursive_graph_traversal(obj.properties.recursive_graph_traversal.state.node, detach); break;
	}
}
function finalize_recursion_dependence(obj:Crecursion_dependence, detach:boolean = false) {
}
function finalize_mandatory__type(obj:Cmandatory__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_optional__type(obj:Coptional__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_reference_behaviour(obj:Creference_behaviour, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.target_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.output.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'mandatory': finalize_mandatory__type(obj.properties.type.state.node, detach); break;
		case 'optional': finalize_optional__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_reference_definer(obj:Creference_definer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Creference_behaviour>obj.input.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.defining_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection_annotation>obj.input.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_annotation>obj.input.recursion)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_dependence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_behaviour>obj.output.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.defining_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection_annotation>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_annotation>obj.output.recursion)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
}
function finalize_reference_set_step(obj:Creference_set_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_set__type__attributes>(obj.properties.reference_set as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.reference_set.inferences.dependencies)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.reference_set.inferences.relative_location_of_referenced_nodes)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.reference_set.inferences.value_source)(detach) !== undefined || detach);
}
function finalize_no__subset(obj:Cno__subset, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
}
function finalize_sibling__type__yes(obj:Csibling__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.head_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.sibling_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csibling__root>obj.inferences.sibling_reference_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.entry_location)(detach) !== undefined || detach);
}
function finalize_simple__type__yes(obj:Csimple__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.entry_location)(detach) !== undefined || detach);
}
function finalize_yes__subset(obj:Cyes__subset, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_variablized_object_path(obj.properties.head, detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.head.inferences.dependency_of_reference_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.head.inferences.subset_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.properties.head.inferences.type_dependency)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'sibling': finalize_sibling__type__yes(obj.properties.type.state.node, detach); break;
		case 'simple': finalize_simple__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_reference_set_subset_step(obj:Creference_set_subset_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_set__type__attributes>obj.input.reference_set)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.input.reference_set_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Clower_bound_cardinality>obj.output.lower_bound_cardinality)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	switch (obj.properties.subset.state.name) {
		case 'no': finalize_no__subset(obj.properties.subset.state.node, detach); break;
		case 'yes': finalize_yes__subset(obj.properties.subset.state.node, detach); break;
	}
}
function finalize_reference_value(obj:Creference_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Creference_definer>obj.input.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	finalize_leaf_value(obj.definitions.leaf_value, detach);
}
function finalize_relative_object_location(obj:Crelative_object_location, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
}
function finalize_root_location(obj:Croot_location, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.root_node_type)(detach) !== undefined || detach);
	finalize_node_location(obj.definitions.node_location, detach);
}
function finalize_dynamic__type__scalar_expression(obj:Cdynamic__type__scalar_expression, detach:boolean = false) {
	finalize_singular_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_scalar>obj.properties.property.inferences.value)(detach) !== undefined || detach);
}
function finalize_expressions__binary_expression__type__number__type__scalar_expression(obj:Cexpressions__binary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	finalize_scalar_expression(obj.properties.left, detach);
	finalize_scalar_expression(obj.properties.right, detach);
}
function finalize_ceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression(obj:Cceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.numerator_set_type)(detach) !== undefined || detach);
}
function finalize_floor__rounding__division__operation__binary_expression__type__number__type__scalar_expression(obj:Cfloor__rounding__division__operation__binary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.numerator_set_type)(detach) !== undefined || detach);
}
function finalize_ordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression(obj:Cordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.numerator_set_type)(detach) !== undefined || detach);
}
function finalize_division__operation__binary_expression__type__number__type__scalar_expression(obj:Cdivision__operation__binary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.left_set_type)(detach) !== undefined || detach);
	switch (obj.properties.rounding.state.name) {
		case 'ceil': finalize_ceil__rounding__division__operation__binary_expression__type__number__type__scalar_expression(obj.properties.rounding.state.node, detach); break;
		case 'floor': finalize_floor__rounding__division__operation__binary_expression__type__number__type__scalar_expression(obj.properties.rounding.state.node, detach); break;
		case 'ordinary': finalize_ordinary__rounding__division__operation__binary_expression__type__number__type__scalar_expression(obj.properties.rounding.state.node, detach); break;
	}
}
function finalize_remainder__operation__binary_expression__type__number__type__scalar_expression(obj:Cremainder__operation__binary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.left_set_type)(detach) !== undefined || detach);
}
function finalize_binary_expression__type__number__type__scalar_expression(obj:Cbinary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	finalize_expressions__binary_expression__type__number__type__scalar_expression(obj.properties.expressions, detach);
	switch (obj.properties.operation.state.name) {
		case 'division': finalize_division__operation__binary_expression__type__number__type__scalar_expression(obj.properties.operation.state.node, detach); break;
		case 'remainder': finalize_remainder__operation__binary_expression__type__number__type__scalar_expression(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_constant__type__number__type__scalar_expression(obj:Cconstant__type__number__type__scalar_expression, detach:boolean = false) {
	finalize_constant_number_value(obj.properties.value, detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.value.inferences.set_type)(detach) !== undefined || detach);
}
function finalize_maximum__operation__list_expression__type__number__type__scalar_expression(obj:Cmaximum__operation__list_expression__type__number__type__scalar_expression, detach:boolean = false) {
}
function finalize_minimum__operation__list_expression__type__number__type__scalar_expression(obj:Cminimum__operation__list_expression__type__number__type__scalar_expression, detach:boolean = false) {
}
function finalize_product__operation__list_expression__type__number__type__scalar_expression(obj:Cproduct__operation__list_expression__type__number__type__scalar_expression, detach:boolean = false) {
}
function finalize_sum__operation__list_expression__type__number__type__scalar_expression(obj:Csum__operation__list_expression__type__number__type__scalar_expression, detach:boolean = false) {
}
function finalize_list_expression__type__number__type__scalar_expression(obj:Clist_expression__type__number__type__scalar_expression, detach:boolean = false) {
	finalize_scalar_expression_list(obj.properties.expression, detach);
	switch (obj.properties.operation.state.name) {
		case 'maximum': finalize_maximum__operation__list_expression__type__number__type__scalar_expression(obj.properties.operation.state.node, detach); break;
		case 'minimum': finalize_minimum__operation__list_expression__type__number__type__scalar_expression(obj.properties.operation.state.node, detach); break;
		case 'product': finalize_product__operation__list_expression__type__number__type__scalar_expression(obj.properties.operation.state.node, detach); break;
		case 'sum': finalize_sum__operation__list_expression__type__number__type__scalar_expression(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_now_in_seconds__type__number__type__scalar_expression(obj:Cnow_in_seconds__type__number__type__scalar_expression, detach:boolean = false) {
}
function finalize_absolute_value__type__unary_expression__type__number__type__scalar_expression(obj:Cabsolute_value__type__unary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
}
function finalize_numerical_type_conversion__type__unary_expression__type__number__type__scalar_expression(obj:Cnumerical_type_conversion__type__unary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csingular_conversions>(obj.properties.conversion as any).resolve)(detach) !== undefined || detach);
}
function finalize_sign_inversion__type__unary_expression__type__number__type__scalar_expression(obj:Csign_inversion__type__unary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__number_set_type>obj.inferences.integer_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
}
function finalize_unary_expression__type__number__type__scalar_expression(obj:Cunary_expression__type__number__type__scalar_expression, detach:boolean = false) {
	finalize_scalar_expression(obj.properties.expression, detach);
	switch (obj.properties.type.state.name) {
		case 'absolute value': finalize_absolute_value__type__unary_expression__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'numerical type conversion': finalize_numerical_type_conversion__type__unary_expression__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'sign inversion': finalize_sign_inversion__type__unary_expression__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__type__scalar_expression(obj:Cnumber__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_number>obj.inferences.number_target)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'binary expression': finalize_binary_expression__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'constant': finalize_constant__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'list expression': finalize_list_expression__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'now in seconds': finalize_now_in_seconds__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'unary expression': finalize_unary_expression__type__number__type__scalar_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_static__type__text__type__scalar_expression(obj:Cstatic__type__text__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.inferences.no_mandatory_reference)(detach) !== undefined || detach);
}
function finalize_text__type__scalar_expression(obj:Ctext__type__scalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand_text>obj.inferences.text_target)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'static': finalize_static__type__text__type__scalar_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_scalar_expression(obj:Cscalar_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_scalar>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__scalar_expression(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__scalar_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_tail__scalar_expression_list(obj:Cno__has_tail__scalar_expression_list, detach:boolean = false) {
}
function finalize_yes__has_tail__scalar_expression_list(obj:Cyes__has_tail__scalar_expression_list, detach:boolean = false) {
	finalize_scalar_expression_list(obj.properties.tail, detach);
}
function finalize_scalar_expression_list(obj:Cscalar_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccommand_scalar>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_tail.state.name) {
		case 'no': finalize_no__has_tail__scalar_expression_list(obj.properties.has_tail.state.node, detach); break;
		case 'yes': finalize_yes__has_tail__scalar_expression_list(obj.properties.has_tail.state.node, detach); break;
	}
	finalize_scalar_expression(obj.properties.head, detach);
}
function finalize_scalar_value(obj:Cscalar_value, detach:boolean = false) {
	finalize_leaf_value(obj.definitions.leaf_value, detach);
}
function finalize_sibling_dependency_retrieval(obj:Csibling_dependency_retrieval, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.output.entity)(detach) !== undefined || detach);
	finalize_relative_object_location(obj.definitions.relative_object_location, detach);
}
function finalize_no__support_self_reference(obj:Cno__support_self_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Csibling_dependency_retrieval>obj.output.location)(detach) !== undefined || detach);
}
function finalize_yes__support_self_reference(obj:Cyes__support_self_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Csibling_dependency_retrieval>obj.output.location)(detach) !== undefined || detach);
}
function finalize_no__graph_participation(obj:Cno__graph_participation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Csibling_dependency_retrieval>obj.output.location)(detach) !== undefined || detach);
	switch (obj.properties.support_self_reference.state.name) {
		case 'no': finalize_no__support_self_reference(obj.properties.support_self_reference.state.node, detach); break;
		case 'yes': finalize_yes__support_self_reference(obj.properties.support_self_reference.state.node, detach); break;
	}
}
function finalize_yes__graph_participation(obj:Cyes__graph_participation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__collection>obj.inferences.base_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__existence>obj.inferences.mandatory_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.inferences.root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csibling_dependency_retrieval>obj.output.location)(detach) !== undefined || detach);
	finalize_graph_traversal_definition(obj.properties.graph_traversal, detach);
}
function finalize_sibling_reference_definition(obj:Csibling_reference_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.definer_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.definer_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csibling_dependency_retrieval>obj.output.location)(detach) !== undefined || detach);
	switch (obj.properties.graph_participation.state.name) {
		case 'no': finalize_no__graph_participation(obj.properties.graph_participation.state.node, detach); break;
		case 'yes': finalize_yes__graph_participation(obj.properties.graph_participation.state.node, detach); break;
	}
}
function finalize_singular_node_path(obj:Csingular_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.path, detach);
}
function finalize_singular_variablized_object_path(obj:Csingular_variablized_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_variablized_object_path(obj.properties.path, detach);
}
function finalize_rule_arguments(obj:Crule_arguments, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crules>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__existence>obj.key.inferences.mandatory)(detach) !== undefined || detach);
	finalize_derivation_expression(obj.properties.expression, detach);
}
function finalize_state_initializer(obj:Cstate_initializer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.input.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_node_initializer(obj.properties.node_initializer, detach);
	for (const [_key, entry] of obj.properties.rule_arguments) {
		finalize_rule_arguments(entry, detach);
	}
	if (!detach) {
		(obj.properties.rule_arguments as any).eval_required_keys(detach);
	}
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_step_type(obj:Cstep_type, detach:boolean = false) {
}
function finalize_no__todo(obj:Cno__todo, detach:boolean = false) {
}
function finalize_yes__todo(obj:Cyes__todo, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Capplication>obj.inferences.dataset_node)(detach) !== undefined || detach);
	finalize_user_requirement(obj.properties.requirement, detach);
	finalize_ui_todo(obj.properties.ui, detach);
}
function finalize_todo_definition(obj:Ctodo_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	switch (obj.properties.todo.state.name) {
		case 'no': finalize_no__todo(obj.properties.todo.state.node, detach); break;
		case 'yes': finalize_yes__todo(obj.properties.todo.state.node, detach); break;
	}
}
function finalize_no__has_next_action(obj:Cno__has_next_action, detach:boolean = false) {
}
function finalize_yes__has_next_action(obj:Cyes__has_next_action, detach:boolean = false) {
	finalize_ui_action(obj.properties.action, detach);
}
function finalize_ui_action(obj:Cui_action, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	finalize_ui_target(obj.definitions.ui_target, detach);
	finalize_ui_expression(obj.properties.expression, detach);
	switch (obj.properties.has_next_action.state.name) {
		case 'no': finalize_no__has_next_action(obj.properties.has_next_action.state.node, detach); break;
		case 'yes': finalize_yes__has_next_action(obj.properties.has_next_action.state.node, detach); break;
	}
}
function finalize_no__has_description__ui_action_attribute(obj:Cno__has_description__ui_action_attribute, detach:boolean = false) {
}
function finalize_yes__has_description__ui_action_attribute(obj:Cyes__has_description__ui_action_attribute, detach:boolean = false) {
}
function finalize_ui_action_attribute(obj:Cui_action_attribute, detach:boolean = false) {
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_action_attribute(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_action_attribute(obj.properties.has_description.state.node, detach); break;
	}
}
function finalize_no__interactive(obj:Cno__interactive, detach:boolean = false) {
}
function finalize_yes__interactive(obj:Cyes__interactive, detach:boolean = false) {
}
function finalize_ui_action_interaction(obj:Cui_action_interaction, detach:boolean = false) {
	switch (obj.properties.interactive.state.name) {
		case 'no': finalize_no__interactive(obj.properties.interactive.state.node, detach); break;
		case 'yes': finalize_yes__interactive(obj.properties.interactive.state.node, detach); break;
	}
}
function finalize_no__show_target(obj:Cno__show_target, detach:boolean = false) {
}
function finalize_yes__show_target(obj:Cyes__show_target, detach:boolean = false) {
}
function finalize_ui_action_visualization(obj:Cui_action_visualization, detach:boolean = false) {
	switch (obj.properties.show_target.state.name) {
		case 'no': finalize_no__show_target(obj.properties.show_target.state.node, detach); break;
		case 'yes': finalize_yes__show_target(obj.properties.show_target.state.node, detach); break;
	}
}
function finalize_no__break_out__ui_collection_property(obj:Cno__break_out__ui_collection_property, detach:boolean = false) {
}
function finalize_yes__break_out__ui_collection_property(obj:Cyes__break_out__ui_collection_property, detach:boolean = false) {
}
function finalize_no__can_be_dormant(obj:Cno__can_be_dormant, detach:boolean = false) {
}
function finalize_yes__can_be_dormant(obj:Cyes__can_be_dormant, detach:boolean = false) {
	finalize_node_path_tail(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.properties.expression.inferences.state_node_type)(detach) !== undefined || detach);
}
function finalize_no__default__ui_collection_property(obj:Cno__default__ui_collection_property, detach:boolean = false) {
}
function finalize_yes__default__ui_collection_property(obj:Cyes__default__ui_collection_property, detach:boolean = false) {
	finalize_node_path_tail(obj.properties.entry_filter, detach);
	finalize_entry_reference_selector(obj.properties.key_reference, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.properties.key_reference.inferences.key)(detach) !== undefined || detach);
}
function finalize_no__has_description__ui_collection_property(obj:Cno__has_description__ui_collection_property, detach:boolean = false) {
}
function finalize_yes__has_description__ui_collection_property(obj:Cyes__has_description__ui_collection_property, detach:boolean = false) {
}
function finalize_no__has_style__ui_collection_property(obj:Cno__has_style__ui_collection_property, detach:boolean = false) {
}
function finalize_yes__has_style__ui_collection_property(obj:Cyes__has_style__ui_collection_property, detach:boolean = false) {
	finalize_ui_expression(obj.properties.style_expression, detach);
}
function finalize_no__icon__ui_collection_property(obj:Cno__icon__ui_collection_property, detach:boolean = false) {
}
function finalize_yes__icon__ui_collection_property(obj:Cyes__icon__ui_collection_property, detach:boolean = false) {
}
function finalize_large(obj:Clarge, detach:boolean = false) {
}
function finalize_small(obj:Csmall, detach:boolean = false) {
}
function finalize_no__sort(obj:Cno__sort, detach:boolean = false) {
}
function finalize_ascending(obj:Cascending, detach:boolean = false) {
}
function finalize_descending(obj:Cdescending, detach:boolean = false) {
}
function finalize_yes__sort(obj:Cyes__sort, detach:boolean = false) {
	switch (obj.properties.direction.state.name) {
		case 'ascending': finalize_ascending(obj.properties.direction.state.node, detach); break;
		case 'descending': finalize_descending(obj.properties.direction.state.node, detach); break;
	}
	finalize_ui_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cscalar_value>obj.properties.property.inferences.scalar_value)(detach) !== undefined || detach);
}
function finalize_ui_collection_property(obj:Cui_collection_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.collection_property)(detach) !== undefined || detach);
	switch (obj.properties.break_out.state.name) {
		case 'no': finalize_no__break_out__ui_collection_property(obj.properties.break_out.state.node, detach); break;
		case 'yes': finalize_yes__break_out__ui_collection_property(obj.properties.break_out.state.node, detach); break;
	}
	switch (obj.properties.can_be_dormant.state.name) {
		case 'no': finalize_no__can_be_dormant(obj.properties.can_be_dormant.state.node, detach); break;
		case 'yes': finalize_yes__can_be_dormant(obj.properties.can_be_dormant.state.node, detach); break;
	}
	finalize_ui_property_classification(obj.properties.classification, detach);
	switch (obj.properties.default_.state.name) {
		case 'no': finalize_no__default__ui_collection_property(obj.properties.default_.state.node, detach); break;
		case 'yes': finalize_yes__default__ui_collection_property(obj.properties.default_.state.node, detach); break;
	}
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_collection_property(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_collection_property(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.has_style.state.name) {
		case 'no': finalize_no__has_style__ui_collection_property(obj.properties.has_style.state.node, detach); break;
		case 'yes': finalize_yes__has_style__ui_collection_property(obj.properties.has_style.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_collection_property(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_collection_property(obj.properties.icon.state.node, detach); break;
	}
	switch (obj.properties.size.state.name) {
		case 'large': finalize_large(obj.properties.size.state.node, detach); break;
		case 'small': finalize_small(obj.properties.size.state.node, detach); break;
	}
	switch (obj.properties.sort.state.name) {
		case 'no': finalize_no__sort(obj.properties.sort.state.node, detach); break;
		case 'yes': finalize_yes__sort(obj.properties.sort.state.node, detach); break;
	}
}
function finalize_no__has_description__ui_command_attribute(obj:Cno__has_description__ui_command_attribute, detach:boolean = false) {
}
function finalize_yes__has_description__ui_command_attribute(obj:Cyes__has_description__ui_command_attribute, detach:boolean = false) {
}
function finalize_false__visible__ui_command_attribute(obj:Cfalse__visible__ui_command_attribute, detach:boolean = false) {
}
function finalize_true__visible__ui_command_attribute(obj:Ctrue__visible__ui_command_attribute, detach:boolean = false) {
}
function finalize_ui_command_attribute(obj:Cui_command_attribute, detach:boolean = false) {
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_command_attribute(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_command_attribute(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.visible.state.name) {
		case 'false': finalize_false__visible__ui_command_attribute(obj.properties.visible.state.node, detach); break;
		case 'true': finalize_true__visible__ui_command_attribute(obj.properties.visible.state.node, detach); break;
	}
}
function finalize_no__more_entries(obj:Cno__more_entries, detach:boolean = false) {
}
function finalize_yes__more_entries(obj:Cyes__more_entries, detach:boolean = false) {
	finalize_ui_entry_expression_list(obj.properties.tail, detach);
}
function finalize_ui_entry_expression_list(obj:Cui_entry_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_target_collection>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.entry_expression, detach);
	switch (obj.properties.more_entries.state.name) {
		case 'no': finalize_no__more_entries(obj.properties.more_entries.state.node, detach); break;
		case 'yes': finalize_yes__more_entries(obj.properties.more_entries.state.node, detach); break;
	}
}
function finalize_create__type__entry__operation__ui_expression(obj:Ccreate__type__entry__operation__ui_expression, detach:boolean = false) {
	finalize_ui_object_expression(obj.properties.expression, detach);
	finalize_ui_action_interaction(obj.properties.interaction, detach);
	finalize_ui_action_visualization(obj.properties.visualization, detach);
}
function finalize_delete__type__entry__operation__ui_expression(obj:Cdelete__type__entry__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupdate__ui_properties_operation>obj.inferences.update)(detach) !== undefined || detach);
	finalize_ui_action_interaction(obj.properties.interaction, detach);
	finalize_singular_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.path.inferences.collection_node_type)(detach) !== undefined || detach);
}
function finalize_entry__operation__ui_expression(obj:Centry__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_target_collection>obj.inferences.target_collection)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'create': finalize_create__type__entry__operation__ui_expression(obj.properties.type.state.node, detach); break;
		case 'delete': finalize_delete__type__entry__operation__ui_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_entry_list(obj:Centry_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_target_collection>obj.inferences.collection)(detach) !== undefined || detach);
	finalize_ui_entry_expression_list(obj.properties.entries, detach);
}
function finalize_execute_operation__operation__ui_expression(obj:Cexecute_operation__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_action>obj.inferences.action_target)(detach) !== undefined || detach);
	finalize_ui_object_expression(obj.properties.expression, detach);
	finalize_ui_action_interaction(obj.properties.interaction, detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.operation as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattribute_class>obj.properties.operation.inferences.attribute_class)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.operation.inferences.parameter_definition)(detach) !== undefined || detach);
	finalize_singular_node_path(obj.properties.path, detach);
}
function finalize_ignore__operation__ui_expression(obj:Cignore__operation__ui_expression, detach:boolean = false) {
}
function finalize_node_case__cases__node_switch__operation__ui_expression(obj:Cnode_case__cases__node_switch__operation__ui_expression, detach:boolean = false) {
	finalize_ui_expression(obj.properties.expression, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_none_case__cases__node_switch__operation__ui_expression(obj:Cnone_case__cases__node_switch__operation__ui_expression, detach:boolean = false) {
	finalize_ui_expression(obj.properties.expression, detach);
}
function finalize_cases__node_switch__operation__ui_expression(obj:Ccases__node_switch__operation__ui_expression, detach:boolean = false) {
	finalize_node_case__cases__node_switch__operation__ui_expression(obj.properties.node_case, detach);
	finalize_none_case__cases__node_switch__operation__ui_expression(obj.properties.none_case, detach);
}
function finalize_node_switch__operation__ui_expression(obj:Cnode_switch__operation__ui_expression, detach:boolean = false) {
	finalize_cases__node_switch__operation__ui_expression(obj.properties.cases, detach);
	finalize_node_expression(obj.properties.expression, detach);
}
function finalize_object__value__produce_value__operation__ui_expression(obj:Cobject__value__produce_value__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_object>obj.inferences.target_object)(detach) !== undefined || detach);
	finalize_ui_object_expression(obj.properties.expression, detach);
}
function finalize_scalar__value__produce_value__operation__ui_expression(obj:Cscalar__value__produce_value__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_scalar>obj.inferences.target_scalar)(detach) !== undefined || detach);
	finalize_ui_scalar_value_expression(obj.properties.expression, detach);
}
function finalize_state__value__produce_value__operation__ui_expression(obj:Cstate__value__produce_value__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_target_stategroup>obj.inferences.target_state_group)(detach) !== undefined || detach);
	finalize_ui_object_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cui_target_state>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_object>obj.properties.state.inferences.target)(detach) !== undefined || detach);
}
function finalize_produce_value__operation__ui_expression(obj:Cproduce_value__operation__ui_expression, detach:boolean = false) {
	switch (obj.properties.value.state.name) {
		case 'object': finalize_object__value__produce_value__operation__ui_expression(obj.properties.value.state.node, detach); break;
		case 'scalar': finalize_scalar__value__produce_value__operation__ui_expression(obj.properties.value.state.node, detach); break;
		case 'state': finalize_state__value__produce_value__operation__ui_expression(obj.properties.value.state.node, detach); break;
	}
}
function finalize_path__state_switch__operation__ui_expression(obj:Cpath__state_switch__operation__ui_expression, detach:boolean = false) {
	finalize_ui_parametrized_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.stategroup as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__state_switch__operation__ui_expression(obj:Cstates__state_switch__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.expression, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_state_switch__operation__ui_expression(obj:Cstate_switch__operation__ui_expression, detach:boolean = false) {
	finalize_path__state_switch__operation__ui_expression(obj.properties.path, detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_switch__operation__ui_expression(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_node__target__update_properties__operation__ui_expression(obj:Cnode__target__update_properties__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_object>obj.inferences.node)(detach) !== undefined || detach);
	finalize_ui_object_expression(obj.properties.expression, detach);
}
function finalize_property__target__update_properties__operation__ui_expression(obj:Cproperty__target__update_properties__operation__ui_expression, detach:boolean = false) {
	finalize_ui_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_target>obj.properties.property.inferences.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.properties.property.inferences.user_value)(detach) !== undefined || detach);
}
function finalize_update_properties__operation__ui_expression(obj:Cupdate_properties__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_action>obj.inferences.action_target)(detach) !== undefined || detach);
	finalize_ui_action_interaction(obj.properties.interaction, detach);
	finalize_singular_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.properties.path.inferences.user_value)(detach) !== undefined || detach);
	switch (obj.properties.target.state.name) {
		case 'node': finalize_node__target__update_properties__operation__ui_expression(obj.properties.target.state.node, detach); break;
		case 'property': finalize_property__target__update_properties__operation__ui_expression(obj.properties.target.state.node, detach); break;
	}
	finalize_ui_action_visualization(obj.properties.visualization, detach);
}
function finalize_walk__operation__ui_expression(obj:Cwalk__operation__ui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__ui_walk_operation_support>obj.inferences.context_supports_walk)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.expression, detach);
	finalize_singular_node_path(obj.properties.path, detach);
	finalize_node_path_tail(obj.properties.tail, detach);
	finalize_optional_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_ui_expression(obj:Cui_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_target>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.operation.state.name) {
		case 'entry': finalize_entry__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'entry list': finalize_entry_list(obj.properties.operation.state.node, detach); break;
		case 'execute operation': finalize_execute_operation__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'ignore': finalize_ignore__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'node switch': finalize_node_switch__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'produce value': finalize_produce_value__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'state switch': finalize_state_switch__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'update properties': finalize_update_properties__operation__ui_expression(obj.properties.operation.state.node, detach); break;
		case 'walk': finalize_walk__operation__ui_expression(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_no__file_name_expression(obj:Cno__file_name_expression, detach:boolean = false) {
}
function finalize_yes__file_name_expression(obj:Cyes__file_name_expression, detach:boolean = false) {
	finalize_ui_scalar_default(obj.properties.file_name_expression, detach);
}
function finalize_no__has_description__ui_file_property(obj:Cno__has_description__ui_file_property, detach:boolean = false) {
}
function finalize_yes__has_description__ui_file_property(obj:Cyes__has_description__ui_file_property, detach:boolean = false) {
}
function finalize_no__icon__ui_file_property(obj:Cno__icon__ui_file_property, detach:boolean = false) {
}
function finalize_yes__icon__ui_file_property(obj:Cyes__icon__ui_file_property, detach:boolean = false) {
}
function finalize_ui_file_property(obj:Cui_file_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cfile>obj.input.file_property)(detach) !== undefined || detach);
	finalize_ui_property_classification(obj.properties.classification, detach);
	switch (obj.properties.file_name_expression.state.name) {
		case 'no': finalize_no__file_name_expression(obj.properties.file_name_expression.state.node, detach); break;
		case 'yes': finalize_yes__file_name_expression(obj.properties.file_name_expression.state.node, detach); break;
	}
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_file_property(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_file_property(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_file_property(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_file_property(obj.properties.icon.state.node, detach); break;
	}
}
function finalize_no__break_out__ui_group_property(obj:Cno__break_out__ui_group_property, detach:boolean = false) {
}
function finalize_yes__break_out__ui_group_property(obj:Cyes__break_out__ui_group_property, detach:boolean = false) {
}
function finalize_no__has_description__ui_group_property(obj:Cno__has_description__ui_group_property, detach:boolean = false) {
}
function finalize_yes__has_description__ui_group_property(obj:Cyes__has_description__ui_group_property, detach:boolean = false) {
}
function finalize_no__icon__ui_group_property(obj:Cno__icon__ui_group_property, detach:boolean = false) {
}
function finalize_yes__icon__ui_group_property(obj:Cyes__icon__ui_group_property, detach:boolean = false) {
}
function finalize_ui_group_property(obj:Cui_group_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.input.group_property)(detach) !== undefined || detach);
	switch (obj.properties.break_out.state.name) {
		case 'no': finalize_no__break_out__ui_group_property(obj.properties.break_out.state.node, detach); break;
		case 'yes': finalize_yes__break_out__ui_group_property(obj.properties.break_out.state.node, detach); break;
	}
	finalize_ui_property_classification(obj.properties.classification, detach);
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_group_property(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_group_property(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_group_property(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_group_property(obj.properties.icon.state.node, detach); break;
	}
}
function finalize_no__has_properties(obj:Cno__has_properties, detach:boolean = false) {
}
function finalize_yes__has_properties(obj:Cyes__has_properties, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperties__ui_identifying_property_selection>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_successor__properties(obj:Cno__has_successor__properties, detach:boolean = false) {
}
function finalize_yes__has_successor__properties(obj:Cyes__has_successor__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperties__ui_identifying_property_selection>(obj.properties.successor as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__state_key_is_identifying(obj:Cno__state_key_is_identifying, detach:boolean = false) {
}
function finalize_yes__state_key_is_identifying(obj:Cyes__state_key_is_identifying, detach:boolean = false) {
}
function finalize_no__has_successor__states__choice(obj:Cno__has_successor__states__choice, detach:boolean = false) {
}
function finalize_yes__has_successor__states__choice(obj:Cyes__has_successor__states__choice, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__choice>(obj.properties.successor as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__choice(obj:Cstates__choice, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__states__choice(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__states__choice(obj.properties.has_successor.state.node, detach); break;
	}
	finalize_ui_identifying_property_selection(obj.properties.selection, detach);
}
function finalize_choice__value_type__properties(obj:Cchoice__value_type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__choice>(obj.properties.first_state as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.state_key_is_identifying.state.name) {
		case 'no': finalize_no__state_key_is_identifying(obj.properties.state_key_is_identifying.state.node, detach); break;
		case 'yes': finalize_yes__state_key_is_identifying(obj.properties.state_key_is_identifying.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__choice(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_node__value_type__properties(obj:Cnode__value_type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group_property)(detach) !== undefined || detach);
	finalize_ui_identifying_property_selection(obj.properties.selection, detach);
}
function finalize_scalar__value_type(obj:Cscalar__value_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cscalar_value>obj.inferences.scalar_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_object>obj.inferences.value_type)(detach) !== undefined || detach);
}
function finalize_properties__ui_identifying_property_selection(obj:Cproperties__ui_identifying_property_selection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__properties(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__properties(obj.properties.has_successor.state.node, detach); break;
	}
	switch (obj.properties.value_type.state.name) {
		case 'choice': finalize_choice__value_type__properties(obj.properties.value_type.state.node, detach); break;
		case 'node': finalize_node__value_type__properties(obj.properties.value_type.state.node, detach); break;
		case 'scalar': finalize_scalar__value_type(obj.properties.value_type.state.node, detach); break;
	}
}
function finalize_ui_identifying_property_selection(obj:Cui_identifying_property_selection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	switch (obj.properties.has_properties.state.name) {
		case 'no': finalize_no__has_properties(obj.properties.has_properties.state.node, detach); break;
		case 'yes': finalize_yes__has_properties(obj.properties.has_properties.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__ui_identifying_property_selection(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_ui_node_path(obj:Cui_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.path, detach);
}
function finalize_ui_number(obj:Cui_number, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.input.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.input.set_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.output.set_type)(detach) !== undefined || detach);
	finalize_ui_property_value(obj.definitions.ui_property_value, detach);
}
function finalize_no__default__ui_number_property(obj:Cno__default__ui_number_property, detach:boolean = false) {
}
function finalize_yes__default__ui_number_property(obj:Cyes__default__ui_number_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__number>obj.inferences.base_value)(detach) !== undefined || detach);
	finalize_ui_scalar_default(obj.properties.default_, detach);
}
function finalize_no__dynamic_numerical_type(obj:Cno__dynamic_numerical_type, detach:boolean = false) {
}
function finalize_yes__dynamic_numerical_type(obj:Cyes__dynamic_numerical_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdynamic__binding>obj.inferences.dynamic_numerical_type)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.binding_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.binding_path.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_no__emphasis__ui_number_property(obj:Cno__emphasis__ui_number_property, detach:boolean = false) {
}
function finalize_yes__emphasis__ui_number_property(obj:Cyes__emphasis__ui_number_property, detach:boolean = false) {
}
function finalize_no__has_description__ui_number_property(obj:Cno__has_description__ui_number_property, detach:boolean = false) {
}
function finalize_yes__has_description__ui_number_property(obj:Cyes__has_description__ui_number_property, detach:boolean = false) {
}
function finalize_no__icon__ui_number_property(obj:Cno__icon__ui_number_property, detach:boolean = false) {
}
function finalize_yes__icon__ui_number_property(obj:Cyes__icon__ui_number_property, detach:boolean = false) {
}
function finalize_no__metadata(obj:Cno__metadata, detach:boolean = false) {
}
function finalize_yes__metadata(obj:Cyes__metadata, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__default__ui_number_property>obj.inferences.has_default)(detach) !== undefined || detach);
}
function finalize_no__has_maximum(obj:Cno__has_maximum, detach:boolean = false) {
}
function finalize_yes__has_maximum(obj:Cyes__has_maximum, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__number>obj.inferences.base_value)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.maximum, detach);
}
function finalize_no__has_minimum(obj:Cno__has_minimum, detach:boolean = false) {
}
function finalize_yes__has_minimum(obj:Cyes__has_minimum, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__number>obj.inferences.base_value)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.minimum, detach);
}
function finalize_validation(obj:Cvalidation, detach:boolean = false) {
	switch (obj.properties.has_maximum.state.name) {
		case 'no': finalize_no__has_maximum(obj.properties.has_maximum.state.node, detach); break;
		case 'yes': finalize_yes__has_maximum(obj.properties.has_maximum.state.node, detach); break;
	}
	switch (obj.properties.has_minimum.state.name) {
		case 'no': finalize_no__has_minimum(obj.properties.has_minimum.state.node, detach); break;
		case 'yes': finalize_yes__has_minimum(obj.properties.has_minimum.state.node, detach); break;
	}
}
function finalize_ui_number_property(obj:Cui_number_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.input.number_property)(detach) !== undefined || detach);
	finalize_ui_property_classification(obj.properties.classification, detach);
	switch (obj.properties.default_.state.name) {
		case 'no': finalize_no__default__ui_number_property(obj.properties.default_.state.node, detach); break;
		case 'yes': finalize_yes__default__ui_number_property(obj.properties.default_.state.node, detach); break;
	}
	switch (obj.properties.dynamic_numerical_type.state.name) {
		case 'no': finalize_no__dynamic_numerical_type(obj.properties.dynamic_numerical_type.state.node, detach); break;
		case 'yes': finalize_yes__dynamic_numerical_type(obj.properties.dynamic_numerical_type.state.node, detach); break;
	}
	switch (obj.properties.emphasis.state.name) {
		case 'no': finalize_no__emphasis__ui_number_property(obj.properties.emphasis.state.node, detach); break;
		case 'yes': finalize_yes__emphasis__ui_number_property(obj.properties.emphasis.state.node, detach); break;
	}
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_number_property(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_number_property(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_number_property(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_number_property(obj.properties.icon.state.node, detach); break;
	}
	switch (obj.properties.metadata.state.name) {
		case 'no': finalize_no__metadata(obj.properties.metadata.state.node, detach); break;
		case 'yes': finalize_yes__metadata(obj.properties.metadata.state.node, detach); break;
	}
	finalize_validation(obj.properties.validation, detach);
}
function finalize_dynamic__binding(obj:Cdynamic__binding, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
	finalize_variable_assignment(obj.properties.assignment, detach);
	finalize_node_type_id(obj.properties.path, detach);
}
function finalize_root(obj:Croot, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
}
function finalize_none__conversion(obj:Cnone__conversion, detach:boolean = false) {
}
function finalize_point_translation(obj:Cpoint_translation, detach:boolean = false) {
	finalize_ui_scalar_value_expression(obj.properties.decimals, detach);
}
function finalize_properties__custom_type(obj:Cproperties__custom_type, detach:boolean = false) {
	switch (obj.properties.conversion.state.name) {
		case 'none': finalize_none__conversion(obj.properties.conversion.state.node, detach); break;
		case 'point translation': finalize_point_translation(obj.properties.conversion.state.node, detach); break;
	}
	finalize_ui_scalar_value_expression(obj.properties.label, detach);
}
function finalize_custom_type(obj:Ccustom_type, detach:boolean = false) {
	switch (obj.properties.binding.state.name) {
		case 'dynamic': finalize_dynamic__binding(obj.properties.binding.state.node, detach); break;
		case 'root': finalize_root(obj.properties.binding.state.node, detach); break;
	}
	finalize_properties__custom_type(obj.properties.properties, detach);
}
function finalize_date(obj:Cdate, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cscale>obj.inferences.scale_type)(detach) !== undefined || detach);
}
function finalize_date_and_time(obj:Cdate_and_time, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cscale>obj.inferences.scale_type)(detach) !== undefined || detach);
}
function finalize_hours(obj:Chours, detach:boolean = false) {
}
function finalize_minutes(obj:Cminutes, detach:boolean = false) {
}
function finalize_seconds__unit(obj:Cseconds__unit, detach:boolean = false) {
}
function finalize_duration(obj:Cduration, detach:boolean = false) {
	switch (obj.properties.unit.state.name) {
		case 'hours': finalize_hours(obj.properties.unit.state.node, detach); break;
		case 'minutes': finalize_minutes(obj.properties.unit.state.node, detach); break;
		case 'seconds': finalize_seconds__unit(obj.properties.unit.state.node, detach); break;
	}
}
function finalize_model(obj:Cmodel, detach:boolean = false) {
}
function finalize_ui_numerical_type(obj:Cui_numerical_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.input.numerical_type)(detach) !== undefined || detach);
	switch (obj.properties.represent_as.state.name) {
		case 'custom type': finalize_custom_type(obj.properties.represent_as.state.node, detach); break;
		case 'date': finalize_date(obj.properties.represent_as.state.node, detach); break;
		case 'date and time': finalize_date_and_time(obj.properties.represent_as.state.node, detach); break;
		case 'duration': finalize_duration(obj.properties.represent_as.state.node, detach); break;
		case 'model': finalize_model(obj.properties.represent_as.state.node, detach); break;
	}
}
function finalize_ui_object(obj:Cui_object, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_properties_operation>obj.input.operation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_properties_operation>obj.output.operation)(detach) !== undefined || detach);
	finalize_ui_target(obj.definitions.ui_target, detach);
}
function finalize_properties__ui_object_expression(obj:Cproperties__ui_object_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_target>obj.key.inferences.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.key.inferences.user_value)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.default_, detach);
}
function finalize_ui_object_expression(obj:Cui_object_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_object>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__ui_object_expression(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_ui_parametrized_node_path(obj:Cui_parametrized_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_conditional_node_path(obj.properties.path, detach);
}
function finalize_ui_properties_operation(obj:Cui_properties_operation, detach:boolean = false) {
}
function finalize_hidden(obj:Chidden, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cderived_value_source>obj.inferences.derived_value)(detach) !== undefined || detach);
}
function finalize_identifying(obj:Cidentifying, detach:boolean = false) {
}
function finalize_outlining(obj:Coutlining, detach:boolean = false) {
}
function finalize_standard(obj:Cstandard, detach:boolean = false) {
}
function finalize_ui_property_classification(obj:Cui_property_classification, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.property)(detach) !== undefined || detach);
	switch (obj.properties.classification.state.name) {
		case 'hidden': finalize_hidden(obj.properties.classification.state.node, detach); break;
		case 'identifying': finalize_identifying(obj.properties.classification.state.node, detach); break;
		case 'outlining': finalize_outlining(obj.properties.classification.state.node, detach); break;
		case 'standard': finalize_standard(obj.properties.classification.state.node, detach); break;
	}
}
function finalize_ui_property_value(obj:Cui_property_value, detach:boolean = false) {
	finalize_ui_scalar(obj.definitions.ui_scalar, detach);
}
function finalize_no__has_validation__ui_reference_rule(obj:Cno__has_validation__ui_reference_rule, detach:boolean = false) {
}
function finalize_yes__has_validation__ui_reference_rule(obj:Cyes__has_validation__ui_reference_rule, detach:boolean = false) {
}
function finalize_ui_reference_rule(obj:Cui_reference_rule, detach:boolean = false) {
	switch (obj.properties.has_validation.state.name) {
		case 'no': finalize_no__has_validation__ui_reference_rule(obj.properties.has_validation.state.node, detach); break;
		case 'yes': finalize_yes__has_validation__ui_reference_rule(obj.properties.has_validation.state.node, detach); break;
	}
}
function finalize_no__break_out__ui_reference_set_attribute(obj:Cno__break_out__ui_reference_set_attribute, detach:boolean = false) {
}
function finalize_yes__break_out__ui_reference_set_attribute(obj:Cyes__break_out__ui_reference_set_attribute, detach:boolean = false) {
}
function finalize_no__has_description__ui_reference_set_attribute(obj:Cno__has_description__ui_reference_set_attribute, detach:boolean = false) {
}
function finalize_yes__has_description__ui_reference_set_attribute(obj:Cyes__has_description__ui_reference_set_attribute, detach:boolean = false) {
}
function finalize_false__visible__ui_reference_set_attribute(obj:Cfalse__visible__ui_reference_set_attribute, detach:boolean = false) {
}
function finalize_true__visible__ui_reference_set_attribute(obj:Ctrue__visible__ui_reference_set_attribute, detach:boolean = false) {
}
function finalize_ui_reference_set_attribute(obj:Cui_reference_set_attribute, detach:boolean = false) {
	switch (obj.properties.break_out.state.name) {
		case 'no': finalize_no__break_out__ui_reference_set_attribute(obj.properties.break_out.state.node, detach); break;
		case 'yes': finalize_yes__break_out__ui_reference_set_attribute(obj.properties.break_out.state.node, detach); break;
	}
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_reference_set_attribute(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_reference_set_attribute(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.visible.state.name) {
		case 'false': finalize_false__visible__ui_reference_set_attribute(obj.properties.visible.state.node, detach); break;
		case 'true': finalize_true__visible__ui_reference_set_attribute(obj.properties.visible.state.node, detach); break;
	}
}
function finalize_ui_scalar(obj:Cui_scalar, detach:boolean = false) {
	finalize_ui_target(obj.definitions.ui_target, detach);
}
function finalize_no__has_fallback(obj:Cno__has_fallback, detach:boolean = false) {
}
function finalize_yes__has_fallback(obj:Cyes__has_fallback, detach:boolean = false) {
	finalize_ui_scalar_default(obj.properties.fallback, detach);
}
function finalize_ui_scalar_default(obj:Cui_scalar_default, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_scalar>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	finalize_ui_expression(obj.properties.expression, detach);
	switch (obj.properties.has_fallback.state.name) {
		case 'no': finalize_no__has_fallback(obj.properties.has_fallback.state.node, detach); break;
		case 'yes': finalize_yes__has_fallback(obj.properties.has_fallback.state.node, detach); break;
	}
}
function finalize_dynamic__type__ui_scalar_value_expression(obj:Cdynamic__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_property_value>obj.inferences.property_target)(detach) !== undefined || detach);
	finalize_ui_parametrized_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_scalar>obj.properties.property.inferences.value)(detach) !== undefined || detach);
}
function finalize_expressions__binary_expression__type__number__type__ui_scalar_value_expression(obj:Cexpressions__binary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	finalize_ui_scalar_value_expression(obj.properties.left, detach);
	finalize_ui_scalar_value_expression(obj.properties.right, detach);
}
function finalize_ceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj:Cceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_floor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj:Cfloor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_ordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj:Cordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj:Cdivision__operation__binary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	switch (obj.properties.rounding.state.name) {
		case 'ceil': finalize_ceil__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.rounding.state.node, detach); break;
		case 'floor': finalize_floor__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.rounding.state.node, detach); break;
		case 'ordinary': finalize_ordinary__rounding__division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.rounding.state.node, detach); break;
	}
}
function finalize_remainder__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj:Cremainder__operation__binary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_binary_expression__type__number__type__ui_scalar_value_expression(obj:Cbinary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	finalize_expressions__binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.expressions, detach);
	switch (obj.properties.operation.state.name) {
		case 'division': finalize_division__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.operation.state.node, detach); break;
		case 'remainder': finalize_remainder__operation__binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_constant__type__number__type__ui_scalar_value_expression(obj:Cconstant__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	finalize_constant_number_value(obj.properties.value, detach);
}
function finalize_maximum__operation__list_expression__type__number__type__ui_scalar_value_expression(obj:Cmaximum__operation__list_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_minimum__operation__list_expression__type__number__type__ui_scalar_value_expression(obj:Cminimum__operation__list_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_product__operation__list_expression__type__number__type__ui_scalar_value_expression(obj:Cproduct__operation__list_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_sum__operation__list_expression__type__number__type__ui_scalar_value_expression(obj:Csum__operation__list_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_list_expression__type__number__type__ui_scalar_value_expression(obj:Clist_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	switch (obj.properties.operation.state.name) {
		case 'maximum': finalize_maximum__operation__list_expression__type__number__type__ui_scalar_value_expression(obj.properties.operation.state.node, detach); break;
		case 'minimum': finalize_minimum__operation__list_expression__type__number__type__ui_scalar_value_expression(obj.properties.operation.state.node, detach); break;
		case 'product': finalize_product__operation__list_expression__type__number__type__ui_scalar_value_expression(obj.properties.operation.state.node, detach); break;
		case 'sum': finalize_sum__operation__list_expression__type__number__type__ui_scalar_value_expression(obj.properties.operation.state.node, detach); break;
	}
	finalize_ui_scalar_value_expression_list(obj.properties.value, detach);
}
function finalize_now_in_seconds__type__number__type__ui_scalar_value_expression(obj:Cnow_in_seconds__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_absolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression(obj:Cabsolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
}
function finalize_no__representation_based(obj:Cno__representation_based, detach:boolean = false) {
}
function finalize_ui_date_and_time(obj:Cui_date_and_time, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdate_and_time>obj.inferences.ui_date_and_time_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdate>obj.inferences.ui_date_target)(detach) !== undefined || detach);
}
function finalize_numerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression(obj:Cnumerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csingular_conversions>(obj.properties.conversion as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.representation_based.state.name) {
		case 'no': finalize_no__representation_based(obj.properties.representation_based.state.node, detach); break;
		case 'ui date and time': finalize_ui_date_and_time(obj.properties.representation_based.state.node, detach); break;
	}
}
function finalize_sign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression(obj:Csign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.numerical_type)(detach) !== undefined || detach);
}
function finalize_unary_expression__type__number__type__ui_scalar_value_expression(obj:Cunary_expression__type__number__type__ui_scalar_value_expression, detach:boolean = false) {
	finalize_ui_scalar_value_expression(obj.properties.expression, detach);
	switch (obj.properties.type.state.name) {
		case 'absolute value': finalize_absolute_value__type__unary_expression__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'numerical type conversion': finalize_numerical_type_conversion__type__unary_expression__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'sign inversion': finalize_sign_inversion__type__unary_expression__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__type__ui_scalar_value_expression(obj:Cnumber__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_number>obj.inferences.number_target)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'binary expression': finalize_binary_expression__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'constant': finalize_constant__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'list expression': finalize_list_expression__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'now in seconds': finalize_now_in_seconds__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'unary expression': finalize_unary_expression__type__number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_state__type__ui_scalar_value_expression(obj:Cstate__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_state_choice>obj.inferences.state_choice_target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_target_state>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_sticky(obj:Csticky, detach:boolean = false) {
}
function finalize_fixed(obj:Cfixed, detach:boolean = false) {
	finalize_ui_style(obj.properties.style, detach);
}
function finalize_property__type__style(obj:Cproperty__type__style, detach:boolean = false) {
	finalize_ui_scalar_value_expression(obj.properties.expression, detach);
}
function finalize_style__type(obj:Cstyle__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstyle__ui_scalar>obj.inferences.style_target)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'fixed': finalize_fixed(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__style(obj.properties.type.state.node, detach); break;
	}
}
function finalize_auto_increment(obj:Cauto_increment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text_property_target)(detach) !== undefined || detach);
	finalize_ancestor_node_path(obj.properties.scope, detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.properties.scope.inferences.root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.scope.inferences.valid_increment_scope)(detach) !== undefined || detach);
}
function finalize_concatenation__type__text__type__ui_scalar_value_expression(obj:Cconcatenation__type__text__type__ui_scalar_value_expression, detach:boolean = false) {
	finalize_ui_scalar_value_expression_list(obj.properties.list, detach);
}
function finalize_guid(obj:Cguid, detach:boolean = false) {
}
function finalize_no__pad(obj:Cno__pad, detach:boolean = false) {
}
function finalize_yes__pad(obj:Cyes__pad, detach:boolean = false) {
}
function finalize_number_to_text(obj:Cnumber_to_text, detach:boolean = false) {
	finalize_ui_scalar_value_expression(obj.properties.expression, detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.properties.numerical_type as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.pad.state.name) {
		case 'no': finalize_no__pad(obj.properties.pad.state.node, detach); break;
		case 'yes': finalize_yes__pad(obj.properties.pad.state.node, detach); break;
	}
}
function finalize_static__type__text__type__ui_scalar_value_expression(obj:Cstatic__type__text__type__ui_scalar_value_expression, detach:boolean = false) {
}
function finalize_text__type__ui_scalar_value_expression(obj:Ctext__type__ui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_text>obj.inferences.text_target)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'auto increment': finalize_auto_increment(obj.properties.type.state.node, detach); break;
		case 'concatenation': finalize_concatenation__type__text__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'guid': finalize_guid(obj.properties.type.state.node, detach); break;
		case 'number to text': finalize_number_to_text(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type__text__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_ui_scalar_value_expression(obj:Cui_scalar_value_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_scalar>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
		case 'sticky': finalize_sticky(obj.properties.type.state.node, detach); break;
		case 'style': finalize_style__type(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__ui_scalar_value_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_tail__ui_scalar_value_expression_list(obj:Cno__has_tail__ui_scalar_value_expression_list, detach:boolean = false) {
}
function finalize_yes__has_tail__ui_scalar_value_expression_list(obj:Cyes__has_tail__ui_scalar_value_expression_list, detach:boolean = false) {
	finalize_ui_scalar_value_expression_list(obj.properties.tail, detach);
}
function finalize_ui_scalar_value_expression_list(obj:Cui_scalar_value_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_scalar>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.this_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_tail.state.name) {
		case 'no': finalize_no__has_tail__ui_scalar_value_expression_list(obj.properties.has_tail.state.node, detach); break;
		case 'yes': finalize_yes__has_tail__ui_scalar_value_expression_list(obj.properties.has_tail.state.node, detach); break;
	}
	finalize_ui_scalar_value_expression(obj.properties.value, detach);
}
function finalize_no__desired_state(obj:Cno__desired_state, detach:boolean = false) {
}
function finalize_yes__desired_state(obj:Cyes__desired_state, detach:boolean = false) {
}
function finalize_no__has_style__ui_state(obj:Cno__has_style__ui_state, detach:boolean = false) {
}
function finalize_yes__has_style__ui_state(obj:Cyes__has_style__ui_state, detach:boolean = false) {
	finalize_ui_expression(obj.properties.style_expression, detach);
}
function finalize_no__icon__ui_state(obj:Cno__icon__ui_state, detach:boolean = false) {
}
function finalize_yes__icon__ui_state(obj:Cyes__icon__ui_state, detach:boolean = false) {
}
function finalize_transitions(obj:Ctransitions, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Caction>(obj.properties.action as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__verified_state(obj:Cno__verified_state, detach:boolean = false) {
}
function finalize_yes__verified_state(obj:Cyes__verified_state, detach:boolean = false) {
}
function finalize_ui_state(obj:Cui_state, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.input.state)(detach) !== undefined || detach);
	switch (obj.properties.desired_state.state.name) {
		case 'no': finalize_no__desired_state(obj.properties.desired_state.state.node, detach); break;
		case 'yes': finalize_yes__desired_state(obj.properties.desired_state.state.node, detach); break;
	}
	switch (obj.properties.has_style.state.name) {
		case 'no': finalize_no__has_style__ui_state(obj.properties.has_style.state.node, detach); break;
		case 'yes': finalize_yes__has_style__ui_state(obj.properties.has_style.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_state(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_state(obj.properties.icon.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.transitions) {
		finalize_transitions(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.verified_state.state.name) {
		case 'no': finalize_no__verified_state(obj.properties.verified_state.state.node, detach); break;
		case 'yes': finalize_yes__verified_state(obj.properties.verified_state.state.node, detach); break;
	}
}
function finalize_ui_state_choice(obj:Cui_state_choice, detach:boolean = false) {
	finalize_ui_property_value(obj.definitions.ui_property_value, detach);
}
function finalize_no__default_state(obj:Cno__default_state, detach:boolean = false) {
}
function finalize_yes__default_state(obj:Cyes__default_state, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__state_group>obj.inferences.base_value)(detach) !== undefined || detach);
	finalize_ui_scalar_default(obj.properties.default_, detach);
}
function finalize_no__emphasis__ui_state_group_property(obj:Cno__emphasis__ui_state_group_property, detach:boolean = false) {
}
function finalize_yes__emphasis__ui_state_group_property(obj:Cyes__emphasis__ui_state_group_property, detach:boolean = false) {
}
function finalize_no__has_description__ui_state_group_property(obj:Cno__has_description__ui_state_group_property, detach:boolean = false) {
}
function finalize_yes__has_description__ui_state_group_property(obj:Cyes__has_description__ui_state_group_property, detach:boolean = false) {
}
function finalize_no__icon__ui_state_group_property(obj:Cno__icon__ui_state_group_property, detach:boolean = false) {
}
function finalize_yes__icon__ui_state_group_property(obj:Cyes__icon__ui_state_group_property, detach:boolean = false) {
}
function finalize_ui_state_group_property(obj:Cui_state_group_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.input.state_group_property)(detach) !== undefined || detach);
	finalize_ui_property_classification(obj.properties.classification, detach);
	switch (obj.properties.default_state.state.name) {
		case 'no': finalize_no__default_state(obj.properties.default_state.state.node, detach); break;
		case 'yes': finalize_yes__default_state(obj.properties.default_state.state.node, detach); break;
	}
	switch (obj.properties.emphasis.state.name) {
		case 'no': finalize_no__emphasis__ui_state_group_property(obj.properties.emphasis.state.node, detach); break;
		case 'yes': finalize_yes__emphasis__ui_state_group_property(obj.properties.emphasis.state.node, detach); break;
	}
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_state_group_property(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_state_group_property(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_state_group_property(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_state_group_property(obj.properties.icon.state.node, detach); break;
	}
}
function finalize_accent(obj:Caccent, detach:boolean = false) {
}
function finalize_background(obj:Cbackground, detach:boolean = false) {
}
function finalize_brand(obj:Cbrand, detach:boolean = false) {
}
function finalize_error(obj:Cerror, detach:boolean = false) {
}
function finalize_foreground(obj:Cforeground, detach:boolean = false) {
}
function finalize_link(obj:Clink, detach:boolean = false) {
}
function finalize_success(obj:Csuccess, detach:boolean = false) {
}
function finalize_warning(obj:Cwarning, detach:boolean = false) {
}
function finalize_ui_style(obj:Cui_style, detach:boolean = false) {
	switch (obj.properties.style.state.name) {
		case 'accent': finalize_accent(obj.properties.style.state.node, detach); break;
		case 'background': finalize_background(obj.properties.style.state.node, detach); break;
		case 'brand': finalize_brand(obj.properties.style.state.node, detach); break;
		case 'error': finalize_error(obj.properties.style.state.node, detach); break;
		case 'foreground': finalize_foreground(obj.properties.style.state.node, detach); break;
		case 'link': finalize_link(obj.properties.style.state.node, detach); break;
		case 'success': finalize_success(obj.properties.style.state.node, detach); break;
		case 'warning': finalize_warning(obj.properties.style.state.node, detach); break;
	}
}
function finalize_ui_target(obj:Cui_target, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_walk_operation_support>obj.output.supports_walk_operation)(detach) !== undefined || detach);
}
function finalize_ui_target_collection(obj:Cui_target_collection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_object>obj.input.entry_object)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_object>obj.output.entry_object)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
	finalize_ui_target(obj.definitions.ui_target, detach);
}
function finalize_ui_target_state(obj:Cui_target_state, detach:boolean = false) {
}
function finalize_ui_target_stategroup(obj:Cui_target_stategroup, detach:boolean = false) {
	finalize_ui_target(obj.definitions.ui_target, detach);
}
function finalize_ui_text(obj:Cui_text, detach:boolean = false) {
	finalize_ui_property_value(obj.definitions.ui_property_value, detach);
}
function finalize_no__default_value(obj:Cno__default_value, detach:boolean = false) {
}
function finalize_yes__default_value(obj:Cyes__default_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.inferences.base_value)(detach) !== undefined || detach);
	finalize_ui_scalar_default(obj.properties.default_, detach);
}
function finalize_no__emphasis__ui_text_property(obj:Cno__emphasis__ui_text_property, detach:boolean = false) {
}
function finalize_yes__emphasis__ui_text_property(obj:Cyes__emphasis__ui_text_property, detach:boolean = false) {
}
function finalize_no__has_custom_identifying_properties(obj:Cno__has_custom_identifying_properties, detach:boolean = false) {
}
function finalize_yes__has_custom_identifying_properties(obj:Cyes__has_custom_identifying_properties, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.reference)(detach) !== undefined || detach);
	finalize_ui_identifying_property_selection(obj.properties.selection, detach);
}
function finalize_no__has_description__ui_text_property(obj:Cno__has_description__ui_text_property, detach:boolean = false) {
}
function finalize_yes__has_description__ui_text_property(obj:Cyes__has_description__ui_text_property, detach:boolean = false) {
}
function finalize_no__has_validation__ui_text_property(obj:Cno__has_validation__ui_text_property, detach:boolean = false) {
}
function finalize_yes__has_validation__ui_text_property(obj:Cyes__has_validation__ui_text_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.inferences.base_text)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.inferences.reference)(detach) !== undefined || detach);
	finalize_ui_text_validation(obj.properties.rules, detach);
}
function finalize_no__icon__ui_text_property(obj:Cno__icon__ui_text_property, detach:boolean = false) {
}
function finalize_yes__icon__ui_text_property(obj:Cyes__icon__ui_text_property, detach:boolean = false) {
}
function finalize_no__is_label(obj:Cno__is_label, detach:boolean = false) {
}
function finalize_yes__is_label(obj:Cyes__is_label, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.is_key)(detach) !== undefined || detach);
}
function finalize_color(obj:Ccolor, detach:boolean = false) {
}
function finalize_default(obj:Cdefault, detach:boolean = false) {
}
function finalize_html(obj:Chtml, detach:boolean = false) {
}
function finalize_markdown(obj:Cmarkdown, detach:boolean = false) {
}
function finalize_multi_line(obj:Cmulti_line, detach:boolean = false) {
}
function finalize_url(obj:Curl, detach:boolean = false) {
}
function finalize_ui_text_property(obj:Cui_text_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.input.text_property)(detach) !== undefined || detach);
	finalize_ui_property_classification(obj.properties.classification, detach);
	switch (obj.properties.default_value.state.name) {
		case 'no': finalize_no__default_value(obj.properties.default_value.state.node, detach); break;
		case 'yes': finalize_yes__default_value(obj.properties.default_value.state.node, detach); break;
	}
	switch (obj.properties.emphasis.state.name) {
		case 'no': finalize_no__emphasis__ui_text_property(obj.properties.emphasis.state.node, detach); break;
		case 'yes': finalize_yes__emphasis__ui_text_property(obj.properties.emphasis.state.node, detach); break;
	}
	switch (obj.properties.has_custom_identifying_properties.state.name) {
		case 'no': finalize_no__has_custom_identifying_properties(obj.properties.has_custom_identifying_properties.state.node, detach); break;
		case 'yes': finalize_yes__has_custom_identifying_properties(obj.properties.has_custom_identifying_properties.state.node, detach); break;
	}
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_text_property(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_text_property(obj.properties.has_description.state.node, detach); break;
	}
	switch (obj.properties.has_validation.state.name) {
		case 'no': finalize_no__has_validation__ui_text_property(obj.properties.has_validation.state.node, detach); break;
		case 'yes': finalize_yes__has_validation__ui_text_property(obj.properties.has_validation.state.node, detach); break;
	}
	switch (obj.properties.icon.state.name) {
		case 'no': finalize_no__icon__ui_text_property(obj.properties.icon.state.node, detach); break;
		case 'yes': finalize_yes__icon__ui_text_property(obj.properties.icon.state.node, detach); break;
	}
	switch (obj.properties.is_label.state.name) {
		case 'no': finalize_no__is_label(obj.properties.is_label.state.node, detach); break;
		case 'yes': finalize_yes__is_label(obj.properties.is_label.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'color': finalize_color(obj.properties.type.state.node, detach); break;
		case 'default': finalize_default(obj.properties.type.state.node, detach); break;
		case 'html': finalize_html(obj.properties.type.state.node, detach); break;
		case 'markdown': finalize_markdown(obj.properties.type.state.node, detach); break;
		case 'multi-line': finalize_multi_line(obj.properties.type.state.node, detach); break;
		case 'url': finalize_url(obj.properties.type.state.node, detach); break;
	}
}
function finalize_ui_text_validation(obj:Cui_text_validation, detach:boolean = false) {
}
function finalize_no__has_description__ui_todo(obj:Cno__has_description__ui_todo, detach:boolean = false) {
}
function finalize_yes__has_description__ui_todo(obj:Cyes__has_description__ui_todo, detach:boolean = false) {
}
function finalize_ui_todo(obj:Cui_todo, detach:boolean = false) {
	switch (obj.properties.has_description.state.name) {
		case 'no': finalize_no__has_description__ui_todo(obj.properties.has_description.state.node, detach); break;
		case 'yes': finalize_yes__has_description__ui_todo(obj.properties.has_description.state.node, detach); break;
	}
}
function finalize_ui_walk_operation_support(obj:Cui_walk_operation_support, detach:boolean = false) {
}
function finalize_unary_number_expression(obj:Cunary_number_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber_value>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.computation_phase)(detach) !== undefined || detach);
	finalize_derivation_expression_tail(obj.properties.expression, detach);
}
function finalize_upper_bound_cardinality(obj:Cupper_bound_cardinality, detach:boolean = false) {
}
function finalize_user_initializer(obj:Cuser_initializer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.input.users_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.username)(detach) !== undefined || detach);
	finalize_command_initialization_operation(obj.definitions.command_initialization_operation, detach);
	finalize_command_object_expression(obj.properties.initializer, detach);
}
function finalize_no__has_alternative(obj:Cno__has_alternative, detach:boolean = false) {
}
function finalize_yes__has_alternative(obj:Cyes__has_alternative, detach:boolean = false) {
	finalize_user_requirement(obj.properties.alternative, detach);
}
function finalize_no__has_filter(obj:Cno__has_filter, detach:boolean = false) {
}
function finalize_yes__has_filter(obj:Cyes__has_filter, detach:boolean = false) {
	finalize_optional_variable_assignment(obj.properties.assignment, detach);
	finalize_user_requirement(obj.properties.path, detach);
}
function finalize_user_requirement(obj:Cuser_requirement, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.definition_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.variable)(detach) !== undefined || detach);
	finalize_node_expression(obj.properties.expression, detach);
	switch (obj.properties.has_alternative.state.name) {
		case 'no': finalize_no__has_alternative(obj.properties.has_alternative.state.node, detach); break;
		case 'yes': finalize_yes__has_alternative(obj.properties.has_alternative.state.node, detach); break;
	}
	switch (obj.properties.has_filter.state.name) {
		case 'no': finalize_no__has_filter(obj.properties.has_filter.state.node, detach); break;
		case 'yes': finalize_yes__has_filter(obj.properties.has_filter.state.node, detach); break;
	}
}
function finalize_value_member_inclusion(obj:Cvalue_member_inclusion, detach:boolean = false) {
}
function finalize_value_object(obj:Cvalue_object, detach:boolean = false) {
}
function finalize_value_source(obj:Cvalue_source, detach:boolean = false) {
}
function finalize_variable__application(obj:Cvariable__application, detach:boolean = false) {
}
function finalize_no__has_successor__named_objects__explicit__name__variable_assignment(obj:Cno__has_successor__named_objects__explicit__name__variable_assignment, detach:boolean = false) {
}
function finalize_yes__has_successor__named_objects__explicit__name__variable_assignment(obj:Cyes__has_successor__named_objects__explicit__name__variable_assignment, detach:boolean = false) {
}
function finalize_named_objects__explicit__name__variable_assignment(obj:Cnamed_objects__explicit__name__variable_assignment, detach:boolean = false) {
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor__named_objects__explicit__name__variable_assignment(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor__named_objects__explicit__name__variable_assignment(obj.properties.has_successor.state.node, detach); break;
	}
}
function finalize_explicit__name__variable_assignment(obj:Cexplicit__name__variable_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnamed_objects__explicit__name__variable_assignment>(obj.properties.name as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cno__has_successor__named_objects__explicit__name__variable_assignment>obj.properties.name.inferences.single_name)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.named_objects) {
		finalize_named_objects__explicit__name__variable_assignment(entry, detach);
	}
	if (!detach) {
		if (obj.properties.named_objects.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_implicit__name__variable_assignment(obj:Cimplicit__name__variable_assignment, detach:boolean = false) {
}
function finalize_variable_assignment(obj:Cvariable_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.input.parent_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.parent_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvariable__application>obj.output.variable)(detach) !== undefined || detach);
	finalize_variable__application(obj.definitions.variable, detach);
	switch (obj.properties.name.state.name) {
		case 'explicit': finalize_explicit__name__variable_assignment(obj.properties.name.state.node, detach); break;
		case 'implicit': finalize_implicit__name__variable_assignment(obj.properties.name.state.node, detach); break;
	}
}
function finalize_variablized_object_path(obj:Cvariablized_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.input.direction_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.modifier)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_support>obj.input.parent_navigation_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.input.this_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_context_object_step(obj.properties.head, detach);
	finalize_object_path_tail(obj.properties.path, detach);
}
function finalize_sibling_rule(obj:Csibling_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.existence)(detach) !== undefined || detach);
}
function finalize_this__context__where_rule_object_path(obj:Cthis__context__where_rule_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	finalize_context_object_step(obj.properties.head, detach);
}
function finalize_where_rule_object_path(obj:Cwhere_rule_object_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cwhere_rules_definer>obj.input.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>obj.input.rule)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'sibling rule': finalize_sibling_rule(obj.properties.context.state.node, detach); break;
		case 'this': finalize_this__context__where_rule_object_path(obj.properties.context.state.node, detach); break;
	}
	finalize_object_path_tail(obj.properties.path, detach);
}
function finalize_where_rule_step(obj:Cwhere_rule_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cwhere_rules_definition>obj.input.definition)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.dependency_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.input.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crecursion_dependence>obj.input.referenced_location_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Croot_location>obj.input.target_root_location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.input.value_source_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnavigation_context>obj.output.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.output.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.properties.rule.inferences.dependencies)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.properties.rule.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.properties.rule.inferences.relative_location_of_referenced_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.properties.rule.inferences.value_source)(detach) !== undefined || detach);
}
function finalize_where_rules_definer(obj:Cwhere_rules_definer, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.output.defining_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source)(detach) !== undefined || detach);
}
function finalize_no__has_rule(obj:Cno__has_rule, detach:boolean = false) {
}
function finalize_yes__has_rule(obj:Cyes__has_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crules>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_predecessor__rules(obj:Cno__has_predecessor__rules, detach:boolean = false) {
}
function finalize_yes__has_predecessor__rules(obj:Cyes__has_predecessor__rules, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
}
function finalize_entry_reference(obj:Centry_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexistence__application>obj.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.inferences.key_value_source)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
	finalize_entry_reference_definer(obj.definitions.entry_reference_definer, detach);
	finalize_entry_reference_definition(obj.properties.expression, detach);
}
function finalize_containment__type__node_reference(obj:Ccontainment__type__node_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.key_reference_definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Creference_definer>obj.inferences.reference_definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.inferences.value_source_when_mandatory)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
	finalize_entry_step_definition(obj.definitions.entry_step_definition, detach);
	finalize_where_rule_object_path(obj.properties.key_node_path, detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.properties.key_node_path.inferences.containment_iubc)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cupper_bound_cardinality>obj.properties.key_node_path.inferences.local_key_node_iubc)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.properties.key_node_path.inferences.node_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.key_node_path.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_equality__type__node_reference(obj:Cequality__type__node_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.inferences.value_source_when_mandatory)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
	finalize_where_rule_object_path(obj.properties.node_path, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.node_path.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.node_path.inferences.node_type)(detach) !== undefined || detach);
}
function finalize_existence__type__node_reference(obj:Cexistence__type__node_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
}
function finalize_node_reference(obj:Cnode_reference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdirection>obj.output.direction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crelative_object_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccomputation_phase>obj.output.phase)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.referenced_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cdependency__application>obj.output.step_dependency)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstep_type>obj.output.step_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cvalue_source>obj.output.value_source_dependence)(detach) !== undefined || detach);
	finalize_reference_value(obj.definitions.reference_value, detach);
	finalize_where_rule_object_path(obj.properties.object_path, detach);
	switch (obj.properties.type.state.name) {
		case 'containment': finalize_containment__type__node_reference(obj.properties.type.state.node, detach); break;
		case 'equality': finalize_equality__type__node_reference(obj.properties.type.state.node, detach); break;
		case 'existence': finalize_existence__type__node_reference(obj.properties.type.state.node, detach); break;
	}
}
function finalize_rules(obj:Crules, detach:boolean = false) {
	finalize_reference_definer(obj.definitions.reference_definer, detach);
	finalize_reference_behaviour(obj.properties.behaviour, detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.behaviour.inferences.existence_requirement_for_path)(detach) !== undefined || detach);
	finalize_direction_annotation(obj.properties.direction, detach);
	switch (obj.properties.has_predecessor.state.name) {
		case 'no': finalize_no__has_predecessor__rules(obj.properties.has_predecessor.state.node, detach); break;
		case 'yes': finalize_yes__has_predecessor__rules(obj.properties.has_predecessor.state.node, detach); break;
	}
	finalize_recursion_annotation(obj.properties.recursion, detach);
	switch (obj.properties.type.state.name) {
		case 'entry reference': finalize_entry_reference(obj.properties.type.state.node, detach); break;
		case 'node reference': finalize_node_reference(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_reference_rule(obj.properties.ui, detach);
}
function finalize_where_rules_definition(obj:Cwhere_rules_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cwhere_rules_definer>obj.input.definer)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnamed_object>obj.input.variable)(detach) !== undefined || detach);
	switch (obj.properties.has_rule.state.name) {
		case 'no': finalize_no__has_rule(obj.properties.has_rule.state.node, detach); break;
		case 'yes': finalize_yes__has_rule(obj.properties.has_rule.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.rules) {
		finalize_rules(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__allow_anonymous_user(obj:Cno__allow_anonymous_user, detach:boolean = false) {
}
function finalize_yes__allow_anonymous_user(obj:Cyes__allow_anonymous_user, detach:boolean = false) {
}
function finalize_no__has_dynamic_users(obj:Cno__has_dynamic_users, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__allow_anonymous_user>obj.inferences.allow_anonymous)(detach) !== undefined || detach);
}
function finalize_no__has_external_authentication(obj:Cno__has_external_authentication, detach:boolean = false) {
}
function finalize_yes__has_external_authentication(obj:Cyes__has_external_authentication, detach:boolean = false) {
	finalize_descendant_base_property_path(obj.properties.authorities_path, detach);
	assert((<(detach?:boolean) => application.Cbase__type__collection>obj.properties.authorities_path.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.authorities_path.inferences.collection)(detach) !== undefined || detach);
	finalize_descendant_base_property_path(obj.properties.identities_path, detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.identities_path.inferences.collection)(detach) !== undefined || detach);
	finalize_identity_initializer(obj.properties.identity_initializer, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.user_reference as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.properties.user_reference.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.user_reference.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.properties.user_reference.inferences.mandatory)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.user_reference.inferences.reference)(detach) !== undefined || detach);
}
function finalize_no__has_password_authentication(obj:Cno__has_password_authentication, detach:boolean = false) {
}
function finalize_cases__password_status(obj:Ccases__password_status, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Kkeyset___keycases__password_status>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_command_object_expression(obj.properties.initializer, detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_password_status(obj:Cpassword_status, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.cases) {
		finalize_cases__password_status(entry, detach);
	}
	if (!detach) {
		(obj.properties.cases as any).eval_required_keys(detach);
	}
	finalize_descendant_base_property_path(obj.properties.state_group_path, detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.state_group_path.inferences.state_group)(detach) !== undefined || detach);
}
function finalize_yes__has_password_authentication(obj:Cyes__has_password_authentication, detach:boolean = false) {
	finalize_password_initializer(obj.properties.password_initializer, detach);
	finalize_password_status(obj.properties.password_status, detach);
	finalize_descendant_base_property_path(obj.properties.password_text_path, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.properties.password_text_path.inferences.text)(detach) !== undefined || detach);
	finalize_descendant_base_property_path(obj.properties.passwords_collection_path, detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.passwords_collection_path.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.passwords_collection_path.inferences.user_constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.passwords_collection_path.inferences.user_reference)(detach) !== undefined || detach);
}
function finalize_no__supports_user_sign_up(obj:Cno__supports_user_sign_up, detach:boolean = false) {
}
function finalize_yes__supports_user_sign_up(obj:Cyes__supports_user_sign_up, detach:boolean = false) {
	finalize_user_initializer(obj.properties.user_initializer, detach);
}
function finalize_yes__has_dynamic_users(obj:Cyes__has_dynamic_users, detach:boolean = false) {
	switch (obj.properties.has_external_authentication.state.name) {
		case 'no': finalize_no__has_external_authentication(obj.properties.has_external_authentication.state.node, detach); break;
		case 'yes': finalize_yes__has_external_authentication(obj.properties.has_external_authentication.state.node, detach); break;
	}
	switch (obj.properties.has_password_authentication.state.name) {
		case 'no': finalize_no__has_password_authentication(obj.properties.has_password_authentication.state.node, detach); break;
		case 'yes': finalize_yes__has_password_authentication(obj.properties.has_password_authentication.state.node, detach); break;
	}
	switch (obj.properties.supports_user_sign_up.state.name) {
		case 'no': finalize_no__supports_user_sign_up(obj.properties.supports_user_sign_up.state.node, detach); break;
		case 'yes': finalize_yes__supports_user_sign_up(obj.properties.supports_user_sign_up.state.node, detach); break;
	}
	finalize_descendant_base_property_path(obj.properties.users_collection_path, detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.users_collection_path.inferences.collection)(detach) !== undefined || detach);
}
function finalize_no__connection_status(obj:Cno__connection_status, detach:boolean = false) {
}
function finalize_no__dataset_status(obj:Cno__dataset_status, detach:boolean = false) {
}
function finalize_cases__yes__dataset_status(obj:Ccases__yes__dataset_status, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Kkeyset___keycases__yes__dataset_status>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_command_object_expression(obj.properties.initializer, detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__dataset_status(obj:Cyes__dataset_status, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cconnection_status>obj.inferences.connected_state)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.cases) {
		finalize_cases__yes__dataset_status(entry, detach);
	}
	if (!detach) {
		(obj.properties.cases as any).eval_required_keys(detach);
	}
	finalize_descendant_base_property_path(obj.properties.state_group_path, detach);
	assert((<(detach?:boolean) => application.Cbase__type__state_group>obj.properties.state_group_path.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.state_group_path.inferences.state_group)(detach) !== undefined || detach);
}
function finalize_cases__yes__connection_status(obj:Ccases__yes__connection_status, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Kkeyset___keycases__yes__connection_status>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_command_initialization_operation(obj.definitions.command_initialization_operation, detach);
	switch (obj.properties.dataset_status.state.name) {
		case 'no': finalize_no__dataset_status(obj.properties.dataset_status.state.node, detach); break;
		case 'yes': finalize_yes__dataset_status(obj.properties.dataset_status.state.node, detach); break;
	}
	finalize_command_object_expression(obj.properties.initializer, detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__connection_status(obj:Cyes__connection_status, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.cases) {
		finalize_cases__yes__connection_status(entry, detach);
	}
	if (!detach) {
		(obj.properties.cases as any).eval_required_keys(detach);
	}
	finalize_descendant_base_property_path(obj.properties.state_group_path, detach);
	assert((<(detach?:boolean) => application.Cbase__type__state_group>obj.properties.state_group_path.inferences.base_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.state_group_path.inferences.state_group)(detach) !== undefined || detach);
}
function finalize_many__instances(obj:Cmany__instances, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnone__constraint>obj.inferences.collection_cardinality_constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cimported_interfaces>obj.inferences.value_source_is_interface)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
	finalize_interface_instance_initializer(obj.properties.initializer, detach);
}
function finalize_one__instances(obj:Cone__instances, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_navigation_context>obj.inferences.root_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cimported_interfaces>obj.inferences.value_source_is_interface)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_parameters(obj:Cparameters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.value as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.properties.value.inferences.value)(detach) !== undefined || detach);
}
function finalize_imported_interfaces(obj:Cimported_interfaces, detach:boolean = false) {
	finalize_external_source(obj.definitions.external_source, detach);
	switch (obj.properties.connection_status.state.name) {
		case 'no': finalize_no__connection_status(obj.properties.connection_status.state.node, detach); break;
		case 'yes': finalize_yes__connection_status(obj.properties.connection_status.state.node, detach); break;
	}
	switch (obj.properties.instances.state.name) {
		case 'many': finalize_many__instances(obj.properties.instances.state.node, detach); break;
		case 'one': finalize_one__instances(obj.properties.instances.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.parameters) {
		finalize_parameters(entry, detach);
	}
	if (!detach) {
	}
	finalize_object_path_tail(obj.properties.path, detach);
}
function finalize_no__unit_conversion(obj:Cno__unit_conversion, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.inferences.numerical_type)(detach) !== undefined || detach);
}
function finalize_yes__unit_conversion(obj:Cyes__unit_conversion, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Csingular_conversions>(obj.properties.conversion_rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cfactor>obj.properties.conversion_rule.inferences.conversion_rule)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.properties.conversion_rule.inferences.range_type)(detach) !== undefined || detach);
}
function finalize_base__type__singular_conversions(obj:Cbase__type__singular_conversions, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cscale>obj.inferences.from)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cscale>obj.inferences.to)(detach) !== undefined || detach);
	switch (obj.properties.unit_conversion.state.name) {
		case 'no': finalize_no__unit_conversion(obj.properties.unit_conversion.state.node, detach); break;
		case 'yes': finalize_yes__unit_conversion(obj.properties.unit_conversion.state.node, detach); break;
	}
}
function finalize_no__invert(obj:Cno__invert, detach:boolean = false) {
}
function finalize_yes__invert(obj:Cyes__invert, detach:boolean = false) {
}
function finalize_factor(obj:Cfactor, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.from)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.inferences.to)(detach) !== undefined || detach);
	switch (obj.properties.invert.state.name) {
		case 'no': finalize_no__invert(obj.properties.invert.state.node, detach); break;
		case 'yes': finalize_yes__invert(obj.properties.invert.state.node, detach); break;
	}
}
function finalize_singular_conversions(obj:Csingular_conversions, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'base': finalize_base__type__singular_conversions(obj.properties.type.state.node, detach); break;
		case 'factor': finalize_factor(obj.properties.type.state.node, detach); break;
	}
}
function finalize_division_conversions(obj:Cdivision_conversions, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.key.inferences.division_conversions)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.properties.denominator as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.properties.denominator.inferences.denominator)(detach) !== undefined || detach);
}
function finalize_product_conversions(obj:Cproduct_conversions, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.key.inferences.product_conversions)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.properties.right as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.properties.right.inferences.right)(detach) !== undefined || detach);
}
function finalize_quantity(obj:Cquantity, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.quantity_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.division_conversions) {
		finalize_division_conversions(entry, detach);
	}
	if (!detach) {
	}
	for (const [_key, entry] of obj.properties.product_conversions) {
		finalize_product_conversions(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_none__timer_resolution(obj:Cnone__timer_resolution, detach:boolean = false) {
}
function finalize_seconds__timer_resolution(obj:Cseconds__timer_resolution, detach:boolean = false) {
}
function finalize_scale(obj:Cscale, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.output.quantity_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.properties.range_type as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cquantity>obj.properties.range_type.inferences.quantity)(detach) !== undefined || detach);
	switch (obj.properties.timer_resolution.state.name) {
		case 'none': finalize_none__timer_resolution(obj.properties.timer_resolution.state.node, detach); break;
		case 'seconds': finalize_seconds__timer_resolution(obj.properties.timer_resolution.state.node, detach); break;
	}
}
function finalize_numerical_types(obj:Cnumerical_types, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.singular_conversions) {
		finalize_singular_conversions(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.type.state.name) {
		case 'quantity': finalize_quantity(obj.properties.type.state.node, detach); break;
		case 'scale': finalize_scale(obj.properties.type.state.node, detach); break;
	}
	finalize_ui_numerical_type(obj.properties.ui, detach);
}
function finalize_application(obj:Capplication, detach:boolean = false) {
	finalize_entity__application(obj.definitions.entity, detach);
	finalize_root_location(obj.definitions.root_location, detach);
	switch (obj.properties.allow_anonymous_user.state.name) {
		case 'no': finalize_no__allow_anonymous_user(obj.properties.allow_anonymous_user.state.node, detach); break;
		case 'yes': finalize_yes__allow_anonymous_user(obj.properties.allow_anonymous_user.state.node, detach); break;
	}
	switch (obj.properties.has_dynamic_users.state.name) {
		case 'no': finalize_no__has_dynamic_users(obj.properties.has_dynamic_users.state.node, detach); break;
		case 'yes': finalize_yes__has_dynamic_users(obj.properties.has_dynamic_users.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.imported_interfaces) {
		finalize_imported_interfaces(entry, detach);
	}
	if (!detach) {
	}
	for (const [_key, entry] of obj.properties.numerical_types) {
		finalize_numerical_types(entry, detach);
	}
	if (!detach) {
	}
	finalize_node__application(obj.properties.root, detach);
}

export namespace Capplication {
	export function create(init:Tapplication, symbols_file?: SymbolsFile):Capplication {
		const instance = new Capplication(init, symbols_file);
		return instance;
	};
}
